/**
 * OVER 9000 APP REGISTRY
 * ======================
 * Live source for the mosaic SPA and the ambient Worker.
 * Schema v2 is a projection (src/registry/v2.ts, `pnpm o9k migrate`).
 * Do not add a fleet door secret. See docs/ARCHITECTURE.md.
 * Append one object when you ship. Pair it with a screenshot in /public/shots.
 * Full workflow: docs/SHIP.md  ·  dual-door model: docs/AMBIENT.md
 *
 * slug        DNS / path label (lowercase, no dots). Not a reserved label
 *             (`id`, `mail`, `admin`, `auth`, `login`, … — see isReservedSlug).
 *             `admin` is the console host, never a row in this list.
 * surface     app | cli | api | desk | edge | land | data | vid | studio
 * upstream    origin the Worker reverse-proxies (e.g. https://pokefit.pages.dev)
 * upstreamStaging  optional origin for `{slug}-staging.over9000.cc` only.
 *             Omitted = that staging host is not proxied. Never the live
 *             door and never a wall tile. See docs/STAGING.md.
 * canonical   "host" (default) → Worker SEO link is {slug}.over9000.cc
 *             "path"           → Worker SEO link is over9000.cc/{slug}
 *             The hub UI always shows the path door, via displayUrl().
 *             `surface` is registry metadata only — it is not in the public URL.
 * status      "live" only when the Worker can actually reach upstream.
 *             "build" = upstream assigned, not smoked yet: not proxied,
 *             not counted, not on the wall. "wip" routes the same way.
 * example     true = seed / mock row — registry index only, excluded from the wall and the 9000 count
 * featured    optional showcase flag for the default wall order
 * featuredOrder  optional sort inside the featured group (lower first)
 * title       short name on the tile / inventory / modal
 * screenshot  path under /public, e.g. /shots/foo.svg
 * shippedAt   ISO date. YYYY-MM-DD is an America/Los_Angeles calendar day, not
 *             UTC midnight. A full ISO timestamp is converted to that zone for
 *             the HUD. 1–n wall order sorts this string, then slug.
 * blurb       one-line description (optional) — tile modal TLDR
 * language    optional two-letter ISO code. Omit or null = English (EN).
 *             Tile, registry card, and modal show a tag when it is not EN.
 * appId       immutable id. Omit to derive app_<slug with underscores>.
 *             Set this when the slug changes so the id stays put.
 * SLUG_REDIRECTS  former public slugs. Ambient 301s them to the current slug.
 * storage     omit or "none" (default) = no D1. "shared" = studio D1
 *             over9000-slop (SLOP_D1). Physical tables are {slug}__{table}
 *             (slopTable); user rows include user_id. Platform tables are
 *             o9k__* only. "dedicated" = this app's own digitalnative D1,
 *             unprefixed names, rows keyed by user_id. Allowlist (Free ~10 D1s).
 *             IdP D1 over9000-id is not a registry row and uses unprefixed
 *             names — see docs/auth/README.md.
 * auth        omit = no IdP client. "optional" | "required" projects an edge
 *             client (client_id {surface}.{slug}). Object form is direct OIDC:
 *             mode, kind ("edge" | "confidential" | "public"), scopes,
 *             redirectUris, postLogoutRedirectUris. kind "public" is PKCE
 *             with no client secret (not the door BFF). mode "optional"
 *             leaves anonymous pages open. mode "required" redirects anonymous
 *             HTML document navigations to /_o9k/auth/login on that door.
 *             Projected by scripts/sync-id-clients.mjs.
 *
 * Host and path URLs are derived — do not store a separate `url` field.
 * `storage` does not change ambient routing.
 * The hub sitemap is derived too (`pnpm sitemap` → public/sitemap.xml).
 * Surface children include a poster when public/posters/<slug>.webp exists.
 * robots.txt names the index only. Do not hand-edit those files.
 */

import { isReservedSlug, type Surface } from "./constants";
import type { CanonicalForm } from "./urls";

export type { Surface } from "./constants";
export type { CanonicalForm } from "./urls";
export { SURFACES, SURFACE_PATTERN, ZONE, isSurface } from "./constants";
export {
  canonicalUrl,
  displayUrl,
  hostLabel,
  hostUrl,
  pathLabel,
  pathUrl,
} from "./urls";
export { identity, identityFromUrl, type Identity, type Via } from "./identity";

/** "live" is proxied and counted. "build" and "wip" stay in the index only. */
export type AppStatus = "live" | "build" | "wip";

/** Product D1 classification. Omit on the row means `"none"`. */
export type AppStorage = "none" | "shared" | "dedicated";

/**
 * Studio D1 for `storage: "shared"` on the digitalnative account.
 * D1 has no schemas: app tables are `{slug}__{table}` and include `user_id`.
 * Not the IdP database — `over9000-id` stays Sign in with Over9000 only.
 */
export const SLOP_D1 = {
  name: "over9000-slop",
  databaseId: "c4bf80c3-468c-4898-8f01-08b76fe8b1ea",
} as const;

/**
 * Staging twins on digitalnative. Empty by default — do not copy prod rows.
 * Apps bind `SLOP_D1_STAGING` only when deploying a staging Worker.
 * `ID_D1_STAGING` is the staging IdP database, not a registry app.
 */
export const SLOP_D1_STAGING = {
  name: "over9000-slop-staging",
  databaseId: "a70f8386-88ff-4d8f-ae44-56c3627ae2a4",
} as const;

export const ID_D1_STAGING = {
  name: "over9000-id-staging",
  databaseId: "e6c4a532-03b5-42c3-842b-da964be823c9",
} as const;

/** Platform tables in over9000-slop (`o9k__migrations`, …). Apps must not create these. */
export const SLOP_PLATFORM_PREFIX = "o9k__";

export type AuthMode = "optional" | "required";

export type AuthConfig = {
  mode: AuthMode;
  kind?: "edge" | "confidential" | "public";
  scopes?: string[];
  redirectUris?: string[];
  postLogoutRedirectUris?: string[];
  tokenEndpointAuthMethod?: "client_secret_basic" | "private_key_jwt" | "none";
};

export type AppEntry = {
  slug: string;
  surface: Surface;
  upstream: string;
  /**
   * Origin for `{slug}-staging.{zone}` only. The live door keeps `upstream`.
   * Absent means the staging host is not proxied.
   */
  upstreamStaging?: string;
  canonical?: CanonicalForm;
  status: AppStatus;
  title: string;
  screenshot: string;
  shippedAt: string;
  /** Opt in to Sign in with Over9000. Projected into IdP clients by scripts/sync-id-clients.mjs. */
  auth?: AuthMode | AuthConfig;
  /**
   * Product persistence. Missing means `"none"` (no D1).
   * `"dedicated"` is an allowlist — the app already has its own D1.
   */
  storage?: AppStorage;
  example?: boolean;
  /** Curated for the default (featured) wall order. */
  featured?: boolean;
  /** Lower numbers lead the featured group. Implies featured. */
  featuredOrder?: number;
  blurb?: string;
  /**
   * Two-letter ISO code for the app's content language.
   * Omit or null for English (EN). The hub shows a tag when it is not EN.
   */
  language?: string | null;
  /**
   * Immutable app id. Omit to derive `app_<slug>`. Set when renaming a slug
   * so migrate does not mint a new id.
   */
  appId?: string;
};

/**
 * Registry order is not the wall order. The HUD sorts live apps
 * (default: featured, then shippedAt + slug). Example rows stay in the
 * index only — they are not wall tiles.
 */
export const APPS: AppEntry[] = [
  // Sign-in pilot (9K-16). Edge string auth so the door BFF owns the code
  // flow at /_o9k/auth/callback. Matches the live IdP client app.fodmap
  // (kind edge, edge_rpc). storage dedicated: Settle already has its own D1.
  // Demo seed frames (hello, lab, atlas) stay removed.
  {
    slug: "fodmap",
    surface: "app",
    title: "Settle",
    upstream: "https://fodmap.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/fodmap.webp",
    shippedAt: "2026-09-22",
    status: "live",
    featured: true,
    featuredOrder: 3,
    storage: "dedicated",
    auth: "optional",
    blurb: "Calm FODMAP companion — check foods, log meals and symptoms, find your triggers.",
  },
  // CoupleSpark. pages.dev upstream (not same-account workers.dev). Host is
  // the SEO canonical (Over90000 / neo 2026-10-06 audit follow-up); apex path
  // remains redirect-only. Sign in with Over9000 is public PKCE on the
  // host-door callback, plus shared slop storage (couplespark__* with
  // user_id). Session/auth cookies stay on the host door per docs/auth.
  // Scopes are openid, profile, and email only — offline_access stays off
  // because refresh is unwired.
  {
    slug: "couplespark",
    surface: "app",
    title: "CoupleSpark",
    upstream: "https://couplespark.pages.dev",
    canonical: "host",
    screenshot: "/shots/couplespark.webp",
    shippedAt: "2026-10-01",
    status: "live",
    featured: true,
    featuredOrder: 7,
    storage: "shared",
    auth: {
      mode: "optional",
      kind: "public",
      scopes: ["openid", "profile", "email"],
      redirectUris: ["https://couplespark.over9000.cc/api/auth/callback"],
    },
    blurb: "Daily sealed answers for couples; reveal together; grow a shared nest",
  },
  // Marrow. Same-account workers.dev on digitalnative (Pages project limit
  // reached), so host is the SEO canonical. Static SPA: no auth, no D1.
  // Source: github.com/021-apps/marrow.
  {
    slug: "marrow",
    surface: "app",
    title: "Marrow",
    upstream: "https://marrow.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/marrow.webp",
    shippedAt: "2026-10-11",
    status: "live",
    featured: true,
    featuredOrder: 37,
    blurb: "A weeknight cookbook: 257 original recipes, scaling, cook mode, recipe box.",
  },
  // MouthType. Same-account workers.dev on digitalnative (like fodmap), so
  // host is the SEO canonical. Client-side only: no auth, no D1.
  {
    slug: "mouthtype",
    surface: "app",
    title: "MouthType",
    upstream: "https://mouthtype.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/mouthtype.webp",
    shippedAt: "2026-10-01",
    status: "live",
    featured: true,
    featuredOrder: 38,
    blurb: "Hold a button and mouth a short phrase; LipNet reads it in the browser.",
  },
  // md-tui. Same-account workers.dev on digitalnative; client-side only.
  {
    slug: "md-tui",
    surface: "app",
    title: "md",
    upstream: "https://md-tui.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/md-tui.webp",
    shippedAt: "2026-10-14",
    status: "live",
    featured: true,
    featuredOrder: 39,
    blurb: "A terminal-style Markdown viewer: search, vim keys, themes. Also a Rust CLI.",
  },
  // Same-account workers.dev on digitalnative (like fodmap and mouthtype), so
  // host is the SEO canonical. kludge, shouldibuildthis, and jobtrackr use
  // public PKCE like couplespark (app-owned /api/auth/callback on the host
  // door, not the door BFF /_o9k) and shared slop storage ({slug}__* with
  // user_id). Scopes are openid, profile, and email only. Anonymous use
  // stays open.
  {
    slug: "kludge",
    surface: "app",
    title: "Kludge",
    upstream: "https://kludge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/kludge.webp",
    shippedAt: "2026-10-01",
    status: "live",
    storage: "shared",
    auth: {
      mode: "optional",
      kind: "public",
      scopes: ["openid", "profile", "email"],
      redirectUris: ["https://kludge.over9000.cc/api/auth/callback"],
    },
    blurb: "Classifieds board for ugly weekly workarounds — signal for which SaaS to build.",
  },
  {
    slug: "isocity-builder",
    surface: "app",
    title: "IsoCity Builder",
    upstream: "https://isocity-builder.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/isocity-builder.webp",
    shippedAt: "2026-10-01",
    status: "live",
    blurb: "Interactive isometric city designer on canvas.",
  },
  {
    slug: "vibefeed",
    surface: "app",
    title: "VibeFeed",
    upstream: "https://vibefeed.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vibefeed.webp",
    shippedAt: "2026-10-01",
    status: "live",
    blurb: "TikTok-style feed of short playable mini-games.",
  },
  {
    slug: "shouldibuildthis",
    surface: "app",
    title: "Should I Build This",
    upstream: "https://shouldibuildthis.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/shouldibuildthis.webp",
    shippedAt: "2026-10-01",
    status: "live",
    storage: "shared",
    auth: {
      mode: "optional",
      kind: "public",
      scopes: ["openid", "profile", "email"],
      redirectUris: ["https://shouldibuildthis.over9000.cc/api/auth/callback"],
    },
    blurb: "Blunt don't-build score for a startup pitch.",
  },
  {
    slug: "jobtrackr",
    surface: "app",
    title: "JobTrackr",
    upstream: "https://jobtrackr.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/jobtrackr.webp",
    shippedAt: "2026-10-01",
    status: "live",
    storage: "shared",
    auth: {
      mode: "optional",
      kind: "public",
      scopes: ["openid", "profile", "email"],
      redirectUris: ["https://jobtrackr.over9000.cc/api/auth/callback"],
    },
    blurb: "Local-first job application tracker kanban; sign in to sync.",
  },
  // PhoneEatsFirst. Same-account workers.dev on digitalnative (Pages at 100/100),
  // so host is the SEO canonical. Public PKCE + shared slop storage
  // (phone-eats-first__* with user_id). Scopes openid/profile/email only.
  {
    slug: "phone-eats-first",
    surface: "app",
    title: "PhoneEatsFirst",
    upstream: "https://phone-eats-first.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/phone-eats-first.webp",
    shippedAt: "2026-10-02",
    status: "live",
    storage: "shared",
    auth: {
      mode: "optional",
      kind: "public",
      scopes: ["openid", "profile", "email"],
      redirectUris: ["https://phone-eats-first.over9000.cc/api/auth/callback"],
    },
    blurb:
      "Unpacking × Pokémon Snap — photograph DoorDash dishes before you eat; the meal can wait, the shot can’t.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "vibekanban",
    surface: "app",
    title: "Vibe Kanban",
    upstream: "https://vibekanban.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vibekanban.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Tiny local-first kanban: maybe vibes, vibing, shipped. Drag, tap, export.",
  },
  {
    slug: "flipbook",
    surface: "app",
    title: "Flipbook",
    upstream: "https://flipbook.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/flipbook.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Draw pages with onion-skinning, flip them into motion, export video.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "sparklink",
    surface: "app",
    title: "Sparklink",
    upstream: "https://sparklink.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/sparklink.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Build a link-in-bio page in seconds; the whole page lives in a shareable URL.",
  },
  {
    slug: "siteforge-ai-website-rebuilder",
    surface: "app",
    title: "SiteForge",
    upstream: "https://siteforge-ai-website-rebuilder.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/siteforge-ai-website-rebuilder.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Paste messy HTML, get an audit and a clean semantic rebuild. Runs in your browser.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "vibeshort",
    surface: "app",
    title: "VibeShort",
    upstream: "https://vibeshort.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vibeshort.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Pick a vibe, write a line, make a looping 6s vertical short in your browser.",
  },
  {
    slug: "zipp-ly-url-shortener",
    surface: "app",
    title: "Zipp.ly",
    upstream: "https://zipp-ly-url-shortener.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/zipp-ly-url-shortener.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Serverless link wrapper: destination lives in the URL, with a leaving-site stop screen.",
  },
  {
    slug: "vercel-ship-25-coding-agent",
    surface: "app",
    title: "Coding Agent Lab",
    upstream: "https://vercel-ship-25-coding-agent.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vercel-ship-25-coding-agent.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Turn a task + GitHub repo into the agent API request and replay the agent loop.",
  },
  {
    slug: "brand-guidelines-kit",
    surface: "app",
    title: "Brand Guidelines Kit",
    upstream: "https://brand-guidelines-kit.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/brand-guidelines-kit.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Interactive sample brand system: logo, Inter type, pixel texture, copyable palette.",
  },
  {
    slug: "airt",
    surface: "app",
    title: "AiRT Studio",
    upstream: "https://airt.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/airt.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Turn a phrase into generative art, tune it with live filters, download it.",
  },
  {
    slug: "how-the-frick-do-i-sell-this",
    surface: "app",
    title: "How The Frick Do I Sell This",
    upstream: "https://how-the-frick-do-i-sell-this.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/how-the-frick-do-i-sell-this.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Type any product idea, get a seven-step sales plan you can expand and tick off.",
  },
  {
    slug: "polyunwrap-research-paperpet-research",
    surface: "app",
    title: "PolyUnwrap Research",
    upstream: "https://polyunwrap-research-paperpet-research.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/polyunwrap-research-paperpet-research.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Mesh UV-unwrapping research notes plus an in-browser STL segmentation and atlas demo",
  },
  {
    slug: "interactive-3d-animations",
    surface: "app",
    title: "Interactive 3D Animations",
    upstream: "https://interactive-3d-animations.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/interactive-3d-animations.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Nine interactive Three.js scenes: AirPods, headphones, wobbles, cosmic cluster.",
  },
  {
    slug: "next-trade",
    surface: "app",
    title: "Next Trade",
    upstream: "https://next-trade.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/next-trade.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Sleek dark-themed trading platform landing page with charts, guides and testimonials",
  },
  {
    slug: "nichequest",
    surface: "app",
    title: "NicheQuest",
    upstream: "https://nichequest.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/nichequest.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Enter an industry, get four 'X for Y' micro-SaaS sub-niche ideas. Instant, no sign-up.",
  },
  {
    slug: "niche-jackpot",
    surface: "app",
    title: "Niche Jackpot",
    upstream: "https://niche-jackpot.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/niche-jackpot.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Slot machine for startup ideas: spin industry x audience x twist, get 3 micro-niche ideas.",
  },
  {
    slug: "interactive-3d-model-viewer",
    surface: "app",
    title: "3D Model Viewer",
    upstream: "https://interactive-3d-model-viewer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/interactive-3d-model-viewer.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Drop a GLB or GLTF file and spin, pan and zoom it in 3D, all in your browser.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "mtg-infinite-oracle",
    surface: "app",
    title: "Infinite Oracle",
    upstream: "https://mtg-infinite-oracle.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/mtg-infinite-oracle.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Pick MTG cards, set the board, get a Judge prompt to check if your combo goes infinite",
  },
  {
    slug: "isospace-ai-room-builder",
    surface: "app",
    title: "IsoSpace Room Builder",
    upstream: "https://isospace-ai-room-builder.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/isospace-ai-room-builder.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Isometric mini-room builder: place furniture, auto-design rooms, walk your avatar.",
  },
  {
    slug: "new-biz-helper",
    surface: "app",
    title: "New Biz Helper",
    upstream: "https://new-biz-helper.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/new-biz-helper.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Answer 4 questions, get an editable landing page and demo leads dashboard.",
  },
  {
    slug: "notion-ai-content-generator",
    surface: "app",
    title: "Notion Page Generator",
    upstream: "https://notion-ai-content-generator.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/notion-ai-content-generator.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Pick a template, name a topic, get editable Markdown ready to paste into Notion.",
  },
  {
    slug: "outreach-ai-agent",
    surface: "app",
    title: "Outreach Agent",
    upstream: "https://outreach-ai-agent.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/outreach-ai-agent.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Turn prospect facts into a short three-part cold email, privately in your browser.",
  },
  {
    slug: "recursive-ai-idea-decomposer",
    surface: "app",
    title: "Idea Decomposer",
    upstream: "https://recursive-ai-idea-decomposer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/recursive-ai-idea-decomposer.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Turn a big idea into a drill-down tree of customer, product, revenue and risk steps.",
  },
  {
    slug: "omni",
    surface: "app",
    title: "Omni",
    upstream: "https://omni.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/omni.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "One local-first command bar for tasks, notes and links.",
  },
  {
    slug: "app-best-idea-to-tasks",
    surface: "app",
    title: "Best Idea to Tasks",
    upstream: "https://app-best-idea-to-tasks.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/app-best-idea-to-tasks.webp",
    shippedAt: "2026-10-03",
    status: "live",
    storage: "shared",
    auth: {
      mode: "optional",
      kind: "public",
      scopes: ["openid", "profile", "email"],
      redirectUris: ["https://app-best-idea-to-tasks.over9000.cc/api/auth/callback"],
    },
    blurb: "Turn one idea or a launch playbook into a mind-map of tasks you break down step by step.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "recursive-ai-task-decomposer",
    surface: "app",
    title: "Recursive AI Task Decomposer",
    upstream: "https://recursive-ai-task-decomposer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/recursive-ai-task-decomposer.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Turn a vague idea into goals, epics and TODOs. Local-first. AI coming soon.",
  },
  {
    slug: "recursive-ai-task-decomposer-alt",
    surface: "app",
    title: "Recursive Task Decomposer",
    upstream: "https://recursive-ai-task-decomposer-alt.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/recursive-ai-task-decomposer-alt.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Break a goal into epics, then todos with requirements and acceptance criteria.",
  },
  {
    slug: "integration-analysis-platform",
    surface: "app",
    title: "Integration Analysis Platform",
    upstream: "https://integration-analysis-platform.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/integration-analysis-platform.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Score your value chain, get ranked vertical and horizontal integration moves.",
  },
  {
    slug: "entiquity-ai-powered-book-discovery",
    surface: "app",
    title: "Entiquity",
    upstream: "https://entiquity-ai-powered-book-discovery.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/entiquity-ai-powered-book-discovery.webp",
    shippedAt: "2026-10-03",
    status: "live",
    featured: true,
    featuredOrder: 40,
    blurb: "Search public-domain books by topic or author and read the classics free online.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "leanvalidator",
    surface: "app",
    title: "LeanValidator",
    upstream: "https://leanvalidator.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/leanvalidator.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Lean-startup workspace: log evidence per method, earn a confidence score before you build.",
  },
  {
    slug: "lexiforge-african",
    surface: "app",
    title: "LexiForge.African",
    upstream: "https://lexiforge-african.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/lexiforge-african.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Draft modern words for African languages from indigenous roots; map gaps, save, export.",
  },
  {
    slug: "espn-nfl-etl",
    surface: "app",
    title: "ESPN NFL ETL",
    upstream: "https://espn-nfl-etl.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/espn-nfl-etl.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Ops dashboard for an ESPN NFL ETL pipeline: teams, players, games, stats, run logs.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "lexigap-africa",
    surface: "app",
    title: "LexiGap Africa",
    upstream: "https://lexigap-africa.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/lexigap-africa.webp",
    shippedAt: "2026-10-03",
    status: "live",
    featured: true,
    featuredOrder: 50,
    blurb: "Lexical gap matrix for modern terms across widely spoken African languages.",
  },
  {
    slug: "marketing-copy-research-agent",
    surface: "app",
    title: "Marketing Copy Research",
    upstream: "https://marketing-copy-research-agent.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/marketing-copy-research-agent.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Turn a topic into starter marketing lines, with the reason each one works.",
  },
  {
    slug: "market-positioning-analyzer",
    surface: "app",
    title: "Market Positioning Analyzer",
    upstream: "https://market-positioning-analyzer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/market-positioning-analyzer.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Map rivals on integration vs breadth. Catalog scores you can drag.",
  },
  {
    slug: "fork-with-voice-and-chat-recursive-ai-task-decomposer",
    surface: "app",
    title: "Recursive AI Task Decomposer",
    upstream: "https://fork-with-voice-and-chat-recursive-ai-task-decomposer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/fork-with-voice-and-chat-recursive-ai-task-decomposer.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Local-first planner: projects and goals in the browser. AI coming soon.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "low-qual-entiquitywebsite",
    surface: "app",
    title: "Entiquity",
    upstream: "https://low-qual-entiquitywebsite.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/low-qual-entiquitywebsite.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Public-domain ebook pitch with an on-device shelf of classics.",
  },
  {
    slug: "lumina-audio",
    surface: "app",
    title: "Lumina Audio",
    upstream: "https://lumina-audio.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/lumina-audio.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Read original short stories, or press play and the browser speaks them.",
  },
  {
    slug: "mermaid-live-architect",
    surface: "app",
    title: "Mermaid Live Architect",
    upstream: "https://mermaid-live-architect.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/mermaid-live-architect.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Drag-and-drop flowchart editor that stays in sync with Mermaid text.",
  },
  {
    slug: "modern-entiquity-website",
    surface: "app",
    title: "Entiquity",
    upstream: "https://modern-entiquity-website.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/modern-entiquity-website.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Boutique press page with sample editions and a local submission note.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "nfl-etl-tool",
    surface: "app",
    title: "NFL ETL Desk",
    upstream: "https://nfl-etl-tool.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/nfl-etl-tool.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Plan today's ESPN NFL extract slate and watch the week scoreboard.",
  },
  {
    slug: "nfl-play-by-play-video-analyzer",
    surface: "app",
    title: "NFL Film Room",
    upstream: "https://nfl-play-by-play-video-analyzer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/nfl-play-by-play-video-analyzer.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Line up a local NFL tape with ESPN play-by-play and jump every play.",
  },
  {
    slug: "nice-animation-entiquity-website",
    surface: "app",
    title: "Entiquity",
    upstream: "https://nice-animation-entiquity-website.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/nice-animation-entiquity-website.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Boutique press site: featured books, a house philosophy, and notes kept on device.",
  },
  {
    slug: "obsidian-3d-chess",
    surface: "app",
    title: "Obsidian 3D Chess",
    upstream: "https://obsidian-3d-chess.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/obsidian-3d-chess.webp",
    shippedAt: "2026-10-03",
    status: "live",
    featured: true,
    featuredOrder: 41,
    blurb: "Play chess on a dark 3D board with a local coach. No account.",
  },
  {
    slug: "p5-studio",
    surface: "app",
    title: "P5 Studio",
    upstream: "https://p5-studio.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/p5-studio.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Edit p5.js sketches with live preview, history, and recipes.",
  },
  {
    slug: "product-idea-validator",
    surface: "app",
    title: "Product Idea Validator",
    upstream: "https://product-idea-validator.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/product-idea-validator.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Twenty-five pre-mortem questions for a product idea, in the browser.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "papercraft-unfolder",
    surface: "app",
    title: "Papercraft Unfolder",
    upstream: "https://papercraft-unfolder.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/papercraft-unfolder.webp",
    shippedAt: "2026-10-03",
    status: "live",
    featured: true,
    featuredOrder: 42,
    blurb: "Printable paper nets with glue tabs, fold lines, and assembly steps.",
  },
  {
    slug: "real-time-video-background-remover",
    surface: "app",
    title: "Background Remover",
    upstream: "https://real-time-video-background-remover.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/real-time-video-background-remover.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Remove or replace a webcam or video background in the browser, live.",
  },
  {
    slug: "remotion-ai-book-video-copy",
    surface: "app",
    title: "Book Trailer",
    upstream: "https://remotion-ai-book-video-copy.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/remotion-ai-book-video-copy.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Five-scene book trailer storyboard from a title and summary.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "recursive-ai-name-generator",
    surface: "app",
    title: "Recursive Name Studio",
    upstream: "https://recursive-ai-name-generator.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/recursive-ai-name-generator.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Branch a concept into approaches, categories, and names you can keep.",
  },
  {
    slug: "recursive-ai-task-decomposer-5",
    surface: "app",
    title: "Recursive Task Decomposer",
    upstream: "https://recursive-ai-task-decomposer-5.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/recursive-ai-task-decomposer-5.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Break a big idea into a map of next steps you can keep drilling into.",
  },
  {
    slug: "riskrail-schedule-risk-analysis",
    surface: "app",
    title: "RiskRail",
    upstream: "https://riskrail-schedule-risk-analysis.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/riskrail-schedule-risk-analysis.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Monte Carlo schedule and cost risk. Commit to a date at 80% confidence.",
  },
  {
    slug: "rizz-ai",
    surface: "app",
    title: "Rizz AI",
    upstream: "https://rizz-ai.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/rizz-ai.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Three short replies from a chat you paste. Stays in the browser.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "gemini-nexus-1",
    surface: "app",
    title: "Nexus",
    upstream: "https://gemini-nexus-1.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/gemini-nexus-1.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Dark-mode streaming chat on your ChatGPT plan. No API key.",
  },
  {
    slug: "saas-business-blueprint",
    surface: "app",
    title: "SaaS Business Blueprint",
    upstream: "https://saas-business-blueprint.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/saas-business-blueprint.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Local operating sheet for a SaaS idea across product, GTM, success, ops, and finance.",
  },
  {
    slug: "seo-indexing-guide",
    surface: "app",
    title: "SEO Indexing Guide",
    upstream: "https://seo-indexing-guide.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/seo-indexing-guide.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Check robots.txt, the sitemap, and the on-page tags a site needs to be indexed.",
  },
  {
    slug: "smol-agents-agentic-learning-platform",
    surface: "app",
    title: "Smol Agents Academy",
    upstream: "https://smol-agents-agentic-learning-platform.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/smol-agents-agentic-learning-platform.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Learn agent patterns, examples, and tutorials. Progress stays in the browser.",
  },
  {
    slug: "system-debugger-environment-analyzer",
    surface: "app",
    title: "System Debugger",
    upstream: "https://system-debugger-environment-analyzer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/system-debugger-environment-analyzer.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Inspect this browser and export a local debug report with on-device triage.",
  },
  {
    slug: "t-shirt-lawyer-landing-page",
    surface: "app",
    title: "T-Shirt Lawyer",
    upstream: "https://t-shirt-lawyer-landing-page.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/t-shirt-lawyer-landing-page.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Comic personal-injury landing page with an on-device free case review.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "internal-dev-tools",
    surface: "app",
    title: "Internal Dev Tools",
    upstream: "https://internal-dev-tools.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/internal-dev-tools.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Turn a repeated chore into an adopt, fork, build, or defer proposal.",
  },
  {
    slug: "test-e2e",
    surface: "app",
    title: "Test E2E",
    upstream: "https://test-e2e.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/test-e2e.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Local run sheet for end-to-end clone, change, and push checks.",
  },
  {
    slug: "thinking-space",
    surface: "app",
    title: "Thinking Space",
    upstream: "https://thinking-space.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/thinking-space.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Orbit a photo set in 3D and search it by what the pictures show.",
  },
  {
    slug: "trendspark-ai",
    surface: "app",
    title: "TrendSpark",
    upstream: "https://trendspark-ai.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/trendspark-ai.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Turn a social clip into a plan sketch, meeting notes, or a song.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "ast-for-typescript-repo",
    surface: "app",
    title: "TypeScript AST Explorer",
    upstream: "https://ast-for-typescript-repo.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ast-for-typescript-repo.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Function call graph for TypeScript, parsed in the browser.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "auto-message",
    surface: "app",
    title: "AutoMessage",
    upstream: "https://auto-message.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/auto-message.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Schedule the messages you mean to send. They stay on this device.",
  },
  {
    slug: "don-t-sell-house-to-pe",
    surface: "app",
    title: "Don't Sell to PE",
    upstream: "https://don-t-sell-house-to-pe.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/don-t-sell-house-to-pe.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Screen a home offer for private-equity and wholesaler patterns before you sign.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "epub-editor",
    surface: "app",
    title: "EPUB Editor",
    upstream: "https://epub-editor.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/epub-editor.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Open an EPUB in the browser, edit it, and download a repacked book.",
  },
  {
    slug: "kw-logo",
    surface: "app",
    title: "KW Logo",
    upstream: "https://kw-logo.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/kw-logo.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Matrix-style wordmarks in the browser. Rain, glitch, or glow, then a PNG.",
  },
  {
    slug: "next-js-speech-app",
    surface: "app",
    title: "Speech Cadence",
    upstream: "https://next-js-speech-app.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/next-js-speech-app.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Practice speech rhythm, intonation, and pacing with instant local feedback.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "paint-ascii",
    surface: "app",
    title: "PaintASCII",
    upstream: "https://paint-ascii.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/paint-ascii.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Draw monospace diagrams and copy them into a README.",
  },
  {
    slug: "power-tool-ranking-meme",
    surface: "app",
    title: "Power Tool Tier List",
    upstream: "https://power-tool-ranking-meme.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/power-tool-ranking-meme.webp",
    shippedAt: "2026-10-03",
    status: "live",
    featured: true,
    featuredOrder: 44,
    blurb: "No-BS tier list of power tool brands, from Milwaukee to the bargain bin.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "validator-ai-business-validation-agent",
    surface: "app",
    title: "Validator AI",
    upstream: "https://validator-ai-business-validation-agent.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/validator-ai-business-validation-agent.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Local workbook that stress-tests a business idea before you build it.",
  },
  {
    slug: "validatorai",
    surface: "app",
    title: "ValidatorAI",
    upstream: "https://validatorai.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/validatorai.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Stage-by-stage startup validation notebook. Notes stay in this browser.",
  },
  {
    slug: "velocraft-configurator",
    surface: "app",
    title: "Velocraft Configurator",
    upstream: "https://velocraft-configurator.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/velocraft-configurator.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Build a Velocraft bike with fitment rules, bundle pricing, and a local matcher.",
  },
  {
    slug: "zero-s-website",
    surface: "app",
    title: "Zero",
    upstream: "https://zero-s-website.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/zero-s-website.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Estimate monthly pipeline from traffic, then save a note on this device.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "video-analyzer",
    surface: "app",
    title: "Video Analyzer",
    upstream: "https://video-analyzer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/video-analyzer.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Drop a video for timecoded scene cuts, loudness, and motion.",
  },
  {
    slug: "video-object-tracker",
    surface: "app",
    title: "Video Object Tracker",
    upstream: "https://video-object-tracker.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/video-object-tracker.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Pin a point on a video and follow it in your browser. Nothing is uploaded.",
  },
  {
    slug: "virtual-photonic-ic-bench-test",
    surface: "app",
    title: "Virtual Photonic IC Bench",
    upstream: "https://virtual-photonic-ic-bench-test.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/virtual-photonic-ic-bench-test.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Sketch a photonic circuit and watch optical power reach the detectors.",
  },
  {
    slug: "website-scraper",
    surface: "app",
    title: "Website Scraper",
    upstream: "https://website-scraper.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/website-scraper.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Paste HTML and extract titles, links, and JSON-LD in the browser.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "command-pipeline-structure",
    appId: "app_command_pipeline_structure_zqwa0",
    surface: "app",
    title: "Command Pipeline",
    upstream: "https://command-pipeline-structure-zqwa0.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/command-pipeline-structure.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Dry-run commands through parse, route, execute, and finalize.",
  },
  {
    slug: "organize-prompts-agents",
    appId: "app_organize_prompts_agents_iznp8",
    surface: "app",
    title: "Prompt Desk",
    upstream: "https://organize-prompts-agents-iznp8.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/organize-prompts-agents.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Compose 021 agent commands and keep a local prompt shelf.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "4d-game-state-system",
    appId: "app_4d_game_state_system_mtxeh",
    surface: "app",
    title: "4D Game State",
    upstream: "https://4d-game-state-system-mtxeh.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/4d-game-state-system.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Paint Life, scrub through time, and fork alternate timelines.",
  },
  {
    slug: "benchmark-card-storage",
    appId: "app_benchmark_card_storage_vytwn",
    surface: "app",
    title: "Card Storage Bench",
    upstream: "https://benchmark-card-storage-vytwn.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/benchmark-card-storage.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Compare one JSON file vs one file per card for a 36,709-card corpus.",
  },
  {
    slug: "geoquery-nearby-locations",
    appId: "app_geoquery_nearby_locations_0z8vv",
    surface: "app",
    title: "Geoquery",
    upstream: "https://geoquery-nearby-locations-0z8vv.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/geoquery-nearby-locations.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Find US towns and counties within a radius of any place.",
  },
  {
    slug: "mrbeast-salesforce-setup",
    appId: "app_mrbeast_salesforce_setup_fiecl",
    surface: "app",
    title: "MrBeast Salesforce Setup",
    upstream: "https://mrbeast-salesforce-setup-fiecl.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/mrbeast-salesforce-setup.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Checklist for the MrBeast Salesforce org: auth, JWT, SOQL, and Id checks.",
  },
  {
    slug: "research-paper-review",
    appId: "app_research_paper_review_dolxg",
    surface: "app",
    title: "Paper Desk",
    upstream: "https://research-paper-review-dolxg.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/research-paper-review.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Score a paper, flag weak claims, and export a peer review.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "dq7-3d-world-map",
    appId: "app_dq7_3d_world_map_l8c2k",
    surface: "app",
    title: "Dragon Quest VII World Map",
    upstream: "https://dq7-3d-world-map-l8c2k.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/dq7-3d-world-map.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Orbitable 3D chart of the Dragon Quest VII islands.",
  },
  {
    slug: "socialmaxxing-charisma-guide",
    appId: "app_socialmaxxing_charisma_guide_rtiuc",
    surface: "app",
    title: "Socialmaxxing",
    upstream: "https://socialmaxxing-charisma-guide-rtiuc.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/socialmaxxing-charisma-guide.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Daily charisma drills: listen, calibrate a room, and tighten a 60-second story.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "friday-13th-counter",
    appId: "app_friday_13th_counter_dvrnn",
    surface: "app",
    title: "Friday the 13th Counter",
    upstream: "https://friday-13th-counter-dvrnn.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/friday-13th-counter.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Countdown to the next Friday the 13th, with a 200-year histogram.",
  },
  {
    slug: "notion-api-integration",
    appId: "app_notion_api_integration_gl8qw",
    surface: "app",
    title: "Notion Payload Lab",
    upstream: "https://notion-api-integration-gl8qw.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/notion-api-integration.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Turn markdown and filters into Notion API JSON, locally.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "obsidian-claude-workflow",
    appId: "app_obsidian_claude_workflow_glzwo",
    surface: "app",
    title: "Vault desk",
    upstream: "https://obsidian-claude-workflow-glzwo.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/obsidian-claude-workflow.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Five-folder Obsidian desk that turns your notes into Claude prompts.",
  },
  {
    slug: "pet-papercraft-wizard",
    appId: "app_pet_papercraft_wizard_xx0ok",
    surface: "app",
    title: "Pet Papercraft Wizard",
    upstream: "https://pet-papercraft-wizard-xx0ok.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/pet-papercraft-wizard.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Print a foldable paper pet: pick a species, color it, and cut the net.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "find-backlink-sites",
    appId: "app_find_backlink_sites_mwo8v",
    surface: "app",
    title: "Backlink Desk",
    upstream: "https://find-backlink-sites-mwo8v.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/find-backlink-sites.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Checklist of 200+ free backlink directories, filtered by DR and dofollow.",
  },
  {
    slug: "investigate-camofox-browser",
    appId: "app_investigate_camofox_browser_0eb8e",
    surface: "app",
    title: "Investigate Camofox",
    upstream: "https://investigate-camofox-browser-0eb8e.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/investigate-camofox-browser.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Live fingerprint read of the signals Camoufox spoofs, plus its REST map.",
  },
  {
    slug: "setup-karpathy-skills",
    appId: "app_setup_karpathy_skills_kivmn",
    surface: "app",
    title: "Karpathy setup",
    upstream: "https://setup-karpathy-skills-kivmn.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/setup-karpathy-skills.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Lint a task and a diff against Karpathy's four LLM coding rules.",
  },
  {
    slug: "sparkboard-idea-kanban",
    surface: "app",
    title: "Sparkboard",
    upstream: "https://sparkboard-idea-kanban.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/sparkboard-idea-kanban.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "A local idea kanban: capture a spark and move it until it ships.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "paperclip-api-skill",
    appId: "app_paperclip_api_skill_ykpls",
    surface: "app",
    title: "Paperclip Desk",
    upstream: "https://paperclip-api-skill-ykpls.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/paperclip-api-skill.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Local sandbox for the Paperclip task loop, with the curl for each step.",
  },
  {
    slug: "plan-implementation",
    appId: "app_plan_implementation_kksxh",
    surface: "app",
    title: "Plan desk",
    upstream: "https://plan-implementation-kksxh.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/plan-implementation.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Turn a written plan into 021 jobs and walk them to ship.",
  },
  {
    slug: "vercel-ios-app-spec",
    appId: "app_vercel_ios_app_spec_ukozl",
    surface: "app",
    title: "Vercel Mobile",
    upstream: "https://vercel-ios-app-spec-ukozl.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vercel-ios-app-spec.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "On-call board for the Vercel iOS spec: deploys, logs, rollback.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "create-business-plan",
    appId: "app_create_business_plan_jlqkp",
    surface: "app",
    title: "One-pager",
    upstream: "https://create-business-plan-jlqkp.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/create-business-plan.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Write a one-page business plan and watch margin and runway update as you type.",
  },
  {
    slug: "investigate-graphify",
    appId: "app_investigate_graphify_60jka",
    surface: "app",
    title: "Graphify",
    upstream: "https://investigate-graphify-60jka.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/investigate-graphify.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Turn notes into a walkable knowledge graph.",
  },
  {
    slug: "openclaw-wrapper-business",
    appId: "app_openclaw_wrapper_business_jou4p",
    surface: "app",
    title: "Claw Bench",
    upstream: "https://openclaw-wrapper-business-jou4p.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/openclaw-wrapper-business.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Score which OpenClaw wrapper to sell, then price the retainer.",
  },
  {
    slug: "research-rust-adb-projects",
    appId: "app_research_rust_adb_projects_l8cqf",
    surface: "app",
    title: "Rust ADB Field Guide",
    upstream: "https://research-rust-adb-projects-l8cqf.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/research-rust-adb-projects.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Search 41 Rust ADB libraries, CLIs, and GUIs from a 2026 survey.",
  },
  {
    slug: "startup-subreddit-list",
    appId: "app_startup_subreddit_list_pcs4q",
    surface: "app",
    title: "Startup Subreddit List",
    upstream: "https://startup-subreddit-list-pcs4q.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/startup-subreddit-list.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Launch board of startup subreddits, with promo hints and a local shortlist.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "backlink-checker-product",
    appId: "app_backlink_checker_product_2fc9u",
    surface: "app",
    title: "Backlink Checker",
    upstream: "https://backlink-checker-product-2fc9u.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/backlink-checker-product.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "See if a page links to your site, with anchor text and follow or nofollow.",
  },
  {
    slug: "password-rotation-service",
    appId: "app_password_rotation_service_ljodm",
    surface: "app",
    title: "Rotate",
    upstream: "https://password-rotation-service-ljodm.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/password-rotation-service.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Track when each password is due, and generate a strong one locally.",
  },
  {
    slug: "student-visa-research",
    appId: "app_student_visa_research_iw2ay",
    surface: "app",
    title: "Student Visa Dashboard",
    upstream: "https://student-visa-research-iw2ay.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/student-visa-research.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Year-by-year F-1 visas, SEVIS stock, refusal rates, and policy events.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "sports-clips",
    surface: "app",
    title: "Sports Clips",
    upstream: "https://sports-clips.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/sports-clips.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Daily viral sports clips from X. Filter, play, and save a shelf.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "blinklink",
    surface: "app",
    title: "Blinklink",
    upstream: "https://blinklink.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/blinklink.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Private short links, QR codes, and open counts that stay in this browser.",
  },
  {
    slug: "job-hunt-kanban",
    surface: "app",
    title: "Job Hunt Kanban",
    upstream: "https://job-hunt-kanban.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/job-hunt-kanban.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Kanban for a job search, saved in this browser.",
  },
  {
    slug: "memevault",
    surface: "app",
    title: "MemeVault",
    upstream: "https://memevault.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/memevault.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Caption a poster or your picture and keep the meme on this device.",
  },
  {
    slug: "trendwidgets",
    surface: "app",
    title: "Trendwidgets",
    upstream: "https://trendwidgets.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/trendwidgets.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "A local desk of weather, topic trends, clocks, notes, and countdowns.",
  },
  {
    slug: "vibelink-nextjs",
    surface: "app",
    title: "VibeLink",
    upstream: "https://vibelink-nextjs.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vibelink-nextjs.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Strip tracking parameters, name the link, and count clicks in this browser.",
  },
  {
    slug: "vibematch-next",
    surface: "app",
    title: "VibeMatch",
    upstream: "https://vibematch-next.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vibematch-next.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Mood matcher for a song, a film, and something to do.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "kanbanflow-nextjs",
    surface: "app",
    title: "Kanban Flow",
    upstream: "https://kanbanflow-nextjs.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/kanbanflow-nextjs.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Local kanban board: drag cards across columns. Stays in this browser.",
  },
  {
    slug: "linkvault-nextjs",
    surface: "app",
    title: "LinkVault",
    upstream: "https://linkvault-nextjs.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/linkvault-nextjs.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Private link drawer with tags, open counts, and QR codes. Stays in this browser.",
  },
  {
    slug: "shrinkr-url-shortener",
    surface: "app",
    title: "Shrinkr",
    upstream: "https://shrinkr-url-shortener.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/shrinkr-url-shortener.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Self-contained short links with a leave-site stop and on-device click counts.",
  },
  {
    slug: "sparkmind-ai",
    surface: "app",
    title: "SparkMind",
    upstream: "https://sparkmind-ai.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/sparkmind-ai.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Private brainstorm canvas: plant a seed idea and grow a map of sparks.",
  },
  {
    slug: "widgetforge-dashboard",
    surface: "app",
    title: "Widgetforge",
    upstream: "https://widgetforge-dashboard.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/widgetforge-dashboard.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "A local widget bench: clock, weather, todos, notes, and links.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "sparkboard",
    surface: "app",
    title: "SparkBoard",
    upstream: "https://sparkboard.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/sparkboard.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Catch ideas, fan them by heat, and share a spark with a link.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "echolink-bio",
    surface: "app",
    title: "EchoLink",
    upstream: "https://echolink-bio.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/echolink-bio.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Build a link-in-bio page in the browser and share it in the URL.",
  },
  {
    slug: "forgresume",
    surface: "app",
    title: "ForgeResume",
    upstream: "https://forgresume.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/forgresume.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Edit a resume, preview it, and print it. It stays in this browser.",
  },
  {
    slug: "shortify-url",
    surface: "app",
    title: "Shortify",
    upstream: "https://shortify-url.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/shortify-url.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Strip tracking junk from URLs. Private notebook, safe preview links.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "memeforge-ai",
    surface: "app",
    title: "MemeForge",
    upstream: "https://memeforge-ai.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/memeforge-ai.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Local meme maker: layouts, pattern captions, and a PNG download.",
  },
  {
    slug: "threadcraft",
    surface: "app",
    title: "Threadcraft",
    upstream: "https://threadcraft.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/threadcraft.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Split a long draft into posts that fit, then copy or share the thread.",
  },
  {
    slug: "vibelink-demo-2026",
    surface: "app",
    title: "VibeLink",
    upstream: "https://vibelink-demo-2026.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vibelink-demo-2026.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Local short links with QR codes, open counts, and a pause before you leave.",
  },
  {
    slug: "viralmemeforge",
    surface: "app",
    title: "Viral Meme Forge",
    upstream: "https://viralmemeforge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/viralmemeforge.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Caption a plate or your photo, then download a PNG. Stays in the browser.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "cliply-url-shortener",
    surface: "app",
    title: "Cliply",
    upstream: "https://cliply-url-shortener.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/cliply-url-shortener.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Clip long links into shareable leave-check URLs, stored in this browser.",
  },
  {
    slug: "dashify",
    surface: "app",
    title: "Dashify",
    upstream: "https://dashify.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/dashify.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Drag-and-drop personal desk: clock, notes, to-dos, links, and countdowns.",
  },
  {
    slug: "dev-clash",
    surface: "app",
    title: "DevClash",
    upstream: "https://dev-clash.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/dev-clash.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "GitHub profile battles. You pick the winner. Elo stays in the browser.",
  },
  {
    slug: "linkforge-nextjs",
    surface: "app",
    title: "Linkforge",
    upstream: "https://linkforge-nextjs.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/linkforge-nextjs.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Local campaign-link forge with UTM tags, QR codes, and open counts.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "nextjs-architecture-research",
    appId: "app_nextjs_architecture_research_nmrou",
    surface: "app",
    title: "Layout Bench",
    upstream: "https://nextjs-architecture-research-nmrou.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/nextjs-architecture-research.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Pick a Next.js folder layout from size, data, and shells.",
  },
  {
    slug: "promptvault",
    surface: "app",
    title: "Prompt Vault",
    upstream: "https://promptvault.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/promptvault.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Private prompt catalog: fill {{blanks}}, copy, and keep cards in this browser.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "linkhub-nextjs",
    surface: "app",
    title: "Linkhub",
    upstream: "https://linkhub-nextjs.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/linkhub-nextjs.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Private short links and a 14-day click board that stay in this browser.",
  },
  {
    slug: "meme-forge",
    surface: "app",
    title: "Meme Forge",
    upstream: "https://meme-forge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/meme-forge.webp",
    shippedAt: "2026-10-03",
    status: "live",
    featured: true,
    featuredOrder: 10,
    blurb: "Caption a picture and download the meme. Nothing leaves your browser.",
  },
  {
    slug: "quicklink-saas",
    surface: "app",
    title: "Quicklink",
    upstream: "https://quicklink-saas.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/quicklink-saas.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Personal short-link pad with local click counts. No account, no public redirector.",
  },
  {
    slug: "vibelink-bio",
    surface: "app",
    title: "VibeLink Bio",
    upstream: "https://vibelink-bio.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vibelink-bio.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "A link-in-bio page you design, preview, and share from the URL.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "linkly-url-shortener",
    surface: "app",
    title: "Linkly",
    upstream: "https://linkly-url-shortener.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/linkly-url-shortener.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Strip tracking parameters and share a link that stops to show the host.",
  },
  {
    slug: "vibethread",
    surface: "app",
    title: "VibeThread",
    upstream: "https://vibethread.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vibethread.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Draft an X thread with a live 280 counter, templates, and a vibe score.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "inspo-vault",
    surface: "app",
    title: "Inspo Vault",
    upstream: "https://inspo-vault.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/inspo-vault.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "A private cabinet for notes, links, quotes, and sparks. Search stays on this device.",
  },
  {
    slug: "linkly-ai",
    surface: "app",
    title: "Linkly",
    upstream: "https://linkly-ai.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/linkly-ai.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Tidy tracking junk off URLs and share them with a leave-this-site check.",
  },
  {
    slug: "meme-forge-ai",
    surface: "app",
    title: "Meme Forge",
    upstream: "https://meme-forge-ai.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/meme-forge-ai.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Caption a template or your photo, then download a PNG. Stays on device.",
  },
  {
    slug: "neon-stack-tower",
    surface: "app",
    title: "Neon Stack Tower",
    upstream: "https://neon-stack-tower.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/neon-stack-tower.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Drop the sliding neon slab and stack a tower as high as you can.",
  },
  {
    slug: "promptforge-ai",
    surface: "app",
    title: "PromptForge",
    upstream: "https://promptforge-ai.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/promptforge-ai.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Local workshop for prompt templates, variables, and versions.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "forgelink",
    surface: "app",
    title: "ForgeLink",
    upstream: "https://forgelink.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/forgelink.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Local link stamps with QR codes and on-device open counts.",
  },
  {
    slug: "jobly-job-application-tracker",
    surface: "app",
    title: "Jobly",
    upstream: "https://jobly-job-application-tracker.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/jobly-job-application-tracker.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "A local kanban for job applications, follow-ups, and how the search is moving.",
  },
  {
    slug: "shortify-ai",
    surface: "app",
    title: "ShortifyAI",
    upstream: "https://shortify-ai.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/shortify-ai.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Local short links with click counts. Nothing is uploaded.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "foo2",
    surface: "app",
    title: "Articulate",
    upstream: "https://foo2.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/foo2.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Reverse dictionary for design and UI words.",
  },
  {
    slug: "shrinkify",
    surface: "app",
    title: "Shrinkify",
    upstream: "https://shrinkify.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/shrinkify.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Shrink bloated links by stripping trackers, wrappers, and junk.",
  },
  {
    slug: "vibecraft",
    surface: "app",
    title: "VibeCraft",
    upstream: "https://vibecraft.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vibecraft.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Craft a creative brief: palette, medium, constraint, and a picture.",
  },
  {
    slug: "ziplink-demo",
    surface: "app",
    title: "ZipLink",
    upstream: "https://ziplink-demo.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ziplink-demo.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Zip a long URL into a short code, a QR, and a private open count.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "pollspark",
    surface: "app",
    title: "Pollspark",
    upstream: "https://pollspark.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/pollspark.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Create a poll and share it as a link. Votes fold back in from the URL.",
  },
  {
    slug: "resumeai",
    surface: "app",
    title: "ResumeAI",
    upstream: "https://resumeai.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/resumeai.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "On-device resume score: gaps, verbs, numbers, and lines to rewrite.",
  },
  {
    slug: "tempforge-ai-sandbox",
    surface: "app",
    title: "TempForge",
    upstream: "https://tempforge-ai-sandbox.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/tempforge-ai-sandbox.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Ephemeral browser sandboxes that self-destruct on a timer.",
  },
  {
    slug: "threadweaver",
    surface: "app",
    title: "ThreadWeaver",
    upstream: "https://threadweaver.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/threadweaver.webp",
    shippedAt: "2026-10-03",
    status: "live",
    featured: true,
    featuredOrder: 27,
    blurb: "Paste a thread, get a blog post. Markdown stays in the browser.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "skill-token-bench",
    surface: "app",
    title: "Skill Token Bench",
    upstream: "https://skill-token-bench.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/skill-token-bench.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Compare baseline vs treatment tokens and list-price cost. Local only.",
  },
  {
    slug: "webforge-api",
    surface: "app",
    title: "Webforge",
    upstream: "https://webforge-api.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/webforge-api.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Turn captured calls into endpoints, OpenAPI, and agent tools.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "ai-app-forge",
    surface: "app",
    title: "AI App Forge",
    upstream: "https://ai-app-forge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ai-app-forge.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Describe a small app and download a working single-file prototype.",
  },
  {
    slug: "echolink-url-shortener",
    surface: "app",
    title: "EchoLink",
    upstream: "https://echolink-url-shortener.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/echolink-url-shortener.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Clean a link, keep a short code in this browser, and open it through a leave-site check.",
  },
  {
    slug: "snapdash-dashboard-builder",
    surface: "app",
    title: "Snapdash",
    upstream: "https://snapdash-dashboard-builder.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/snapdash-dashboard-builder.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Snap together a live personal dashboard of clocks, timers, and notes.",
  },
  {
    slug: "spark-link-url-shortener",
    surface: "app",
    title: "SparkLink",
    upstream: "https://spark-link-url-shortener.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/spark-link-url-shortener.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Shorter links with a host check, a QR code, and on-device open counts.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "linkify-shortener",
    surface: "app",
    title: "Linkify",
    upstream: "https://linkify-shortener.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/linkify-shortener.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Short names for long links, with QR codes and click counts, kept in this browser.",
  },
  {
    slug: "shortly",
    surface: "app",
    title: "Shortly",
    upstream: "https://shortly.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/shortly.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Personal short links in the browser, with a leave-site check.",
  },
  {
    slug: "token-efficiency-benchmark-lidfur",
    surface: "app",
    title: "tokenbench",
    upstream: "https://token-efficiency-benchmark-lidfur.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/token-efficiency-benchmark-lidfur.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Paste tokenbench runs.jsonl and compare token cost to a baseline.",
  },
  // Least-recently-committed wave (2026-10-03). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "annotated-web",
    surface: "app",
    title: "Annotated",
    upstream: "https://annotated-web.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/annotated-web.webp",
    shippedAt: "2026-10-03",
    status: "live",
    featured: true,
    featuredOrder: 29,
    blurb: "Clip the web, add commentary, publish a page that always links back to the source.",
  },
  {
    slug: "link-pulse",
    surface: "app",
    title: "LinkPulse",
    upstream: "https://link-pulse.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/link-pulse.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Private short links with an exit check and on-device click stats.",
  },
  {
    slug: "optiroute",
    surface: "app",
    title: "OptiRoute",
    upstream: "https://optiroute.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/optiroute.webp",
    shippedAt: "2026-10-03",
    status: "live",
    featured: true,
    featuredOrder: 22,
    blurb: "Add stops, get the shortest round-trip route on a map. Runs in your browser.",
  },
  {
    slug: "snaplink",
    surface: "app",
    title: "SnapLink",
    upstream: "https://snaplink.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/snaplink.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Private named links with expiry and continue counts, stored in this browser.",
  },
  {
    slug: "sparkstack",
    surface: "app",
    title: "Sparkstack",
    upstream: "https://sparkstack.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/sparkstack.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Capture a fleeting idea and get a stack plus three next steps.",
  },
  {
    slug: "tinycloud",
    surface: "app",
    title: "TinyCloud",
    upstream: "https://tinycloud.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/tinycloud.webp",
    shippedAt: "2026-10-03",
    status: "live",
    blurb: "Fill in a small tool and share it as a link. No account, no deploy step.",
  },
  {
    slug: "toolscout",
    surface: "app",
    title: "ToolScout",
    upstream: "https://toolscout.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/toolscout.webp",
    shippedAt: "2026-10-03",
    status: "live",
    featured: true,
    featuredOrder: 45,
    blurb: "Describe a use case, get ranked software tool picks with pros, cons and pricing.",
  },
  // Least-recently-committed wave (2026-10-04). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "liminal",
    surface: "app",
    title: "Liminal",
    upstream: "https://liminal.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/liminal.webp",
    shippedAt: "2026-10-04",
    status: "live",
    featured: true,
    featuredOrder: 34,
    blurb: "Cinematic ambient radio for liminal spaces, synthesized live in your browser.",
  },
  {
    slug: "linkpulse",
    surface: "app",
    title: "LinkPulse",
    upstream: "https://linkpulse.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/linkpulse.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Shareable pulse links with on-device open counts and a leaving check.",
  },
  {
    slug: "shrinkly",
    surface: "app",
    title: "Shrinkly",
    upstream: "https://shrinkly.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/shrinkly.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Personal short codes with on-device open counts and a leave-page check.",
  },
  {
    slug: "synthdate",
    surface: "app",
    title: "SynthDate",
    upstream: "https://synthdate.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/synthdate.webp",
    shippedAt: "2026-10-04",
    status: "live",
    featured: true,
    featuredOrder: 32,
    blurb: "Tinder, but every match is AI: swipe, chat, go on dates, or get ghosted.",
  },
  {
    slug: "textcal",
    surface: "app",
    title: "TextCal",
    upstream: "https://textcal.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/textcal.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Snap a meal photo, log estimated macros, track your day against a calorie goal.",
  },
  // Least-recently-committed wave (2026-10-04). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "bg-removal-lab",
    surface: "app",
    title: "BG Removal Lab",
    upstream: "https://bg-removal-lab.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/bg-removal-lab.webp",
    shippedAt: "2026-10-04",
    status: "live",
    featured: true,
    featuredOrder: 21,
    blurb: "Real-time background removal lab: compare methods with per-frame timings for low-end phones",
  },
  {
    slug: "cardex",
    surface: "app",
    title: "Cardex",
    upstream: "https://cardex.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/cardex.webp",
    shippedAt: "2026-10-04",
    status: "live",
    featured: true,
    featuredOrder: 33,
    blurb: "Pokémon GO for cars: spot cars in the wild and collect them in your garage.",
  },
  {
    slug: "debateforge",
    surface: "app",
    title: "DebateForge",
    upstream: "https://debateforge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/debateforge.webp",
    shippedAt: "2026-10-04",
    status: "live",
    featured: true,
    featuredOrder: 30,
    blurb: "Duolingo for debate: lessons, fallacy library, AI sparring with scoring.",
  },
  {
    slug: "habitflow",
    surface: "app",
    title: "Habitflow",
    upstream: "https://habitflow.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/habitflow.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "A calm habit log with streaks and a year heatmap, saved in the browser.",
  },
  {
    slug: "linksnap",
    surface: "app",
    title: "LinkSnap",
    upstream: "https://linksnap.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/linksnap.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Snap a link into an expiring card with a QR, kept on this device.",
  },
  {
    slug: "resume-radar-ai",
    surface: "app",
    title: "Resume Radar",
    upstream: "https://resume-radar-ai.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/resume-radar-ai.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Private resume scorecard: ATS, sections, recruiter notes, and job match.",
  },
  {
    slug: "shortly-url-analytics",
    surface: "app",
    title: "Shortly",
    upstream: "https://shortly-url-analytics.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/shortly-url-analytics.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Personal short links with click analytics, stored only in this browser.",
  },
  {
    slug: "stackdraft",
    surface: "app",
    title: "Stackdraft",
    upstream: "https://stackdraft.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/stackdraft.webp",
    shippedAt: "2026-10-04",
    status: "live",
    featured: true,
    featuredOrder: 20,
    blurb: "Plain English in, editable architecture map out. Share links and SVG export.",
  },
  {
    slug: "url-shortener-analytics",
    surface: "app",
    title: "LinkPulse",
    upstream: "https://url-shortener-analytics.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/url-shortener-analytics.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Short links with on-device click analytics and a leave-page check.",
  },
  // Least-recently-committed wave (2026-10-04). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "mediawrapped",
    surface: "app",
    title: "MediaWrapped",
    upstream: "https://mediawrapped.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/mediawrapped.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Spotify Wrapped for movies, shows, books, podcasts and games, all local in your browser.",
  },
  {
    slug: "pulsemap",
    surface: "app",
    title: "PulseMap",
    upstream: "https://pulsemap.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/pulsemap.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Living visual maps of your product: pages, APIs, data, and animated pulses.",
  },
  {
    slug: "roomleft",
    surface: "app",
    title: "RoomLeft",
    upstream: "https://roomleft.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/roomleft.webp",
    shippedAt: "2026-10-04",
    status: "live",
    featured: true,
    featuredOrder: 23,
    blurb: "See what is actually left of your paycheck after the bills that already own it.",
  },
  {
    slug: "vibemarket",
    surface: "app",
    title: "Vibe Market",
    upstream: "https://vibemarket.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vibemarket.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Friend-group prediction markets with virtual points, odds, and a leaderboard.",
  },
  // Least-recently-committed wave (2026-10-04). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "blink-shortener",
    surface: "app",
    title: "Blink",
    upstream: "https://blink-shortener.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/blink-shortener.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Pack a link into a shareable address and preview where it goes.",
  },
  {
    slug: "civilingo",
    surface: "app",
    title: "Civilingo",
    upstream: "https://civilingo.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/civilingo.webp",
    shippedAt: "2026-10-04",
    status: "live",
    featured: true,
    featuredOrder: 31,
    blurb: "Duolingo for knowing your rights: short gamified civic and legal lessons.",
  },
  {
    slug: "pose-coach",
    surface: "app",
    title: "PoseCoach",
    upstream: "https://pose-coach.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/pose-coach.webp",
    shippedAt: "2026-10-04",
    status: "live",
    featured: true,
    featuredOrder: 46,
    blurb: "Pose library, demo photo feedback and a coach Q&A for looking natural on camera.",
  },
  {
    slug: "researchforge",
    surface: "app",
    title: "ResearchForge",
    upstream: "https://researchforge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/researchforge.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Turn a question into a plan, searches, a critique, and a Markdown brief.",
  },
  {
    slug: "resumelens",
    surface: "app",
    title: "ResumeLens",
    upstream: "https://resumelens.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/resumelens.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Score a resume in the browser — keywords, impact, and ATS. Nothing uploaded.",
  },
  {
    slug: "shelf",
    surface: "app",
    title: "Shelf",
    upstream: "https://shelf.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/shelf.webp",
    shippedAt: "2026-10-04",
    status: "live",
    featured: true,
    featuredOrder: 35,
    blurb: "A visual cupboard for the small apps regular people make with AI. No git.",
  },
  {
    slug: "snipforge",
    surface: "app",
    title: "Snipforge",
    upstream: "https://snipforge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/snipforge.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Named, expiring links with a QR code and a stop screen.",
  },
  // Hype District. Same-account workers.dev on digitalnative (Pages at cap),
  // so host is the SEO canonical. Local-only SPA: no auth client, no D1.
  {
    slug: "hype-district",
    surface: "app",
    title: "Hype District",
    upstream: "https://hype-district.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/hype-district.webp",
    shippedAt: "2026-10-04",
    status: "live",
    featured: true,
    featuredOrder: 4,
    blurb: "Interactive layered streetwear playground. Local only.",
  },
  // City Bookstore. Same-account workers.dev on digitalnative (Pages at cap),
  // so host is the SEO canonical. Client-side catalog only: no auth, no D1.
  {
    slug: "city-bookstore",
    surface: "app",
    title: "City Bookstore",
    upstream: "https://city-bookstore.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/city-bookstore.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Explore a lavender-lit 3D bookshop and keep a reading list in this browser.",
  },
  // Least-recently-committed wave (2026-10-04). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "link-spark",
    surface: "app",
    title: "LinkSpark",
    upstream: "https://link-spark.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/link-spark.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Local URL shortener with QR codes, expiration, and click notes.",
  },
  {
    slug: "nook",
    surface: "app",
    title: "Nook",
    upstream: "https://nook.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/nook.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "A shelf for the apps you made: browse builds by room, open your own nook.",
  },
  {
    slug: "sidequestly",
    surface: "app",
    title: "Sidequestly",
    upstream: "https://sidequestly.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/sidequestly.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Draw one life-changing side quest a day and keep the streak.",
  },
  {
    slug: "smart-resume-analyzer",
    surface: "app",
    title: "ResumAI",
    upstream: "https://smart-resume-analyzer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/smart-resume-analyzer.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Score a resume against a job description in the browser. Nothing is uploaded.",
  },
  {
    slug: "vtuber-content-planner",
    surface: "app",
    title: "VTuber Content Planner",
    upstream: "https://vtuber-content-planner.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vtuber-content-planner.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "In-character idea generator, scripts and content calendar for VTubers.",
  },
  // Least-recently-committed wave (2026-10-04). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "kith",
    surface: "app",
    title: "Kith",
    upstream: "https://kith.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/kith.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Private play-money prediction markets for your friend group.",
  },
  {
    slug: "linklytics",
    surface: "app",
    title: "Linklytics",
    upstream: "https://linklytics.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/linklytics.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Private short-link book with a preview before you leave.",
  },
  {
    slug: "resuai",
    surface: "app",
    title: "ResuAI",
    upstream: "https://resuai.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/resuai.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Score a resume in the browser: ATS, keywords, and job-description match.",
  },
  {
    slug: "sketchtype",
    surface: "app",
    title: "SketchType",
    upstream: "https://sketchtype.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/sketchtype.webp",
    shippedAt: "2026-10-04",
    status: "live",
    featured: true,
    featuredOrder: 19,
    blurb: "Doodle a letterform or describe a vibe; get ranked Google Fonts matches.",
  },
  {
    slug: "streakly-2026",
    surface: "app",
    title: "Streakly",
    upstream: "https://streakly-2026.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/streakly-2026.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Gamified habit tracker with streaks, XP, levels, and badges.",
  },
  {
    slug: "verseclash",
    surface: "app",
    title: "VerseClash",
    upstream: "https://verseclash.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/verseclash.webp",
    shippedAt: "2026-10-04",
    status: "live",
    featured: true,
    featuredOrder: 18,
    blurb: "Your LLM vs my LLM: a live rap battle arena with judges, votes and replay links.",
  },
  // Least-recently-committed wave (2026-10-04). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "repospark",
    surface: "app",
    title: "RepoSpark",
    upstream: "https://repospark.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/repospark.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Turn a public GitHub repo into a shareable explainer page.",
  },
  {
    slug: "resume-wise-ai",
    surface: "app",
    title: "Resume Wise",
    upstream: "https://resume-wise-ai.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/resume-wise-ai.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "On-device ATS scorecard, six-second recruiter read, and a share link.",
  },
  {
    slug: "spinboxd",
    surface: "app",
    title: "Spinboxd",
    upstream: "https://spinboxd.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/spinboxd.webp",
    shippedAt: "2026-10-04",
    status: "live",
    featured: true,
    featuredOrder: 17,
    blurb: "Letterboxd for albums: log full listens, rate, review, keep a music diary.",
  },
  {
    slug: "stitchloom",
    surface: "app",
    title: "Stitchloom",
    upstream: "https://stitchloom.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/stitchloom.webp",
    shippedAt: "2026-10-04",
    status: "live",
    featured: true,
    featuredOrder: 2,
    blurb: "Turn any photo into a DMC cross-stitch pattern with chart and floss list.",
  },
  {
    slug: "streakflow",
    surface: "app",
    title: "StreakFlow",
    upstream: "https://streakflow.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/streakflow.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "A local habit tracker with streaks, a month calendar, and JSON backup.",
  },
  {
    slug: "url-pulse",
    surface: "app",
    title: "URL Pulse",
    upstream: "https://url-pulse.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/url-pulse.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Short links, QR codes, and click notes that stay in this browser.",
  },
  {
    slug: "veto-rooms",
    surface: "app",
    title: "Veto",
    upstream: "https://veto-rooms.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/veto-rooms.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Group decision rooms: everyone votes, everyone gets a veto, survivors win.",
  },
  // Least-recently-committed wave (2026-10-04). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "needle-lab",
    surface: "app",
    title: "Needle Lab",
    upstream: "https://needle-lab.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/needle-lab.webp",
    shippedAt: "2026-10-04",
    status: "live",
    featured: true,
    featuredOrder: 47,
    blurb: "Score your X draft for hook, substance, distribution and bait risk, locally.",
  },
  {
    slug: "replyminer",
    surface: "app",
    title: "Replyminer",
    upstream: "https://replyminer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/replyminer.webp",
    shippedAt: "2026-10-04",
    status: "live",
    featured: true,
    featuredOrder: 28,
    blurb: "Paste social replies into ranked ideas with an MVP scope.",
  },
  {
    slug: "unbuilt",
    surface: "app",
    title: "Unbuilt",
    upstream: "https://unbuilt.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/unbuilt.webp",
    shippedAt: "2026-10-04",
    status: "live",
    featured: true,
    featuredOrder: 36,
    blurb: "A graveyard of unbuilt app ideas: dump yours, get a brief and a build prompt.",
  },
  // Least-recently-committed wave (2026-10-04). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "applyforge",
    surface: "app",
    title: "Applyforge",
    upstream: "https://applyforge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/applyforge.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Tailor a cover letter and bullets to a job posting. Stays in your browser.",
  },
  {
    slug: "gb-repo",
    surface: "app",
    title: "Skill shelf",
    upstream: "https://gb-repo.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/gb-repo.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Search and pin the Grok Bot skills snapshot.",
  },
  {
    slug: "macrosnap",
    surface: "app",
    title: "MacroSnap",
    upstream: "https://macrosnap.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/macrosnap.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Log a meal in plain language and track today's macros on this device.",
  },
  {
    slug: "midplot",
    surface: "app",
    title: "MidPlot",
    upstream: "https://midplot.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/midplot.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Non-spoiler recap of a movie up to the minute you paused.",
  },
  {
    slug: "repo-glow",
    surface: "app",
    title: "RepoGlow",
    upstream: "https://repo-glow.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/repo-glow.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Shareable product pages for any public GitHub repo.",
  },
  {
    slug: "shortlink-analytics",
    surface: "app",
    title: "Shortlink Analytics",
    upstream: "https://shortlink-analytics.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/shortlink-analytics.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Local short links with slugs, expiry, QR codes, and click charts.",
  },
  // Least-recently-committed wave (2026-10-04). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "bookstore-clone-analyses",
    surface: "app",
    title: "Shelf ledger",
    upstream: "https://bookstore-clone-analyses.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/bookstore-clone-analyses.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Compare sixteen bookstore storefronts: clubs, loyalty, and seller fees.",
  },
  {
    slug: "docs-github-clone-analysis",
    surface: "app",
    title: "Get started lens",
    upstream: "https://docs-github-clone-analysis.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/docs-github-clone-analysis.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Searchable teardown of GitHub Docs Get started: nav, journey, CTAs, pricing analog.",
  },
  {
    slug: "doordash-clone-analysis",
    surface: "app",
    title: "DoorDash Clone Analysis",
    upstream: "https://doordash-clone-analysis.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/doordash-clone-analysis.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Teardown of DoorDash's public site plus a DashPass break-even check.",
  },
  // Least-recently-committed wave (2026-10-04). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "llm-tiktoks",
    surface: "app",
    title: "LLM TikToks",
    upstream: "https://llm-tiktoks.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/llm-tiktoks.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Three vertical shorts that explain LLMs, tokens, and next-word prediction.",
  },
  {
    slug: "schema-watch",
    surface: "app",
    title: "SchemaWatch",
    upstream: "https://schema-watch.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/schema-watch.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Diff JSON Schemas or payloads and see what would break a frontend.",
  },
  {
    slug: "vinext-research",
    surface: "app",
    title: "Vinext Research",
    upstream: "https://vinext-research.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vinext-research.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Captured Next.js vs vinext force-dynamic benchmark, with a local run scorer.",
  },
  // Least-recently-committed wave (2026-10-04). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "shortpulse",
    surface: "app",
    title: "ShortPulse",
    upstream: "https://shortpulse.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/shortpulse.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Local short links, QR codes, and click stats that never leave your browser.",
  },
  {
    slug: "yt-ai-week-links",
    surface: "app",
    title: "AI this week",
    upstream: "https://yt-ai-week-links.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/yt-ai-week-links.webp",
    shippedAt: "2026-10-04",
    status: "live",
    blurb: "Corrected source links for the AI Honeycove short that posted none.",
  },
  // Loudframe. Same-account workers.dev on digitalnative (like fodmap), so
  // host is the SEO canonical. Client-side Remotion Player; no auth, no D1.
  // Origin protection is provider-native on the Worker.
  {
    slug: "loudframe",
    surface: "app",
    title: "Loudframe",
    upstream: "https://loudframe.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/loudframe.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Neo-brutalist Remotion sting. A vertical MAKE IT LOUD short, played in the site.",
  },
  // Loudframe HF. HyperFrames-rendered sting + Next.js static export, as a
  // Worker on digitalnative (Pages at cap). No auth, no D1.
  // Origin protection is provider-native on the Worker.
  {
    slug: "loudframe-hf",
    surface: "app",
    title: "Loudframe HF",
    upstream: "https://loudframe-hf.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/loudframe-hf.webp",
    shippedAt: "2026-10-16",
    status: "live",
    blurb: "Neo-brutalist HyperFrames sting. HTML-rendered MAKE IT LOUD short, played in the site.",
  },
  // Sweet Nothings. Overflow Pages on over9000-pool-000 (digitalnative Pages full).
  // Client-side only: no auth, no D1. Host is SEO canonical; apex path redirects.
  {
    slug: "sweetnothings",
    surface: "app",
    title: "Sweet Nothings",
    upstream: "https://sweetnothings.pages.dev",
    canonical: "host",
    screenshot: "/shots/sweetnothings.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Whisperable loving phrases — nine thousand sweet nothings.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "pinstream-videos",
    surface: "app",
    title: "Pinstream Videos",
    upstream: "https://pinstream-videos.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/pinstream-videos.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Pinterest-style video wall: click a clip to pin it and play it in place.",
  },
  {
    slug: "ux-research-video-previews",
    surface: "app",
    title: "Video Preview Lab",
    upstream: "https://ux-research-video-previews.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ux-research-video-previews.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Flip hover, scrub, and click preview rules on the same six clips.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "capital",
    surface: "app",
    title: "Capital",
    upstream: "https://capital.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/capital.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Private runway desk for cash, burn, raise gap, and allocation.",
  },
  {
    slug: "studio-os",
    surface: "app",
    title: "Studio OS",
    upstream: "https://studio-os.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/studio-os.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Route studio work to a lane, clear the founder inbox, and log the decision.",
  },
  {
    slug: "wayfinder",
    surface: "app",
    title: "Wayfinder",
    upstream: "https://wayfinder.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/wayfinder.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Settle one decision at a time on a map that stays in this browser.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "aliexpress-clone-analysis",
    surface: "app",
    title: "AliExpress teardown",
    upstream: "https://aliexpress-clone-analysis.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/aliexpress-clone-analysis.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Logged-out AliExpress teardown: coupon floors, the 9% card, and pages that 404.",
  },
  {
    slug: "bay-area-demographics",
    surface: "app",
    title: "Bay Area Demographics",
    upstream: "https://bay-area-demographics.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/bay-area-demographics.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Nine-county Bay Area population, income, housing, and poverty from ACS 2024.",
  },
  {
    slug: "keep-going-prompt-catalog",
    surface: "app",
    title: "Keep Going",
    upstream: "https://keep-going-prompt-catalog.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/keep-going-prompt-catalog.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "One nudge for a stalled agent, from the AI math sessions.",
  },
  {
    slug: "n8n-box",
    surface: "app",
    title: "n8n box",
    upstream: "https://n8n-box.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/n8n-box.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Strip secrets from an n8n workflow export before git.",
  },
  {
    slug: "sniply",
    surface: "app",
    title: "Sniply",
    upstream: "https://sniply.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/sniply.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Name a URL, share a safe link, and preview the site before you leave.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "url-spark",
    surface: "app",
    title: "URL Spark",
    upstream: "https://url-spark.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/url-spark.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Spark cards that show the real site before you leave.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "cool-link-shortener",
    surface: "app",
    title: "Shortly",
    upstream: "https://cool-link-shortener.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/cool-link-shortener.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Short nicknames for long URLs, with QR codes and a private click log.",
  },
  {
    slug: "nano-link",
    surface: "app",
    title: "NanoLink",
    upstream: "https://nano-link.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/nano-link.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Cut trackers off a URL and keep a private short link, QR, and click chart.",
  },
  {
    slug: "retro-finds",
    surface: "app",
    title: "Retro Finds",
    upstream: "https://retro-finds.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/retro-finds.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Pocket ledger for thrift finds: photos, prices, and what you walked past.",
  },
  {
    slug: "safe-git-purge",
    surface: "app",
    title: "Safe Git Purge",
    upstream: "https://safe-git-purge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/safe-git-purge.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Sort dead local git branches and copy safe delete commands.",
  },
  {
    slug: "vibe-resume-ai",
    surface: "app",
    title: "VibeResume AI",
    upstream: "https://vibe-resume-ai.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vibe-resume-ai.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "On-device resume score: ATS, impact, keywords, and vibe.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "maxx-qa-context",
    surface: "app",
    title: "MAXX QA",
    upstream: "https://maxx-qa-context.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/maxx-qa-context.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Searchable production smash board for the MAXX collage QA queue.",
  },
  {
    slug: "recallradar",
    surface: "app",
    title: "Recall Radar",
    upstream: "https://recallradar.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/recallradar.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Type a food and check it against active U.S. FDA recalls.",
  },
  {
    slug: "snappost",
    surface: "app",
    title: "SnapPose",
    upstream: "https://snappost.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/snappost.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "A pose library with steps, mistakes, and lighting tips.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "ff-snapshot",
    surface: "app",
    title: "FF Snapshot",
    upstream: "https://ff-snapshot.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ff-snapshot.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Crosswalk Sleeper, ESPN, and GSIS ids, including defense ESPN ids.",
  },
  {
    slug: "grok-cli-in-grok-bot",
    surface: "app",
    title: "Bridge desk",
    upstream: "https://grok-cli-in-grok-bot.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/grok-cli-in-grok-bot.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Compose a grok-bridge command and see the grok invocation and write policy.",
  },
  {
    slug: "unfold",
    surface: "app",
    title: "Unfold",
    upstream: "https://unfold.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/unfold.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Paper puzzle: flood-fill a sheet in one color before the folds run out.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "restaurant-battle",
    surface: "app",
    title: "Restaurant Battle",
    upstream: "https://restaurant-battle.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/restaurant-battle.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Head-to-head bracket that picks where you eat tonight.",
  },
  {
    slug: "resume-ai-coach",
    surface: "app",
    title: "Resume AI Coach",
    upstream: "https://resume-ai-coach.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/resume-ai-coach.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Private resume scorecard with a recruiter read, ATS check, and fixes.",
  },
  {
    slug: "streetpulse-fintwit",
    surface: "app",
    title: "StreetPulse",
    upstream: "https://streetpulse-fintwit.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/streetpulse-fintwit.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "FinTwit Fear & Greed gauge for retail chatter on X.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "hourcost",
    surface: "app",
    title: "Hourcost",
    upstream: "https://hourcost.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/hourcost.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Turn a price into hours of take-home work, plus a blunt verdict.",
  },
  {
    slug: "knuckle",
    surface: "app",
    title: "Knuckle",
    upstream: "https://knuckle.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/knuckle.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Cook-along recipes that wait for messy hands.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "flappyreps",
    surface: "app",
    title: "FlappyReps",
    upstream: "https://flappyreps.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/flappyreps.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Webcam Flappy Bird. Flap with your face, a push-up, or a tap.",
  },
  {
    slug: "stackpurge",
    surface: "app",
    title: "Stackpurge",
    upstream: "https://stackpurge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/stackpurge.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Shareable calculator for what in your SaaS stack a weekend could replace.",
  },
  {
    slug: "what-do-i-wear",
    surface: "app",
    title: "What Do I Wear?",
    upstream: "https://what-do-i-wear.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/what-do-i-wear.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Weather and the occasion, then an outfit from the clothes you already own.",
  },
  {
    slug: "wouldyou",
    surface: "app",
    title: "WouldYou",
    upstream: "https://wouldyou.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/wouldyou.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Ask if they’d use it: a shareable page, three votes, and a local waitlist.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "kitday",
    surface: "app",
    title: "Kitday",
    upstream: "https://kitday.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/kitday.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Daily outfit and meals from your closet, pantry, and the weather.",
  },
  {
    slug: "pipeup",
    surface: "app",
    title: "PIPEUP",
    upstream: "https://pipeup.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/pipeup.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Flappy Bird you play with push-ups. The gym log stays in this browser.",
  },
  {
    slug: "pulsekit",
    surface: "app",
    title: "Pulsekit",
    upstream: "https://pulsekit.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/pulsekit.webp",
    shippedAt: "2026-10-07",
    status: "live",
    featured: true,
    featuredOrder: 26,
    blurb: "Paste audience comments and get a ranked weekly brief. Local only.",
  },
  {
    slug: "snitch",
    surface: "app",
    title: "SNITCH",
    upstream: "https://snitch.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/snitch.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Miss the habit. We tell your friends.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "castid",
    surface: "app",
    title: "CastID",
    upstream: "https://castid.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/castid.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Snap a catch, name the species, and log the conditions that produced the bite.",
  },
  {
    slug: "linkedout",
    surface: "app",
    title: "LinkedOut",
    upstream: "https://linkedout.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/linkedout.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Satirical company dossiers with walk-away scores and recruiter translations.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "castlog",
    surface: "app",
    title: "CastLog",
    upstream: "https://castlog.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/castlog.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Scan a catch, name the fish, and log the weather and moon with it.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "allrecipes-id-json-format",
    surface: "app",
    title: "{id}.json format",
    upstream: "https://allrecipes-id-json-format.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/allrecipes-id-json-format.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Validate one Allrecipes-style recipe record and save {id}.json.",
  },
  {
    slug: "grokforge",
    surface: "app",
    title: "GrokForge",
    upstream: "https://grokforge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/grokforge.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Turn one sentence into a PRD, a name, and a starter file tree.",
  },
  {
    slug: "top-100-recipe-websites",
    surface: "app",
    title: "Top 100 recipe websites",
    upstream: "https://top-100-recipe-websites.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/top-100-recipe-websites.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Search August 2026 recipe-site ranks, filter by market, and shortlist locally.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "g-pl-data",
    surface: "app",
    title: "g.pl Index",
    upstream: "https://g-pl-data.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/g-pl-data.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Search 34,357 public g.pl listing titles by section.",
  },
  {
    slug: "housedigest-crawl-data",
    surface: "app",
    title: "House Digest Shelf",
    upstream: "https://housedigest-crawl-data.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/housedigest-crawl-data.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Search 61,878 House Digest headlines, then open the story.",
  },
  {
    slug: "omnifly",
    surface: "app",
    title: "OMNIFLY",
    upstream: "https://omnifly.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/omnifly.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "A browser RL gym: one fly connectome dropped into 23 arenas.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "deeppicks",
    surface: "app",
    title: "DeepPicks",
    upstream: "https://deeppicks.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/deeppicks.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Snake draft board: ADP, VBD, and roster need. Saved in this browser.",
  },
  {
    slug: "paper-pet-x-creators",
    surface: "app",
    title: "Paper Pet Creators",
    upstream: "https://paper-pet-x-creators.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/paper-pet-x-creators.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Search paper pet and animal papercraft creators on X.",
  },
  {
    slug: "parade-com-clone",
    surface: "app",
    title: "Parade Rack",
    upstream: "https://parade-com-clone.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/parade-com-clone.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Browse and search 9,772 public Parade.com headlines, then clip them locally.",
  },
  {
    slug: "woman-excite-index",
    appId: "app_woman_excite_crawl",
    surface: "app",
    title: "Woman Excite Index (ウーマンエキサイト索引)",
    upstream: "https://woman-excite-crawl.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/woman-excite-index.webp",
    shippedAt: "2026-10-07",
    status: "live",
    language: "JP",
    blurb:
      "A cozy index of Woman Excite stories, from comic essays to parenting and food, sorted by series and year.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "seo-referral-blog-research",
    surface: "app",
    title: "Proof Desk",
    upstream: "https://seo-referral-blog-research.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/seo-referral-blog-research.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Score a niche, brief a money page, and queue the pet-supply proof cluster.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "data-weathering",
    surface: "app",
    title: "Data Weathering",
    upstream: "https://data-weathering.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/data-weathering.webp",
    shippedAt: "2026-10-07",
    status: "live",
    featured: true,
    featuredOrder: 14,
    blurb: "Watch a photo decay through local JPEG re-encodes. Nothing is uploaded.",
  },
  {
    slug: "resume-ai-optimizer",
    surface: "app",
    title: "Resume AI Optimizer",
    upstream: "https://resume-ai-optimizer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/resume-ai-optimizer.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Score a resume in the browser: scorecard, rewrites, and a 6-second recruiter view.",
  },
  {
    slug: "snapmacro",
    surface: "app",
    title: "SnapMacro",
    upstream: "https://snapmacro.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/snapmacro.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Photograph a plate, name the foods, and log calories and macros.",
  },
  {
    slug: "spark-ideas",
    surface: "app",
    title: "Spark Ideas",
    upstream: "https://spark-ideas.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/spark-ideas.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Generate personalized project ideas and keep favorites in this browser.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "anthropic-kernel-path",
    surface: "app",
    title: "Anthropic Kernel Path",
    upstream: "https://anthropic-kernel-path.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/anthropic-kernel-path.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Study desk for the Anthropic kernel-engineer path, with a local cycle log.",
  },
  {
    slug: "spinlog",
    surface: "app",
    title: "Spinlog",
    upstream: "https://spinlog.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/spinlog.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "A diary for albums you listened to front to back.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "loopmaster-3d",
    surface: "app",
    title: "LoopMaster 3D",
    upstream: "https://loopmaster-3d.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/loopmaster-3d.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Watch a JavaScript for-loop run in twelve 3D views.",
  },
  {
    slug: "purify-software-reclaimed",
    surface: "app",
    title: "Purify",
    upstream: "https://purify-software-reclaimed.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/purify-software-reclaimed.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Find privacy-respecting replacements for the apps that track you.",
  },
  {
    slug: "skillnexus-os",
    surface: "app",
    title: "SkillNexus OS",
    upstream: "https://skillnexus-os.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/skillnexus-os.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Search a skill graph, see how competencies connect, and read practice notes.",
  },
  {
    slug: "stayguide",
    surface: "app",
    title: "StayGuide",
    upstream: "https://stayguide.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/stayguide.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Welcome book for a rental: Wi-Fi, house rules, and local tips in one link.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "git-visuals",
    surface: "app",
    title: "Git Visuals",
    upstream: "https://git-visuals.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/git-visuals.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Interactive commit-graph lessons for branches, conflicts, and rebase.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "entiquity-publishing-2",
    surface: "app",
    title: "Entiquity",
    upstream: "https://entiquity-publishing-2.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/entiquity-publishing-2.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Brutalist publishing house: catalog, browser bag, and a local editorial desk.",
  },
  {
    slug: "f2f-visualizer-3",
    surface: "app",
    title: "F2F Visualizer",
    upstream: "https://f2f-visualizer-3.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/f2f-visualizer-3.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Trace a sample storefront click back to the function and line that handles it.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "the-argentum-archive",
    surface: "app",
    title: "Argentum Archive",
    upstream: "https://the-argentum-archive.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/the-argentum-archive.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Field guide to New Phyrexia's five spheres, with local scripture and notes.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "kinesis-stick-figure-library",
    surface: "app",
    title: "Kinesis",
    upstream: "https://kinesis-stick-figure-library.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/kinesis-stick-figure-library.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Stick-figure pose library: search, drag joints, save, and download SVG.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "polyunwrap",
    surface: "app",
    title: "PolyUnwrap",
    upstream: "https://polyunwrap.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/polyunwrap.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Chart mesh seams with spanning-forest, greedy, hierarchical, and spectral cuts.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "culinara",
    surface: "app",
    title: "Culinara",
    upstream: "https://culinara.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/culinara.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Search cookable recipes and save a cookbook in this browser.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "pandakim",
    surface: "app",
    title: "PandaKim",
    upstream: "https://pandakim.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/pandakim.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "A little shrine for Kim: love notes, panda portraits, and a cuteness meter.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "video-to-storyboard-ai",
    surface: "app",
    title: "Video to Storyboard",
    upstream: "https://video-to-storyboard-ai.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/video-to-storyboard-ai.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Turn a screen recording into a scene-by-scene storyboard.",
  },
  // Least-recently-committed wave (2026-10-07). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical.
  {
    slug: "ai-3d-model-unfolder",
    surface: "app",
    title: "AI 3D Model Unfolder",
    upstream: "https://ai-3d-model-unfolder.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ai-3d-model-unfolder.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Unfold a low-poly OBJ or STL into a printable papercraft net.",
  },
  {
    slug: "copy-of-ai-3d-model-unfolder",
    surface: "app",
    title: "Model Unfolder",
    upstream: "https://copy-of-ai-3d-model-unfolder.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/copy-of-ai-3d-model-unfolder.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Unfold a low-poly OBJ or STL into printable 2D papercraft nets.",
  },
  {
    slug: "stable-fully-featured-video-to-screens-graph",
    surface: "app",
    title: "Video to Screens Graph",
    upstream: "https://stable-fully-featured-video-to-screens-graph.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/stable-fully-featured-video-to-screens-graph.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Turn a video into a flowchart of scenes, layouts, and voted cuts.",
  },
  {
    slug: "video-to-screens-graph",
    surface: "app",
    title: "Video to Screens Graph",
    upstream: "https://video-to-screens-graph.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/video-to-screens-graph.webp",
    shippedAt: "2026-10-07",
    status: "live",
    blurb: "Turn a video into a flowchart of its screens, in the browser.",
  },
  // CorinthianColumnBench. Static-assets Worker on digitalnative (Pages is at
  // 100/100), same-account workers.dev like mouthtype. No auth, no D1.
  {
    slug: "corinthiancolumnbench",
    surface: "app",
    title: "CorinthianColumnBench",
    upstream: "https://corinthiancolumnbench.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/corinthiancolumnbench.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "v0 models draw a Corinthian column from the same prompt, side by side.",
  },
  // Fix-24 ship pass (2026-10-10). Same-account workers.dev on digitalnative.
  // context-graphs and min-duration-v2s-graph stay as the short doors. The
  // worker hostnames are registered too, grandfathered past 40 characters.
  {
    slug: "budgetbite",
    surface: "app",
    title: "BudgetBite",
    upstream: "https://budgetbite.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/budgetbite.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Generate a week of budget-friendly meals for your diet, with a smart shopping list.",
  },
  {
    slug: "crm-for-driving-schools",
    surface: "app",
    title: "DriveCRM",
    upstream: "https://crm-for-driving-schools.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/crm-for-driving-schools.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A CRM for driving schools: students, lessons, and instructors in one place.",
  },
  {
    slug: "friendforge",
    surface: "app",
    title: "FriendForge",
    upstream: "https://friendforge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/friendforge.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "An agent gets to know you, then introduces you to compatible people nearby every weekend.",
  },
  {
    slug: "halfbuilt",
    surface: "app",
    title: "halfbuilt",
    upstream: "https://halfbuilt.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/halfbuilt.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A public board for the half-built side project your friends keep talking about.",
  },
  {
    slug: "context-graphs",
    surface: "app",
    title: "Context Graphs",
    upstream: "https://context-graphs-the-trillion-dollar-opportunity.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/context-graphs.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Why decision traces, not just data, will power the next generation of enterprise AI agents.",
  },
  {
    slug: "min-duration-v2s-graph",
    surface: "app",
    title: "Video to Screens Graph (min duration)",
    upstream: "https://min-duration-experiement-video-to-screens-graph.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/min-duration-v2s-graph.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Video to screens graph experiment with a minimum scene duration.",
  },
  {
    slug: "how-to-speak",
    surface: "app",
    title: "How to Speak",
    upstream: "https://how-to-speak.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/how-to-speak.webp",
    shippedAt: "2026-10-10",
    status: "live",
    featured: true,
    featuredOrder: 48,
    blurb: "Real-time exercise book for Patrick Winston's MIT lecture How to Speak.",
  },
  // GlowBoard. Static-assets Worker on digitalnative. No auth, no D1.
  {
    slug: "glowboard",
    surface: "app",
    title: "GlowBoard",
    upstream: "https://glowboard.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/glowboard.webp",
    shippedAt: "2026-10-10",
    status: "live",
    featured: true,
    featuredOrder: 16,
    blurb: "Identity-preserving beauty consult. One selfie, a stylist brief, and an after.",
  },
  // Host doors for workers already live on digitalnative (2026-10-10).
  {
    slug: "effect",
    surface: "app",
    title: "Effect kitchen",
    upstream: "https://effect.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/effect.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A live Effect v4 kitchen. Schema decode, a service layer, jittered retry, and a TTL cache — running in the browser.",
  },
  {
    slug: "how-many-side-bars",
    surface: "app",
    title: "How Many Side Bars",
    upstream: "https://how-many-side-bars.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/how-many-side-bars.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Compare sidebar patterns, from a normal nav rail to the point where the navigation becomes the product.",
  },
  {
    slug: "i-phone-word-cloud",
    surface: "app",
    title: "iPhone Word Cloud",
    upstream: "https://i-phone-word-cloud.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/i-phone-word-cloud.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Paste a note or upload a text file and turn repeated words into a picture. Processing stays in the browser.",
  },
  {
    slug: "idea-spark",
    surface: "app",
    title: "IdeaSpark",
    upstream: "https://idea-spark.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/idea-spark.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Generate unique, shippable project ideas tailored to your tech stack, difficulty, and audience.",
  },
  {
    slug: "knotly",
    surface: "app",
    title: "Knotly",
    upstream: "https://knotly.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/knotly.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "An all-in-one wedding planner: checklist, budget, vendors, website, guest list, and registry.",
  },
  {
    slug: "palmora",
    surface: "app",
    title: "Palmora",
    upstream: "https://palmora.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/palmora.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Upload a photo of your palm and get a reading of life, love, career, and destiny.",
  },
  {
    slug: "repo-shipper",
    surface: "app",
    title: "RepoShipper",
    upstream: "https://repo-shipper.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/repo-shipper.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Rank abandoned GitHub repos by how close they are to shippable.",
  },
  {
    slug: "squadpredict",
    surface: "app",
    title: "SquadPredict",
    upstream: "https://squadpredict.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/squadpredict.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Polymarket for your friend group.",
  },
  {
    slug: "stylevibe",
    surface: "app",
    title: "StyleVibe",
    upstream: "https://stylevibe.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/stylevibe.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Upload a photo, get styled in six iconic aesthetics, and shop the looks.",
  },
  {
    slug: "tokenguard",
    surface: "app",
    title: "TokenGuard",
    upstream: "https://tokenguard.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/tokenguard.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Track LLM provider usage against a monthly budget, saved in this browser.",
  },
  {
    slug: "ugc-image-ai-studio",
    surface: "app",
    title: "UGC Image AI Studio",
    upstream: "https://ugc-image-ai-studio.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ugc-image-ai-studio.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Select a vibe, customize your vision, and generate visuals in seconds.",
  },
  {
    slug: "url-analytics-shortener",
    surface: "app",
    title: "LinkPulse",
    upstream: "https://url-analytics-shortener.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/url-analytics-shortener.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Create short links, track every click, generate QR codes, and set expiration dates.",
  },
  {
    slug: "linked-out",
    surface: "app",
    title: "Linked-Out",
    upstream: "https://linked-out.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/linked-out.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "The other professional network. Anonymous stories about why people actually left.",
  },
  {
    slug: "webapi-forge",
    surface: "app",
    title: "WebAPI Forge",
    upstream: "https://webapi-forge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/webapi-forge.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Capture HTTP calls and forge an OpenAPI 3.1 spec plus client snippets, in the browser.",
  },
  {
    slug: "x-fintwit-gauge",
    surface: "app",
    title: "FinTwit Pulse",
    upstream: "https://x-fintwit-gauge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/x-fintwit-gauge.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A Fear & Greed meter for finance Twitter: retail sentiment, ticker gravity, and influencer tilt.",
  },
  {
    slug: "dashforge",
    surface: "app",
    title: "DashForge",
    upstream: "https://dashforge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/dashforge.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Build your perfect personal dashboard with draggable widgets. Clock, notes, tasks, and links stay in this browser.",
  },
  {
    slug: "video-collage",
    surface: "app",
    title: "Video Collage",
    upstream: "https://video-collage.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/video-collage.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Create, watch, and share video collages.",
  },
  {
    slug: "espn-ws-pickup",
    surface: "app",
    title: "ESPN WS Pickup",
    upstream: "https://espn-ws-pickup.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/espn-ws-pickup.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Watch an ESPN fantasy draft from the socket line protocol, or read a finished board from the league API.",
  },
  {
    slug: "jev-capture-solver",
    surface: "app",
    title: "Jev Capture",
    upstream: "https://jev-capture-solver.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/jev-capture-solver.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Score a captured agent trace with the Jev checkpoint policy: pass, retry, review, or stop. Keyless in the browser.",
  },
  {
    slug: "context-graphs-the-trillion-dollar-opportunity",
    surface: "app",
    title: "Context Graphs: AI's Trillion-Dollar Opportunity",
    upstream: "https://context-graphs-the-trillion-dollar-opportunity.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/context-graphs-the-trillion-dollar-opportunity.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Why decision traces, not just data, will power the next generation of enterprise AI agents.",
  },
  {
    slug: "min-duration-experiement-video-to-screens-graph",
    surface: "app",
    title: "Video to Screens Graph",
    upstream: "https://min-duration-experiement-video-to-screens-graph.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/min-duration-experiement-video-to-screens-graph.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Video to screens graph experiment with a minimum scene duration.",
  },
  {
    slug: "isocity-godot",
    surface: "app",
    title: "IsoCity Godot",
    upstream: "https://isocity-godot.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/isocity-godot.webp",
    shippedAt: "2026-10-10",
    status: "live",
    featured: true,
    featuredOrder: 5,
    blurb: "IsoCity Builder remade in Godot 4. Web export, same tiles and build loop.",
  },
  {
    slug: "pokefit",
    surface: "app",
    title: "PokeFit",
    upstream: "https://pokefit.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/pokefit.webp",
    shippedAt: "2026-10-10",
    status: "live",
    featured: true,
    featuredOrder: 8,
    blurb: "Gamified weight loss. Log weight, catch FitMons, battle friends, and grow a Pokedex.",
  },
  {
    slug: "buyers-radar",
    surface: "app",
    title: "Buyer's Radar",
    upstream: "https://buyers-radar.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/buyers-radar.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Ownership reality check before you buy. On-device checklist plus live Reddit, X, and forum searches.",
  },
  {
    slug: "text2voice",
    surface: "app",
    title: "Text2Voice",
    upstream: "https://text2voice.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/text2voice.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Play the same lines through historical and current text-to-speech systems. A catalog, a bench, and the methods underneath.",
  },
  // Never-live ship pass (2026-10-10). Same-account workers.dev on
  // digitalnative (Pages at cap), so host is the SEO canonical. Placeholder
  // shots until shots:capture runs.
  {
    slug: "agent-ab",
    surface: "app",
    title: "Agent Ab",
    upstream: "https://agent-ab.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/agent-ab.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A/B Testing Platform for AI Agents & Prompts",
  },
  {
    slug: "afterglow-studio",
    surface: "app",
    title: "Afterglow Studio",
    upstream: "https://afterglow-studio.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/afterglow-studio.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Instant film studio in the browser.",
  },
  {
    slug: "coolideaforge",
    surface: "app",
    title: "CoolIdeaForge",
    upstream: "https://coolideaforge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/coolideaforge.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Turn cool ideas from Twitter/X into full Product Requirements Documents in seconds.",
  },
  {
    slug: "affiliateeverything-architect",
    surface: "app",
    title: "AffiliateEverything Architect",
    upstream: "https://affiliateeverything-architect.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/affiliateeverything-architect.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Blueprint a product requirement document for AffiliateEverything. Fill in mission, audience, and features,…",
  },
  {
    slug: "afrilex-gap-explorer",
    surface: "app",
    title: "AfriLex Gap Explorer",
    upstream: "https://afrilex-gap-explorer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/afrilex-gap-explorer.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "An interactive look at how an English concept shows up in Igbo, Yoruba, Swahili, Zulu, and Hausa: a native…",
  },
  {
    slug: "afterlife",
    surface: "app",
    title: "Afterlife",
    upstream: "https://afterlife.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/afterlife.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Second acts for unused domains.",
  },
  {
    slug: "3d-wobble-explorer",
    surface: "app",
    title: "3D Wobble Explorer",
    upstream: "https://3d-wobble-explorer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/3d-wobble-explorer.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Interactive three.js recreations of abstract geometric art. Drag to rotate.",
  },
  {
    slug: "aftername",
    surface: "app",
    title: "Aftername",
    upstream: "https://aftername.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/aftername.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Unused domains still have a product inside them.",
  },
  {
    slug: "dreamforge-ai-game-studio",
    surface: "app",
    title: "DreamForge-AI-Game-Studio",
    upstream: "https://dreamforge-ai-game-studio.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/dreamforge-ai-game-studio.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A web app to turn dreams into playable game prototypes. Inspired by dream hack studios.",
  },
  {
    slug: "africa-language-improvements",
    surface: "app",
    title: "Africa Language Improvements",
    upstream: "https://africa-language-improvements.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/africa-language-improvements.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Language is infrastructure. If we want African creators, students, entrepreneurs, and public servants to…",
  },
  {
    slug: "aggressr",
    surface: "app",
    title: "Aggressr",
    upstream: "https://aggressr.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/aggressr.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Pick a fight. Find your opposite. Shout it out.",
  },
  {
    slug: "ai-finance-tracker",
    surface: "app",
    title: "AI Finance Tracker",
    upstream: "https://ai-finance-tracker.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ai-finance-tracker.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "On-device personal finance tracker with budgeting, expense categorization, and spending insights. No account.",
  },
  {
    slug: "ai-person-creator",
    surface: "app",
    title: "AI Person Creator",
    upstream: "https://ai-person-creator.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ai-person-creator.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Adjust body, face, skin, hair, and distinguishing marks, then generate a portrait.",
  },
  {
    slug: "agentic-brainstorm",
    surface: "app",
    title: "Agentic Brainstorm",
    upstream: "https://agentic-brainstorm.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/agentic-brainstorm.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Agentic Brainstorm is a lightweight, real-time workspace where you talk through an idea with purpose-built…",
  },
  {
    slug: "ai-hiring-upgrade-widget",
    surface: "app",
    title: "Upgrade to Business",
    upstream: "https://ai-hiring-upgrade-widget.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ai-hiring-upgrade-widget.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Checkout widget for an AI hiring platform. Visitors pick Monthly or Yearly and see plan features next to a…",
  },
  {
    slug: "ai-niche-crm-extender",
    surface: "app",
    title: "Niche CRM Extender",
    upstream: "https://ai-niche-crm-extender.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ai-niche-crm-extender.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Capture LinkedIn, X, and email leads into one niche CRM book. No account. Parsing stays in the browser.",
  },
  {
    slug: "agiskill",
    surface: "app",
    title: "agiskill",
    upstream: "https://agiskill.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/agiskill.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "An AI-shaped skill path: type a topic, get a short curriculum and a tutor pane.",
  },
  {
    slug: "ai-3d-model-inspector",
    surface: "app",
    title: "AI 3D Model Inspector",
    upstream: "https://ai-3d-model-inspector.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ai-3d-model-inspector.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Load a GLB or GLTF file, orbit the mesh, and ask for a short design read of the current view.",
  },
  {
    slug: "ai-api-explorer",
    surface: "app",
    title: "AI API Explorer",
    upstream: "https://ai-api-explorer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ai-api-explorer.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A modern, AI-assisted workspace for turning “a pile of URLs” into an organized API map you can explore,…",
  },
  {
    slug: "agentfloor",
    surface: "app",
    title: "Agentfloor",
    upstream: "https://agentfloor.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/agentfloor.webp",
    shippedAt: "2026-10-10",
    status: "live",
    featured: true,
    featuredOrder: 49,
    blurb: "Gather-style virtual office where avatars show live coding-agent work and token efficiency.",
  },
  {
    slug: "ai-quote-video-generator",
    surface: "app",
    title: "AI Quote Video Generator",
    upstream: "https://ai-quote-video-generator.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ai-quote-video-generator.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "An in-browser editor that turns book quotes into a short video plan: idea, script, stills, blocking, a…",
  },
  {
    slug: "ai-realistic-character-creator",
    surface: "app",
    title: "AI Realistic Character Creator",
    upstream: "https://ai-realistic-character-creator.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ai-realistic-character-creator.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Define gender, ethnicity, age, hair, eyes, body, and style, then generate a close-up, a full-body shot,…",
  },
  {
    slug: "ai-product-roadmap-modeler",
    surface: "app",
    title: "AI Product Roadmap Modeler",
    upstream: "https://ai-product-roadmap-modeler.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ai-product-roadmap-modeler.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Upstream (Cloudflare Worker, digitalnative): https://ai-product-roadmap-modeler.digitalnative.workers.dev",
  },
  {
    slug: "ai-shark-tank-simulator",
    surface: "app",
    title: "AI Shark Tank Simulator",
    upstream: "https://ai-shark-tank-simulator.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ai-shark-tank-simulator.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Upstream (Cloudflare Worker, digitalnative): https://ai-shark-tank-simulator.digitalnative.workers.dev",
  },
  {
    slug: "ai-website-analyzer",
    surface: "app",
    title: "AI Website Analyzer",
    upstream: "https://ai-website-analyzer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ai-website-analyzer.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Enter a URL and get a short breakdown of strengths, weaknesses, and suggestions.",
  },
  {
    slug: "ai-wrapped",
    surface: "app",
    title: "Ai Wrapped",
    upstream: "https://ai-wrapped.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ai-wrapped.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Spotify Wrapped, but for your AI chat history.",
  },
  {
    slug: "alien-talk",
    surface: "app",
    title: "Alien Talk",
    upstream: "https://alien-talk.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/alien-talk.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Chat with Zorp, an AI alien from planet Zorgon. Fun conversational AI companion app built with Next.js.",
  },
  {
    slug: "airwatt-physics-sim",
    surface: "app",
    title: "AirWatt Physics Visualization",
    upstream: "https://airwatt-physics-sim.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/airwatt-physics-sim.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A 2D physics visualization of a robotic vacuum arm demonstrating AirWatt suction power. Drag the arm.…",
  },
  {
    slug: "ai-social-image-generator",
    surface: "app",
    title: "AI Social Image Generator",
    upstream: "https://ai-social-image-generator.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ai-social-image-generator.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A social-style feed that turns a text prompt into an image card. Enter a prompt and Generate adds a post…",
  },
  {
    slug: "airesume-builder",
    surface: "app",
    title: "Airesume Builder",
    upstream: "https://airesume-builder.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/airesume-builder.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "AI-Powered Resume Builder built with Next.js 15.",
  },
  {
    slug: "ambitious-notes",
    surface: "app",
    title: "Ambitious Notes",
    upstream: "https://ambitious-notes.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ambitious-notes.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A public graveyard for ideas rotting in your Notes app.",
  },
  {
    slug: "audience-echo",
    surface: "app",
    title: "Audience Echo",
    upstream: "https://audience-echo.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/audience-echo.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "AI Listener Agent for creators & founders",
  },
  {
    slug: "app-2",
    surface: "app",
    title: "RECON.OS - Galactic Terminal",
    upstream: "https://app-2.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/app-2.webp",
    shippedAt: "2026-10-10",
    status: "live",
  },
  {
    slug: "aura-journal",
    surface: "app",
    title: "Aura Journal",
    upstream: "https://aura-journal.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/aura-journal.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A beautiful, calm mood journaling app that keeps everything on your device.",
  },
  {
    slug: "assertivenow-workplace-communication-coach",
    surface: "app",
    title: "AssertiveNow: Workplace Communication Coach",
    upstream: "https://assertivenow-workplace-communication-coach.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/assertivenow-workplace-communication-coach.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Practice assertive replies when a manager is harsh, last-minute, dismissive, or taking credit. Pick a…",
  },
  {
    slug: "biteline",
    surface: "app",
    title: "Biteline",
    upstream: "https://biteline.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/biteline.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Text a plate. Get the macros back.",
  },
  {
    slug: "best-recursive-ai-task-decomposer",
    surface: "app",
    title: "Recursive AI Task Decomposer",
    upstream: "https://best-recursive-ai-task-decomposer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/best-recursive-ai-task-decomposer.webp",
    shippedAt: "2026-10-10",
    status: "live",
  },
  {
    slug: "berth-aftermarket",
    surface: "app",
    title: "Berth Aftermarket",
    upstream: "https://berth-aftermarket.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/berth-aftermarket.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "The aftermarket for indie software.",
  },
  {
    slug: "animated-cloud-infrastructure",
    surface: "app",
    title: "Animated Cloud Infrastructure",
    upstream: "https://animated-cloud-infrastructure.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/animated-cloud-infrastructure.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A visually animated landing page for a modern cloud computing and data-processing landscape: hero,…",
  },
  {
    slug: "bad-3d-center-of-mass-analyzer",
    surface: "app",
    title: "3D Center of Mass Analyzer",
    upstream: "https://bad-3d-center-of-mass-analyzer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/bad-3d-center-of-mass-analyzer.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Upload a .obj mesh to see its center of mass, the vertices furthest from that point, and an optional…",
  },
  {
    slug: "budget-plate",
    surface: "app",
    title: "Budget Plate",
    upstream: "https://budget-plate.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/budget-plate.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Smart weekly meal plans tailored to your budget.",
  },
  {
    slug: "braindump-organizer",
    surface: "app",
    title: "BrainDump Organizer – Messy thoughts → Clear action",
    upstream: "https://braindump-organizer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/braindump-organizer.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Paste any brain dump and instantly get a prioritized task list with time estimates. 100% private, client-side.",
  },
  {
    slug: "blockbuster-walk",
    surface: "app",
    title: "Blockbuster Walk",
    upstream: "https://blockbuster-walk.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/blockbuster-walk.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Walk a nostalgic video-store aisle, pick up DVD cases, and find the titles someone shelved in the wrong place.",
  },
  {
    slug: "bookremotion-ai",
    surface: "app",
    title: "BookRemotion AI",
    upstream: "https://bookremotion-ai.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/bookremotion-ai.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Turn a book passage into a short video plan: paste a scene, pick visual, voice, and music styles, and…",
  },
  {
    slug: "boomerbanters-the-golden-era-of-conversation",
    surface: "app",
    title: "BoomerBanters",
    upstream: "https://boomerbanters-the-golden-era-of-conversation.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/boomerbanters-the-golden-era-of-conversation.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A curated conversation-starter deck and history trivia page for the Baby Boomer generation. Large type, a…",
  },
  {
    slug: "bitforge-url-shortener",
    surface: "app",
    title: "BitForge: tiny links, forged fast",
    upstream: "https://bitforge-url-shortener.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/bitforge-url-shortener.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "BitForge turns long URLs into short links that redirect instantly, with click counts and a history that…",
  },
  {
    slug: "bitewatch",
    surface: "app",
    title: "Bitewatch",
    upstream: "https://bitewatch.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/bitewatch.webp",
    shippedAt: "2026-10-10",
    status: "live",
    featured: true,
    featuredOrder: 51,
    blurb: "Type a food, brand, or grocery list. See if the FDA has recalled it.",
  },
  {
    slug: "boomer-brain-workout",
    surface: "app",
    title: "Boomer Brain Workout",
    upstream: "https://boomer-brain-workout.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/boomer-brain-workout.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A daily cognitive exercise suite for the Baby Boomer generation: nostalgia trivia, phrase recall, and a…",
  },
  {
    slug: "book-store-idea-literary-alloy",
    surface: "app",
    title: "Literary Alloy",
    upstream: "https://book-store-idea-literary-alloy.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/book-store-idea-literary-alloy.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A curated bookstore with an artistic layout. Open a title to read its description and a short gallery note.",
  },
  {
    slug: "book-club-companion",
    surface: "app",
    title: "Book Club Companion",
    upstream: "https://book-club-companion.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/book-club-companion.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Enter a book title and get a summary, character notes, themes, and discussion questions for the next meeting.",
  },
  {
    slug: "case-hq",
    surface: "app",
    title: "Case Hq",
    upstream: "https://case-hq.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/case-hq.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A dark, premium reputation interface for builders. Not a résumé — a case-file system.",
  },
  {
    slug: "buymeabook",
    surface: "app",
    title: "Buymeabook",
    upstream: "https://buymeabook.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/buymeabook.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Gift books the way people gift coffee.",
  },
  {
    slug: "cardwise",
    surface: "app",
    title: "Cardwise",
    upstream: "https://cardwise.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/cardwise.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Know which credit card to swipe.",
  },
  {
    slug: "carenest",
    surface: "app",
    title: "Carenest",
    upstream: "https://carenest.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/carenest.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Shared care coordination for aging parents.",
  },
  {
    slug: "cheflingo",
    surface: "app",
    title: "Cheflingo",
    upstream: "https://cheflingo.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/cheflingo.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Duolingo, but for learning how to cook.",
  },
  {
    slug: "chat-with-docs",
    surface: "app",
    title: "Documentation Browser",
    upstream: "https://chat-with-docs.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/chat-with-docs.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Upstream (Cloudflare Worker, digitalnative): https://chat-with-docs.digitalnative.workers.dev",
  },
  {
    slug: "build-a-context-graph",
    surface: "app",
    title: "Build A Context Graph",
    upstream: "https://build-a-context-graph.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/build-a-context-graph.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Automatically synced with your [v0.app](https://v0.app) deployments",
  },
  {
    slug: "careercraft-ai",
    surface: "app",
    title: "CareerCraft AI - Smart Cover Letters",
    upstream: "https://careercraft-ai.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/careercraft-ai.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Paste a resume and a job description, then generate a cover letter. The letter stays in the page session.…",
  },
  {
    slug: "buildr",
    surface: "app",
    title: "Buildr",
    upstream: "https://buildr.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/buildr.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Swipe. Match. Ship. Tinder for finding people to build cool projects with.",
  },
  {
    slug: "citeagent-research",
    surface: "app",
    title: "CiteAgent — Personal Research Agent",
    upstream: "https://citeagent-research.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/citeagent-research.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "CiteAgent searches public sources and writes a cited Markdown research report. No account.",
  },
  {
    slug: "compensation-intelligence-agent",
    surface: "app",
    title: "CompIntel — Compensation Intelligence Agent",
    upstream: "https://compensation-intelligence-agent.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/compensation-intelligence-agent.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Build a compensation negotiation brief in the browser. Market band, replacement cost, and a one-pager.…",
  },
  {
    slug: "copy-of-p5-js-react-atelier-src-test",
    surface: "app",
    title: "P5.js React Atelier",
    upstream: "https://copy-of-p5-js-react-atelier-src-test.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/copy-of-p5-js-react-atelier-src-test.webp",
    shippedAt: "2026-10-10",
    status: "live",
  },
  {
    slug: "collabpad",
    surface: "app",
    title: "Collabpad",
    upstream: "https://collabpad.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/collabpad.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A personal link pad. Give a long URL a nickname, copy the real address, and keep a count of how often you…",
  },
  {
    slug: "copy-of-pin-teresting-gallery",
    surface: "app",
    title: "Pin-teresting Gallery",
    upstream: "https://copy-of-pin-teresting-gallery.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/copy-of-pin-teresting-gallery.webp",
    shippedAt: "2026-10-10",
    status: "live",
  },
  {
    slug: "codespaghetti",
    surface: "app",
    title: "CodeSpaghetti",
    upstream: "https://codespaghetti.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/codespaghetti.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Visualize JavaScript as a plate of spaghetti: functions and components are meatballs, calls are noodles.",
  },
  {
    slug: "club-ozz-fantasy-scoring-eval-platform",
    surface: "app",
    title: "Club Ozz Fantasy Scoring Eval Platform",
    upstream: "https://club-ozz-fantasy-scoring-eval-platform.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/club-ozz-fantasy-scoring-eval-platform.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Recalculate an ESPN fantasy football season with best-ball lineups and a second weekly result against the…",
  },
  {
    slug: "context-trace",
    surface: "app",
    title: "Context Trace // System of Agents",
    upstream: "https://context-trace.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/context-trace.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Upstream (Cloudflare Worker, digitalnative): https://context-trace.digitalnative.workers.dev",
  },
  {
    slug: "clipr-analytics",
    surface: "app",
    title: "Clipr Analytics",
    upstream: "https://clipr-analytics.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/clipr-analytics.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A beautiful, full-featured URL shortener inspired by popular Twitter/X side-project ideas.",
  },
  {
    slug: "devroast",
    surface: "app",
    title: "Devroast",
    upstream: "https://devroast.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/devroast.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Savage (but fun) GitHub profile roasts.",
  },
  {
    slug: "domain-architect-ddd-workbench",
    surface: "app",
    title: "Domain Architect — DDD Workbench",
    upstream: "https://domain-architect-ddd-workbench.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/domain-architect-ddd-workbench.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A browser workbench for Domain-Driven Design: bounded contexts, ubiquitous language, and a local model review.",
  },
  {
    slug: "ddd-explorer-insurance-domain",
    surface: "app",
    title: "DDD Explorer: Insurance Domain",
    upstream: "https://ddd-explorer-insurance-domain.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ddd-explorer-insurance-domain.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Interactive workshop for Domain-Driven Design using an insurance policy aggregate: bounded contexts,…",
  },
  {
    slug: "copyrightai-validator",
    surface: "app",
    title: "CopyrightAI Validator",
    upstream: "https://copyrightai-validator.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/copyrightai-validator.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A legal-tech validation tool that analyzes AI training scenarios against the U.S. Copyright Office's 2025…",
  },
  {
    slug: "dashvibe-nextjs",
    surface: "app",
    title: "Dashvibe Nextjs",
    upstream: "https://dashvibe-nextjs.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/dashvibe-nextjs.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A customizable draggable widgets dashboard built with Next.js, Tailwind, and dnd-kit. Personal…",
  },
  {
    slug: "cowomap",
    surface: "app",
    title: "Cowomap",
    upstream: "https://cowomap.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/cowomap.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Google Maps for coworking coffee shops.",
  },
  {
    slug: "dev-excuse-generator",
    surface: "app",
    title: "Dev Excuse Generator",
    upstream: "https://dev-excuse-generator.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/dev-excuse-generator.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A fun, shareable tool that generates professional, sarcastic, overly technical, tool-blaming, or…",
  },
  {
    slug: "dnd-backstory-builder",
    surface: "app",
    title: "Dnd Backstory Builder",
    upstream: "https://dnd-backstory-builder.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/dnd-backstory-builder.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "I’m building D&D Backstory Forge because I’ve watched the same scene play out in every campaign: the table…",
  },
  {
    slug: "domain-phoenix",
    surface: "app",
    title: "Domain Phoenix",
    upstream: "https://domain-phoenix.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/domain-phoenix.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Give dead domains a second life.",
  },
  {
    slug: "edge-weight-lab",
    surface: "app",
    title: "Edge Weight Lab",
    upstream: "https://edge-weight-lab.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/edge-weight-lab.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Interactive lab for mesh edge-weight functions used in UV unwrapping. Tune dihedral curves (linear,…",
  },
  {
    slug: "domainarchitect",
    surface: "app",
    title: "DomainArchitect - DDD Toolkit",
    upstream: "https://domainarchitect.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/domainarchitect.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "DomainArchitect exists because most software failures aren’t technical—they’re semantic. Teams ship…",
  },
  {
    slug: "domainforge-ai",
    surface: "app",
    title: "Domainforge Ai",
    upstream: "https://domainforge-ai.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/domainforge-ai.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Paste a domain, get three product ideas (audience, price, and why the name fits). The core pass runs in…",
  },
  {
    slug: "dynamic-simulation-engine",
    surface: "app",
    title: "Dynamic Simulation Engine",
    upstream: "https://dynamic-simulation-engine.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/dynamic-simulation-engine.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Rewrite a project idea through six lenses — children, security, ecology, a tiny budget, accessibility, and…",
  },
  {
    slug: "dupehaus",
    surface: "app",
    title: "Dupehaus",
    upstream: "https://dupehaus.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/dupehaus.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Saw it on a reel. Find it — or the cheaper twin.",
  },
  {
    slug: "dragon-quest-vii-world-map-visualization",
    surface: "app",
    title: "Dragon Quest VII World Map",
    upstream: "https://dragon-quest-vii-world-map-visualization.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/dragon-quest-vii-world-map-visualization.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "An unofficial 3D chart of the Dragon Quest VII archipelago. Restore islands from the sea and read their…",
  },
  {
    slug: "dq7-world-restoration",
    surface: "app",
    title: "DQ7 World Restoration Map",
    upstream: "https://dq7-world-restoration.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/dq7-world-restoration.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Restore the Dragon Quest VII archipelago, inspect each island, and track mini medals.",
  },
  {
    slug: "domain-vibe-ai",
    surface: "app",
    title: "Domain Vibe Ai",
    upstream: "https://domain-vibe-ai.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/domain-vibe-ai.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Turn unused domains into product ideas.",
  },
  {
    slug: "entiquity-publishing",
    surface: "app",
    title: "Entiquity Publishing",
    upstream: "https://entiquity-publishing.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/entiquity-publishing.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Entiquity is a small publishing house. Read the fall list, hold a desk copy, and ask the librarian. No…",
  },
  {
    slug: "embedding-to-video",
    surface: "app",
    title: "Embedding → Video · Learning is Compression",
    upstream: "https://embedding-to-video.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/embedding-to-video.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Turn a 16-number embedding into a moving picture in the browser. No account and no upload.",
  },
  {
    slug: "f2f-function-2-feature",
    surface: "app",
    title: "F2F - Function 2 Feature",
    upstream: "https://f2f-function-2-feature.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/f2f-function-2-feature.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Function to Feature maps source functions onto the UI behavior they implement.",
  },
  {
    slug: "eternal-pulse",
    surface: "app",
    title: "Eternal Pulse",
    upstream: "https://eternal-pulse.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/eternal-pulse.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "An interactive artifact: an eternal energy source you can strike, attune, and hear.",
  },
  {
    slug: "explore-the-void",
    surface: "app",
    title: "Explore the Void",
    upstream: "https://explore-the-void.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/explore-the-void.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "An ancient kernel pulsing in the dark. Touch the orb, tune the carrier, and listen.",
  },
  {
    slug: "entiquity-publishing-3",
    surface: "app",
    title: "Entiquity Publishing",
    upstream: "https://entiquity-publishing-3.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/entiquity-publishing-3.webp",
    shippedAt: "2026-10-10",
    status: "live",
  },
  {
    slug: "ephemera",
    surface: "app",
    title: "Ephemera",
    upstream: "https://ephemera.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ephemera.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Temporary interactive experiences that live for a moment, then vanish.",
  },
  {
    slug: "f2f-function-2-feature-visualizer",
    surface: "app",
    title: "F2F | Function 2 Feature Visualizer",
    upstream: "https://f2f-function-2-feature-visualizer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/f2f-function-2-feature-visualizer.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Map UI features to the functions and tables behind them. Load a recorded trace or analyze JavaScript and…",
  },
  {
    slug: "finpulse",
    surface: "app",
    title: "Finpulse",
    upstream: "https://finpulse.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/finpulse.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Minimal Next.js 16 landing page for a personal finance tracker. Dependencies for charts, CSV, and local…",
  },
  {
    slug: "flickplay",
    surface: "app",
    title: "Flickplay",
    upstream: "https://flickplay.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/flickplay.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "TikTok-style endless scroll of bite-sized browser mini-games.",
  },
  {
    slug: "fintrack",
    surface: "app",
    title: "Fintrack",
    upstream: "https://fintrack.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/fintrack.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Personal Finance Tracker — Beautiful expense & income tracker with charts, budgets, and insights.",
  },
  {
    slug: "floor100",
    surface: "app",
    title: "Floor100",
    upstream: "https://floor100.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/floor100.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Pick a topic. Take a side. First to 100.",
  },
  {
    slug: "fairshift",
    surface: "app",
    title: "Fairshift",
    upstream: "https://fairshift.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/fairshift.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Local-first team scheduler inspired by a simple manager complaint: building the week by hand rewards…",
  },
  {
    slug: "fintwit-pulse",
    surface: "app",
    title: "Fintwit Pulse",
    upstream: "https://fintwit-pulse.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/fintwit-pulse.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "The retail sentiment gauge for Financial Twitter.",
  },
  {
    slug: "fantasy-football-schedule-organizer",
    surface: "app",
    title: "Fantasy Football Schedule Organizer",
    upstream: "https://fantasy-football-schedule-organizer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/fantasy-football-schedule-organizer.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Fantasy leagues don’t fall apart because of bad trades—they fall apart because trust erodes.",
  },
  {
    slug: "ff-remotion",
    surface: "app",
    title: "Ff Remotion",
    upstream: "https://ff-remotion.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ff-remotion.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Deployed app: https://vejgfrgiu7v0.space.minimax.io",
  },
  {
    slug: "fintwit-gauge",
    surface: "app",
    title: "Fintwit Gauge",
    upstream: "https://fintwit-gauge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/fintwit-gauge.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Retail fear & greed meter for finance Twitter.",
  },
  {
    slug: "finora",
    surface: "app",
    title: "Finora",
    upstream: "https://finora.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/finora.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A beautiful, modern personal finance tracker built with Next.js. Track income & expenses, set budgets,…",
  },
  {
    slug: "fridgecast",
    surface: "app",
    title: "Fridgecast",
    upstream: "https://fridgecast.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/fridgecast.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Photograph your fridge. Get told what to cook tonight.",
  },
  {
    slug: "geolingo",
    surface: "app",
    title: "Geolingo",
    upstream: "https://geolingo.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/geolingo.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Duolingo, but for geography.",
  },
  {
    slug: "fullscreen-canvas-demo",
    surface: "app",
    title: "Fullscreen Canvas App",
    upstream: "https://fullscreen-canvas-demo.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/fullscreen-canvas-demo.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "The web has become the default runtime for interactive experiences: product walk-throughs, playful landing…",
  },
  {
    slug: "friend-market",
    surface: "app",
    title: "Friend Market",
    upstream: "https://friend-market.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/friend-market.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Polymarket for your friend group.",
  },
  {
    slug: "forgeprd",
    surface: "app",
    title: "Forgeprd",
    upstream: "https://forgeprd.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/forgeprd.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Turn any idea (or X post) into a complete Product Requirements Document in seconds.",
  },
  {
    slug: "geoquest-app",
    surface: "app",
    title: "Geoquest App",
    upstream: "https://geoquest-app.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/geoquest-app.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Duolingo-style geography learning app",
  },
  {
    slug: "fronttoback",
    surface: "app",
    title: "Fronttoback",
    upstream: "https://fronttoback.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/fronttoback.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Letterboxd for albums.",
  },
  {
    slug: "fugi-marketing-coach",
    surface: "app",
    title: "FUGI Marketing Coach",
    upstream: "https://fugi-marketing-coach.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/fugi-marketing-coach.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "FUGI Marketing Coach drafts Fear, Urgency, Greed, and Indifference sales lines from a product brief. No…",
  },
  {
    slug: "gemini-os-arwes",
    surface: "app",
    title: "Gemini OS // Arwes",
    upstream: "https://gemini-os-arwes.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/gemini-os-arwes.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A sci-fi terminal console. Onboard commands run in the browser. Optional model uplink. No sign-in.",
  },
  {
    slug: "grok-imagine-tool-kit",
    surface: "app",
    title: "Grok Imagine Toolkit",
    upstream: "https://grok-imagine-tool-kit.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/grok-imagine-tool-kit.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Search saved Imagine prompts, organize tags, download a library, and reuse prompt templates. Runs in this…",
  },
  {
    slug: "grandmaster-ai-chess",
    surface: "app",
    title: "Grandmaster AI Chess",
    upstream: "https://grandmaster-ai-chess.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/grandmaster-ai-chess.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Play chess against an on-device engine with legal moves, opening book, and position analysis.",
  },
  {
    slug: "gong-ocean-demo",
    surface: "app",
    title: "Gong Ocean Demo",
    upstream: "https://gong-ocean-demo.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/gong-ocean-demo.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Interactive Gong + Ocean demo UI.",
  },
  {
    slug: "github-docs-get-started",
    surface: "app",
    title: "Get started",
    upstream: "https://github-docs-get-started.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/github-docs-get-started.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Practice the GitHub Get started curriculum in the browser: journeys, 101 article notes, and a local Git…",
  },
  {
    slug: "guided-video-experience",
    surface: "app",
    title: "Guided Video Experience",
    upstream: "https://guided-video-experience.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/guided-video-experience.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Open Reel is a guided video cut. Choose a short chapter, watch it through, and the next choice follows the…",
  },
  {
    slug: "golf-trainer-app",
    surface: "app",
    title: "Golf Mastery - 10 Hacks Mobile Trainer",
    upstream: "https://golf-trainer-app.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/golf-trainer-app.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Master golf with 10 proven training techniques. Interactive lessons, progress tracking, and practice tools.",
  },
  {
    slug: "github-roaster",
    surface: "app",
    title: "Github Roaster",
    upstream: "https://github-roaster.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/github-roaster.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A fun, data-driven GitHub profile roaster. Enter any username and get roasted based on real stats: stars,…",
  },
  {
    slug: "git-viz-guide",
    surface: "app",
    title: "Git Viz Guide",
    upstream: "https://git-viz-guide.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/git-viz-guide.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Walk through branching, merge conflicts, and rebase on a live Git graph. No account.",
  },
  {
    slug: "ghostless",
    surface: "app",
    title: "Ghostless",
    upstream: "https://ghostless.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ghostless.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Find jobs. Verify they still exist. Shortlist only the ones worth applying to.",
  },
  {
    slug: "hyperfocus-stream",
    surface: "app",
    title: "HyperFocus Stream",
    upstream: "https://hyperfocus-stream.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/hyperfocus-stream.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A canvas for watching several open-sample loops at once. Search a vibe, then drag, stack, or grid the streams.",
  },
  {
    slug: "idea-morgue",
    surface: "app",
    title: "Idea Morgue",
    upstream: "https://idea-morgue.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/idea-morgue.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A public autopsy room for side projects that died.",
  },
  {
    slug: "hyperion-control",
    surface: "app",
    title: "Hyperion Control",
    upstream: "https://hyperion-control.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/hyperion-control.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "An open bridge console. Telemetry, setpoints, ship buses, and work orders share one plant that runs in the…",
  },
  {
    slug: "habit-roast",
    surface: "app",
    title: "Habit Roast",
    upstream: "https://habit-roast.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/habit-roast.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Get roasted. Get better.",
  },
  {
    slug: "heavy-metal-mission-log",
    surface: "app",
    title: "Heavy Metal Mission Log",
    upstream: "https://heavy-metal-mission-log.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/heavy-metal-mission-log.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "File a gritty starship captain's log from a mission title, a type, and the events you saw. No account.",
  },
  {
    slug: "idea-forge",
    surface: "app",
    title: "Idea Forge",
    upstream: "https://idea-forge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/idea-forge.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Generate unique, shippable project ideas for indie hackers and developers.",
  },
  {
    slug: "echopath",
    surface: "app",
    title: "Echopath",
    upstream: "https://echopath.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/echopath.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "AI-powered personalized self-guided walking tours",
  },
  {
    slug: "idea-pulse",
    surface: "app",
    title: "Idea Pulse",
    upstream: "https://idea-pulse.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/idea-pulse.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A private board for scoring product and startup ideas with votes, feedback, and a validation meter.",
  },
  {
    slug: "ideaarchitect",
    surface: "app",
    title: "IdeaArchitect",
    upstream: "https://ideaarchitect.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ideaarchitect.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Turn an idea into ordered tasks, run each one, and keep the deliverables.",
  },
  {
    slug: "ideascroll",
    surface: "app",
    title: "IdeaScroll — infinite project ideas",
    upstream: "https://ideascroll.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ideascroll.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "TikTok-style infinite scroll of buildable project ideas. Filter by stack and time, save favorites, and…",
  },
  {
    slug: "ink-flock",
    surface: "app",
    title: "Ink Flock",
    upstream: "https://ink-flock.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ink-flock.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Interactive Japanese ink-painting (sumi-e) style flocking simulator.",
  },
  {
    slug: "image-display-lab",
    surface: "app",
    title: "Image Display Lab",
    upstream: "https://image-display-lab.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/image-display-lab.webp",
    shippedAt: "2026-10-10",
    status: "live",
    featured: true,
    featuredOrder: 43,
    blurb: "A browser lab for trying different ways to browse a large photograph set: grid, masonry, infinite scroll,…",
  },
  {
    slug: "inspo-wiki",
    surface: "app",
    title: "Inspo Wiki",
    upstream: "https://inspo-wiki.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/inspo-wiki.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Chat with your inspiration.",
  },
  {
    slug: "indie-idea-forge",
    surface: "app",
    title: "Indie Idea Forge",
    upstream: "https://indie-idea-forge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/indie-idea-forge.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Discover indie project ideas, pressure-test the buyer and the pain, and leave with a one-week build.…",
  },
  {
    slug: "idea-validate",
    surface: "app",
    title: "Idea Validate",
    upstream: "https://idea-validate.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/idea-validate.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Community board for posting product ideas and collecting votes, tagged feedback, and a short survey before…",
  },
  {
    slug: "kinepose-designer",
    surface: "app",
    title: "KinePose Designer",
    upstream: "https://kinepose-designer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/kinepose-designer.webp",
    shippedAt: "2026-10-10",
    status: "live",
  },
  {
    slug: "internet-roulette",
    surface: "app",
    title: "Internet Roulette",
    upstream: "https://internet-roulette.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/internet-roulette.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Discover the coolest corners of the internet.",
  },
  {
    slug: "last-light",
    surface: "app",
    title: "Last Light",
    upstream: "https://last-light.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/last-light.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A cinematic late-night focus desk. Name one thing before you sleep, break it into three small steps, run a…",
  },
  {
    slug: "integration-analysis",
    surface: "app",
    title: "Integration Analysis",
    upstream: "https://integration-analysis.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/integration-analysis.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Draft a company's vertical and horizontal integration: value-chain stages, seams, and expansion moves.",
  },
  {
    slug: "interactive-image-pinboard",
    surface: "app",
    title: "Interactive Image Pinboard",
    upstream: "https://interactive-image-pinboard.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/interactive-image-pinboard.webp",
    shippedAt: "2026-10-10",
    status: "live",
  },
  {
    slug: "launchforge",
    surface: "app",
    title: "Launchforge",
    upstream: "https://launchforge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/launchforge.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Instant Launch Kit Generator for Indie Makers",
  },
  {
    slug: "jev-digital-rain-room",
    surface: "app",
    title: "Jev Digital Rain — Construct",
    upstream: "https://jev-digital-rain-room.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/jev-digital-rain-room.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "First-person 3D room whose walls run cascading code rain drawn with the original Jev Digital Rain font.",
  },
  {
    slug: "is-dave-in-it",
    surface: "app",
    title: "Is Dave In It",
    upstream: "https://is-dave-in-it.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/is-dave-in-it.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A fun Next.js app that tells you whether Dave Bautista (Drax the Destroyer) is in any movie or TV show.",
  },
  {
    slug: "isocity",
    surface: "app",
    title: "Isocity",
    upstream: "https://isocity.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/isocity.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A pure Next.js + HTML5 Canvas isometric city builder & simulator.",
  },
  {
    slug: "launchpad",
    surface: "app",
    title: "Launchpad",
    upstream: "https://launchpad.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/launchpad.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Your personal idea launchpad. Capture, organize, prioritize and ship product ideas.",
  },
  {
    slug: "my-paper-pet",
    surface: "app",
    title: "My Paper Pet",
    upstream: "https://my-paper-pet.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/my-paper-pet.webp",
    shippedAt: "2026-10-10",
    status: "live",
  },
  {
    slug: "lumpdump",
    surface: "app",
    title: "Lumpdump",
    upstream: "https://lumpdump.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/lumpdump.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Dump the chaos in your head. Get a timed, sequenced plan back.",
  },
  {
    slug: "myworld",
    surface: "app",
    title: "MyWorld",
    upstream: "https://myworld.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/myworld.webp",
    shippedAt: "2026-10-10",
    status: "live",
  },
  {
    slug: "longplay",
    surface: "app",
    title: "Longplay",
    upstream: "https://longplay.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/longplay.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Letterboxd for albums. Log records you actually finish front to back.",
  },
  {
    slug: "meltfocus",
    surface: "app",
    title: "Meltfocus",
    upstream: "https://meltfocus.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/meltfocus.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A beautiful focus timer where you watch an ice cube melt as you work.",
  },
  {
    slug: "mulligan",
    surface: "app",
    title: "Mulligan",
    upstream: "https://mulligan.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/mulligan.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A ritual decision studio. Stop spinning. Draw a line.",
  },
  {
    slug: "neon-keys",
    surface: "app",
    title: "Neon Keys",
    upstream: "https://neon-keys.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/neon-keys.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Cyberpunk neon typing speed test built with Next.js.",
  },
  {
    slug: "p5-ai-studio",
    surface: "app",
    title: "P5.AI Studio",
    upstream: "https://p5-ai-studio.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/p5-ai-studio.webp",
    shippedAt: "2026-10-10",
    status: "live",
  },
  {
    slug: "nostalgia-store",
    surface: "app",
    title: "Nostalgia Store",
    upstream: "https://nostalgia-store.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/nostalgia-store.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Walk the aisles. Pick up the cases. Rediscover movies.",
  },
  {
    slug: "openalts",
    surface: "app",
    title: "Openalts",
    upstream: "https://openalts.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/openalts.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Discover clean open-source alternatives to proprietary & bloated software.",
  },
  {
    slug: "nfl-video-kinda-bad",
    surface: "app",
    title: "Nfl Video Kinda Bad",
    upstream: "https://nfl-video-kinda-bad.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/nfl-video-kinda-bad.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A fantasy football analytics + content-generation prototype that turns raw ESPN data into a…",
  },
  {
    slug: "nfl-play-partitioner",
    surface: "app",
    title: "NFL Play Partitioner",
    upstream: "https://nfl-play-partitioner.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/nfl-play-partitioner.webp",
    shippedAt: "2026-10-10",
    status: "live",
  },
  {
    slug: "nocturne",
    surface: "app",
    title: "Nocturne",
    upstream: "https://nocturne.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/nocturne.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A beautiful, dark-mode-first habit tracker, focus timer, and mood journal designed for night-shift…",
  },
  {
    slug: "nightowl",
    surface: "app",
    title: "Nightowl",
    upstream: "https://nightowl.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/nightowl.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Dark-mode first habit & energy tracker for night-shift workers, night owls, and irregular schedules.",
  },
  {
    slug: "nexus-7-cyber-blog",
    surface: "app",
    title: "Nexus 7 // Cyberlog",
    upstream: "https://nexus-7-cyber-blog.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/nexus-7-cyber-blog.webp",
    shippedAt: "2026-10-10",
    status: "live",
  },
  {
    slug: "nexus-art",
    surface: "app",
    title: "Arwes Art Hub",
    upstream: "https://nexus-art.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/nexus-art.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "I started NEXUS // ART with a simple belief: the next generation of creative tools won’t feel like…",
  },
  {
    slug: "pagegift",
    surface: "app",
    title: "Pagegift",
    upstream: "https://pagegift.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/pagegift.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Buy me a book — not another coffee.",
  },
  {
    slug: "paperforge",
    surface: "app",
    title: "PaperForge - AI Papercraft",
    upstream: "https://paperforge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/paperforge.webp",
    shippedAt: "2026-10-10",
    status: "live",
  },
  {
    slug: "photon-fusion-lab",
    surface: "app",
    title: "Photon Fusion Lab",
    upstream: "https://photon-fusion-lab.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/photon-fusion-lab.webp",
    shippedAt: "2026-10-10",
    status: "live",
  },
  {
    slug: "procrastination-penalty",
    surface: "app",
    title: "Procrastination Penalty",
    upstream: "https://procrastination-penalty.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/procrastination-penalty.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Loss aversion as motivation.",
  },
  {
    slug: "pathforge",
    surface: "app",
    title: "Pathforge",
    upstream: "https://pathforge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/pathforge.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Turn any job description into a personalized learning roadmap.",
  },
  {
    slug: "poseperfect",
    surface: "app",
    title: "Poseperfect",
    upstream: "https://poseperfect.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/poseperfect.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "AI-powered (and rule-based) photo posing guide – Learn how to pose for perfect photos in minutes.",
  },
  {
    slug: "pinboard-gallery",
    surface: "app",
    title: "Pinboard Gallery",
    upstream: "https://pinboard-gallery.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/pinboard-gallery.webp",
    shippedAt: "2026-10-10",
    status: "live",
  },
  {
    slug: "probe-idea-hunter",
    surface: "app",
    title: "Probe Idea Hunter",
    upstream: "https://probe-idea-hunter.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/probe-idea-hunter.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Turn social media complaints into scored startup ideas.",
  },
  {
    slug: "polywrap-research",
    surface: "app",
    title: "PolyWrap Research",
    upstream: "https://polywrap-research.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/polywrap-research.webp",
    shippedAt: "2026-10-10",
    status: "live",
  },
  {
    slug: "paw-studio",
    surface: "app",
    title: "PAW Studio",
    upstream: "https://paw-studio.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/paw-studio.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "PAW Studio is a desk for ProgramAsWeights: compile a text spec, run it, and ask a video scene-card index.…",
  },
  {
    slug: "seenon",
    surface: "app",
    title: "Seenon",
    upstream: "https://seenon.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/seenon.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "See a look. Shop the pieces.",
  },
  {
    slug: "questly",
    surface: "app",
    title: "Questly",
    upstream: "https://questly.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/questly.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Gamified personal quest tracker. Turn your goals into epic quests, earn XP, level up, build streaks, and…",
  },
  {
    slug: "rubiks-cube-solver",
    surface: "app",
    title: "Rubik's Cube Solver",
    upstream: "https://rubiks-cube-solver.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/rubiks-cube-solver.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "This template provides a minimal setup to get React working in Vite with HMR and some ESLint rules.",
  },
  {
    slug: "saga",
    surface: "app",
    title: "Saga",
    upstream: "https://saga.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/saga.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Binge history like a series.",
  },
  {
    slug: "rate-limit-visualizer",
    surface: "app",
    title: "Rate Limit Visualizer",
    upstream: "https://rate-limit-visualizer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/rate-limit-visualizer.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Interactive Rate Limiting Algorithm Visualizer built with Next.js.",
  },
  {
    slug: "projectforge",
    surface: "app",
    title: "Projectforge",
    upstream: "https://projectforge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/projectforge.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "AI-powered side project idea generator",
  },
  {
    slug: "railrisk-monte-carlo",
    surface: "app",
    title: "RailRisk Monte Carlo",
    upstream: "https://railrisk-monte-carlo.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/railrisk-monte-carlo.webp",
    shippedAt: "2026-10-10",
    status: "live",
  },
  {
    slug: "realtime-browser-upscaling",
    surface: "app",
    title: "Realtime Browser AI Upscaling",
    upstream: "https://realtime-browser-upscaling.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/realtime-browser-upscaling.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Client-side WebGPU AI upscaling demo — wipe / split / triple / diff compare with WebSR (Anime4K). Load…",
  },
  {
    slug: "recall-life-vault",
    surface: "app",
    title: "Recall Life Vault",
    upstream: "https://recall-life-vault.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/recall-life-vault.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A private second-brain vault inspired by a tweet asking for an app that stores screenshots, notes, links,…",
  },
  {
    slug: "social-verdict",
    surface: "app",
    title: "Social Verdict",
    upstream: "https://social-verdict.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/social-verdict.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Real opinions from real people. Before you buy.",
  },
  {
    slug: "skillnet-intelligence",
    surface: "app",
    title: "SkillNet Intelligence",
    upstream: "https://skillnet-intelligence.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/skillnet-intelligence.webp",
    shippedAt: "2026-10-10",
    status: "live",
  },
  {
    slug: "sparkshelf",
    surface: "app",
    title: "Sparkshelf",
    upstream: "https://sparkshelf.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/sparkshelf.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "The app store for tiny AI-made tools that would otherwise vanish into random links.",
  },
  {
    slug: "snapmind",
    surface: "app",
    title: "Snapmind",
    upstream: "https://snapmind.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/snapmind.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "An AI-powered second brain that turns the screenshots you take every day into searchable knowledge and…",
  },
  {
    slug: "shipyard",
    surface: "app",
    title: "Shipyard",
    upstream: "https://shipyard.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/shipyard.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Showcase what you shipped.",
  },
  {
    slug: "sparkinbox",
    surface: "app",
    title: "Sparkinbox",
    upstream: "https://sparkinbox.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/sparkinbox.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Capture fleeting ideas. Collect context. Evolve plans.",
  },
  {
    slug: "spotdex",
    surface: "app",
    title: "Spotdex",
    upstream: "https://spotdex.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/spotdex.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Catch spots like Pokémon Go — never forget a place or its memory.",
  },
  {
    slug: "snapjournal",
    surface: "app",
    title: "Snapjournal",
    upstream: "https://snapjournal.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/snapjournal.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Your photos write your life story.",
  },
  {
    slug: "sparkhub",
    surface: "app",
    title: "Sparkhub",
    upstream: "https://sparkhub.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/sparkhub.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Discover, generate, and share Next.js & web dev project ideas.",
  },
  {
    slug: "support-doc-ai",
    surface: "app",
    title: "Support Doc Ai",
    upstream: "https://support-doc-ai.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/support-doc-ai.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Inspired by a cool idea from [@tibomaker](https://x.com/tibomaker) on X:",
  },
  {
    slug: "sweatstake",
    surface: "app",
    title: "Sweatstake",
    upstream: "https://sweatstake.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/sweatstake.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A web app that helps users stick to fitness goals by tying them to real monetary stakes. Users set custom…",
  },
  {
    slug: "swipewise",
    surface: "app",
    title: "Swipewise",
    upstream: "https://swipewise.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/swipewise.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Know which credit card to swipe.",
  },
  {
    slug: "streetpulse",
    surface: "app",
    title: "Streetpulse",
    upstream: "https://streetpulse.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/streetpulse.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Fintwit Fear & Greed gauge. Inspired by…",
  },
  {
    slug: "sumiflock",
    surface: "app",
    title: "Sumiflock",
    upstream: "https://sumiflock.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/sumiflock.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Interactive Japanese ink-style (sumi-e) flocking simulation.",
  },
  {
    slug: "streakly-habit-tracker",
    surface: "app",
    title: "Streakly Habit Tracker",
    upstream: "https://streakly-habit-tracker.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/streakly-habit-tracker.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A beautiful, modern habit tracker built with Next.js, Tailwind CSS, and Framer Motion.",
  },
  {
    slug: "stimulation-station",
    surface: "app",
    title: "Stimulation Station",
    upstream: "https://stimulation-station.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/stimulation-station.webp",
    shippedAt: "2026-10-10",
    status: "live",
  },
  {
    slug: "swipematch",
    surface: "app",
    title: "Swipematch",
    upstream: "https://swipematch.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/swipematch.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Tinder for movies & TV shows. Swipe solo to build a watchlist, or match with friends to instantly discover…",
  },
  {
    slug: "statushunter",
    surface: "app",
    title: "Statushunter",
    upstream: "https://statushunter.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/statushunter.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Inspired by [@gregisenberg](https://x.com/gregisenberg)’s growth-agent idea:",
  },
  {
    slug: "thumbforge",
    surface: "app",
    title: "Thumbforge",
    upstream: "https://thumbforge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/thumbforge.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Free YouTube Thumbnail Generator",
  },
  {
    slug: "tuneai-ai-music-studio",
    surface: "app",
    title: "TuneAI: AI Music Studio",
    upstream: "https://tuneai-ai-music-studio.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/tuneai-ai-music-studio.webp",
    shippedAt: "2026-10-10",
    status: "live",
  },
  {
    slug: "taskbloom",
    surface: "app",
    title: "Taskbloom",
    upstream: "https://taskbloom.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/taskbloom.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A delightful productivity app where projects grow into beautiful flowers as you complete tasks.",
  },
  {
    slug: "tomorrow-tax",
    surface: "app",
    title: "Tomorrow Tax",
    upstream: "https://tomorrow-tax.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/tomorrow-tax.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Procrastination has a price.",
  },
  {
    slug: "threadly-ai",
    surface: "app",
    title: "Threadly Ai",
    upstream: "https://threadly-ai.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/threadly-ai.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "AI-powered X/Twitter thread generator",
  },
  {
    slug: "ticket-slayer",
    surface: "app",
    title: "Ticket Slayer",
    upstream: "https://ticket-slayer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ticket-slayer.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "AI-powered parking ticket appeal generator",
  },
  {
    slug: "the-void",
    surface: "app",
    title: "The Void",
    upstream: "https://the-void.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/the-void.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "This contains everything you need to run your app locally.",
  },
  {
    slug: "vibespace-3d",
    surface: "app",
    title: "VibeSpace 3D",
    upstream: "https://vibespace-3d.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vibespace-3d.webp",
    shippedAt: "2026-10-10",
    status: "live",
  },
  {
    slug: "vibecheck",
    surface: "app",
    title: "Vibecheck",
    upstream: "https://vibecheck.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vibecheck.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Is it vibe coded?",
  },
  {
    slug: "vibegames",
    surface: "app",
    title: "Vibegames",
    upstream: "https://vibegames.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vibegames.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "TikTok for vibe-coded minigames",
  },
  {
    slug: "vibe24",
    surface: "app",
    title: "Vibe24",
    upstream: "https://vibe24.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vibe24.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Anonymous vibes that live for 24 hours.",
  },
  {
    slug: "veilmap",
    surface: "app",
    title: "Veilmap",
    upstream: "https://veilmap.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/veilmap.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A Fog-of-World style personal exploration atlas.",
  },
  {
    slug: "vibescroll-minigames",
    surface: "app",
    title: "VibeScroll — Infinite Mini-Games",
    upstream: "https://vibescroll-minigames.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vibescroll-minigames.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "TikTok for mini-games. Swipe when bored.",
  },
  {
    slug: "vent24",
    surface: "app",
    title: "Vent24",
    upstream: "https://vent24.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vent24.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Say it. React with emojis. Gone in 24 hours.",
  },
  {
    slug: "vibeminis",
    surface: "app",
    title: "Vibeminis",
    upstream: "https://vibeminis.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vibeminis.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "TikTok for vibecoded minigames.",
  },
  {
    slug: "uv-viz-algorithm-explorer",
    surface: "app",
    title: "UV-Viz: Algorithm Explorer",
    upstream: "https://uv-viz-algorithm-explorer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/uv-viz-algorithm-explorer.webp",
    shippedAt: "2026-10-10",
    status: "live",
  },
  {
    slug: "vibescroll",
    surface: "app",
    title: "Vibescroll",
    upstream: "https://vibescroll.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vibescroll.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "TikTok for browser minigames.",
  },
  {
    slug: "vintique",
    surface: "app",
    title: "Vintique",
    upstream: "https://vintique.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vintique.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A beautiful one-person vintage shop built with Next.js App Router + Tailwind CSS.",
  },
  {
    slug: "vintage-vault",
    surface: "app",
    title: "Vintage Vault",
    upstream: "https://vintage-vault.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vintage-vault.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A simple, beautiful one-person vintage shop built with Next.js App Router, TypeScript, and Tailwind CSS.",
  },
  {
    slug: "vintage-vibes-shop",
    surface: "app",
    title: "Vintage Vibes Shop",
    upstream: "https://vintage-vibes-shop.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vintage-vibes-shop.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A beautiful one-person vintage shop built with Next.js 16 (App Router) + Tailwind CSS.",
  },
  {
    slug: "widgetdash",
    surface: "app",
    title: "Widgetdash",
    upstream: "https://widgetdash.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/widgetdash.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A drag-and-drop board for a clock, tasks, notes, a sample weather card, quotes, and a short meme line. The…",
  },
  {
    slug: "vroomdex",
    surface: "app",
    title: "Vroomdex",
    upstream: "https://vroomdex.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vroomdex.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Pokémon GO for cars.",
  },
  {
    slug: "video-loop-creator",
    surface: "app",
    title: "Video Loop Creator",
    upstream: "https://video-loop-creator.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/video-loop-creator.webp",
    shippedAt: "2026-10-10",
    status: "live",
  },
  {
    slug: "whatchanged",
    surface: "app",
    title: "Whatchanged",
    upstream: "https://whatchanged.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/whatchanged.svg",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "A beautiful, client-side app to track UI updates, pricing changes, algorithm shifts, API contracts —…",
  },
  {
    slug: "vitae-ai",
    surface: "app",
    title: "Vitae Ai",
    upstream: "https://vitae-ai.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vitae-ai.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Inspired by a popular Twitter/X project idea list.",
  },
  {
    slug: "wobble-game",
    surface: "app",
    title: "Robot Catch - Match 3 Launcher Game",
    upstream: "https://wobble-game.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/wobble-game.webp",
    shippedAt: "2026-10-10",
    status: "live",
    blurb: "Wobble Game exists because “3D creation” is still treated like software you learn, not something you play.…",
  },
  {
    slug: "zenith-video-platform",
    surface: "app",
    title: "Zenith Video Platform",
    upstream: "https://zenith-video-platform.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/zenith-video-platform.webp",
    shippedAt: "2026-10-10",
    status: "live",
  },
  {
    slug: "fixate",
    surface: "app",
    title: "Fixate",
    upstream: "https://fixate.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/fixate.webp",
    shippedAt: "2026-10-11",
    status: "live",
    blurb: "Match a hyperfixating person with a willing infodumper. Timed, text-first sessions; no feed, no followers.",
  },
  {
    slug: "flipwell",
    surface: "app",
    title: "Flipwell",
    upstream: "https://flipwell.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/flipwell.webp",
    shippedAt: "2026-10-11",
    status: "live",
    blurb: "A playful, audio-first frame-by-frame animation studio. Draw the sound.",
  },
  {
    slug: "recipe-rescue",
    surface: "app",
    title: "Recipe Rescue",
    upstream: "https://recipe-rescue.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/recipe-rescue.webp",
    shippedAt: "2026-10-11",
    status: "live",
    blurb: "Fix one cooking mistake mid-disaster: dish, symptom, stage, then a calm rescue card.",
  },
  {
    slug: "storm-router",
    surface: "app",
    title: "Storm Router",
    upstream: "https://storm-router.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/storm-router.webp",
    shippedAt: "2026-10-11",
    status: "live",
    blurb: "A* vs Dijkstra on a map where storms shift travel costs mid-search.",
  },
  {
    slug: "cache-arcade",
    surface: "app",
    title: "Cache Eviction Arcade",
    upstream: "https://cache-arcade.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/cache-arcade.webp",
    shippedAt: "2026-10-11",
    status: "live",
    blurb: "Race LRU, LFU and FIFO on the same request tape. Can you make LRU lose?",
  },
  {
    slug: "slow-letters",
    surface: "app",
    title: "Slow Letters",
    upstream: "https://slow-letters.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/slow-letters.webp",
    shippedAt: "2026-10-11",
    status: "live",
    blurb: "Pen-pal matching built on writing prompts, with letters that take time to arrive.",
  },
  {
    slug: "matinee",
    surface: "app",
    title: "Matinee",
    upstream: "https://matinee.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/matinee.webp",
    shippedAt: "2026-10-11",
    status: "live",
    blurb: "A virtual picture palace with timed showings you watch together.",
  },
  {
    slug: "daybit",
    surface: "app",
    title: "Daybit",
    upstream: "https://daybit.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/daybit.webp",
    shippedAt: "2026-10-12",
    status: "live",
    blurb: "One second of your life every day, stitched into a film. Local-first: clips never leave your device.",
  },
  {
    slug: "bloomgarden",
    surface: "app",
    title: "BloomGarden",
    upstream: "https://bloomgarden.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/bloomgarden.webp",
    shippedAt: "2026-10-12",
    status: "live",
    blurb: "Grow a digital garden by finishing tasks, with each project blooming into its own plant.",
  },
  {
    slug: "pigeon-post",
    surface: "app",
    title: "PigeonPost",
    upstream: "https://pigeon-post.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/pigeon-post.webp",
    shippedAt: "2026-10-12",
    status: "live",
    blurb: "Dispatch virtual carrier pigeons between cities, with real-distance flight times and a chance of getting lost.",
  },
  {
    slug: "punnett-square-builder",
    surface: "app",
    title: "Punnett Square Builder",
    upstream: "https://punnett-square-builder.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/punnett-square-builder.webp",
    shippedAt: "2026-10-12",
    status: "live",
    blurb: "Generate, auto-fill, and edit Punnett squares from parent variants for quick genetics lessons.",
  },
  {
    slug: "scrollarcade",
    surface: "app",
    title: "ScrollArcade",
    upstream: "https://scrollarcade.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/scrollarcade.webp",
    shippedAt: "2026-10-12",
    status: "live",
    blurb: "Scroll through bite-sized mini-games, from reaction tests and memory matches to typing races.",
  },
  {
    slug: "streakly-habits",
    surface: "app",
    title: "Streakly",
    upstream: "https://streakly-habits.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/streakly-habits.webp",
    shippedAt: "2026-10-12",
    status: "live",
    blurb: "Track habits with streaks, heatmaps, and completion stats, saved locally in your browser.",
  },
  {
    slug: "threadloom",
    surface: "app",
    title: "Threadloom",
    upstream: "https://threadloom.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/threadloom.webp",
    shippedAt: "2026-10-12",
    status: "live",
    blurb: "Draft and refine X threads with tweet cards, writing scores, templates, and a live preview.",
  },
  {
    slug: "ship-or-skip",
    surface: "app",
    title: "Ship or Skip",
    upstream: "https://ship-or-skip.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ship-or-skip.webp",
    shippedAt: "2026-10-12",
    status: "live",
    blurb: "Answer eight questions about your app idea for a scored Ship, Iterate, or Skip verdict.",
  },
  {
    slug: "resumeforge-ai",
    surface: "app",
    title: "ResumeForge AI",
    upstream: "https://resumeforge-ai.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/resumeforge-ai.webp",
    shippedAt: "2026-10-12",
    status: "live",
    blurb: "Analyze your resume with scorecards and recruiter feedback; connect ChatGPT for live analysis or explore a demo.",
  },
  {
    slug: "tinytrail",
    surface: "app",
    title: "TinyTrail",
    upstream: "https://tinytrail.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/tinytrail.webp",
    shippedAt: "2026-10-12",
    status: "live",
    blurb: "A status page for a URL shortener in development; link creation and redirects are not yet available.",
  },
  {
    slug: "unlisted",
    surface: "app",
    title: "Unlisted",
    upstream: "https://unlisted.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/unlisted.webp",
    shippedAt: "2026-10-12",
    status: "live",
    blurb: "Discover places loved in personal blogs and field notes, with city guides, filters, and local saves.",
  },
  {
    slug: "billy-webber-hype-page",
    surface: "app",
    title: "Billy Webber",
    upstream: "https://billy-webber-hype-page.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/billy-webber-hype-page.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "An editorial tribute spanning nuclear physics and black holes, with shareable field-note dedications.",
  },
  {
    slug: "entiquity-com-launch-page",
    surface: "app",
    title: "Entiquity",
    upstream: "https://entiquity-com-launch-page.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/entiquity-com-launch-page.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "A boutique publishing launch concept showcasing collectible editions, traditional bookbinding, and the Curator’s Circle.",
  },
  {
    slug: "napkin-brief",
    surface: "app",
    title: "Napkin",
    upstream: "https://napkin-brief.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/napkin-brief.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Shape a rough app idea into a focused product brief, risk scores, and a seven-day build plan.",
  },
  {
    slug: "kinora",
    surface: "app",
    title: "Kinora",
    upstream: "https://kinora.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/kinora.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Compile small hardware ideas into editable bills of materials, assembly instructions, cost estimates, and exportable factory packets.",
  },
  {
    slug: "mailforge",
    surface: "app",
    title: "Mailforge",
    upstream: "https://mailforge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/mailforge.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Explore Outlook mailbox provisioning for outbound agencies, with a capacity calculator, pricing, and an inquiry form.",
  },
  {
    slug: "ofc-site",
    surface: "app",
    title: "OFC — Offline File Converter",
    upstream: "https://ofc-site.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ofc-site.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "A launch site for private desktop file conversion on macOS, Windows, and Linux; downloads are coming soon.",
  },
  {
    slug: "frequency",
    surface: "app",
    title: "FREQUENCY",
    upstream: "https://frequency.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/frequency.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Tune into synchronized generative radio across six stations, with saved rooms and a personal listening history.",
  },
  {
    slug: "ship-not-code",
    surface: "app",
    title: "Ship Not Code",
    upstream: "https://ship-not-code.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ship-not-code.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Three daily growth missions help founders find customers and revenue, while Feature Lock records the urge to build more.",
  },
  {
    slug: "ambit",
    surface: "app",
    title: "Ambit",
    upstream: "https://ambit.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ambit.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Track your 168-hour week with a painted grid and timer, then compare actual hours against your ideal budget.",
  },
  {
    slug: "orbit-character-studio",
    surface: "app",
    title: "Orbit",
    upstream: "https://orbit-character-studio.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/orbit-character-studio.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Explore original character references with a turntable, palettes, costume notes, pins, and side-by-side comparison.",
  },
  {
    slug: "tailorapply",
    surface: "app",
    title: "TailorApply",
    upstream: "https://tailorapply.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/tailorapply.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Turn a job posting URL into a tailored resume and cover letter matched to the role.",
  },
  {
    slug: "saas-workflow-app",
    surface: "app",
    title: "SaaS Workflow",
    upstream: "https://saas-workflow-app.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/saas-workflow-app.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Track a SaaS from idea to scale across 16 phases. Mark each step, and keep progress in this browser.",
  },
  {
    slug: "slowfilm",
    surface: "app",
    title: "Slowfilm",
    upstream: "https://slowfilm.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/slowfilm.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "A digital film camera. Load a roll, shoot without instant previews, send it to the lab, open the envelope.",
  },
  {
    slug: "birthday-plate",
    surface: "app",
    title: "Birthday Plate",
    upstream: "https://birthday-plate.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/birthday-plate.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "US chain birthday food deals from official terms, with a join-early planner.",
  },
  {
    slug: "extract-claude-mem-data",
    appId: "app_extract_claude_mem_data_lxuar",
    surface: "app",
    title: "claude-mem extract",
    upstream: "https://extract-claude-mem-data-lxuar.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/extract-claude-mem-data.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Turn a local claude-mem SQLite database into redacted fine-tuning JSONL. The file stays in your browser.",
  },
  {
    slug: "image-grid-animations",
    surface: "app",
    title: "Image Grid Animations",
    upstream: "https://image-grid-animations.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/image-grid-animations.webp",
    shippedAt: "2026-10-13",
    status: "live",
    featured: true,
    featuredOrder: 15,
    blurb: "A fullscreen image wall cycling through wipes, clock sweeps, crossfades, and other transitions.",
  },
  {
    slug: "design-bookstore-pages",
    surface: "app",
    title: "Entiquity",
    upstream: "https://design-bookstore-pages.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/design-bookstore-pages.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "A library reimagined: rare books, modern firsts, and remarkable reading, curated since 1987.",
  },
  {
    slug: "blackjack-counter-trainer",
    surface: "app",
    title: "Blackjack Counter Trainer",
    upstream: "https://blackjack-counter-trainer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/blackjack-counter-trainer.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Drill a running count, true count, and count-aware basic strategy against a multi-deck shoe.",
  },
  {
    slug: "video-creation-platform",
    surface: "app",
    title: "Video Creator Platform",
    upstream: "https://video-creation-platform.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/video-creation-platform.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "A collaborative video studio built with Remotion.",
  },
  {
    slug: "vercel-api-deployments",
    surface: "app",
    title: "Vercel Deployments",
    upstream: "https://vercel-api-deployments.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vercel-api-deployments.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "View and manage Vercel projects, deployments, and subdomains.",
  },
  {
    slug: "unsit",
    surface: "app",
    title: "Unsit",
    upstream: "https://unsit.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/unsit.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Sixty-second desk stretches between focus blocks, for people who sit too much.",
  },
  {
    slug: "snapbrain",
    surface: "app",
    title: "SnapBrain",
    upstream: "https://snapbrain.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/snapbrain.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Turn screenshots into a searchable second brain of notes, tags, and todos.",
  },
  {
    slug: "signal-scout",
    surface: "app",
    title: "Signal Scout",
    upstream: "https://signal-scout.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/signal-scout.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Watch where your audience talks, then get a Monday brief of pains, questions, and objections.",
  },
  {
    slug: "shorty",
    surface: "app",
    title: "Shorty",
    upstream: "https://shorty.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/shorty.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Shorten a URL, share the link, and see how many times it was opened.",
  },
  {
    slug: "shortify-analytics",
    surface: "app",
    title: "Shortify Analytics",
    upstream: "https://shortify-analytics.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/shortify-analytics.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Shorten links and read click analytics, with QR codes, custom slugs, and expirations.",
  },
  {
    slug: "resumeiq",
    surface: "app",
    title: "ResumeIQ",
    upstream: "https://resumeiq.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/resumeiq.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Upload a PDF resume for an ATS scorecard, keyword match, and a recruiter's six-second view.",
  },
  {
    slug: "pulse-link",
    surface: "app",
    title: "PulseLink",
    upstream: "https://pulse-link.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/pulse-link.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Short links with click analytics, QR codes, custom slugs, and expiration dates.",
  },
  {
    slug: "progressive-webapp-fullscreen",
    surface: "app",
    title: "MultiView",
    upstream: "https://progressive-webapp-fullscreen.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/progressive-webapp-fullscreen.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Watch several videos at once in a fullscreen grid. Paste URLs and arrange the tiles.",
  },
  {
    slug: "progressive-web-app",
    surface: "app",
    title: "Multi Video Player",
    upstream: "https://progressive-web-app.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/progressive-web-app.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Watch multiple videos together in one installable player.",
  },
  {
    slug: "problem-solving-platform-design",
    surface: "app",
    title: "CodeMaster",
    upstream: "https://problem-solving-platform-design.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/problem-solving-platform-design.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Practice interview problems with step-by-step algorithm animations and multi-language solutions.",
  },
  {
    slug: "podletter-ai",
    surface: "app",
    title: "PodLetter AI",
    upstream: "https://podletter-ai.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/podletter-ai.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Paste a podcast transcript and get a newsletter with a summary, takeaways, quotes, and a call to action.",
  },
  {
    slug: "nexus-short",
    surface: "app",
    title: "Nexus Short",
    upstream: "https://nexus-short.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/nexus-short.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Short links with a slug you can remember, a QR code, and a recorded click.",
  },
  {
    slug: "linkspark-url-shortener",
    surface: "app",
    title: "LinkSpark",
    upstream: "https://linkspark-url-shortener.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/linkspark-url-shortener.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Paste a long URL and get a compact link on this host.",
  },
  {
    slug: "latch",
    surface: "app",
    title: "Latch",
    upstream: "https://latch.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/latch.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Roll out, target, and kill feature flags from a small-team console.",
  },
  {
    slug: "lane",
    surface: "app",
    title: "Lane",
    upstream: "https://lane.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/lane.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "A short daily course for the written driving exam: signs, right of way, mock tests, and streaks.",
  },
  {
    slug: "gpt2-124m-knowledge-book",
    surface: "app",
    title: "Let's reproduce GPT-2 (124M)",
    upstream: "https://gpt2-124m-knowledge-book.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/gpt2-124m-knowledge-book.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "A study book for Karpathy's lecture on reproducing GPT-2 (124M), with hover explainers on the transcript.",
  },
  {
    slug: "gatherfilm",
    surface: "app",
    title: "GatherFilm",
    upstream: "https://gatherfilm.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/gatherfilm.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Collect guest phone clips and cut a multi-camera film of the night.",
  },
  {
    slug: "framedrop",
    surface: "app",
    title: "FrameDrop",
    upstream: "https://framedrop.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/framedrop.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Drop a photo, pick a film pack, write a caption, and peel a Polaroid in the browser.",
  },
  {
    slug: "fin-gauge",
    surface: "app",
    title: "FinGauge",
    upstream: "https://fin-gauge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/fin-gauge.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "A retail sentiment gauge for FinTwit, in the spirit of a fear-and-greed meter.",
  },
  {
    slug: "draftjudge",
    surface: "app",
    title: "DraftJudge",
    upstream: "https://draftjudge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/draftjudge.webp",
    shippedAt: "2026-10-13",
    status: "live",
    featured: true,
    featuredOrder: 9,
    blurb: "Score AI drafts on a rubric and record which version you publish. The call stays in this browser.",
  },
  {
    slug: "ambience-byo",
    surface: "app",
    title: "Ambience",
    upstream: "https://ambience-byo.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ambience-byo.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Book the room, bring your own food. The venue sells atmosphere, not a menu.",
  },
  {
    slug: "ai-resume-analyzer",
    surface: "app",
    title: "AI Resume Analyzer",
    upstream: "https://ai-resume-analyzer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ai-resume-analyzer.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Upload a resume and get an instant score, skill gaps, and improvement notes.",
  },
  {
    slug: "linkforge-ai",
    surface: "app",
    title: "LinkForge AI",
    upstream: "https://linkforge-ai.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/linkforge-ai.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Paste a long URL and get a short link from a smart shortener.",
  },
  {
    slug: "ai-sdk-next-openai",
    surface: "app",
    title: "AI SDK - Next.js OpenAI Examples",
    upstream: "https://ai-sdk-next-openai.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ai-sdk-next-openai.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Examples of using the AI SDK with Next.js and OpenAI.",
  },
  {
    slug: "ai-architect-agent",
    surface: "app",
    title: "AI Architect Agent",
    upstream: "https://ai-architect-agent.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ai-architect-agent.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Turn a messy brief and five constraints into three architectures, then commit one decision record.",
  },
  {
    slug: "signal-desk",
    surface: "app",
    title: "Signal Desk",
    upstream: "https://signal-desk.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/signal-desk.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "Daily agentic engineering briefing plus runnable labs for graph orchestration, handoff tax, and multi-agent dashboards.",
  },
  {
    slug: "pulsetwit-gauge",
    surface: "app",
    title: "PULSE",
    upstream: "https://pulsetwit-gauge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/pulsetwit-gauge.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "A Fear and Greed meter for FinTwit, the crowd on X that talks markets all day.",
  },
  {
    slug: "flaglane",
    surface: "app",
    title: "Flaglane",
    upstream: "https://flaglane.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/flaglane.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "A feature-flag console for small teams: percentage rollouts, targeting rules, and an audit log.",
  },
  {
    slug: "flagdeck",
    surface: "app",
    title: "Flagdeck",
    upstream: "https://flagdeck.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/flagdeck.webp",
    shippedAt: "2026-10-13",
    status: "live",
    blurb: "A feature-flag console for small teams: percentage rollouts, allowlists, and a one-click promote.",
  },
  {
    slug: "ai-resume-helper",
    surface: "app",
    title: "AI Cover Letter Writer",
    upstream: "https://ai-resume-helper.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ai-resume-helper.webp",
    shippedAt: "2026-10-14",
    status: "live",
    blurb: "Paste resume text and a job description, then generate and download a tailored cover letter.",
  },
  {
    slug: "instantframe",
    surface: "app",
    title: "InstantFrame",
    upstream: "https://instantframe.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/instantframe.webp",
    shippedAt: "2026-10-14",
    status: "live",
    blurb: "Turn any photo into a Polaroid: film stocks, darkroom filters, captions, and a private local roll.",
  },
  {
    slug: "dspy-jev",
    surface: "app",
    title: "Field notes: Jev × DSPy",
    upstream: "https://dspy-jev.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/dspy-jev.webp",
    shippedAt: "2026-10-14",
    status: "live",
    blurb: "Practitioner field notes on Jev + DSPy: the Predict backend, Re-anchor, four agent patterns, gotchas, when to escalate.",
  },
  {
    slug: "fourier-epicycles",
    surface: "app",
    title: "Fourier Epicycles",
    upstream: "https://fourier-epicycles.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/fourier-epicycles.webp",
    shippedAt: "2026-10-14",
    status: "live",
    blurb: "Draw any shape and watch spinning circles retrace it via Fourier series.",
  },
  {
    slug: "julia-morph",
    surface: "app",
    title: "Julia Morph",
    upstream: "https://julia-morph.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/julia-morph.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Drag a point on the Mandelbrot set and see its Julia set morph live.",
  },
  {
    slug: "bayes-boxes",
    surface: "app",
    title: "Bayes Boxes",
    upstream: "https://bayes-boxes.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/bayes-boxes.webp",
    shippedAt: "2026-10-14",
    status: "live",
    blurb: "Bayes theorem with draggable area boxes for medical-test intuition.",
  },
  {
    slug: "lenia-pond",
    surface: "app",
    title: "Lenia Pond",
    upstream: "https://lenia-pond.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/lenia-pond.webp",
    shippedAt: "2026-10-14",
    status: "live",
    blurb: "Continuous cellular automata (Lenia) with lifelike creatures.",
  },
  {
    slug: "golden-sunflower",
    surface: "app",
    title: "Golden Sunflower",
    upstream: "https://golden-sunflower.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/golden-sunflower.webp",
    shippedAt: "2026-10-15",
    status: "live",
    featured: true,
    featuredOrder: 13,
    blurb: "Phyllotaxis: change the divergence angle and watch seed patterns break.",
  },
  {
    slug: "raft-consensus",
    surface: "app",
    title: "Raft Consensus",
    upstream: "https://raft-consensus.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/raft-consensus.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Raft leader election and log replication with killable nodes. Kill the leader; a new one wins.",
  },
  {
    slug: "regex-nfa",
    surface: "app",
    title: "Regex NFA",
    upstream: "https://regex-nfa.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/regex-nfa.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Compile a regex to an NFA/DFA and step through matching.",
  },
  {
    slug: "crdt-merge",
    surface: "app",
    title: "CRDT Merge",
    upstream: "https://crdt-merge.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/crdt-merge.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Two offline editors merging with a text CRDT.",
  },
  {
    slug: "astar-maze",
    surface: "app",
    title: "Astar Maze",
    upstream: "https://astar-maze.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/astar-maze.webp",
    shippedAt: "2026-10-14",
    status: "live",
    featured: true,
    featuredOrder: 12,
    blurb: "Draw walls and race A*, Dijkstra, BFS, and greedy best-first.",
  },
  {
    slug: "complex-mapper",
    surface: "app",
    title: "Complex Mapper",
    upstream: "https://complex-mapper.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/complex-mapper.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Domain-color complex functions and watch grids warp under z^2, 1/z, and exp.",
  },
  {
    slug: "gradient-hike",
    surface: "app",
    title: "Gradient Hike",
    upstream: "https://gradient-hike.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/gradient-hike.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Gradient descent, momentum, and Adam race down a loss surface.",
  },
  {
    slug: "tokenizer-split",
    surface: "app",
    title: "Tokenizer Split",
    upstream: "https://tokenizer-split.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/tokenizer-split.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "See how byte-pair encoding splits text into tokens, one merge at a time.",
  },
  {
    slug: "float-anatomy",
    surface: "app",
    title: "Float Anatomy",
    upstream: "https://float-anatomy.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/float-anatomy.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "IEEE 754 on the bench: sign, exponent, and fraction for the number you type.",
  },
  {
    slug: "queue-theory",
    surface: "app",
    title: "Queue Theory",
    upstream: "https://queue-theory.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/queue-theory.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "M/M/1 queues where utilization near 100% explodes wait times.",
  },
  {
    slug: "wave-tank",
    surface: "app",
    title: "Wave Tank",
    upstream: "https://wave-tank.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/wave-tank.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "A ripple tank for interference and diffraction. Two slits, and fringes appear.",
  },
  {
    slug: "fluid-smoke",
    surface: "app",
    title: "Fluid Smoke",
    upstream: "https://fluid-smoke.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/fluid-smoke.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Real-time 2D fluid simulation with smoke and vortices.",
  },
  {
    slug: "double-slit-quantum",
    surface: "app",
    title: "Double Slit Quantum",
    upstream: "https://double-slit-quantum.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/double-slit-quantum.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Single photons building an interference pattern, one detection at a time.",
  },
  {
    slug: "reaction-diffusion",
    surface: "app",
    title: "Reaction Diffusion",
    upstream: "https://reaction-diffusion.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/reaction-diffusion.webp",
    shippedAt: "2026-10-15",
    status: "live",
    featured: true,
    featuredOrder: 11,
    blurb: "Gray-Scott reaction-diffusion. Tune the feed rate and spots join into stripes.",
  },
  {
    slug: "epidemic-sir",
    surface: "app",
    title: "Epidemic SIR",
    upstream: "https://epidemic-sir.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/epidemic-sir.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "An SIR epidemic model with vaccination and distancing sliders.",
  },
  {
    slug: "slime-mold",
    surface: "app",
    title: "Slime Mold",
    upstream: "https://slime-mold.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/slime-mold.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Physarum slime mold. Place food on cities and a rail-like network grows.",
  },
  {
    slug: "firefly-sync",
    surface: "app",
    title: "Firefly Sync",
    upstream: "https://firefly-sync.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/firefly-sync.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "A meadow of fireflies locking into one rhythm. Drag coupling and they synchronize.",
  },
  {
    slug: "compound-snowball",
    surface: "app",
    title: "Compound Snowball",
    upstream: "https://compound-snowball.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/compound-snowball.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "The same monthly deposit, started at two ages, with compound interest beside simple interest.",
  },
  {
    slug: "order-book",
    surface: "app",
    title: "Order Book",
    upstream: "https://order-book.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/order-book.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "A synthetic limit order book. Place a market order and watch it walk the book.",
  },
  {
    slug: "retirement-montecarlo",
    surface: "app",
    title: "Retirement Montecarlo",
    upstream: "https://retirement-montecarlo.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/retirement-montecarlo.webp",
    shippedAt: "2026-10-15",
    status: "live",
    featured: true,
    featuredOrder: 24,
    blurb: "Monte Carlo retirement success odds across a few thousand market futures.",
  },
  {
    slug: "map-projections",
    surface: "app",
    title: "Map Projections",
    upstream: "https://map-projections.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/map-projections.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Compare map projections and watch Greenland shrink as you drag it toward the equator.",
  },
  {
    slug: "true-size-drag",
    surface: "app",
    title: "True Size Drag",
    upstream: "https://true-size-drag.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/true-size-drag.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Drag countries over each other at true scale.",
  },
  {
    slug: "voting-systems",
    surface: "app",
    title: "Voting Systems",
    upstream: "https://voting-systems.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/voting-systems.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Plurality, ranked choice, and approval, counted on the same ballots.",
  },
  {
    slug: "traffic-jam",
    surface: "app",
    title: "Traffic Jam",
    upstream: "https://traffic-jam.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/traffic-jam.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Phantom traffic jams from one braking car. Tap the brake and the wave travels backward.",
  },
  {
    slug: "circle-fifths",
    surface: "app",
    title: "Circle Fifths",
    upstream: "https://circle-fifths.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/circle-fifths.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "An interactive circle of fifths. Click C, G, Am, F and hear the loop.",
  },
  {
    slug: "wordle-entropy",
    surface: "app",
    title: "Wordle Entropy",
    upstream: "https://wordle-entropy.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/wordle-entropy.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Rank Wordle opening guesses by how much information their color pattern carries.",
  },
  // Top50 wave45. Same-account workers.dev on digitalnative, so host is the
  // SEO canonical. Utility workers have no product auth and no D1.
  {
    slug: "jwt-inspect",
    surface: "app",
    title: "JWT Inspect",
    upstream: "https://jwt-inspect.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/jwt-inspect.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Decode and lint a JSON Web Token without verifying the signature or accepting a secret.",
  },
  {
    slug: "headers-check",
    surface: "app",
    title: "Headers Check",
    upstream: "https://headers-check.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/headers-check.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Analyze the HTTP security headers of a public URL.",
  },
  {
    slug: "transit-departures",
    surface: "app",
    title: "Transit Departures",
    upstream: "https://transit-departures.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/transit-departures.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Next scheduled departures from a GTFS feed, as JSON.",
  },
  {
    slug: "gpx-stats",
    surface: "app",
    title: "GPX Stats",
    upstream: "https://gpx-stats.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/gpx-stats.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "GPX file to distance, elevation and pace.",
  },
  {
    slug: "pii-detect",
    surface: "app",
    title: "PII Detect",
    upstream: "https://pii-detect.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/pii-detect.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Detect emails, phones, and IDs in text.",
  },
  {
    slug: "og-image-gen",
    surface: "app",
    title: "OG Image Gen",
    upstream: "https://og-image-gen.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/og-image-gen.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Generate an Open Graph PNG from a template and a few lines of text.",
  },
  {
    slug: "email-dns-check",
    surface: "app",
    title: "Email DNS Check",
    upstream: "https://email-dns-check.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/email-dns-check.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "SPF, DKIM and DMARC checker.",
  },
  // AI apps: Sign in with Over9000 is stubbed in the app. There is no OAuth
  // client. Omit `auth` (schema: omit = no IdP client) so id:clients does not
  // project a client or invent redirect URIs, scopes, or secrets.
  {
    slug: "meeting-minutes",
    surface: "app",
    title: "Meeting Minutes",
    upstream: "https://meeting-minutes.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/meeting-minutes.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Paste a transcript. Get decisions, owners, and deadlines.",
  },
  {
    slug: "decision-memo",
    surface: "app",
    title: "Decision Memo",
    upstream: "https://decision-memo.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/decision-memo.webp",
    shippedAt: "2026-10-15",
    status: "live",
    featured: true,
    featuredOrder: 25,
    blurb: "Turn a decision question into a one-page memo with options and tradeoffs.",
  },
  {
    slug: "explain-like-five",
    surface: "app",
    title: "Explain Like Five",
    upstream: "https://explain-like-five.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/explain-like-five.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Explain any concept at age 5, teen, or expert level.",
  },
  {
    slug: "socratic-tutor",
    surface: "app",
    title: "Socratic Tutor",
    upstream: "https://socratic-tutor.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/socratic-tutor.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "A tutor that only asks guiding questions.",
  },
  {
    slug: "sql-from-english",
    surface: "app",
    title: "SQL From English",
    upstream: "https://sql-from-english.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/sql-from-english.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "English question plus a schema, written out as SQL.",
  },
  {
    slug: "error-decoder",
    surface: "app",
    title: "Error Decoder",
    upstream: "https://error-decoder.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/error-decoder.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Paste a stack trace. Get the likely cause and a fix you can apply.",
  },
  {
    slug: "fridge-chef",
    surface: "app",
    title: "Fridge Chef",
    upstream: "https://fridge-chef.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/fridge-chef.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "List what's in your fridge. Get a recipe you can cook.",
  },
  {
    slug: "dungeon-master",
    surface: "app",
    title: "Dungeon Master",
    upstream: "https://dungeon-master.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/dungeon-master.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Solo text adventure. You enter a written crypt and play it in the page.",
  },
  {
    slug: "resume-tailor",
    surface: "app",
    title: "Resume Tailor",
    upstream: "https://resume-tailor.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/resume-tailor.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Tailor a resume to a job description.",
  },
  {
    slug: "bill-explainer",
    surface: "app",
    title: "Bill Explainer",
    upstream: "https://bill-explainer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/bill-explainer.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Explain a legislative bill in plain terms.",
  },
  {
    slug: "receipt-reader",
    surface: "app",
    title: "Receipt Reader",
    upstream: "https://receipt-reader.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/receipt-reader.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Photo of a receipt to itemized expense.",
  },
  {
    slug: "whiteboard-cleaner",
    surface: "app",
    title: "Whiteboard Cleaner",
    upstream: "https://whiteboard-cleaner.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/whiteboard-cleaner.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Whiteboard photo to clean notes.",
  },
  {
    slug: "menu-translator",
    surface: "app",
    title: "Menu Translator",
    upstream: "https://menu-translator.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/menu-translator.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Photograph a menu, then read what each dish is and the sentence to say when you order.",
  },
  {
    slug: "merkle-detective",
    surface: "app",
    title: "Merkle Tree Detective",
    upstream: "https://merkle-detective.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/merkle-detective.webp",
    shippedAt: "2026-10-14",
    status: "live",
    blurb: "Tamper with one record and follow the hash up the evidence board to a new root.",
  },
  {
    slug: "pagerank-aquarium",
    surface: "app",
    title: "PageRank Aquarium",
    upstream: "https://pagerank-aquarium.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/pagerank-aquarium.webp",
    shippedAt: "2026-10-14",
    status: "live",
    blurb: "Watch authority drift through a glowing network, one iteration at a time.",
  },
  {
    slug: "hamming-disco",
    surface: "app",
    title: "Error Correction Disco",
    upstream: "https://hamming-disco.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/hamming-disco.webp",
    shippedAt: "2026-10-14",
    status: "live",
    blurb: "Flip bits on the dance floor and watch parity and Hamming codes catch the damage.",
  },
  {
    slug: "spot-the-bot",
    surface: "app",
    title: "Spot the Bot",
    upstream: "https://spot-the-bot.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/spot-the-bot.webp",
    shippedAt: "2026-10-14",
    status: "live",
    blurb: "A dinner-party deduction game: one guest is a bot in disguise. Vote it out.",
  },
  {
    slug: "tidetales",
    surface: "app",
    title: "Tidetales",
    upstream: "https://tidetales.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/tidetales.webp",
    shippedAt: "2026-10-14",
    status: "live",
    blurb: "Bedtime stories with choices that fade into ocean sounds when you drift off.",
  },
  {
    slug: "event-horizon",
    surface: "app",
    title: "Event Horizon",
    upstream: "https://event-horizon.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/event-horizon.webp",
    shippedAt: "2026-10-14",
    status: "live",
    blurb: "Drag a black hole across a starfield and watch gravity bend the sky.",
  },
  {
    slug: "caffeine-clock",
    surface: "app",
    title: "Caffeine Clock",
    upstream: "https://caffeine-clock.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/caffeine-clock.webp",
    shippedAt: "2026-10-14",
    status: "live",
    blurb: "Log your coffees and see how much caffeine is still in you at bedtime.",
  },
  {
    slug: "polywheel",
    surface: "app",
    title: "Polywheel",
    upstream: "https://polywheel.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/polywheel.webp",
    shippedAt: "2026-10-14",
    status: "live",
    blurb: "Spin concentric rings to build polyrhythms and Euclidean beats.",
  },
  {
    slug: "cart-mashup",
    surface: "app",
    title: "Cart Mashup",
    upstream: "https://cart-mashup.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/cart-mashup.webp",
    shippedAt: "2026-10-14",
    status: "live",
    blurb: "Pull a lever to fuse two retro arcade genres into a playable microgame.",
  },
  {
    slug: "boids-arena",
    surface: "app",
    title: "Boids Arena",
    upstream: "https://boids-arena.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/boids-arena.webp",
    shippedAt: "2026-10-14",
    status: "live",
    blurb: "Tune a 600-boid flock live and watch it swarm at 60 fps.",
  },
  {
    slug: "falling-sand",
    surface: "app",
    title: "falling·sand",
    upstream: "https://falling-sand.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/falling-sand.webp",
    shippedAt: "2026-10-14",
    status: "live",
    featured: true,
    featuredOrder: 6,
    blurb: "Paint sand, water, lava and acid and watch them react.",
  },
  {
    slug: "fluid-ink",
    surface: "app",
    title: "fluid·ink",
    upstream: "https://fluid-ink.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/fluid-ink.webp",
    shippedAt: "2026-10-14",
    status: "live",
    blurb: "Paint with glowing, swirling ink in a real-time GPU fluid sim.",
  },
  {
    slug: "json-shape",
    surface: "app",
    title: "json-shape",
    upstream: "https://json-shape.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/json-shape.webp",
    shippedAt: "2026-10-14",
    status: "live",
    blurb: "Paste JSON samples, get a JSON Schema and TypeScript types.",
  },
  {
    slug: "orbit-sandbox",
    surface: "app",
    title: "orbit·sandbox",
    upstream: "https://orbit-sandbox.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/orbit-sandbox.webp",
    shippedAt: "2026-10-14",
    status: "live",
    blurb: "Fling planets, stars and black holes into orbit.",
  },
  {
    slug: "packet-river",
    surface: "app",
    title: "packet-river",
    upstream: "https://packet-river.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/packet-river.webp",
    shippedAt: "2026-10-14",
    status: "live",
    blurb: "Watch TCP congestion control fill and drain a link.",
  },
  {
    slug: "turing-garden",
    surface: "app",
    title: "turing garden",
    upstream: "https://turing-garden.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/turing-garden.webp",
    shippedAt: "2026-10-14",
    status: "live",
    blurb: "Paint with living Turing patterns.",
  },
  {
    slug: "voronoi-garden",
    surface: "app",
    title: "Voronoi Garden",
    upstream: "https://voronoi-garden.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/voronoi-garden.webp",
    shippedAt: "2026-10-14",
    status: "live",
    blurb: "Plant seeds and grow Voronoi diagrams on a canvas.",
  },
  {
    slug: "gpt2-anatomy",
    surface: "app",
    title: "GPT-2 Anatomy",
    upstream: "https://gpt2-anatomy.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/gpt2-anatomy.webp",
    shippedAt: "2026-10-14",
    status: "live",
    blurb: "Six isometric machines that run GPT-2, set by real numbers from the 124M checkpoint.",
  },
  {
    slug: "callsheet",
    surface: "app",
    title: "callsheet",
    upstream: "https://callsheet.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/callsheet.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Billick-style call sheet for AI coding agents: prep in proportion to how often each situation happens.",
  },
  {
    slug: "boss-anatomy",
    surface: "app",
    title: "Encounter Anatomy",
    upstream: "https://boss-anatomy.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/boss-anatomy.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "The Lich King and Gruul drawn as live isometric machines whose motion is the raid encounter's design.",
  },
  // Recipe Table. Same-account workers.dev on digitalnative; static SPA, no auth, storage none.
  {
    slug: "recipetable",
    surface: "app",
    title: "Recipe Table",
    upstream: "https://recipetable.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/recipetable.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Michael Chu's recipe summary table, celebrated: a gallery and a live editor for Gantt-style recipe cards.",
  },
  // Party Circuit. Same-account workers.dev on digitalnative; client-side board game, no auth, storage none.
  {
    slug: "party-circuit",
    surface: "app",
    title: "Party Circuit",
    upstream: "https://party-circuit.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/party-circuit.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "A board-night in the browser: roll, hop a sixteen-tile loop, and buy crowns before the bots do.",
  },
  {
    slug: "vacuum-reviews",
    surface: "app",
    title: "VacuumReviewer",
    upstream: "https://vacuum-reviews.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vacuum-reviews.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Find the best vacuum cleaner for your needs with detailed reviews, comparisons, and buying guides.",
  },
  {
    slug: "video-to-screen",
    surface: "app",
    title: "AI UX Journey Analyzer",
    upstream: "https://video-to-screen.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/video-to-screen.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Turn a screen recording into screens, timelines, and flowcharts.",
  },
  {
    slug: "ofc",
    surface: "app",
    title: "OFC",
    upstream: "https://ofc.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ofc.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Offline desktop file converter for images, audio, video, PDF and archives. Conversion stays on your machine.",
  },
  {
    slug: "chladni",
    surface: "app",
    title: "Chladni",
    upstream: "https://chladni.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/chladni.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Hum or play a tone and watch sand dance into resonance patterns on a plate.",
  },
  {
    slug: "lightspeed-line",
    surface: "app",
    title: "Lightspeed Line",
    upstream: "https://lightspeed-line.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/lightspeed-line.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Ride a train near light speed and watch clocks, lengths and colors warp.",
  },
  {
    slug: "true-scale",
    surface: "app",
    title: "True Scale",
    upstream: "https://true-scale.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/true-scale.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Scroll the solar system at real scale, one pixel per moon.",
  },
  {
    slug: "tiny-evolution",
    surface: "app",
    title: "Tiny Evolution",
    upstream: "https://tiny-evolution.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/tiny-evolution.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Watch blob creatures evolve speed, size and senses across generations.",
  },
  {
    slug: "rotor-cipher",
    surface: "app",
    title: "Rotor Cipher",
    upstream: "https://rotor-cipher.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/rotor-cipher.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "A tactile wartime rotor cipher machine with lamps and stepping wheels.",
  },
  {
    slug: "glitch-sort",
    surface: "app",
    title: "Glitch Sort",
    upstream: "https://glitch-sort.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/glitch-sort.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Pixel-sort your photos into gorgeous glitch art.",
  },
  {
    slug: "shoal",
    surface: "app",
    title: "Shoal",
    upstream: "https://shoal.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/shoal.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Guide a glittering fish school and watch it split around a shark.",
  },
  {
    slug: "drift",
    surface: "app",
    title: "Drift",
    upstream: "https://drift.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/drift.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Slide through 250 million years and watch the continents tear apart.",
  },
  {
    slug: "spiral-chemistry",
    surface: "app",
    title: "Spiral Chemistry",
    upstream: "https://spiral-chemistry.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/spiral-chemistry.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Paint a reaction that grows living spirals and coral patterns.",
  },
  {
    slug: "frostgrow",
    surface: "app",
    title: "Frostgrow",
    upstream: "https://frostgrow.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/frostgrow.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Grow frost-like crystals by diffusion and save them as art prints.",
  },
  {
    slug: "flipper-lab",
    surface: "app",
    title: "Flipper Lab",
    upstream: "https://flipper-lab.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/flipper-lab.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Build your own pinball table, then play it with real physics.",
  },
  {
    slug: "quiz-night-live",
    surface: "app",
    title: "Quiz Night Live",
    upstream: "https://quiz-night-live.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/quiz-night-live.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "A live-show trivia night hosted by a cheeky virtual host with a countdown.",
  },
  {
    slug: "echo-cave",
    surface: "app",
    title: "Echo Cave",
    upstream: "https://echo-cave.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/echo-cave.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Clap and see your sound bounce around a cave you draw.",
  },
  {
    slug: "night-shift-sky",
    surface: "app",
    title: "Night Sky Clock",
    upstream: "https://night-shift-sky.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/night-shift-sky.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Tell time by the stars: a rotating sky that shows constellations rising tonight.",
  },
  // Vocab MCP. Same-account workers.dev on digitalnative; static SPA, no auth, storage none. Repo over9000-apps/vocab-mcp.
  {
    slug: "vocab-mcp",
    surface: "app",
    title: "Vocab MCP",
    upstream: "https://vocab-mcp.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vocab-mcp.webp",
    shippedAt: "2026-10-15",
    status: "live",
    blurb: "Scoreboards, copy-paste configs and a live lookup for dictionary, flashcard, glossary and Anki MCP servers.",
  },
  // Bay Area Over-Asking. Moved from 021-maps (Lake Map). Static Leaflet map on
  // same-account workers.dev (digitalnative). No auth, no storage.
  {
    slug: "bay-area-over-asking",
    surface: "app",
    title: "Bay Area Over-Asking",
    upstream: "https://bay-area-over-asking.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/bay-area-over-asking.webp",
    shippedAt: "2026-10-16",
    status: "live",
    blurb: "ZIP-level map of how far Bay Area homes sell over list price, from Redfin data.",
  },
  {
    slug: "gpt-2-machines",
    surface: "app",
    title: "GPT-2 Machine Workshop",
    upstream: "https://gpt-2-machines.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/gpt-2-machines.webp",
    shippedAt: "2026-10-16",
    status: "live",
    featured: true,
    featuredOrder: 1,
    blurb: "Thirty-two interactive isometric instruments, each running one GPT-2 operation as a live numerical model you can poke.",
  },
  // Software Compare. Static-assets Worker on digitalnative. No auth, no D1.
  // Software Compare. Static Vite SPA on digitalnative R2 techdemos-static
  // (digitalnative workers.dev upstream was CF 1042 / missing Worker at Free cap).
  // Repo over9000-apps/software-compare. No auth, storage none.
  {
    slug: "software-compare",
    surface: "app",
    title: "Software Compare",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/software-compare",
    canonical: "host",
    screenshot: "/shots/software-compare.webp",
    shippedAt: "2026-10-16",
    status: "live",
    blurb: "Compare software side by side from JSON datasets, starting with a tailnet fleet stack.",
  },
  {
    slug: "paint-timeline",
    surface: "app",
    title: "Paint Timeline",
    upstream: "https://paint-timeline.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/paint-timeline.webp",
    shippedAt: "2026-10-16",
    status: "live",
    blurb: "See what painted when: a frame-by-frame paint timeline of over9000.cc loading, or drop in your own screen recording.",
  },
  {
    slug: "x-algorithm-anatomy",
    surface: "app",
    title: "X Algorithm Anatomy",
    upstream: "https://x-algorithm-anatomy.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/x-algorithm-anatomy.webp",
    shippedAt: "2026-10-16",
    status: "live",
    blurb: "The X For You algorithm as a grading machine: hoppers, 17 sieves, a scale of real engagement weights and a gate that keeps the top 50.",
  },
  {
    slug: "flybrain",
    surface: "app",
    title: "Flybrain",
    upstream: "https://flybrain.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/flybrain.webp",
    shippedAt: "2026-10-16",
    status: "live",
    blurb: "Type an instruction; an in-browser language model and a fly-inspired ring attractor steer a robot to it, live.",
  },
  // Mood Wrapped + Gift Radar. Same-account workers.dev on digitalnative; local-first static apps, no auth, storage none.
  {
    slug: "mood-wrapped",
    surface: "app",
    title: "Mood Wrapped",
    upstream: "https://mood-wrapped.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/mood-wrapped.webp",
    shippedAt: "2026-10-16",
    status: "live",
    blurb: "Log a mood a day, then get your year back as a Spotify-Wrapped-style recap.",
  },
  {
    slug: "gift-radar",
    surface: "app",
    title: "Gift Radar",
    upstream: "https://gift-radar.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/gift-radar.webp",
    shippedAt: "2026-10-16",
    status: "live",
    blurb: "Catch gift clues the moment people drop them, so birthdays never sneak up on you.",
  },
  // Suggestion box. Dedicated app D1 (gateway not live). No auth. Same-account workers.dev.
  {
    slug: "suggest",
    surface: "app",
    title: "Suggestion box",
    upstream: "https://suggest.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/suggest.webp",
    shippedAt: "2026-10-17",
    status: "live",
    storage: "dedicated",
    blurb: "Drop an app idea. The public list ranks by votes, then newest.",
  },
  // Sextant. Same-account workers.dev on digitalnative; static PWA (camera + motion sensors, needs HTTPS), no auth, storage none.
  {
    slug: "sextant",
    surface: "app",
    title: "Sextant",
    upstream: "https://sextant.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/sextant.webp",
    shippedAt: "2026-10-16",
    status: "live",
    blurb: "A phone sextant for celestial navigation: sight the sun or stars, reduce the sight, and plot your line of position.",
  },
  {
    slug: "roadster-ramp",
    surface: "app",
    title: "Roadster Ramp",
    upstream: "https://roadster-ramp.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/roadster-ramp.webp",
    shippedAt: "2026-10-16",
    status: "live",
    blurb: "Drive a roadster up the ramp and fire thrusters for altitude — a Godot web game.",
  },
  {
    slug: "sitemap",
    surface: "app",
    title: "Site Map Explorer",
    upstream: "https://sitemap.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/sitemap.webp",
    shippedAt: "2026-10-16",
    status: "live",
    blurb: "Hand it a domain: reads robots.txt and sitemaps, crawls if needed, shows every URL as a tree you can filter and export.",
  },
  // Drain Vortex. Overflow Worker on over9000-pool-000 (digitalnative at its Worker cap);
  // static Next export, no auth, storage none. Repo over9000-apps/drain-vortex.
  {
    slug: "drain-vortex",
    surface: "app",
    title: "Drain Vortex",
    upstream: "https://drain-vortex.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/drain-vortex.webp",
    shippedAt: "2026-10-16",
    status: "live",
    blurb: "Pull the plug and watch a drain vortex form: Navier-Stokes, Burgers and Rankine models with live particles and a sagging free surface.",
  },
  // Diff Diplomat. AI app; Sign in with Over9000 is stubbed in the app
  // (`GET /api/auth/login` → 501 ai_gate_stubbed). Omit `auth` (schema: omit =
  // no IdP client) so id:clients does not project a client or invent redirect
  // URIs, scopes, or secrets. Same-account workers.dev. Repo over9000-apps/diff-diplomat.
  {
    slug: "diff-diplomat",
    surface: "app",
    title: "Diff Diplomat",
    upstream: "https://diff-diplomat.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/diff-diplomat.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Paste a pull request diff and get a plain-English brief for PMs, founders, and reviewers who do not want to read hunks.",
  },
  // Bloom Garden. Same-account workers.dev on digitalnative; static SPA, no
  // auth, storage none. Repo over9000-apps/bloom-garden.
  {
    slug: "bloom-garden",
    surface: "app",
    title: "Bloom Garden",
    upstream: "https://bloom-garden.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/bloom-garden.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Watch a probabilistic set trade perfect accuracy for tiny memory.",
  },
  // Hash Ring Café. Same-account workers.dev on digitalnative; static SPA, no
  // auth, storage none. Repo over9000-apps/hash-ring-cafe.
  {
    slug: "hash-ring-cafe",
    surface: "app",
    title: "Consistent Hashing Café",
    upstream: "https://hash-ring-cafe.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/hash-ring-cafe.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Explore how requests move around a hash ring when servers join or leave.",
  },
  // ASCII Mirror. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/ascii-mirror.
  {
    slug: "ascii-mirror",
    surface: "app",
    title: "ASCII Mirror",
    upstream: "https://ascii-mirror.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/ascii-mirror.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Turn your webcam into live glowing text art you can style and record.",
  },
  // Kaleido. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/kaleido.
  {
    slug: "kaleido",
    surface: "app",
    title: "Kaleido",
    upstream: "https://kaleido.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/kaleido.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Turn your camera or a photo into a living kaleidoscope.",
  },
  // Sprite Loom. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sprite-loom.
  {
    slug: "sprite-loom",
    surface: "app",
    title: "Sprite Loom",
    upstream: "https://sprite-loom.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sprite-loom.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Draw pixel-art sprites with layers, onion-skin animation, palettes, and GIF or PNG sprite sheet export.",
  },
  // Ant Trails. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/ant-trails.
  {
    slug: "ant-trails",
    surface: "app",
    title: "Ant Trails",
    upstream: "https://ant-trails.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/ant-trails.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Drop food and watch an ant colony find the shortest path with pheromones.",
  },
  // Bar Cart. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/bar-cart.
  {
    slug: "bar-cart",
    surface: "app",
    title: "Bar Cart",
    upstream: "https://bar-cart.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/bar-cart.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Tell it which bottles you own and get cocktails you can make tonight.",
  },
  // Paper Plane Lab. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/paper-plane-lab.
  {
    slug: "paper-plane-lab",
    surface: "app",
    title: "Paper Plane Lab",
    upstream: "https://paper-plane-lab.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/paper-plane-lab.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Fold a paper plane step by step, then throw it in a wind tunnel.",
  },
  // Starter Buddy. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sourdough-buddy.
  {
    slug: "sourdough-buddy",
    surface: "app",
    title: "Starter Buddy",
    upstream: "https://sourdough-buddy.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sourdough-buddy.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "A tamagotchi for your sourdough starter that reminds you to feed it.",
  },
  // Mood Weather. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/mood-weather.
  {
    slug: "mood-weather",
    surface: "app",
    title: "Mood Weather",
    upstream: "https://mood-weather.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/mood-weather.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Log your mood as weather and watch your month become a sky.",
  },
  // Tiny Orchestra. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/tiny-orchestra.
  {
    slug: "tiny-orchestra",
    surface: "app",
    title: "Tiny Orchestra",
    upstream: "https://tiny-orchestra.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/tiny-orchestra.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Conduct a band of animal musicians with your finger's tempo.",
  },
  // Cloud Spotter. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/cloud-atlas-game.
  {
    slug: "cloud-atlas-game",
    surface: "app",
    title: "Cloud Spotter",
    upstream: "https://cloud-atlas-game.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/cloud-atlas-game.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Identify the clouds above you in 20 seconds and fill your atlas.",
  },
  // Tiny Planet. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/tiny-planet-builder.
  {
    slug: "tiny-planet-builder",
    surface: "app",
    title: "Tiny Planet",
    upstream: "https://tiny-planet-builder.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/tiny-planet-builder.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Sculpt a little planet and watch weather and life appear on it.",
  },
  // Hand Talk ABC. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sign-language-alphabet.
  {
    slug: "sign-language-alphabet",
    surface: "app",
    title: "Hand Talk ABC",
    upstream: "https://sign-language-alphabet.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sign-language-alphabet.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Learn the fingerspelling alphabet with a friendly practice coach.",
  },
  // Tide Pool. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/tide-pool.
  {
    slug: "tide-pool",
    surface: "app",
    title: "Tide Pool",
    upstream: "https://tide-pool.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/tide-pool.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Explore a tide pool and discover creatures as the water rises and falls.",
  },
  // Loop Station. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/loop-pedal.
  {
    slug: "loop-pedal",
    surface: "app",
    title: "Loop Station",
    upstream: "https://loop-pedal.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/loop-pedal.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Stack mic loops into a song like a street busker.",
  },
  // Street Food Passport. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/street-food-passport.
  {
    slug: "street-food-passport",
    surface: "app",
    title: "Street Food Passport",
    upstream: "https://street-food-passport.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/street-food-passport.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Stamp a passport for every street food you try around the world.",
  },
  // Chaos Pendulum. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/chaos-pendulum.
  {
    slug: "chaos-pendulum",
    surface: "app",
    title: "Chaos Pendulum",
    upstream: "https://chaos-pendulum.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/chaos-pendulum.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Release two double pendulums a hair apart and watch them diverge.",
  },
  // Word Garden. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/word-garden.
  {
    slug: "word-garden",
    surface: "app",
    title: "Word Garden",
    upstream: "https://word-garden.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/word-garden.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Every word you journal grows into a plant in your garden.",
  },
  // Heist Planner. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/heist-planner.
  {
    slug: "heist-planner",
    surface: "app",
    title: "Heist Planner",
    upstream: "https://heist-planner.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/heist-planner.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Plan a cartoon heist with gadgets, then watch the run play out.",
  },
  // Hush Mixer. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sleep-sound-mixer.
  {
    slug: "sleep-sound-mixer",
    surface: "app",
    title: "Hush Mixer",
    upstream: "https://sleep-sound-mixer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sleep-sound-mixer.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Mix your own sleep soundscape from rain, fans, trains and waves.",
  },
  // Pocket Museum. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/pocket-museum.
  {
    slug: "pocket-museum",
    surface: "app",
    title: "Pocket Museum",
    upstream: "https://pocket-museum.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/pocket-museum.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Curate a tiny museum of your own objects with placards.",
  },
  // Ghost Typist. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/typing-race-ghosts.
  {
    slug: "typing-race-ghosts",
    surface: "app",
    title: "Ghost Typist",
    upstream: "https://typing-race-ghosts.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/typing-race-ghosts.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Race your own past self in a typing race on quotes.",
  },
  // Hum Tuner. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/hum-tuner.
  {
    slug: "hum-tuner",
    surface: "app",
    title: "Hum Tuner",
    upstream: "https://hum-tuner.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/hum-tuner.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Hum a note and see how in tune you are.",
  },
  // Map Folds. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/map-folds.
  {
    slug: "map-folds",
    surface: "app",
    title: "Map Folds",
    upstream: "https://map-folds.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/map-folds.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Solve the impossible paper-map folding puzzle.",
  },
  // Signal Flags. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/signal-flags.
  {
    slug: "signal-flags",
    surface: "app",
    title: "Signal Flags",
    upstream: "https://signal-flags.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/signal-flags.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Spell messages in maritime signal flags.",
  },
  // Ferment Log. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/ferment-log.
  {
    slug: "ferment-log",
    surface: "app",
    title: "Ferment Log",
    upstream: "https://ferment-log.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/ferment-log.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Track sourdough, kimchi and kombucha with a fermentation countdown dashboard.",
  },
  // Braille Pad. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/braille-pad.
  {
    slug: "braille-pad",
    surface: "app",
    title: "Braille Pad",
    upstream: "https://braille-pad.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/braille-pad.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Learn and type Braille on six big keys.",
  },
  // Knot Book. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/knot-book.
  {
    slug: "knot-book",
    surface: "app",
    title: "Knot Book",
    upstream: "https://knot-book.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/knot-book.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Scrub through animated knots at your own pace.",
  },
  // Specimen. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/type-specimen.
  {
    slug: "type-specimen",
    surface: "app",
    title: "Specimen",
    upstream: "https://type-specimen.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/type-specimen.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Make a type specimen poster in a minute.",
  },
  // Chirp Quiz. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/bird-call-quiz.
  {
    slug: "bird-call-quiz",
    surface: "app",
    title: "Chirp Quiz",
    upstream: "https://bird-call-quiz.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/bird-call-quiz.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Learn bird songs with synthesized calls.",
  },
  // Snow Day. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/snow-day.
  {
    slug: "snow-day",
    surface: "app",
    title: "Snow Day",
    upstream: "https://snow-day.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/snow-day.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Design a snowflake, then watch it fall with others.",
  },
  // Lighthouse Keeper. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/lighthouse-keeper.
  {
    slug: "lighthouse-keeper",
    surface: "app",
    title: "Lighthouse Keeper",
    upstream: "https://lighthouse-keeper.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/lighthouse-keeper.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Keep the light turning through a stormy night.",
  },
  // Gear Train. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/gear-train.
  {
    slug: "gear-train",
    surface: "app",
    title: "Gear Train",
    upstream: "https://gear-train.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/gear-train.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Build gear trains and hit a target RPM.",
  },
  // Through Their Eyes. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/color-blind-sim.
  {
    slug: "color-blind-sim",
    surface: "app",
    title: "Through Their Eyes",
    upstream: "https://color-blind-sim.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/color-blind-sim.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "See how your image looks to people with color blindness.",
  },
  // Tiny Town Budget. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/tiny-towns-tax.
  {
    slug: "tiny-towns-tax",
    surface: "app",
    title: "Tiny Town Budget",
    upstream: "https://tiny-towns-tax.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/tiny-towns-tax.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Balance a toy town's budget and watch it react.",
  },
  // Lunch Roulette. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/lunch-roulette.
  {
    slug: "lunch-roulette",
    surface: "app",
    title: "Lunch Roulette",
    upstream: "https://lunch-roulette.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/lunch-roulette.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Spin a wheel for the team lunch, with vetoes.",
  },
  // Pixel Plant. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/pixel-plant.
  {
    slug: "pixel-plant",
    surface: "app",
    title: "Pixel Plant",
    upstream: "https://pixel-plant.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/pixel-plant.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "A pixel houseplant that needs real attention.",
  },
  // Sound Garden. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sound-garden.
  {
    slug: "sound-garden",
    surface: "app",
    title: "Sound Garden",
    upstream: "https://sound-garden.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sound-garden.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Plant seeds that play loops.",
  },
  // Snowball. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/debt-snowball.
  {
    slug: "debt-snowball",
    surface: "app",
    title: "Snowball",
    upstream: "https://debt-snowball.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/debt-snowball.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "See exactly when you'll be debt-free.",
  },
  // Overlap. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/time-zone-meet.
  {
    slug: "time-zone-meet",
    surface: "app",
    title: "Overlap",
    upstream: "https://time-zone-meet.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/time-zone-meet.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Find the sane meeting hour across time zones.",
  },
  // Ripple Tank. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/ripple-tank.
  {
    slug: "ripple-tank",
    surface: "app",
    title: "Ripple Tank",
    upstream: "https://ripple-tank.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/ripple-tank.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Wave interference you can poke.",
  },
  // Haiku Loom. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/haiku-loom.
  {
    slug: "haiku-loom",
    surface: "app",
    title: "Haiku Loom",
    upstream: "https://haiku-loom.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/haiku-loom.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Weave a haiku with syllable-aware tiles.",
  },
  // Meter Maid. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/parking-meter.
  {
    slug: "parking-meter",
    surface: "app",
    title: "Meter Maid",
    upstream: "https://parking-meter.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/parking-meter.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Track your parking meter with a countdown timer.",
  },
  // Aroma Wheel. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/wine-wheel.
  {
    slug: "wine-wheel",
    surface: "app",
    title: "Aroma Wheel",
    upstream: "https://wine-wheel.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/wine-wheel.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Name what you're tasting.",
  },
  // Ghost Signs. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/ghost-signs.
  {
    slug: "ghost-signs",
    surface: "app",
    title: "Ghost Signs",
    upstream: "https://ghost-signs.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/ghost-signs.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Collect faded hand-painted wall ads on a city map.",
  },
  // Domino Run. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/domino-run.
  {
    slug: "domino-run",
    surface: "app",
    title: "Domino Run",
    upstream: "https://domino-run.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/domino-run.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Draw a domino path and knock it down in slow motion.",
  },
  // Tide Clock. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/tide-clock.
  {
    slug: "tide-clock",
    surface: "app",
    title: "Tide Clock",
    upstream: "https://tide-clock.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/tide-clock.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "A living tide chart for any coast.",
  },
  // Mini Cross. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/crossword-mini.
  {
    slug: "crossword-mini",
    surface: "app",
    title: "Mini Cross",
    upstream: "https://crossword-mini.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/crossword-mini.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "A daily 5x5 crossword.",
  },
  // Plot. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/garden-planner.
  {
    slug: "garden-planner",
    surface: "app",
    title: "Plot",
    upstream: "https://garden-planner.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/garden-planner.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Plan a raised bed with companion planting.",
  },
  // Dit Dah. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/morse-chat.
  {
    slug: "morse-chat",
    surface: "app",
    title: "Dit Dah",
    upstream: "https://morse-chat.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/morse-chat.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Tap Morse with one button and decode live.",
  },
  // Dice Forge. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/dice-forge.
  {
    slug: "dice-forge",
    surface: "app",
    title: "Dice Forge",
    upstream: "https://dice-forge.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/dice-forge.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Roll any dice expression with physics.",
  },
  // Coffee vs Compound. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/tiny-loans.
  {
    slug: "tiny-loans",
    surface: "app",
    title: "Coffee vs Compound",
    upstream: "https://tiny-loans.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/tiny-loans.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "See what your daily habit costs over 30 years.",
  },
  // Booth. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/photo-booth-strip.
  {
    slug: "photo-booth-strip",
    surface: "app",
    title: "Booth",
    upstream: "https://photo-booth-strip.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/photo-booth-strip.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Make a 4-shot photo booth strip in your browser.",
  },
  // Box Breath. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/calm-breath.
  {
    slug: "calm-breath",
    surface: "app",
    title: "Box Breath",
    upstream: "https://calm-breath.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/calm-breath.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Follow guided breathing with a shape that breathes.",
  },
  // Riddle Door. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/riddle-door.
  {
    slug: "riddle-door",
    surface: "app",
    title: "Riddle Door",
    upstream: "https://riddle-door.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/riddle-door.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Answer the door's riddle to go deeper.",
  },
  // Fretboard. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/guitar-chords.
  {
    slug: "guitar-chords",
    surface: "app",
    title: "Fretboard",
    upstream: "https://guitar-chords.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/guitar-chords.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Find any chord shape on a fretboard.",
  },
  // Lemonade Empire. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/market-sim.
  {
    slug: "market-sim",
    surface: "app",
    title: "Lemonade Empire",
    upstream: "https://market-sim.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/market-sim.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Run a lemonade stand against the weather.",
  },
  // Inkblot. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/ink-blot.
  {
    slug: "ink-blot",
    surface: "app",
    title: "Inkblot",
    upstream: "https://ink-blot.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/ink-blot.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Make a symmetric inkblot and caption it.",
  },
  // Flash. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/speed-read.
  {
    slug: "speed-read",
    surface: "app",
    title: "Flash",
    upstream: "https://speed-read.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/speed-read.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Read faster with rapid serial word display.",
  },
  // Sun Roof. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/solar-sizer.
  {
    slug: "solar-sizer",
    surface: "app",
    title: "Sun Roof",
    upstream: "https://solar-sizer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/solar-sizer.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Find out how many solar panels fit your roof.",
  },
  // Terrarium. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/terrarium.
  {
    slug: "terrarium",
    surface: "app",
    title: "Terrarium",
    upstream: "https://terrarium.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/terrarium.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Build a sealed ecosystem and watch the cycle.",
  },
  // One Line. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/one-line-draw.
  {
    slug: "one-line-draw",
    surface: "app",
    title: "One Line",
    upstream: "https://one-line-draw.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/one-line-draw.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Draw anything without lifting the pen.",
  },
  // Fair Rent. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/rent-split.
  {
    slug: "rent-split",
    surface: "app",
    title: "Fair Rent",
    upstream: "https://rent-split.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/rent-split.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Split rent by room size and perks, fairly and transparently.",
  },
  // Focus Forest. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/pomodoro-tree.
  {
    slug: "pomodoro-tree",
    surface: "app",
    title: "Focus Forest",
    upstream: "https://pomodoro-tree.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/pomodoro-tree.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Each focus session grows a tree on your hill.",
  },
  // Progression. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/chord-progressor.
  {
    slug: "chord-progressor",
    surface: "app",
    title: "Progression",
    upstream: "https://chord-progressor.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/chord-progressor.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Build chord progressions that sound good.",
  },
  // Bug Tarot. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/tarot-code.
  {
    slug: "tarot-code",
    surface: "app",
    title: "Bug Tarot",
    upstream: "https://tarot-code.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/tarot-code.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Draw tarot cards for your debugging session.",
  },
  // Weather Verse. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/weather-poem.
  {
    slug: "weather-poem",
    surface: "app",
    title: "Weather Verse",
    upstream: "https://weather-poem.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/weather-poem.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Your forecast, written as a short poem.",
  },
  // Beaker. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/chemistry-mixer.
  {
    slug: "chemistry-mixer",
    surface: "app",
    title: "Beaker",
    upstream: "https://chemistry-mixer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/chemistry-mixer.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Mix elements and discover compounds.",
  },
  // Line Map. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/subway-maker.
  {
    slug: "subway-maker",
    surface: "app",
    title: "Line Map",
    upstream: "https://subway-maker.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/subway-maker.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Draw a transit map like a designer.",
  },
  // Fair Tip. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/tip-calc-plus.
  {
    slug: "tip-calc-plus",
    surface: "app",
    title: "Fair Tip",
    upstream: "https://tip-calc-plus.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/tip-calc-plus.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Split a restaurant bill with tips and shared dishes.",
  },
  // Plate Spinner. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/plate-spinner.
  {
    slug: "plate-spinner",
    surface: "app",
    title: "Plate Spinner",
    upstream: "https://plate-spinner.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/plate-spinner.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Keep every plate spinning.",
  },
  // Memory Palace. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/memory-palace.
  {
    slug: "memory-palace",
    surface: "app",
    title: "Memory Palace",
    upstream: "https://memory-palace.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/memory-palace.webp",
    shippedAt: "2026-10-17",
    status: "live",
    blurb: "Memorize lists by walking through rooms.",
  },
  // Cadence. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/bike-gear.
  {
    slug: "bike-gear",
    surface: "app",
    title: "Cadence",
    upstream: "https://bike-gear.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/bike-gear.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Pick the right bike gear ratios.",
  },
  // Ladder. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/word-ladder.
  {
    slug: "word-ladder",
    surface: "app",
    title: "Ladder",
    upstream: "https://word-ladder.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/word-ladder.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Change one letter at a time to climb from word to word.",
  },
  // Lanterns. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/lantern-sky.
  {
    slug: "lantern-sky",
    surface: "app",
    title: "Lanterns",
    upstream: "https://lantern-sky.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/lantern-sky.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Release a wish lantern into a night sky full of others.",
  },
  // Attractor Loom. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/attractor-loom.
  {
    slug: "attractor-loom",
    surface: "app",
    title: "Attractor Loom",
    upstream: "https://attractor-loom.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/attractor-loom.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Create strange-attractor art with millions of glowing points, sliders and a surprise-me button.",
  },
  // Golden Hour. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/golden-hour.
  {
    slug: "golden-hour",
    surface: "app",
    title: "Golden Hour",
    upstream: "https://golden-hour.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/golden-hour.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "See sunrise, sunset, golden and blue hour for any place and day, with an animated sky and sun arc.",
  },
  // L-Forest. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/l-forest.
  {
    slug: "l-forest",
    surface: "app",
    title: "L-Forest",
    upstream: "https://l-forest.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/l-forest.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Grow procedural trees and parallax forests from editable L-system rules, with wind and seasons.",
  },
  // Mandel Dive. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/mandel-dive.
  {
    slug: "mandel-dive",
    surface: "app",
    title: "Mandel Dive",
    upstream: "https://mandel-dive.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/mandel-dive.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Explore GPU-rendered Mandelbrot and Julia sets with deep zoom, palette cycling and shareable views.",
  },
  // Planet Forge. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/planet-forge.
  {
    slug: "planet-forge",
    surface: "app",
    title: "Planet Forge",
    upstream: "https://planet-forge.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/planet-forge.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Generate procedural planets with biomes, oceans, clouds and rings, then orbit and export them.",
  },
  // Split Flap. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/split-flap.
  {
    slug: "split-flap",
    surface: "app",
    title: "Split Flap",
    upstream: "https://split-flap.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/split-flap.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Watch your message clatter onto an airport-style split-flap board, with sound and shareable links.",
  },
  // Invoice Sketch. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/invoice-sketch.
  {
    slug: "invoice-sketch",
    surface: "app",
    title: "Invoice Sketch",
    upstream: "https://invoice-sketch.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/invoice-sketch.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Make a printable invoice in a minute with line items, tax, discounts, a logo, saved clients and PDF export.",
  },
  // Unicode X-Ray. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/unicode-xray.
  {
    slug: "unicode-xray",
    surface: "app",
    title: "Unicode X-Ray",
    upstream: "https://unicode-xray.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/unicode-xray.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Paste text to inspect every character: code points, names, invisible characters and look-alikes.",
  },
  // Race Pace. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/race-pace.
  {
    slug: "race-pace",
    surface: "app",
    title: "Race Pace",
    upstream: "https://race-pace.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/race-pace.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Calculate running pace, splits and finish times for any distance, with negative splits and printable pace bands.",
  },
  // Paint Mixer. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/paint-mixer.
  {
    slug: "paint-mixer",
    surface: "app",
    title: "Paint Mixer",
    upstream: "https://paint-mixer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/paint-mixer.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Mix virtual paints with realistic subtractive blending and find a recipe to match any target color.",
  },
  // Tap Tempo. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/tap-tempo.
  {
    slug: "tap-tempo",
    surface: "app",
    title: "Tap Tempo",
    upstream: "https://tap-tempo.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/tap-tempo.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Tap to find a song's BPM, then practice with an accented metronome, subdivisions and a tempo trainer.",
  },
  // Passphrase Forge. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/passphrase-forge.
  {
    slug: "passphrase-forge",
    surface: "app",
    title: "Passphrase Forge",
    upstream: "https://passphrase-forge.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/passphrase-forge.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Generate memorable diceware-style passphrases offline with a live entropy meter and crack-time estimate.",
  },
  // Moon Calendar. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/moon-calendar.
  {
    slug: "moon-calendar",
    surface: "app",
    title: "Moon Calendar",
    upstream: "https://moon-calendar.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/moon-calendar.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Explore a monthly moon phase calendar with rise and set times, illumination and the next full and new moon.",
  },
  // Star Wheel. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/star-wheel.
  {
    slug: "star-wheel",
    surface: "app",
    title: "Star Wheel",
    upstream: "https://star-wheel.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/star-wheel.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Dial in your latitude, date and time on a digital planisphere to see which constellations are overhead.",
  },
  // QR Studio. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/qr-studio.
  {
    slug: "qr-studio",
    surface: "app",
    title: "QR Studio",
    upstream: "https://qr-studio.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/qr-studio.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Design QR codes offline for URLs, Wi-Fi, contacts and text, with colors, logos, error correction and PNG or SVG export.",
  },
  // Contrast Fixer. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/contrast-fixer.
  {
    slug: "contrast-fixer",
    surface: "app",
    title: "Contrast Fixer",
    upstream: "https://contrast-fixer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/contrast-fixer.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Check text and background colors against WCAG and find the nearest accessible colors with one click.",
  },
  // Bitmap Font Lab. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/bitmap-font-lab.
  {
    slug: "bitmap-font-lab",
    surface: "app",
    title: "Bitmap Font Lab",
    upstream: "https://bitmap-font-lab.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/bitmap-font-lab.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Draw a pixel font glyph by glyph, preview live sample text and export a PNG sheet or JSON.",
  },
  // Seating Chart. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/seating-chart.
  {
    slug: "seating-chart",
    surface: "app",
    title: "Seating Chart",
    upstream: "https://seating-chart.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/seating-chart.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Drag guests onto tables, set must-sit-together and keep-apart rules, and auto-solve the seating plan.",
  },
  // Chore Wheel. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/chore-wheel.
  {
    slug: "chore-wheel",
    surface: "app",
    title: "Chore Wheel",
    upstream: "https://chore-wheel.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/chore-wheel.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Create a fair rotating chore schedule for roommates or families, with weights, streaks and a printable weekly chart.",
  },
  // Packing Pal. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/packing-pal.
  {
    slug: "packing-pal",
    surface: "app",
    title: "Packing Pal",
    upstream: "https://packing-pal.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/packing-pal.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Build a smart packing list from trip length, climate and activities, then tick items off as you pack.",
  },
  // Sudoku Coach. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sudoku-coach.
  {
    slug: "sudoku-coach",
    surface: "app",
    title: "Sudoku Coach",
    upstream: "https://sudoku-coach.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sudoku-coach.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Play sudoku with a coach that explains the next logical step: singles, pairs, pointing and X-wings.",
  },
  // Life Lab. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/life-lab.
  {
    slug: "life-lab",
    surface: "app",
    title: "Life Lab",
    upstream: "https://life-lab.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/life-lab.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Explore Conway's Game of Life with a pattern library, B/S rule editor, infinite pan and zoom.",
  },
  // Kerning Duel. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/kerning-duel.
  {
    slug: "kerning-duel",
    surface: "app",
    title: "Kerning Duel",
    upstream: "https://kerning-duel.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/kerning-duel.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Train your typographic eye by spacing letters by hand, then see how close you got to the pro.",
  },
  // Unit Circle. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/unit-circle.
  {
    slug: "unit-circle",
    surface: "app",
    title: "Unit Circle",
    upstream: "https://unit-circle.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/unit-circle.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Drag a point around the unit circle and watch sine, cosine and tangent trace their waves in sync.",
  },
  // Bonsai Time. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/bonsai-time.
  {
    slug: "bonsai-time",
    surface: "app",
    title: "Bonsai Time",
    upstream: "https://bonsai-time.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/bonsai-time.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Grow a bonsai, one prune a day.",
  },
  // Sundial. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sun-dial.
  {
    slug: "sun-dial",
    surface: "app",
    title: "Sundial",
    upstream: "https://sun-dial.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sun-dial.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Make a sundial for your latitude.",
  },
  // Pinball Sketch. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/pinball-sketch.
  {
    slug: "pinball-sketch",
    surface: "app",
    title: "Pinball Sketch",
    upstream: "https://pinball-sketch.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/pinball-sketch.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Draw your own pinball table with a pen, then play it with real physics.",
  },
  // Cloud Namer. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/cloud-namer.
  {
    slug: "cloud-namer",
    surface: "app",
    title: "Cloud Namer",
    upstream: "https://cloud-namer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/cloud-namer.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Generate dreamy clouds and name the shapes you see in them.",
  },
  // Spice Rack. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/spice-rack.
  {
    slug: "spice-rack",
    surface: "app",
    title: "Spice Rack",
    upstream: "https://spice-rack.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/spice-rack.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Pick a dish and get balanced spice blends you can actually mix at home.",
  },
  // Orbit Golf. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/orbit-golf.
  {
    slug: "orbit-golf",
    surface: "app",
    title: "Orbit Golf",
    upstream: "https://orbit-golf.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/orbit-golf.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Play mini golf in space: slingshot a ball around planets into the hole.",
  },
  // Noise Scape. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/noise-scape.
  {
    slug: "noise-scape",
    surface: "app",
    title: "Noise Scape",
    upstream: "https://noise-scape.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/noise-scape.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Paint a landscape of ambient sound with layered generative noise.",
  },
  // Font Pair. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/font-pair.
  {
    slug: "font-pair",
    surface: "app",
    title: "Font Pair",
    upstream: "https://font-pair.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/font-pair.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Find heading and body font pairs that look great together, with system and self-hosted fonts.",
  },
  // Plant Waterer. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/plant-waterer.
  {
    slug: "plant-waterer",
    surface: "app",
    title: "Plant Waterer",
    upstream: "https://plant-waterer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/plant-waterer.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Track a tiny watering schedule for your houseplants and see who is thirsty today.",
  },
  // Rhythm Grid. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/rhythm-grid.
  {
    slug: "rhythm-grid",
    surface: "app",
    title: "Rhythm Grid",
    upstream: "https://rhythm-grid.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/rhythm-grid.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Play a 16-step drum machine with synthesized kits in your browser.",
  },
  // Cipher Wheel. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/cipher-wheel.
  {
    slug: "cipher-wheel",
    surface: "app",
    title: "Cipher Wheel",
    upstream: "https://cipher-wheel.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/cipher-wheel.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Encode and decode secret messages with classic ciphers and a spinning wheel.",
  },
  // Lava Lamp. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/lava-lamp.
  {
    slug: "lava-lamp",
    surface: "app",
    title: "Lava Lamp",
    upstream: "https://lava-lamp.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/lava-lamp.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Customize a relaxing metaball lava lamp and leave it on.",
  },
  // Fractal Tree. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/fractal-tree.
  {
    slug: "fractal-tree",
    surface: "app",
    title: "Fractal Tree",
    upstream: "https://fractal-tree.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/fractal-tree.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Grow recursive trees with sliders and watch them sway in the wind.",
  },
  // Tangram Play. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/tangram-play.
  {
    slug: "tangram-play",
    surface: "app",
    title: "Tangram Play",
    upstream: "https://tangram-play.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/tangram-play.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Solve classic tangram silhouettes by dragging and rotating seven pieces.",
  },
  // Kite Flyer. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/kite-flyer.
  {
    slug: "kite-flyer",
    surface: "app",
    title: "Kite Flyer",
    upstream: "https://kite-flyer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/kite-flyer.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Fly a kite through gusty skies and keep it aloft with string tension.",
  },
  // Galaxy Paint. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/galaxy-paint.
  {
    slug: "galaxy-paint",
    surface: "app",
    title: "Galaxy Paint",
    upstream: "https://galaxy-paint.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/galaxy-paint.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Paint swirling galaxies with particle brushes.",
  },
  // Mortgage Mate. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/mortgage-mate.
  {
    slug: "mortgage-mate",
    surface: "app",
    title: "Mortgage Mate",
    upstream: "https://mortgage-mate.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/mortgage-mate.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "See exactly what a home loan costs, month by month, with extra payment what-ifs.",
  },
  // Bubble Wrap. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/bubble-wrap.
  {
    slug: "bubble-wrap",
    surface: "app",
    title: "Bubble Wrap",
    upstream: "https://bubble-wrap.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/bubble-wrap.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Pop endless bubble wrap with satisfying sounds.",
  },
  // Recipe Scaler. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/recipe-scaler.
  {
    slug: "recipe-scaler",
    surface: "app",
    title: "Recipe Scaler",
    upstream: "https://recipe-scaler.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/recipe-scaler.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Paste a recipe and scale it to any number of servings with clean units.",
  },
  // Star Trace. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/star-trace.
  {
    slug: "star-trace",
    surface: "app",
    title: "Star Trace",
    upstream: "https://star-trace.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/star-trace.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Connect the stars to draw constellations and learn their stories.",
  },
  // Ice Cream Lab. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/ice-cream-lab.
  {
    slug: "ice-cream-lab",
    surface: "app",
    title: "Ice Cream Lab",
    upstream: "https://ice-cream-lab.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/ice-cream-lab.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Design your dream ice cream cone, scoop by scoop.",
  },
  // Wave Maker. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/wave-maker.
  {
    slug: "wave-maker",
    surface: "app",
    title: "Wave Maker",
    upstream: "https://wave-maker.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/wave-maker.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Build sound from sine waves and see how harmonics shape tone.",
  },
  // Pixel Quilt. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/pixel-quilt.
  {
    slug: "pixel-quilt",
    surface: "app",
    title: "Pixel Quilt",
    upstream: "https://pixel-quilt.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/pixel-quilt.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Design patchwork quilt patterns block by block.",
  },
  // Reaction Time. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/reaction-time.
  {
    slug: "reaction-time",
    surface: "app",
    title: "Reaction Time",
    upstream: "https://reaction-time.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/reaction-time.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Test your reflexes with quick tap challenges and track your best.",
  },
  // Maze Maker. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/maze-maker.
  {
    slug: "maze-maker",
    surface: "app",
    title: "Maze Maker",
    upstream: "https://maze-maker.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/maze-maker.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Generate mazes, solve them, and print them for kids.",
  },
  // Coffee Ratio. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/coffee-ratio.
  {
    slug: "coffee-ratio",
    surface: "app",
    title: "Coffee Ratio",
    upstream: "https://coffee-ratio.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/coffee-ratio.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Dial in pour-over, espresso and French press with a brew ratio calculator and timer.",
  },
  // Emoji Mosaic. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/emoji-mosaic.
  {
    slug: "emoji-mosaic",
    surface: "app",
    title: "Emoji Mosaic",
    upstream: "https://emoji-mosaic.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/emoji-mosaic.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Turn any photo into a mosaic made of emoji.",
  },
  // Desk Stretch. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/desk-stretch.
  {
    slug: "desk-stretch",
    surface: "app",
    title: "Desk Stretch",
    upstream: "https://desk-stretch.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/desk-stretch.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Guided two-minute stretch breaks for people stuck at a desk.",
  },
  // City Skyline. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/city-skyline.
  {
    slug: "city-skyline",
    surface: "app",
    title: "City Skyline",
    upstream: "https://city-skyline.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/city-skyline.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Generate pixel-art city skylines with day and night cycles.",
  },
  // Budget Jars. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/budget-jars.
  {
    slug: "budget-jars",
    surface: "app",
    title: "Budget Jars",
    upstream: "https://budget-jars.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/budget-jars.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Split your income into jars and watch them fill as you spend.",
  },
  // Tongue Twister. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/tongue-twister.
  {
    slug: "tongue-twister",
    surface: "app",
    title: "Tongue Twister",
    upstream: "https://tongue-twister.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/tongue-twister.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Practice tongue twisters with a speed trainer and a timer that never records you.",
  },
  // Grid Paint. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/grid-paint.
  {
    slug: "grid-paint",
    surface: "app",
    title: "Grid Paint",
    upstream: "https://grid-paint.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/grid-paint.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Make cross-stitch and perler bead patterns from a grid.",
  },
  // Time Capsule. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/time-capsule.
  {
    slug: "time-capsule",
    surface: "app",
    title: "Time Capsule",
    upstream: "https://time-capsule.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/time-capsule.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Write a note to your future self that unlocks on a date you choose.",
  },
  // Sound Bath. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sound-bath.
  {
    slug: "sound-bath",
    surface: "app",
    title: "Sound Bath",
    upstream: "https://sound-bath.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sound-bath.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Play virtual singing bowls and chimes for a calm sound bath.",
  },
  // Tiny Zoo. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/tiny-zoo.
  {
    slug: "tiny-zoo",
    surface: "app",
    title: "Tiny Zoo",
    upstream: "https://tiny-zoo.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/tiny-zoo.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Build a pocket zoo of procedurally generated critters.",
  },
  // Paper Snowflake. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/paper-snowflake.
  {
    slug: "paper-snowflake",
    surface: "app",
    title: "Paper Snowflake",
    upstream: "https://paper-snowflake.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/paper-snowflake.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Fold and cut virtual paper to make unique snowflakes.",
  },
  // Quote Card. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/quote-card.
  {
    slug: "quote-card",
    surface: "app",
    title: "Quote Card",
    upstream: "https://quote-card.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/quote-card.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Make beautiful quote cards for sharing in seconds.",
  },
  // Dino Dig. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/dino-dig.
  {
    slug: "dino-dig",
    surface: "app",
    title: "Dino Dig",
    upstream: "https://dino-dig.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/dino-dig.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Excavate fossils layer by layer and assemble the skeleton.",
  },
  // Larder. Overflow Worker on over9000-pool-000 (digitalnative at its Worker cap) with a dedicated
  // D1 database `larder` on the same account (463,866 recipes, FTS5). Read-only, no auth. Repo over9000-apps/larder.
  {
    slug: "larder",
    surface: "app",
    title: "Larder",
    upstream: "https://larder.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/larder.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Search 463,866 recipes from 73 recipe sites by title or ingredient, with full steps and provenance for each.",
  },
  // Word Clock. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/word-clock.
  {
    slug: "word-clock",
    surface: "app",
    title: "Word Clock",
    upstream: "https://word-clock.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/word-clock.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "A clock that spells out the time in words on a letter grid.",
  },
  // Lights Out. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/light-bulb-puzzle.
  {
    slug: "light-bulb-puzzle",
    surface: "app",
    title: "Lights Out",
    upstream: "https://light-bulb-puzzle.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/light-bulb-puzzle.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "The classic lights-out puzzle with levels and a solver hint.",
  },
  // Color Mixer. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/color-mixer.
  {
    slug: "color-mixer",
    surface: "app",
    title: "Color Mixer",
    upstream: "https://color-mixer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/color-mixer.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Mix paint-like colors and learn how pigments blend.",
  },
  // Sleep Cycle. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sleep-cycle.
  {
    slug: "sleep-cycle",
    surface: "app",
    title: "Sleep Cycle",
    upstream: "https://sleep-cycle.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sleep-cycle.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Find the best times to fall asleep or wake up based on 90-minute cycles.",
  },
  // Campfire. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/fire-sim.
  {
    slug: "fire-sim",
    surface: "app",
    title: "Campfire",
    upstream: "https://fire-sim.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/fire-sim.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "A cozy pixel campfire you can stoke, with crackling synthesized sound.",
  },
  // Domino Math. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/domino-math.
  {
    slug: "domino-math",
    surface: "app",
    title: "Domino Math",
    upstream: "https://domino-math.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/domino-math.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Practice arithmetic by matching domino tiles.",
  },
  // Bird Song. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/bird-song.
  {
    slug: "bird-song",
    surface: "app",
    title: "Bird Song",
    upstream: "https://bird-song.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/bird-song.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Compose melodies with birds sitting on power lines.",
  },
  // Abacus Coach. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/abacus-coach.
  {
    slug: "abacus-coach",
    surface: "app",
    title: "Abacus Coach",
    upstream: "https://abacus-coach.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/abacus-coach.webp",
    shippedAt: "2026-10-18",
    status: "live",
    blurb: "Learn to count and calculate on a virtual soroban abacus.",
  },
  // Snow Globe. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/snow-globe.
  {
    slug: "snow-globe",
    surface: "app",
    title: "Snow Globe",
    upstream: "https://snow-globe.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/snow-globe.webp",
    shippedAt: "2026-10-19",
    status: "live",
    blurb: "Shake a 3D snow globe and build the tiny winter scene inside it.",
  },
  // Rubber Duck. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/rubber-duck.
  {
    slug: "rubber-duck",
    surface: "app",
    title: "Rubber Duck",
    upstream: "https://rubber-duck.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/rubber-duck.webp",
    shippedAt: "2026-10-19",
    status: "live",
    blurb: "Talk your bug through to a patient rubber duck that asks the right questions.",
  },
  // Marble Run. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/marble-run.
  {
    slug: "marble-run",
    surface: "app",
    title: "Marble Run",
    upstream: "https://marble-run.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/marble-run.webp",
    shippedAt: "2026-10-19",
    status: "live",
    blurb: "Snap together ramps, funnels and loops, then drop marbles and watch them race.",
  },
  // Fortune Cookie. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/fortune-cookie.
  {
    slug: "fortune-cookie",
    surface: "app",
    title: "Fortune Cookie",
    upstream: "https://fortune-cookie.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/fortune-cookie.webp",
    shippedAt: "2026-10-19",
    status: "live",
    blurb: "Crack open a cookie for a daily fortune, lucky numbers and a saved collection.",
  },
  // Neon Sign. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/neon-sign.
  {
    slug: "neon-sign",
    surface: "app",
    title: "Neon Sign",
    upstream: "https://neon-sign.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/neon-sign.webp",
    shippedAt: "2026-10-19",
    status: "live",
    blurb: "Design a glowing neon sign with flicker, tubes and a brick wall, then save it as an image.",
  },
  // Booth Footprint. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/booth-footprint.
  {
    slug: "booth-footprint",
    surface: "app",
    title: "Booth Footprint",
    upstream: "https://booth-footprint.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/booth-footprint.webp",
    shippedAt: "2026-10-20",
    status: "live",
    blurb: "Place tables, displays and a chair inside a stall to spot overlaps before arriving at the market.",
  },
  // Bead Cadence. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/bead-cadence.
  {
    slug: "bead-cadence",
    surface: "app",
    title: "Bead Cadence",
    upstream: "https://bead-cadence.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/bead-cadence.webp",
    shippedAt: "2026-10-20",
    status: "live",
    blurb: "Fit a repeating three-bead motif around a necklace to see the exact counts and leftover length.",
  },
  // Kiln / Shrink. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/kiln-shrink.
  {
    slug: "kiln-shrink",
    surface: "app",
    title: "Kiln / Shrink",
    upstream: "https://kiln-shrink.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/kiln-shrink.webp",
    shippedAt: "2026-10-20",
    status: "live",
    blurb: "Scale a clay form from finished dimensions using your own tested shrinkage rate.",
  },
  // Dating PFP Scorer. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/dating-pfp-scorer.
  {
    slug: "dating-pfp-scorer",
    surface: "app",
    title: "Dating PFP Scorer",
    upstream: "https://dating-pfp-scorer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/dating-pfp-scorer.webp",
    shippedAt: "2026-10-20",
    status: "live",
    blurb: "Run the on-device photo model from Tinder's Photo Selector on your pics, fully in the browser. Unofficial research toy.",
  },
  // Queue Sim. Same-account workers.dev on digitalnative; static Next export, no auth, storage none. Repo over9000-apps/queue-sim.
  {
    slug: "queue-sim",
    surface: "app",
    title: "Queue Sim",
    upstream: "https://queue-sim.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/queue-sim.webp",
    shippedAt: "2026-10-21",
    status: "live",
    blurb: "Pixel shoppers, live wait histograms and Little's law: watch how one more cashier changes everything.",
  },
  // Outbreak. Same-account workers.dev on digitalnative; static Next export, no auth, storage none. Repo over9000-apps/epidemic-dots.
  {
    slug: "epidemic-dots",
    surface: "app",
    title: "Outbreak",
    upstream: "https://epidemic-dots.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/epidemic-dots.webp",
    shippedAt: "2026-10-21",
    status: "live",
    blurb: "Thousands of dots, one curve: see how distancing and recovery bend an epidemic in real time.",
  },
  // Big O Race. Same-account workers.dev on digitalnative; static Next export, no auth, storage none. Repo over9000-apps/sorting-hat-js.
  {
    slug: "sorting-hat-js",
    surface: "app",
    title: "Big O Race",
    upstream: "https://sorting-hat-js.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/sorting-hat-js.webp",
    shippedAt: "2026-10-21",
    status: "live",
    blurb: "Eight sorting algorithms race on the same input. Set the track, pick a winner, watch Big O play out.",
  },
  // Deep Zoom. Same-account workers.dev on digitalnative; static Next export, no auth, storage none. Repo over9000-apps/fractal-zoom.
  {
    slug: "fractal-zoom",
    surface: "app",
    title: "Deep Zoom",
    upstream: "https://fractal-zoom.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/fractal-zoom.webp",
    shippedAt: "2026-10-21",
    status: "live",
    blurb: "An infinite Mandelbrot dive: explore fractal worlds, tune the colors, bookmark the extraordinary.",
  },
  // Matboard Studio. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/matboard-studio.
  {
    slug: "matboard-studio",
    surface: "app",
    title: "Matboard Studio",
    upstream: "https://matboard-studio.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/matboard-studio.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Give artwork room to breathe by planning a centered or bottom-weighted mat before making a cut.",
  },
  // Drapery Draft. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/drapery-draft.
  {
    slug: "drapery-draft",
    surface: "app",
    title: "Drapery Draft",
    upstream: "https://drapery-draft.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/drapery-draft.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Turn a window measurement into panels, seams and a practical fabric shopping length.",
  },
  // Repeat / Roll. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/repeat-roll.
  {
    slug: "repeat-roll",
    surface: "app",
    title: "Repeat / Roll",
    upstream: "https://repeat-roll.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/repeat-roll.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Plan wallpaper rolls with pattern-aware takeoffs, trimmed drops and repeat waste.",
  },
  // Poetic Meter Workbench. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/poetic-meter-workbench.
  {
    slug: "poetic-meter-workbench",
    surface: "app",
    title: "Poetic Meter Workbench",
    upstream: "https://poetic-meter-workbench.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/poetic-meter-workbench.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Tap a line’s stresses, divide it into feet, and compare its rhythm with four classical metrical patterns.",
  },
  // Manuscript Collator. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/manuscript-collator.
  {
    slug: "manuscript-collator",
    surface: "app",
    title: "Manuscript Collator",
    upstream: "https://manuscript-collator.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/manuscript-collator.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Align variant readings from two witnesses and assemble an edition with your editorial choices visible.",
  },
  // Tense Transit. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/tense-transit.
  {
    slug: "tense-transit",
    surface: "app",
    title: "Tense Transit",
    upstream: "https://tense-transit.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/tense-transit.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Move the reference point, change the aspect, and watch an English verb phrase reveal how it organizes time.",
  },
  // Historical Source Desk. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/historical-source-desk.
  {
    slug: "historical-source-desk",
    surface: "app",
    title: "Historical Source Desk",
    upstream: "https://historical-source-desk.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/historical-source-desk.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Reconstruct a fictional harbor fire from imperfect evidence, separating direct support from suggestion.",
  },
  // Etymology Branches. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/etymology-branches.
  {
    slug: "etymology-branches",
    surface: "app",
    title: "Etymology Branches",
    upstream: "https://etymology-branches.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/etymology-branches.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Explore six classical roots and sort their word descendants by ancestry rather than spelling.",
  },
  // Case Ending Workshop. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/case-ending-workshop.
  {
    slug: "case-ending-workshop",
    surface: "app",
    title: "Case Ending Workshop",
    upstream: "https://case-ending-workshop.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/case-ending-workshop.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Explore a Latin first-declension paradigm and select the noun form that fits a sentence’s role.",
  },
  // Relational Join Studio. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/relational-join-studio.
  {
    slug: "relational-join-studio",
    surface: "app",
    title: "Relational Join Studio",
    upstream: "https://relational-join-studio.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/relational-join-studio.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Connect two small tables and see exactly which rows survive, multiply, or turn into NULL.",
  },
  // Pascal Paths. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/pascal-paths.
  {
    slug: "pascal-paths",
    surface: "app",
    title: "Pascal Paths",
    upstream: "https://pascal-paths.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/pascal-paths.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Block a crossing and watch the number of right-and-down routes change across the grid.",
  },
  // Proof Dominoes. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/proof-dominoes.
  {
    slug: "proof-dominoes",
    surface: "app",
    title: "Proof Dominoes",
    upstream: "https://proof-dominoes.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/proof-dominoes.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Arrange a proof so every step follows from the reasoning that came before it.",
  },
  // Dependency Workshop. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/normal-form-workshop.
  {
    slug: "normal-form-workshop",
    surface: "app",
    title: "Dependency Workshop",
    upstream: "https://normal-form-workshop.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/normal-form-workshop.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Follow attribute closure to discover database dependencies and test whether a key is minimal.",
  },
  // Fraction Staircase. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/continued-fraction-staircase.
  {
    slug: "continued-fraction-staircase",
    surface: "app",
    title: "Fraction Staircase",
    upstream: "https://continued-fraction-staircase.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/continued-fraction-staircase.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Build closer approximations to a rational number, one continued-fraction floor at a time.",
  },
  // Grammar Derivation. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/grammar-derivation.
  {
    slug: "grammar-derivation",
    surface: "app",
    title: "Grammar Derivation",
    upstream: "https://grammar-derivation.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/grammar-derivation.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Grow a sentence from a single symbol and discover how a tiny grammar generates an infinite language.",
  },
  // Modal Worlds. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/modal-worlds.
  {
    slug: "modal-worlds",
    surface: "app",
    title: "Modal Worlds",
    upstream: "https://modal-worlds.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/modal-worlds.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Change the connections between possible worlds and discover how a point of view shapes modal truth.",
  },
  // Rhetoric Lens. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/rhetoric-lens.
  {
    slug: "rhetoric-lens",
    surface: "app",
    title: "Rhetoric Lens",
    upstream: "https://rhetoric-lens.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/rhetoric-lens.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Highlight the exact language that gives a sentence its rhetorical force.",
  },
  // IPA Feature Matrix. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/ipa-feature-matrix.
  {
    slug: "ipa-feature-matrix",
    surface: "app",
    title: "IPA Feature Matrix",
    upstream: "https://ipa-feature-matrix.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/ipa-feature-matrix.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Explore consonants by filtering a compact IPA chart for place, manner, and voicing.",
  },
  // Counterexample Foundry. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/counterexample-foundry.
  {
    slug: "counterexample-foundry",
    surface: "app",
    title: "Counterexample Foundry",
    upstream: "https://counterexample-foundry.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/counterexample-foundry.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Break a universal claim with one carefully chosen counterexample.",
  },
  // Modular Clock Orbits. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/modular-clock-orbits.
  {
    slug: "modular-clock-orbits",
    surface: "app",
    title: "Modular Clock Orbits",
    upstream: "https://modular-clock-orbits.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/modular-clock-orbits.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Keep adding the same number around a clock to discover which stops you reach and when you return.",
  },
  // Syllogism Studio. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/syllogism-studio.
  {
    slug: "syllogism-studio",
    surface: "app",
    title: "Syllogism Studio",
    upstream: "https://syllogism-studio.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/syllogism-studio.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Test categorical arguments against every three-set model to see whether the conclusion can be false.",
  },
  // Lambda Reducer. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/lambda-reducer.
  {
    slug: "lambda-reducer",
    surface: "app",
    title: "Lambda Reducer",
    upstream: "https://lambda-reducer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/lambda-reducer.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Watch function application become substitution, one careful beta reduction at a time.",
  },
  // Euclid’s Tiling Room. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/euclid-tiling.
  {
    slug: "euclid-tiling",
    surface: "app",
    title: "Euclid’s Tiling Room",
    upstream: "https://euclid-tiling.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/euclid-tiling.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Cut a rectangle into the largest possible squares until the last square reveals the greatest common divisor.",
  },
  // Prism Debt. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/prism-debt.
  {
    slug: "prism-debt",
    surface: "app",
    title: "Prism Debt",
    upstream: "https://prism-debt.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/prism-debt.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Every crystal has a price in another color.",
  },
  // Ribbon Register. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/ribbon-register.
  {
    slug: "ribbon-register",
    surface: "app",
    title: "Ribbon Register",
    upstream: "https://ribbon-register.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/ribbon-register.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "What you see depends on what sits on top.",
  },
  // Beetle Burden. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/beetle-burden.
  {
    slug: "beetle-burden",
    surface: "app",
    title: "Beetle Burden",
    upstream: "https://beetle-burden.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/beetle-burden.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Carry enough home while leaving enough in reserve.",
  },
  // Comet Postage. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/comet-postage.
  {
    slug: "comet-postage",
    surface: "app",
    title: "Comet Postage",
    upstream: "https://comet-postage.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/comet-postage.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "A tiny parcel with a lot of momentum.",
  },
  // Hush Harbor. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/hush-harbor.
  {
    slug: "hush-harbor",
    surface: "app",
    title: "Hush Harbor",
    upstream: "https://hush-harbor.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/hush-harbor.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "A busy harbor with a small sound budget.",
  },
  // Pressure Choir. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/pressure-choir.
  {
    slug: "pressure-choir",
    surface: "app",
    title: "Pressure Choir",
    upstream: "https://pressure-choir.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/pressure-choir.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Open a valve and hear the balance settle.",
  },
  // Root Treaty. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/root-treaty.
  {
    slug: "root-treaty",
    surface: "app",
    title: "Root Treaty",
    upstream: "https://root-treaty.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/root-treaty.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Grow together without taking one another’s space.",
  },
  // Relic Auction. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/relic-auction.
  {
    slug: "relic-auction",
    surface: "app",
    title: "Relic Auction",
    upstream: "https://relic-auction.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/relic-auction.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "The best collection is not the most expensive one.",
  },
  // Glacier Steps. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/glacier-steps.
  {
    slug: "glacier-steps",
    surface: "app",
    title: "Glacier Steps",
    upstream: "https://glacier-steps.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/glacier-steps.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Every departure changes the way home.",
  },
  // Mirror Ferry. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/mirror-ferry.
  {
    slug: "mirror-ferry",
    surface: "app",
    title: "Mirror Ferry",
    upstream: "https://mirror-ferry.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/mirror-ferry.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Guide one boat between two banks through a delicate reflection.",
  },
  // Orbit Escrow. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/orbit-escrow.
  {
    slug: "orbit-escrow",
    surface: "app",
    title: "Orbit Escrow",
    upstream: "https://orbit-escrow.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/orbit-escrow.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Wait for the window to release into motion.",
  },
  // Moth Lanterns. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/moth-lanterns.
  {
    slug: "moth-lanterns",
    surface: "app",
    title: "Moth Lanterns",
    upstream: "https://moth-lanterns.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/moth-lanterns.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Three wings listen to three different lights.",
  },
  // Echo Parcel. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/echo-parcel.
  {
    slug: "echo-parcel",
    surface: "app",
    title: "Echo Parcel",
    upstream: "https://echo-parcel.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/echo-parcel.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Dispatch a parcel to arrive exactly eight beats later.",
  },
  // Alloy Orders. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/alloy-orders.
  {
    slug: "alloy-orders",
    surface: "app",
    title: "Alloy Orders",
    upstream: "https://alloy-orders.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/alloy-orders.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "An exact recipe for an impossible metal.",
  },
  // Ember Break. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/ember-break.
  {
    slug: "ember-break",
    surface: "app",
    title: "Ember Break",
    upstream: "https://ember-break.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/ember-break.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Three barriers between a spark and a forest.",
  },
  // Fossil Strata. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/fossil-strata.
  {
    slug: "fossil-strata",
    surface: "app",
    title: "Fossil Strata",
    upstream: "https://fossil-strata.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/fossil-strata.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Put the earth’s record back in order.",
  },
  // Rain Cistern. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/rain-cistern.
  {
    slug: "rain-cistern",
    surface: "app",
    title: "Rain Cistern",
    upstream: "https://rain-cistern.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/rain-cistern.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Catch every drop and leave nothing to chance.",
  },
  // Phase Vault. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/phase-vault.
  {
    slug: "phase-vault",
    surface: "app",
    title: "Phase Vault",
    upstream: "https://phase-vault.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/phase-vault.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Every turn changes more than one lock.",
  },
  // Mine Odds. Same-account workers.dev on digitalnative; static Next export, no auth, storage none. Repo over9000-apps/minesweeper-prob.
  {
    slug: "minesweeper-prob",
    surface: "app",
    title: "Mine Odds",
    upstream: "https://minesweeper-prob.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/minesweeper-prob.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "See the exact odds behind every hidden tile.",
  },
  // Cooperate?. Same-account workers.dev on digitalnative; static Next export, no auth, storage none. Repo over9000-apps/prisoners-dilemma.
  {
    slug: "prisoners-dilemma",
    surface: "app",
    title: "Cooperate?",
    upstream: "https://prisoners-dilemma.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/prisoners-dilemma.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "An iterated prisoner's dilemma tournament you can rig.",
  },
  // Gas Box. Same-account workers.dev on digitalnative; static Next export, no auth, storage none. Repo over9000-apps/ideal-gas.
  {
    slug: "ideal-gas",
    surface: "app",
    title: "Gas Box",
    upstream: "https://ideal-gas.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/ideal-gas.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Squeeze a box of bouncing molecules and feel the pressure.",
  },
  // Hull Race. Same-account workers.dev on digitalnative; static Next export, no auth, storage none. Repo over9000-apps/convex-hull-race.
  {
    slug: "convex-hull-race",
    surface: "app",
    title: "Hull Race",
    upstream: "https://convex-hull-race.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/convex-hull-race.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Three algorithms race to wrap the same points.",
  },
  // Balancer. Same-account workers.dev on digitalnative; static Next export, no auth, storage none. Repo over9000-apps/load-balancer.
  {
    slug: "load-balancer",
    surface: "app",
    title: "Balancer",
    upstream: "https://load-balancer.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/load-balancer.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Watch traffic pile up on servers and fix it.",
  },
  // Never Repeat. Same-account workers.dev on digitalnative; static Next export, no auth, storage none. Repo over9000-apps/tiling-penrose.
  {
    slug: "tiling-penrose",
    surface: "app",
    title: "Never Repeat",
    upstream: "https://tiling-penrose.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/tiling-penrose.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Generate tilings that never repeat.",
  },
  // Fill the Square. Same-account workers.dev on digitalnative; static Next export, no auth, storage none. Repo over9000-apps/hilbert-curve.
  {
    slug: "hilbert-curve",
    surface: "app",
    title: "Fill the Square",
    upstream: "https://hilbert-curve.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/hilbert-curve.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Watch one line fill an entire square.",
  },
  // Oath Council. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/oath-council.
  {
    slug: "oath-council",
    surface: "app",
    title: "Oath Council",
    upstream: "https://oath-council.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/oath-council.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Guide six voices across three seats to hold one fragile treaty together.",
  },
  // Cargo Keel. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/cargo-keel.
  {
    slug: "cargo-keel",
    surface: "app",
    title: "Cargo Keel",
    upstream: "https://cargo-keel.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/cargo-keel.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Balance the cargo on a little ship with exacting precision.",
  },
  // RIVER / PROOF. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/typographic-river.
  {
    slug: "typographic-river",
    surface: "app",
    title: "RIVER / PROOF",
    upstream: "https://typographic-river.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/typographic-river.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Find the white paths hiding in your paragraph.",
  },
  // REPEAT / FIELD. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/tile-symmetry-workshop.
  {
    slug: "tile-symmetry-workshop",
    surface: "app",
    title: "REPEAT / FIELD",
    upstream: "https://tile-symmetry-workshop.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/tile-symmetry-workshop.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Turn a single tile into a whole world of repeating patterns.",
  },
  // MARKS. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/stage-blocking-map.
  {
    slug: "stage-blocking-map",
    surface: "app",
    title: "MARKS",
    upstream: "https://stage-blocking-map.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/stage-blocking-map.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Map stage movements so every entrance has a reason.",
  },
  // IN BETWEEN. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/stopmotion-onion-desk.
  {
    slug: "stopmotion-onion-desk",
    surface: "app",
    title: "IN BETWEEN",
    upstream: "https://stopmotion-onion-desk.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/stopmotion-onion-desk.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Plan the movement before you move the object.",
  },
  // TEN PACES. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/poster-distance-test.
  {
    slug: "poster-distance-test",
    surface: "app",
    title: "TEN PACES",
    upstream: "https://poster-distance-test.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/poster-distance-test.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "A poster has three seconds to make its point.",
  },
  // TURN / REST. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/score-page-turner.
  {
    slug: "score-page-turner",
    surface: "app",
    title: "TURN / REST",
    upstream: "https://score-page-turner.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/score-page-turner.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Plan the quietest moment to turn a page.",
  },
  // THREE THREADS. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/narrative-braid-board.
  {
    slug: "narrative-braid-board",
    surface: "app",
    title: "THREE THREADS",
    upstream: "https://narrative-braid-board.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/narrative-braid-board.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Braid separate lives into one unfolding story.",
  },
  // MOTIF / MIRROR. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/leitmotif-transformer.
  {
    slug: "leitmotif-transformer",
    surface: "app",
    title: "MOTIF / MIRROR",
    upstream: "https://leitmotif-transformer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/leitmotif-transformer.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Hear one musical idea transformed four ways.",
  },
  // OPEN WALL. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/exhibition-sightlines.
  {
    slug: "exhibition-sightlines",
    surface: "app",
    title: "OPEN WALL",
    upstream: "https://exhibition-sightlines.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/exhibition-sightlines.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Give every artwork a place to be seen.",
  },
  // PRUSSIAN. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/cyanotype-exposure-room.
  {
    slug: "cyanotype-exposure-room",
    surface: "app",
    title: "PRUSSIAN",
    upstream: "https://cyanotype-exposure-room.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/cyanotype-exposure-room.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Make a sun print without the darkroom.",
  },
  // COUNTERFORM. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/negative-space-seal.
  {
    slug: "negative-space-seal",
    surface: "app",
    title: "COUNTERFORM",
    upstream: "https://negative-space-seal.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/negative-space-seal.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Design the space you leave behind.",
  },
  // LEADLIGHT. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/stained-glass-carton.
  {
    slug: "stained-glass-carton",
    surface: "app",
    title: "LEADLIGHT",
    upstream: "https://stained-glass-carton.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/stained-glass-carton.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Build a window from light and line.",
  },
  // WORD / SHAPE. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/concrete-poetry-press.
  {
    slug: "concrete-poetry-press",
    surface: "app",
    title: "WORD / SHAPE",
    upstream: "https://concrete-poetry-press.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/concrete-poetry-press.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Let language take a physical form.",
  },
  // DOUBLE TAKE. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/lenticular-story-cards.
  {
    slug: "lenticular-story-cards",
    surface: "app",
    title: "DOUBLE TAKE",
    upstream: "https://lenticular-story-cards.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/lenticular-story-cards.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Two moments, hidden in one surface.",
  },
  // ROOM TONE. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/foley-cue-desk.
  {
    slug: "foley-cue-desk",
    surface: "app",
    title: "ROOM TONE",
    upstream: "https://foley-cue-desk.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/foley-cue-desk.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Make the unseen half of a scene.",
  },
  // CUT / LINE. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/plotter-hatch-studio.
  {
    slug: "plotter-hatch-studio",
    surface: "app",
    title: "CUT / LINE",
    upstream: "https://plotter-hatch-studio.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/plotter-hatch-studio.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Turn tonal bands into an engraver’s language.",
  },
  // GUTTER. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/comic-beat-cutter.
  {
    slug: "comic-beat-cutter",
    surface: "app",
    title: "GUTTER",
    upstream: "https://comic-beat-cutter.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/comic-beat-cutter.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Time is the space between panels.",
  },
  // FOLIO / FOLD. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/book-signature-planner.
  {
    slug: "book-signature-planner",
    surface: "app",
    title: "FOLIO / FOLD",
    upstream: "https://book-signature-planner.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/book-signature-planner.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "The right page, on the right side, every time.",
  },
  // OBLIQUE. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/anamorphic-type-stage.
  {
    slug: "anamorphic-type-stage",
    surface: "app",
    title: "OBLIQUE",
    upstream: "https://anamorphic-type-stage.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/anamorphic-type-stage.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Letters that only resolve from one point of view.",
  },
  // Ink Registration. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/risograph-register.
  {
    slug: "risograph-register",
    surface: "app",
    title: "Ink Registration",
    upstream: "https://risograph-register.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/risograph-register.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Explore two-color print registration, plate alignment and ink density with exportable artwork.",
  },
  // Heat / crossing. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/thermal-bridge-grid.
  {
    slug: "thermal-bridge-grid",
    surface: "app",
    title: "Heat / crossing",
    upstream: "https://thermal-bridge-grid.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/thermal-bridge-grid.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Solve the temperature field around a conductive bridge through an insulating wall and see heat concentrate in the shortcut.",
  },
  // Atoms / in concert. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/phonon-chain.
  {
    slug: "phonon-chain",
    surface: "app",
    title: "Atoms / in concert",
    upstream: "https://phonon-chain.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/phonon-chain.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Excite a discrete atomic chain, watch its mode shape and follow the lattice dispersion relation.",
  },
  // A planet / in silhouette. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/kepler-transit-detective.
  {
    slug: "kepler-transit-detective",
    surface: "app",
    title: "A planet / in silhouette",
    upstream: "https://kepler-transit-detective.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/kepler-transit-detective.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Adjust a planet’s size and crossing geometry to build a transit light curve from a missing sliver of starlight.",
  },
  // Slices / of a crystal. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/crystallographic-plane-explorer.
  {
    slug: "crystallographic-plane-explorer",
    surface: "app",
    title: "Slices / of a crystal",
    upstream: "https://crystallographic-plane-explorer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/crystallographic-plane-explorer.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Cut a unit cell with indexed lattice planes, rotate the projection and compare parallel families of atomic slices.",
  },
  // Small tubes / tall water. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/capillary-wetting-lab.
  {
    slug: "capillary-wetting-lab",
    surface: "app",
    title: "Small tubes / tall water",
    upstream: "https://capillary-wetting-lab.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/capillary-wetting-lab.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Compare three capillaries and explore how contact angle turns liquid rise into depression.",
  },
  // Hanging / equilibrium. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/catenary-workshop.
  {
    slug: "catenary-workshop",
    surface: "app",
    title: "Hanging / equilibrium",
    upstream: "https://catenary-workshop.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/catenary-workshop.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Change a hanging cable’s slack and compare its equilibrium curve under its own weight to a simple parabola.",
  },
  // Intercept / moving target. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/projectile-intercept-lab.
  {
    slug: "projectile-intercept-lab",
    surface: "app",
    title: "Intercept / moving target",
    upstream: "https://projectile-intercept-lab.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/projectile-intercept-lab.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Lead a moving target and compare the analytic vacuum intercept with a drag-affected flight.",
  },
  // The fastest descent. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/brachistochrone-race.
  {
    slug: "brachistochrone-race",
    surface: "app",
    title: "The fastest descent",
    upstream: "https://brachistochrone-race.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/brachistochrone-race.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Race a straight ramp against a cycloid and a steep-start curve under gravity to discover the fastest descent.",
  },
  // Passing / sound. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/doppler-passby.
  {
    slug: "doppler-passby",
    surface: "app",
    title: "Passing / sound",
    upstream: "https://doppler-passby.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/doppler-passby.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Watch emitted wavefronts keep their original centers while a sound source moves past a listener.",
  },
  // Spectral / orders. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/diffraction-grating-spectrometer.
  {
    slug: "diffraction-grating-spectrometer",
    surface: "app",
    title: "Spectral / orders",
    upstream: "https://diffraction-grating-spectrometer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/diffraction-grating-spectrometer.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Separate wavelengths into diffraction orders and change groove spacing to discover which orders can physically exist.",
  },
  // Layers of the sea. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/thermocline-profiler.
  {
    slug: "thermocline-profiler",
    surface: "app",
    title: "Layers of the sea",
    upstream: "https://thermocline-profiler.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/thermocline-profiler.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Build an ocean column from temperature and salinity to spot density increases and where water wants to overturn.",
  },
  // Light / after filters. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/polarization-bench.
  {
    slug: "polarization-bench",
    surface: "app",
    title: "Light / after filters",
    upstream: "https://polarization-bench.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/polarization-bench.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Add a third filter between two crossed filters and explore why light transmission comes back.",
  },
  // Dipole / field notes. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/magnetic-dipole-field.
  {
    slug: "magnetic-dipole-field",
    surface: "app",
    title: "Dipole / field notes",
    upstream: "https://magnetic-dipole-field.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/magnetic-dipole-field.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Rotate two magnetic moments and inspect the invisible vector field they create together.",
  },
  // Ice budget. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/glacier-flowline.
  {
    slug: "glacier-flowline",
    surface: "app",
    title: "Ice budget",
    upstream: "https://glacier-flowline.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/glacier-flowline.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Move the equilibrium snow line and watch a mountain glacier gain or lose its accumulation zone.",
  },
  // Watershed / flow atlas. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/watershed-routing.
  {
    slug: "watershed-routing",
    surface: "app",
    title: "Watershed / flow atlas",
    upstream: "https://watershed-routing.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/watershed-routing.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Trace rainfall across synthetic terrain to reveal catchment structure, local sinks and downstream accumulation.",
  },
  // Three bars / one load. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/truss-force-studio.
  {
    slug: "truss-force-studio",
    surface: "app",
    title: "Three bars / one load",
    upstream: "https://truss-force-studio.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/truss-force-studio.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Change a triangular truss’s span and apex to see how compression and tension change in magnitude.",
  },
  // Boundary / deep time. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/plate-boundary-studio.
  {
    slug: "plate-boundary-studio",
    surface: "app",
    title: "Boundary / deep time",
    upstream: "https://plate-boundary-studio.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/plate-boundary-studio.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Compress or separate tectonic plates and scrub geological time to read the distance hidden behind slow motion.",
  },
  // Snell / interface bench. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/snell-interface-bench.
  {
    slug: "snell-interface-bench",
    surface: "app",
    title: "Snell / interface bench",
    upstream: "https://snell-interface-bench.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/snell-interface-bench.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Send a beam across a boundary and explore how refraction gives way to total internal reflection.",
  },
  // The resonant room. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/acoustic-room-modes.
  {
    slug: "acoustic-room-modes",
    surface: "app",
    title: "The resonant room",
    upstream: "https://acoustic-room-modes.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/acoustic-room-modes.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Find the quiet zones hiding inside a room by inspecting axial and tangential pressure patterns.",
  },
  // Seismic triangulator. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/seismic-triangulator.
  {
    slug: "seismic-triangulator",
    surface: "app",
    title: "Seismic triangulator",
    upstream: "https://seismic-triangulator.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/seismic-triangulator.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Locate a hidden earthquake on the map using arrival-time differences from three seismic stations.",
  },
  // CodeMaster. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/problem-solving-platform.
  {
    slug: "problem-solving-platform",
    surface: "app",
    title: "CodeMaster",
    upstream: "https://problem-solving-platform.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/problem-solving-platform.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Practice coding interview problems with step-by-step visualizations and solutions in multiple languages.",
  },
  // Copy Generation Studio. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/copy-generation-studio.
  {
    slug: "copy-generation-studio",
    surface: "app",
    title: "Copy Generation Studio",
    upstream: "https://copy-generation-studio.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/copy-generation-studio.webp",
    shippedAt: "2026-10-22",
    status: "live",
    blurb: "Copy a pixel image through six lossy generations, then paint rescue masks to protect the pixels you want to keep.",
  },
  // Flexagon Face Mapper. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/flexagon-face-mapper.
  {
    slug: "flexagon-face-mapper",
    surface: "app",
    title: "Flexagon Face Mapper",
    upstream: "https://flexagon-face-mapper.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/flexagon-face-mapper.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Design physical-side artwork for one documented six-face flexagon.",
  },
  // Foam Wire Negotiation. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/foam-wire-negotiation.
  {
    slug: "foam-wire-negotiation",
    surface: "app",
    title: "Foam Wire Negotiation",
    upstream: "https://foam-wire-negotiation.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/foam-wire-negotiation.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Author selective cage-weight sculptures with canonical geometry exports.",
  },
  // Fictional Recipe for a Person. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/fictional-recipe-for-a-person.
  {
    slug: "fictional-recipe-for-a-person",
    surface: "app",
    title: "Fictional Recipe for a Person",
    upstream: "https://fictional-recipe-for-a-person.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/fictional-recipe-for-a-person.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Compose a fictional recipe for a person.",
  },
  // Fictional Relic Restoration. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/fictional-relic-restoration.
  {
    slug: "fictional-relic-restoration",
    surface: "app",
    title: "Fictional Relic Restoration",
    upstream: "https://fictional-relic-restoration.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/fictional-relic-restoration.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Restore a fictional relic.",
  },
  // Fictional Marginalia Duel. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/fictional-marginalia-duel.
  {
    slug: "fictional-marginalia-duel",
    surface: "app",
    title: "Fictional Marginalia Duel",
    upstream: "https://fictional-marginalia-duel.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/fictional-marginalia-duel.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Stage a fictional duel in the margins.",
  },
  // Fictional Inheritance Dispute. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/fictional-inheritance-dispute.
  {
    slug: "fictional-inheritance-dispute",
    surface: "app",
    title: "Fictional Inheritance Dispute",
    upstream: "https://fictional-inheritance-dispute.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/fictional-inheritance-dispute.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Solve a fictional keepsake-allocation story puzzle with exhaustive comparisons and offline exports.",
  },
  // Export Survivors. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/export-survivors.
  {
    slug: "export-survivors",
    surface: "app",
    title: "Export Survivors",
    upstream: "https://export-survivors.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/export-survivors.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Author portable graphics fallbacks across all 16 capability states, with visible image fallback and self-contained exports.",
  },
  // Brew Borough. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/brew-borough.
  {
    slug: "brew-borough",
    surface: "app",
    title: "Brew Borough",
    upstream: "https://brew-borough.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/brew-borough.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore office coffee tastings and discover subscriptions guided by your preferences.",
  },
  // Fingertip Topography. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/fingertip-topography.
  {
    slug: "fingertip-topography",
    surface: "app",
    title: "Fingertip Topography",
    upstream: "https://fingertip-topography.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/fingertip-topography.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Create scalar-field contour art with certified magnetic isoline strokes.",
  },
  // Fictional Sound Taxonomy. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/fictional-sound-taxonomy.
  {
    slug: "fictional-sound-taxonomy",
    surface: "app",
    title: "Fictional Sound Taxonomy",
    upstream: "https://fictional-sound-taxonomy.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/fictional-sound-taxonomy.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore procedural sound families with audited trait inheritance, three-generation pedigrees, and PCM/WAV exports.",
  },
  // Fictional Language Misfire. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/fictional-language-misfire.
  {
    slug: "fictional-language-misfire",
    surface: "app",
    title: "Fictional Language Misfire",
    upstream: "https://fictional-language-misfire.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/fictional-language-misfire.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore fictional language misfires.",
  },
  // Fictional Errand Excuses. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/fictional-errand-excuses.
  {
    slug: "fictional-errand-excuses",
    surface: "app",
    title: "Fictional Errand Excuses",
    upstream: "https://fictional-errand-excuses.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/fictional-errand-excuses.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore excuses for fictional errands.",
  },
  // False Loop Detector Art. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/false-loop-detector-art.
  {
    slug: "false-loop-detector-art",
    surface: "app",
    title: "False Loop Detector Art",
    upstream: "https://false-loop-detector-art.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/false-loop-detector-art.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore false loop detection through art.",
  },
  // Fallback Metric Dock. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/fallback-metric-dock.
  {
    slug: "fallback-metric-dock",
    surface: "app",
    title: "Fallback Metric Dock",
    upstream: "https://fallback-metric-dock.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/fallback-metric-dock.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore fallback metrics.",
  },
  // Exclusion Channels. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/exclusion-channels.
  {
    slug: "exclusion-channels",
    surface: "app",
    title: "Exclusion Channels",
    upstream: "https://exclusion-channels.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/exclusion-channels.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore exclusion channels.",
  },
  // Errant Audio Description. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/errant-audio-description.
  {
    slug: "errant-audio-description",
    surface: "app",
    title: "Errant Audio Description",
    upstream: "https://errant-audio-description.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/errant-audio-description.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Read a text-only dual-track narrative of truthful events and explicitly imagined near misses.",
  },
  // Equivalent Action Score. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/equivalent-action-score.
  {
    slug: "equivalent-action-score",
    surface: "app",
    title: "Equivalent Action Score",
    upstream: "https://equivalent-action-score.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/equivalent-action-score.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore equivalent action scores.",
  },
  // Ending Perspective Prism. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/ending-perspective-prism.
  {
    slug: "ending-perspective-prism",
    surface: "app",
    title: "Ending Perspective Prism",
    upstream: "https://ending-perspective-prism.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/ending-perspective-prism.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore one fixed world and final line through three reader-knowledge schedules.",
  },
  // Equation Alignment Stage. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/equation-alignment-stage.
  {
    slug: "equation-alignment-stage",
    surface: "app",
    title: "Equation Alignment Stage",
    upstream: "https://equation-alignment-stage.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/equation-alignment-stage.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Arrange semantic expression-tree typography on shared alignment rails with intact fractions and exponents.",
  },
  // Elastic Margins. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/elastic-margins.
  {
    slug: "elastic-margins",
    surface: "app",
    title: "Elastic Margins",
    upstream: "https://elastic-margins.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/elastic-margins.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore elastic margins.",
  },
  // Edition Choice Tree. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/edition-choice-tree.
  {
    slug: "edition-choice-tree",
    surface: "app",
    title: "Edition Choice Tree",
    upstream: "https://edition-choice-tree.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/edition-choice-tree.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore a tree of edition choices.",
  },
  // Edge Ownership Theatre. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/edge-ownership-theatre.
  {
    slug: "edge-ownership-theatre",
    surface: "app",
    title: "Edge Ownership Theatre",
    upstream: "https://edge-ownership-theatre.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/edge-ownership-theatre.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore region-wide shared-border cues in an interactive studio.",
  },
  // Edge Flip Gardener. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/edge-flip-gardener.
  {
    slug: "edge-flip-gardener",
    surface: "app",
    title: "Edge Flip Gardener",
    upstream: "https://edge-flip-gardener.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/edge-flip-gardener.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Create faceted art through connectivity changes with fixed vertices, valid diagonal flips, and face ancestry.",
  },
  // Edge Hierarchy Editor. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/edge-hierarchy-editor.
  {
    slug: "edge-hierarchy-editor",
    surface: "app",
    title: "Edge Hierarchy Editor",
    upstream: "https://edge-hierarchy-editor.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/edge-hierarchy-editor.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore edge hierarchies.",
  },
  // Duplex Metrics Foundry. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/duplex-metrics-foundry.
  {
    slug: "duplex-metrics-foundry",
    surface: "app",
    title: "Duplex Metrics Foundry",
    upstream: "https://duplex-metrics-foundry.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/duplex-metrics-foundry.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Author twelve independent glyph pairs with exact shared advances and fixed-origin mixed-style proofs.",
  },
  // Drypoint Memory. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/drypoint-memory.
  {
    slug: "drypoint-memory",
    surface: "app",
    title: "Drypoint Memory",
    upstream: "https://drypoint-memory.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/drypoint-memory.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Create illustrative prints with fixed incision grooves and directional burr wear across explicit editions.",
  },
  // Ductus Card Studio. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/ductus-card-studio.
  {
    slug: "ductus-card-studio",
    surface: "app",
    title: "Ductus Card Studio",
    upstream: "https://ductus-card-studio.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/ductus-card-studio.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore ductus cards in an interactive studio.",
  },
  // Drawing Coordinate Translator. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/drawing-coordinate-translator.
  {
    slug: "drawing-coordinate-translator",
    surface: "app",
    title: "Drawing Coordinate Translator",
    upstream: "https://drawing-coordinate-translator.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/drawing-coordinate-translator.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Reinterpret one original gesture through authored Cartesian, polar, and strip charts with explicit transitions.",
  },
  // Disappearing Scaffold. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/disappearing-scaffold.
  {
    slug: "disappearing-scaffold",
    surface: "app",
    title: "Disappearing Scaffold",
    upstream: "https://disappearing-scaffold.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/disappearing-scaffold.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore disappearing scaffolds.",
  },
  // Direction Switchboard. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/direction-switchboard.
  {
    slug: "direction-switchboard",
    surface: "app",
    title: "Direction Switchboard",
    upstream: "https://direction-switchboard.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/direction-switchboard.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore a direction switchboard.",
  },
  // Diacritic Airspace. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/diacritic-airspace.
  {
    slug: "diacritic-airspace",
    surface: "app",
    title: "Diacritic Airspace",
    upstream: "https://diacritic-airspace.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/diacritic-airspace.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore diacritic airspace.",
  },
  // Curved Seam Pairer. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/curved-seam-pairer.
  {
    slug: "curved-seam-pairer",
    surface: "app",
    title: "Curved Seam Pairer",
    upstream: "https://curved-seam-pairer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/curved-seam-pairer.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore curved seam pairing.",
  },
  // Curvature Budget Sculptor. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/curvature-budget-sculptor.
  {
    slug: "curvature-budget-sculptor",
    surface: "app",
    title: "Curvature Budget Sculptor",
    upstream: "https://curvature-budget-sculptor.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/curvature-budget-sculptor.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore curvature-budget sculpture.",
  },
  // Crop Constitution. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/crop-constitution.
  {
    slug: "crop-constitution",
    surface: "app",
    title: "Crop Constitution",
    upstream: "https://crop-constitution.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/crop-constitution.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Analyze crop feasibility with must-keep regions, named relations and explicitly bounded layer translations.",
  },
  // Cue Context Disambiguator. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/cue-context-disambiguator.
  {
    slug: "cue-context-disambiguator",
    surface: "app",
    title: "Cue Context Disambiguator",
    upstream: "https://cue-context-disambiguator.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/cue-context-disambiguator.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore cue and context disambiguation.",
  },
  // Crumple Scores. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/crumple-scores.
  {
    slug: "crumple-scores",
    surface: "app",
    title: "Crumple Scores",
    upstream: "https://crumple-scores.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/crumple-scores.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Draw signed ridges into a constrained faceted height-field relief for geometric studies rather than physical folding.",
  },
  // Crease Memory Stones. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/crease-memory-stones.
  {
    slug: "crease-memory-stones",
    surface: "app",
    title: "Crease Memory Stones",
    upstream: "https://crease-memory-stones.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/crease-memory-stones.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore crease memory stones.",
  },
  // Counterpoint Crossing Puzzle. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/counterpoint-crossing-puzzle.
  {
    slug: "counterpoint-crossing-puzzle",
    surface: "app",
    title: "Counterpoint Crossing Puzzle",
    upstream: "https://counterpoint-crossing-puzzle.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/counterpoint-crossing-puzzle.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Solve a fixed-inventory two-voice timing puzzle with exhaustive crossing checks and audible replay.",
  },
  // Crank Score Puncher. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/crank-score-puncher.
  {
    slug: "crank-score-puncher",
    surface: "app",
    title: "Crank Score Puncher",
    upstream: "https://crank-score-puncher.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/crank-score-puncher.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore crank score punching.",
  },
  // Copyfit Auction. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/copyfit-auction.
  {
    slug: "copyfit-auction",
    surface: "app",
    title: "Copyfit Auction",
    upstream: "https://copyfit-auction.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/copyfit-auction.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Review editorial inclusion sets: keep mandatory copy, trade region tokens and never shrink below minimum type.",
  },
  // Corner Bargain. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/corner-bargain.
  {
    slug: "corner-bargain",
    surface: "app",
    title: "Corner Bargain",
    upstream: "https://corner-bargain.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/corner-bargain.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore corner bargains.",
  },
  // Copyable Outline Desk. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/copyable-outline-desk.
  {
    slug: "copyable-outline-desk",
    surface: "app",
    title: "Copyable Outline Desk",
    upstream: "https://copyable-outline-desk.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/copyable-outline-desk.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Map outlines to text atomically, copying ligatures and combining-mark clusters exactly once.",
  },
  // Convolution Post Office. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/convolution-post-office.
  {
    slug: "convolution-post-office",
    surface: "app",
    title: "Convolution Post Office",
    upstream: "https://convolution-post-office.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/convolution-post-office.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Invent fictional acoustic correspondence with signed arrival stamps and exact convolution.",
  },
  // Convex Layer Carving. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/convex-layer-carving.
  {
    slug: "convex-layer-carving",
    surface: "app",
    title: "Convex Layer Carving",
    upstream: "https://convex-layer-carving.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/convex-layer-carving.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Sculpt full 3D convex-depth shells with editable point studies and canonical open-cutaway exports.",
  },
  // Contrast Shape Relay. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/contrast-shape-relay.
  {
    slug: "contrast-shape-relay",
    surface: "app",
    title: "Contrast Shape Relay",
    upstream: "https://contrast-shape-relay.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/contrast-shape-relay.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore explicit open-edge fragment groups and manual reveal combinations.",
  },
  // Contour Tiling Without Tiles. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/contour-tiling-without-tiles.
  {
    slug: "contour-tiling-without-tiles",
    surface: "app",
    title: "Contour Tiling Without Tiles",
    upstream: "https://contour-tiling-without-tiles.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/contour-tiling-without-tiles.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Pair boundary ports and sculpt independently validated noncrossing interior paths.",
  },
  // Contour Intent Resolver. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/contour-intent-resolver.
  {
    slug: "contour-intent-resolver",
    surface: "app",
    title: "Contour Intent Resolver",
    upstream: "https://contour-intent-resolver.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/contour-intent-resolver.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Author junction continuations on overdrawn sketches while retaining rejected alternatives.",
  },
  // Contour Frequency Mask. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/contour-frequency-mask.
  {
    slug: "contour-frequency-mask",
    surface: "app",
    title: "Contour Frequency Mask",
    upstream: "https://contour-frequency-mask.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/contour-frequency-mask.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Draw a hidden target through compact-support boundary-band line orientation, spacing and phase.",
  },
  // Contour Exchange. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/contour-exchange.
  {
    slug: "contour-exchange",
    surface: "app",
    title: "Contour Exchange",
    upstream: "https://contour-exchange.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/contour-exchange.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Trade paired contour arcs with endpoint-locked closure and discrete provenance.",
  },
  // Contour Cairn. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/contour-cairn.
  {
    slug: "contour-cairn",
    surface: "app",
    title: "Contour Cairn",
    upstream: "https://contour-cairn.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/contour-cairn.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Sculpt lofted forms through local section correspondence.",
  },
  // Contour Borrowing Booth. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/contour-borrowing-booth.
  {
    slug: "contour-borrowing-booth",
    surface: "app",
    title: "Contour Borrowing Booth",
    upstream: "https://contour-borrowing-booth.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/contour-borrowing-booth.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Map complete illustrated interiors between paired contour landmarks with transparent barycentric correspondence.",
  },
  // Contact Sheet Sculpture. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/contact-sheet-sculpture.
  {
    slug: "contact-sheet-sculpture",
    surface: "app",
    title: "Contact Sheet Sculpture",
    upstream: "https://contact-sheet-sculpture.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/contact-sheet-sculpture.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore sculpture through contact sheets.",
  },
  // Consonant Percussion Shop. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/consonant-percussion-shop.
  {
    slug: "consonant-percussion-shop",
    surface: "app",
    title: "Consonant Percussion Shop",
    upstream: "https://consonant-percussion-shop.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/consonant-percussion-shop.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore consonants as percussion.",
  },
  // Compression Choreographer. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/compression-choreographer.
  {
    slug: "compression-choreographer",
    surface: "app",
    title: "Compression Choreographer",
    upstream: "https://compression-choreographer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/compression-choreographer.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Author actual 8x8 DCT coefficient survival maps and original compression artwork.",
  },
  // Constraint Drawing Solids. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/constraint-drawing-solids.
  {
    slug: "constraint-drawing-solids",
    surface: "app",
    title: "Constraint Drawing Solids",
    upstream: "https://constraint-drawing-solids.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/constraint-drawing-solids.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Shape 3D wire sculptures through relationships, bounded local constraints and independent residual certification.",
  },
  // Compass Without Circles. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/compass-without-circles.
  {
    slug: "compass-without-circles",
    surface: "app",
    title: "Compass Without Circles",
    upstream: "https://compass-without-circles.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/compass-without-circles.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Draw two-distance loci with captured constraint replay.",
  },
  // Comic Pause Trials. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/comic-pause-trials.
  {
    slug: "comic-pause-trials",
    surface: "app",
    title: "Comic Pause Trials",
    upstream: "https://comic-pause-trials.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/comic-pause-trials.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore how pauses shape comic timing.",
  },
  // Color Interval Instrument. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/color-interval-instrument.
  {
    slug: "color-interval-instrument",
    surface: "app",
    title: "Color Interval Instrument",
    upstream: "https://color-interval-instrument.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/color-interval-instrument.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Author coupled OKLCH interval graphs.",
  },
  // Color Echo Delay. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/color-echo-delay.
  {
    slug: "color-echo-delay",
    surface: "app",
    title: "Color Echo Delay",
    upstream: "https://color-echo-delay.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/color-echo-delay.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Paint locally with independent touch-triggered region color clocks.",
  },
  // Collision Dialogue. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/collision-dialogue.
  {
    slug: "collision-dialogue",
    surface: "app",
    title: "Collision Dialogue",
    upstream: "https://collision-dialogue.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/collision-dialogue.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore local audio where sample-local conversation openings govern question, refusal and repair interruptions.",
  },
  // Collagraph Contact. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/collagraph-contact.
  {
    slug: "collagraph-contact",
    surface: "app",
    title: "Collagraph Contact",
    upstream: "https://collagraph-contact.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/collagraph-contact.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore collagraph contact printing.",
  },
  // Brush Autopsy. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/brush-autopsy.
  {
    slug: "brush-autopsy",
    surface: "app",
    title: "Brush Autopsy",
    upstream: "https://brush-autopsy.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/brush-autopsy.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Inspect deterministic brush deposition through primitive stamps, arc-length replay, seeded jitter and exact raster autopsy.",
  },
  // Branchline Edition. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/branchline-edition.
  {
    slug: "branchline-edition",
    surface: "app",
    title: "Branchline Edition",
    upstream: "https://branchline-edition.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/branchline-edition.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Align accepted parallel texts monotonically and paginate at approved boundaries.",
  },
  // Braid Junction Planner. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/braid-junction-planner.
  {
    slug: "braid-junction-planner",
    surface: "app",
    title: "Braid Junction Planner",
    upstream: "https://braid-junction-planner.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/braid-junction-planner.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Compose branching braid scores with conserved strand identities across split and merge junctions.",
  },
  // Bobbin Crossing Desk. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/bobbin-crossing-desk.
  {
    slug: "bobbin-crossing-desk",
    surface: "app",
    title: "Bobbin Crossing Desk",
    upstream: "https://bobbin-crossing-desk.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/bobbin-crossing-desk.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Compose signed bobbin-lace crossing scores while tracing every strand identity.",
  },
  // Bent Picture Extruder. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/bent-picture-extruder.
  {
    slug: "bent-picture-extruder",
    surface: "app",
    title: "Bent Picture Extruder",
    upstream: "https://bent-picture-extruder.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/bent-picture-extruder.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Extrude textured picture regions while preserving the reference view.",
  },
  // Before the First Sentence. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/before-the-first-sentence.
  {
    slug: "before-the-first-sentence",
    surface: "app",
    title: "Before the First Sentence",
    upstream: "https://before-the-first-sentence.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/before-the-first-sentence.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore one causal history through five disclosure depths with one fixed ending.",
  },
  // Bead Cloud Composer. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/bead-cloud-composer.
  {
    slug: "bead-cloud-composer",
    surface: "app",
    title: "Bead Cloud Composer",
    upstream: "https://bead-cloud-composer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/bead-cloud-composer.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Compose target-bound bead pictures across depth planes with exact reference projection.",
  },
  // Cred Explorer. Overflow Worker on over9000-pool-000; static page, no auth, storage none.
  // Client-side only: the user's Gemini key goes straight from the browser to Google. Repo over9000-apps/cred-explorer.
  {
    slug: "cred-explorer",
    surface: "app",
    title: "Cred Explorer",
    upstream: "https://cred-explorer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/cred-explorer.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Paste a Gemini API key and see what it unlocks: valid or not, which models it can reach, and a live Flash Lite test. Runs in your browser.",
  },
  // Click Language Constructor. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/click-language-constructor.
  {
    slug: "click-language-constructor",
    surface: "app",
    title: "Click Language Constructor",
    upstream: "https://click-language-constructor.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/click-language-constructor.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Construct a compositional click language and certify bounded-noise decoding.",
  },
  // Coil Join Atlas. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/coil-join-atlas.
  {
    slug: "coil-join-atlas",
    surface: "app",
    title: "Coil Join Atlas",
    upstream: "https://coil-join-atlas.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/coil-join-atlas.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Plan earlier-coil stitch catches, repair unresolved joins, and track geometric lengths.",
  },
  // Click Gap Mender. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/click-gap-mender.
  {
    slug: "click-gap-mender",
    surface: "app",
    title: "Click Gap Mender",
    upstream: "https://click-gap-mender.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/click-gap-mender.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore Click Gap Mender.",
  },
  // Character Collision Scheduler. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/character-collision-scheduler.
  {
    slug: "character-collision-scheduler",
    surface: "app",
    title: "Character Collision Scheduler",
    upstream: "https://character-collision-scheduler.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/character-collision-scheduler.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Schedule deterministic atomic farce as four private plans collide over unique props.",
  },
  // Chorale With Private Rules. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/chorale-with-private-rules.
  {
    slug: "chorale-with-private-rules",
    surface: "app",
    title: "Chorale With Private Rules",
    upstream: "https://chorale-with-private-rules.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/chorale-with-private-rules.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore Chorale With Private Rules.",
  },
  // Causal Palimpsest Play. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/causal-palimpsest-play.
  {
    slug: "causal-palimpsest-play",
    surface: "app",
    title: "Causal Palimpsest Play",
    upstream: "https://causal-palimpsest-play.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/causal-palimpsest-play.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Rewrite fictional causes while erased worlds persist as character-specific memories.",
  },
  // Causal Joke Clock. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/causal-joke-clock.
  {
    slug: "causal-joke-clock",
    surface: "app",
    title: "Causal Joke Clock",
    upstream: "https://causal-joke-clock.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/causal-joke-clock.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Time authored expectation elimination across four interpretations in original comedy scenes.",
  },
  // Cast of an Edit. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/cast-of-an-edit.
  {
    slug: "cast-of-an-edit",
    surface: "app",
    title: "Cast of an Edit",
    upstream: "https://cast-of-an-edit.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/cast-of-an-edit.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Stage digital text theatre with disputed word identities and explicit crossing casts between two sentences.",
  },
  // Carousel Book Chambers. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/carousel-book-chambers.
  {
    slug: "carousel-book-chambers",
    surface: "app",
    title: "Carousel Book Chambers",
    upstream: "https://carousel-book-chambers.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/carousel-book-chambers.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore four cyclic book chambers with single-source shared seam anchors.",
  },
  // Camshaft Cabaret. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/camshaft-cabaret.
  {
    slug: "camshaft-cabaret",
    surface: "app",
    title: "Camshaft Cabaret",
    upstream: "https://camshaft-cabaret.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/camshaft-cabaret.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Author a virtual puppet cabaret with three editable radial cams whose profiles determine motion.",
  },
  // Cadence Right of Way. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/cadence-right-of-way.
  {
    slug: "cadence-right-of-way",
    surface: "app",
    title: "Cadence Right of Way",
    upstream: "https://cadence-right-of-way.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/cadence-right-of-way.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Negotiate recurring fair closure tokens for three original musical parts.",
  },
  // Cadence Postponement Machine. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/cadence-postponement-machine.
  {
    slug: "cadence-postponement-machine",
    surface: "app",
    title: "Cadence Postponement Machine",
    upstream: "https://cadence-postponement-machine.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/cadence-postponement-machine.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore an eight-room harmony graph whose only ending token forces the withheld target chord next.",
  },
  // Basket Stake Switchboard. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/basket-stake-switchboard.
  {
    slug: "basket-stake-switchboard",
    surface: "app",
    title: "Basket Stake Switchboard",
    upstream: "https://basket-stake-switchboard.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/basket-stake-switchboard.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Plan changing circular stake topology and stable over-under crossings by course.",
  },
  // Barcode Relief Secret. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/barcode-relief-secret.
  {
    slug: "barcode-relief-secret",
    surface: "app",
    title: "Barcode Relief Secret",
    upstream: "https://barcode-relief-secret.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/barcode-relief-secret.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Encode decorative bitmaps in stepped relief solids and reveal them by geometric section.",
  },
  // Badge Distance Lab. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/badge-distance-lab.
  {
    slug: "badge-distance-lab",
    surface: "app",
    title: "Badge Distance Lab",
    upstream: "https://badge-distance-lab.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/badge-distance-lab.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore six editable primitives, twelve original badges, and bounded raster-distance selection.",
  },
  // Audience Veto Canon. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/audience-veto-canon.
  {
    slug: "audience-veto-canon",
    surface: "app",
    title: "Audience Veto Canon",
    upstream: "https://audience-veto-canon.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/audience-veto-canon.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Shape a finite quartet score by vetoing future vocabulary while every performer retains a legal action.",
  },
  // Audience Secret Sharing. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/audience-secret-sharing.
  {
    slug: "audience-secret-sharing",
    surface: "app",
    title: "Audience Secret Sharing",
    upstream: "https://audience-secret-sharing.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/audience-secret-sharing.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore a toy-only three-of-five audience fragment ceremony.",
  },
  // Audience Agency Difference Lens. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/audience-agency-difference-lens.
  {
    slug: "audience-agency-difference-lens",
    surface: "app",
    title: "Audience Agency Difference Lens",
    upstream: "https://audience-agency-difference-lens.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/audience-agency-difference-lens.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Compare audience-choice consequence languages with replayable contrasting paths.",
  },
  // Attack Sustain Splitter. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/attack-sustain-splitter.
  {
    slug: "attack-sustain-splitter",
    surface: "app",
    title: "Attack Sustain Splitter",
    upstream: "https://attack-sustain-splitter.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/attack-sustain-splitter.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore Attack Sustain Splitter.",
  },
  // Artwork Capsule Packer. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/artwork-capsule-packer.
  {
    slug: "artwork-capsule-packer",
    surface: "app",
    title: "Artwork Capsule Packer",
    upstream: "https://artwork-capsule-packer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/artwork-capsule-packer.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore Artwork Capsule Packer.",
  },
  // Spike. Same-account workers.dev on digitalnative; static Next export, no auth, storage none. Repo over9000-apps/neuron-spike.
  {
    slug: "neuron-spike",
    surface: "app",
    title: "Spike",
    upstream: "https://neuron-spike.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/neuron-spike.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Fire a neuron and watch the action potential.",
  },
  // Express Lanes. Same-account workers.dev on digitalnative; static Next export, no auth, storage none. Repo over9000-apps/skiplist-lanes.
  {
    slug: "skiplist-lanes",
    surface: "app",
    title: "Express Lanes",
    upstream: "https://skiplist-lanes.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/skiplist-lanes.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "A skip list drawn as highway express lanes.",
  },
  // Who Came First. Same-account workers.dev on digitalnative; static Next export, no auth, storage none. Repo over9000-apps/vector-clocks.
  {
    slug: "vector-clocks",
    surface: "app",
    title: "Who Came First",
    upstream: "https://vector-clocks.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/vector-clocks.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "See causality between machines with vector clocks.",
  },
  // Field Lines. Same-account workers.dev on digitalnative; static Next export, no auth, storage none. Repo over9000-apps/em-fields.
  {
    slug: "em-fields",
    surface: "app",
    title: "Field Lines",
    upstream: "https://em-fields.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/em-fields.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Drag charges and see the invisible field.",
  },
  // 42 Degrees. Same-account workers.dev on digitalnative; static Next export, no auth, storage none. Repo over9000-apps/rainbow-angle.
  {
    slug: "rainbow-angle",
    surface: "app",
    title: "42 Degrees",
    upstream: "https://rainbow-angle.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/rainbow-angle.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Why every rainbow sits at the same angle.",
  },
  // Foxes & Rabbits. Same-account workers.dev on digitalnative; static Next export, no auth, storage none. Repo over9000-apps/predator-prey.
  {
    slug: "predator-prey",
    surface: "app",
    title: "Foxes & Rabbits",
    upstream: "https://predator-prey.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/predator-prey.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Watch populations boom and crash in cycles.",
  },
  // Bean Machine. Same-account workers.dev on digitalnative; static Next export, no auth, storage none. Repo over9000-apps/central-limit-box.
  {
    slug: "central-limit-box",
    surface: "app",
    title: "Bean Machine",
    upstream: "https://central-limit-box.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/central-limit-box.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Drop balls and watch the bell curve appear.",
  },
  // Into Chaos. Same-account workers.dev on digitalnative; static Next export, no auth, storage none. Repo over9000-apps/logistic-bifurcation.
  {
    slug: "logistic-bifurcation",
    surface: "app",
    title: "Into Chaos",
    upstream: "https://logistic-bifurcation.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/logistic-bifurcation.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Zoom into the doorway of chaos.",
  },
  // Deadlock Diner. Same-account workers.dev on digitalnative; static Next export, no auth, storage none. Repo over9000-apps/deadlock-diner.
  {
    slug: "deadlock-diner",
    surface: "app",
    title: "Deadlock Diner",
    upstream: "https://deadlock-diner.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/deadlock-diner.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Hungry philosophers, too few forks.",
  },
  // Truth Hunt. Same-account workers.dev on digitalnative; static Next export, no auth, storage none. Repo over9000-apps/sat-solver.
  {
    slug: "sat-solver",
    surface: "app",
    title: "Truth Hunt",
    upstream: "https://sat-solver.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/sat-solver.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Watch a SAT solver hunt for a solution.",
  },
  // Flip the Trend. Same-account workers.dev on digitalnative; static Next export, no auth, storage none. Repo over9000-apps/simpson-paradox.
  {
    slug: "simpson-paradox",
    surface: "app",
    title: "Flip the Trend",
    upstream: "https://simpson-paradox.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/simpson-paradox.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Every group goes up, yet the total goes down.",
  },
  // Bet Size. Same-account workers.dev on digitalnative; static Next export, no auth, storage none. Repo over9000-apps/kelly-bet.
  {
    slug: "kelly-bet",
    surface: "app",
    title: "Bet Size",
    upstream: "https://kelly-bet.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/kelly-bet.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Find the bet size that grows money fastest.",
  },
  // Antagonist Absence Engine. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/antagonist-absence-engine.
  {
    slug: "antagonist-absence-engine",
    surface: "app",
    title: "Antagonist Absence Engine",
    upstream: "https://antagonist-absence-engine.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/antagonist-absence-engine.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Reconstruct an absent actor from action histories that cover their effects.",
  },
  // Anaglyph Object Painter. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/anaglyph-object-painter.
  {
    slug: "anaglyph-object-painter",
    surface: "app",
    title: "Anaglyph Object Painter",
    upstream: "https://anaglyph-object-painter.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/anaglyph-object-painter.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Paint eye-specific surface channels on a depth-tested cube.",
  },
  // Area Loyal Animator. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/area-loyal-animator.
  {
    slug: "area-loyal-animator",
    surface: "app",
    title: "Area Loyal Animator",
    upstream: "https://area-loyal-animator.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/area-loyal-animator.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Animate discrete polygons with pinned anchors and independently audited area conservation.",
  },
  // Ambigram Hinges. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/ambigram-hinges.
  {
    slug: "ambigram-hinges",
    surface: "app",
    title: "Ambigram Hinges",
    upstream: "https://ambigram-hinges.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/ambigram-hinges.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Draw shared-skeleton ambigram lettering with constrained hinges and exact half-turn proofs.",
  },
  // Ambient Ink. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/ambient-ink.
  {
    slug: "ambient-ink",
    surface: "app",
    title: "Ambient Ink",
    upstream: "https://ambient-ink.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/ambient-ink.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Author region states at four atmosphere corners and blend them manually.",
  },
  // Alpha Fringe Clinic. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/alpha-fringe-clinic.
  {
    slug: "alpha-fringe-clinic",
    surface: "app",
    title: "Alpha Fringe Clinic",
    upstream: "https://alpha-fringe-clinic.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/alpha-fringe-clinic.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Repair RGB contamination within an approved alpha boundary band without changing the silhouette in a local raw-RGBA image preflight.",
  },
  // Wishes With Literal Edges. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/wishes-with-literal-edges.
  {
    slug: "wishes-with-literal-edges",
    surface: "app",
    title: "Wishes With Literal Edges",
    upstream: "https://wishes-with-literal-edges.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/wishes-with-literal-edges.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Solve finite toy-world wish puzzles with quantified targets, exceptions, simultaneous effects, and concrete counterexamples.",
  },
  // Unsounded Duet. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/unsounded-duet.
  {
    slug: "unsounded-duet",
    surface: "app",
    title: "Unsounded Duet",
    upstream: "https://unsounded-duet.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/unsounded-duet.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Play a live two-player XOR listening instrument where shared pitches vanish.",
  },
  // Unremembering Chorus. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/unremembering-chorus.
  {
    slug: "unremembering-chorus",
    surface: "app",
    title: "Unremembering Chorus",
    upstream: "https://unremembering-chorus.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/unremembering-chorus.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore a four-reader human-retention chorus with branch-specific word capacities.",
  },
  // Unreliable Credits. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/unreliable-credits.
  {
    slug: "unreliable-credits",
    surface: "app",
    title: "Unreliable Credits",
    upstream: "https://unreliable-credits.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/unreliable-credits.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Tell a fictional hidden story through evidence-backed disputed credits and transitive acknowledgments.",
  },
  // Typing Gesture Score. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/typing-gesture-score.
  {
    slug: "typing-gesture-score",
    surface: "app",
    title: "Typing Gesture Score",
    upstream: "https://typing-gesture-score.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/typing-gesture-score.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Turn deliberate composition timings and revisions into expressive typographic performance.",
  },
  // Type Pressure Map. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/type-pressure-map.
  {
    slug: "type-pressure-map",
    surface: "app",
    title: "Type Pressure Map",
    upstream: "https://type-pressure-map.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/type-pressure-map.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore a map of typographic pressure.",
  },
  // Transient Punctuation Editor. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/transient-punctuation-editor.
  {
    slug: "transient-punctuation-editor",
    surface: "app",
    title: "Transient Punctuation Editor",
    upstream: "https://transient-punctuation-editor.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/transient-punctuation-editor.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Punctuate continuous sound without changing its duration.",
  },
  // Touch Weather. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/touch-weather.
  {
    slug: "touch-weather",
    surface: "app",
    title: "Touch Weather",
    upstream: "https://touch-weather.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/touch-weather.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Paint a local age map where fresh pigment moves, settling pigment hesitates, and fixed pigment holds.",
  },
  // Tape Transfer Atlas. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/tape-transfer-atlas.
  {
    slug: "tape-transfer-atlas",
    surface: "app",
    title: "Tape Transfer Atlas",
    upstream: "https://tape-transfer-atlas.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/tape-transfer-atlas.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Lift original marks onto tape, transfer them once, and retain residue in a finite-pigment collage.",
  },
  // Substitution Theater. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/substitution-theater.
  {
    slug: "substitution-theater",
    surface: "app",
    title: "Substitution Theater",
    upstream: "https://substitution-theater.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/substitution-theater.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Author original contextual glyph substitutions and inspect every winning cue.",
  },
  // Subtitle Mutineers. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/subtitle-mutineers.
  {
    slug: "subtitle-mutineers",
    surface: "app",
    title: "Subtitle Mutineers",
    upstream: "https://subtitle-mutineers.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/subtitle-mutineers.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Experiment with mutinous subtitles.",
  },
  // Stroke Ferry. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/stroke-ferry.
  {
    slug: "stroke-ferry",
    surface: "app",
    title: "Stroke Ferry",
    upstream: "https://stroke-ferry.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/stroke-ferry.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Draw across disconnected pages with affine edge portals.",
  },
  // Story of a Correction. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/story-of-a-correction.
  {
    slug: "story-of-a-correction",
    surface: "app",
    title: "Story of a Correction",
    upstream: "https://story-of-a-correction.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/story-of-a-correction.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore original interactive fiction revealed through corrections that retain exact prior statements.",
  },
  // Status Seesaw Stage. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/status-seesaw-stage.
  {
    slug: "status-seesaw-stage",
    surface: "app",
    title: "Status Seesaw Stage",
    upstream: "https://status-seesaw-stage.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/status-seesaw-stage.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore shifting status on a seesaw stage.",
  },
  // Squeegee Routes. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/squeegee-routes.
  {
    slug: "squeegee-routes",
    surface: "app",
    title: "Squeegee Routes",
    upstream: "https://squeegee-routes.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/squeegee-routes.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Print with a finite 16-band pigment reservoir, editable squeegee routes and exact local exports.",
  },
  // Spread Echo. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/spread-echo.
  {
    slug: "spread-echo",
    surface: "app",
    title: "Spread Echo",
    upstream: "https://spread-echo.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/spread-echo.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Reorder eight illustrated spreads and mend their edges to keep three visual threads continuous.",
  },
  // Soundtrack Without the Film. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/soundtrack-without-the-film.
  {
    slug: "soundtrack-without-the-film",
    surface: "app",
    title: "Soundtrack Without the Film",
    upstream: "https://soundtrack-without-the-film.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/soundtrack-without-the-film.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Co-create incompatible imagined films against one unchanged original sound reel.",
  },
  // Sound With a Hidden Handle. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sound-with-a-hidden-handle.
  {
    slug: "sound-with-a-hidden-handle",
    surface: "app",
    title: "Sound With a Hidden Handle",
    upstream: "https://sound-with-a-hidden-handle.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sound-with-a-hidden-handle.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Discover a hidden gesture-to-sound mapping, then reveal and audit its 27-cell control diagram.",
  },
  // Sound Object Name Game. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sound-object-name-game.
  {
    slug: "sound-object-name-game",
    surface: "app",
    title: "Sound Object Name Game",
    upstream: "https://sound-object-name-game.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sound-object-name-game.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Negotiate invented names for twelve original sounds, then compose and decode through saved dictionaries.",
  },
  // Sound Echo of a Choice. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sound-echo-of-a-choice.
  {
    slug: "sound-echo-of-a-choice",
    surface: "app",
    title: "Sound Echo of a Choice",
    upstream: "https://sound-echo-of-a-choice.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sound-echo-of-a-choice.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Four actions remember your promises as convergent rooms answer with history-dependent sounds.",
  },
  // Sound Assembly Autopsy. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sound-assembly-autopsy.
  {
    slug: "sound-assembly-autopsy",
    surface: "app",
    title: "Sound Assembly Autopsy",
    upstream: "https://sound-assembly-autopsy.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sound-assembly-autopsy.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Disassemble original impacts into fixed-endpoint attack, body, rattle and decay stems.",
  },
  // Sonic Calligraphy Without Ink. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sonic-calligraphy-without-ink.
  {
    slug: "sonic-calligraphy-without-ink",
    surface: "app",
    title: "Sonic Calligraphy Without Ink",
    upstream: "https://sonic-calligraphy-without-ink.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sonic-calligraphy-without-ink.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Turn one hidden stroke into a reproducible six-primitive sonic signature.",
  },
  // Slow Event Magnifier. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/slow-event-magnifier.
  {
    slug: "slow-event-magnifier",
    surface: "app",
    title: "Slow Event Magnifier",
    upstream: "https://slow-event-magnifier.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/slow-event-magnifier.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Expand twelve manual onset intervals while preserving each synthetic foley microfragment's local sample spacing.",
  },
  // Shadow Custody Theatre. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/shadow-custody-theatre.
  {
    slug: "shadow-custody-theatre",
    surface: "app",
    title: "Shadow Custody Theatre",
    upstream: "https://shadow-custody-theatre.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/shadow-custody-theatre.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Two puppets negotiate one existence-enabling shadow.",
  },
  // Silent Character Veto. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/silent-character-veto.
  {
    slug: "silent-character-veto",
    surface: "app",
    title: "Silent Character Veto",
    upstream: "https://silent-character-veto.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/silent-character-veto.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Block dependent plans through refusal-driven theatrical microfiction without selecting replacements.",
  },
  // Shadow Swap Collage. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/shadow-swap-collage.
  {
    slug: "shadow-swap-collage",
    surface: "app",
    title: "Shadow Swap Collage",
    upstream: "https://shadow-swap-collage.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/shadow-swap-collage.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Author a 2D silhouette collage by editing a graph of shadow ownership.",
  },
  // Sentence Baton Relay. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sentence-baton-relay.
  {
    slug: "sentence-baton-relay",
    surface: "app",
    title: "Sentence Baton Relay",
    upstream: "https://sentence-baton-relay.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sentence-baton-relay.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Write a local relay where each clause closes one typed grammatical obligation and opens exactly one next dependency.",
  },
  // Sentence Currency Stage. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sentence-currency-stage.
  {
    slug: "sentence-currency-stage",
    surface: "app",
    title: "Sentence Currency Stage",
    upstream: "https://sentence-currency-stage.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sentence-currency-stage.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Two performers spend a shared vocabulary budget with one-time partner-word rebates.",
  },
  // Scanline Reweaver. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/scanline-reweaver.
  {
    slug: "scanline-reweaver",
    surface: "app",
    title: "Scanline Reweaver",
    upstream: "https://scanline-reweaver.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/scanline-reweaver.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Draw inverse scanline trajectories, pin exact regions, and export raw RGBA artwork.",
  },
  // Same Color Different World. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/same-color-different-world.
  {
    slug: "same-color-different-world",
    surface: "app",
    title: "Same Color Different World",
    upstream: "https://same-color-different-world.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/same-color-different-world.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Author different surrounds around identical RGB centers, then reveal equality with a movable bridge.",
  },
  // Reverse Cause Radio. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/reverse-cause-radio.
  {
    slug: "reverse-cause-radio",
    surface: "app",
    title: "Reverse Cause Radio",
    upstream: "https://reverse-cause-radio.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/reverse-cause-radio.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Reconstruct any valid causal order in effect-before-cause audio fiction while every clip plays forward.",
  },
  // Rumor Mutation Story. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/rumor-mutation-story.
  {
    slug: "rumor-mutation-story",
    surface: "app",
    title: "Rumor Mutation Story",
    upstream: "https://rumor-mutation-story.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/rumor-mutation-story.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Pass one original story through six messengers and track how each retelling changes the account.",
  },
  // Reveal Reciprocity. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/reveal-reciprocity.
  {
    slug: "reveal-reciprocity",
    surface: "app",
    title: "Reveal Reciprocity",
    upstream: "https://reveal-reciprocity.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/reveal-reciprocity.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Edit an original diptych while conserving total visibility across both panels.",
  },
  // Redaction Garden. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/redaction-garden.
  {
    slug: "redaction-garden",
    surface: "app",
    title: "Redaction Garden",
    upstream: "https://redaction-garden.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/redaction-garden.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Paint isolated reveal masks over original text and drawings, each unlocked by hold, scrub or circle gestures.",
  },
  // Reflection Exceptions. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/reflection-exceptions.
  {
    slug: "reflection-exceptions",
    surface: "app",
    title: "Reflection Exceptions",
    upstream: "https://reflection-exceptions.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/reflection-exceptions.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Create almost-symmetric artwork with paired exception masks, independent side edits and exact RGBA exports.",
  },
  // Rectangular Contact Press. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/rectangular-contact-press.
  {
    slug: "rectangular-contact-press",
    surface: "app",
    title: "Rectangular Contact Press",
    upstream: "https://rectangular-contact-press.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/rectangular-contact-press.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Specify which topics must touch, then arrange them into a rectangular contact plate.",
  },
  // Puppet Four Directors. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/puppet-four-directors.
  {
    slug: "puppet-four-directors",
    surface: "app",
    title: "Puppet Four Directors",
    upstream: "https://puppet-four-directors.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/puppet-four-directors.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Negotiate atomic geometric poses through four-owned joint angles in a local paper puppet theatre.",
  },
  // Pronoun Possession Story. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/pronoun-possession-story.
  {
    slug: "pronoun-possession-story",
    surface: "app",
    title: "Pronoun Possession Story",
    upstream: "https://pronoun-possession-story.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/pronoun-possession-story.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Assign I, you and they to transfer agency in referent-bound fiction while fixed event facts remain unchanged.",
  },
  // Punctuation Performance Exchange. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/punctuation-performance-exchange.
  {
    slug: "punctuation-performance-exchange",
    surface: "app",
    title: "Punctuation Performance Exchange",
    upstream: "https://punctuation-performance-exchange.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/punctuation-performance-exchange.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Perform immutable twenty-word scenes with two readers and conserved movable punctuation IDs.",
  },
  // Promise Debt Fable. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/promise-debt-fable.
  {
    slug: "promise-debt-fable",
    surface: "app",
    title: "Promise Debt Fable",
    upstream: "https://promise-debt-fable.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/promise-debt-fable.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore an original fable whose promises change owners, not scores.",
  },
  // Pressure Islands. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/pressure-islands.
  {
    slug: "pressure-islands",
    surface: "app",
    title: "Pressure Islands",
    upstream: "https://pressure-islands.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/pressure-islands.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Draw a pressure field and lift a threshold to split exact 4-connected islands.",
  },
  // Pocket Vandal. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/pocket-vandal.
  {
    slug: "pocket-vandal",
    surface: "app",
    title: "Pocket Vandal",
    upstream: "https://pocket-vandal.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/pocket-vandal.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Reshape resilient authored artwork with bounded tug, squash and scratch rules.",
  },
  // Pocket Acousmatic Museum. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/pocket-acousmatic-museum.
  {
    slug: "pocket-acousmatic-museum",
    surface: "app",
    title: "Pocket Acousmatic Museum",
    upstream: "https://pocket-acousmatic-museum.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/pocket-acousmatic-museum.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore a private sound-first museum, describing unscored original exhibits before revealing their sources.",
  },
  // Plot Hole Postcards. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/plot-hole-postcards.
  {
    slug: "plot-hole-postcards",
    surface: "app",
    title: "Plot Hole Postcards",
    upstream: "https://plot-hole-postcards.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/plot-hole-postcards.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Repair twenty original causal-gap postcards with minimum-card fact sets.",
  },
  // Pixel Archaeologist. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/pixel-archaeologist.
  {
    slug: "pixel-archaeologist",
    surface: "app",
    title: "Pixel Archaeologist",
    upstream: "https://pixel-archaeologist.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/pixel-archaeologist.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Paint original image strata and abrade a continuous depth field.",
  },
  // Pictogram Syntax. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/pictogram-syntax.
  {
    slug: "pictogram-syntax",
    surface: "app",
    title: "Pictogram Syntax",
    upstream: "https://pictogram-syntax.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/pictogram-syntax.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Author an original visual language and audit exact composited-occupancy collisions.",
  },
  // Parallel Pen Pal. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/parallel-pen-pal.
  {
    slug: "parallel-pen-pal",
    surface: "app",
    title: "Parallel Pen Pal",
    upstream: "https://parallel-pen-pal.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/parallel-pen-pal.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Draw with deliberate local rules, exact ghost previews and swept ink boundaries.",
  },
  // Patch Rotation Court. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/patch-rotation-court.
  {
    slug: "patch-rotation-court",
    surface: "app",
    title: "Patch Rotation Court",
    upstream: "https://patch-rotation-court.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/patch-rotation-court.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Compose a pixel-exact reversible RGBA tile collage.",
  },
  // Paper Weave Slit Check. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/paper-weave-slit-check.
  {
    slug: "paper-weave-slit-check",
    surface: "app",
    title: "Paper Weave Slit Check",
    upstream: "https://paper-weave-slit-check.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/paper-weave-slit-check.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Check paper backing connectivity and numbered straight-band insertion routes.",
  },
  // Only Offstage Things Happen. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/only-offstage-things-happen.
  {
    slug: "only-offstage-things-happen",
    surface: "app",
    title: "Only Offstage Things Happen",
    upstream: "https://only-offstage-things-happen.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/only-offstage-things-happen.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Freeze a puppet room through observation and close the curtain to permit one authored transformation.",
  },
  // One Word Stage Right. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/one-word-stage-right.
  {
    slug: "one-word-stage-right",
    surface: "app",
    title: "One Word Stage Right",
    upstream: "https://one-word-stage-right.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/one-word-stage-right.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Two readers share one script under independent forbidden speaking windows.",
  },
  // Nonexistent Book Reviews. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/nonexistent-book-reviews.
  {
    slug: "nonexistent-book-reviews",
    surface: "app",
    title: "Nonexistent Book Reviews",
    upstream: "https://nonexistent-book-reviews.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/nonexistent-book-reviews.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Invent an absent book through typed factual canon and independently conflicting taste.",
  },
  // One Note Dramaturgy. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/one-note-dramaturgy.
  {
    slug: "one-note-dramaturgy",
    surface: "app",
    title: "One Note Dramaturgy",
    upstream: "https://one-note-dramaturgy.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/one-note-dramaturgy.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Compose a sixty-second dramatic arc with invariant pitch class, octave doubling, density and silence.",
  },
  // Narrator With a Finite Lexicon. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/narrator-with-a-finite-lexicon.
  {
    slug: "narrator-with-a-finite-lexicon",
    surface: "app",
    title: "Narrator With a Finite Lexicon",
    upstream: "https://narrator-with-a-finite-lexicon.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/narrator-with-a-finite-lexicon.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Earn lexical inventory through encounters to reveal an original twelve-passage story.",
  },
  // Narrative Smuggling Game. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/narrative-smuggling-game.
  {
    slug: "narrative-smuggling-game",
    surface: "app",
    title: "Narrative Smuggling Game",
    upstream: "https://narrative-smuggling-game.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/narrative-smuggling-game.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Play a game of narrative smuggling.",
  },
  // Narrative Zoom Contract. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/narrative-zoom-contract.
  {
    slug: "narrative-zoom-contract",
    surface: "app",
    title: "Narrative Zoom Contract",
    upstream: "https://narrative-zoom-contract.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/narrative-zoom-contract.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore eight scenes at three reading levels within an exact authored granularity budget.",
  },
  // Narrative Hotel Switchboard. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/narrative-hotel-switchboard.
  {
    slug: "narrative-hotel-switchboard",
    surface: "app",
    title: "Narrative Hotel Switchboard",
    upstream: "https://narrative-hotel-switchboard.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/narrative-hotel-switchboard.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Route twelve fictional calls, transferring one fact to one permitted hotel character.",
  },
  // Narrative Floor Is Lava. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/narrative-floor-is-lava.
  {
    slug: "narrative-floor-is-lava",
    surface: "app",
    title: "Narrative Floor Is Lava",
    upstream: "https://narrative-floor-is-lava.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/narrative-floor-is-lava.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Renew fictional facts before their turn-expiring evidence disappears.",
  },
  // Musical Question Mark. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/musical-question-mark.
  {
    slug: "musical-question-mark",
    surface: "app",
    title: "Musical Question Mark",
    upstream: "https://musical-question-mark.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/musical-question-mark.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore six open musical calls and eighteen explicit answer continuations.",
  },
  // Musical Flip of a Coin. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/musical-flip-of-a-coin.
  {
    slug: "musical-flip-of-a-coin",
    surface: "app",
    title: "Musical Flip of a Coin",
    upstream: "https://musical-flip-of-a-coin.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/musical-flip-of-a-coin.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Compose with auditable seeded chance, retaining every accepted and rejected musical proposal.",
  },
  // Museum of Unfinished Sounds. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/museum-of-unfinished-sounds.
  {
    slug: "museum-of-unfinished-sounds",
    surface: "app",
    title: "Museum of Unfinished Sounds",
    upstream: "https://museum-of-unfinished-sounds.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/museum-of-unfinished-sounds.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore a museum of unfinished sounds.",
  },
  // Moire Route Studio. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/moire-route-studio.
  {
    slug: "moire-route-studio",
    surface: "app",
    title: "Moire Route Studio",
    upstream: "https://moire-route-studio.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/moire-route-studio.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore moire routes in an interactive studio.",
  },
  // Missing Piece Collage. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/missing-piece-collage.
  {
    slug: "missing-piece-collage",
    surface: "app",
    title: "Missing Piece Collage",
    upstream: "https://missing-piece-collage.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/missing-piece-collage.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Compose polygon collages with exact pixel ownership, cavity reuse, an offcut ledger and paired compositions.",
  },
  // Misheard Phonetics Theatre. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/misheard-phonetics-theatre.
  {
    slug: "misheard-phonetics-theatre",
    surface: "app",
    title: "Misheard Phonetics Theatre",
    upstream: "https://misheard-phonetics-theatre.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/misheard-phonetics-theatre.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Give one immutable audio clip two poetic readings with six original textures and local-only authoring.",
  },
  // Microphone-Free Beat Sculpt. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/microphone-free-beat-sculpt.
  {
    slug: "microphone-free-beat-sculpt",
    surface: "app",
    title: "Microphone-Free Beat Sculpt",
    upstream: "https://microphone-free-beat-sculpt.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/microphone-free-beat-sculpt.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Sculpt percussion through slow position and dwell gestures with twelve pressure-free articulations.",
  },
  // Metaphor Physics Cabinet. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/metaphor-physics-cabinet.
  {
    slug: "metaphor-physics-cabinet",
    surface: "app",
    title: "Metaphor Physics Cabinet",
    upstream: "https://metaphor-physics-cabinet.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/metaphor-physics-cabinet.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore ten authored events under six fictional laws, tracing expected versus actual exception causality.",
  },
  // Memoryless Mural. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/memoryless-mural.
  {
    slug: "memoryless-mural",
    surface: "app",
    title: "Memoryless Mural",
    upstream: "https://memoryless-mural.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/memoryless-mural.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Create a memoryless mural.",
  },
  // Mass Transport Painting. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/mass-transport-painting.
  {
    slug: "mass-transport-painting",
    surface: "app",
    title: "Mass Transport Painting",
    upstream: "https://mass-transport-painting.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/mass-transport-painting.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore painting through mass transport.",
  },
  // Margin Lanes. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/margin-lanes.
  {
    slug: "margin-lanes",
    surface: "app",
    title: "Margin Lanes",
    upstream: "https://margin-lanes.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/margin-lanes.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Edit across three voice lanes with stable text ranges, measured DOM anchors and collision-free note packing.",
  },
  // Luminance Weaving Desk. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/luminance-weaving-desk.
  {
    slug: "luminance-weaving-desk",
    surface: "app",
    title: "Luminance Weaving Desk",
    upstream: "https://luminance-weaving-desk.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/luminance-weaving-desk.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Paint with independent OKLab lightness and chroma while seeing gamut compromises.",
  },
  // Literal Stage Directions. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/literal-stage-directions.
  {
    slug: "literal-stage-directions",
    surface: "app",
    title: "Literal Stage Directions",
    upstream: "https://literal-stage-directions.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/literal-stage-directions.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Direct a toy world with twenty typed stage verbs, deterministic transitions and token-budget repairs.",
  },
  // Lineage Stamps. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/lineage-stamps.
  {
    slug: "lineage-stamps",
    surface: "app",
    title: "Lineage Stamps",
    upstream: "https://lineage-stamps.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/lineage-stamps.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore seeded per-vertex stamp inheritance and live-linked printmaking.",
  },
  // Line-Wrap Players. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/line-wrap-players.
  {
    slug: "line-wrap-players",
    surface: "app",
    title: "Line-Wrap Players",
    upstream: "https://line-wrap-players.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/line-wrap-players.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Let browser line wrapping gate authored neighbor dialogue.",
  },
  // Letter Addressed to an Object. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/letter-addressed-to-an-object.
  {
    slug: "letter-addressed-to-an-object",
    surface: "app",
    title: "Letter Addressed to an Object",
    upstream: "https://letter-addressed-to-an-object.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/letter-addressed-to-an-object.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore six original letters through typed material-state correspondence without object speech.",
  },
  // Layout Grammar Bench. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/layout-grammar-bench.
  {
    slug: "layout-grammar-bench",
    surface: "app",
    title: "Layout Grammar Bench",
    upstream: "https://layout-grammar-bench.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/layout-grammar-bench.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore layout grammar at an interactive workbench.",
  },
  // Laugh Track Against the Grain. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/laugh-track-against-the-grain.
  {
    slug: "laugh-track-against-the-grain",
    surface: "app",
    title: "Laugh Track Against the Grain",
    upstream: "https://laugh-track-against-the-grain.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/laugh-track-against-the-grain.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Turn deliberately delayed spectator responses into human performance constraints.",
  },
  // Lag Brush Duet. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/lag-brush-duet.
  {
    slug: "lag-brush-duet",
    surface: "app",
    title: "Lag Brush Duet",
    upstream: "https://lag-brush-duet.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/lag-brush-duet.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Draw human-timed lead and follower ribbons with local delay and bounded attractor fields.",
  },
  // Iris Folding Routebook. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/iris-folding-routebook.
  {
    slug: "iris-folding-routebook",
    surface: "app",
    title: "Iris Folding Routebook",
    upstream: "https://iris-folding-routebook.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/iris-folding-routebook.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore symbolic shrinking-spiral folded-strip routes with exact ordered ownership replay.",
  },
  // Interval Spelling Bee. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/interval-spelling-bee.
  {
    slug: "interval-spelling-bee",
    surface: "app",
    title: "Interval Spelling Bee",
    upstream: "https://interval-spelling-bee.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/interval-spelling-bee.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Practice interval spelling in a musical spelling bee.",
  },
  // Interruption Stack Theatre. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/interruption-stack-theatre.
  {
    slug: "interruption-stack-theatre",
    surface: "app",
    title: "Interruption Stack Theatre",
    upstream: "https://interruption-stack-theatre.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/interruption-stack-theatre.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore a theatre of stacked interruptions.",
  },
  // Interruption Ink. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/interruption-ink.
  {
    slug: "interruption-ink",
    surface: "app",
    title: "Interruption Ink",
    upstream: "https://interruption-ink.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/interruption-ink.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Perform timed pigment gates on invisible gesture paths.",
  },
  // Intent Envelope Theatre. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/intent-envelope-theatre.
  {
    slug: "intent-envelope-theatre",
    surface: "app",
    title: "Intent Envelope Theatre",
    upstream: "https://intent-envelope-theatre.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/intent-envelope-theatre.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Perform three live readings of invariant text with hidden fictional intentions and a local reveal grid.",
  },
  // Ink Ration. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/ink-ration.
  {
    slug: "ink-ration",
    surface: "app",
    title: "Ink Ration",
    upstream: "https://ink-ration.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/ink-ration.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Draw with finite arc-length ink, per-tool allocations, partial erasure refunds, a conservation ledger and an SVG spend strip.",
  },
  // Ink Censor. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/ink-censor.
  {
    slug: "ink-censor",
    surface: "app",
    title: "Ink Censor",
    upstream: "https://ink-censor.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/ink-censor.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore planar forest and angle-constrained drawing with legal alternate continuations.",
  },
  // Incomplete Sound Jigsaw. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/incomplete-sound-jigsaw.
  {
    slug: "incomplete-sound-jigsaw",
    surface: "app",
    title: "Incomplete Sound Jigsaw",
    upstream: "https://incomplete-sound-jigsaw.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/incomplete-sound-jigsaw.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Assemble one-use sound clues around a deliberately user-created missing middle.",
  },
  // Growth Fence Studio. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/growth-fence-studio.
  {
    slug: "growth-fence-studio",
    surface: "app",
    title: "Growth Fence Studio",
    upstream: "https://growth-fence-studio.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/growth-fence-studio.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Edit differential contour growth with locked collision fences and deterministic interventions.",
  },
  // Impact Chain Cookbook. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/impact-chain-cookbook.
  {
    slug: "impact-chain-cookbook",
    surface: "app",
    title: "Impact Chain Cookbook",
    upstream: "https://impact-chain-cookbook.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/impact-chain-cookbook.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Build discrete causal-path Foley with parent-event provenance and canonical PCM.",
  },
  // Gradient Excavation. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/gradient-excavation.
  {
    slug: "gradient-excavation",
    surface: "app",
    title: "Gradient Excavation",
    upstream: "https://gradient-excavation.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/gradient-excavation.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Sculpt nested convex color boundaries with fixed palette order, signed-distance interpolation and protected plateaus.",
  },
  // Gesture Afterimage Score. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/gesture-afterimage-score.
  {
    slug: "gesture-afterimage-score",
    surface: "app",
    title: "Gesture Afterimage Score",
    upstream: "https://gesture-afterimage-score.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/gesture-afterimage-score.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Turn a past gesture into a timed, interruptible performance partner.",
  },
  // Glyph Graft. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/glyph-graft.
  {
    slug: "glyph-graft",
    surface: "app",
    title: "Glyph Graft",
    upstream: "https://glyph-graft.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/glyph-graft.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Shape a shared cubic alphabet with exact cuts, G1 grafts and detached instances.",
  },
  // Friction Notes. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/friction-notes.
  {
    slug: "friction-notes",
    surface: "app",
    title: "Friction Notes",
    upstream: "https://friction-notes.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/friction-notes.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Articulate a deterministic gesture score with five resisted note sliders, spring-friction physics and hysteresis.",
  },
  // Found Sound Casting. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/found-sound-casting.
  {
    slug: "found-sound-casting",
    surface: "app",
    title: "Found Sound Casting",
    upstream: "https://found-sound-casting.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/found-sound-casting.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Cast nonverbal characters through fixed gestures and local PCM auditions.",
  },
  // Forgetful House. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/forgetful-house.
  {
    slug: "forgetful-house",
    surface: "app",
    title: "Forgetful House",
    upstream: "https://forgetful-house.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/forgetful-house.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore a finite-slot fictional house where atomic memory eviction changes authored descriptions.",
  },
  // Fictional Postscript Engine. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/fictional-postscript-engine.
  {
    slug: "fictional-postscript-engine",
    surface: "app",
    title: "Fictional Postscript Engine",
    upstream: "https://fictional-postscript-engine.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/fictional-postscript-engine.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore invariant-letter fiction through single-fact postscripts and finite proof-traced interpretations.",
  },
  // Fictional Object Charades. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/fictional-object-charades.
  {
    slug: "fictional-object-charades",
    surface: "app",
    title: "Fictional Object Charades",
    upstream: "https://fictional-object-charades.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/fictional-object-charades.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Invent impossible objects through union-composed affordance deduction.",
  },
  // Fictional Lost and Found. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/fictional-lost-and-found.
  {
    slug: "fictional-lost-and-found",
    surface: "app",
    title: "Fictional Lost and Found",
    upstream: "https://fictional-lost-and-found.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/fictional-lost-and-found.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Competing fictional memories alter dependent scenes without declaring a single true owner.",
  },
  // Fictional Interview Blackout. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/fictional-interview-blackout.
  {
    slug: "fictional-interview-blackout",
    surface: "app",
    title: "Fictional Interview Blackout",
    upstream: "https://fictional-interview-blackout.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/fictional-interview-blackout.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore fictional interviews through blackouts.",
  },
  // Fictional Archive Query Play. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/fictional-archive-query-play.
  {
    slug: "fictional-archive-query-play",
    surface: "app",
    title: "Fictional Archive Query Play",
    upstream: "https://fictional-archive-query-play.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/fictional-archive-query-play.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Repeated searches revise a local microfiction archive, with deterministic replay and a disclosure ledger.",
  },
  // Feedback Aperture Bench. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/feedback-aperture-bench.
  {
    slug: "feedback-aperture-bench",
    surface: "app",
    title: "Feedback Aperture Bench",
    upstream: "https://feedback-aperture-bench.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/feedback-aperture-bench.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Experiment with feedback apertures.",
  },
  // Fictional Absence Calendar. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/fictional-absence-calendar.
  {
    slug: "fictional-absence-calendar",
    surface: "app",
    title: "Fictional Absence Calendar",
    upstream: "https://fictional-absence-calendar.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/fictional-absence-calendar.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore a calendar of fictional absences.",
  },
  // Exquisite Apertures. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/exquisite-apertures.
  {
    slug: "exquisite-apertures",
    surface: "app",
    title: "Exquisite Apertures",
    upstream: "https://exquisite-apertures.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/exquisite-apertures.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Compose static-image collages with arc-length moving apertures and exact overlap-bitmask source routes.",
  },
  // Eraser Orchestra. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/eraser-orchestra.
  {
    slug: "eraser-orchestra",
    surface: "app",
    title: "Eraser Orchestra",
    upstream: "https://eraser-orchestra.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/eraser-orchestra.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Compose an eraser orchestra.",
  },
  // Encore Deadlock. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/encore-deadlock.
  {
    slug: "encore-deadlock",
    surface: "app",
    title: "Encore Deadlock",
    upstream: "https://encore-deadlock.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/encore-deadlock.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Solve a puppet curtain-call puzzle with atomic minimal-source-SCC moves.",
  },
  // Endings As Evidence. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/endings-as-evidence.
  {
    slug: "endings-as-evidence",
    surface: "app",
    title: "Endings As Evidence",
    upstream: "https://endings-as-evidence.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/endings-as-evidence.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Infer a missing middle from multiple futures with exact minimum-cardinality story subset search.",
  },
  // Edge Choir. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/edge-choir.
  {
    slug: "edge-choir",
    surface: "app",
    title: "Edge Choir",
    upstream: "https://edge-choir.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/edge-choir.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Sculpt nested polygon contours with local neighbor nudging and exact clearance.",
  },
  // Dialogue Missing Question. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/dialogue-missing-question.
  {
    slug: "dialogue-missing-question",
    surface: "app",
    title: "Dialogue Missing Question",
    upstream: "https://dialogue-missing-question.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/dialogue-missing-question.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Reconstruct implied questions through explicit missing or conflicting premises and exhaustive authored-policy proofs.",
  },
  // Directional Dust. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/directional-dust.
  {
    slug: "directional-dust",
    surface: "app",
    title: "Directional Dust",
    upstream: "https://directional-dust.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/directional-dust.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Draw with fixed-center grains and independent axial combing.",
  },
  // Dependency Doodle. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/dependency-doodle.
  {
    slug: "dependency-doodle",
    surface: "app",
    title: "Dependency Doodle",
    upstream: "https://dependency-doodle.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/dependency-doodle.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Draw typed geometric compositions and export local interactive SVGs.",
  },
  // The Curtain No One Can Click. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/curtain-no-one-can-click.
  {
    slug: "curtain-no-one-can-click",
    surface: "app",
    title: "The Curtain No One Can Click",
    upstream: "https://curtain-no-one-can-click.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/curtain-no-one-can-click.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Withhold input in a quiet-gated silent umbrella theatre to create one finale and a local timing receipt.",
  },
  // Crossing Etiquette. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/crossing-etiquette.
  {
    slug: "crossing-etiquette",
    surface: "app",
    title: "Crossing Etiquette",
    upstream: "https://crossing-etiquette.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/crossing-etiquette.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore crossing etiquette through play.",
  },
  // Counterweight Letters. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/counterweight-letters.
  {
    slug: "counterweight-letters",
    surface: "app",
    title: "Counterweight Letters",
    upstream: "https://counterweight-letters.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/counterweight-letters.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Compose counterweighted letters.",
  },
  // Counterfactual Receipt Book. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/counterfactual-receipt-book.
  {
    slug: "counterfactual-receipt-book",
    surface: "app",
    title: "Counterfactual Receipt Book",
    upstream: "https://counterfactual-receipt-book.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/counterfactual-receipt-book.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore independent narrative interventions with strict descendant withdrawal and immutable causal-cost receipts.",
  },
  // Conditional Stage Caption. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/conditional-stage-caption.
  {
    slug: "conditional-stage-caption",
    surface: "app",
    title: "Conditional Stage Caption",
    upstream: "https://conditional-stage-caption.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/conditional-stage-caption.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore conditional captions for the stage.",
  },
  // Comedy of Exactly One Lie. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/comedy-of-exactly-one-lie.
  {
    slug: "comedy-of-exactly-one-lie",
    surface: "app",
    title: "Comedy of Exactly One Lie",
    upstream: "https://comedy-of-exactly-one-lie.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/comedy-of-exactly-one-lie.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Stage a three-role farce with one global false claim, independent beliefs and traceable mistaken actions.",
  },
  // Chromatic Debt. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/chromatic-debt.
  {
    slug: "chromatic-debt",
    surface: "app",
    title: "Chromatic Debt",
    upstream: "https://chromatic-debt.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/chromatic-debt.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Compose exact-area orthogonal colors with atomic paired boundary exchanges.",
  },
  // Chorus Sort Hearing. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/chorus-sort-hearing.
  {
    slug: "chorus-sort-hearing",
    surface: "app",
    title: "Chorus Sort Hearing",
    upstream: "https://chorus-sort-hearing.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/chorus-sort-hearing.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Hear six voices perform four stable permutations of an original poem.",
  },
  // Character Osmosis. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/character-osmosis.
  {
    slug: "character-osmosis",
    surface: "app",
    title: "Character Osmosis",
    upstream: "https://character-osmosis.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/character-osmosis.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Watch conserved mannerisms diffuse through a fictional museum ensemble.",
  },
  // Character Knowledge Doorways. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/character-knowledge-doorways.
  {
    slug: "character-knowledge-doorways",
    surface: "app",
    title: "Character Knowledge Doorways",
    upstream: "https://character-knowledge-doorways.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/character-knowledge-doorways.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Build provenance-aware interactive narratives with incompatible character reports.",
  },
  // Ceremony Permit Office. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/ceremony-permit-office.
  {
    slug: "ceremony-permit-office",
    surface: "app",
    title: "Ceremony Permit Office",
    upstream: "https://ceremony-permit-office.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/ceremony-permit-office.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore fictional consumable-stamp ceremonies with bounded reachability.",
  },
  // Boundary Borrowers. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/boundary-borrowers.
  {
    slug: "boundary-borrowers",
    surface: "app",
    title: "Boundary Borrowers",
    upstream: "https://boundary-borrowers.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/boundary-borrowers.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Shape shared-Bezier silhouettes in a local studio with draw-to-split topology.",
  },
  // Between Hands. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/between-hands.
  {
    slug: "between-hands",
    surface: "app",
    title: "Between Hands",
    upstream: "https://between-hands.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/between-hands.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Paint the shared space between two independent boundary traces.",
  },
  // Because It Already Happened. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/because-it-already-happened.
  {
    slug: "because-it-already-happened",
    surface: "app",
    title: "Because It Already Happened",
    upstream: "https://because-it-already-happened.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/because-it-already-happened.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Stage effects before their authored compatible causes in a reverse-explanation performance studio.",
  },
  // Bead Inventory Recomposer. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/bead-inventory-recomposer.
  {
    slug: "bead-inventory-recomposer",
    surface: "app",
    title: "Bead Inventory Recomposer",
    upstream: "https://bead-inventory-recomposer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/bead-inventory-recomposer.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Recolor mosaics with locked cells, min-cost assignment and a conserved bead inventory ledger.",
  },
  // Baseline Rendezvous. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/baseline-rendezvous.
  {
    slug: "baseline-rendezvous",
    surface: "app",
    title: "Baseline Rendezvous",
    upstream: "https://baseline-rendezvous.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/baseline-rendezvous.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Reunite editorial baselines with rational precision.",
  },
  // Audience Understudy Shadow. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/audience-understudy-shadow.
  {
    slug: "audience-understudy-shadow",
    surface: "app",
    title: "Audience Understudy Shadow",
    upstream: "https://audience-understudy-shadow.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/audience-understudy-shadow.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Spectators predict while performers evade aggregate expectations.",
  },
  // Audience Scene Segmentation. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/audience-scene-segmentation.
  {
    slug: "audience-scene-segmentation",
    surface: "app",
    title: "Audience Scene Segmentation",
    upstream: "https://audience-scene-segmentation.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/audience-scene-segmentation.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Four anonymous spectators mark subjective scene boundaries with immutable raw tracks and gap-chain timing portraits.",
  },
  // Auction of Unrepeatable Sounds. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/auction-of-unrepeatable-sounds.
  {
    slug: "auction-of-unrepeatable-sounds",
    surface: "app",
    title: "Auction of Unrepeatable Sounds",
    upstream: "https://auction-of-unrepeatable-sounds.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/auction-of-unrepeatable-sounds.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Allocate human sounds across four roles with irreversible material depletion.",
  },
  // Asynchronous Curtain Stage. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/asynchronous-curtain-stage.
  {
    slug: "asynchronous-curtain-stage",
    surface: "app",
    title: "Asynchronous Curtain Stage",
    upstream: "https://asynchronous-curtain-stage.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/asynchronous-curtain-stage.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Independent actor clocks preserve contradictory moments in an original parcel puppet theatre.",
  },
  // Addressless Mouth Router. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/addressless-mouth-router.
  {
    slug: "addressless-mouth-router",
    surface: "app",
    title: "Addressless Mouth Router",
    upstream: "https://addressless-mouth-router.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/addressless-mouth-router.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Perform three local roles with deterministic text-property routing, concealed delivery cards and a provenance reveal.",
  },
  // Actor as Automaton. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/actor-as-automaton.
  {
    slug: "actor-as-automaton",
    surface: "app",
    title: "Actor as Automaton",
    upstream: "https://actor-as-automaton.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/actor-as-automaton.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Steer a local human automaton with audience input, covered performer cards and traversed scores.",
  },
  // Acrostic Linebreak Studio. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/acrostic-linebreak-studio.
  {
    slug: "acrostic-linebreak-studio",
    surface: "app",
    title: "Acrostic Linebreak Studio",
    upstream: "https://acrostic-linebreak-studio.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/acrostic-linebreak-studio.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Find a second reading in unchanged words with a Unicode-aware full-passage acrostic linebreak solver.",
  },
  // Acoustic Misdelivery. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/acoustic-misdelivery.
  {
    slug: "acoustic-misdelivery",
    surface: "app",
    title: "Acoustic Misdelivery",
    upstream: "https://acoustic-misdelivery.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/acoustic-misdelivery.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore acoustic misdelivery through play.",
  },
  // Accent Without Tempo. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/accent-without-tempo.
  {
    slug: "accent-without-tempo",
    surface: "app",
    title: "Accent Without Tempo",
    upstream: "https://accent-without-tempo.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/accent-without-tempo.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Compare frozen-onset articulation with identical scheduled sample indices.",
  },
  // Creative Constraint Telegraph. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/creative-constraint-telegraph.
  {
    slug: "creative-constraint-telegraph",
    surface: "app",
    title: "Creative Constraint Telegraph",
    upstream: "https://creative-constraint-telegraph.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/creative-constraint-telegraph.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Reconstruct an unseen shape scene from a restricted visual DSL and inspect exactly what got lost.",
  },
  // Puzzle Symmetry Folder. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/puzzle-symmetry-folder.
  {
    slug: "puzzle-symmetry-folder",
    surface: "app",
    title: "Puzzle Symmetry Folder",
    upstream: "https://puzzle-symmetry-folder.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/puzzle-symmetry-folder.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Find exact geometric equivalence classes for authored puzzle boards, with a witness for each original.",
  },
  // Metaphor Mechanics. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/metaphor-mechanics.
  {
    slug: "metaphor-mechanics",
    surface: "app",
    title: "Metaphor Mechanics",
    upstream: "https://metaphor-mechanics.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/metaphor-mechanics.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Edit paired finite-state worlds to expose precisely where a declared analogy breaks.",
  },
  // Unfinished Piece Exchange. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/unfinished-piece-exchange.
  {
    slug: "unfinished-piece-exchange",
    surface: "app",
    title: "Unfinished Piece Exchange",
    upstream: "https://unfinished-piece-exchange.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/unfinished-piece-exchange.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Continue another maker's artwork locally under a sealed geometric predicate.",
  },
  // Constraint Auction Studio. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/constraint-auction-studio.
  {
    slug: "constraint-auction-studio",
    surface: "app",
    title: "Constraint Auction Studio",
    upstream: "https://constraint-auction-studio.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/constraint-auction-studio.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Negotiate with fictional credits to create a usable art brief.",
  },
  // Story Through Instructions. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/story-through-instructions.
  {
    slug: "story-through-instructions",
    surface: "app",
    title: "Story Through Instructions",
    upstream: "https://story-through-instructions.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/story-through-instructions.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore a story told through instructions.",
  },
  // One Letter Too Late. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/one-letter-too-late.
  {
    slug: "one-letter-too-late",
    surface: "app",
    title: "One Letter Too Late",
    upstream: "https://one-letter-too-late.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/one-letter-too-late.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore a story one letter too late.",
  },
  // Unreliable Index. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/unreliable-index.
  {
    slug: "unreliable-index",
    surface: "app",
    title: "Unreliable Index",
    upstream: "https://unreliable-index.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/unreliable-index.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Read a forty-entry index-novel of canonical aliases, disputed accounts and mutually exclusive fragments.",
  },
  // Story in the Pause Button. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/story-in-the-pause-button.
  {
    slug: "story-in-the-pause-button",
    surface: "app",
    title: "Story in the Pause Button",
    upstream: "https://story-in-the-pause-button.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/story-in-the-pause-button.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Pause actual narration to reveal a parallel authored account.",
  },
  // Object First Biography. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/object-first-biography.
  {
    slug: "object-first-biography",
    surface: "app",
    title: "Object First Biography",
    upstream: "https://object-first-biography.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/object-first-biography.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Follow a copper bowl through six fictional owners and ten scenes, with material continuity shaping its biography.",
  },
  // Stereo Fold Letter. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/stereo-fold-letter.
  {
    slug: "stereo-fold-letter",
    surface: "app",
    title: "Stereo Fold Letter",
    upstream: "https://stereo-fold-letter.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/stereo-fold-letter.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Create two-edition audio postcards through mid-side channel algebra.",
  },
  // Borrowed Ending Theatre. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/borrowed-ending-theatre.
  {
    slug: "borrowed-ending-theatre",
    surface: "app",
    title: "Borrowed Ending Theatre",
    upstream: "https://borrowed-ending-theatre.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/borrowed-ending-theatre.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Recombine nine original microfictions with minimal authored causal repairs.",
  },
  // False Map Fable. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/false-map-fable.
  {
    slug: "false-map-fable",
    surface: "app",
    title: "False Map Fable",
    upstream: "https://false-map-fable.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/false-map-fable.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Use evidence to repair a narrator's vocabulary across a fixed eight-room fable.",
  },
  // Projection Patch Quilt. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/projection-patch-quilt.
  {
    slug: "projection-patch-quilt",
    surface: "app",
    title: "Projection Patch Quilt",
    upstream: "https://projection-patch-quilt.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/projection-patch-quilt.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Map moving ribbon artwork onto independent manually aligned planar quads using true homographies.",
  },
  // Material Translation Relay. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/material-translation-relay.
  {
    slug: "material-translation-relay",
    surface: "app",
    title: "Material Translation Relay",
    upstream: "https://material-translation-relay.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/material-translation-relay.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore a creative relay of material translations.",
  },
  // Fog Pocket Sculptor. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/fog-pocket-sculptor.
  {
    slug: "fog-pocket-sculptor",
    surface: "app",
    title: "Fog Pocket Sculptor",
    upstream: "https://fog-pocket-sculptor.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/fog-pocket-sculptor.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Paint static 3D colored-density pockets, erase sight tunnels and explore bounded ray-integrated views.",
  },
  // Millefiori Slice Theatre. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/millefiori-slice-theatre.
  {
    slug: "millefiori-slice-theatre",
    surface: "app",
    title: "Millefiori Slice Theatre",
    upstream: "https://millefiori-slice-theatre.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/millefiori-slice-theatre.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Compose transformed colored polygon rods and discover real plane-intersection slices.",
  },
  // Light Slit. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/light-slit-portrait-volume.
  {
    slug: "light-slit-portrait-volume",
    surface: "app",
    title: "Light Slit",
    upstream: "https://light-slit-portrait-volume.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/light-slit-portrait-volume.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Turn local video slit samples into a rotatable depth sculpture.",
  },
  // Branch Graft Studio. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/branch-graft-studio.
  {
    slug: "branch-graft-studio",
    surface: "app",
    title: "Branch Graft Studio",
    upstream: "https://branch-graft-studio.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/branch-graft-studio.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Create a manual branching sculpture with independent graft topology.",
  },
  // Marquetry Grain Director. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/marquetry-grain-director.
  {
    slug: "marquetry-grain-director",
    surface: "app",
    title: "Marquetry Grain Director",
    upstream: "https://marquetry-grain-director.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/marquetry-grain-director.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Direct polygon grain orientation and seam-reflected bookmatching in an original marquetry study.",
  },
  // Frontback Stitch Dialogue. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/frontback-stitch-dialogue.
  {
    slug: "frontback-stitch-dialogue",
    surface: "app",
    title: "Frontback Stitch Dialogue",
    upstream: "https://frontback-stitch-dialogue.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/frontback-stitch-dialogue.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Compose two intentional card surfaces with one alternating-side stitch route.",
  },
  // Quilling Strip Score. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/quilling-strip-score.
  {
    slug: "quilling-strip-score",
    surface: "app",
    title: "Quilling Strip Score",
    upstream: "https://quilling-strip-score.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/quilling-strip-score.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Shape continuous paper-strip centerlines with converged geometric lengths and stable-ID assembly exports.",
  },
  // Occlusion Switchboard. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/occlusion-switchboard.
  {
    slug: "occlusion-switchboard",
    surface: "app",
    title: "Occlusion Switchboard",
    upstream: "https://occlusion-switchboard.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/occlusion-switchboard.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore occlusion through an interactive switchboard.",
  },
  // Drawn Grammar Forge. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/drawn-grammar-forge.
  {
    slug: "drawn-grammar-forge",
    surface: "app",
    title: "Drawn Grammar Forge",
    upstream: "https://drawn-grammar-forge.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/drawn-grammar-forge.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Rewrite hand-drawn typed-port motifs in a finite art field.",
  },
  // Negative Brush Chase. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/negative-brush-chase.
  {
    slug: "negative-brush-chase",
    surface: "app",
    title: "Negative Brush Chase",
    upstream: "https://negative-brush-chase.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/negative-brush-chase.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Steer temporary empty islands through a deterministic pigment tide.",
  },
  // Inversion Trail. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/inversion-trail.
  {
    slug: "inversion-trail",
    surface: "app",
    title: "Inversion Trail",
    upstream: "https://inversion-trail.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/inversion-trail.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Make spatial rule-brush art where authored boundary crossings invert fill and side.",
  },
  // Palette Smuggler. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/palette-smuggler.
  {
    slug: "palette-smuggler",
    surface: "app",
    title: "Palette Smuggler",
    upstream: "https://palette-smuggler.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/palette-smuggler.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Author two drawings in a digital RGB channel cipher, then export an encoded PNG and an offline interactive decoder.",
  },
  // Implied Edge Atelier. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/implied-edge-atelier.
  {
    slug: "implied-edge-atelier",
    surface: "app",
    title: "Implied Edge Atelier",
    upstream: "https://implied-edge-atelier.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/implied-edge-atelier.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Author contours that are never drawn.",
  },
  // Touch Patina Objects. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/touch-patina-objects.
  {
    slug: "touch-patina-objects",
    surface: "app",
    title: "Touch Patina Objects",
    upstream: "https://touch-patina-objects.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/touch-patina-objects.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Wear, polish and reveal color on one memory sphere through contact alone.",
  },
  // Recursive Frame Studio. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/recursive-frame-studio.
  {
    slug: "recursive-frame-studio",
    surface: "app",
    title: "Recursive Frame Studio",
    upstream: "https://recursive-frame-studio.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/recursive-frame-studio.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Compose finite picture recurrence with editable strokes, transformed windows and bounded textures.",
  },
  // Foley Impossible Machine. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/foley-impossible-machine.
  {
    slug: "foley-impossible-machine",
    surface: "app",
    title: "Foley Impossible Machine",
    upstream: "https://foley-impossible-machine.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/foley-impossible-machine.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Invent a fictional causal sound machine with six cells, four rules and strain before failure.",
  },
  // Wire Airwriting. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/wire-airwriting.
  {
    slug: "wire-airwriting",
    surface: "app",
    title: "Wire Airwriting",
    upstream: "https://wire-airwriting.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/wire-airwriting.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Reconcile matched front and side gesture waypoints into an editable spatial wire study.",
  },
  // Chromatic Switchyard. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/chromatic-switchyard.
  {
    slug: "chromatic-switchyard",
    surface: "app",
    title: "Chromatic Switchyard",
    upstream: "https://chromatic-switchyard.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/chromatic-switchyard.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore color through an interactive chromatic switchyard.",
  },
  // Silence Cartographer. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/silence-cartographer.
  {
    slug: "silence-cartographer",
    surface: "app",
    title: "Silence Cartographer",
    upstream: "https://silence-cartographer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/silence-cartographer.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Compose around forbidden time windows with ordered whole-fragment audio packing.",
  },
  // Foreedge. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/foreedge-reveal-studio.
  {
    slug: "foreedge-reveal-studio",
    surface: "app",
    title: "Foreedge",
    upstream: "https://foreedge-reveal-studio.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/foreedge-reveal-studio.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Reveal procedural artwork through synthetic fanning of fixed-color page-edge strips.",
  },
  // Story With a Missing Verb. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/story-with-a-missing-verb.
  {
    slug: "story-with-a-missing-verb",
    surface: "app",
    title: "Story With a Missing Verb",
    upstream: "https://story-with-a-missing-verb.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/story-with-a-missing-verb.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Recover an absent action through modeled consequences in ten causal story puzzles and a local authorable deck.",
  },
  // Reduction Print Clock. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/reduction-print-clock.
  {
    slug: "reduction-print-clock",
    surface: "app",
    title: "Reduction Print Clock",
    upstream: "https://reduction-print-clock.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/reduction-print-clock.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Create prints from one irreversibly carved plate on three numbered sheets.",
  },
  // Prop Memory Play. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/prop-memory-play.
  {
    slug: "prop-memory-play",
    surface: "app",
    title: "Prop Memory Play",
    upstream: "https://prop-memory-play.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/prop-memory-play.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Improvise a six-turn local play with inherited prop obligations, custom decks and biography export.",
  },
  // Contact Transfer Dolls. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/contact-transfer-dolls.
  {
    slug: "contact-transfer-dolls",
    surface: "app",
    title: "Contact Transfer Dolls",
    upstream: "https://contact-transfer-dolls.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/contact-transfer-dolls.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Exchange pigment between sphere surfaces through explicit analytic contact presses.",
  },
  // Brush Treaty. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/brush-treaty.
  {
    slug: "brush-treaty",
    surface: "app",
    title: "Brush Treaty",
    upstream: "https://brush-treaty.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/brush-treaty.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Negotiate one shared mark with two hands using independent heading and ink streams.",
  },
  // Rolling Object Printshop. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/rolling-object-printshop.
  {
    slug: "rolling-object-printshop",
    surface: "app",
    title: "Rolling Object Printshop",
    upstream: "https://rolling-object-printshop.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/rolling-object-printshop.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Print a cylindrical surface onto paper with signed phase and explicit lift and turn.",
  },
  // Room Within a Marble. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/room-within-a-marble.
  {
    slug: "room-within-a-marble",
    surface: "app",
    title: "Room Within a Marble",
    upstream: "https://room-within-a-marble.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/room-within-a-marble.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore three nested rooms through continuous scale-changing marble passages.",
  },
  // Footnote Revolt. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/footnote-revolt.
  {
    slug: "footnote-revolt",
    surface: "app",
    title: "Footnote Revolt",
    upstream: "https://footnote-revolt.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/footnote-revolt.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Transfer narrator authority through reader attention in the original twelve-paragraph Attic Coronation.",
  },
  // Notational Ambiguity Room. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/notational-ambiguity-room.
  {
    slug: "notational-ambiguity-room",
    surface: "app",
    title: "Notational Ambiguity Room",
    upstream: "https://notational-ambiguity-room.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/notational-ambiguity-room.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Author one graphic score with two permitted interpretations per mark.",
  },
  // Elastic Tape Knot. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/elastic-tape-knot.
  {
    slug: "elastic-tape-knot",
    surface: "app",
    title: "Elastic Tape Knot",
    upstream: "https://elastic-tape-knot.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/elastic-tape-knot.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Shape tape sound with strain controls and reversible time knots.",
  },
  // Overprint Logic Cards. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/overprint-logic-cards.
  {
    slug: "overprint-logic-cards",
    surface: "app",
    title: "Overprint Logic Cards",
    upstream: "https://overprint-logic-cards.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/overprint-logic-cards.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Compose expressive colors through set membership.",
  },
  // Frottage Expedition. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/frottage-expedition.
  {
    slug: "frottage-expedition",
    surface: "app",
    title: "Frottage Expedition",
    upstream: "https://frottage-expedition.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/frottage-expedition.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Discover concealed procedural reliefs with immutable directional crayon rubbings.",
  },
  // Carbon Relay. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/carbon-relay.
  {
    slug: "carbon-relay",
    surface: "app",
    title: "Carbon Relay",
    upstream: "https://carbon-relay.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/carbon-relay.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Share a pass-and-play art ritual with selective inherited carbon traces.",
  },
  // Shutter Painting Desk. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/shutter-painting-desk.
  {
    slug: "shutter-painting-desk",
    surface: "app",
    title: "Shutter Painting Desk",
    upstream: "https://shutter-painting-desk.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/shutter-painting-desk.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Paint regional shutter windows over moving light.",
  },
  // Empty Stage Recovery. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/empty-stage-recovery.
  {
    slug: "empty-stage-recovery",
    surface: "app",
    title: "Empty Stage Recovery",
    upstream: "https://empty-stage-recovery.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/empty-stage-recovery.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore Empty Stage Recovery.",
  },
  // Distance Doubletake. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/distance-doubletake.
  {
    slug: "distance-doubletake",
    surface: "app",
    title: "Distance Doubletake",
    upstream: "https://distance-doubletake.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/distance-doubletake.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore Distance Doubletake.",
  },
  // Chalk Windbreak. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/chalk-windbreak.
  {
    slug: "chalk-windbreak",
    surface: "app",
    title: "Chalk Windbreak",
    upstream: "https://chalk-windbreak.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/chalk-windbreak.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Shape finite chalk pigment with source-ray shelters and deliberate conserved gusts.",
  },
  // Roomtone Dissolver. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/roomtone-dissolver.
  {
    slug: "roomtone-dissolver",
    surface: "app",
    title: "Roomtone Dissolver",
    upstream: "https://roomtone-dissolver.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/roomtone-dissolver.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Subtract persistent spectral bands from a local recording to expose fleeting events.",
  },
  // Common Word Crossing. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/common-word-crossing.
  {
    slug: "common-word-crossing",
    surface: "app",
    title: "Common Word Crossing",
    upstream: "https://common-word-crossing.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/common-word-crossing.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore Common Word Crossing.",
  },
  // Story That Spends Its Title. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/story-that-spends-its-title.
  {
    slug: "story-that-spends-its-title",
    surface: "app",
    title: "Story That Spends Its Title",
    upstream: "https://story-that-spends-its-title.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/story-that-spends-its-title.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Spend five title words as finite permissions for ten original narrative actions in a local literary studio.",
  },
  // Butter Bench. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/butter-bench.
  {
    slug: "butter-bench",
    surface: "app",
    title: "Butter Bench",
    upstream: "https://butter-bench.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/butter-bench.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Assemble artisan breakfast gift boxes for small corporate teams.",
  },
  // Miniature Measure. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/miniature-measure.
  {
    slug: "miniature-measure",
    surface: "app",
    title: "Miniature Measure",
    upstream: "https://miniature-measure.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/miniature-measure.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Translate real dimensions into model measurements and compare two scales side by side.",
  },
  // Linen Drop. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/linen-drop.
  {
    slug: "linen-drop",
    surface: "app",
    title: "Linen Drop",
    upstream: "https://linen-drop.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/linen-drop.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Size a rectangular tablecloth for the drop you want, with folded hems included in the cutting dimensions.",
  },
  // Label Orbit. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/label-orbit.
  {
    slug: "label-orbit",
    surface: "app",
    title: "Label Orbit",
    upstream: "https://label-orbit.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/label-orbit.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Lay out a printable sheet of repeated labels with exact margins, gutters and useful trim coordinates.",
  },
  // Grout Grid. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/grout-grid.
  {
    slug: "grout-grid",
    surface: "app",
    title: "Grout Grid",
    upstream: "https://grout-grid.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/grout-grid.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "See a tile layout with edge cuts and estimate grout volume from joint geometry.",
  },
  // Gallery Level. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/gallery-level.
  {
    slug: "gallery-level",
    surface: "app",
    title: "Gallery Level",
    upstream: "https://gallery-level.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/gallery-level.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Calculate actual wall-hook coordinates from frame sizes and hanging-wire drop, not just frame centers.",
  },
  // Bias Ribbon. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/bias-ribbon.
  {
    slug: "bias-ribbon",
    surface: "app",
    title: "Bias Ribbon",
    upstream: "https://bias-ribbon.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/bias-ribbon.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Estimate how much bias binding a fabric square can yield, including a seam allowance and a realistic waste margin.",
  },
  // Dough Dispatch. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/dough-dispatch.
  {
    slug: "dough-dispatch",
    surface: "app",
    title: "Dough Dispatch",
    upstream: "https://dough-dispatch.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/dough-dispatch.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Coordinate bread subscription handoff points to reduce individual deliveries.",
  },
  // Crumb Chronicle. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/crumb-chronicle.
  {
    slug: "crumb-chronicle",
    surface: "app",
    title: "Crumb Chronicle",
    upstream: "https://crumb-chronicle.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/crumb-chronicle.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Explore limited-run pastry drops with transparent batch availability.",
  },
  // Divider Nest. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/divider-nest.
  {
    slug: "divider-nest",
    surface: "app",
    title: "Divider Nest",
    upstream: "https://divider-nest.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/divider-nest.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Plan a slotted drawer grid with board thickness, inside dimensions and a clear cut list.",
  },
  // Roast Roster. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/roast-roster.
  {
    slug: "roast-roster",
    surface: "app",
    title: "Roast Roster",
    upstream: "https://roast-roster.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/roast-roster.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Coordinate shared roasting slots for independent coffee labels.",
  },
  // Grain Gather. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/grain-gather.
  {
    slug: "grain-gather",
    surface: "app",
    title: "Grain Gather",
    upstream: "https://grain-gather.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/grain-gather.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Organize neighborhood collective grain orders split into reusable containers.",
  },
  // Oven Overture. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/oven-overture.
  {
    slug: "oven-overture",
    surface: "app",
    title: "Oven Overture",
    upstream: "https://oven-overture.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/oven-overture.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Plan private baking experiences around group needs and available equipment.",
  },
  // Pantry Passage. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/pantry-passage.
  {
    slug: "pantry-passage",
    surface: "app",
    title: "Pantry Passage",
    upstream: "https://pantry-passage.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/pantry-passage.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Exchange surplus ingredients between neighborhood kitchens on the same day.",
  },
  // Menu Mosaic. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/menu-mosaic.
  {
    slug: "menu-mosaic",
    surface: "app",
    title: "Menu Mosaic",
    upstream: "https://menu-mosaic.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/menu-mosaic.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Build dietary-labeled event menus for independent caterers.",
  },
  // Cask Commons. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/cask-commons.
  {
    slug: "cask-commons",
    surface: "app",
    title: "Cask Commons",
    upstream: "https://cask-commons.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/cask-commons.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Pool reusable beverage containers for small nonalcoholic producers.",
  },
  // Harvest Handshake. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/harvest-handshake.
  {
    slug: "harvest-handshake",
    surface: "app",
    title: "Harvest Handshake",
    upstream: "https://harvest-handshake.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/harvest-handshake.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Preorder farm-share boxes around grower availability and household swaps.",
  },
  // Tool Roll Draft. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/tool-roll-draft.
  {
    slug: "tool-roll-draft",
    surface: "app",
    title: "Tool Roll Draft",
    upstream: "https://tool-roll-draft.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/tool-roll-draft.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Build a pocket layout around the widths of real tools, with ease and seam marks included.",
  },
  // Weed Window. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/weed-window.
  {
    slug: "weed-window",
    surface: "app",
    title: "Weed Window",
    upstream: "https://weed-window.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/weed-window.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Divide a decal into manageable weeding sections and export a clean SVG guide for your design tool.",
  },
  // Rug in Room. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/rug-in-room.
  {
    slug: "rug-in-room",
    surface: "app",
    title: "Rug in Room",
    upstream: "https://rug-in-room.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/rug-in-room.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Compare a rug footprint with a room and a sofa, including the exposed border on every side.",
  },
  // Pennant Party. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/pennant-party.
  {
    slug: "pennant-party",
    surface: "app",
    title: "Pennant Party",
    upstream: "https://pennant-party.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/pennant-party.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Plan a bunting strand with balanced end ties, exact pennant spacing and fabric-row yield.",
  },
  // Panel Echo. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/panel-echo.
  {
    slug: "panel-echo",
    surface: "app",
    title: "Panel Echo",
    upstream: "https://panel-echo.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/panel-echo.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Balance a row of decorative panels with equal gaps and precise edge-to-edge mounting coordinates.",
  },
  // Offcut Order. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/offcut-order.
  {
    slug: "offcut-order",
    surface: "app",
    title: "Offcut Order",
    upstream: "https://offcut-order.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/offcut-order.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Pack repeated parts into stock lengths with a first-fit-decreasing cut plan and explicit saw kerf.",
  },
  // Saddle Spacing. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/saddle-spacing.
  {
    slug: "saddle-spacing",
    surface: "app",
    title: "Saddle Spacing",
    upstream: "https://saddle-spacing.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/saddle-spacing.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Lay out an evenly spaced line of stitch holes with useful end margins and a numbered drilling guide.",
  },
  // Flour Foundry. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/flour-foundry.
  {
    slug: "flour-foundry",
    surface: "app",
    title: "Flour Foundry",
    upstream: "https://flour-foundry.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/flour-foundry.webp",
    shippedAt: "2026-10-23",
    status: "live",
    blurb: "Coordinate microbakery preorder windows with oven capacity.",
  },
  // Candy Clusters. Same-account workers.dev on digitalnative; static Next export, no auth, storage none. Repo over9000-apps/kmeans-candy.
  {
    slug: "kmeans-candy",
    surface: "app",
    title: "Candy Clusters",
    upstream: "https://kmeans-candy.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/kmeans-candy.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Watch k-means sort a pile of candy.",
  },
  // Sound Object Witness Lineup. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sound-object-witness-lineup.
  {
    slug: "sound-object-witness-lineup",
    surface: "app",
    title: "Sound Object Witness Lineup",
    upstream: "https://sound-object-witness-lineup.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sound-object-witness-lineup.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Match original sounds to the processes that changed them.",
  },
  // Sound Matter Sorting. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sound-matter-sorting.
  {
    slug: "sound-matter-sorting",
    surface: "app",
    title: "Sound Matter Sorting",
    upstream: "https://sound-matter-sorting.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sound-matter-sorting.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Sort sounds by the qualities of the matter behind them.",
  },
  // Sound Evidence Collage. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sound-evidence-collage.
  {
    slug: "sound-evidence-collage",
    surface: "app",
    title: "Sound Evidence Collage",
    upstream: "https://sound-evidence-collage.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sound-evidence-collage.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Assemble a collage of sonic evidence.",
  },
  // Sound Cutaway Window. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sound-cutaway-window.
  {
    slug: "sound-cutaway-window",
    surface: "app",
    title: "Sound Cutaway Window",
    upstream: "https://sound-cutaway-window.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sound-cutaway-window.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore sound through a cutaway window.",
  },
  // Sonic Redaction Letter. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sonic-redaction-letter.
  {
    slug: "sonic-redaction-letter",
    surface: "app",
    title: "Sonic Redaction Letter",
    upstream: "https://sonic-redaction-letter.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sonic-redaction-letter.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Compose a letter through sonic redactions.",
  },
  // Sonic Perspective Box. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sonic-perspective-box.
  {
    slug: "sonic-perspective-box",
    surface: "app",
    title: "Sonic Perspective Box",
    upstream: "https://sonic-perspective-box.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sonic-perspective-box.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore how perspective shapes a sonic scene.",
  },
  // Sonic Occlusion Puzzle. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sonic-occlusion-puzzle.
  {
    slug: "sonic-occlusion-puzzle",
    surface: "app",
    title: "Sonic Occlusion Puzzle",
    upstream: "https://sonic-occlusion-puzzle.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sonic-occlusion-puzzle.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Solve a puzzle of hidden and obstructed sounds.",
  },
  // Sonic Gravity Well. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sonic-gravity-well.
  {
    slug: "sonic-gravity-well",
    surface: "app",
    title: "Sonic Gravity Well",
    upstream: "https://sonic-gravity-well.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sonic-gravity-well.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore sound in a sonic gravity well.",
  },
  // Sonic Joinery. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sonic-joinery.
  {
    slug: "sonic-joinery",
    surface: "app",
    title: "Sonic Joinery",
    upstream: "https://sonic-joinery.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sonic-joinery.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Join sounds into a sonic construction.",
  },
  // Sonic Bestiary of Actions. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sonic-bestiary-of-actions.
  {
    slug: "sonic-bestiary-of-actions",
    surface: "app",
    title: "Sonic Bestiary of Actions",
    upstream: "https://sonic-bestiary-of-actions.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sonic-bestiary-of-actions.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore a bestiary of actions through sound.",
  },
  // Soap Without Soap. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/soap-without-soap.
  {
    slug: "soap-without-soap",
    surface: "app",
    title: "Soap Without Soap",
    upstream: "https://soap-without-soap.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/soap-without-soap.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore the idea of soap without soap.",
  },
  // Sonic Affordance Switchboard. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sonic-affordance-switchboard.
  {
    slug: "sonic-affordance-switchboard",
    surface: "app",
    title: "Sonic Affordance Switchboard",
    upstream: "https://sonic-affordance-switchboard.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sonic-affordance-switchboard.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore the actions that sounds suggest with a sonic switchboard.",
  },
  // Smocking Gather Map. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/smocking-gather-map.
  {
    slug: "smocking-gather-map",
    surface: "app",
    title: "Smocking Gather Map",
    upstream: "https://smocking-gather-map.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/smocking-gather-map.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Map gathers into smocking patterns.",
  },
  // Sliding Section Mosaic. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sliding-section-mosaic.
  {
    slug: "sliding-section-mosaic",
    surface: "app",
    title: "Sliding Section Mosaic",
    upstream: "https://sliding-section-mosaic.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sliding-section-mosaic.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Arrange one set of slabs into two sculptural compositions with a shared atlas.",
  },
  // Smear Shape Atelier. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/smear-shape-atelier.
  {
    slug: "smear-shape-atelier",
    surface: "app",
    title: "Smear Shape Atelier",
    upstream: "https://smear-shape-atelier.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/smear-shape-atelier.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Shape expressive contours with protected landmarks and local motion smears.",
  },
  // Silhouette Lathe Argument. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/silhouette-lathe-argument.
  {
    slug: "silhouette-lathe-argument",
    surface: "app",
    title: "Silhouette Lathe Argument",
    upstream: "https://silhouette-lathe-argument.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/silhouette-lathe-argument.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore sculptural forms through silhouettes and lathe profiles.",
  },
  // Sketch Compression Cards. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sketch-compression-cards.
  {
    slug: "sketch-compression-cards",
    surface: "app",
    title: "Sketch Compression Cards",
    upstream: "https://sketch-compression-cards.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sketch-compression-cards.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore how sketches change when compressed into cards.",
  },
  // Sightline Ratios. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sightline-ratios.
  {
    slug: "sightline-ratios",
    surface: "app",
    title: "Sightline Ratios",
    upstream: "https://sightline-ratios.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sightline-ratios.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore proportions through sightline ratios.",
  },
  // Shadow Puppet Joint Editor. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/shadow-puppet-joint-editor.
  {
    slug: "shadow-puppet-joint-editor",
    surface: "app",
    title: "Shadow Puppet Joint Editor",
    upstream: "https://shadow-puppet-joint-editor.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/shadow-puppet-joint-editor.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Edit shadow puppet joints to shape their movement.",
  },
  // Shadow Registration Cards. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/shadow-registration-cards.
  {
    slug: "shadow-registration-cards",
    surface: "app",
    title: "Shadow Registration Cards",
    upstream: "https://shadow-registration-cards.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/shadow-registration-cards.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore shadow alignment with registration cards.",
  },
  // Shadow-Free Sculpture. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/shadow-free-sculpture.
  {
    slug: "shadow-free-sculpture",
    surface: "app",
    title: "Shadow-Free Sculpture",
    upstream: "https://shadow-free-sculpture.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/shadow-free-sculpture.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Remove geometry while preserving finite-view silhouettes in a local sculpture studio.",
  },
  // Shadow Hour Comic. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/shadow-hour-comic.
  {
    slug: "shadow-hour-comic",
    surface: "app",
    title: "Shadow Hour Comic",
    upstream: "https://shadow-hour-comic.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/shadow-hour-comic.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Author fictional shadow stories with one rigid object arrangement.",
  },
  // Sentence Stage Mechanics. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sentence-stage-mechanics.
  {
    slug: "sentence-stage-mechanics",
    surface: "app",
    title: "Sentence Stage Mechanics",
    upstream: "https://sentence-stage-mechanics.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sentence-stage-mechanics.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore the mechanics of staging a sentence.",
  },
  // Segmented Ring Sketcher. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/segmented-ring-sketcher.
  {
    slug: "segmented-ring-sketcher",
    surface: "app",
    title: "Segmented Ring Sketcher",
    upstream: "https://segmented-ring-sketcher.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/segmented-ring-sketcher.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Sketch nonstructural polygon rings and choreograph their seams.",
  },
  // Section Reliquary. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/section-reliquary.
  {
    slug: "section-reliquary",
    surface: "app",
    title: "Section Reliquary",
    upstream: "https://section-reliquary.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/section-reliquary.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore sculptural sections in a reliquary.",
  },
  // Secret Fold Theatre. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/secret-fold-theatre.
  {
    slug: "secret-fold-theatre",
    surface: "app",
    title: "Secret Fold Theatre",
    upstream: "https://secret-fold-theatre.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/secret-fold-theatre.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Stage a theatre of secret folds.",
  },
  // Seam Ledger Resize. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/seam-ledger-resize.
  {
    slug: "seam-ledger-resize",
    surface: "app",
    title: "Seam Ledger Resize",
    upstream: "https://seam-ledger-resize.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/seam-ledger-resize.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Resize images with seam carving, hard protection, and an exact source-pixel ledger.",
  },
  // Seam Alibi. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/seam-alibi.
  {
    slug: "seam-alibi",
    surface: "app",
    title: "Seam Alibi",
    upstream: "https://seam-alibi.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/seam-alibi.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore the traces and stories left by seams.",
  },
  // Sculpture from Erased Strokes. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sculpture-from-erased-strokes.
  {
    slug: "sculpture-from-erased-strokes",
    surface: "app",
    title: "Sculpture from Erased Strokes",
    upstream: "https://sculpture-from-erased-strokes.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sculpture-from-erased-strokes.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Shape a sculpture from the traces of erased strokes.",
  },
  // Sculptural Exquisite Corpse. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sculptural-exquisite-corpse.
  {
    slug: "sculptural-exquisite-corpse",
    surface: "app",
    title: "Sculptural Exquisite Corpse",
    upstream: "https://sculptural-exquisite-corpse.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sculptural-exquisite-corpse.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Compose sculptural forms through an exquisite corpse exercise.",
  },
  // Script Mixing Rail. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/script-mixing-rail.
  {
    slug: "script-mixing-rail",
    surface: "app",
    title: "Script Mixing Rail",
    upstream: "https://script-mixing-rail.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/script-mixing-rail.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore script combinations along a mixing rail.",
  },
  // Score That Changes Its Rules. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/score-that-changes-its-rules.
  {
    slug: "score-that-changes-its-rules",
    surface: "app",
    title: "Score That Changes Its Rules",
    upstream: "https://score-that-changes-its-rules.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/score-that-changes-its-rules.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore a score whose rules change as it unfolds.",
  },
  // Score Foldout Reveal. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/score-foldout-reveal.
  {
    slug: "score-foldout-reveal",
    surface: "app",
    title: "Score Foldout Reveal",
    upstream: "https://score-foldout-reveal.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/score-foldout-reveal.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Unfold a score to reveal its hidden structure.",
  },
  // Scene Ownership Exchange. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/scene-ownership-exchange.
  {
    slug: "scene-ownership-exchange",
    surface: "app",
    title: "Scene Ownership Exchange",
    upstream: "https://scene-ownership-exchange.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/scene-ownership-exchange.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore how a scene changes when its ownership shifts.",
  },
  // Sample Weathering Bench. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sample-weathering-bench.
  {
    slug: "sample-weathering-bench",
    surface: "app",
    title: "Sample Weathering Bench",
    upstream: "https://sample-weathering-bench.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sample-weathering-bench.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore how weathering transforms samples.",
  },
  // Rule Load Bench. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/rule-load-bench.
  {
    slug: "rule-load-bench",
    surface: "app",
    title: "Rule Load Bench",
    upstream: "https://rule-load-bench.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/rule-load-bench.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore how rules behave under load.",
  },
  // Rotating Relief Stories. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/rotating-relief-stories.
  {
    slug: "rotating-relief-stories",
    surface: "app",
    title: "Rotating Relief Stories",
    upstream: "https://rotating-relief-stories.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/rotating-relief-stories.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore stories through rotating reliefs.",
  },
  // Root Locus Sculpture. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/root-locus-sculpture.
  {
    slug: "root-locus-sculpture",
    surface: "app",
    title: "Root Locus Sculpture",
    upstream: "https://root-locus-sculpture.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/root-locus-sculpture.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore root loci as sculptural forms.",
  },
  // Room Soundless Echo. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/room-soundless-echo.
  {
    slug: "room-soundless-echo",
    surface: "app",
    title: "Room Soundless Echo",
    upstream: "https://room-soundless-echo.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/room-soundless-echo.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Compose spatial prints as planes remember their passages through directed chambers.",
  },
  // Room by Refusal. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/room-by-refusal.
  {
    slug: "room-by-refusal",
    surface: "app",
    title: "Room by Refusal",
    upstream: "https://room-by-refusal.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/room-by-refusal.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore a room shaped by what it refuses.",
  },
  // Rule Collision Canvas. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/rule-collision-canvas.
  {
    slug: "rule-collision-canvas",
    surface: "app",
    title: "Rule Collision Canvas",
    upstream: "https://rule-collision-canvas.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/rule-collision-canvas.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore collisions between rules on a canvas.",
  },
  // Role View Leak Check. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/role-view-leak-check.
  {
    slug: "role-view-leak-check",
    surface: "app",
    title: "Role View Leak Check",
    upstream: "https://role-view-leak-check.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/role-view-leak-check.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Check what different roles can see and where views may leak.",
  },
  // Ribbon Vault. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/ribbon-vault.
  {
    slug: "ribbon-vault",
    surface: "app",
    title: "Ribbon Vault",
    upstream: "https://ribbon-vault.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/ribbon-vault.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore a vault of ribbon forms.",
  },
  // Riddle Through Repairs. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/riddle-through-repairs.
  {
    slug: "riddle-through-repairs",
    surface: "app",
    title: "Riddle Through Repairs",
    upstream: "https://riddle-through-repairs.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/riddle-through-repairs.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Unravel a riddle through a series of repairs.",
  },
  // Rhythmic Misquotation. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/rhythmic-misquotation.
  {
    slug: "rhythmic-misquotation",
    surface: "app",
    title: "Rhythmic Misquotation",
    upstream: "https://rhythmic-misquotation.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/rhythmic-misquotation.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore how misquotation changes a rhythm.",
  },
  // Rhythm With Memory Loss. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/rhythm-with-memory-loss.
  {
    slug: "rhythm-with-memory-loss",
    surface: "app",
    title: "Rhythm With Memory Loss",
    upstream: "https://rhythm-with-memory-loss.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/rhythm-with-memory-loss.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore rhythms shaped by forgetting.",
  },
  // Reversible Riddle Room. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/reversible-riddle-room.
  {
    slug: "reversible-riddle-room",
    surface: "app",
    title: "Reversible Riddle Room",
    upstream: "https://reversible-riddle-room.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/reversible-riddle-room.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore a riddle room where actions can be reversed.",
  },
  // Retrace Budget Router. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/retrace-budget-router.
  {
    slug: "retrace-budget-router",
    surface: "app",
    title: "Retrace Budget Router",
    upstream: "https://retrace-budget-router.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/retrace-budget-router.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore routes with a limited budget for retracing your steps.",
  },
  // Rest as a Currency. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/rest-as-a-currency.
  {
    slug: "rest-as-a-currency",
    surface: "app",
    title: "Rest as a Currency",
    upstream: "https://rest-as-a-currency.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/rest-as-a-currency.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore what happens when rest becomes a currency.",
  },
  // Residue Roundtrip. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/residue-roundtrip.
  {
    slug: "residue-roundtrip",
    surface: "app",
    title: "Residue Roundtrip",
    upstream: "https://residue-roundtrip.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/residue-roundtrip.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore what a roundtrip leaves behind.",
  },
  // Reference Glimpses. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/reference-glimpses.
  {
    slug: "reference-glimpses",
    surface: "app",
    title: "Reference Glimpses",
    upstream: "https://reference-glimpses.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/reference-glimpses.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore references through brief glimpses.",
  },
  // Reliable Map Unreliable Key. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/reliable-map-unreliable-key.
  {
    slug: "reliable-map-unreliable-key",
    surface: "app",
    title: "Reliable Map Unreliable Key",
    upstream: "https://reliable-map-unreliable-key.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/reliable-map-unreliable-key.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore a reliable map through an unreliable key.",
  },
  // Reflective Letterless Totem. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/reflective-letterless-totem.
  {
    slug: "reflective-letterless-totem",
    surface: "app",
    title: "Reflective Letterless Totem",
    upstream: "https://reflective-letterless-totem.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/reflective-letterless-totem.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore a reflective totem without letters.",
  },
  // Red Herring Budget. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/red-herring-budget.
  {
    slug: "red-herring-budget",
    surface: "app",
    title: "Red Herring Budget",
    upstream: "https://red-herring-budget.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/red-herring-budget.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore a mystery with a limited budget for red herrings.",
  },
  // Reading Window Composer. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/reading-window-composer.
  {
    slug: "reading-window-composer",
    surface: "app",
    title: "Reading Window Composer",
    upstream: "https://reading-window-composer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/reading-window-composer.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Compose a reading experience through windows.",
  },
  // Raster Contour Foundry. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/raster-contour-foundry.
  {
    slug: "raster-contour-foundry",
    surface: "app",
    title: "Raster Contour Foundry",
    upstream: "https://raster-contour-foundry.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/raster-contour-foundry.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Trace raster images into vector contours while preserving the little things.",
  },
  // Reading Order Alibi. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/reading-order-alibi.
  {
    slug: "reading-order-alibi",
    surface: "app",
    title: "Reading Order Alibi",
    upstream: "https://reading-order-alibi.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/reading-order-alibi.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore how reading order shapes an alibi.",
  },
  // Punchneedle Relief Map. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/punchneedle-relief-map.
  {
    slug: "punchneedle-relief-map",
    surface: "app",
    title: "Punchneedle Relief Map",
    upstream: "https://punchneedle-relief-map.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/punchneedle-relief-map.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore relief maps through punchneedle textures.",
  },
  // Private Boundary Improv Deck. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/private-boundary-improv-deck.
  {
    slug: "private-boundary-improv-deck",
    surface: "app",
    title: "Private Boundary Improv Deck",
    upstream: "https://private-boundary-improv-deck.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/private-boundary-improv-deck.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Choose privately and play together in a local, low-stakes fictional improv deck for four people.",
  },
  // Porcelain Patchwork. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/porcelain-patchwork.
  {
    slug: "porcelain-patchwork",
    surface: "app",
    title: "Porcelain Patchwork",
    upstream: "https://porcelain-patchwork.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/porcelain-patchwork.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Compose synthetic ceramic fragments and intentional gaps in a local studio.",
  },
  // Polyphonic Punctuation. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/polyphonic-punctuation.
  {
    slug: "polyphonic-punctuation",
    surface: "app",
    title: "Polyphonic Punctuation",
    upstream: "https://polyphonic-punctuation.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/polyphonic-punctuation.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore punctuation through multiple voices.",
  },
  // Pocket Architectural Rhyme. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/pocket-architectural-rhyme.
  {
    slug: "pocket-architectural-rhyme",
    surface: "app",
    title: "Pocket Architectural Rhyme",
    upstream: "https://pocket-architectural-rhyme.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/pocket-architectural-rhyme.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore architectural rhymes at pocket scale.",
  },
  // Photo Depth Dioramist. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/photo-depth-dioramist.
  {
    slug: "photo-depth-dioramist",
    surface: "app",
    title: "Photo Depth Dioramist",
    upstream: "https://photo-depth-dioramist.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/photo-depth-dioramist.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore photo depth through diorama compositions.",
  },
  // Pixel Scale Exceptions. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/pixel-scale-exceptions.
  {
    slug: "pixel-scale-exceptions",
    surface: "app",
    title: "Pixel Scale Exceptions",
    upstream: "https://pixel-scale-exceptions.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/pixel-scale-exceptions.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore exceptions at pixel scale.",
  },
  // Pinhole Object Theatre. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/pinhole-object-theatre.
  {
    slug: "pinhole-object-theatre",
    surface: "app",
    title: "Pinhole Object Theatre",
    upstream: "https://pinhole-object-theatre.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/pinhole-object-theatre.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore object theatre through a pinhole view.",
  },
  // Phase Unwrapped Sculpture. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/phase-unwrapped-sculpture.
  {
    slug: "phase-unwrapped-sculpture",
    surface: "app",
    title: "Phase Unwrapped Sculpture",
    upstream: "https://phase-unwrapped-sculpture.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/phase-unwrapped-sculpture.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore phase unwrapping as sculpture.",
  },
  // Phase Portrait Painter. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/phase-portrait-painter.
  {
    slug: "phase-portrait-painter",
    surface: "app",
    title: "Phase Portrait Painter",
    upstream: "https://phase-portrait-painter.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/phase-portrait-painter.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Paint with phase portraits.",
  },
  // Permutation Procession. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/permutation-procession.
  {
    slug: "permutation-procession",
    surface: "app",
    title: "Permutation Procession",
    upstream: "https://permutation-procession.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/permutation-procession.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore permutations as a procession.",
  },
  // Performance Divergence Atlas. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/performance-divergence-atlas.
  {
    slug: "performance-divergence-atlas",
    surface: "app",
    title: "Performance Divergence Atlas",
    upstream: "https://performance-divergence-atlas.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/performance-divergence-atlas.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Compare two enactments of an open score without ranking either performance.",
  },
  // Perforation Orchard. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/perforation-orchard.
  {
    slug: "perforation-orchard",
    surface: "app",
    title: "Perforation Orchard",
    upstream: "https://perforation-orchard.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/perforation-orchard.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore an orchard of perforated forms.",
  },
  // Peel Shell Studio. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/peel-shell-studio.
  {
    slug: "peel-shell-studio",
    surface: "app",
    title: "Peel Shell Studio",
    upstream: "https://peel-shell-studio.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/peel-shell-studio.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore peeled shells in a creative studio.",
  },
  // Participation Recovery Drills. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/participation-recovery-drills.
  {
    slug: "participation-recovery-drills",
    surface: "app",
    title: "Participation Recovery Drills",
    upstream: "https://participation-recovery-drills.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/participation-recovery-drills.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Rehearse ways to recover participation.",
  },
  // Parallel Voices Page. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/parallel-voices-page.
  {
    slug: "parallel-voices-page",
    surface: "app",
    title: "Parallel Voices Page",
    upstream: "https://parallel-voices-page.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/parallel-voices-page.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Compose a page with parallel voices.",
  },
  // Parallel Transport Brush. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/parallel-transport-brush.
  {
    slug: "parallel-transport-brush",
    surface: "app",
    title: "Parallel Transport Brush",
    upstream: "https://parallel-transport-brush.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/parallel-transport-brush.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore parallel transport through brushwork.",
  },
  // Paper Tension Map. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/paper-tension-map.
  {
    slug: "paper-tension-map",
    surface: "app",
    title: "Paper Tension Map",
    upstream: "https://paper-tension-map.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/paper-tension-map.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore tension through paper maps.",
  },
  // Paper Shadow Cabinet. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/paper-shadow-cabinet.
  {
    slug: "paper-shadow-cabinet",
    surface: "app",
    title: "Paper Shadow Cabinet",
    upstream: "https://paper-shadow-cabinet.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/paper-shadow-cabinet.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore a cabinet of paper shadows.",
  },
  // Paper Scar Book. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/paper-scar-book.
  {
    slug: "paper-scar-book",
    surface: "app",
    title: "Paper Scar Book",
    upstream: "https://paper-scar-book.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/paper-scar-book.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore a book shaped by scars in paper.",
  },
  // Palindrome Publication. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/palindrome-publication.
  {
    slug: "palindrome-publication",
    surface: "app",
    title: "Palindrome Publication",
    upstream: "https://palindrome-publication.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/palindrome-publication.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore a publication that reads in both directions.",
  },
  // Pairwise Rehearsal Weaver. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/pairwise-rehearsal-weaver.
  {
    slug: "pairwise-rehearsal-weaver",
    surface: "app",
    title: "Pairwise Rehearsal Weaver",
    upstream: "https://pairwise-rehearsal-weaver.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/pairwise-rehearsal-weaver.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Weave pairwise rehearsals together.",
  },
  // Page Cadence Editor. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/page-cadence-editor.
  {
    slug: "page-cadence-editor",
    surface: "app",
    title: "Page Cadence Editor",
    upstream: "https://page-cadence-editor.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/page-cadence-editor.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Compose a publication's visual rhythm without changing its page images.",
  },
  // Packing With Personality. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/packing-with-personality.
  {
    slug: "packing-with-personality",
    surface: "app",
    title: "Packing With Personality",
    upstream: "https://packing-with-personality.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/packing-with-personality.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore packing patterns with personality.",
  },
  // Packing Defect Crown. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/packing-defect-crown.
  {
    slug: "packing-defect-crown",
    surface: "app",
    title: "Packing Defect Crown",
    upstream: "https://packing-defect-crown.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/packing-defect-crown.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore the crown formed by packing defects.",
  },
  // One Switch Artstage. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/one-switch-artstage.
  {
    slug: "one-switch-artstage",
    surface: "app",
    title: "One Switch Artstage",
    upstream: "https://one-switch-artstage.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/one-switch-artstage.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Compose graphics with one switch: tap, hold, create.",
  },
  // Orbitless Rosette. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/orbitless-rosette.
  {
    slug: "orbitless-rosette",
    surface: "app",
    title: "Orbitless Rosette",
    upstream: "https://orbitless-rosette.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/orbitless-rosette.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore rosette patterns without orbits.",
  },
  // Object Trace Stopbook. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/object-trace-stopbook.
  {
    slug: "object-trace-stopbook",
    surface: "app",
    title: "Object Trace Stopbook",
    upstream: "https://object-trace-stopbook.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/object-trace-stopbook.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore a stopbook of object traces.",
  },
  // Object to Landscape. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/object-to-landscape.
  {
    slug: "object-to-landscape",
    surface: "app",
    title: "Object to Landscape",
    upstream: "https://object-to-landscape.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/object-to-landscape.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Turn everyday objects into landscapes.",
  },
  // Object Rubbing Atlas. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/object-rubbing-atlas.
  {
    slug: "object-rubbing-atlas",
    surface: "app",
    title: "Object Rubbing Atlas",
    upstream: "https://object-rubbing-atlas.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/object-rubbing-atlas.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore an atlas of object rubbings.",
  },
  // Object Flock Freeze. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/object-flock-freeze.
  {
    slug: "object-flock-freeze",
    surface: "app",
    title: "Object Flock Freeze",
    upstream: "https://object-flock-freeze.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/object-flock-freeze.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Freeze a flock of objects into a composition.",
  },
  // Object Flattening Parade. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/object-flattening-parade.
  {
    slug: "object-flattening-parade",
    surface: "app",
    title: "Object Flattening Parade",
    upstream: "https://object-flattening-parade.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/object-flattening-parade.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore a parade of flattened objects.",
  },
  // Object Difference Museum. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/object-difference-museum.
  {
    slug: "object-difference-museum",
    surface: "app",
    title: "Object Difference Museum",
    upstream: "https://object-difference-museum.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/object-difference-museum.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore a museum of differences between objects.",
  },
  // Object Brush Foundry. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/object-brush-foundry.
  {
    slug: "object-brush-foundry",
    surface: "app",
    title: "Object Brush Foundry",
    upstream: "https://object-brush-foundry.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/object-brush-foundry.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Turn objects into expressive brushes.",
  },
  // Object Annotation Bloom. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/object-annotation-bloom.
  {
    slug: "object-annotation-bloom",
    surface: "app",
    title: "Object Annotation Bloom",
    upstream: "https://object-annotation-bloom.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/object-annotation-bloom.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Let annotations bloom around objects.",
  },
  // Nonrepeating Bellpath. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/nonrepeating-bellpath.
  {
    slug: "nonrepeating-bellpath",
    surface: "app",
    title: "Nonrepeating Bellpath",
    upstream: "https://nonrepeating-bellpath.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/nonrepeating-bellpath.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore bell paths that never repeat.",
  },
  // Nonrepeat Ribbon. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/nonrepeat-ribbon.
  {
    slug: "nonrepeat-ribbon",
    surface: "app",
    title: "Nonrepeat Ribbon",
    upstream: "https://nonrepeat-ribbon.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/nonrepeat-ribbon.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Compose connected ornaments that refuse your forbidden repeats.",
  },
  // Nonorientable Paintwalk. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/nonorientable-paintwalk.
  {
    slug: "nonorientable-paintwalk",
    surface: "app",
    title: "Nonorientable Paintwalk",
    upstream: "https://nonorientable-paintwalk.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/nonorientable-paintwalk.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore a painted walk across nonorientable surfaces.",
  },
  // Noise Notary. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/noise-notary.
  {
    slug: "noise-notary",
    surface: "app",
    title: "Noise Notary",
    upstream: "https://noise-notary.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/noise-notary.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Paint the statistics of reproducible seamless grain.",
  },
  // No Repeated Narrator. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/no-repeated-narrator.
  {
    slug: "no-repeated-narrator",
    surface: "app",
    title: "No Repeated Narrator",
    upstream: "https://no-repeated-narrator.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/no-repeated-narrator.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore a story that never repeats its narrator.",
  },
  // Negative Shape Hunt. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/negative-shape-hunt.
  {
    slug: "negative-shape-hunt",
    surface: "app",
    title: "Negative Shape Hunt",
    upstream: "https://negative-shape-hunt.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/negative-shape-hunt.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Hunt for shapes in the spaces between objects.",
  },
  // Narrative Time Lent. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/narrative-time-lent.
  {
    slug: "narrative-time-lent",
    surface: "app",
    title: "Narrative Time Lent",
    upstream: "https://narrative-time-lent.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/narrative-time-lent.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore stories shaped by borrowed time.",
  },
  // Narrative Clockmaker. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/narrative-clockmaker.
  {
    slug: "narrative-clockmaker",
    surface: "app",
    title: "Narrative Clockmaker",
    upstream: "https://narrative-clockmaker.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/narrative-clockmaker.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore the clockwork of a narrative.",
  },
  // Narrative Supply Chain. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/narrative-supply-chain.
  {
    slug: "narrative-supply-chain",
    surface: "app",
    title: "Narrative Supply Chain",
    upstream: "https://narrative-supply-chain.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/narrative-supply-chain.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore how a story travels through a supply chain.",
  },
  // Music of an Argument. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/music-of-an-argument.
  {
    slug: "music-of-an-argument",
    surface: "app",
    title: "Music of an Argument",
    upstream: "https://music-of-an-argument.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/music-of-an-argument.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore the music hidden in an argument.",
  },
  // Motion Stencil. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/motion-stencil.
  {
    slug: "motion-stencil",
    surface: "app",
    title: "Motion Stencil",
    upstream: "https://motion-stencil.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/motion-stencil.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Shape movement with a motion stencil.",
  },
  // Moire Depth Stage. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/moire-depth-stage.
  {
    slug: "moire-depth-stage",
    surface: "app",
    title: "Moire Depth Stage",
    upstream: "https://moire-depth-stage.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/moire-depth-stage.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore depth through moire patterns.",
  },
  // Missing Instrument Detectives. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/missing-instrument-detectives.
  {
    slug: "missing-instrument-detectives",
    surface: "app",
    title: "Missing Instrument Detectives",
    upstream: "https://missing-instrument-detectives.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/missing-instrument-detectives.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Investigate the case of a missing instrument.",
  },
  // Mirror Tile Image Weaver. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/mirror-tile-image-weaver.
  {
    slug: "mirror-tile-image-weaver",
    surface: "app",
    title: "Mirror Tile Image Weaver",
    upstream: "https://mirror-tile-image-weaver.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/mirror-tile-image-weaver.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Weave optical art on your device with independently aimed conceptual mirror tiles.",
  },
  // Mime Viewpoint Switchboard. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/mime-viewpoint-switchboard.
  {
    slug: "mime-viewpoint-switchboard",
    surface: "app",
    title: "Mime Viewpoint Switchboard",
    upstream: "https://mime-viewpoint-switchboard.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/mime-viewpoint-switchboard.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore mime from shifting viewpoints.",
  },
  // Mime Contact Atlas. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/mime-contact-atlas.
  {
    slug: "mime-contact-atlas",
    surface: "app",
    title: "Mime Contact Atlas",
    upstream: "https://mime-contact-atlas.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/mime-contact-atlas.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore an atlas of contact in mime.",
  },
  // Microscopic Transition Library. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/microscopic-transition-library.
  {
    slug: "microscopic-transition-library",
    surface: "app",
    title: "Microscopic Transition Library",
    upstream: "https://microscopic-transition-library.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/microscopic-transition-library.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore a library of microscopic transitions.",
  },
  // Mezzotint Moon. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/mezzotint-moon.
  {
    slug: "mezzotint-moon",
    surface: "app",
    title: "Mezzotint Moon",
    upstream: "https://mezzotint-moon.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/mezzotint-moon.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore a moon rendered in mezzotint.",
  },
  // Metafictional Save Slot. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/metafictional-save-slot.
  {
    slug: "metafictional-save-slot",
    surface: "app",
    title: "Metafictional Save Slot",
    upstream: "https://metafictional-save-slot.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/metafictional-save-slot.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore a story through its save slots.",
  },
  // Mesh Net Shaper. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/mesh-net-shaper.
  {
    slug: "mesh-net-shaper",
    surface: "app",
    title: "Mesh Net Shaper",
    upstream: "https://mesh-net-shaper.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/mesh-net-shaper.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Shape a mesh into a net.",
  },
  // Mesh Memory Weather. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/mesh-memory-weather.
  {
    slug: "mesh-memory-weather",
    surface: "app",
    title: "Mesh Memory Weather",
    upstream: "https://mesh-memory-weather.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/mesh-memory-weather.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore weather made from mesh and memory.",
  },
  // Mesh Harmonic Masks. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/mesh-harmonic-masks.
  {
    slug: "mesh-harmonic-masks",
    surface: "app",
    title: "Mesh Harmonic Masks",
    upstream: "https://mesh-harmonic-masks.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/mesh-harmonic-masks.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore harmonic masks shaped from meshes.",
  },
  // Mesh Dual Menagerie. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/mesh-dual-menagerie.
  {
    slug: "mesh-dual-menagerie",
    surface: "app",
    title: "Mesh Dual Menagerie",
    upstream: "https://mesh-dual-menagerie.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/mesh-dual-menagerie.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore a menagerie of dual meshes.",
  },
  // Memory of an Unplayed Scene. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/memory-of-an-unplayed-scene.
  {
    slug: "memory-of-an-unplayed-scene",
    surface: "app",
    title: "Memory of an Unplayed Scene",
    upstream: "https://memory-of-an-unplayed-scene.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/memory-of-an-unplayed-scene.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore the memory of a scene that was never played.",
  },
  // Memory Buffer Ballad. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/memory-buffer-ballad.
  {
    slug: "memory-buffer-ballad",
    surface: "app",
    title: "Memory Buffer Ballad",
    upstream: "https://memory-buffer-ballad.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/memory-buffer-ballad.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore a ballad shaped by a memory buffer.",
  },
  // Melody Event Horizon. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/melody-event-horizon.
  {
    slug: "melody-event-horizon",
    surface: "app",
    title: "Melody Event Horizon",
    upstream: "https://melody-event-horizon.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/melody-event-horizon.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore a melody at its event horizon.",
  },
  // Melody With No Owner. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/melody-with-no-owner.
  {
    slug: "melody-with-no-owner",
    surface: "app",
    title: "Melody With No Owner",
    upstream: "https://melody-with-no-owner.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/melody-with-no-owner.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore a melody that belongs to no one.",
  },
  // Melody As a Walking Proof. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/melody-as-a-walking-proof.
  {
    slug: "melody-as-a-walking-proof",
    surface: "app",
    title: "Melody As a Walking Proof",
    upstream: "https://melody-as-a-walking-proof.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/melody-as-a-walking-proof.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore a melody as a walking proof.",
  },
  // Macrame Dependency Board. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/macrame-dependency-board.
  {
    slug: "macrame-dependency-board",
    surface: "app",
    title: "Macrame Dependency Board",
    upstream: "https://macrame-dependency-board.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/macrame-dependency-board.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore dependencies through a macrame board.",
  },
  // Medial Skeleton Jewels. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/medial-skeleton-jewels.
  {
    slug: "medial-skeleton-jewels",
    surface: "app",
    title: "Medial Skeleton Jewels",
    upstream: "https://medial-skeleton-jewels.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/medial-skeleton-jewels.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore jewel forms built around medial skeletons.",
  },
  // Mask Marketplace Local. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/mask-marketplace-local.
  {
    slug: "mask-marketplace-local",
    surface: "app",
    title: "Mask Marketplace Local",
    upstream: "https://mask-marketplace-local.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/mask-marketplace-local.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore a local marketplace of masks.",
  },
  // Loop Bloom Sculptor. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/loop-bloom-sculptor.
  {
    slug: "loop-bloom-sculptor",
    surface: "app",
    title: "Loop Bloom Sculptor",
    upstream: "https://loop-bloom-sculptor.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/loop-bloom-sculptor.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Sculpt blooming forms from loops.",
  },
  // Listener Path Mixdown. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/listener-path-mixdown.
  {
    slug: "listener-path-mixdown",
    surface: "app",
    title: "Listener Path Mixdown",
    upstream: "https://listener-path-mixdown.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/listener-path-mixdown.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore a mix shaped by the listener's path.",
  },
  // Lightpainting Ribbon Camera. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/lightpainting-ribbon-camera.
  {
    slug: "lightpainting-ribbon-camera",
    surface: "app",
    title: "Lightpainting Ribbon Camera",
    upstream: "https://lightpainting-ribbon-camera.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/lightpainting-ribbon-camera.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Paint ribbons of light through a camera.",
  },
  // Layout Stress Mutator. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/layout-stress-mutator.
  {
    slug: "layout-stress-mutator",
    surface: "app",
    title: "Layout Stress Mutator",
    upstream: "https://layout-stress-mutator.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/layout-stress-mutator.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore how layouts respond to stress and mutation.",
  },
  // Late Arrival Stage Pass. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/late-arrival-stage-pass.
  {
    slug: "late-arrival-stage-pass",
    surface: "app",
    title: "Late Arrival Stage Pass",
    upstream: "https://late-arrival-stage-pass.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/late-arrival-stage-pass.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore stage entrances for late arrivals.",
  },
  // Kerning Class Cartographer. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/kerning-class-cartographer.
  {
    slug: "kerning-class-cartographer",
    surface: "app",
    title: "Kerning Class Cartographer",
    upstream: "https://kerning-class-cartographer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/kerning-class-cartographer.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Map kerning classes and their spacing relationships.",
  },
  // Junction Composer. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/junction-composer.
  {
    slug: "junction-composer",
    surface: "app",
    title: "Junction Composer",
    upstream: "https://junction-composer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/junction-composer.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Compose forms around junctions.",
  },
  // Invisible Character Casting. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/invisible-character-casting.
  {
    slug: "invisible-character-casting",
    surface: "app",
    title: "Invisible Character Casting",
    upstream: "https://invisible-character-casting.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/invisible-character-casting.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Cast invisible characters into a scene.",
  },
  // Interlocking Absence. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/interlocking-absence.
  {
    slug: "interlocking-absence",
    surface: "app",
    title: "Interlocking Absence",
    upstream: "https://interlocking-absence.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/interlocking-absence.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore interlocking forms through the spaces they leave behind.",
  },
  // Interlace Field Unweaver. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/interlace-field-unweaver.
  {
    slug: "interlace-field-unweaver",
    surface: "app",
    title: "Interlace Field Unweaver",
    upstream: "https://interlace-field-unweaver.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/interlace-field-unweaver.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Unweave an interlaced field.",
  },
  // Ink Spread Countershape. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/ink-spread-countershape.
  {
    slug: "ink-spread-countershape",
    surface: "app",
    title: "Ink Spread Countershape",
    upstream: "https://ink-spread-countershape.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/ink-spread-countershape.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore how spreading ink changes countershapes.",
  },
  // Infinite Stair Painter. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/infinite-stair-painter.
  {
    slug: "infinite-stair-painter",
    surface: "app",
    title: "Infinite Stair Painter",
    upstream: "https://infinite-stair-painter.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/infinite-stair-painter.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Paint an endless staircase.",
  },
  // In-World Editor. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/in-world-editor.
  {
    slug: "in-world-editor",
    surface: "app",
    title: "In-World Editor",
    upstream: "https://in-world-editor.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/in-world-editor.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore editing from inside the world.",
  },
  // Improv Assumption Ledger. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/improv-assumption-ledger.
  {
    slug: "improv-assumption-ledger",
    surface: "app",
    title: "Improv Assumption Ledger",
    upstream: "https://improv-assumption-ledger.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/improv-assumption-ledger.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Track the assumptions that shape an improvised scene.",
  },
  // Impossible Radio Schedule. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/impossible-radio-schedule.
  {
    slug: "impossible-radio-schedule",
    surface: "app",
    title: "Impossible Radio Schedule",
    upstream: "https://impossible-radio-schedule.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/impossible-radio-schedule.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore an impossible schedule of radio programs.",
  },
  // Impossible Proscenium. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/impossible-proscenium.
  {
    slug: "impossible-proscenium",
    surface: "app",
    title: "Impossible Proscenium",
    upstream: "https://impossible-proscenium.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/impossible-proscenium.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore impossible forms within a theatrical frame.",
  },
  // Image Triage Brush. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/image-triage-brush.
  {
    slug: "image-triage-brush",
    surface: "app",
    title: "Image Triage Brush",
    upstream: "https://image-triage-brush.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/image-triage-brush.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Triage images with a brush.",
  },
  // Icon Optical Masters. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/icon-optical-masters.
  {
    slug: "icon-optical-masters",
    surface: "app",
    title: "Icon Optical Masters",
    upstream: "https://icon-optical-masters.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/icon-optical-masters.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore optical adjustments for icons at different sizes.",
  },
  // Image Compass Cuts. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/image-compass-cuts.
  {
    slug: "image-compass-cuts",
    surface: "app",
    title: "Image Compass Cuts",
    upstream: "https://image-compass-cuts.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/image-compass-cuts.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Individually authored radial cuts with independent source compasses.",
  },
  // Hyperbolic Flower Chart. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/hyperbolic-flower-chart.
  {
    slug: "hyperbolic-flower-chart",
    surface: "app",
    title: "Hyperbolic Flower Chart",
    upstream: "https://hyperbolic-flower-chart.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/hyperbolic-flower-chart.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore flowers arranged in hyperbolic space.",
  },
  // Hue Path Ribbons. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/hue-path-ribbons.
  {
    slug: "hue-path-ribbons",
    surface: "app",
    title: "Hue Path Ribbons",
    upstream: "https://hue-path-ribbons.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/hue-path-ribbons.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Trace paths through hues as ribbons.",
  },
  // Held Tone Time Loom. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/held-tone-time-loom.
  {
    slug: "held-tone-time-loom",
    surface: "app",
    title: "Held Tone Time Loom",
    upstream: "https://held-tone-time-loom.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/held-tone-time-loom.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Weave held tones through time.",
  },
  // Highlight Cartographer. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/highlight-cartographer.
  {
    slug: "highlight-cartographer",
    surface: "app",
    title: "Highlight Cartographer",
    upstream: "https://highlight-cartographer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/highlight-cartographer.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Map the highlights across a form.",
  },
  // Harmony By Subtraction. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/harmony-by-subtraction.
  {
    slug: "harmony-by-subtraction",
    surface: "app",
    title: "Harmony By Subtraction",
    upstream: "https://harmony-by-subtraction.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/harmony-by-subtraction.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore harmony by taking notes away.",
  },
  // Handshadow Pose Studio. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/handshadow-pose-studio.
  {
    slug: "handshadow-pose-studio",
    surface: "app",
    title: "Handshadow Pose Studio",
    upstream: "https://handshadow-pose-studio.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/handshadow-pose-studio.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore poses for hand shadows.",
  },
  // Harmonic Wear Test. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/harmonic-wear-test.
  {
    slug: "harmonic-wear-test",
    surface: "app",
    title: "Harmonic Wear Test",
    upstream: "https://harmonic-wear-test.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/harmonic-wear-test.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore how harmonics change with wear.",
  },
  // Hand Tremor Garden. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/hand-tremor-garden.
  {
    slug: "hand-tremor-garden",
    surface: "app",
    title: "Hand Tremor Garden",
    upstream: "https://hand-tremor-garden.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/hand-tremor-garden.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Grow a garden from the movement of a trembling hand.",
  },
  // Gusset Form Studio. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/gusset-form-studio.
  {
    slug: "gusset-form-studio",
    surface: "app",
    title: "Gusset Form Studio",
    upstream: "https://gusset-form-studio.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/gusset-form-studio.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore forms shaped by gussets.",
  },
  // Guilloche Negotiator. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/guilloche-negotiator.
  {
    slug: "guilloche-negotiator",
    surface: "app",
    title: "Guilloche Negotiator",
    upstream: "https://guilloche-negotiator.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/guilloche-negotiator.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore the interplay of guilloche curves.",
  },
  // Gridfit Hint Theater. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/gridfit-hint-theater.
  {
    slug: "gridfit-hint-theater",
    surface: "app",
    title: "Gridfit Hint Theater",
    upstream: "https://gridfit-hint-theater.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/gridfit-hint-theater.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "An original device-grid constraint workshop for small type.",
  },
  // Gradient Banding Clinic. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/gradient-banding-clinic.
  {
    slug: "gradient-banding-clinic",
    surface: "app",
    title: "Gradient Banding Clinic",
    upstream: "https://gradient-banding-clinic.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/gradient-banding-clinic.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore banding in color gradients.",
  },
  // Glyph Winding Bench. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/glyph-winding-bench.
  {
    slug: "glyph-winding-bench",
    surface: "app",
    title: "Glyph Winding Bench",
    upstream: "https://glyph-winding-bench.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/glyph-winding-bench.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore glyph contours and their winding.",
  },
  // Gloss Weft. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/gloss-weft.
  {
    slug: "gloss-weft",
    surface: "app",
    title: "Gloss Weft",
    upstream: "https://gloss-weft.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/gloss-weft.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore gloss woven into a weft.",
  },
  // Gesture Macro Weaver. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/gesture-macro-weaver.
  {
    slug: "gesture-macro-weaver",
    surface: "app",
    title: "Gesture Macro Weaver",
    upstream: "https://gesture-macro-weaver.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/gesture-macro-weaver.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Weave gestures into macros.",
  },
  // Geodesic Ribbon Press. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/geodesic-ribbon-press.
  {
    slug: "geodesic-ribbon-press",
    surface: "app",
    title: "Geodesic Ribbon Press",
    upstream: "https://geodesic-ribbon-press.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/geodesic-ribbon-press.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore ribbons shaped along geodesic paths.",
  },
  // Gap Aware Color Desk. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/gap-aware-color-desk.
  {
    slug: "gap-aware-color-desk",
    surface: "app",
    title: "Gap Aware Color Desk",
    upstream: "https://gap-aware-color-desk.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/gap-aware-color-desk.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore color relationships with the gaps in view.",
  },
  // Frequency Rubbing. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/frequency-rubbing.
  {
    slug: "frequency-rubbing",
    surface: "app",
    title: "Frequency Rubbing",
    upstream: "https://frequency-rubbing.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/frequency-rubbing.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore frequencies through rubbing textures.",
  },
  // Frame Thief. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/frame-thief.
  {
    slug: "frame-thief",
    surface: "app",
    title: "Frame Thief",
    upstream: "https://frame-thief.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/frame-thief.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Draw one continuous world and choreograph what a moving window is allowed to reveal.",
  },
  // Frame Exposure Anchor. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/frame-exposure-anchor.
  {
    slug: "frame-exposure-anchor",
    surface: "app",
    title: "Frame Exposure Anchor",
    upstream: "https://frame-exposure-anchor.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/frame-exposure-anchor.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Correct exposure flicker in stop-motion frames while preserving movement and intended lighting changes.",
  },
  // Fragment Texture Quilter. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/fragment-texture-quilter.
  {
    slug: "fragment-texture-quilter",
    surface: "app",
    title: "Fragment Texture Quilter",
    upstream: "https://fragment-texture-quilter.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/fragment-texture-quilter.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Grow a texture from a small image fragment and inspect every patch choice and seam.",
  },
  // Foldline Projection Repair. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/foldline-projection-repair.
  {
    slug: "foldline-projection-repair",
    surface: "app",
    title: "Foldline Projection Repair",
    upstream: "https://foldline-projection-repair.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/foldline-projection-repair.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Author drawings for folded surfaces and adjust their projection as the hinges move.",
  },
  // Folded View Postcards. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/folded-view-postcards.
  {
    slug: "folded-view-postcards",
    surface: "app",
    title: "Folded View Postcards",
    upstream: "https://folded-view-postcards.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/folded-view-postcards.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Compose two views of one rigid hinged postcard, locally.",
  },
  // Folded Horizon Room. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/folded-horizon-room.
  {
    slug: "folded-horizon-room",
    surface: "app",
    title: "Folded Horizon Room",
    upstream: "https://folded-horizon-room.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/folded-horizon-room.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Explore a room of connected surfaces and author seams that rotate your view as you cross them.",
  },
  // Folded Light Rooms. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/folded-light-rooms.
  {
    slug: "folded-light-rooms",
    surface: "app",
    title: "Folded Light Rooms",
    upstream: "https://folded-light-rooms.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/folded-light-rooms.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "Paint across a flat room atlas and watch your strokes wrap around its folded walls.",
  },
  // Evil Brow. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/evil-brow.
  {
    slug: "evil-brow",
    surface: "app",
    title: "Evil Brow",
    upstream: "https://evil-brow.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/evil-brow.webp",
    shippedAt: "2026-10-24",
    status: "live",
    blurb: "An eyebrow curling device for the villain arch. Slide a face from Neutral to Full Evil Eye.",
  },
  // AI Metadata Inspector. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none (client-side only). Repo over9000-apps/ai-metadata.
  {
    slug: "ai-metadata",
    surface: "app",
    title: "AI Metadata Inspector",
    upstream: "https://ai-metadata.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/ai-metadata.webp",
    shippedAt: "2026-10-25",
    status: "live",
    blurb: "Check images and videos for AI metadata (C2PA, IPTC, SD, ComfyUI), or stamp an AI disclosure onto a clean copy.",
  },
  // Rug Tuft Direction Map. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/rug-tuft-direction-map.
  {
    slug: "rug-tuft-direction-map",
    surface: "app",
    title: "Rug Tuft Direction Map",
    upstream: "https://rug-tuft-direction-map.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/rug-tuft-direction-map.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Paint tuft directions across a 16 × 12 color map, editing individual cells or rectangular regions.",
  },
  // Roller Seam Foundry. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/roller-seam-foundry.
  {
    slug: "roller-seam-foundry",
    surface: "app",
    title: "Roller Seam Foundry",
    upstream: "https://roller-seam-foundry.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/roller-seam-foundry.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Explore digital textures in a local workspace for roller seam studies.",
  },
  // Resonance Inheritance. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/resonance-inheritance.
  {
    slug: "resonance-inheritance",
    surface: "app",
    title: "Resonance Inheritance",
    upstream: "https://resonance-inheritance.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/resonance-inheritance.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Play an instrument whose five resonators inherit spectral peaks from the previous sound, and revisit its sound lineage.",
  },
  // Repeat Lattice Recovery. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/repeat-lattice-recovery.
  {
    slug: "repeat-lattice-recovery",
    surface: "app",
    title: "Repeat Lattice Recovery",
    upstream: "https://repeat-lattice-recovery.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/repeat-lattice-recovery.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Explore repeating color patterns with lattice and origin controls, local PNG imports, and pixel editing.",
  },
  // Repair Constellation Map. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/repair-constellation-map.
  {
    slug: "repair-constellation-map",
    surface: "app",
    title: "Repair Constellation Map",
    upstream: "https://repair-constellation-map.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/repair-constellation-map.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Plan visible mending with traced damage, patch polygons, decorative stitch paths, and marked keep-out areas.",
  },
  // Relative Loudness Drama. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/relative-loudness-drama.
  {
    slug: "relative-loudness-drama",
    surface: "app",
    title: "Relative Loudness Drama",
    upstream: "https://relative-loudness-drama.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/relative-loudness-drama.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Compose four synthetic parts and shape their changing balance with timed relative loudness constraints.",
  },
  // Reference Lightness Equalizer. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/reference-lightness-equalizer.
  {
    slug: "reference-lightness-equalizer",
    surface: "app",
    title: "Reference Lightness Equalizer",
    upstream: "https://reference-lightness-equalizer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/reference-lightness-equalizer.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Compare lightness across vessel studies or imported images using editable rectangular reference patches.",
  },
  // Reassembly Route Sketch. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/reassembly-route-sketch.
  {
    slug: "reassembly-route-sketch",
    surface: "app",
    title: "Reassembly Route Sketch",
    upstream: "https://reassembly-route-sketch.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/reassembly-route-sketch.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Sketch reassembly routes for found-object art in a local authoring workspace.",
  },
  // Pressed Flower Stack. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/pressed-flower-stack.
  {
    slug: "pressed-flower-stack",
    surface: "app",
    title: "Pressed Flower Stack",
    upstream: "https://pressed-flower-stack.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/pressed-flower-stack.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Arrange botanical specimens across six transparent layers, import local photos, and paint alpha masks.",
  },
  // Porous Border Garden. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/porous-border-garden.
  {
    slug: "porous-border-garden",
    surface: "app",
    title: "Porous Border Garden",
    upstream: "https://porous-border-garden.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/porous-border-garden.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Paint height, transition-distance, and lock masks to shape a porous border garden, or import a 32 × 32 project.",
  },
  // Popup Motion Notebook. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/popup-motion-notebook.
  {
    slug: "popup-motion-notebook",
    surface: "app",
    title: "Popup Motion Notebook",
    upstream: "https://popup-motion-notebook.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/popup-motion-notebook.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Create a digital pop-up scene and its artwork sheets, starting with a lantern garden or a blank canvas.",
  },
  // Polarization Capture Loom. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/polarization-capture-loom.
  {
    slug: "polarization-capture-loom",
    surface: "app",
    title: "Polarization Capture Loom",
    upstream: "https://polarization-capture-loom.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/polarization-capture-loom.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Reconstruct polarization at each pixel from aligned linear-luminance observations in a local workbench.",
  },
  // Plankton Lantern Atelier. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/plankton-lantern-atelier.
  {
    slug: "plankton-lantern-atelier",
    surface: "app",
    title: "Plankton Lantern Atelier",
    upstream: "https://plankton-lantern-atelier.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/plankton-lantern-atelier.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Design nested lantern shells with selective openings and branching spines attached to each shell.",
  },
  // Pixel Repair Receipt. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/pixel-repair-receipt.
  {
    slug: "pixel-repair-receipt",
    surface: "app",
    title: "Pixel Repair Receipt",
    upstream: "https://pixel-repair-receipt.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/pixel-repair-receipt.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Stamp circular clone repairs onto an image and compare the original, edited result, and exact difference map.",
  },
  // Pilot Tone Time Repair. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/pilot-tone-time-repair.
  {
    slug: "pilot-tone-time-repair",
    surface: "app",
    title: "Pilot Tone Time Repair",
    upstream: "https://pilot-tone-time-repair.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/pilot-tone-time-repair.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Restore your pilot-equipped recordings by estimating and correcting their shared time warp.",
  },
  // Pitch Island Crossing. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/pitch-island-crossing.
  {
    slug: "pitch-island-crossing",
    surface: "app",
    title: "Pitch Island Crossing",
    upstream: "https://pitch-island-crossing.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/pitch-island-crossing.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Assign three rational scales, connect them with shared-note bridges, and compose a one-voice route between them.",
  },
  // Phrase Fork Rehearsal. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/phrase-fork-rehearsal.
  {
    slug: "phrase-fork-rehearsal",
    surface: "app",
    title: "Phrase Fork Rehearsal",
    upstream: "https://phrase-fork-rehearsal.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/phrase-fork-rehearsal.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Replace a note at a planned moment in a phrase and practice returning to its original ending.",
  },
  // Phonation Origami. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/phonation-origami.
  {
    slug: "phonation-origami",
    surface: "app",
    title: "Phonation Origami",
    upstream: "https://phonation-origami.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/phonation-origami.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Explore experimental audio ideas for vocal improvisation.",
  },
  // Perforated Page Lightbook. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/perforated-page-lightbook.
  {
    slug: "perforated-page-lightbook",
    surface: "app",
    title: "Perforated Page Lightbook",
    upstream: "https://perforated-page-lightbook.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/perforated-page-lightbook.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Draw openings in opaque digital pages, arrange named page groups, and explore how their apertures align.",
  },
  // Phantom Fossil Press. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/phantom-fossil-press.
  {
    slug: "phantom-fossil-press",
    surface: "app",
    title: "Phantom Fossil Press",
    upstream: "https://phantom-fossil-press.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/phantom-fossil-press.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Edit a fictional relief and its paired positive and negative contact prints, choosing which details to omit.",
  },
  // Pearl Shell Bead Surface. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/pearl-shell-bead-surface.
  {
    slug: "pearl-shell-bead-surface",
    surface: "app",
    title: "Pearl Shell Bead Surface",
    upstream: "https://pearl-shell-bead-surface.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/pearl-shell-bead-surface.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Arrange closed bead rows on an irregular shell and adjust their height, wave, bead count, and seam phase.",
  },
  // Partial Residual Workshop. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/partial-residual-workshop.
  {
    slug: "partial-residual-workshop",
    surface: "app",
    title: "Partial Residual Workshop",
    upstream: "https://partial-residual-workshop.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/partial-residual-workshop.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Shape tracked partials and residual sound from your own short, nonvoice mono clip in a local audio workspace.",
  },
  // Pearl Layer Peeling. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/pearl-layer-peeling.
  {
    slug: "pearl-layer-peeling",
    surface: "app",
    title: "Pearl Layer Peeling",
    upstream: "https://pearl-layer-peeling.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/pearl-layer-peeling.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Create and edit material-art studies in a local studio.",
  },
  // Paracord Color Transit. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/paracord-color-transit.
  {
    slug: "paracord-color-transit",
    surface: "app",
    title: "Paracord Color Transit",
    upstream: "https://paracord-color-transit.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/paracord-color-transit.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Trace source colors through a three-strand crossing pattern with editable strand colors, boundaries, and path lengths.",
  },
  // Palette Role Migrator. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/palette-role-migrator.
  {
    slug: "palette-role-migrator",
    surface: "app",
    title: "Palette Role Migrator",
    upstream: "https://palette-role-migrator.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/palette-role-migrator.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Recolor illustrations by semantic roles using explicit region masks and shared color assignments.",
  },
  // Paper Pulp Island Plan. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/paper-pulp-island-plan.
  {
    slug: "paper-pulp-island-plan",
    surface: "app",
    title: "Paper Pulp Island Plan",
    upstream: "https://paper-pulp-island-plan.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/paper-pulp-island-plan.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Compose symbolic pulp regions and embedded elliptical fragments, then inspect authored cross sections.",
  },
  // Packet Lantern. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/packet-lantern.
  {
    slug: "packet-lantern",
    surface: "app",
    title: "Packet Lantern",
    upstream: "https://packet-lantern.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/packet-lantern.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Paint a four-color 64 × 64 picture, encode it into eight QR frames, and reassemble it exactly despite missing frames.",
  },
  // Packing Poem Cabinet. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/packing-poem-cabinet.
  {
    slug: "packing-poem-cabinet",
    surface: "app",
    title: "Packing Poem Cabinet",
    upstream: "https://packing-poem-cabinet.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/packing-poem-cabinet.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Arrange cuboids in an abstract box and author sightlines through the spaces they leave open.",
  },
  // Ornament Negative Kit. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/ornament-negative-kit.
  {
    slug: "ornament-negative-kit",
    surface: "app",
    title: "Ornament Negative Kit",
    upstream: "https://ornament-negative-kit.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/ornament-negative-kit.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Plan a family of imaginary ornaments with one reusable digital cavity and independently named fill masks.",
  },
  // Optical Soundtrack Reader. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/optical-soundtrack-reader.
  {
    slug: "optical-soundtrack-reader",
    surface: "app",
    title: "Optical Soundtrack Reader",
    upstream: "https://optical-soundtrack-reader.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/optical-soundtrack-reader.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Turn black-and-white optical soundtrack images into audio with transmission calibration, filtering, and resampling.",
  },
  // One Sample Many Distances. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/one-sample-many-distances.
  {
    slug: "one-sample-many-distances",
    surface: "app",
    title: "One Sample Many Distances",
    upstream: "https://one-sample-many-distances.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/one-sample-many-distances.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Author a family of distance-response audio assets for a fictional game event from one sample.",
  },
  // Origami Watermark Mapper. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/origami-watermark-mapper.
  {
    slug: "origami-watermark-mapper",
    surface: "app",
    title: "Origami Watermark Mapper",
    upstream: "https://origami-watermark-mapper.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/origami-watermark-mapper.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Place artwork by working backward through a documented sequence of two half-folds.",
  },
  // Offset Ghostwriter. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/offset-ghostwriter.
  {
    slug: "offset-ghostwriter",
    surface: "app",
    title: "Offset Ghostwriter",
    upstream: "https://offset-ghostwriter.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/offset-ghostwriter.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Create contact-printing studies in a local workspace.",
  },
  // One Room Many Genres — catalog 648. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/one-room-many-genres.
  {
    slug: "one-room-many-genres",
    surface: "app",
    title: "One Room Many Genres — catalog 648",
    upstream: "https://one-room-many-genres.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/one-room-many-genres.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Keep story events fixed and compare how coincidence, intention, and supernatural explanations change their reading.",
  },
  // Offline Mural Merge. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/offline-mural-merge.
  {
    slug: "offline-mural-merge",
    surface: "app",
    title: "Offline Mural Merge",
    upstream: "https://offline-mural-merge.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/offline-mural-merge.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Author vector mural histories and reconcile three-way edits locally.",
  },
  // Object Packing Theatre. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/object-packing-theatre.
  {
    slug: "object-packing-theatre",
    surface: "app",
    title: "Object Packing Theatre",
    upstream: "https://object-packing-theatre.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/object-packing-theatre.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Compose symbolic digital unpacking stories for gifts, starting with a constellation study or a blank canvas.",
  },
  // Needle Felt Volume Score. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/needle-felt-volume-score.
  {
    slug: "needle-felt-volume-score",
    surface: "app",
    title: "Needle Felt Volume Score",
    upstream: "https://needle-felt-volume-score.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/needle-felt-volume-score.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Arrange soft masses, inspect surface, cutaway, and exploded views, and export editable studies or printable scores.",
  },
  // Musical Pursuit Without Chase. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/musical-pursuit-without-chase.
  {
    slug: "musical-pursuit-without-chase",
    surface: "app",
    title: "Musical Pursuit Without Chase",
    upstream: "https://musical-pursuit-without-chase.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/musical-pursuit-without-chase.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Compose two independently routed voices through eight note vertices and twelve moving target windows.",
  },
  // Musical Deletion Proof. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/musical-deletion-proof.
  {
    slug: "musical-deletion-proof",
    surface: "app",
    title: "Musical Deletion Proof",
    upstream: "https://musical-deletion-proof.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/musical-deletion-proof.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Compose four voices around six landmarks and explore whether the structure survives any one voice disappearing.",
  },
  // Musical Absence Register. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/musical-absence-register.
  {
    slug: "musical-absence-register",
    surface: "app",
    title: "Musical Absence Register",
    upstream: "https://musical-absence-register.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/musical-absence-register.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Compose three sounding parts and one symbolic silent part to explore expected musical entries that never arrive.",
  },
  // Motif Interference Proof. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/motif-interference-proof.
  {
    slug: "motif-interference-proof",
    surface: "app",
    title: "Motif Interference Proof",
    upstream: "https://motif-interference-proof.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/motif-interference-proof.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Author decorative motifs and explore the patterns that emerge where they overlap.",
  },
  // Negative to Keepsake. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/negative-to-keepsake.
  {
    slug: "negative-to-keepsake",
    surface: "app",
    title: "Negative to Keepsake",
    upstream: "https://negative-to-keepsake.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/negative-to-keepsake.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Convert film-negative scans into keepsake images with local cropping, base sampling, transfer controls, and channel curves.",
  },
  // Motif Extinction Lab. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/motif-extinction-lab.
  {
    slug: "motif-extinction-lab",
    surface: "app",
    title: "Motif Extinction Lab",
    upstream: "https://motif-extinction-lab.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/motif-extinction-lab.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Compose original motifs and explore their elimination in a finite artwork, starting blank or with a coral archipelago.",
  },
  // Motif Census Map. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/motif-census-map.
  {
    slug: "motif-census-map",
    surface: "app",
    title: "Motif Census Map",
    upstream: "https://motif-census-map.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/motif-census-map.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Map motif identities across artworks and compare their shared geometry, scale, rotation, and frequency.",
  },
  // Mosaique Shard Fit. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/mosaique-shard-fit.
  {
    slug: "mosaique-shard-fit",
    surface: "app",
    title: "Mosaique Shard Fit",
    upstream: "https://mosaique-shard-fit.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/mosaique-shard-fit.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Trace an overhead image and study irregular mosaic layouts within a fixed inventory of shards.",
  },
  // Mirror Pair Jewels. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/mirror-pair-jewels.
  {
    slug: "mirror-pair-jewels",
    surface: "app",
    title: "Mirror Pair Jewels",
    upstream: "https://mirror-pair-jewels.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/mirror-pair-jewels.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Design abstract crossing ornaments, compare reflected pairs, and explore their forms in a rotatable 3D view.",
  },
  // Miniature Brick Bond. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/miniature-brick-bond.
  {
    slug: "miniature-brick-bond",
    surface: "app",
    title: "Miniature Brick Bond",
    upstream: "https://miniature-brick-bond.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/miniature-brick-bond.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Draw miniature facades and openings, place numbered reusable bricks, and compare seams across adjacent courses.",
  },
  // Miniature Patina Continuity. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/miniature-patina-continuity.
  {
    slug: "miniature-patina-continuity",
    surface: "app",
    title: "Miniature Patina Continuity",
    upstream: "https://miniature-patina-continuity.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/miniature-patina-continuity.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Author patina studies and explore their continuity for miniature sets in a local workspace.",
  },
  // Microtonal Common Ground. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/microtonal-common-ground.
  {
    slug: "microtonal-common-ground",
    surface: "app",
    title: "Microtonal Common Ground",
    upstream: "https://microtonal-common-ground.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/microtonal-common-ground.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Compare three frequency lists and explore common ground with explicit pitch-bend permissions and cents tolerances.",
  },
  // Microscopic Material Stage. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/microscopic-material-stage.
  {
    slug: "microscopic-material-stage",
    surface: "app",
    title: "Microscopic Material Stage",
    upstream: "https://microscopic-material-stage.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/microscopic-material-stage.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Author fictional artwork at three nested scales, share landmarks across views, and export labelled SVGs and editable projects.",
  },
  // Metrical Translation Desk. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/metrical-translation-desk.
  {
    slug: "metrical-translation-desk",
    surface: "app",
    title: "Metrical Translation Desk",
    upstream: "https://metrical-translation-desk.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/metrical-translation-desk.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Explore duration allocations that minimize a chosen cost while preserving exact totals and pinned event relationships.",
  },
  // Mechanical Pen Orchestra. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/mechanical-pen-orchestra.
  {
    slug: "mechanical-pen-orchestra",
    surface: "app",
    title: "Mechanical Pen Orchestra",
    upstream: "https://mechanical-pen-orchestra.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/mechanical-pen-orchestra.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Construct coupled drawing instruments and explore the generative patterns produced by their ideal motion.",
  },
  // Measured Overprint Separator. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/measured-overprint-separator.
  {
    slug: "measured-overprint-separator",
    surface: "app",
    title: "Measured Overprint Separator",
    upstream: "https://measured-overprint-separator.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/measured-overprint-separator.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Explore two-ink separations for a local image using measured color samples and compare competing overprint recipes.",
  },
  // Material Sample Storyboard. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/material-sample-storyboard.
  {
    slug: "material-sample-storyboard",
    surface: "app",
    title: "Material Sample Storyboard",
    upstream: "https://material-sample-storyboard.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/material-sample-storyboard.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Assign your material samples to object regions, adjust crops and orientation, and compare synchronized installation views.",
  },
  // Material Misfit Collage. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/material-misfit-collage.
  {
    slug: "material-misfit-collage",
    surface: "app",
    title: "Material Misfit Collage",
    upstream: "https://material-misfit-collage.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/material-misfit-collage.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Author material collages by partitioning surfaces in a local workspace.",
  },
  // Material Light Unweaver. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/material-light-unweaver.
  {
    slug: "material-light-unweaver",
    surface: "app",
    title: "Material Light Unweaver",
    upstream: "https://material-light-unweaver.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/material-light-unweaver.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Estimate reflectance and lighting from authored constraints, recolor reflectance, and inspect the remaining image differences.",
  },
  // Material Evidence Cards. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/material-evidence-cards.
  {
    slug: "material-evidence-cards",
    surface: "app",
    title: "Material Evidence Cards",
    upstream: "https://material-evidence-cards.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/material-evidence-cards.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Create a synthetic band shader and compare its appearance under three fixed lighting and viewing setups on simple 3D forms.",
  },
  // Mask Edge Contract. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/mask-edge-contract.
  {
    slug: "mask-edge-contract",
    surface: "app",
    title: "Mask Edge Contract",
    upstream: "https://mask-edge-contract.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/mask-edge-contract.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Declare protected visible components and holes, apply Boolean masks, and find the first operation that breaks those rules.",
  },
  // Master Correspondence Dock. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/master-correspondence-dock.
  {
    slug: "master-correspondence-dock",
    surface: "app",
    title: "Master Correspondence Dock",
    upstream: "https://master-correspondence-dock.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/master-correspondence-dock.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Pair cubic glyph contours and semantic landmarks by hand before normalizing their structure without losing shape.",
  },
  // Locking Link Alphabet. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/locking-link-alphabet.
  {
    slug: "locking-link-alphabet",
    surface: "app",
    title: "Locking Link Alphabet",
    upstream: "https://locking-link-alphabet.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/locking-link-alphabet.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Invent decorative link silhouettes, define symbolic connector rules, and compose digital chains.",
  },
  // Line Accent Distributor. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/line-accent-distributor.
  {
    slug: "line-accent-distributor",
    surface: "app",
    title: "Line Accent Distributor",
    upstream: "https://line-accent-distributor.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/line-accent-distributor.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Classify illustration marks as contour, structure, or accent, fit shared role bases, and inspect exceptions across a family.",
  },
  // Lightstage Lie Painter. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/lightstage-lie-painter.
  {
    slug: "lightstage-lie-painter",
    surface: "app",
    title: "Lightstage Lie Painter",
    upstream: "https://lightstage-lie-painter.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/lightstage-lie-painter.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Author synthetic inverse diffuse-shading studies in a local painting workspace.",
  },
  // Light Arithmetic Bench. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/light-arithmetic-bench.
  {
    slug: "light-arithmetic-bench",
    surface: "app",
    title: "Light Arithmetic Bench",
    upstream: "https://light-arithmetic-bench.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/light-arithmetic-bench.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Explore bounded image arithmetic with explicit value types.",
  },
  // Leaf Vein Circuit Artist. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/leaf-vein-circuit-artist.
  {
    slug: "leaf-vein-circuit-artist",
    surface: "app",
    title: "Leaf Vein Circuit Artist",
    upstream: "https://leaf-vein-circuit-artist.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/leaf-vein-circuit-artist.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Draw a leaf outline and primary vein chain, place attraction sites and barriers, and grow fictional branching vein artwork.",
  },
  // Layered Color Font Foundry. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/layered-color-font-foundry.
  {
    slug: "layered-color-font-foundry",
    surface: "app",
    title: "Layered Color Font Foundry",
    upstream: "https://layered-color-font-foundry.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/layered-color-font-foundry.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Draw layered color glyphs and fallback contours, then compile them into a bounded OpenType font.",
  },
  // Known Blur Reversal. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/known-blur-reversal.
  {
    slug: "known-blur-reversal",
    surface: "app",
    title: "Known Blur Reversal",
    upstream: "https://known-blur-reversal.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/known-blur-reversal.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Restore local grayscale images with a declared blur kernel, explore iterative updates, and choose a history step to export.",
  },
  // Layer Contribution Inspector. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/layer-contribution-inspector.
  {
    slug: "layer-contribution-inspector",
    surface: "app",
    title: "Layer Contribution Inspector",
    upstream: "https://layer-contribution-inspector.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/layer-contribution-inspector.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Author raster layer stacks through editable JSON and inspect how individual layers contribute to the image.",
  },
  // Kinetic Drape Score. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/kinetic-drape-score.
  {
    slug: "kinetic-drape-score",
    surface: "app",
    title: "Kinetic Drape Score",
    upstream: "https://kinetic-drape-score.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/kinetic-drape-score.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Author attachment trajectories, rehearse a coarse hanging surface in 3D, and inspect remaining edge errors for stage concepts.",
  },
  // Intonation Reconciliation. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/intonation-reconciliation.
  {
    slug: "intonation-reconciliation",
    surface: "app",
    title: "Intonation Reconciliation",
    upstream: "https://intonation-reconciliation.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/intonation-reconciliation.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Tune six notes against rational interval constraints, pin pure intervals, and explore where conflicting ratios must compromise.",
  },
  // Iris Aperture Painter. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/iris-aperture-painter.
  {
    slug: "iris-aperture-painter",
    surface: "app",
    title: "Iris Aperture Painter",
    upstream: "https://iris-aperture-painter.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/iris-aperture-painter.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Design a shared aperture opening by rotating planar blades in a local SVG workspace.",
  },
  // Intaglio Reveal Object. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/intaglio-reveal-object.
  {
    slug: "intaglio-reveal-object",
    surface: "app",
    title: "Intaglio Reveal Object",
    upstream: "https://intaglio-reveal-object.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/intaglio-reveal-object.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Author signed shallow relief carved into the rear of an ideal transparent slab.",
  },
  // Insect Shell Panel Atelier. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/insect-shell-panel-atelier.
  {
    slug: "insect-shell-panel-atelier",
    surface: "app",
    title: "Insect Shell Panel Atelier",
    upstream: "https://insect-shell-panel-atelier.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/insect-shell-panel-atelier.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Design fantasy creature shells in an interactive 3D authoring studio.",
  },
  // Impossible Transparency. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/impossible-transparency.
  {
    slug: "impossible-transparency",
    surface: "app",
    title: "Impossible Transparency",
    upstream: "https://impossible-transparency.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/impossible-transparency.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Arrange colored rectangles, author their intersection colors, and compare them with linear-light transparency in both layer orders.",
  },
  // Imaginary Shell Maker. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/imaginary-shell-maker.
  {
    slug: "imaginary-shell-maker",
    surface: "app",
    title: "Imaginary Shell Maker",
    upstream: "https://imaginary-shell-maker.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/imaginary-shell-maker.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Shape creature shells by guiding a closed aperture along an editable path with scaling and rotation.",
  },
  // Image Foldback. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/image-foldback.
  {
    slug: "image-foldback",
    surface: "app",
    title: "Image Foldback",
    upstream: "https://image-foldback.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/image-foldback.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Draw crossing image ribbons, assign strip visits, inspect seams, and export a PNG with its exact pixel ownership map.",
  },
  // Hinge Note Studio. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/hinge-note-studio.
  {
    slug: "hinge-note-studio",
    surface: "app",
    title: "Hinge Note Studio",
    upstream: "https://hinge-note-studio.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/hinge-note-studio.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Edit two eight-note phrases around a shared pitch and onset, pin notes, and generate alternatives within direction, range, and leap bounds.",
  },
  // Halftone Treaty. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/halftone-treaty.
  {
    slug: "halftone-treaty",
    surface: "app",
    title: "Halftone Treaty",
    upstream: "https://halftone-treaty.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/halftone-treaty.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Draw tone and gradient fields, then adjust periodic halftone screens across adjacent regions.",
  },
  // Herbarium of Impossibilities. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/herbarium-of-impossibilities.
  {
    slug: "herbarium-of-impossibilities",
    surface: "app",
    title: "Herbarium of Impossibilities",
    upstream: "https://herbarium-of-impossibilities.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/herbarium-of-impossibilities.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Assemble fictional botanical collages from traced leaf and stem fragments, connect typed sockets, and export an SVG and assembly graph.",
  },
  // Halftone Screen Lift. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/halftone-screen-lift.
  {
    slug: "halftone-screen-lift",
    surface: "app",
    title: "Halftone Screen Lift",
    upstream: "https://halftone-screen-lift.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/halftone-screen-lift.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Inspect grayscale print images, measure periodic halftone screens, and suppress their patterns locally.",
  },
  // Grain Spectrum Foundry. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/grain-spectrum-foundry.
  {
    slug: "grain-spectrum-foundry",
    surface: "app",
    title: "Grain Spectrum Foundry",
    upstream: "https://grain-spectrum-foundry.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/grain-spectrum-foundry.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Author small grayscale textures, randomize their spectral phases, and compare floating-point and exported PNG spectra.",
  },
  // Glyph Dependency Subsetter. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/glyph-dependency-subsetter.
  {
    slug: "glyph-dependency-subsetter",
    surface: "app",
    title: "Glyph Dependency Subsetter",
    upstream: "https://glyph-dependency-subsetter.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/glyph-dependency-subsetter.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Explore font subsets seeded by text or glyph IDs, trace component and ligature dependencies, and see why each glyph is retained.",
  },
  // Grain Direction Tutor. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/grain-direction-tutor.
  {
    slug: "grain-direction-tutor",
    surface: "app",
    title: "Grain Direction Tutor",
    upstream: "https://grain-direction-tutor.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/grain-direction-tutor.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Paint a direction guide, draw manual hatching, and compare stroke tangents with your own guide field.",
  },
  // Gamut Repair Map. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/gamut-repair-map.
  {
    slug: "gamut-repair-map",
    surface: "app",
    title: "Gamut Repair Map",
    upstream: "https://gamut-repair-map.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/gamut-repair-map.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Author rectangular Display P3 artwork and choose color compression compromises for regions outside the target gamut.",
  },
  // Gallery Reset Steward. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/gallery-reset-steward.
  {
    slug: "gallery-reset-steward",
    surface: "app",
    title: "Gallery Reset Steward",
    upstream: "https://gallery-reset-steward.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/gallery-reset-steward.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Paint a 16-tile exhibition mosaic, customize its ink and captions, and edit the complete composition as JSON.",
  },
  // Fringe Twist Loom. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/fringe-twist-loom.
  {
    slug: "fringe-twist-loom",
    surface: "app",
    title: "Fringe Twist Loom",
    upstream: "https://fringe-twist-loom.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/fringe-twist-loom.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Design ruled slit groups around a common uncut spine and vary the twist of individual strips.",
  },
  // Frame Budget Studio. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/frame-budget-studio.
  {
    slug: "frame-budget-studio",
    surface: "app",
    title: "Frame Budget Studio",
    upstream: "https://frame-budget-studio.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/frame-budget-studio.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Prioritize procedural Canvas components, protect central landmarks, measure rendering time, and export an adaptive recipe.",
  },
  // Fragment Reunion Desk. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/fragment-reunion-desk.
  {
    slug: "fragment-reunion-desk",
    surface: "app",
    title: "Fragment Reunion Desk",
    upstream: "https://fragment-reunion-desk.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/fragment-reunion-desk.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Trace fragment boundaries, pair edge features manually, and explore independent reassembly hypotheses.",
  },
  // Fourbar Drawing Menagerie. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/fourbar-drawing-menagerie.
  {
    slug: "fourbar-drawing-menagerie",
    surface: "app",
    title: "Fourbar Drawing Menagerie",
    upstream: "https://fourbar-drawing-menagerie.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/fourbar-drawing-menagerie.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Design planar four-bar linkages, choose an assembly branch and pen position, and record up to twelve drawing traces.",
  },
  // Foldout Shadow Letter. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/foldout-shadow-letter.
  {
    slug: "foldout-shadow-letter",
    surface: "app",
    title: "Foldout Shadow Letter",
    upstream: "https://foldout-shadow-letter.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/foldout-shadow-letter.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Arrange SVG motifs and a discrete shadow mask, edit the project as JSON, save locally, and print your foldout letter.",
  },
  // Foldout Object Labels. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/foldout-object-labels.
  {
    slug: "foldout-object-labels",
    surface: "app",
    title: "Foldout Object Labels",
    upstream: "https://foldout-object-labels.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/foldout-object-labels.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Plan foldout object labels for museum craft activities.",
  },
  // Folding Screen Face Map. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/folding-screen-face-map.
  {
    slug: "folding-screen-face-map",
    surface: "app",
    title: "Folding Screen Face Map",
    upstream: "https://folding-screen-face-map.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/folding-screen-face-map.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Allocate vector motifs to the front and back of hinged panels and compare the artwork across two screen arrangements.",
  },
  // Folded Reflection Mask. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/folded-reflection-mask.
  {
    slug: "folded-reflection-mask",
    surface: "app",
    title: "Folded Reflection Mask",
    upstream: "https://folded-reflection-mask.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/folded-reflection-mask.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Paint a surrounding image, attach named patches to five hinged reflector planes, and author timed poses.",
  },
  // Folded Photogram Stage. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/folded-photogram-stage.
  {
    slug: "folded-photogram-stage",
    surface: "app",
    title: "Folded Photogram Stage",
    upstream: "https://folded-photogram-stage.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/folded-photogram-stage.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Compose symbolic geometric-light studies on a folded photogram stage.",
  },
  // Folded Petal Kinships. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/folded-petal-kinships.
  {
    slug: "folded-petal-kinships",
    surface: "app",
    title: "Folded Petal Kinships",
    upstream: "https://folded-petal-kinships.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/folded-petal-kinships.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Design families of hinged petals, link or release them, and export sculpture geometry with a shared-parameter map.",
  },
  // Folded Metal Fiction. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/folded-metal-fiction.
  {
    slug: "folded-metal-fiction",
    surface: "app",
    title: "Folded Metal Fiction",
    upstream: "https://folded-metal-fiction.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/folded-metal-fiction.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Build fictional panel sculptures, edit crease angles, inspect possible intersections, and export JSON and OBJ geometry.",
  },
  // Focus Stack Craft Capture. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/focus-stack-craft-capture.
  {
    slug: "focus-stack-craft-capture",
    surface: "app",
    title: "Focus Stack Craft Capture",
    upstream: "https://focus-stack-craft-capture.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/focus-stack-craft-capture.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Align a local burst of miniature photos and combine their sharpest regions into a focus-stacked image.",
  },
  // Folded Corrugation Lab. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/folded-corrugation-lab.
  {
    slug: "folded-corrugation-lab",
    surface: "app",
    title: "Folded Corrugation Lab",
    upstream: "https://folded-corrugation-lab.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/folded-corrugation-lab.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Design accordion sheets with alternating mountain and valley folds, editable strip widths, depth, and opening angle.",
  },
  // Foam Cut Menagerie. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/foam-cut-alphabet-free.
  {
    slug: "foam-cut-alphabet-free",
    surface: "app",
    title: "Foam Cut Menagerie",
    upstream: "https://foam-cut-alphabet-free.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/foam-cut-alphabet-free.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Turn creature polygons into coarse cellular volumes, remove interior cells, and merge adjacent solid regions while preserving the exterior.",
  },
  // Fold Thickness Rehearsal. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/fold-thickness-rehearsal.
  {
    slug: "fold-thickness-rehearsal",
    surface: "app",
    title: "Fold Thickness Rehearsal",
    upstream: "https://fold-thickness-rehearsal.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/fold-thickness-rehearsal.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Design chains of thick rectangular panels, adjust hinge offsets, animate closure, and inspect collisions.",
  },
  // Flipface Object Display. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/flipface-object-display.
  {
    slug: "flipface-object-display",
    surface: "app",
    title: "Flipface Object Display",
    upstream: "https://flipface-object-display.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/flipface-object-display.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Design interactive kinetic tile displays in a 3D studio.",
  },
  // Flatbed Fragment Stitcher. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/flatbed-fragment-stitcher.
  {
    slug: "flatbed-fragment-stitcher",
    surface: "app",
    title: "Flatbed Fragment Stitcher",
    upstream: "https://flatbed-fragment-stitcher.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/flatbed-fragment-stitcher.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Import four same-scale scanner captures, edit their positions, and align overlapping fragments locally.",
  },
  // Flash Click Sync Ruler. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/flash-click-sync-ruler.
  {
    slug: "flash-click-sync-ruler",
    surface: "app",
    title: "Flash Click Sync Ruler",
    upstream: "https://flash-click-sync-ruler.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/flash-click-sync-ruler.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Create irregular audio and visual cues, align them with a local recording, and measure relative timing offsets.",
  },
  // Fictional Bone Cabinet. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/fictional-bone-cabinet.
  {
    slug: "fictional-bone-cabinet",
    surface: "app",
    title: "Fictional Bone Cabinet",
    upstream: "https://fictional-bone-cabinet.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/fictional-bone-cabinet.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Design original fictional creature skeletons as editable 3D sculptures.",
  },
  // Facet Flash Composer. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/facet-flash-composer.
  {
    slug: "facet-flash-composer",
    surface: "app",
    title: "Facet Flash Composer",
    upstream: "https://facet-flash-composer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/facet-flash-composer.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Shape convex forms with editable facet planes and choreograph their ideal reflected flashes.",
  },
  // Exposure Radiance Loom. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/exposure-radiance-loom.
  {
    slug: "exposure-radiance-loom",
    surface: "app",
    title: "Exposure Radiance Loom",
    upstream: "https://exposure-radiance-loom.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/exposure-radiance-loom.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Compose radiance studies locally from aligned exposure brackets of stationary scenes.",
  },
  // Embroidered Folding Map. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/embroidered-folding-map.
  {
    slug: "embroidered-folding-map",
    surface: "app",
    title: "Embroidered Folding Map",
    upstream: "https://embroidered-folding-map.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/embroidered-folding-map.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Compose and edit thread routes on both faces of a folding map made of two connected panels.",
  },
  // Emboss Depth Proof. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/emboss-depth-proof.
  {
    slug: "emboss-depth-proof",
    surface: "app",
    title: "Emboss Depth Proof",
    upstream: "https://emboss-depth-proof.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/emboss-depth-proof.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Design rectangular relief islands with stepped heights, inspect cross-sections, and explore illustrative raked light.",
  },
  // Embellishment Reach Map. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/embellishment-reach-map.
  {
    slug: "embellishment-reach-map",
    surface: "app",
    title: "Embellishment Reach Map",
    upstream: "https://embellishment-reach-map.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/embellishment-reach-map.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Plan an addition order for decorative elements that preserves access along your declared tool rays.",
  },
  // Edge Inventory Drawer. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/edge-inventory-drawer.
  {
    slug: "edge-inventory-drawer",
    surface: "app",
    title: "Edge Inventory Drawer",
    upstream: "https://edge-inventory-drawer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/edge-inventory-drawer.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Select rectangular edge bands from artworks and reuse them in a local collage composition.",
  },
  // Drawing Assumption Cards. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/drawing-assumption-cards.
  {
    slug: "drawing-assumption-cards",
    surface: "app",
    title: "Drawing Assumption Cards",
    upstream: "https://drawing-assumption-cards.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/drawing-assumption-cards.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Attach assumptions to exact artwork regions, draw manual alternatives, compare them, and export your study.",
  },
  // Delta Frame Cabinet. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/delta-frame-cabinet.
  {
    slug: "delta-frame-cabinet",
    surface: "app",
    title: "Delta Frame Cabinet",
    upstream: "https://delta-frame-cabinet.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/delta-frame-cabinet.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Compose incremental PNG frame updates with explicit rectangles and compare retained, cleared, or restored pixels.",
  },
  // Distance Portrait Room. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/distance-portrait-room.
  {
    slug: "distance-portrait-room",
    surface: "app",
    title: "Distance Portrait Room",
    upstream: "https://distance-portrait-room.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/distance-portrait-room.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Arrange symbolic objects in 3D from an authored relationship-distance matrix and inspect requested versus actual distances.",
  },
  // Detail Ladder. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/detail-ladder.
  {
    slug: "detail-ladder",
    surface: "app",
    title: "Detail Ladder",
    upstream: "https://detail-ladder.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/detail-ladder.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Compose grid-aligned rectangles with parent relationships and coarse, middle, or close arrival tiers.",
  },
  // Dash Phase Network. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/dash-phase-network.
  {
    slug: "dash-phase-network",
    surface: "app",
    title: "Dash Phase Network",
    upstream: "https://dash-phase-network.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/dash-phase-network.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Author orthogonal path graphs, solve compatible dash phases at junctions, and export expanded SVG marks.",
  },
  // Curve Budget Compiler. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/curve-budget-compiler.
  {
    slug: "curve-budget-compiler",
    surface: "app",
    title: "Curve Budget Compiler",
    upstream: "https://curve-budget-compiler.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/curve-budget-compiler.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Simplify authored paths within chosen budgets, protect endpoints, and refit curves.",
  },
  // Cut Thread Surface Index. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/cut-thread-surface-index.
  {
    slug: "cut-thread-surface-index",
    surface: "app",
    title: "Cut Thread Surface Index",
    upstream: "https://cut-thread-surface-index.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/cut-thread-surface-index.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Cut edges in symbolic strand graphs, inspect the resulting connected components, and export paired artwork.",
  },
  // Crystal Habit Fiction. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/crystal-habit-fiction.
  {
    slug: "crystal-habit-fiction",
    surface: "app",
    title: "Crystal Habit Fiction",
    upstream: "https://crystal-habit-fiction.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/crystal-habit-fiction.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Sculpt fictional faceted forms with rotational face families and export editable JSON or faceted OBJ.",
  },
  // Craft Variant Branchbook. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/craft-variant-branchbook.
  {
    slug: "craft-variant-branchbook",
    surface: "app",
    title: "Craft Variant Branchbook",
    upstream: "https://craft-variant-branchbook.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/craft-variant-branchbook.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Fork craft process recipes, align operation histories, and create before-and-after cards tied to exact revisions.",
  },
  // Credit Chain Atlas. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/credit-chain-atlas.
  {
    slug: "credit-chain-atlas",
    surface: "app",
    title: "Credit Chain Atlas",
    upstream: "https://credit-chain-atlas.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/credit-chain-atlas.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Keep each local asset's credit line connected to every page and layout where it appears.",
  },
  // Crease Color Concealment. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/crease-color-concealment.
  {
    slug: "crease-color-concealment",
    surface: "app",
    title: "Crease Color Concealment",
    upstream: "https://crease-color-concealment.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/crease-color-concealment.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Author front and back quadrant colors for a fixed map fold and explore which regions become hidden.",
  },
  // Crack Path Portrait. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/crack-path-portrait.
  {
    slug: "crack-path-portrait",
    surface: "app",
    title: "Crack Path Portrait",
    upstream: "https://crack-path-portrait.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/crack-path-portrait.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Create expressive portraits with crack-inspired negative linework in a local canvas studio.",
  },
  // Contour Landmark Swap. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/contour-landmark-swap.
  {
    slug: "contour-landmark-swap",
    surface: "app",
    title: "Contour Landmark Swap",
    upstream: "https://contour-landmark-swap.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/contour-landmark-swap.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Draw and group contour landmarks, lock anchors, vary one group, and export an SVG variant sheet.",
  },
  // Craft Repeat Remainder. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/craft-repeat-remainder.
  {
    slug: "craft-repeat-remainder",
    surface: "app",
    title: "Craft Repeat Remainder",
    upstream: "https://craft-repeat-remainder.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/craft-repeat-remainder.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Design repeating borders along open or closed paths, keeping motif widths fixed while transition gaps absorb the remainder.",
  },
  // Continuous Cord Auditor. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/continuous-cord-auditor.
  {
    slug: "continuous-cord-auditor",
    surface: "app",
    title: "Continuous Cord Auditor",
    upstream: "https://continuous-cord-auditor.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/continuous-cord-auditor.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Edit decorative cord graphs and inspect their connectivity in a local SVG workbench.",
  },
  // Construction Color Lift. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/construction-color-lift.
  {
    slug: "construction-color-lift",
    surface: "app",
    title: "Construction Color Lift",
    upstream: "https://construction-color-lift.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/construction-color-lift.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Separate colored construction marks from final ink in small scanned-artwork crops locally.",
  },
  // Contact Fit Test Cards. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/contact-fit-test-cards.
  {
    slug: "contact-fit-test-cards",
    surface: "app",
    title: "Contact Fit Test Cards",
    upstream: "https://contact-fit-test-cards.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/contact-fit-test-cards.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Create decorative connector dimension cards and attach manual observations to exact revisions.",
  },
  // Color Memory Tiles. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/color-memory-tiles.
  {
    slug: "color-memory-tiles",
    surface: "app",
    title: "Color Memory Tiles",
    upstream: "https://color-memory-tiles.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/color-memory-tiles.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Compose color sequences that return after changing surrounds while shared references preserve the center color.",
  },
  // Composition Gravity Audit. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/composition-gravity-audit.
  {
    slug: "composition-gravity-audit",
    surface: "app",
    title: "Composition Gravity Audit",
    upstream: "https://composition-gravity-audit.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/composition-gravity-audit.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Assign weights to image-region masks and inspect their combined centroid relative to a reference rectangle.",
  },
  // Collage Footnotes. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/collage-footnotes.
  {
    slug: "collage-footnotes",
    surface: "app",
    title: "Collage Footnotes",
    upstream: "https://collage-footnotes.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/collage-footnotes.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Compose local image fragments with source crops and artist notes, then export an interactive collage.",
  },
  // Clay Impression Orchestra. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/clay-impression-orchestra.
  {
    slug: "clay-impression-orchestra",
    surface: "app",
    title: "Clay Impression Orchestra",
    upstream: "https://clay-impression-orchestra.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/clay-impression-orchestra.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Compose rolled-stamp impressions in a local heightfield art studio.",
  },
  // Chroma Border Broker. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/chroma-border-broker.
  {
    slug: "chroma-border-broker",
    surface: "app",
    title: "Chroma Border Broker",
    upstream: "https://chroma-border-broker.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/chroma-border-broker.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Paint a region map, tune interior pigments and border colors, allocate shared-edge widths, and export SVG.",
  },
  // Chart Light Color Anchor. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/chart-light-color-anchor.
  {
    slug: "chart-light-color-anchor",
    surface: "app",
    title: "Chart Light Color Anchor",
    upstream: "https://chart-light-color-anchor.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/chart-light-color-anchor.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Fit a color transform from marked image patches and your own linear RGB references, with separate training and withheld samples.",
  },
  // Chord Bridge Engineer. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/chord-bridge-engineer.
  {
    slug: "chord-bridge-engineer",
    surface: "app",
    title: "Chord Bridge Engineer",
    upstream: "https://chord-bridge-engineer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/chord-bridge-engineer.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Connect four pinned voices through four intermediate chord stages using paths with the least total motion.",
  },
  // Cellular Cutaway Storybox. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/cellular-cutaway-storybox.
  {
    slug: "cellular-cutaway-storybox",
    surface: "app",
    title: "Cellular Cutaway Storybox",
    upstream: "https://cellular-cutaway-storybox.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/cellular-cutaway-storybox.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Explore fictional nested compartments through guided cutaways.",
  },
  // Charred Edge Memory. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/charred-edge-memory.
  {
    slug: "charred-edge-memory",
    surface: "app",
    title: "Charred Edge Memory",
    upstream: "https://charred-edge-memory.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/charred-edge-memory.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Edit a material color sheet, layer symbolic incisions and diffusion events, and export your evolving artwork.",
  },
  // Chainmail Topology Desk. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/chainmail-topology-desk.
  {
    slug: "chainmail-topology-desk",
    surface: "app",
    title: "Chainmail Topology Desk",
    upstream: "https://chainmail-topology-desk.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/chainmail-topology-desk.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Design decorative ring graphs with oriented rings, symbolic links, adjacency diagrams, and color-coded assembly sequences.",
  },
  // Camera Skin Baker. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/camera-skin-baker.
  {
    slug: "camera-skin-baker",
    surface: "app",
    title: "Camera Skin Baker",
    upstream: "https://camera-skin-baker.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/camera-skin-baker.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Bake local photos onto a triangle mesh using calibrated cameras, with a texture atlas, camera provenance, and conflict masks.",
  },
  // Camouflage Surface Puzzle. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/camouflage-surface-puzzle.
  {
    slug: "camouflage-surface-puzzle",
    surface: "app",
    title: "Camouflage Surface Puzzle",
    upstream: "https://camouflage-surface-puzzle.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/camouflage-surface-puzzle.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Paint a pixel background, project it onto a cuboid, and move the camera to reveal its unpainted surfaces.",
  },
  // Camera Path Steadybench. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/camera-path-steadybench.
  {
    slug: "camera-path-steadybench",
    surface: "app",
    title: "Camera Path Steadybench",
    upstream: "https://camera-path-steadybench.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/camera-path-steadybench.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Stabilize a local PNG frame sequence while preserving a deliberate camera pan using a selected background region.",
  },
  // Breathing Room Score. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/breathing-room-score.
  {
    slug: "breathing-room-score",
    surface: "app",
    title: "Breathing Room Score",
    upstream: "https://breathing-room-score.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/breathing-room-score.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Compose four parts across sixteen measures with entry windows, phrase limits, recovery gaps, and ensemble coverage.",
  },
  // Breakpoint Lexicon. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/breakpoint-lexicon.
  {
    slug: "breakpoint-lexicon",
    surface: "app",
    title: "Breakpoint Lexicon",
    upstream: "https://breakpoint-lexicon.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/breakpoint-lexicon.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Compose paragraphs with custom allowed and forbidden word boundaries, inspect line breaks, and export HTML and a private dictionary.",
  },
  // Branch Section Story. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/branch-section-story.
  {
    slug: "branch-section-story",
    surface: "app",
    title: "Branch Section Story",
    upstream: "https://branch-section-story.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/branch-section-story.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Sculpt branching tubes, assign colored channel routes, and explore horizontal cuts with exportable geometry and cross-sections.",
  },
  // Branching Coral Alphabet. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/branching-coral-alphabet.
  {
    slug: "branching-coral-alphabet",
    surface: "app",
    title: "Branching Coral Alphabet",
    upstream: "https://branching-coral-alphabet.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/branching-coral-alphabet.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Build branching sculptures from typed substitution grammars, editable junction modules, and explicit terminal rules.",
  },
  // Book Spine Fringe. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/book-spine-fringe.
  {
    slug: "book-spine-fringe",
    surface: "app",
    title: "Book Spine Fringe",
    upstream: "https://book-spine-fringe.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/book-spine-fringe.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Edit page-group silhouettes and explore their combined shape in a closed book stack.",
  },
  // Book Hinge Panorama. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/book-hinge-panorama.
  {
    slug: "book-hinge-panorama",
    surface: "app",
    title: "Book Hinge Panorama",
    upstream: "https://book-hinge-panorama.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/book-hinge-panorama.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Create accordion artwork with view compensation for a chosen perspective.",
  },
  // Biomorphic Silhouette Evolution. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/biomorphic-silhouette-evolution.
  {
    slug: "biomorphic-silhouette-evolution",
    surface: "app",
    title: "Biomorphic Silhouette Evolution",
    upstream: "https://biomorphic-silhouette-evolution.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/biomorphic-silhouette-evolution.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Branch fictional design lineages through vertex moves, outline shifts, and quarter-turns while tracking shared vertices.",
  },
  // Between Frame Workshop. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/between-frame-workshop.
  {
    slug: "between-frame-workshop",
    surface: "app",
    title: "Between Frame Workshop",
    upstream: "https://between-frame-workshop.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/between-frame-workshop.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Create intermediate frames with bounded bidirectional interpolation in a local workspace.",
  },
  // Bent Grain Wooddream. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/bent-grain-wooddream.
  {
    slug: "bent-grain-wooddream",
    surface: "app",
    title: "Bent Grain Wooddream",
    upstream: "https://bent-grain-wooddream.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/bent-grain-wooddream.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Author synthetic growth volumes and explore shared surface cuts.",
  },
  // Beating Interval Loom. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/beating-interval-loom.
  {
    slug: "beating-interval-loom",
    surface: "app",
    title: "Beating Interval Loom",
    upstream: "https://beating-interval-loom.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/beating-interval-loom.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Shape a beat-frequency envelope, listen to two continuous sine carriers, and export their interference as a stereo WAV.",
  },
  // Beadloom Warp Detour. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/beadloom-warp-detour.
  {
    slug: "beadloom-warp-detour",
    surface: "app",
    title: "Beadloom Warp Detour",
    upstream: "https://beadloom-warp-detour.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/beadloom-warp-detour.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Design openwork bead patterns with warp runs, bridges, and traced weft routes, then inspect gaps and derive detours.",
  },
  // Beaded Polyhedron Workshop. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/beaded-polyhedron-workshop.
  {
    slug: "beaded-polyhedron-workshop",
    surface: "app",
    title: "Beaded Polyhedron Workshop",
    upstream: "https://beaded-polyhedron-workshop.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/beaded-polyhedron-workshop.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Design beaded polyhedra with editable face rings, shared joins, colors, subdivisions, and assembly rounds.",
  },
  // Batik Crackle Cartographer. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/batik-crackle-cartographer.
  {
    slug: "batik-crackle-cartographer",
    surface: "app",
    title: "Batik Crackle Cartographer",
    upstream: "https://batik-crackle-cartographer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/batik-crackle-cartographer.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Create batik-inspired artwork with editable resist masks, seeded crack patterns, and staged dye layers.",
  },
  // Balloon Loop Ledger. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/balloon-loop-ledger.
  {
    slug: "balloon-loop-ledger",
    surface: "app",
    title: "Balloon Loop Ledger",
    upstream: "https://balloon-loop-ledger.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/balloon-loop-ledger.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Author symbolic balloon creatures with finite tube intervals and reversible pinch locks.",
  },
  // Autostereogram Patchworks. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/autostereogram-patchworks.
  {
    slug: "autostereogram-patchworks",
    surface: "app",
    title: "Autostereogram Patchworks",
    upstream: "https://autostereogram-patchworks.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/autostereogram-patchworks.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Paint a shallow depth drawing and a color texture, then explore the resulting single-image stereogram.",
  },
  // Atlas Bleed Architect. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/atlas-bleed-architect.
  {
    slug: "atlas-bleed-architect",
    surface: "app",
    title: "Atlas Bleed Architect",
    upstream: "https://atlas-bleed-architect.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/atlas-bleed-architect.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Pack texture atlases with per-edge extrusion and inspect how bilinear sampling behaves at their boundaries.",
  },
  // Assembly View Occlusion Check. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/assembly-view-occlusion-check.
  {
    slug: "assembly-view-occlusion-check",
    surface: "app",
    title: "Assembly View Occlusion Check",
    upstream: "https://assembly-view-occlusion-check.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/assembly-view-occlusion-check.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Author cuboid assemblies, contact patches, and assembly steps, then compare camera views to find the most visible target area.",
  },
  // Acoustic Tube Menagerie. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/acoustic-tube-menagerie.
  {
    slug: "acoustic-tube-menagerie",
    surface: "app",
    title: "Acoustic Tube Menagerie",
    upstream: "https://acoustic-tube-menagerie.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/acoustic-tube-menagerie.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Shape a twelve-section acoustic tube and explore synthetic sounds driven by pulses and seeded noise.",
  },
  // Assembly Comic Director. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/assembly-comic-director.
  {
    slug: "assembly-comic-director",
    surface: "app",
    title: "Assembly Comic Director",
    upstream: "https://assembly-comic-director.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/assembly-comic-director.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Create craft-kit picture instructions with SVG parts, ordered steps, dependencies, exploded views, and carry-over highlights.",
  },
  // Watershed of a Sculpture. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/watershed-of-a-sculpture.
  {
    slug: "watershed-of-a-sculpture",
    surface: "app",
    title: "Watershed of a Sculpture",
    upstream: "https://watershed-of-a-sculpture.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/watershed-of-a-sculpture.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Paint scalar fields on a sculpture, shape watershed basins, and export colored meshes, atlases, and basin graphs.",
  },
  // Witness Without a Face. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/witness-without-a-face.
  {
    slug: "witness-without-a-face",
    surface: "app",
    title: "Witness Without a Face",
    upstream: "https://witness-without-a-face.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/witness-without-a-face.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Author fictional mysteries through constraints on what witnesses can see and hear.",
  },
  // Volume Pixel Painting. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/volume-pixel-painting.
  {
    slug: "volume-pixel-painting",
    surface: "app",
    title: "Volume Pixel Painting",
    upstream: "https://volume-pixel-painting.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/volume-pixel-painting.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Paint voxels through coupled orthogonal views with lock masks and a synchronized slice atlas.",
  },
  // Void Carver. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/void-carver.
  {
    slug: "void-carver",
    surface: "app",
    title: "Void Carver",
    upstream: "https://void-carver.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/void-carver.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Carve a centered block with rounded circular sweeps and explore three cross-sections alongside a 3D boundary mesh.",
  },
  // Visual Palindrome Press. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/visual-palindrome-press.
  {
    slug: "visual-palindrome-press",
    surface: "app",
    title: "Visual Palindrome Press",
    upstream: "https://visual-palindrome-press.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/visual-palindrome-press.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Compose polygons that reverse in order with per-attribute constraints and explicit exceptions.",
  },
  // Viewbox Sculpture Mail. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/viewbox-sculpture-mail.
  {
    slug: "viewbox-sculpture-mail",
    surface: "app",
    title: "Viewbox Sculpture Mail",
    upstream: "https://viewbox-sculpture-mail.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/viewbox-sculpture-mail.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Create a tiny interior artwork with three distinct window views in a local-first art studio.",
  },
  // View Fragment Museum. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/view-fragment-museum.
  {
    slug: "view-fragment-museum",
    surface: "app",
    title: "View Fragment Museum",
    upstream: "https://view-fragment-museum.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/view-fragment-museum.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Explore six abstract cuboid sculptures through rectangular apertures in enclosed housings.",
  },
  // Vanishing Point Switchyard. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/vanishing-point-switchyard.
  {
    slug: "vanishing-point-switchyard",
    surface: "app",
    title: "Vanishing Point Switchyard",
    upstream: "https://vanishing-point-switchyard.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/vanishing-point-switchyard.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Compose hybrid-perspective illustrations by aligning two perspective groups and an orthographic group to shared screen anchors.",
  },
  // Unselected Choices Cemetery. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/unselected-choices-cemetery.
  {
    slug: "unselected-choices-cemetery",
    surface: "app",
    title: "Unselected Choices Cemetery",
    upstream: "https://unselected-choices-cemetery.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/unselected-choices-cemetery.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Explore ten decisions in The Island of Almost, where unchosen options trigger offstage events that reshape the story.",
  },
  // Value Camouflage. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/value-camouflage.
  {
    slug: "value-camouflage",
    surface: "app",
    title: "Value Camouflage",
    upstream: "https://value-camouflage.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/value-camouflage.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Paint a 24×24 mask and explore palettes with matching luminance but visibly different colors.",
  },
  // Unsent Sound Conversation. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/unsent-sound-conversation.
  {
    slug: "unsent-sound-conversation",
    surface: "app",
    title: "Unsent Sound Conversation",
    upstream: "https://unsent-sound-conversation.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/unsent-sound-conversation.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Compose a dialogue between expression and withholding by marking public and private audio ranges and adjusting timing and gain.",
  },
  // Unreliable Page Count. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/unreliable-page-count.
  {
    slug: "unreliable-page-count",
    surface: "app",
    title: "Unreliable Page Count",
    upstream: "https://unreliable-page-count.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/unreliable-page-count.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Shape a reading edition from fifteen original passages, edit its source, save locally, and export the reading.",
  },
  // Unison Splitting Lens. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/unison-splitting-lens.
  {
    slug: "unison-splitting-lens",
    surface: "app",
    title: "Unison Splitting Lens",
    upstream: "https://unison-splitting-lens.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/unison-splitting-lens.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Explore four musical parts sharing a sixteen-event stream, with editable rules for departing from and rejoining unison.",
  },
  // Type Token Loom. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/type-token-loom.
  {
    slug: "type-token-loom",
    surface: "app",
    title: "Type Token Loom",
    upstream: "https://type-token-loom.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/type-token-loom.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Build a typographic measurement system with twelve editable, unit-aware tokens and see changes flow through their dependencies.",
  },
  // Transparent Inventory. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/transparent-inventory.
  {
    slug: "transparent-inventory",
    surface: "app",
    title: "Transparent Inventory",
    upstream: "https://transparent-inventory.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/transparent-inventory.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Paint and layer translucent pixel scraps, protect their features, and inspect how each layer affects visibility.",
  },
  // Tunnel Depth Director. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/tunnel-depth-director.
  {
    slug: "tunnel-depth-director",
    surface: "app",
    title: "Tunnel Depth Director",
    upstream: "https://tunnel-depth-director.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/tunnel-depth-director.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Author a five-plane paper scene, inspect visibility from a single viewpoint, and export SVG cut sheets.",
  },
  // Transparency Turnover. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/transparency-turnover.
  {
    slug: "transparency-turnover",
    surface: "app",
    title: "Transparency Turnover",
    upstream: "https://transparency-turnover.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/transparency-turnover.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Design artwork on four clear sheets, edit each face, reorder layers, and preview front and back views with guides.",
  },
  // Torus Route Weaver. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/torus-route-weaver.
  {
    slug: "torus-route-weaver",
    surface: "app",
    title: "Torus Route Weaver",
    upstream: "https://torus-route-weaver.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/torus-route-weaver.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Weave up to six winding routes around a torus with editable radii, offsets, widths, and colors.",
  },
  // Tonal Checkpoint Painter. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/tonal-checkpoint-painter.
  {
    slug: "tonal-checkpoint-painter",
    surface: "app",
    title: "Tonal Checkpoint Painter",
    upstream: "https://tonal-checkpoint-painter.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/tonal-checkpoint-painter.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Develop a painting from a coarse value plan into fine detail while preserving explicitly locked tonal checkpoints.",
  },
  // Tonal Tugboat. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/tonal-tugboat.
  {
    slug: "tonal-tugboat",
    surface: "app",
    title: "Tonal Tugboat",
    upstream: "https://tonal-tugboat.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/tonal-tugboat.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Drag lightness targets onto image regions while unpinned regions compensate to preserve the original average luminance.",
  },
  // Three View Paradox Maker. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/three-view-paradox-maker.
  {
    slug: "three-view-paradox-maker",
    surface: "app",
    title: "Three View Paradox Maker",
    upstream: "https://three-view-paradox-maker.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/three-view-paradox-maker.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Draw three orthogonal silhouettes, intersect their volumes into a voxel sculpture, and see which target pixels remain unsupported.",
  },
  // Thin Slice Reconstruction. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/thin-slice-reconstruction.
  {
    slug: "thin-slice-reconstruction",
    surface: "app",
    title: "Thin Slice Reconstruction",
    upstream: "https://thin-slice-reconstruction.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/thin-slice-reconstruction.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Draw radial cross-sections of an imagined object, mark missing intervals, and compare linear, eased, and bulged reconstructions.",
  },
  // Texture Topography Stage. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/texture-topography-stage.
  {
    slug: "texture-topography-stage",
    surface: "app",
    title: "Texture Topography Stage",
    upstream: "https://texture-topography-stage.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/texture-topography-stage.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Paint an 8×8 relief field, edit region functions and borders, inspect the sculpture in 3D, and export OBJ and grayscale PNG files.",
  },
  // Texture Repeat Seam Desk. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/texture-repeat-seam-desk.
  {
    slug: "texture-repeat-seam-desk",
    surface: "app",
    title: "Texture Repeat Seam Desk",
    upstream: "https://texture-repeat-seam-desk.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/texture-repeat-seam-desk.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Repair repeating raster textures with pixel corrections, protection masks, wrapped-edge editing, and undo.",
  },
  // Texture Custody. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/texture-custody.
  {
    slug: "texture-custody",
    surface: "app",
    title: "Texture Custody",
    upstream: "https://texture-custody.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/texture-custody.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Paint a pixel texture, choose source patches and destination rectangles, and draw arrows to control texture direction and scale.",
  },
  // Tethered Fill. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/tethered-fill.
  {
    slug: "tethered-fill",
    surface: "app",
    title: "Tethered Fill",
    upstream: "https://tethered-fill.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/tethered-fill.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Create color-routing compositions in a local workspace.",
  },
  // Tension-Only Marionette. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/tension-only-marionette.
  {
    slug: "tension-only-marionette",
    surface: "app",
    title: "Tension-Only Marionette",
    upstream: "https://tension-only-marionette.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/tension-only-marionette.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Move a two-anchor control bar to animate four points linked by three pull-only strings and see when pulling corrections occur.",
  },
  // Tense Border Crossing. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/tense-border-crossing.
  {
    slug: "tense-border-crossing",
    surface: "app",
    title: "Tense Border Crossing",
    upstream: "https://tense-border-crossing.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/tense-border-crossing.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Compose prose from twelve events that shift between past, present, and possibility, with prerequisites shaping their consequences.",
  },
  // Tatting Route Sketch. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/tatting-route-sketch.
  {
    slug: "tatting-route-sketch",
    surface: "app",
    title: "Tatting Route Sketch",
    upstream: "https://tatting-route-sketch.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/tatting-route-sketch.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Sketch symbolic lace constructions with rings, chains, named points, shuttle ordering, and joins to earlier rings.",
  },
  // Tangent Companions. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/tangent-companions.
  {
    slug: "tangent-companions",
    surface: "app",
    title: "Tangent Companions",
    upstream: "https://tangent-companions.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/tangent-companions.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Grow cubic flourishes along curved parent paths with editable roots and tangent-aligned handles.",
  },
  // Symbol Weatherfront. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/symbol-weatherfront.
  {
    slug: "symbol-weatherfront",
    surface: "app",
    title: "Symbol Weatherfront",
    upstream: "https://symbol-weatherfront.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/symbol-weatherfront.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Create polyline symbol vocabularies and morph them across a spatial field using explicit correspondences and local exceptions.",
  },
  // Tab Edge Atlas. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/tab-edge-atlas.
  {
    slug: "tab-edge-atlas",
    surface: "app",
    title: "Tab Edge Atlas",
    upstream: "https://tab-edge-atlas.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/tab-edge-atlas.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Design printed section-edge navigation in a browser-based studio.",
  },
  // Surface Window Portals. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/surface-window-portals.
  {
    slug: "surface-window-portals",
    surface: "app",
    title: "Surface Window Portals",
    upstream: "https://surface-window-portals.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/surface-window-portals.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Paint aperture masks on a cube shell to reveal three editable scenes with independent cameras and scales.",
  },
  // Surtitle Reveal Guard. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/surtitle-reveal-guard.
  {
    slug: "surtitle-reveal-guard",
    surface: "app",
    title: "Surtitle Reveal Guard",
    upstream: "https://surtitle-reveal-guard.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/surtitle-reveal-guard.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Rehearse surtitle reveals by checking marked facts against uncertain delivery timing in your browser.",
  },
  // Surface Cut Comic. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/surface-cut-comic.
  {
    slug: "surface-cut-comic",
    surface: "app",
    title: "Surface Cut Comic",
    upstream: "https://surface-cut-comic.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/surface-cut-comic.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Paint narrative vignettes on planes and move a clipping boundary through a volume to explore which scenes emerge.",
  },
  // Subtractive Avatar Passport. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/subtractive-avatar-passport.
  {
    slug: "subtractive-avatar-passport",
    surface: "app",
    title: "Subtractive Avatar Passport",
    upstream: "https://subtractive-avatar-passport.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/subtractive-avatar-passport.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Build a sculptural avatar from deliberate additions and cuts, preserving a replayable record of your creative choices.",
  },
  // Stroke Latency Bench. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/stroke-latency-bench.
  {
    slug: "stroke-latency-bench",
    surface: "app",
    title: "Stroke Latency Bench",
    upstream: "https://stroke-latency-bench.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/stroke-latency-bench.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Record a drawing gesture and compare three stroke followers to explore smoothing, lag, and the samples behind each path.",
  },
  // Stroke Weights. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/stroke-weights.
  {
    slug: "stroke-weights",
    surface: "app",
    title: "Stroke Weights",
    upstream: "https://stroke-weights.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/stroke-weights.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Redistribute a fixed amount of stroke thickness along a drawing while keeping its centerline unchanged.",
  },
  // Stroke Economy Coach. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/stroke-economy-coach.
  {
    slug: "stroke-economy-coach",
    surface: "app",
    title: "Stroke Economy Coach",
    upstream: "https://stroke-economy-coach.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/stroke-economy-coach.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Refine a finished drawing into fewer continuous gestures while preserving the landmarks you choose.",
  },
  // String Path Portrait. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/string-path-portrait.
  {
    slug: "string-path-portrait",
    surface: "app",
    title: "String Path Portrait",
    upstream: "https://string-path-portrait.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/string-path-portrait.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Paint or import a tonal target and compose circular-pin string art with adjustable pins, density, and thread limits.",
  },
  // Story Shared Blind Spot. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/story-shared-blind-spot.
  {
    slug: "story-shared-blind-spot",
    surface: "app",
    title: "Story Shared Blind Spot",
    upstream: "https://story-shared-blind-spot.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/story-shared-blind-spot.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Follow connections among eight accounts to uncover an unnamed central concept, then declare your answer or reveal the solution.",
  },
  // Story Safe Combination. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/story-safe-combination.
  {
    slug: "story-safe-combination",
    surface: "app",
    title: "Story Safe Combination",
    upstream: "https://story-safe-combination.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/story-safe-combination.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Solve a fictional story puzzle by making six decisions that shape which of four vows remain true.",
  },
  // Story Puzzle With No Questions. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/story-puzzle-with-no-questions.
  {
    slug: "story-puzzle-with-no-questions",
    surface: "app",
    title: "Story Puzzle With No Questions",
    upstream: "https://story-puzzle-with-no-questions.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/story-puzzle-with-no-questions.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Change eight household facts in The House That Waits and use the story's responses to infer its hidden objective.",
  },
  // Story Permutation Box. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/story-permutation-box.
  {
    slug: "story-permutation-box",
    surface: "app",
    title: "Story Permutation Box",
    upstream: "https://story-permutation-box.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/story-permutation-box.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Reorder seven story scenes, explore all 5,040 sequences, and write repair passages with explicit prerequisites and effects.",
  },
  // Story Credit Expiration. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/story-credit-expiration.
  {
    slug: "story-credit-expiration",
    surface: "app",
    title: "Story Credit Expiration",
    upstream: "https://story-credit-expiration.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/story-credit-expiration.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Carry a letter through an obsolete tidal post office and weigh a brass clerk's predictions against the outcomes you witness.",
  },
  // Stipple Route Composer. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/stipple-route-composer.
  {
    slug: "stipple-route-composer",
    surface: "app",
    title: "Stipple Route Composer",
    upstream: "https://stipple-route-composer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/stipple-route-composer.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Paint a density field, arrange checkpoints, and reveal a fixed set of stipple dots along a route you compose.",
  },
  // Stencil Bridge Rescue. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/stencil-bridge-rescue.
  {
    slug: "stencil-bridge-rescue",
    surface: "app",
    title: "Stencil Bridge Rescue",
    upstream: "https://stencil-bridge-rescue.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/stencil-bridge-rescue.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Add bridges to connect a stencil's material, then export a connected SVG alongside an editable design and bridge list.",
  },
  // Staff Note Lift. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/staff-note-lift.
  {
    slug: "staff-note-lift",
    surface: "app",
    title: "Staff Note Lift",
    upstream: "https://staff-note-lift.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/staff-note-lift.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Transcribe a short handwritten melody by aligning staff curves, marking noteheads, and setting accidentals and rhythm.",
  },
  // Stem Logic Harmonizer. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/stem-logic-harmonizer.
  {
    slug: "stem-logic-harmonizer",
    surface: "app",
    title: "Stem Logic Harmonizer",
    upstream: "https://stem-logic-harmonizer.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/stem-logic-harmonizer.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Create and refine ten original stencil glyphs in a browser-local lettering studio.",
  },
  // Stage Door Palindrome. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/stage-door-palindrome.
  {
    slug: "stage-door-palindrome",
    surface: "app",
    title: "Stage Door Palindrome",
    upstream: "https://stage-door-palindrome.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/stage-door-palindrome.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Rehearse reversible prop transfers among three actors, authoring each cue and its inverse for a physical-theatre ensemble.",
  },
  // Spine Vanishing Band. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/spine-vanishing-band.
  {
    slug: "spine-vanishing-band",
    surface: "app",
    title: "Spine Vanishing Band",
    upstream: "https://spine-vanishing-band.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/spine-vanishing-band.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Design a two-page SVG spread with paired landmarks and a hidden spine band that transforms and clips your artwork.",
  },
  // Spherical Seam Studio. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/spherical-seam-studio.
  {
    slug: "spherical-seam-studio",
    surface: "app",
    title: "Spherical Seam Studio",
    upstream: "https://spherical-seam-studio.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/spherical-seam-studio.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Draw north-south seams on a sphere, choose regional projections, inspect distortion, and ornament a reassembled globe.",
  },
  // Spherical Photo Fragments. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/spherical-photo-fragments.
  {
    slug: "spherical-photo-fragments",
    surface: "app",
    title: "Spherical Photo Fragments",
    upstream: "https://spherical-photo-fragments.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/spherical-photo-fragments.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Arrange your PNG images in angular windows on a fictional panorama sphere, compose inside and outside views, and export an atlas.",
  },
  // Spectrum Postcard Press. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/spectrum-postcard-press.
  {
    slug: "spectrum-postcard-press",
    surface: "app",
    title: "Spectrum Postcard Press",
    upstream: "https://spectrum-postcard-press.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/spectrum-postcard-press.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Draw a grayscale sound spectrum, reserve quiet frequency bands, and reconstruct audio from an editable phase seed.",
  },
  // Spatial Palindrome. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/spatial-palindrome.
  {
    slug: "spatial-palindrome",
    surface: "app",
    title: "Spatial Palindrome",
    upstream: "https://spatial-palindrome.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/spatial-palindrome.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Compose paired box scenes along an outward-and-return path with separate geometry, matched cameras, and editable transforms.",
  },
  // Spatial Lacework. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/spatial-lacework.
  {
    slug: "spatial-lacework",
    surface: "app",
    title: "Spatial Lacework",
    upstream: "https://spatial-lacework.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/spatial-lacework.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Design open-cage ornaments in an interactive 3D studio.",
  },
  // Spatial Erasure Walk. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/spatial-erasure-walk.
  {
    slug: "spatial-erasure-walk",
    surface: "app",
    title: "Spatial Erasure Walk",
    upstream: "https://spatial-erasure-walk.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/spatial-erasure-walk.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Walk around a concave shell and paint away its nearest visible outward-facing texture cells in an on-device 3D studio.",
  },
  // Soundscape With One Truth. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/soundscape-with-one-truth.
  {
    slug: "soundscape-with-one-truth",
    surface: "app",
    title: "Soundscape With One Truth",
    upstream: "https://soundscape-with-one-truth.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/soundscape-with-one-truth.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Solve or author a fictional sound-deduction game with six sine-pulse events and explicit rules for the scene.",
  },
  // Sound Threshold Crossing. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sound-threshold-crossing.
  {
    slug: "sound-threshold-crossing",
    surface: "app",
    title: "Sound Threshold Crossing",
    upstream: "https://sound-threshold-crossing.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sound-threshold-crossing.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Shape a synthetic sound event by holding, crossing a boundary, and releasing with pointer or numeric controls.",
  },
  // Sound Signature Constellation. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sound-signature-constellation.
  {
    slug: "sound-signature-constellation",
    surface: "app",
    title: "Sound Signature Constellation",
    upstream: "https://sound-signature-constellation.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sound-signature-constellation.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Author eight procedural audio events with shared acoustic constraints and compare their measured pairwise differences.",
  },
  // Sound That Has to Be Earned. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sound-that-has-to-be-earned.
  {
    slug: "sound-that-has-to-be-earned",
    surface: "app",
    title: "Sound That Has to Be Earned",
    upstream: "https://sound-that-has-to-be-earned.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sound-that-has-to-be-earned.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Reveal a six-layer listening sculpture by arranging timing relationships, with numeric timelines and an accessibility bypass.",
  },
  // Sound Seam Hide and Seek. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sound-seam-hide-and-seek.
  {
    slug: "sound-seam-hide-and-seek",
    surface: "app",
    title: "Sound Seam Hide and Seek",
    upstream: "https://sound-seam-hide-and-seek.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sound-seam-hide-and-seek.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Edit a mono clip, pass it to another player to guess the seams by ear, then reveal the edit log.",
  },
  // Sound Posture Deck. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sound-posture-deck.
  {
    slug: "sound-posture-deck",
    surface: "app",
    title: "Sound Posture Deck",
    upstream: "https://sound-posture-deck.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sound-posture-deck.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Perform the same sound fragment with gesture curves for attack hesitation, release persistence, and response readiness.",
  },
  // Sound Ransom Note. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sound-ransom-note.
  {
    slug: "sound-ransom-note",
    surface: "app",
    title: "Sound Ransom Note",
    upstream: "https://sound-ransom-note.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sound-ransom-note.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Compose a wordless audio postcard from synthetic sound fragments, then reveal each whole source and its chosen sample range.",
  },
  // Sound Polaroid Delay. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sound-polaroid-delay.
  {
    slug: "sound-polaroid-delay",
    surface: "app",
    title: "Sound Polaroid Delay",
    upstream: "https://sound-polaroid-delay.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sound-polaroid-delay.webp",
    shippedAt: "2026-10-26",
    status: "live",
    blurb: "Shape six progressive envelopes for a mono sound, hear its bands enter, and export a developing portrait, audio postcard, and six stems.",
  },
  // Cron Explain. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/cron-explain.
  {
    slug: "cron-explain",
    surface: "app",
    title: "Cron Explain",
    upstream: "https://cron-explain.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/cron-explain.webp",
    shippedAt: "2026-10-27",
    status: "live",
    blurb: "Type a Unix cron expression to see it in plain English and view the next fire times, entirely in your browser.",
  },
  // Block Blast Rigged. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/blockblast-rigged.
  {
    slug: "blockblast-rigged",
    surface: "app",
    title: "Block Blast Rigged",
    upstream: "https://blockblast-rigged.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/blockblast-rigged.webp",
    shippedAt: "2026-10-28",
    status: "live",
    blurb: "Play a Block Blast-style puzzle that exposes ad-harvest mechanics through a local parody simulation.",
  },
  // GPT-2 Knowledge Book. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/gpt2-kb.
  {
    slug: "gpt2-kb",
    surface: "app",
    title: "GPT-2 Knowledge Book",
    upstream: "https://gpt2-kb.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/gpt2-kb.webp",
    shippedAt: "2026-10-28",
    status: "live",
    blurb: "Follow a knowledge-book walkthrough of reproducing GPT-2 124M.",
  },

  // GPT-2 Knowledge Book Worker. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/gpt2-cf-worker.
  {
    slug: "gpt2-cf-worker",
    surface: "app",
    title: "GPT-2 Knowledge Book Worker",
    upstream: "https://gpt2-cf-worker.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/gpt2-cf-worker.webp",
    shippedAt: "2026-10-28",
    status: "live",
    blurb: "Explore knowledge-book pages that walk through reproducing GPT-2 124M.",
  },
  // Nonlinear Breakdown. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/nonlinear-breakdown.
  {
    slug: "nonlinear-breakdown",
    surface: "app",
    title: "Nonlinear Breakdown",
    upstream: "https://nonlinear-breakdown.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/nonlinear-breakdown.webp",
    shippedAt: "2026-10-28",
    status: "live",
    blurb: "Read a critical essay breaking down Nonlinear.",
  },
  // System One Bench. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/systemonebench-site.
  {
    slug: "systemonebench-site",
    surface: "app",
    title: "System One Bench",
    upstream: "https://systemonebench-site.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/systemonebench-site.webp",
    shippedAt: "2026-10-28",
    status: "live",
    blurb: "Explore the System One Bench leaderboard and the methodology behind its results.",
  },
  // Rocks. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/rocks.
  {
    slug: "rocks",
    surface: "app",
    title: "Rocks",
    upstream: "https://rocks.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/rocks.webp",
    shippedAt: "2026-10-28",
    status: "live",
    blurb: "Explore a quiet gallery of ten rock studies, from alpine granite to cave minerals.",
  },
  // Cheng Raid School. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/cheng-raid-school.
  {
    slug: "cheng-raid-school",
    surface: "app",
    title: "Cheng Raid School",
    upstream: "https://cheng-raid-school.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/cheng-raid-school.webp",
    shippedAt: "2026-10-28",
    status: "live",
    blurb: "Study classic WoW raid design through field-manual plates distilled from Wyatt Cheng boss-design shorts.",
  },
  // Cheng Raid School Naxx Rest. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/cheng-raid-school-naxx-rest.
  {
    slug: "cheng-raid-school-naxx-rest",
    surface: "app",
    title: "Cheng Raid School Naxx Rest",
    upstream: "https://cheng-raid-school-naxx-rest.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/cheng-raid-school-naxx-rest.webp",
    shippedAt: "2026-10-28",
    status: "live",
    blurb: "Explore Grobbulus, Gothik, and the Four Horsemen through a field guide built from their raid mechanics.",
  },
  // WoW Boss Design Breakdowns. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/wow-boss-design-breakdowns.
  {
    slug: "wow-boss-design-breakdowns",
    surface: "app",
    title: "WoW Boss Design Breakdowns",
    upstream: "https://wow-boss-design-breakdowns.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/wow-boss-design-breakdowns.webp",
    shippedAt: "2026-10-28",
    status: "live",
    blurb: "Explore the design of real WoW raid bosses through reverse-engineered lesson plates.",
  },
  // Iron Isles. Overflow Worker on over9000-pool-000; plain static site (no build step upstream), no auth, storage none (localStorage). Repo over9000-apps/iron-isles, copied from kdy618/iron-isles.
  {
    slug: "iron-isles",
    surface: "app",
    title: "Iron Isles",
    upstream: "https://iron-isles.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/iron-isles.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "A workout game for home or gym: pick an island and a coach, then run bodyweight, kettlebell and medicine ball quests for XP, streaks and badges.",
  },
  // One Step Orchard. Overflow Worker on over9000-pool-000; static build, no auth, storage none. Repo over9000-apps/game-0001-one-step-orchard.
  {
    slug: "game-0001-one-step-orchard",
    surface: "app",
    title: "One Step Orchard",
    upstream: "https://game-0001-one-step-orchard.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0001-one-step-orchard.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Slide tiles one space at a time, combine 1 and 2 into 3, then merge equal numbers to reach 192.",
  },
  // Orb Conservatory. Overflow Worker on over9000-pool-000; static build, no auth, storage none. Repo over9000-apps/game-0002-orb-conservatory.
  {
    slug: "game-0002-orb-conservatory",
    surface: "app",
    title: "Orb Conservatory",
    upstream: "https://game-0002-orb-conservatory.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0002-orb-conservatory.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Drop fruit into a container and merge matching pairs into larger fruit while keeping the pile from overflowing.",
  },
  // Seedspire. Overflow Worker on over9000-pool-000; static build, no auth, storage none. Repo over9000-apps/game-0003-seedspire.
  {
    slug: "game-0003-seedspire",
    surface: "app",
    title: "Seedspire",
    upstream: "https://game-0003-seedspire.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0003-seedspire.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Place seeds and combine three adjacent matching objects into higher tiers while keeping the board open.",
  },
  // Tile Atelier. Overflow Worker on over9000-pool-000; static build, no auth, storage none. Repo over9000-apps/game-0004-tile-atelier.
  {
    slug: "game-0004-tile-atelier",
    surface: "app",
    title: "Tile Atelier",
    upstream: "https://game-0004-tile-atelier.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0004-tile-atelier.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Place shapes on an 8-by-8 board and complete rows or columns to clear space and build your score.",
  },
  // Prism Sweep. Overflow Worker on over9000-pool-000; static build, no auth, storage none. Repo over9000-apps/game-0005-prism-sweep.
  {
    slug: "game-0005-prism-sweep",
    surface: "app",
    title: "Prism Sweep",
    upstream: "https://game-0005-prism-sweep.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0005-prism-sweep.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Rotate and drop two-color blocks to form matching squares, then time their clearing with a sweeping beam.",
  },
  // Valley Standard. Overflow Worker on over9000-pool-000; static build, no auth, storage none. Repo over9000-apps/game-0006-valley-standard.
  {
    slug: "game-0006-valley-standard",
    surface: "app",
    title: "Valley Standard",
    upstream: "https://game-0006-valley-standard.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0006-valley-standard.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Place terrain and buildings, gather resources and troops, and improve your deck to win four encounters.",
  },
  // Tidebrick Beacon. Overflow Worker on over9000-pool-000; static build, no auth, storage none. Repo over9000-apps/game-0007-tidebrick-beacon.
  {
    slug: "game-0007-tidebrick-beacon",
    surface: "app",
    title: "Tidebrick Beacon",
    upstream: "https://game-0007-tidebrick-beacon.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0007-tidebrick-beacon.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Stack falling bricks into a stable tower and use anchor spells to keep narrow overlaps from toppling.",
  },
  // Ninefold Mosaic. Overflow Worker on over9000-pool-000; static build, no auth, storage none. Repo over9000-apps/game-0008-ninefold-mosaic.
  {
    slug: "game-0008-ninefold-mosaic",
    surface: "app",
    title: "Ninefold Mosaic",
    upstream: "https://game-0008-ninefold-mosaic.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0008-ninefold-mosaic.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Place shapes and complete rows, columns, or 3-by-3 regions to clear room for more pieces.",
  },
  // Dune Ribbons. Overflow Worker on over9000-pool-000; static build, no auth, storage none. Repo over9000-apps/game-0009-dune-ribbons.
  {
    slug: "game-0009-dune-ribbons",
    surface: "app",
    title: "Dune Ribbons",
    upstream: "https://game-0009-dune-ribbons.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0009-dune-ribbons.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Drop blocks that crumble into sand, then connect one color from wall to wall to clear it.",
  },
  // Gem Cascade. Overflow Worker on over9000-pool-000; static build, no auth, storage none. Repo over9000-apps/game-0010-gem-cascade.
  {
    slug: "game-0010-gem-cascade",
    surface: "app",
    title: "Gem Cascade",
    upstream: "https://game-0010-gem-cascade.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0010-gem-cascade.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Arrange falling pieces to clear full lines or groups of four matching gems and trigger chain reactions.",
  },
  // Prism Pantry. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0011-prism-pantry.
  {
    slug: "game-0011-prism-pantry",
    surface: "app",
    title: "Prism Pantry",
    upstream: "https://game-0011-prism-pantry.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0011-prism-pantry.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Prism Pantry, a small original browser game.",
  },
  // Fizzy Harbour. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0012-fizzy-harbour.
  {
    slug: "game-0012-fizzy-harbour",
    surface: "app",
    title: "Fizzy Harbour",
    upstream: "https://game-0012-fizzy-harbour.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0012-fizzy-harbour.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Fizzy Harbour, a small original browser game.",
  },
  // Companion Comets. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0013-companion-comets.
  {
    slug: "game-0013-companion-comets",
    surface: "app",
    title: "Companion Comets",
    upstream: "https://game-0013-companion-comets.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0013-companion-comets.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Companion Comets, a small original browser game.",
  },
  // Velvet Current. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0014-velvet-current.
  {
    slug: "game-0014-velvet-current",
    surface: "app",
    title: "Velvet Current",
    upstream: "https://game-0014-velvet-current.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0014-velvet-current.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Velvet Current, a small original browser game.",
  },
  // Harvest Ledger. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0015-harvest-ledger.
  {
    slug: "game-0015-harvest-ledger",
    surface: "app",
    title: "Harvest Ledger",
    upstream: "https://game-0015-harvest-ledger.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0015-harvest-ledger.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Harvest Ledger, a small original browser game.",
  },
  // Lantern Launcher. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0016-lantern-launcher.
  {
    slug: "game-0016-lantern-launcher",
    surface: "app",
    title: "Lantern Launcher",
    upstream: "https://game-0016-lantern-launcher.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0016-lantern-launcher.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Lantern Launcher, a small original browser game.",
  },
  // Canopy Couriers. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0017-canopy-couriers.
  {
    slug: "game-0017-canopy-couriers",
    surface: "app",
    title: "Canopy Couriers",
    upstream: "https://game-0017-canopy-couriers.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0017-canopy-couriers.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Canopy Couriers, a small original browser game.",
  },
  // Loop Letters. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0018-loop-letters.
  {
    slug: "game-0018-loop-letters",
    surface: "app",
    title: "Loop Letters",
    upstream: "https://game-0018-loop-letters.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0018-loop-letters.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Loop Letters, a small original browser game.",
  },
  // Sprout Sanctuary. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0019-sprout-sanctuary.
  {
    slug: "game-0019-sprout-sanctuary",
    surface: "app",
    title: "Sprout Sanctuary",
    upstream: "https://game-0019-sprout-sanctuary.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0019-sprout-sanctuary.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Sprout Sanctuary, a small original browser game.",
  },
  // Mistbound Orchard. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0020-mistbound-orchard.
  {
    slug: "game-0020-mistbound-orchard",
    surface: "app",
    title: "Mistbound Orchard",
    upstream: "https://game-0020-mistbound-orchard.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0020-mistbound-orchard.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Mistbound Orchard, a small original browser game.",
  },
  // Pocket Foundry. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0021-pocket-foundry.
  {
    slug: "game-0021-pocket-foundry",
    surface: "app",
    title: "Pocket Foundry",
    upstream: "https://game-0021-pocket-foundry.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0021-pocket-foundry.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Pocket Foundry, a small original browser game.",
  },
  // Colour Quilt. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0022-colour-quilt.
  {
    slug: "game-0022-colour-quilt",
    surface: "app",
    title: "Colour Quilt",
    upstream: "https://game-0022-colour-quilt.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0022-colour-quilt.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Colour Quilt, a small original browser game.",
  },
  // Pip Atelier. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0023-pip-atelier.
  {
    slug: "game-0023-pip-atelier",
    surface: "app",
    title: "Pip Atelier",
    upstream: "https://game-0023-pip-atelier.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0023-pip-atelier.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Pip Atelier, a small original browser game.",
  },
  // Rocket Ruckus. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0024-rocket-ruckus.
  {
    slug: "game-0024-rocket-ruckus",
    surface: "app",
    title: "Rocket Ruckus",
    upstream: "https://game-0024-rocket-ruckus.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0024-rocket-ruckus.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Rocket Ruckus, a small original browser game.",
  },
  // Crate Carnival. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0025-crate-carnival.
  {
    slug: "game-0025-crate-carnival",
    surface: "app",
    title: "Crate Carnival",
    upstream: "https://game-0025-crate-carnival.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0025-crate-carnival.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Crate Carnival, a small original browser game.",
  },
  // Moss Mason. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0027-moss-mason.
  {
    slug: "game-0027-moss-mason",
    surface: "app",
    title: "Moss Mason",
    upstream: "https://game-0027-moss-mason.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0027-moss-mason.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Moss Mason, a small original browser game.",
  },
  // Keylight Keep. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0026-keylight-keep.
  {
    slug: "game-0026-keylight-keep",
    surface: "app",
    title: "Keylight Keep",
    upstream: "https://game-0026-keylight-keep.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0026-keylight-keep.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Keylight Keep, a small original browser game.",
  },
  // Woven Rooms. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0028-woven-rooms.
  {
    slug: "game-0028-woven-rooms",
    surface: "app",
    title: "Woven Rooms",
    upstream: "https://game-0028-woven-rooms.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0028-woven-rooms.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Woven Rooms, a small original browser game.",
  },
  // Tideglass Studio. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0029-tideglass-studio.
  {
    slug: "game-0029-tideglass-studio",
    surface: "app",
    title: "Tideglass Studio",
    upstream: "https://game-0029-tideglass-studio.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0029-tideglass-studio.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Tideglass Studio, a small original browser game.",
  },
  // Shardpath Scout. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0030-shardpath-scout.
  {
    slug: "game-0030-shardpath-scout",
    surface: "app",
    title: "Shardpath Scout",
    upstream: "https://game-0030-shardpath-scout.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0030-shardpath-scout.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Shardpath Scout, a small original browser game.",
  },
  // Aurora Reservoir. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0036-aurora-reservoir.
  {
    slug: "game-0036-aurora-reservoir",
    surface: "app",
    title: "Aurora Reservoir",
    upstream: "https://game-0036-aurora-reservoir.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0036-aurora-reservoir.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Aurora Reservoir, a small original browser game.",
  },
  // Velvet Thread. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0035-velvet-thread.
  {
    slug: "game-0035-velvet-thread",
    surface: "app",
    title: "Velvet Thread",
    upstream: "https://game-0035-velvet-thread.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0035-velvet-thread.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Velvet Thread, a small original browser game.",
  },
  // Lantern Launcher. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0034-lantern-launcher.
  {
    slug: "game-0034-lantern-launcher",
    surface: "app",
    title: "Lantern Launcher",
    upstream: "https://game-0034-lantern-launcher.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0034-lantern-launcher.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Lantern Launcher, a small original browser game.",
  },
  // Tide Switch. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0033-tide-switch.
  {
    slug: "game-0033-tide-switch",
    surface: "app",
    title: "Tide Switch",
    upstream: "https://game-0033-tide-switch.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0033-tide-switch.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Tide Switch, a small original browser game.",
  },
  // Prism Descent. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0032-prism-descent.
  {
    slug: "game-0032-prism-descent",
    surface: "app",
    title: "Prism Descent",
    upstream: "https://game-0032-prism-descent.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0032-prism-descent.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Prism Descent, a small original browser game.",
  },
  // Capsule Harbor. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0031-capsule-harbor.
  {
    slug: "game-0031-capsule-harbor",
    surface: "app",
    title: "Capsule Harbor",
    upstream: "https://game-0031-capsule-harbor.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0031-capsule-harbor.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Capsule Harbor, a small original browser game.",
  },
  // Glyph Foundry. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0040-glyph-foundry.
  {
    slug: "game-0040-glyph-foundry",
    surface: "app",
    title: "Glyph Foundry",
    upstream: "https://game-0040-glyph-foundry.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0040-glyph-foundry.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Glyph Foundry, a small original browser game.",
  },
  // Orchard Reserve. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0039-orchard-reserve.
  {
    slug: "game-0039-orchard-reserve",
    surface: "app",
    title: "Orchard Reserve",
    upstream: "https://game-0039-orchard-reserve.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0039-orchard-reserve.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Orchard Reserve, a small original browser game.",
  },
  // Twin Wells. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0038-twin-wells.
  {
    slug: "game-0038-twin-wells",
    surface: "app",
    title: "Twin Wells",
    upstream: "https://game-0038-twin-wells.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0038-twin-wells.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Twin Wells, a small original browser game.",
  },
  // Ember Detour. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0037-ember-detour.
  {
    slug: "game-0037-ember-detour",
    surface: "app",
    title: "Ember Detour",
    upstream: "https://game-0037-ember-detour.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0037-ember-detour.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Ember Detour, a small original browser game.",
  },
  // Jelly Rival. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0041-jelly-rival.
  {
    slug: "game-0041-jelly-rival",
    surface: "app",
    title: "Jelly Rival",
    upstream: "https://game-0041-jelly-rival.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0041-jelly-rival.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Jelly Rival, a small original browser game.",
  },
  // Pawprint Draft. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0042-pawprint-draft.
  {
    slug: "game-0042-pawprint-draft",
    surface: "app",
    title: "Pawprint Draft",
    upstream: "https://game-0042-pawprint-draft.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0042-pawprint-draft.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Pawprint Draft, a small original browser game.",
  },
  // Pulse Mosaic. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0043-pulse-mosaic.
  {
    slug: "game-0043-pulse-mosaic",
    surface: "app",
    title: "Pulse Mosaic",
    upstream: "https://game-0043-pulse-mosaic.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0043-pulse-mosaic.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Pulse Mosaic, a small original browser game.",
  },
  // Mooncake Workshop. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0044-mooncake-workshop.
  {
    slug: "game-0044-mooncake-workshop",
    surface: "app",
    title: "Mooncake Workshop",
    upstream: "https://game-0044-mooncake-workshop.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0044-mooncake-workshop.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Mooncake Workshop, a small original browser game.",
  },
  // Summit Lock. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0045-summit-lock.
  {
    slug: "game-0045-summit-lock",
    surface: "app",
    title: "Summit Lock",
    upstream: "https://game-0045-summit-lock.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0045-summit-lock.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Summit Lock, a small original browser game.",
  },
  // Burrow Brawler. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0046-burrow-brawler.
  {
    slug: "game-0046-burrow-brawler",
    surface: "app",
    title: "Burrow Brawler",
    upstream: "https://game-0046-burrow-brawler.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0046-burrow-brawler.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Burrow Brawler, a small original browser game.",
  },
  // Token Tactics. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0047-token-tactics.
  {
    slug: "game-0047-token-tactics",
    surface: "app",
    title: "Token Tactics",
    upstream: "https://game-0047-token-tactics.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0047-token-tactics.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Token Tactics, a small original browser game.",
  },
  // Squish Courier. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0048-squish-courier.
  {
    slug: "game-0048-squish-courier",
    surface: "app",
    title: "Squish Courier",
    upstream: "https://game-0048-squish-courier.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0048-squish-courier.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Squish Courier, a small original browser game.",
  },
  // Epoch Blocks. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0049-epoch-blocks.
  {
    slug: "game-0049-epoch-blocks",
    surface: "app",
    title: "Epoch Blocks",
    upstream: "https://game-0049-epoch-blocks.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0049-epoch-blocks.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Epoch Blocks, a small original browser game.",
  },
  // Harvest Orders. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0050-harvest-orders.
  {
    slug: "game-0050-harvest-orders",
    surface: "app",
    title: "Harvest Orders",
    upstream: "https://game-0050-harvest-orders.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0050-harvest-orders.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Harvest Orders, a small original browser game.",
  },
  // Rover Footbridge. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0053-rover-footbridge.
  {
    slug: "game-0053-rover-footbridge",
    surface: "app",
    title: "Rover Footbridge",
    upstream: "https://game-0053-rover-footbridge.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0053-rover-footbridge.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Rover Footbridge, a small original browser game.",
  },
  // Bloom Impulse. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0052-bloom-impulse.
  {
    slug: "game-0052-bloom-impulse",
    surface: "app",
    title: "Bloom Impulse",
    upstream: "https://game-0052-bloom-impulse.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0052-bloom-impulse.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Bloom Impulse, a small original browser game.",
  },
  // Circuit Mason. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0051-circuit-mason.
  {
    slug: "game-0051-circuit-mason",
    surface: "app",
    title: "Circuit Mason",
    upstream: "https://game-0051-circuit-mason.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0051-circuit-mason.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Circuit Mason, a small original browser game.",
  },
  // Chromatic Brackets. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0054-chromatic-brackets.
  {
    slug: "game-0054-chromatic-brackets",
    surface: "app",
    title: "Chromatic Brackets",
    upstream: "https://game-0054-chromatic-brackets.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0054-chromatic-brackets.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Chromatic Brackets, a small original browser game.",
  },
  // Diamond Resonance. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0055-diamond-resonance.
  {
    slug: "game-0055-diamond-resonance",
    surface: "app",
    title: "Diamond Resonance",
    upstream: "https://game-0055-diamond-resonance.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0055-diamond-resonance.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Diamond Resonance, a small original browser game.",
  },
  // Aurora Bonbons. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0057-aurora-bonbons.
  {
    slug: "game-0057-aurora-bonbons",
    surface: "app",
    title: "Aurora Bonbons",
    upstream: "https://game-0057-aurora-bonbons.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0057-aurora-bonbons.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Aurora Bonbons, a small original browser game.",
  },
  // Mask Mosaic. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0058-mask-mosaic.
  {
    slug: "game-0058-mask-mosaic",
    surface: "app",
    title: "Mask Mosaic",
    upstream: "https://game-0058-mask-mosaic.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0058-mask-mosaic.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Mask Mosaic, a small original browser game.",
  },
  // Prism Duel. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0059-prism-duel.
  {
    slug: "game-0059-prism-duel",
    surface: "app",
    title: "Prism Duel",
    upstream: "https://game-0059-prism-duel.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0059-prism-duel.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Prism Duel, a small original browser game.",
  },
  // Orb Courier. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0060-orb-courier.
  {
    slug: "game-0060-orb-courier",
    surface: "app",
    title: "Orb Courier",
    upstream: "https://game-0060-orb-courier.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0060-orb-courier.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Orb Courier, a small original browser game.",
  },
  // Crate Caravan. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0061-crate-caravan.
  {
    slug: "game-0061-crate-caravan",
    surface: "app",
    title: "Crate Caravan",
    upstream: "https://game-0061-crate-caravan.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0061-crate-caravan.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Crate Caravan, a small original browser game.",
  },
  // Recruit Rampart. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0062-recruit-rampart.
  {
    slug: "game-0062-recruit-rampart",
    surface: "app",
    title: "Recruit Rampart",
    upstream: "https://game-0062-recruit-rampart.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0062-recruit-rampart.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Recruit Rampart, a small original browser game.",
  },
  // Pocket Archipelago. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0063-pocket-archipelago.
  {
    slug: "game-0063-pocket-archipelago",
    surface: "app",
    title: "Pocket Archipelago",
    upstream: "https://game-0063-pocket-archipelago.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0063-pocket-archipelago.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Pocket Archipelago, a small original browser game.",
  },
  // Prism Foundry. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0064-prism-foundry.
  {
    slug: "game-0064-prism-foundry",
    surface: "app",
    title: "Prism Foundry",
    upstream: "https://game-0064-prism-foundry.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0064-prism-foundry.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Prism Foundry, a small original browser game.",
  },
  // Pebble Depths. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0065-pebble-depths.
  {
    slug: "game-0065-pebble-depths",
    surface: "app",
    title: "Pebble Depths",
    upstream: "https://game-0065-pebble-depths.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0065-pebble-depths.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Pebble Depths, a small original browser game.",
  },
  // Pastry Dispatch. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/game-0056-pastry-dispatch.
  {
    slug: "game-0056-pastry-dispatch",
    surface: "app",
    title: "Pastry Dispatch",
    upstream: "https://game-0056-pastry-dispatch.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/game-0056-pastry-dispatch.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Play Pastry Dispatch, a small original browser game.",
  },
  // Sigstrip. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sigstrip.
  {
    slug: "sigstrip",
    surface: "app",
    title: "Sigstrip",
    upstream: "https://sigstrip.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sigstrip.webp",
    shippedAt: "2026-10-29",
    status: "live",
    blurb: "Turn brand details into an over-the-top email signature with an animated GIF demo and copy-ready HTML for Gmail, Outlook, and Apple Mail.",
  },
  // LLM Memory Calculator. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/llm-memory-calculator.
  {
    slug: "llm-memory-calculator",
    surface: "app",
    title: "LLM Memory Calculator",
    upstream: "https://llm-memory-calculator.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/llm-memory-calculator.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Calculate the GPU memory needed to serve an LLM from its parameter count and quantization.",
  },
  // Sweet Nothings. Overflow Worker on over9000-pool-000; static Vite build, no auth, storage none. Repo over9000-apps/sweet-nothings.
  {
    slug: "sweet-nothings",
    surface: "app",
    title: "Sweet Nothings",
    upstream: "https://sweet-nothings.over9000-pool-000.workers.dev",
    canonical: "host",
    screenshot: "/shots/sweet-nothings.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Discover romantic phrases one quiet line at a time from a collection of 9,000.",
  },
  // Montecompound. Static Vite SPA on digitalnative R2 (pool-000 Workers Free cap / 10037); no auth, storage none. Repo over9000-apps/montecompound.
  {
    slug: "montecompound",
    surface: "app",
    title: "Montecompound",
    upstream: "https://pub-4e8896a0b17441ee83557ad4e9332a05.r2.dev",
    canonical: "host",
    screenshot: "/shots/montecompound.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Seeded Monte Carlo compound-growth cockpit with fan charts, fat tails, and a shareable long view.",
  },
  // UI UX Pro Max Demo. Static Vite on digitalnative R2 techdemos-static (pool-000 Workers Free cap / 10037); no auth, storage none. Repo over9000-apps/ui-ux-pro-max-demo.
  {
    slug: "ui-ux-pro-max-demo",
    surface: "app",
    title: "UI UX Pro Max Demo",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/ui-ux-pro-max-demo",
    canonical: "host",
    screenshot: "/shots/ui-ux-pro-max-demo.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Pick an industry and get real UI UX Pro Max palettes, font pairings, and taste rules in the browser.",
  },
  // Impeccable Demo. Static Vite on digitalnative R2 techdemos-static (pool-000 Workers Free cap / 10037); no auth, storage none. Repo over9000-apps/impeccable-demo.
  {
    slug: "impeccable-demo",
    surface: "app",
    title: "Impeccable Demo",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/impeccable-demo",
    canonical: "host",
    screenshot: "/shots/impeccable-demo.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Side-by-side AI-slop vs polished UI, scored live against Impeccable's antipattern checklist.",
  },
  // Framelink Figma MCP Demo. Static Vite on digitalnative R2 techdemos-static (pool-000 Workers Free cap / 10037); no auth, storage none. Repo over9000-apps/figma-mcp-demo.
  {
    slug: "figma-mcp-demo",
    surface: "app",
    title: "Framelink Figma MCP Demo",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/figma-mcp-demo",
    canonical: "host",
    screenshot: "/shots/figma-mcp-demo.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "See a Figma frame become exact React code with a bundled design payload — no token required in the browser.",
  },
  // React Bits Demo. Static Vite on digitalnative R2 techdemos-static (pool-000 Workers Free cap / 10037); no auth, storage none. Repo over9000-apps/react-bits-demo.
  {
    slug: "react-bits-demo",
    surface: "app",
    title: "React Bits Demo",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/react-bits-demo",
    canonical: "host",
    screenshot: "/shots/react-bits-demo.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Live playground of real React Bits animations: DotGrid, CountUp, BlurText, RotatingText, BounceCards, GradientText.",
  },
  // GSAP Skills Demo. Static Vite on digitalnative R2 techdemos-static (pool-000 Workers Free cap / 10037); no auth, storage none. Repo over9000-apps/gsap-skills-demo.
  {
    slug: "gsap-skills-demo",
    surface: "app",
    title: "GSAP Skills Demo",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/gsap-skills-demo",
    canonical: "host",
    screenshot: "/shots/gsap-skills-demo.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Scroll-driven timelines, SplitText, and scrubbed scenes built the way gsap-skills teaches — GSAP is free now.",
  },
  // HyperFrames Demo. Static Vite on digitalnative R2 techdemos-static (pool-000 Workers Free cap / 10037); no auth, storage none. Repo over9000-apps/hyperframes-demo.
  {
    slug: "hyperframes-demo",
    surface: "app",
    title: "HyperFrames Demo",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/hyperframes-demo",
    canonical: "host",
    screenshot: "/shots/hyperframes-demo.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Watch a real HyperFrames render: HTML composition in, finished product promo video out — no After Effects.",
  },

  // Domino Designer. Pages on over9000-pool-000 (digitalnative Workers 500 + pool Workers Free 100 → 10037).
  // Client-only Vite + Rapier workers; no auth; storage none. Repo over9000-apps/domino-designer.
  {
    slug: "domino",
    surface: "app",
    title: "Domino Designer",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/domino",
    canonical: "host",
    screenshot: "/shots/domino.webp",
    shippedAt: "2026-10-31",
    status: "live",
    blurb: "Design and simulate million-scale domino runs — splines, bridges, Rapier physics, picture-to-domino.",
  },

  // Quadrant Courier. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0101-quadrant-courier.
  {
    slug: "game-0101-quadrant-courier",
    surface: "app",
    title: "Quadrant Courier",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0101-quadrant-courier",
    canonical: "host",
    screenshot: "/shots/game-0101-quadrant-courier.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0101 quadrant courier, a small original browser game.",
  },
  // Breeze Mason. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0102-breeze-mason.
  {
    slug: "game-0102-breeze-mason",
    surface: "app",
    title: "Breeze Mason",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0102-breeze-mason",
    canonical: "host",
    screenshot: "/shots/game-0102-breeze-mason.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game-0102-breeze-mason, a small original browser game.",
  },
  // Crane Circuit. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0103-crane-circuit.
  {
    slug: "game-0103-crane-circuit",
    surface: "app",
    title: "Crane Circuit",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0103-crane-circuit",
    canonical: "host",
    screenshot: "/shots/game-0103-crane-circuit.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0103 crane circuit, a small original browser game.",
  },
  // Cascade Lanterns. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0104-cascade-lanterns.
  {
    slug: "game-0104-cascade-lanterns",
    surface: "app",
    title: "Cascade Lanterns",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0104-cascade-lanterns",
    canonical: "host",
    screenshot: "/shots/game-0104-cascade-lanterns.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0104 cascade lanterns, a small original browser game.",
  },
  // Dawnwatch Hamlet. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0105-dawnwatch-hamlet.
  {
    slug: "game-0105-dawnwatch-hamlet",
    surface: "app",
    title: "Dawnwatch Hamlet",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0105-dawnwatch-hamlet",
    canonical: "host",
    screenshot: "/shots/game-0105-dawnwatch-hamlet.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0105 dawnwatch hamlet, a small original browser game.",
  },
  // Loop Orchard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0106-loop-orchard.
  {
    slug: "game-0106-loop-orchard",
    surface: "app",
    title: "Loop Orchard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0106-loop-orchard",
    canonical: "host",
    screenshot: "/shots/game-0106-loop-orchard.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0106 loop orchard, a small original browser game.",
  },
  // Orbital Foundry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0107-orbital-foundry.
  {
    slug: "game-0107-orbital-foundry",
    surface: "app",
    title: "Orbital Foundry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0107-orbital-foundry",
    canonical: "host",
    screenshot: "/shots/game-0107-orbital-foundry.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0107 orbital foundry, a small original browser game.",
  },
  // Zigzag Quarry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0108-zigzag-quarry.
  {
    slug: "game-0108-zigzag-quarry",
    surface: "app",
    title: "Zigzag Quarry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0108-zigzag-quarry",
    canonical: "host",
    screenshot: "/shots/game-0108-zigzag-quarry.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0108 zigzag quarry, a small original browser game.",
  },
  // Paddle Mason. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0201-paddle-mason.
  {
    slug: "game-0201-paddle-mason",
    surface: "app",
    title: "Paddle Mason",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0201-paddle-mason",
    canonical: "host",
    screenshot: "/shots/game-0201-paddle-mason.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0201 paddle mason, a small original browser game.",
  },
  // Cascade Lanterns. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0202-cascade-lanterns.
  {
    slug: "game-0202-cascade-lanterns",
    surface: "app",
    title: "Cascade Lanterns",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0202-cascade-lanterns",
    canonical: "host",
    screenshot: "/shots/game-0202-cascade-lanterns.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0202 cascade lanterns, a small original browser game.",
  },
  // Serpent Quarry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0203-serpent-quarry.
  {
    slug: "game-0203-serpent-quarry",
    surface: "app",
    title: "Serpent Quarry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0203-serpent-quarry",
    canonical: "host",
    screenshot: "/shots/game-0203-serpent-quarry.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0203 serpent quarry, a small original browser game.",
  },
  // Copper Accord. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0204-copper-accord.
  {
    slug: "game-0204-copper-accord",
    surface: "app",
    title: "Copper Accord",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0204-copper-accord",
    canonical: "host",
    screenshot: "/shots/game-0204-copper-accord.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0204 copper accord, a small original browser game.",
  },
  // Compass Kiln. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0205-compass-kiln.
  {
    slug: "game-0205-compass-kiln",
    surface: "app",
    title: "Compass Kiln",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0205-compass-kiln",
    canonical: "host",
    screenshot: "/shots/game-0205-compass-kiln.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0205 compass kiln, a small original browser game.",
  },
  // Fourwall Courtyard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0206-fourwall-courtyard.
  {
    slug: "game-0206-fourwall-courtyard",
    surface: "app",
    title: "Fourwall Courtyard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0206-fourwall-courtyard",
    canonical: "host",
    screenshot: "/shots/game-0206-fourwall-courtyard.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0206 fourwall courtyard, a small original browser game.",
  },
  // Mineral Porter. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0207-mineral-porter.
  {
    slug: "game-0207-mineral-porter",
    surface: "app",
    title: "Mineral Porter",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0207-mineral-porter",
    canonical: "host",
    screenshot: "/shots/game-0207-mineral-porter.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0207 mineral porter, a small original browser game.",
  },
  // Terminal Grove. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0208-terminal-grove.
  {
    slug: "game-0208-terminal-grove",
    surface: "app",
    title: "Terminal Grove",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0208-terminal-grove",
    canonical: "host",
    screenshot: "/shots/game-0208-terminal-grove.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0208 terminal grove, a small original browser game.",
  },
  // Secondhand Volley. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0209-secondhand-volley.
  {
    slug: "game-0209-secondhand-volley",
    surface: "app",
    title: "Secondhand Volley",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0209-secondhand-volley",
    canonical: "host",
    screenshot: "/shots/game-0209-secondhand-volley.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0209 secondhand volley, a small original browser game.",
  },
  // Chromatic Litany. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0210-chromatic-litany.
  {
    slug: "game-0210-chromatic-litany",
    surface: "app",
    title: "Chromatic Litany",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0210-chromatic-litany",
    canonical: "host",
    screenshot: "/shots/game-0210-chromatic-litany.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0210 chromatic litany, a small original browser game.",
  },
  // Bastion Loom. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0211-bastion-loom.
  {
    slug: "game-0211-bastion-loom",
    surface: "app",
    title: "Bastion Loom",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0211-bastion-loom",
    canonical: "host",
    screenshot: "/shots/game-0211-bastion-loom.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0211 bastion loom, a small original browser game.",
  },
  // Horizon Atelier. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0212-horizon-atelier.
  {
    slug: "game-0212-horizon-atelier",
    surface: "app",
    title: "Horizon Atelier",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0212-horizon-atelier",
    canonical: "host",
    screenshot: "/shots/game-0212-horizon-atelier.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0212 horizon atelier, a small original browser game.",
  },
  // Voxel Terraces. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0213-voxel-terraces.
  {
    slug: "game-0213-voxel-terraces",
    surface: "app",
    title: "Voxel Terraces",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0213-voxel-terraces",
    canonical: "host",
    screenshot: "/shots/game-0213-voxel-terraces.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0213 voxel terraces, a small original browser game.",
  },
  // Screensaver Springs. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0214-screensaver-springs.
  {
    slug: "game-0214-screensaver-springs",
    surface: "app",
    title: "Screensaver Springs",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0214-screensaver-springs",
    canonical: "host",
    screenshot: "/shots/game-0214-screensaver-springs.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0214 screensaver springs, a small original browser game.",
  },
  // Aroma Cabinet. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0215-aroma-cabinet.
  {
    slug: "game-0215-aroma-cabinet",
    surface: "app",
    title: "Aroma Cabinet",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0215-aroma-cabinet",
    canonical: "host",
    screenshot: "/shots/game-0215-aroma-cabinet.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0215 aroma cabinet, a small original browser game.",
  },
  // Gargoyle Conservatory. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0216-gargoyle-conservatory.
  {
    slug: "game-0216-gargoyle-conservatory",
    surface: "app",
    title: "Gargoyle Conservatory",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0216-gargoyle-conservatory",
    canonical: "host",
    screenshot: "/shots/game-0216-gargoyle-conservatory.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0216 gargoyle conservatory, a small original browser game.",
  },
  // Unpin Salvage. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0217-unpin-salvage.
  {
    slug: "game-0217-unpin-salvage",
    surface: "app",
    title: "Unpin Salvage",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0217-unpin-salvage",
    canonical: "host",
    screenshot: "/shots/game-0217-unpin-salvage.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0217 unpin salvage, a small original browser game.",
  },
  // Fulcrum Freight. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0218-fulcrum-freight.
  {
    slug: "game-0218-fulcrum-freight",
    surface: "app",
    title: "Fulcrum Freight",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0218-fulcrum-freight",
    canonical: "host",
    screenshot: "/shots/game-0218-fulcrum-freight.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0218 fulcrum freight, a small original browser game.",
  },
  // Panel Orchard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0219-panel-orchard.
  {
    slug: "game-0219-panel-orchard",
    surface: "app",
    title: "Panel Orchard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0219-panel-orchard",
    canonical: "host",
    screenshot: "/shots/game-0219-panel-orchard.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0219 panel orchard, a small original browser game.",
  },
  // Nightwatch Console. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0220-nightwatch-console.
  {
    slug: "game-0220-nightwatch-console",
    surface: "app",
    title: "Nightwatch Console",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0220-nightwatch-console",
    canonical: "host",
    screenshot: "/shots/game-0220-nightwatch-console.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0220 nightwatch console, a small original browser game.",
  },
  // Grumble Garden. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0221-grumble-garden.
  {
    slug: "game-0221-grumble-garden",
    surface: "app",
    title: "Grumble Garden",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0221-grumble-garden",
    canonical: "host",
    screenshot: "/shots/game-0221-grumble-garden.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0221 grumble garden, a small original browser game.",
  },
  // Pulse Foundry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0222-pulse-foundry.
  {
    slug: "game-0222-pulse-foundry",
    surface: "app",
    title: "Pulse Foundry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0222-pulse-foundry",
    canonical: "host",
    screenshot: "/shots/game-0222-pulse-foundry.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0222 pulse foundry, a small original browser game.",
  },
  // Jelly Counterweight. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0223-jelly-counterweight.
  {
    slug: "game-0223-jelly-counterweight",
    surface: "app",
    title: "Jelly Counterweight",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0223-jelly-counterweight",
    canonical: "host",
    screenshot: "/shots/game-0223-jelly-counterweight.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0223 jelly counterweight, a small original browser game.",
  },
  // Fivefold Quilt. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0224-fivefold-quilt.
  {
    slug: "game-0224-fivefold-quilt",
    surface: "app",
    title: "Fivefold Quilt",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0224-fivefold-quilt",
    canonical: "host",
    screenshot: "/shots/game-0224-fivefold-quilt.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0224 fivefold quilt, a small original browser game.",
  },
  // Negative Quarry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0225-negative-quarry.
  {
    slug: "game-0225-negative-quarry",
    surface: "app",
    title: "Negative Quarry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0225-negative-quarry",
    canonical: "host",
    screenshot: "/shots/game-0225-negative-quarry.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0225 negative quarry, a small original browser game.",
  },
  // Orbit Compactor. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0226-orbit-compactor.
  {
    slug: "game-0226-orbit-compactor",
    surface: "app",
    title: "Orbit Compactor",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0226-orbit-compactor",
    canonical: "host",
    screenshot: "/shots/game-0226-orbit-compactor.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0226 orbit compactor, a small original browser game.",
  },
  // Twinbolt Rain. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0227-twinbolt-rain.
  {
    slug: "game-0227-twinbolt-rain",
    surface: "app",
    title: "Twinbolt Rain",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0227-twinbolt-rain",
    canonical: "host",
    screenshot: "/shots/game-0227-twinbolt-rain.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0227 twinbolt rain, a small original browser game.",
  },
  // Polarity Porter. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0228-polarity-porter.
  {
    slug: "game-0228-polarity-porter",
    surface: "app",
    title: "Polarity Porter",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0228-polarity-porter",
    canonical: "host",
    screenshot: "/shots/game-0228-polarity-porter.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0228 polarity porter, a small original browser game.",
  },
  // Column Loom. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0229-column-loom.
  {
    slug: "game-0229-column-loom",
    surface: "app",
    title: "Column Loom",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0229-column-loom",
    canonical: "host",
    screenshot: "/shots/game-0229-column-loom.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0229 column loom, a small original browser game.",
  },
  // Transit Triads. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0230-transit-triads.
  {
    slug: "game-0230-transit-triads",
    surface: "app",
    title: "Transit Triads",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0230-transit-triads",
    canonical: "host",
    screenshot: "/shots/game-0230-transit-triads.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0230 transit triads, a small original browser game.",
  },
  // Purrcel Shelf. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0231-purrcel-shelf.
  {
    slug: "game-0231-purrcel-shelf",
    surface: "app",
    title: "Purrcel Shelf",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0231-purrcel-shelf",
    canonical: "host",
    screenshot: "/shots/game-0231-purrcel-shelf.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0231 purrcel shelf, a small original browser game.",
  },
  // Hexglass Duel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0232-hexglass-duel.
  {
    slug: "game-0232-hexglass-duel",
    surface: "app",
    title: "Hexglass Duel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0232-hexglass-duel",
    canonical: "host",
    screenshot: "/shots/game-0232-hexglass-duel.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0232 hexglass duel, a small original browser game.",
  },
  // Fuse Orchard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0233-fuse-orchard.
  {
    slug: "game-0233-fuse-orchard",
    surface: "app",
    title: "Fuse Orchard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0233-fuse-orchard",
    canonical: "host",
    screenshot: "/shots/game-0233-fuse-orchard.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0233 fuse orchard, a small original browser game.",
  },
  // Ashline Siege. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0234-ashline-siege.
  {
    slug: "game-0234-ashline-siege",
    surface: "app",
    title: "Ashline Siege",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0234-ashline-siege",
    canonical: "host",
    screenshot: "/shots/game-0234-ashline-siege.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0234 ashline siege, a small original browser game.",
  },
  // Dune Sluice. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0235-dune-sluice.
  {
    slug: "game-0235-dune-sluice",
    surface: "app",
    title: "Dune Sluice",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0235-dune-sluice",
    canonical: "host",
    screenshot: "/shots/game-0235-dune-sluice.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0235 dune sluice, a small original browser game.",
  },
  // Prism Exchange. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0236-prism-exchange.
  {
    slug: "game-0236-prism-exchange",
    surface: "app",
    title: "Prism Exchange",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0236-prism-exchange",
    canonical: "host",
    screenshot: "/shots/game-0236-prism-exchange.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0236 prism exchange, a small original browser game.",
  },
  // Parallel Wells. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0237-parallel-wells.
  {
    slug: "game-0237-parallel-wells",
    surface: "app",
    title: "Parallel Wells",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0237-parallel-wells",
    canonical: "host",
    screenshot: "/shots/game-0237-parallel-wells.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0237 parallel wells, a small original browser game.",
  },
  // Picnic Mosaic. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0238-picnic-mosaic.
  {
    slug: "game-0238-picnic-mosaic",
    surface: "app",
    title: "Picnic Mosaic",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0238-picnic-mosaic",
    canonical: "host",
    screenshot: "/shots/game-0238-picnic-mosaic.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0238 picnic mosaic, a small original browser game.",
  },
  // Airlock Cargo. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0239-airlock-cargo.
  {
    slug: "game-0239-airlock-cargo",
    surface: "app",
    title: "Airlock Cargo",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0239-airlock-cargo",
    canonical: "host",
    screenshot: "/shots/game-0239-airlock-cargo.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0239 airlock cargo, a small original browser game.",
  },
  // Chromatic Delta. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0240-chromatic-delta.
  {
    slug: "game-0240-chromatic-delta",
    surface: "app",
    title: "Chromatic Delta",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0240-chromatic-delta",
    canonical: "host",
    screenshot: "/shots/game-0240-chromatic-delta.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0240 chromatic delta, a small original browser game.",
  },
  // Runeshape Tactics. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0241-runeshape-tactics.
  {
    slug: "game-0241-runeshape-tactics",
    surface: "app",
    title: "Runeshape Tactics",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0241-runeshape-tactics",
    canonical: "host",
    screenshot: "/shots/game-0241-runeshape-tactics.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0241 runeshape tactics, a small original browser game.",
  },
  // Bough Builder. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0242-bough-builder.
  {
    slug: "game-0242-bough-builder",
    surface: "app",
    title: "Bough Builder",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0242-bough-builder",
    canonical: "host",
    screenshot: "/shots/game-0242-bough-builder.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0242 bough builder, a small original browser game.",
  },
  // Tenfold Tokens. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0243-tenfold-tokens.
  {
    slug: "game-0243-tenfold-tokens",
    surface: "app",
    title: "Tenfold Tokens",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0243-tenfold-tokens",
    canonical: "host",
    screenshot: "/shots/game-0243-tenfold-tokens.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0243 tenfold tokens, a small original browser game.",
  },
  // Scaffold Skipper. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0244-scaffold-skipper.
  {
    slug: "game-0244-scaffold-skipper",
    surface: "app",
    title: "Scaffold Skipper",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0244-scaffold-skipper",
    canonical: "host",
    screenshot: "/shots/game-0244-scaffold-skipper.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0244 scaffold skipper, a small original browser game.",
  },
  // Moonbun Diner. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0245-moonbun-diner.
  {
    slug: "game-0245-moonbun-diner",
    surface: "app",
    title: "Moonbun Diner",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0245-moonbun-diner",
    canonical: "host",
    screenshot: "/shots/game-0245-moonbun-diner.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0245 moonbun diner, a small original browser game.",
  },
  // Haven Relay. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0246-haven-relay.
  {
    slug: "game-0246-haven-relay",
    surface: "app",
    title: "Haven Relay",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0246-haven-relay",
    canonical: "host",
    screenshot: "/shots/game-0246-haven-relay.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0246 haven relay, a small original browser game.",
  },
  // Pegwork Mill. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0247-pegwork-mill.
  {
    slug: "game-0247-pegwork-mill",
    surface: "app",
    title: "Pegwork Mill",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0247-pegwork-mill",
    canonical: "host",
    screenshot: "/shots/game-0247-pegwork-mill.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0247 pegwork mill, a small original browser game.",
  },
  // Cardamom Counter. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0248-cardamom-counter.
  {
    slug: "game-0248-cardamom-counter",
    surface: "app",
    title: "Cardamom Counter",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0248-cardamom-counter",
    canonical: "host",
    screenshot: "/shots/game-0248-cardamom-counter.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0248 cardamom counter, a small original browser game.",
  },
  // Crossbar Atelier. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0249-crossbar-atelier.
  {
    slug: "game-0249-crossbar-atelier",
    surface: "app",
    title: "Crossbar Atelier",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0249-crossbar-atelier",
    canonical: "host",
    screenshot: "/shots/game-0249-crossbar-atelier.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0249 crossbar atelier, a small original browser game.",
  },
  // Snowfield Studio. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0250-snowfield-studio.
  {
    slug: "game-0250-snowfield-studio",
    surface: "app",
    title: "Snowfield Studio",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0250-snowfield-studio",
    canonical: "host",
    screenshot: "/shots/game-0250-snowfield-studio.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0250 snowfield studio, a small original browser game.",
  },
  // Charge and Ricochet. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0251-charge-and-ricochet.
  {
    slug: "game-0251-charge-and-ricochet",
    surface: "app",
    title: "Charge and Ricochet",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0251-charge-and-ricochet",
    canonical: "host",
    screenshot: "/shots/game-0251-charge-and-ricochet.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0251 charge and ricochet, a small original browser game.",
  },
  // Fuse Garden. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0252-fuse-garden.
  {
    slug: "game-0252-fuse-garden",
    surface: "app",
    title: "Fuse Garden",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0252-fuse-garden",
    canonical: "host",
    screenshot: "/shots/game-0252-fuse-garden.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0252 fuse garden, a small original browser game.",
  },
  // Sum Salvagers. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0253-sum-salvagers.
  {
    slug: "game-0253-sum-salvagers",
    surface: "app",
    title: "Sum Salvagers",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0253-sum-salvagers",
    canonical: "host",
    screenshot: "/shots/game-0253-sum-salvagers.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0253 sum salvagers, a small original browser game.",
  },
  // Quarry Stitch. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0254-quarry-stitch.
  {
    slug: "game-0254-quarry-stitch",
    surface: "app",
    title: "Quarry Stitch",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0254-quarry-stitch",
    canonical: "host",
    screenshot: "/shots/game-0254-quarry-stitch.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0254 quarry stitch, a small original browser game.",
  },
  // Earthward Patrol. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0255-earthward-patrol.
  {
    slug: "game-0255-earthward-patrol",
    surface: "app",
    title: "Earthward Patrol",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0255-earthward-patrol",
    canonical: "host",
    screenshot: "/shots/game-0255-earthward-patrol.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0255 earthward patrol, a small original browser game.",
  },
  // Solstice Jar. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0256-solstice-jar.
  {
    slug: "game-0256-solstice-jar",
    surface: "app",
    title: "Solstice Jar",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0256-solstice-jar",
    canonical: "host",
    screenshot: "/shots/game-0256-solstice-jar.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0256 solstice jar, a small original browser game.",
  },
  // Orbital Harvest. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0257-orbital-harvest.
  {
    slug: "game-0257-orbital-harvest",
    surface: "app",
    title: "Orbital Harvest",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0257-orbital-harvest",
    canonical: "host",
    screenshot: "/shots/game-0257-orbital-harvest.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0257 orbital harvest, a small original browser game.",
  },
  // Quarterturn Crate. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0258-quarterturn-crate.
  {
    slug: "game-0258-quarterturn-crate",
    surface: "app",
    title: "Quarterturn Crate",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0258-quarterturn-crate",
    canonical: "host",
    screenshot: "/shots/game-0258-quarterturn-crate.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0258 quarterturn crate, a small original browser game.",
  },
  // Pulse Mason. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0259-pulse-mason.
  {
    slug: "game-0259-pulse-mason",
    surface: "app",
    title: "Pulse Mason",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0259-pulse-mason",
    canonical: "host",
    screenshot: "/shots/game-0259-pulse-mason.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0259 pulse mason, a small original browser game.",
  },
  // Pivot Paws. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0260-pivot-paws.
  {
    slug: "game-0260-pivot-paws",
    surface: "app",
    title: "Pivot Paws",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0260-pivot-paws",
    canonical: "host",
    screenshot: "/shots/game-0260-pivot-paws.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0260 pivot paws, a small original browser game.",
  },
  // Magnet Loft. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0261-magnet-loft.
  {
    slug: "game-0261-magnet-loft",
    surface: "app",
    title: "Magnet Loft",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0261-magnet-loft",
    canonical: "host",
    screenshot: "/shots/game-0261-magnet-loft.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0261 magnet loft, a small original browser game.",
  },
  // Rampart Runner. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0262-rampart-runner.
  {
    slug: "game-0262-rampart-runner",
    surface: "app",
    title: "Rampart Runner",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0262-rampart-runner",
    canonical: "host",
    screenshot: "/shots/game-0262-rampart-runner.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0262 rampart runner, a small original browser game.",
  },
  // Bistro Clusters. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0263-bistro-clusters.
  {
    slug: "game-0263-bistro-clusters",
    surface: "app",
    title: "Bistro Clusters",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0263-bistro-clusters",
    canonical: "host",
    screenshot: "/shots/game-0263-bistro-clusters.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0263 bistro clusters, a small original browser game.",
  },
  // Loadbearing Wishes. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0264-loadbearing-wishes.
  {
    slug: "game-0264-loadbearing-wishes",
    surface: "app",
    title: "Loadbearing Wishes",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0264-loadbearing-wishes",
    canonical: "host",
    screenshot: "/shots/game-0264-loadbearing-wishes.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0264 loadbearing wishes, a small original browser game.",
  },
  // Tractor Trio. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0265-tractor-trio.
  {
    slug: "game-0265-tractor-trio",
    surface: "app",
    title: "Tractor Trio",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0265-tractor-trio",
    canonical: "host",
    screenshot: "/shots/game-0265-tractor-trio.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0265 tractor trio, a small original browser game.",
  },
  // Square Orchard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0266-square-orchard.
  {
    slug: "game-0266-square-orchard",
    surface: "app",
    title: "Square Orchard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0266-square-orchard",
    canonical: "host",
    screenshot: "/shots/game-0266-square-orchard.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0266 square orchard, a small original browser game.",
  },
  // Cinder Cycleway. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0267-cinder-cycleway.
  {
    slug: "game-0267-cinder-cycleway",
    surface: "app",
    title: "Cinder Cycleway",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0267-cinder-cycleway",
    canonical: "host",
    screenshot: "/shots/game-0267-cinder-cycleway.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0267 cinder cycleway, a small original browser game.",
  },
  // Soulbound Volley. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0268-soulbound-volley.
  {
    slug: "game-0268-soulbound-volley",
    surface: "app",
    title: "Soulbound Volley",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0268-soulbound-volley",
    canonical: "host",
    screenshot: "/shots/game-0268-soulbound-volley.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0268 soulbound volley, a small original browser game.",
  },
  // Falling Lexicon. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0269-falling-lexicon.
  {
    slug: "game-0269-falling-lexicon",
    surface: "app",
    title: "Falling Lexicon",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0269-falling-lexicon",
    canonical: "host",
    screenshot: "/shots/game-0269-falling-lexicon.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0269 falling lexicon, a small original browser game.",
  },
  // Twinwell Accord. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0270-twinwell-accord.
  {
    slug: "game-0270-twinwell-accord",
    surface: "app",
    title: "Twinwell Accord",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0270-twinwell-accord",
    canonical: "host",
    screenshot: "/shots/game-0270-twinwell-accord.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0270 twinwell accord, a small original browser game.",
  },
  // Reverse Relics. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0271-reverse-relics.
  {
    slug: "game-0271-reverse-relics",
    surface: "app",
    title: "Reverse Relics",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0271-reverse-relics",
    canonical: "host",
    screenshot: "/shots/game-0271-reverse-relics.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0271 reverse relics, a small original browser game.",
  },
  // Fragment Cadence. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0272-fragment-cadence.
  {
    slug: "game-0272-fragment-cadence",
    surface: "app",
    title: "Fragment Cadence",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0272-fragment-cadence",
    canonical: "host",
    screenshot: "/shots/game-0272-fragment-cadence.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0272 fragment cadence, a small original browser game.",
  },
  // Versefall. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0273-versefall.
  {
    slug: "game-0273-versefall",
    surface: "app",
    title: "Versefall",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0273-versefall",
    canonical: "host",
    screenshot: "/shots/game-0273-versefall.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0273 versefall, a small original browser game.",
  },
  // Undertow Citadel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0274-undertow-citadel.
  {
    slug: "game-0274-undertow-citadel",
    surface: "app",
    title: "Undertow Citadel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0274-undertow-citadel",
    canonical: "host",
    screenshot: "/shots/game-0274-undertow-citadel.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0274 undertow citadel, a small original browser game.",
  },
  // Orbital Chisel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0275-orbital-chisel.
  {
    slug: "game-0275-orbital-chisel",
    surface: "app",
    title: "Orbital Chisel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0275-orbital-chisel",
    canonical: "host",
    screenshot: "/shots/game-0275-orbital-chisel.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0275 orbital chisel, a small original browser game.",
  },
  // Pocket Prospector. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0276-pocket-prospector.
  {
    slug: "game-0276-pocket-prospector",
    surface: "app",
    title: "Pocket Prospector",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0276-pocket-prospector",
    canonical: "host",
    screenshot: "/shots/game-0276-pocket-prospector.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0276 pocket prospector, a small original browser game.",
  },
  // Fourfold Register. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0277-fourfold-register.
  {
    slug: "game-0277-fourfold-register",
    surface: "app",
    title: "Fourfold Register",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0277-fourfold-register",
    canonical: "host",
    screenshot: "/shots/game-0277-fourfold-register.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0277 fourfold register, a small original browser game.",
  },
  // Rune Brickworks. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0278-rune-brickworks.
  {
    slug: "game-0278-rune-brickworks",
    surface: "app",
    title: "Rune Brickworks",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0278-rune-brickworks",
    canonical: "host",
    screenshot: "/shots/game-0278-rune-brickworks.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0278 rune brickworks, a small original browser game.",
  },
  // Letter Switchback. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0279-letter-switchback.
  {
    slug: "game-0279-letter-switchback",
    surface: "app",
    title: "Letter Switchback",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0279-letter-switchback",
    canonical: "host",
    screenshot: "/shots/game-0279-letter-switchback.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0279 letter switchback, a small original browser game.",
  },
  // Goo Relay. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0280-goo-relay.
  {
    slug: "game-0280-goo-relay",
    surface: "app",
    title: "Goo Relay",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0280-goo-relay",
    canonical: "host",
    screenshot: "/shots/game-0280-goo-relay.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0280 goo relay, a small original browser game.",
  },
  // Prism Petals. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0281-prism-petals.
  {
    slug: "game-0281-prism-petals",
    surface: "app",
    title: "Prism Petals",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0281-prism-petals",
    canonical: "host",
    screenshot: "/shots/game-0281-prism-petals.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0281 prism petals, a small original browser game.",
  },
  // Exit Foundry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0282-exit-foundry.
  {
    slug: "game-0282-exit-foundry",
    surface: "app",
    title: "Exit Foundry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0282-exit-foundry",
    canonical: "host",
    screenshot: "/shots/game-0282-exit-foundry.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0282 exit foundry, a small original browser game.",
  },
  // Pebble Resonance. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0283-pebble-resonance.
  {
    slug: "game-0283-pebble-resonance",
    surface: "app",
    title: "Pebble Resonance",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0283-pebble-resonance",
    canonical: "host",
    screenshot: "/shots/game-0283-pebble-resonance.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0283 pebble resonance, a small original browser game.",
  },
  // Velvet Cardfall. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0284-velvet-cardfall.
  {
    slug: "game-0284-velvet-cardfall",
    surface: "app",
    title: "Velvet Cardfall",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0284-velvet-cardfall",
    canonical: "host",
    screenshot: "/shots/game-0284-velvet-cardfall.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0284 velvet cardfall, a small original browser game.",
  },
  // Compass Furnace. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0285-compass-furnace.
  {
    slug: "game-0285-compass-furnace",
    surface: "app",
    title: "Compass Furnace",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0285-compass-furnace",
    canonical: "host",
    screenshot: "/shots/game-0285-compass-furnace.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0285 compass furnace, a small original browser game.",
  },
  // Quiet Quilt. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0286-quiet-quilt.
  {
    slug: "game-0286-quiet-quilt",
    surface: "app",
    title: "Quiet Quilt",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0286-quiet-quilt",
    canonical: "host",
    screenshot: "/shots/game-0286-quiet-quilt.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0286 quiet quilt, a small original browser game.",
  },
  // Elbow Atelier. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0287-elbow-atelier.
  {
    slug: "game-0287-elbow-atelier",
    surface: "app",
    title: "Elbow Atelier",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0287-elbow-atelier",
    canonical: "host",
    screenshot: "/shots/game-0287-elbow-atelier.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0287 elbow atelier, a small original browser game.",
  },
  // Ricochet Teacup. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0288-ricochet-teacup.
  {
    slug: "game-0288-ricochet-teacup",
    surface: "app",
    title: "Ricochet Teacup",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0288-ricochet-teacup",
    canonical: "host",
    screenshot: "/shots/game-0288-ricochet-teacup.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0288 ricochet teacup, a small original browser game.",
  },
  // Lantern Lunch. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0289-lantern-lunch.
  {
    slug: "game-0289-lantern-lunch",
    surface: "app",
    title: "Lantern Lunch",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0289-lantern-lunch",
    canonical: "host",
    screenshot: "/shots/game-0289-lantern-lunch.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0289 lantern lunch, a small original browser game.",
  },
  // Fuse Shuffle. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0290-fuse-shuffle.
  {
    slug: "game-0290-fuse-shuffle",
    surface: "app",
    title: "Fuse Shuffle",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0290-fuse-shuffle",
    canonical: "host",
    screenshot: "/shots/game-0290-fuse-shuffle.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0290 fuse shuffle, a small original browser game.",
  },
  // Rune Duel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0291-rune-duel.
  {
    slug: "game-0291-rune-duel",
    surface: "app",
    title: "Rune Duel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0291-rune-duel",
    canonical: "host",
    screenshot: "/shots/game-0291-rune-duel.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0291 rune duel, a small original browser game.",
  },
  // Bloom Exchange. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0292-bloom-exchange.
  {
    slug: "game-0292-bloom-exchange",
    surface: "app",
    title: "Bloom Exchange",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0292-bloom-exchange",
    canonical: "host",
    screenshot: "/shots/game-0292-bloom-exchange.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0292 bloom exchange, a small original browser game.",
  },
  // Tidepost. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0293-tidepost.
  {
    slug: "game-0293-tidepost",
    surface: "app",
    title: "Tidepost",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0293-tidepost",
    canonical: "host",
    screenshot: "/shots/game-0293-tidepost.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0293 tidepost, a small original browser game.",
  },
  // Coinbrick Mill. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0294-coinbrick-mill.
  {
    slug: "game-0294-coinbrick-mill",
    surface: "app",
    title: "Coinbrick Mill",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0294-coinbrick-mill",
    canonical: "host",
    screenshot: "/shots/game-0294-coinbrick-mill.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0294 coinbrick mill, a small original browser game.",
  },
  // Falling Forecast. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0295-falling-forecast.
  {
    slug: "game-0295-falling-forecast",
    surface: "app",
    title: "Falling Forecast",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0295-falling-forecast",
    canonical: "host",
    screenshot: "/shots/game-0295-falling-forecast.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0295 falling forecast, a small original browser game.",
  },
  // Aurum Chain. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0296-aurum-chain.
  {
    slug: "game-0296-aurum-chain",
    surface: "app",
    title: "Aurum Chain",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0296-aurum-chain",
    canonical: "host",
    screenshot: "/shots/game-0296-aurum-chain.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0296 aurum chain, a small original browser game.",
  },
  // Porch Purge. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0297-porch-purge.
  {
    slug: "game-0297-porch-purge",
    surface: "app",
    title: "Porch Purge",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0297-porch-purge",
    canonical: "host",
    screenshot: "/shots/game-0297-porch-purge.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0297 porch purge, a small original browser game.",
  },
  // Pocket Borough. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0298-pocket-borough.
  {
    slug: "game-0298-pocket-borough",
    surface: "app",
    title: "Pocket Borough",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0298-pocket-borough",
    canonical: "host",
    screenshot: "/shots/game-0298-pocket-borough.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0298 pocket borough, a small original browser game.",
  },
  // Domino Dispatch. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0299-domino-dispatch.
  {
    slug: "game-0299-domino-dispatch",
    surface: "app",
    title: "Domino Dispatch",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0299-domino-dispatch",
    canonical: "host",
    screenshot: "/shots/game-0299-domino-dispatch.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0299 domino dispatch, a small original browser game.",
  },
  // Blackout Ledger. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0300-blackout-ledger.
  {
    slug: "game-0300-blackout-ledger",
    surface: "app",
    title: "Blackout Ledger",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0300-blackout-ledger",
    canonical: "host",
    screenshot: "/shots/game-0300-blackout-ledger.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0300 blackout ledger, a small original browser game.",
  },
  // Marble Moon. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0301-marble-moon.
  {
    slug: "game-0301-marble-moon",
    surface: "app",
    title: "Marble Moon",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0301-marble-moon",
    canonical: "host",
    screenshot: "/shots/game-0301-marble-moon.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0301 marble moon, a small original browser game.",
  },
  // Liberty Lanterns. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0302-liberty-lanterns.
  {
    slug: "game-0302-liberty-lanterns",
    surface: "app",
    title: "Liberty Lanterns",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0302-liberty-lanterns",
    canonical: "host",
    screenshot: "/shots/game-0302-liberty-lanterns.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0302 liberty lanterns, a small original browser game.",
  },
  // Basalt Courier. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0303-basalt-courier.
  {
    slug: "game-0303-basalt-courier",
    surface: "app",
    title: "Basalt Courier",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0303-basalt-courier",
    canonical: "host",
    screenshot: "/shots/game-0303-basalt-courier.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0303 basalt courier, a small original browser game.",
  },
  // Tenfold Sweets. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0304-tenfold-sweets.
  {
    slug: "game-0304-tenfold-sweets",
    surface: "app",
    title: "Tenfold Sweets",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0304-tenfold-sweets",
    canonical: "host",
    screenshot: "/shots/game-0304-tenfold-sweets.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0304 tenfold sweets, a small original browser game.",
  },
  // Clockwork Quarry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0305-clockwork-quarry.
  {
    slug: "game-0305-clockwork-quarry",
    surface: "app",
    title: "Clockwork Quarry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0305-clockwork-quarry",
    canonical: "host",
    screenshot: "/shots/game-0305-clockwork-quarry.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0305 clockwork quarry, a small original browser game.",
  },
  // Counterweight Cargo. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0306-counterweight-cargo.
  {
    slug: "game-0306-counterweight-cargo",
    surface: "app",
    title: "Counterweight Cargo",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0306-counterweight-cargo",
    canonical: "host",
    screenshot: "/shots/game-0306-counterweight-cargo.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0306 counterweight cargo, a small original browser game.",
  },
  // Orbit Cairn. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0307-orbit-cairn.
  {
    slug: "game-0307-orbit-cairn",
    surface: "app",
    title: "Orbit Cairn",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0307-orbit-cairn",
    canonical: "host",
    screenshot: "/shots/game-0307-orbit-cairn.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0307 orbit cairn, a small original browser game.",
  },
  // Jamburst Orchard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0308-jamburst-orchard.
  {
    slug: "game-0308-jamburst-orchard",
    surface: "app",
    title: "Jamburst Orchard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0308-jamburst-orchard",
    canonical: "host",
    screenshot: "/shots/game-0308-jamburst-orchard.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0308 jamburst orchard, a small original browser game.",
  },
  // Cluster Kiln. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0309-cluster-kiln.
  {
    slug: "game-0309-cluster-kiln",
    surface: "app",
    title: "Cluster Kiln",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0309-cluster-kiln",
    canonical: "host",
    screenshot: "/shots/game-0309-cluster-kiln.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0309 cluster kiln, a small original browser game.",
  },
  // Spanwright. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0310-spanwright.
  {
    slug: "game-0310-spanwright",
    surface: "app",
    title: "Spanwright",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0310-spanwright",
    canonical: "host",
    screenshot: "/shots/game-0310-spanwright.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0310 spanwright, a small original browser game.",
  },
  // Decimal Falls. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0311-decimal-falls.
  {
    slug: "game-0311-decimal-falls",
    surface: "app",
    title: "Decimal Falls",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0311-decimal-falls",
    canonical: "host",
    screenshot: "/shots/game-0311-decimal-falls.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0311 decimal falls, a small original browser game.",
  },
  // Crag Constructor. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0312-crag-constructor.
  {
    slug: "game-0312-crag-constructor",
    surface: "app",
    title: "Crag Constructor",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0312-crag-constructor",
    canonical: "host",
    screenshot: "/shots/game-0312-crag-constructor.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0312 crag constructor, a small original browser game.",
  },
  // Relic Rowcrawl. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0313-relic-rowcrawl.
  {
    slug: "game-0313-relic-rowcrawl",
    surface: "app",
    title: "Relic Rowcrawl",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0313-relic-rowcrawl",
    canonical: "host",
    screenshot: "/shots/game-0313-relic-rowcrawl.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0313 relic rowcrawl, a small original browser game.",
  },
  // Seam Splitter. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0314-seam-splitter.
  {
    slug: "game-0314-seam-splitter",
    surface: "app",
    title: "Seam Splitter",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0314-seam-splitter",
    canonical: "host",
    screenshot: "/shots/game-0314-seam-splitter.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0314 seam splitter, a small original browser game.",
  },
  // Orbit Shears. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0315-orbit-shears.
  {
    slug: "game-0315-orbit-shears",
    surface: "app",
    title: "Orbit Shears",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0315-orbit-shears",
    canonical: "host",
    screenshot: "/shots/game-0315-orbit-shears.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0315 orbit shears, a small original browser game.",
  },
  // Magnet March. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0316-magnet-march.
  {
    slug: "game-0316-magnet-march",
    surface: "app",
    title: "Magnet March",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0316-magnet-march",
    canonical: "host",
    screenshot: "/shots/game-0316-magnet-march.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0316 magnet march, a small original browser game.",
  },
  // Cascade Dividend. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0317-cascade-dividend.
  {
    slug: "game-0317-cascade-dividend",
    surface: "app",
    title: "Cascade Dividend",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0317-cascade-dividend",
    canonical: "host",
    screenshot: "/shots/game-0317-cascade-dividend.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0317 cascade dividend, a small original browser game.",
  },
  // Letter Landing. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0318-letter-landing.
  {
    slug: "game-0318-letter-landing",
    surface: "app",
    title: "Letter Landing",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0318-letter-landing",
    canonical: "host",
    screenshot: "/shots/game-0318-letter-landing.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0318 letter landing, a small original browser game.",
  },
  // Stormscore Smith. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0319-stormscore-smith.
  {
    slug: "game-0319-stormscore-smith",
    surface: "app",
    title: "Stormscore Smith",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0319-stormscore-smith",
    canonical: "host",
    screenshot: "/shots/game-0319-stormscore-smith.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0319 stormscore smith, a small original browser game.",
  },
  // Catcrate Picnic. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0320-catcrate-picnic.
  {
    slug: "game-0320-catcrate-picnic",
    surface: "app",
    title: "Catcrate Picnic",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0320-catcrate-picnic",
    canonical: "host",
    screenshot: "/shots/game-0320-catcrate-picnic.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0320 catcrate picnic, a small original browser game.",
  },
  // Parcel Circuit. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0321-parcel-circuit.
  {
    slug: "game-0321-parcel-circuit",
    surface: "app",
    title: "Parcel Circuit",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0321-parcel-circuit",
    canonical: "host",
    screenshot: "/shots/game-0321-parcel-circuit.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0321 parcel circuit, a small original browser game.",
  },
  // Blast Quarry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0322-blast-quarry.
  {
    slug: "game-0322-blast-quarry",
    surface: "app",
    title: "Blast Quarry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0322-blast-quarry",
    canonical: "host",
    screenshot: "/shots/game-0322-blast-quarry.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0322 blast quarry, a small original browser game.",
  },
  // Rooftop Evader. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0323-rooftop-evader.
  {
    slug: "game-0323-rooftop-evader",
    surface: "app",
    title: "Rooftop Evader",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0323-rooftop-evader",
    canonical: "host",
    screenshot: "/shots/game-0323-rooftop-evader.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0323 rooftop evader, a small original browser game.",
  },
  // Banner Breach. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0324-banner-breach.
  {
    slug: "game-0324-banner-breach",
    surface: "app",
    title: "Banner Breach",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0324-banner-breach",
    canonical: "host",
    screenshot: "/shots/game-0324-banner-breach.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0324 banner breach, a small original browser game.",
  },
  // River Rivets. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0325-river-rivets.
  {
    slug: "game-0325-river-rivets",
    surface: "app",
    title: "River Rivets",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0325-river-rivets",
    canonical: "host",
    screenshot: "/shots/game-0325-river-rivets.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0325 river rivets, a small original browser game.",
  },
  // Freight Dividend. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0326-freight-dividend.
  {
    slug: "game-0326-freight-dividend",
    surface: "app",
    title: "Freight Dividend",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0326-freight-dividend",
    canonical: "host",
    screenshot: "/shots/game-0326-freight-dividend.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0326 freight dividend, a small original browser game.",
  },
  // Counterweight Grove. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0327-counterweight-grove.
  {
    slug: "game-0327-counterweight-grove",
    surface: "app",
    title: "Counterweight Grove",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0327-counterweight-grove",
    canonical: "host",
    screenshot: "/shots/game-0327-counterweight-grove.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0327 counterweight grove, a small original browser game.",
  },
  // Ghost Notation. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0328-ghost-notation.
  {
    slug: "game-0328-ghost-notation",
    surface: "app",
    title: "Ghost Notation",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0328-ghost-notation",
    canonical: "host",
    screenshot: "/shots/game-0328-ghost-notation.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0328 ghost notation, a small original browser game.",
  },
  // Cylinder Loom. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0329-cylinder-loom.
  {
    slug: "game-0329-cylinder-loom",
    surface: "app",
    title: "Cylinder Loom",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0329-cylinder-loom",
    canonical: "host",
    screenshot: "/shots/game-0329-cylinder-loom.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0329 cylinder loom, a small original browser game.",
  },
  // Reserve Stack. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0330-reserve-stack.
  {
    slug: "game-0330-reserve-stack",
    surface: "app",
    title: "Reserve Stack",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0330-reserve-stack",
    canonical: "host",
    screenshot: "/shots/game-0330-reserve-stack.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0330 reserve stack, a small original browser game.",
  },
  // Prism Zap. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0331-prism-zap.
  {
    slug: "game-0331-prism-zap",
    surface: "app",
    title: "Prism Zap",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0331-prism-zap",
    canonical: "host",
    screenshot: "/shots/game-0331-prism-zap.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0331 prism zap, a small original browser game.",
  },
  // Resolve Garden. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0332-resolve-garden.
  {
    slug: "game-0332-resolve-garden",
    surface: "app",
    title: "Resolve Garden",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0332-resolve-garden",
    canonical: "host",
    screenshot: "/shots/game-0332-resolve-garden.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0332 resolve garden, a small original browser game.",
  },
  // Microflat Movers. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0333-microflat-movers.
  {
    slug: "game-0333-microflat-movers",
    surface: "app",
    title: "Microflat Movers",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0333-microflat-movers",
    canonical: "host",
    screenshot: "/shots/game-0333-microflat-movers.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0333 microflat movers, a small original browser game.",
  },
  // Upright Archive. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0334-upright-archive.
  {
    slug: "game-0334-upright-archive",
    surface: "app",
    title: "Upright Archive",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0334-upright-archive",
    canonical: "host",
    screenshot: "/shots/game-0334-upright-archive.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0334 upright archive, a small original browser game.",
  },
  // Frost Pond. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0335-frost-pond.
  {
    slug: "game-0335-frost-pond",
    surface: "app",
    title: "Frost Pond",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0335-frost-pond",
    canonical: "host",
    screenshot: "/shots/game-0335-frost-pond.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0335 frost pond, a small original browser game.",
  },
  // Summit Reversal. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0336-summit-reversal.
  {
    slug: "game-0336-summit-reversal",
    surface: "app",
    title: "Summit Reversal",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0336-summit-reversal",
    canonical: "host",
    screenshot: "/shots/game-0336-summit-reversal.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0336 summit reversal, a small original browser game.",
  },
  // Rebound Bastion. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0337-rebound-bastion.
  {
    slug: "game-0337-rebound-bastion",
    surface: "app",
    title: "Rebound Bastion",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0337-rebound-bastion",
    canonical: "host",
    screenshot: "/shots/game-0337-rebound-bastion.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0337 rebound bastion, a small original browser game.",
  },
  // Core Halo. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0338-core-halo.
  {
    slug: "game-0338-core-halo",
    surface: "app",
    title: "Core Halo",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0338-core-halo",
    canonical: "host",
    screenshot: "/shots/game-0338-core-halo.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0338 core halo, a small original browser game.",
  },
  // Core Artillery. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0339-core-artillery.
  {
    slug: "game-0339-core-artillery",
    surface: "app",
    title: "Core Artillery",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0339-core-artillery",
    canonical: "host",
    screenshot: "/shots/game-0339-core-artillery.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0339 core artillery, a small original browser game.",
  },
  // Twinlane Courier. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0340-twinlane-courier.
  {
    slug: "game-0340-twinlane-courier",
    surface: "app",
    title: "Twinlane Courier",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0340-twinlane-courier",
    canonical: "host",
    screenshot: "/shots/game-0340-twinlane-courier.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0340 twinlane courier, a small original browser game.",
  },
  // Fourwind Mosaic. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0341-fourwind-mosaic.
  {
    slug: "game-0341-fourwind-mosaic",
    surface: "app",
    title: "Fourwind Mosaic",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0341-fourwind-mosaic",
    canonical: "host",
    screenshot: "/shots/game-0341-fourwind-mosaic.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0341 fourwind mosaic, a small original browser game.",
  },
  // Gravity Club. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0342-gravity-club.
  {
    slug: "game-0342-gravity-club",
    surface: "app",
    title: "Gravity Club",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0342-gravity-club",
    canonical: "host",
    screenshot: "/shots/game-0342-gravity-club.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0342 gravity club, a small original browser game.",
  },
  // Hidden Margin. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0343-hidden-margin.
  {
    slug: "game-0343-hidden-margin",
    surface: "app",
    title: "Hidden Margin",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0343-hidden-margin",
    canonical: "host",
    screenshot: "/shots/game-0343-hidden-margin.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0343 hidden margin, a small original browser game.",
  },
  // Furniture Skylift. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0344-furniture-skylift.
  {
    slug: "game-0344-furniture-skylift",
    surface: "app",
    title: "Furniture Skylift",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0344-furniture-skylift",
    canonical: "host",
    screenshot: "/shots/game-0344-furniture-skylift.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0344 furniture skylift, a small original browser game.",
  },
  // Wishbrick Homes. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0345-wishbrick-homes.
  {
    slug: "game-0345-wishbrick-homes",
    surface: "app",
    title: "Wishbrick Homes",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0345-wishbrick-homes",
    canonical: "host",
    screenshot: "/shots/game-0345-wishbrick-homes.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0345 wishbrick homes, a small original browser game.",
  },
  // Rust Digger. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0346-rust-digger.
  {
    slug: "game-0346-rust-digger",
    surface: "app",
    title: "Rust Digger",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0346-rust-digger",
    canonical: "host",
    screenshot: "/shots/game-0346-rust-digger.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0346 rust digger, a small original browser game.",
  },
  // Grainfall Primer. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0347-grainfall-primer.
  {
    slug: "game-0347-grainfall-primer",
    surface: "app",
    title: "Grainfall Primer",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0347-grainfall-primer",
    canonical: "host",
    screenshot: "/shots/game-0347-grainfall-primer.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0347 grainfall primer, a small original browser game.",
  },
  // Spore Quadrants. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0348-spore-quadrants.
  {
    slug: "game-0348-spore-quadrants",
    surface: "app",
    title: "Spore Quadrants",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0348-spore-quadrants",
    canonical: "host",
    screenshot: "/shots/game-0348-spore-quadrants.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0348 spore quadrants, a small original browser game.",
  },
  // Summoner Step. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0349-summoner-step.
  {
    slug: "game-0349-summoner-step",
    surface: "app",
    title: "Summoner Step",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0349-summoner-step",
    canonical: "host",
    screenshot: "/shots/game-0349-summoner-step.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0349 summoner step, a small original browser game.",
  },
  // Brew Orbit. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0350-brew-orbit.
  {
    slug: "game-0350-brew-orbit",
    surface: "app",
    title: "Brew Orbit",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0350-brew-orbit",
    canonical: "host",
    screenshot: "/shots/game-0350-brew-orbit.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0350 brew orbit, a small original browser game.",
  },
  // Oddpiece Foundry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0351-oddpiece-foundry.
  {
    slug: "game-0351-oddpiece-foundry",
    surface: "app",
    title: "Oddpiece Foundry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0351-oddpiece-foundry",
    canonical: "host",
    screenshot: "/shots/game-0351-oddpiece-foundry.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0351 oddpiece foundry, a small original browser game.",
  },
  // Corner Aperture. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0352-corner-aperture.
  {
    slug: "game-0352-corner-aperture",
    surface: "app",
    title: "Corner Aperture",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0352-corner-aperture",
    canonical: "host",
    screenshot: "/shots/game-0352-corner-aperture.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0352 corner aperture, a small original browser game.",
  },
  // Shape Sprites. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0353-shape-sprites.
  {
    slug: "game-0353-shape-sprites",
    surface: "app",
    title: "Shape Sprites",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0353-shape-sprites",
    canonical: "host",
    screenshot: "/shots/game-0353-shape-sprites.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0353 shape sprites, a small original browser game.",
  },
  // Relic Duel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0354-relic-duel.
  {
    slug: "game-0354-relic-duel",
    surface: "app",
    title: "Relic Duel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0354-relic-duel",
    canonical: "host",
    screenshot: "/shots/game-0354-relic-duel.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0354 relic duel, a small original browser game.",
  },
  // Glyph Trek. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0355-glyph-trek.
  {
    slug: "game-0355-glyph-trek",
    surface: "app",
    title: "Glyph Trek",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0355-glyph-trek",
    canonical: "host",
    screenshot: "/shots/game-0355-glyph-trek.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0355 glyph trek, a small original browser game.",
  },
  // Ceiling Blaster. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0356-ceiling-blaster.
  {
    slug: "game-0356-ceiling-blaster",
    surface: "app",
    title: "Ceiling Blaster",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0356-ceiling-blaster",
    canonical: "host",
    screenshot: "/shots/game-0356-ceiling-blaster.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0356 ceiling blaster, a small original browser game.",
  },
  // Fuse Arithmetic. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0357-fuse-arithmetic.
  {
    slug: "game-0357-fuse-arithmetic",
    surface: "app",
    title: "Fuse Arithmetic",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0357-fuse-arithmetic",
    canonical: "host",
    screenshot: "/shots/game-0357-fuse-arithmetic.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0357 fuse arithmetic, a small original browser game.",
  },
  // Hamlet Slide. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0358-hamlet-slide.
  {
    slug: "game-0358-hamlet-slide",
    surface: "app",
    title: "Hamlet Slide",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0358-hamlet-slide",
    canonical: "host",
    screenshot: "/shots/game-0358-hamlet-slide.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0358 hamlet slide, a small original browser game.",
  },
  // Whisker Gravity. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0359-whisker-gravity.
  {
    slug: "game-0359-whisker-gravity",
    surface: "app",
    title: "Whisker Gravity",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0359-whisker-gravity",
    canonical: "host",
    screenshot: "/shots/game-0359-whisker-gravity.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0359 whisker gravity, a small original browser game.",
  },
  // Twelvefold Yard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0401-twelvefold-yard.
  {
    slug: "game-0401-twelvefold-yard",
    surface: "app",
    title: "Twelvefold Yard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0401-twelvefold-yard",
    canonical: "host",
    screenshot: "/shots/game-0401-twelvefold-yard.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0401 twelvefold yard, a small original browser game.",
  },
  // Orchard Dispatch. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0402-orchard-dispatch.
  {
    slug: "game-0402-orchard-dispatch",
    surface: "app",
    title: "Orchard Dispatch",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0402-orchard-dispatch",
    canonical: "host",
    screenshot: "/shots/game-0402-orchard-dispatch.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0402 orchard dispatch, a small original browser game.",
  },
  // Isotope Orders. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0403-isotope-orders.
  {
    slug: "game-0403-isotope-orders",
    surface: "app",
    title: "Isotope Orders",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0403-isotope-orders",
    canonical: "host",
    screenshot: "/shots/game-0403-isotope-orders.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0403 isotope orders, a small original browser game.",
  },
  // Quiet Dividend. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0404-quiet-dividend.
  {
    slug: "game-0404-quiet-dividend",
    surface: "app",
    title: "Quiet Dividend",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0404-quiet-dividend",
    canonical: "host",
    screenshot: "/shots/game-0404-quiet-dividend.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0404 quiet dividend, a small original browser game.",
  },
  // Gelatin Sentinel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0405-gelatin-sentinel.
  {
    slug: "game-0405-gelatin-sentinel",
    surface: "app",
    title: "Gelatin Sentinel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0405-gelatin-sentinel",
    canonical: "host",
    screenshot: "/shots/game-0405-gelatin-sentinel.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0405 gelatin sentinel, a small original browser game.",
  },
  // Fauna Second Chance. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0406-fauna-second-chance.
  {
    slug: "game-0406-fauna-second-chance",
    surface: "app",
    title: "Fauna Second Chance",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0406-fauna-second-chance",
    canonical: "host",
    screenshot: "/shots/game-0406-fauna-second-chance.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0406 fauna second chance, a small original browser game.",
  },
  // Socket Foundry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0407-socket-foundry.
  {
    slug: "game-0407-socket-foundry",
    surface: "app",
    title: "Socket Foundry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0407-socket-foundry",
    canonical: "host",
    screenshot: "/shots/game-0407-socket-foundry.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0407 socket foundry, a small original browser game.",
  },
  // Gyre Numbers. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0408-gyre-numbers.
  {
    slug: "game-0408-gyre-numbers",
    surface: "app",
    title: "Gyre Numbers",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0408-gyre-numbers",
    canonical: "host",
    screenshot: "/shots/game-0408-gyre-numbers.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0408 gyre numbers, a small original browser game.",
  },
  // Chromatic Rain. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0409-chromatic-rain.
  {
    slug: "game-0409-chromatic-rain",
    surface: "app",
    title: "Chromatic Rain",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0409-chromatic-rain",
    canonical: "host",
    screenshot: "/shots/game-0409-chromatic-rain.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0409 chromatic rain, a small original browser game.",
  },
  // Longway Beacon. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0410-longway-beacon.
  {
    slug: "game-0410-longway-beacon",
    surface: "app",
    title: "Longway Beacon",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0410-longway-beacon",
    canonical: "host",
    screenshot: "/shots/game-0410-longway-beacon.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0410 longway beacon, a small original browser game.",
  },
  // Harvest Roll. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0411-harvest-roll.
  {
    slug: "game-0411-harvest-roll",
    surface: "app",
    title: "Harvest Roll",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0411-harvest-roll",
    canonical: "host",
    screenshot: "/shots/game-0411-harvest-roll.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0411 harvest roll, a small original browser game.",
  },
  // Antistar Assembly. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0412-antistar-assembly.
  {
    slug: "game-0412-antistar-assembly",
    surface: "app",
    title: "Antistar Assembly",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0412-antistar-assembly",
    canonical: "host",
    screenshot: "/shots/game-0412-antistar-assembly.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0412 antistar assembly, a small original browser game.",
  },
  // Thirteen Burden. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0413-thirteen-burden.
  {
    slug: "game-0413-thirteen-burden",
    surface: "app",
    title: "Thirteen Burden",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0413-thirteen-burden",
    canonical: "host",
    screenshot: "/shots/game-0413-thirteen-burden.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0413 thirteen burden, a small original browser game.",
  },
  // Crosswind Locks. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0414-crosswind-locks.
  {
    slug: "game-0414-crosswind-locks",
    surface: "app",
    title: "Crosswind Locks",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0414-crosswind-locks",
    canonical: "host",
    screenshot: "/shots/game-0414-crosswind-locks.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0414 crosswind locks, a small original browser game.",
  },
  // Relic Wayfarer. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0415-relic-wayfarer.
  {
    slug: "game-0415-relic-wayfarer",
    surface: "app",
    title: "Relic Wayfarer",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0415-relic-wayfarer",
    canonical: "host",
    screenshot: "/shots/game-0415-relic-wayfarer.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0415 relic wayfarer, a small original browser game.",
  },
  // Kindred Landscape. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0416-kindred-landscape.
  {
    slug: "game-0416-kindred-landscape",
    surface: "app",
    title: "Kindred Landscape",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0416-kindred-landscape",
    canonical: "host",
    screenshot: "/shots/game-0416-kindred-landscape.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0416 kindred landscape, a small original browser game.",
  },
  // Nova Basin. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0417-nova-basin.
  {
    slug: "game-0417-nova-basin",
    surface: "app",
    title: "Nova Basin",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0417-nova-basin",
    canonical: "host",
    screenshot: "/shots/game-0417-nova-basin.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0417 nova basin, a small original browser game.",
  },
  // Elastic Steps. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0418-elastic-steps.
  {
    slug: "game-0418-elastic-steps",
    surface: "app",
    title: "Elastic Steps",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0418-elastic-steps",
    canonical: "host",
    screenshot: "/shots/game-0418-elastic-steps.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0418 elastic steps, a small original browser game.",
  },
  // Squad Corner. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0419-squad-corner.
  {
    slug: "game-0419-squad-corner",
    surface: "app",
    title: "Squad Corner",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0419-squad-corner",
    canonical: "host",
    screenshot: "/shots/game-0419-squad-corner.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0419 squad corner, a small original browser game.",
  },
  // Delta Tiers. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0420-delta-tiers.
  {
    slug: "game-0420-delta-tiers",
    surface: "app",
    title: "Delta Tiers",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0420-delta-tiers",
    canonical: "host",
    screenshot: "/shots/game-0420-delta-tiers.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0420 delta tiers, a small original browser game.",
  },
  // Random Rampart. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0421-random-rampart.
  {
    slug: "game-0421-random-rampart",
    surface: "app",
    title: "Random Rampart",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0421-random-rampart",
    canonical: "host",
    screenshot: "/shots/game-0421-random-rampart.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0421 random rampart, a small original browser game.",
  },
  // Rainbow Culture. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0422-rainbow-culture.
  {
    slug: "game-0422-rainbow-culture",
    surface: "app",
    title: "Rainbow Culture",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0422-rainbow-culture",
    canonical: "host",
    screenshot: "/shots/game-0422-rainbow-culture.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0422 rainbow culture, a small original browser game.",
  },
  // Freeblock Expedition. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0423-freeblock-expedition.
  {
    slug: "game-0423-freeblock-expedition",
    surface: "app",
    title: "Freeblock Expedition",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0423-freeblock-expedition",
    canonical: "host",
    screenshot: "/shots/game-0423-freeblock-expedition.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0423 freeblock expedition, a small original browser game.",
  },
  // Island Dividend. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0424-island-dividend.
  {
    slug: "game-0424-island-dividend",
    surface: "app",
    title: "Island Dividend",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0424-island-dividend",
    canonical: "host",
    screenshot: "/shots/game-0424-island-dividend.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0424 island dividend, a small original browser game.",
  },
  // Vanishing Quotients. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0425-vanishing-quotients.
  {
    slug: "game-0425-vanishing-quotients",
    surface: "app",
    title: "Vanishing Quotients",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0425-vanishing-quotients",
    canonical: "host",
    screenshot: "/shots/game-0425-vanishing-quotients.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0425 vanishing quotients, a small original browser game.",
  },
  // Squish Reserve. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0426-squish-reserve.
  {
    slug: "game-0426-squish-reserve",
    surface: "app",
    title: "Squish Reserve",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0426-squish-reserve",
    canonical: "host",
    screenshot: "/shots/game-0426-squish-reserve.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0426 squish reserve, a small original browser game.",
  },
  // Runic Countdown. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0427-runic-countdown.
  {
    slug: "game-0427-runic-countdown",
    surface: "app",
    title: "Runic Countdown",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0427-runic-countdown",
    canonical: "host",
    screenshot: "/shots/game-0427-runic-countdown.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0427 runic countdown, a small original browser game.",
  },
  // Deepfall Cascade. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0428-deepfall-cascade.
  {
    slug: "game-0428-deepfall-cascade",
    surface: "app",
    title: "Deepfall Cascade",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0428-deepfall-cascade",
    canonical: "host",
    screenshot: "/shots/game-0428-deepfall-cascade.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0428 deepfall cascade, a small original browser game.",
  },
  // Dewglass Recipes. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0429-dewglass-recipes.
  {
    slug: "game-0429-dewglass-recipes",
    surface: "app",
    title: "Dewglass Recipes",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0429-dewglass-recipes",
    canonical: "host",
    screenshot: "/shots/game-0429-dewglass-recipes.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0429 dewglass recipes, a small original browser game.",
  },
  // Rowbound Works. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0430-rowbound-works.
  {
    slug: "game-0430-rowbound-works",
    surface: "app",
    title: "Rowbound Works",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0430-rowbound-works",
    canonical: "host",
    screenshot: "/shots/game-0430-rowbound-works.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0430 rowbound works, a small original browser game.",
  },
  // Corelight Keep. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0431-corelight-keep.
  {
    slug: "game-0431-corelight-keep",
    surface: "app",
    title: "Corelight Keep",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0431-corelight-keep",
    canonical: "host",
    screenshot: "/shots/game-0431-corelight-keep.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0431 corelight keep, a small original browser game.",
  },
  // Raised Causeway. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0432-raised-causeway.
  {
    slug: "game-0432-raised-causeway",
    surface: "app",
    title: "Raised Causeway",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0432-raised-causeway",
    canonical: "host",
    screenshot: "/shots/game-0432-raised-causeway.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0432 raised causeway, a small original browser game.",
  },
  // Ember Inventory. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0433-ember-inventory.
  {
    slug: "game-0433-ember-inventory",
    surface: "app",
    title: "Ember Inventory",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0433-ember-inventory",
    canonical: "host",
    screenshot: "/shots/game-0433-ember-inventory.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0433 ember inventory, a small original browser game.",
  },
  // Cube Ricochet. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0434-cube-ricochet.
  {
    slug: "game-0434-cube-ricochet",
    surface: "app",
    title: "Cube Ricochet",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0434-cube-ricochet",
    canonical: "host",
    screenshot: "/shots/game-0434-cube-ricochet.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0434 cube ricochet, a small original browser game.",
  },
  // Pounce Clock. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0435-pounce-clock.
  {
    slug: "game-0435-pounce-clock",
    surface: "app",
    title: "Pounce Clock",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0435-pounce-clock",
    canonical: "host",
    screenshot: "/shots/game-0435-pounce-clock.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0435 pounce clock, a small original browser game.",
  },
  // Rival Signal. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0436-rival-signal.
  {
    slug: "game-0436-rival-signal",
    surface: "app",
    title: "Rival Signal",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0436-rival-signal",
    canonical: "host",
    screenshot: "/shots/game-0436-rival-signal.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0436 rival signal, a small original browser game.",
  },
  // Power Cadence. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0437-power-cadence.
  {
    slug: "game-0437-power-cadence",
    surface: "app",
    title: "Power Cadence",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0437-power-cadence",
    canonical: "host",
    screenshot: "/shots/game-0437-power-cadence.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0437 power cadence, a small original browser game.",
  },
  // Evergreen Relay. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0438-evergreen-relay.
  {
    slug: "game-0438-evergreen-relay",
    surface: "app",
    title: "Evergreen Relay",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0438-evergreen-relay",
    canonical: "host",
    screenshot: "/shots/game-0438-evergreen-relay.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0438 evergreen relay, a small original browser game.",
  },
  // Logarithm Loft. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0439-logarithm-loft.
  {
    slug: "game-0439-logarithm-loft",
    surface: "app",
    title: "Logarithm Loft",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0439-logarithm-loft",
    canonical: "host",
    screenshot: "/shots/game-0439-logarithm-loft.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0439 logarithm loft, a small original browser game.",
  },
  // Facet Atelier. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0440-facet-atelier.
  {
    slug: "game-0440-facet-atelier",
    surface: "app",
    title: "Facet Atelier",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0440-facet-atelier",
    canonical: "host",
    screenshot: "/shots/game-0440-facet-atelier.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0440 facet atelier, a small original browser game.",
  },
  // Cedar Circuit. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0441-cedar-circuit.
  {
    slug: "game-0441-cedar-circuit",
    surface: "app",
    title: "Cedar Circuit",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0441-cedar-circuit",
    canonical: "host",
    screenshot: "/shots/game-0441-cedar-circuit.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0441 cedar circuit, a small original browser game.",
  },
  // Dusk Battalion. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0442-dusk-battalion.
  {
    slug: "game-0442-dusk-battalion",
    surface: "app",
    title: "Dusk Battalion",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0442-dusk-battalion",
    canonical: "host",
    screenshot: "/shots/game-0442-dusk-battalion.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0442 dusk battalion, a small original browser game.",
  },
  // Copper Kettle Menu. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0443-copper-kettle-menu.
  {
    slug: "game-0443-copper-kettle-menu",
    surface: "app",
    title: "Copper Kettle Menu",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0443-copper-kettle-menu",
    canonical: "host",
    screenshot: "/shots/game-0443-copper-kettle-menu.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0443 copper kettle menu, a small original browser game.",
  },
  // Element Stair. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0444-element-stair.
  {
    slug: "game-0444-element-stair",
    surface: "app",
    title: "Element Stair",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0444-element-stair",
    canonical: "host",
    screenshot: "/shots/game-0444-element-stair.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0444 element stair, a small original browser game.",
  },
  // Spiral Ledger. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0445-spiral-ledger.
  {
    slug: "game-0445-spiral-ledger",
    surface: "app",
    title: "Spiral Ledger",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0445-spiral-ledger",
    canonical: "host",
    screenshot: "/shots/game-0445-spiral-ledger.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0445 spiral ledger, a small original browser game.",
  },
  // Triangle Trail. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0446-triangle-trail.
  {
    slug: "game-0446-triangle-trail",
    surface: "app",
    title: "Triangle Trail",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0446-triangle-trail",
    canonical: "host",
    screenshot: "/shots/game-0446-triangle-trail.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0446 triangle trail, a small original browser game.",
  },
  // Alphabet Loom. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0447-alphabet-loom.
  {
    slug: "game-0447-alphabet-loom",
    surface: "app",
    title: "Alphabet Loom",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0447-alphabet-loom",
    canonical: "host",
    screenshot: "/shots/game-0447-alphabet-loom.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0447 alphabet loom, a small original browser game.",
  },
  // Salvage Square. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0448-salvage-square.
  {
    slug: "game-0448-salvage-square",
    surface: "app",
    title: "Salvage Square",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0448-salvage-square",
    canonical: "host",
    screenshot: "/shots/game-0448-salvage-square.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0448 salvage square, a small original browser game.",
  },
  // Stone Logic. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0449-stone-logic.
  {
    slug: "game-0449-stone-logic",
    surface: "app",
    title: "Stone Logic",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0449-stone-logic",
    canonical: "host",
    screenshot: "/shots/game-0449-stone-logic.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0449 stone logic, a small original browser game.",
  },
  // Chorus Blocks. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0450-chorus-blocks.
  {
    slug: "game-0450-chorus-blocks",
    surface: "app",
    title: "Chorus Blocks",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0450-chorus-blocks",
    canonical: "host",
    screenshot: "/shots/game-0450-chorus-blocks.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0450 chorus blocks, a small original browser game.",
  },
  // Inkstep Garden. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0451-inkstep-garden.
  {
    slug: "game-0451-inkstep-garden",
    surface: "app",
    title: "Inkstep Garden",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0451-inkstep-garden",
    canonical: "host",
    screenshot: "/shots/game-0451-inkstep-garden.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0451 inkstep garden, a small original browser game.",
  },
  // Sticky Skyline. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0452-sticky-skyline.
  {
    slug: "game-0452-sticky-skyline",
    surface: "app",
    title: "Sticky Skyline",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0452-sticky-skyline",
    canonical: "host",
    screenshot: "/shots/game-0452-sticky-skyline.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0452 sticky skyline, a small original browser game.",
  },
  // Orchard Vessel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0453-orchard-vessel.
  {
    slug: "game-0453-orchard-vessel",
    surface: "app",
    title: "Orchard Vessel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0453-orchard-vessel",
    canonical: "host",
    screenshot: "/shots/game-0453-orchard-vessel.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0453 orchard vessel, a small original browser game.",
  },
  // Honest Alloy. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0454-honest-alloy.
  {
    slug: "game-0454-honest-alloy",
    surface: "app",
    title: "Honest Alloy",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0454-honest-alloy",
    canonical: "host",
    screenshot: "/shots/game-0454-honest-alloy.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0454 honest alloy, a small original browser game.",
  },
  // Rune Skirmish. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0455-rune-skirmish.
  {
    slug: "game-0455-rune-skirmish",
    surface: "app",
    title: "Rune Skirmish",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0455-rune-skirmish",
    canonical: "host",
    screenshot: "/shots/game-0455-rune-skirmish.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0455 rune skirmish, a small original browser game.",
  },
  // Hollow Keep. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0456-hollow-keep.
  {
    slug: "game-0456-hollow-keep",
    surface: "app",
    title: "Hollow Keep",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0456-hollow-keep",
    canonical: "host",
    screenshot: "/shots/game-0456-hollow-keep.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0456 hollow keep, a small original browser game.",
  },
  // Cube Workshop. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0457-cube-workshop.
  {
    slug: "game-0457-cube-workshop",
    surface: "app",
    title: "Cube Workshop",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0457-cube-workshop",
    canonical: "host",
    screenshot: "/shots/game-0457-cube-workshop.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0457 cube workshop, a small original browser game.",
  },
  // Growing Stacks. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0458-growing-stacks.
  {
    slug: "game-0458-growing-stacks",
    surface: "app",
    title: "Growing Stacks",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0458-growing-stacks",
    canonical: "host",
    screenshot: "/shots/game-0458-growing-stacks.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0458 growing stacks, a small original browser game.",
  },
  // Prism Basin. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0459-prism-basin.
  {
    slug: "game-0459-prism-basin",
    surface: "app",
    title: "Prism Basin",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0459-prism-basin",
    canonical: "host",
    screenshot: "/shots/game-0459-prism-basin.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0459 prism basin, a small original browser game.",
  },
  // Falling Quartet. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0460-falling-quartet.
  {
    slug: "game-0460-falling-quartet",
    surface: "app",
    title: "Falling Quartet",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0460-falling-quartet",
    canonical: "host",
    screenshot: "/shots/game-0460-falling-quartet.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0460 falling quartet, a small original browser game.",
  },
  // Brick Rewind. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0461-brick-rewind.
  {
    slug: "game-0461-brick-rewind",
    surface: "app",
    title: "Brick Rewind",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0461-brick-rewind",
    canonical: "host",
    screenshot: "/shots/game-0461-brick-rewind.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0461 brick rewind, a small original browser game.",
  },
  // Signed Foundry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0462-signed-foundry.
  {
    slug: "game-0462-signed-foundry",
    surface: "app",
    title: "Signed Foundry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0462-signed-foundry",
    canonical: "host",
    screenshot: "/shots/game-0462-signed-foundry.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0462 signed foundry, a small original browser game.",
  },
  // Deck of Embers. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0463-deck-of-embers.
  {
    slug: "game-0463-deck-of-embers",
    surface: "app",
    title: "Deck of Embers",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0463-deck-of-embers",
    canonical: "host",
    screenshot: "/shots/game-0463-deck-of-embers.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0463 deck of embers, a small original browser game.",
  },
  // Isotope Archive. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0464-isotope-archive.
  {
    slug: "game-0464-isotope-archive",
    surface: "app",
    title: "Isotope Archive",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0464-isotope-archive",
    canonical: "host",
    screenshot: "/shots/game-0464-isotope-archive.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0464 isotope archive, a small original browser game.",
  },
  // Twin Corner Ledger. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0465-twin-corner-ledger.
  {
    slug: "game-0465-twin-corner-ledger",
    surface: "app",
    title: "Twin Corner Ledger",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0465-twin-corner-ledger",
    canonical: "host",
    screenshot: "/shots/game-0465-twin-corner-ledger.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0465 twin corner ledger, a small original browser game.",
  },
  // Rose Balance. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0466-rose-balance.
  {
    slug: "game-0466-rose-balance",
    surface: "app",
    title: "Rose Balance",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0466-rose-balance",
    canonical: "host",
    screenshot: "/shots/game-0466-rose-balance.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0466 rose balance, a small original browser game.",
  },
  // Recall Crossing. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0467-recall-crossing.
  {
    slug: "game-0467-recall-crossing",
    surface: "app",
    title: "Recall Crossing",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0467-recall-crossing",
    canonical: "host",
    screenshot: "/shots/game-0467-recall-crossing.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0467 recall crossing, a small original browser game.",
  },
  // Nightwing Keys. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0468-nightwing-keys.
  {
    slug: "game-0468-nightwing-keys",
    surface: "app",
    title: "Nightwing Keys",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0468-nightwing-keys",
    canonical: "host",
    screenshot: "/shots/game-0468-nightwing-keys.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0468 nightwing keys, a small original browser game.",
  },
  // Jewel Treasury. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0469-jewel-treasury.
  {
    slug: "game-0469-jewel-treasury",
    surface: "app",
    title: "Jewel Treasury",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0469-jewel-treasury",
    canonical: "host",
    screenshot: "/shots/game-0469-jewel-treasury.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0469 jewel treasury, a small original browser game.",
  },
  // Curator Elements. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0470-curator-elements.
  {
    slug: "game-0470-curator-elements",
    surface: "app",
    title: "Curator Elements",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0470-curator-elements",
    canonical: "host",
    screenshot: "/shots/game-0470-curator-elements.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0470 curator elements, a small original browser game.",
  },
  // Harbor Ranks. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0471-harbor-ranks.
  {
    slug: "game-0471-harbor-ranks",
    surface: "app",
    title: "Harbor Ranks",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0471-harbor-ranks",
    canonical: "host",
    screenshot: "/shots/game-0471-harbor-ranks.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0471 harbor ranks, a small original browser game.",
  },
  // Pocket Hearth. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0472-pocket-hearth.
  {
    slug: "game-0472-pocket-hearth",
    surface: "app",
    title: "Pocket Hearth",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0472-pocket-hearth",
    canonical: "host",
    screenshot: "/shots/game-0472-pocket-hearth.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0472 pocket hearth, a small original browser game.",
  },
  // Shieldline Duel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0473-shieldline-duel.
  {
    slug: "game-0473-shieldline-duel",
    surface: "app",
    title: "Shieldline Duel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0473-shieldline-duel",
    canonical: "host",
    screenshot: "/shots/game-0473-shieldline-duel.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0473 shieldline duel, a small original browser game.",
  },
  // Triple Halo. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0474-triple-halo.
  {
    slug: "game-0474-triple-halo",
    surface: "app",
    title: "Triple Halo",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0474-triple-halo",
    canonical: "host",
    screenshot: "/shots/game-0474-triple-halo.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0474 triple halo, a small original browser game.",
  },
  // Borderworks. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0475-borderworks.
  {
    slug: "game-0475-borderworks",
    surface: "app",
    title: "Borderworks",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0475-borderworks",
    canonical: "host",
    screenshot: "/shots/game-0475-borderworks.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0475 borderworks, a small original browser game.",
  },
  // Chain Lanterns. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0476-chain-lanterns.
  {
    slug: "game-0476-chain-lanterns",
    surface: "app",
    title: "Chain Lanterns",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0476-chain-lanterns",
    canonical: "host",
    screenshot: "/shots/game-0476-chain-lanterns.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0476 chain lanterns, a small original browser game.",
  },
  // Pulse Pairs. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0477-pulse-pairs.
  {
    slug: "game-0477-pulse-pairs",
    surface: "app",
    title: "Pulse Pairs",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0477-pulse-pairs",
    canonical: "host",
    screenshot: "/shots/game-0477-pulse-pairs.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0477 pulse pairs, a small original browser game.",
  },
  // Meadow Ladder. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0478-meadow-ladder.
  {
    slug: "game-0478-meadow-ladder",
    surface: "app",
    title: "Meadow Ladder",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0478-meadow-ladder",
    canonical: "host",
    screenshot: "/shots/game-0478-meadow-ladder.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0478 meadow ladder, a small original browser game.",
  },
  // Dockside Numbers. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0479-dockside-numbers.
  {
    slug: "game-0479-dockside-numbers",
    surface: "app",
    title: "Dockside Numbers",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0479-dockside-numbers",
    canonical: "host",
    screenshot: "/shots/game-0479-dockside-numbers.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0479 dockside numbers, a small original browser game.",
  },
  // Crate Budget. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0480-crate-budget.
  {
    slug: "game-0480-crate-budget",
    surface: "app",
    title: "Crate Budget",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0480-crate-budget",
    canonical: "host",
    screenshot: "/shots/game-0480-crate-budget.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0480 crate budget, a small original browser game.",
  },
  // Prism Rampart. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0481-prism-rampart.
  {
    slug: "game-0481-prism-rampart",
    surface: "app",
    title: "Prism Rampart",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0481-prism-rampart",
    canonical: "host",
    screenshot: "/shots/game-0481-prism-rampart.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0481 prism rampart, a small original browser game.",
  },
  // Triad Foundry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0482-triad-foundry.
  {
    slug: "game-0482-triad-foundry",
    surface: "app",
    title: "Triad Foundry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0482-triad-foundry",
    canonical: "host",
    screenshot: "/shots/game-0482-triad-foundry.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0482 triad foundry, a small original browser game.",
  },
  // Nimbus Numberfall. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0483-nimbus-numberfall.
  {
    slug: "game-0483-nimbus-numberfall",
    surface: "app",
    title: "Nimbus Numberfall",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0483-nimbus-numberfall",
    canonical: "host",
    screenshot: "/shots/game-0483-nimbus-numberfall.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0483 nimbus numberfall, a small original browser game.",
  },
  // Honeycell Accord. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0484-honeycell-accord.
  {
    slug: "game-0484-honeycell-accord",
    surface: "app",
    title: "Honeycell Accord",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0484-honeycell-accord",
    canonical: "host",
    screenshot: "/shots/game-0484-honeycell-accord.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0484 honeycell accord, a small original browser game.",
  },
  // Clockwork Thirtytwo. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0485-clockwork-thirtytwo.
  {
    slug: "game-0485-clockwork-thirtytwo",
    surface: "app",
    title: "Clockwork Thirtytwo",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0485-clockwork-thirtytwo",
    canonical: "host",
    screenshot: "/shots/game-0485-clockwork-thirtytwo.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0485 clockwork thirtytwo, a small original browser game.",
  },
  // Signal Noodles. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0486-signal-noodles.
  {
    slug: "game-0486-signal-noodles",
    surface: "app",
    title: "Signal Noodles",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0486-signal-noodles",
    canonical: "host",
    screenshot: "/shots/game-0486-signal-noodles.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0486 signal noodles, a small original browser game.",
  },
  // Sevenfold Hearth. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0487-sevenfold-hearth.
  {
    slug: "game-0487-sevenfold-hearth",
    surface: "app",
    title: "Sevenfold Hearth",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0487-sevenfold-hearth",
    canonical: "host",
    screenshot: "/shots/game-0487-sevenfold-hearth.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0487 sevenfold hearth, a small original browser game.",
  },
  // Cabinet of Creatures. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0488-cabinet-of-creatures.
  {
    slug: "game-0488-cabinet-of-creatures",
    surface: "app",
    title: "Cabinet of Creatures",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0488-cabinet-of-creatures",
    canonical: "host",
    screenshot: "/shots/game-0488-cabinet-of-creatures.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0488 cabinet of creatures, a small original browser game.",
  },
  // Last Star Orchard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0489-last-star-orchard.
  {
    slug: "game-0489-last-star-orchard",
    surface: "app",
    title: "Last Star Orchard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0489-last-star-orchard",
    canonical: "host",
    screenshot: "/shots/game-0489-last-star-orchard.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0489 last star orchard, a small original browser game.",
  },
  // River Sum. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0490-river-sum.
  {
    slug: "game-0490-river-sum",
    surface: "app",
    title: "River Sum",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0490-river-sum",
    canonical: "host",
    screenshot: "/shots/game-0490-river-sum.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0490 river sum, a small original browser game.",
  },
  // Fourfold Wishes. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0491-fourfold-wishes.
  {
    slug: "game-0491-fourfold-wishes",
    surface: "app",
    title: "Fourfold Wishes",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0491-fourfold-wishes",
    canonical: "host",
    screenshot: "/shots/game-0491-fourfold-wishes.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0491 fourfold wishes, a small original browser game.",
  },
  // Cannon Ledger. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0492-cannon-ledger.
  {
    slug: "game-0492-cannon-ledger",
    surface: "app",
    title: "Cannon Ledger",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0492-cannon-ledger",
    canonical: "host",
    screenshot: "/shots/game-0492-cannon-ledger.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0492 cannon ledger, a small original browser game.",
  },
  // Velvet Parade. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0493-velvet-parade.
  {
    slug: "game-0493-velvet-parade",
    surface: "app",
    title: "Velvet Parade",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0493-velvet-parade",
    canonical: "host",
    screenshot: "/shots/game-0493-velvet-parade.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0493 velvet parade, a small original browser game.",
  },
  // Pocket Hypercube. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0494-pocket-hypercube.
  {
    slug: "game-0494-pocket-hypercube",
    surface: "app",
    title: "Pocket Hypercube",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0494-pocket-hypercube",
    canonical: "host",
    screenshot: "/shots/game-0494-pocket-hypercube.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0494 pocket hypercube, a small original browser game.",
  },
  // Claykin Nursery. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0495-claykin-nursery.
  {
    slug: "game-0495-claykin-nursery",
    surface: "app",
    title: "Claykin Nursery",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0495-claykin-nursery",
    canonical: "host",
    screenshot: "/shots/game-0495-claykin-nursery.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0495 claykin nursery, a small original browser game.",
  },
  // Amber Coil. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0496-amber-coil.
  {
    slug: "game-0496-amber-coil",
    surface: "app",
    title: "Amber Coil",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0496-amber-coil",
    canonical: "host",
    screenshot: "/shots/game-0496-amber-coil.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0496 amber coil, a small original browser game.",
  },
  // Twin Perch. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0497-twin-perch.
  {
    slug: "game-0497-twin-perch",
    surface: "app",
    title: "Twin Perch",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0497-twin-perch",
    canonical: "host",
    screenshot: "/shots/game-0497-twin-perch.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0497 twin perch, a small original browser game.",
  },
  // Neon Heatline. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0498-neon-heatline.
  {
    slug: "game-0498-neon-heatline",
    surface: "app",
    title: "Neon Heatline",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0498-neon-heatline",
    canonical: "host",
    screenshot: "/shots/game-0498-neon-heatline.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0498 neon heatline, a small original browser game.",
  },
  // Coinwheel Canal. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0499-coinwheel-canal.
  {
    slug: "game-0499-coinwheel-canal",
    surface: "app",
    title: "Coinwheel Canal",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0499-coinwheel-canal",
    canonical: "host",
    screenshot: "/shots/game-0499-coinwheel-canal.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0499 coinwheel canal, a small original browser game.",
  },
  // Rune Lantern. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0500-rune-lantern.
  {
    slug: "game-0500-rune-lantern",
    surface: "app",
    title: "Rune Lantern",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0500-rune-lantern",
    canonical: "host",
    screenshot: "/shots/game-0500-rune-lantern.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0500 rune lantern, a small original browser game.",
  },
  // Meadow Rescue. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0501-meadow-rescue.
  {
    slug: "game-0501-meadow-rescue",
    surface: "app",
    title: "Meadow Rescue",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0501-meadow-rescue",
    canonical: "host",
    screenshot: "/shots/game-0501-meadow-rescue.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0501 meadow rescue, a small original browser game.",
  },
  // Rival Rivers. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0502-rival-rivers.
  {
    slug: "game-0502-rival-rivers",
    surface: "app",
    title: "Rival Rivers",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0502-rival-rivers",
    canonical: "host",
    screenshot: "/shots/game-0502-rival-rivers.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0502 rival rivers, a small original browser game.",
  },
  // Railbound Glow. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0503-railbound-glow.
  {
    slug: "game-0503-railbound-glow",
    surface: "app",
    title: "Railbound Glow",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0503-railbound-glow",
    canonical: "host",
    screenshot: "/shots/game-0503-railbound-glow.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0503 railbound glow, a small original browser game.",
  },
  // Sunfall Catch. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0504-sunfall-catch.
  {
    slug: "game-0504-sunfall-catch",
    surface: "app",
    title: "Sunfall Catch",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0504-sunfall-catch",
    canonical: "host",
    screenshot: "/shots/game-0504-sunfall-catch.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0504 sunfall catch, a small original browser game.",
  },
  // Serpent Orchard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0505-serpent-orchard.
  {
    slug: "game-0505-serpent-orchard",
    surface: "app",
    title: "Serpent Orchard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0505-serpent-orchard",
    canonical: "host",
    screenshot: "/shots/game-0505-serpent-orchard.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0505 serpent orchard, a small original browser game.",
  },
  // Orbital Trellis. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0506-orbital-trellis.
  {
    slug: "game-0506-orbital-trellis",
    surface: "app",
    title: "Orbital Trellis",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0506-orbital-trellis",
    canonical: "host",
    screenshot: "/shots/game-0506-orbital-trellis.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0506 orbital trellis, a small original browser game.",
  },
  // Shellshock Canopy. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0507-shellshock-canopy.
  {
    slug: "game-0507-shellshock-canopy",
    surface: "app",
    title: "Shellshock Canopy",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0507-shellshock-canopy",
    canonical: "host",
    screenshot: "/shots/game-0507-shellshock-canopy.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0507 shellshock canopy, a small original browser game.",
  },
  // Chromatic Capacitor. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0508-chromatic-capacitor.
  {
    slug: "game-0508-chromatic-capacitor",
    surface: "app",
    title: "Chromatic Capacitor",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0508-chromatic-capacitor",
    canonical: "host",
    screenshot: "/shots/game-0508-chromatic-capacitor.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0508 chromatic capacitor, a small original browser game.",
  },
  // Mirrorburst Arcade. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0509-mirrorburst-arcade.
  {
    slug: "game-0509-mirrorburst-arcade",
    surface: "app",
    title: "Mirrorburst Arcade",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0509-mirrorburst-arcade",
    canonical: "host",
    screenshot: "/shots/game-0509-mirrorburst-arcade.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0509 mirrorburst arcade, a small original browser game.",
  },
  // Cloudship Volley. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0510-cloudship-volley.
  {
    slug: "game-0510-cloudship-volley",
    surface: "app",
    title: "Cloudship Volley",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0510-cloudship-volley",
    canonical: "host",
    screenshot: "/shots/game-0510-cloudship-volley.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0510 cloudship volley, a small original browser game.",
  },
  // Relic Canopy. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0511-relic-canopy.
  {
    slug: "game-0511-relic-canopy",
    surface: "app",
    title: "Relic Canopy",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0511-relic-canopy",
    canonical: "host",
    screenshot: "/shots/game-0511-relic-canopy.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0511 relic canopy, a small original browser game.",
  },
  // Pattern Pilgrim. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0512-pattern-pilgrim.
  {
    slug: "game-0512-pattern-pilgrim",
    surface: "app",
    title: "Pattern Pilgrim",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0512-pattern-pilgrim",
    canonical: "host",
    screenshot: "/shots/game-0512-pattern-pilgrim.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0512 pattern pilgrim, a small original browser game.",
  },
  // Quiet Courtyard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0513-quiet-courtyard.
  {
    slug: "game-0513-quiet-courtyard",
    surface: "app",
    title: "Quiet Courtyard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0513-quiet-courtyard",
    canonical: "host",
    screenshot: "/shots/game-0513-quiet-courtyard.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0513 quiet courtyard, a small original browser game.",
  },
  // Lantern Table. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0514-lantern-table.
  {
    slug: "game-0514-lantern-table",
    surface: "app",
    title: "Lantern Table",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0514-lantern-table",
    canonical: "host",
    screenshot: "/shots/game-0514-lantern-table.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0514 lantern table, a small original browser game.",
  },
  // Lucky Whiskers. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0515-lucky-whiskers.
  {
    slug: "game-0515-lucky-whiskers",
    surface: "app",
    title: "Lucky Whiskers",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0515-lucky-whiskers",
    canonical: "host",
    screenshot: "/shots/game-0515-lucky-whiskers.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0515 lucky whiskers, a small original browser game.",
  },
  // Purr Parcel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0601-purr-parcel.
  {
    slug: "game-0601-purr-parcel",
    surface: "app",
    title: "Purr Parcel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0601-purr-parcel",
    canonical: "host",
    screenshot: "/shots/game-0601-purr-parcel.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0601 purr parcel, a small original browser game.",
  },
  // Vector Lantern. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0602-vector-lantern.
  {
    slug: "game-0602-vector-lantern",
    surface: "app",
    title: "Vector Lantern",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0602-vector-lantern",
    canonical: "host",
    screenshot: "/shots/game-0602-vector-lantern.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0602 vector lantern, a small original browser game.",
  },
  // Dusk Duel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0603-dusk-duel.
  {
    slug: "game-0603-dusk-duel",
    surface: "app",
    title: "Dusk Duel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0603-dusk-duel",
    canonical: "host",
    screenshot: "/shots/game-0603-dusk-duel.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0603 dusk duel, a small original browser game.",
  },
  // Grove Reversal. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0604-grove-reversal.
  {
    slug: "game-0604-grove-reversal",
    surface: "app",
    title: "Grove Reversal",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0604-grove-reversal",
    canonical: "host",
    screenshot: "/shots/game-0604-grove-reversal.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0604 grove reversal, a small original browser game.",
  },
  // Prism Furnace. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0605-prism-furnace.
  {
    slug: "game-0605-prism-furnace",
    surface: "app",
    title: "Prism Furnace",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0605-prism-furnace",
    canonical: "host",
    screenshot: "/shots/game-0605-prism-furnace.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0605 prism furnace, a small original browser game.",
  },
  // Visitor Masonry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0606-visitor-masonry.
  {
    slug: "game-0606-visitor-masonry",
    surface: "app",
    title: "Visitor Masonry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0606-visitor-masonry",
    canonical: "host",
    screenshot: "/shots/game-0606-visitor-masonry.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0606 visitor masonry, a small original browser game.",
  },
  // Promotion Duel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0607-promotion-duel.
  {
    slug: "game-0607-promotion-duel",
    surface: "app",
    title: "Promotion Duel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0607-promotion-duel",
    canonical: "host",
    screenshot: "/shots/game-0607-promotion-duel.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0607 promotion duel, a small original browser game.",
  },
  // Arcana Ledger. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0608-arcana-ledger.
  {
    slug: "game-0608-arcana-ledger",
    surface: "app",
    title: "Arcana Ledger",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0608-arcana-ledger",
    canonical: "host",
    screenshot: "/shots/game-0608-arcana-ledger.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0608 arcana ledger, a small original browser game.",
  },
  // Foundry Forum. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0609-foundry-forum.
  {
    slug: "game-0609-foundry-forum",
    surface: "app",
    title: "Foundry Forum",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0609-foundry-forum",
    canonical: "host",
    screenshot: "/shots/game-0609-foundry-forum.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0609 foundry forum, a small original browser game.",
  },
  // Turning Teahouse. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0610-turning-teahouse.
  {
    slug: "game-0610-turning-teahouse",
    surface: "app",
    title: "Turning Teahouse",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0610-turning-teahouse",
    canonical: "host",
    screenshot: "/shots/game-0610-turning-teahouse.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0610 turning teahouse, a small original browser game.",
  },
  // Tide Oath. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0611-tide-oath.
  {
    slug: "game-0611-tide-oath",
    surface: "app",
    title: "Tide Oath",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0611-tide-oath",
    canonical: "host",
    screenshot: "/shots/game-0611-tide-oath.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0611 tide oath, a small original browser game.",
  },
  // Caravan Keystone. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0612-caravan-keystone.
  {
    slug: "game-0612-caravan-keystone",
    surface: "app",
    title: "Caravan Keystone",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0612-caravan-keystone",
    canonical: "host",
    screenshot: "/shots/game-0612-caravan-keystone.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0612 caravan keystone, a small original browser game.",
  },
  // Seahorse Shelves. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0613-seahorse-shelves.
  {
    slug: "game-0613-seahorse-shelves",
    surface: "app",
    title: "Seahorse Shelves",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0613-seahorse-shelves",
    canonical: "host",
    screenshot: "/shots/game-0613-seahorse-shelves.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0613 seahorse shelves, a small original browser game.",
  },
  // Chromatic Inlay. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0614-chromatic-inlay.
  {
    slug: "game-0614-chromatic-inlay",
    surface: "app",
    title: "Chromatic Inlay",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0614-chromatic-inlay",
    canonical: "host",
    screenshot: "/shots/game-0614-chromatic-inlay.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0614 chromatic inlay, a small original browser game.",
  },
  // Hex Cape. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0615-hex-cape.
  {
    slug: "game-0615-hex-cape",
    surface: "app",
    title: "Hex Cape",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0615-hex-cape",
    canonical: "host",
    screenshot: "/shots/game-0615-hex-cape.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0615 hex cape, a small original browser game.",
  },
  // Gyre Lock. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0616-gyre-lock.
  {
    slug: "game-0616-gyre-lock",
    surface: "app",
    title: "Gyre Lock",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0616-gyre-lock",
    canonical: "host",
    screenshot: "/shots/game-0616-gyre-lock.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0616 gyre lock, a small original browser game.",
  },
  // Trail of Runes. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0617-trail-of-runes.
  {
    slug: "game-0617-trail-of-runes",
    surface: "app",
    title: "Trail of Runes",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0617-trail-of-runes",
    canonical: "host",
    screenshot: "/shots/game-0617-trail-of-runes.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0617 trail of runes, a small original browser game.",
  },
  // Gilded Footprints. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0618-gilded-footprints.
  {
    slug: "game-0618-gilded-footprints",
    surface: "app",
    title: "Gilded Footprints",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0618-gilded-footprints",
    canonical: "host",
    screenshot: "/shots/game-0618-gilded-footprints.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0618 gilded footprints, a small original browser game.",
  },
  // Pearl Switchback. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0619-pearl-switchback.
  {
    slug: "game-0619-pearl-switchback",
    surface: "app",
    title: "Pearl Switchback",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0619-pearl-switchback",
    canonical: "host",
    screenshot: "/shots/game-0619-pearl-switchback.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0619 pearl switchback, a small original browser game.",
  },
  // Ink Seven. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0620-ink-seven.
  {
    slug: "game-0620-ink-seven",
    surface: "app",
    title: "Ink Seven",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0620-ink-seven",
    canonical: "host",
    screenshot: "/shots/game-0620-ink-seven.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0620 ink seven, a small original browser game.",
  },
  // Rune Intruder. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0621-rune-intruder.
  {
    slug: "game-0621-rune-intruder",
    surface: "app",
    title: "Rune Intruder",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0621-rune-intruder",
    canonical: "host",
    screenshot: "/shots/game-0621-rune-intruder.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0621 rune intruder, a small original browser game.",
  },
  // Bog Bistro. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0622-bog-bistro.
  {
    slug: "game-0622-bog-bistro",
    surface: "app",
    title: "Bog Bistro",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0622-bog-bistro",
    canonical: "host",
    screenshot: "/shots/game-0622-bog-bistro.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0622 bog bistro, a small original browser game.",
  },
  // Guild of Arches. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0623-guild-of-arches.
  {
    slug: "game-0623-guild-of-arches",
    surface: "app",
    title: "Guild of Arches",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0623-guild-of-arches",
    canonical: "host",
    screenshot: "/shots/game-0623-guild-of-arches.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0623 guild of arches, a small original browser game.",
  },
  // Prism Party. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0624-prism-party.
  {
    slug: "game-0624-prism-party",
    surface: "app",
    title: "Prism Party",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0624-prism-party",
    canonical: "host",
    screenshot: "/shots/game-0624-prism-party.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0624 prism party, a small original browser game.",
  },
  // Hanoi Hues. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0625-hanoi-hues.
  {
    slug: "game-0625-hanoi-hues",
    surface: "app",
    title: "Hanoi Hues",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0625-hanoi-hues",
    canonical: "host",
    screenshot: "/shots/game-0625-hanoi-hues.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0625 hanoi hues, a small original browser game.",
  },
  // Storm Relics. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0626-storm-relics.
  {
    slug: "game-0626-storm-relics",
    surface: "app",
    title: "Storm Relics",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0626-storm-relics",
    canonical: "host",
    screenshot: "/shots/game-0626-storm-relics.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0626 storm relics, a small original browser game.",
  },
  // Island Mosaic. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0627-island-mosaic.
  {
    slug: "game-0627-island-mosaic",
    surface: "app",
    title: "Island Mosaic",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0627-island-mosaic",
    canonical: "host",
    screenshot: "/shots/game-0627-island-mosaic.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0627 island mosaic, a small original browser game.",
  },
  // Dice Rampart. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0628-dice-rampart.
  {
    slug: "game-0628-dice-rampart",
    surface: "app",
    title: "Dice Rampart",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0628-dice-rampart",
    canonical: "host",
    screenshot: "/shots/game-0628-dice-rampart.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0628 dice rampart, a small original browser game.",
  },
  // Orbit Pantry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0629-orbit-pantry.
  {
    slug: "game-0629-orbit-pantry",
    surface: "app",
    title: "Orbit Pantry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0629-orbit-pantry",
    canonical: "host",
    screenshot: "/shots/game-0629-orbit-pantry.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0629 orbit pantry, a small original browser game.",
  },
  // Lucky Loop. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0630-lucky-loop.
  {
    slug: "game-0630-lucky-loop",
    surface: "app",
    title: "Lucky Loop",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0630-lucky-loop",
    canonical: "host",
    screenshot: "/shots/game-0630-lucky-loop.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0630 lucky loop, a small original browser game.",
  },
  // Cairn Village. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0631-cairn-village.
  {
    slug: "game-0631-cairn-village",
    surface: "app",
    title: "Cairn Village",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0631-cairn-village",
    canonical: "host",
    screenshot: "/shots/game-0631-cairn-village.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0631 cairn village, a small original browser game.",
  },
  // Minnow Mittens. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0632-minnow-mittens.
  {
    slug: "game-0632-minnow-mittens",
    surface: "app",
    title: "Minnow Mittens",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0632-minnow-mittens",
    canonical: "host",
    screenshot: "/shots/game-0632-minnow-mittens.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0632 minnow mittens, a small original browser game.",
  },
  // Marble Meadow. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0633-marble-meadow.
  {
    slug: "game-0633-marble-meadow",
    surface: "app",
    title: "Marble Meadow",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0633-marble-meadow",
    canonical: "host",
    screenshot: "/shots/game-0633-marble-meadow.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0633 marble meadow, a small original browser game.",
  },
  // Harvest Circuit. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0634-harvest-circuit.
  {
    slug: "game-0634-harvest-circuit",
    surface: "app",
    title: "Harvest Circuit",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0634-harvest-circuit",
    canonical: "host",
    screenshot: "/shots/game-0634-harvest-circuit.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0634 harvest circuit, a small original browser game.",
  },
  // Triple Skyline. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0635-triple-skyline.
  {
    slug: "game-0635-triple-skyline",
    surface: "app",
    title: "Triple Skyline",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0635-triple-skyline",
    canonical: "host",
    screenshot: "/shots/game-0635-triple-skyline.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0635 triple skyline, a small original browser game.",
  },
  // Council Rampart. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0636-council-rampart.
  {
    slug: "game-0636-council-rampart",
    surface: "app",
    title: "Council Rampart",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0636-council-rampart",
    canonical: "host",
    screenshot: "/shots/game-0636-council-rampart.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0636 council rampart, a small original browser game.",
  },
  // Charm Current. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0637-charm-current.
  {
    slug: "game-0637-charm-current",
    surface: "app",
    title: "Charm Current",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0637-charm-current",
    canonical: "host",
    screenshot: "/shots/game-0637-charm-current.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0637 charm current, a small original browser game.",
  },
  // Reversible Stitch. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0638-reversible-stitch.
  {
    slug: "game-0638-reversible-stitch",
    surface: "app",
    title: "Reversible Stitch",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0638-reversible-stitch",
    canonical: "host",
    screenshot: "/shots/game-0638-reversible-stitch.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0638 reversible stitch, a small original browser game.",
  },
  // Pastry Stack. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0639-pastry-stack.
  {
    slug: "game-0639-pastry-stack",
    surface: "app",
    title: "Pastry Stack",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0639-pastry-stack",
    canonical: "host",
    screenshot: "/shots/game-0639-pastry-stack.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0639 pastry stack, a small original browser game.",
  },
  // Foam Atlas. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0640-foam-atlas.
  {
    slug: "game-0640-foam-atlas",
    surface: "app",
    title: "Foam Atlas",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0640-foam-atlas",
    canonical: "host",
    screenshot: "/shots/game-0640-foam-atlas.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0640 foam atlas, a small original browser game.",
  },
  // Petal Pushers. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0641-petal-pushers.
  {
    slug: "game-0641-petal-pushers",
    surface: "app",
    title: "Petal Pushers",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0641-petal-pushers",
    canonical: "host",
    screenshot: "/shots/game-0641-petal-pushers.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0641 petal pushers, a small original browser game.",
  },
  // Arrow Orchard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0642-arrow-orchard.
  {
    slug: "game-0642-arrow-orchard",
    surface: "app",
    title: "Arrow Orchard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0642-arrow-orchard",
    canonical: "host",
    screenshot: "/shots/game-0642-arrow-orchard.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0642 arrow orchard, a small original browser game.",
  },
  // Prism Surveyor. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0643-prism-surveyor.
  {
    slug: "game-0643-prism-surveyor",
    surface: "app",
    title: "Prism Surveyor",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0643-prism-surveyor",
    canonical: "host",
    screenshot: "/shots/game-0643-prism-surveyor.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0643 prism surveyor, a small original browser game.",
  },
  // Cape Cascade. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0644-cape-cascade.
  {
    slug: "game-0644-cape-cascade",
    surface: "app",
    title: "Cape Cascade",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0644-cape-cascade",
    canonical: "host",
    screenshot: "/shots/game-0644-cape-cascade.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0644 cape cascade, a small original browser game.",
  },
  // Quiet Keyways. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0645-quiet-keyways.
  {
    slug: "game-0645-quiet-keyways",
    surface: "app",
    title: "Quiet Keyways",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0645-quiet-keyways",
    canonical: "host",
    screenshot: "/shots/game-0645-quiet-keyways.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0645 quiet keyways, a small original browser game.",
  },
  // Lantern Rescue. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0646-lantern-rescue.
  {
    slug: "game-0646-lantern-rescue",
    surface: "app",
    title: "Lantern Rescue",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0646-lantern-rescue",
    canonical: "host",
    screenshot: "/shots/game-0646-lantern-rescue.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0646 lantern rescue, a small original browser game.",
  },
  // Thawbell Meadow. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0647-thawbell-meadow.
  {
    slug: "game-0647-thawbell-meadow",
    surface: "app",
    title: "Thawbell Meadow",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0647-thawbell-meadow",
    canonical: "host",
    screenshot: "/shots/game-0647-thawbell-meadow.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0647 thawbell meadow, a small original browser game.",
  },
  // Hex Halo. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0648-hex-halo.
  {
    slug: "game-0648-hex-halo",
    surface: "app",
    title: "Hex Halo",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0648-hex-halo",
    canonical: "host",
    screenshot: "/shots/game-0648-hex-halo.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0648 hex halo, a small original browser game.",
  },
  // Wobble Kettle. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0649-wobble-kettle.
  {
    slug: "game-0649-wobble-kettle",
    surface: "app",
    title: "Wobble Kettle",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0649-wobble-kettle",
    canonical: "host",
    screenshot: "/shots/game-0649-wobble-kettle.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0649 wobble kettle, a small original browser game.",
  },
  // Inkbound Apprentice. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0650-inkbound-apprentice.
  {
    slug: "game-0650-inkbound-apprentice",
    surface: "app",
    title: "Inkbound Apprentice",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0650-inkbound-apprentice",
    canonical: "host",
    screenshot: "/shots/game-0650-inkbound-apprentice.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0650 inkbound apprentice, a small original browser game.",
  },
  // Card Confluence. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0651-card-confluence.
  {
    slug: "game-0651-card-confluence",
    surface: "app",
    title: "Card Confluence",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0651-card-confluence",
    canonical: "host",
    screenshot: "/shots/game-0651-card-confluence.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0651 card confluence, a small original browser game.",
  },
  // Compass Reactor. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0652-compass-reactor.
  {
    slug: "game-0652-compass-reactor",
    surface: "app",
    title: "Compass Reactor",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0652-compass-reactor",
    canonical: "host",
    screenshot: "/shots/game-0652-compass-reactor.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0652 compass reactor, a small original browser game.",
  },
  // Cove Collisions. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0653-cove-collisions.
  {
    slug: "game-0653-cove-collisions",
    surface: "app",
    title: "Cove Collisions",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0653-cove-collisions",
    canonical: "host",
    screenshot: "/shots/game-0653-cove-collisions.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0653 cove collisions, a small original browser game.",
  },
  // After Hours Inbox. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0654-after-hours-inbox.
  {
    slug: "game-0654-after-hours-inbox",
    surface: "app",
    title: "After Hours Inbox",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0654-after-hours-inbox",
    canonical: "host",
    screenshot: "/shots/game-0654-after-hours-inbox.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0654 after hours inbox, a small original browser game.",
  },
  // Marble Beacon. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0655-marble-beacon.
  {
    slug: "game-0655-marble-beacon",
    surface: "app",
    title: "Marble Beacon",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0655-marble-beacon",
    canonical: "host",
    screenshot: "/shots/game-0655-marble-beacon.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0655 marble beacon, a small original browser game.",
  },
  // Supper Paws. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0656-supper-paws.
  {
    slug: "game-0656-supper-paws",
    surface: "app",
    title: "Supper Paws",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0656-supper-paws",
    canonical: "host",
    screenshot: "/shots/game-0656-supper-paws.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0656 supper paws, a small original browser game.",
  },
  // Fourwind Foundry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0657-fourwind-foundry.
  {
    slug: "game-0657-fourwind-foundry",
    surface: "app",
    title: "Fourwind Foundry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0657-fourwind-foundry",
    canonical: "host",
    screenshot: "/shots/game-0657-fourwind-foundry.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0657 fourwind foundry, a small original browser game.",
  },
  // Linkloom. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0658-linkloom.
  {
    slug: "game-0658-linkloom",
    surface: "app",
    title: "Linkloom",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0658-linkloom",
    canonical: "host",
    screenshot: "/shots/game-0658-linkloom.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0658 linkloom, a small original browser game.",
  },
  // Adaptation Isle. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0659-adaptation-isle.
  {
    slug: "game-0659-adaptation-isle",
    surface: "app",
    title: "Adaptation Isle",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0659-adaptation-isle",
    canonical: "host",
    screenshot: "/shots/game-0659-adaptation-isle.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0659 adaptation isle, a small original browser game.",
  },
  // Relic Stair. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0660-relic-stair.
  {
    slug: "game-0660-relic-stair",
    surface: "app",
    title: "Relic Stair",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0660-relic-stair",
    canonical: "host",
    screenshot: "/shots/game-0660-relic-stair.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0660 relic stair, a small original browser game.",
  },
  // Mana Footsteps. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0661-mana-footsteps.
  {
    slug: "game-0661-mana-footsteps",
    surface: "app",
    title: "Mana Footsteps",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0661-mana-footsteps",
    canonical: "host",
    screenshot: "/shots/game-0661-mana-footsteps.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0661 mana footsteps, a small original browser game.",
  },
  // Frost Kiln. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0662-frost-kiln.
  {
    slug: "game-0662-frost-kiln",
    surface: "app",
    title: "Frost Kiln",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0662-frost-kiln",
    canonical: "host",
    screenshot: "/shots/game-0662-frost-kiln.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0662 frost kiln, a small original browser game.",
  },
  // Underworld Mason. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0663-underworld-mason.
  {
    slug: "game-0663-underworld-mason",
    surface: "app",
    title: "Underworld Mason",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0663-underworld-mason",
    canonical: "host",
    screenshot: "/shots/game-0663-underworld-mason.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0663 underworld mason, a small original browser game.",
  },
  // Morrow Farm. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0664-morrow-farm.
  {
    slug: "game-0664-morrow-farm",
    surface: "app",
    title: "Morrow Farm",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0664-morrow-farm",
    canonical: "host",
    screenshot: "/shots/game-0664-morrow-farm.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0664 morrow farm, a small original browser game.",
  },
  // Zodiac Wayfarers. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0665-zodiac-wayfarers.
  {
    slug: "game-0665-zodiac-wayfarers",
    surface: "app",
    title: "Zodiac Wayfarers",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0665-zodiac-wayfarers",
    canonical: "host",
    screenshot: "/shots/game-0665-zodiac-wayfarers.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0665 zodiac wayfarers, a small original browser game.",
  },
  // Preserve Hopper. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0666-preserve-hopper.
  {
    slug: "game-0666-preserve-hopper",
    surface: "app",
    title: "Preserve Hopper",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0666-preserve-hopper",
    canonical: "host",
    screenshot: "/shots/game-0666-preserve-hopper.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0666 preserve hopper, a small original browser game.",
  },
  // Mirror Current. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0667-mirror-current.
  {
    slug: "game-0667-mirror-current",
    surface: "app",
    title: "Mirror Current",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0667-mirror-current",
    canonical: "host",
    screenshot: "/shots/game-0667-mirror-current.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0667 mirror current, a small original browser game.",
  },
  // Snowglobe Spark. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0668-snowglobe-spark.
  {
    slug: "game-0668-snowglobe-spark",
    surface: "app",
    title: "Snowglobe Spark",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0668-snowglobe-spark",
    canonical: "host",
    screenshot: "/shots/game-0668-snowglobe-spark.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0668 snowglobe spark, a small original browser game.",
  },
  // Pearl Basins. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0669-pearl-basins.
  {
    slug: "game-0669-pearl-basins",
    surface: "app",
    title: "Pearl Basins",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0669-pearl-basins",
    canonical: "host",
    screenshot: "/shots/game-0669-pearl-basins.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0669 pearl basins, a small original browser game.",
  },
  // Crown Cutters. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0670-crown-cutters.
  {
    slug: "game-0670-crown-cutters",
    surface: "app",
    title: "Crown Cutters",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0670-crown-cutters",
    canonical: "host",
    screenshot: "/shots/game-0670-crown-cutters.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0670 crown cutters, a small original browser game.",
  },
  // Pocket Panorama. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0671-pocket-panorama.
  {
    slug: "game-0671-pocket-panorama",
    surface: "app",
    title: "Pocket Panorama",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0671-pocket-panorama",
    canonical: "host",
    screenshot: "/shots/game-0671-pocket-panorama.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0671 pocket panorama, a small original browser game.",
  },
  // Tyrant Tactician. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0672-tyrant-tactician.
  {
    slug: "game-0672-tyrant-tactician",
    surface: "app",
    title: "Tyrant Tactician",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0672-tyrant-tactician",
    canonical: "host",
    screenshot: "/shots/game-0672-tyrant-tactician.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0672 tyrant tactician, a small original browser game.",
  },
  // Amulet Assembly. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0673-amulet-assembly.
  {
    slug: "game-0673-amulet-assembly",
    surface: "app",
    title: "Amulet Assembly",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0673-amulet-assembly",
    canonical: "host",
    screenshot: "/shots/game-0673-amulet-assembly.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0673 amulet assembly, a small original browser game.",
  },
  // Tide Modes. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0674-tide-modes.
  {
    slug: "game-0674-tide-modes",
    surface: "app",
    title: "Tide Modes",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0674-tide-modes",
    canonical: "host",
    screenshot: "/shots/game-0674-tide-modes.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0674 tide modes, a small original browser game.",
  },
  // Sliding Study. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0675-sliding-study.
  {
    slug: "game-0675-sliding-study",
    surface: "app",
    title: "Sliding Study",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0675-sliding-study",
    canonical: "host",
    screenshot: "/shots/game-0675-sliding-study.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0675 sliding study, a small original browser game.",
  },
  // Companion Spire. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0676-companion-spire.
  {
    slug: "game-0676-companion-spire",
    surface: "app",
    title: "Companion Spire",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0676-companion-spire",
    canonical: "host",
    screenshot: "/shots/game-0676-companion-spire.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0676 companion spire, a small original browser game.",
  },
  // Circuit Hexer. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0677-circuit-hexer.
  {
    slug: "game-0677-circuit-hexer",
    surface: "app",
    title: "Circuit Hexer",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0677-circuit-hexer",
    canonical: "host",
    screenshot: "/shots/game-0677-circuit-hexer.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0677 circuit hexer, a small original browser game.",
  },
  // Gecko Gemshift. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0678-gecko-gemshift.
  {
    slug: "game-0678-gecko-gemshift",
    surface: "app",
    title: "Gecko Gemshift",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0678-gecko-gemshift",
    canonical: "host",
    screenshot: "/shots/game-0678-gecko-gemshift.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0678 gecko gemshift, a small original browser game.",
  },
  // Crate Comet. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0679-crate-comet.
  {
    slug: "game-0679-crate-comet",
    surface: "app",
    title: "Crate Comet",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0679-crate-comet",
    canonical: "host",
    screenshot: "/shots/game-0679-crate-comet.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0679 crate comet, a small original browser game.",
  },
  // Century Garden. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0680-century-garden.
  {
    slug: "game-0680-century-garden",
    surface: "app",
    title: "Century Garden",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0680-century-garden",
    canonical: "host",
    screenshot: "/shots/game-0680-century-garden.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0680 century garden, a small original browser game.",
  },
  // Wardlight Siege. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0681-wardlight-siege.
  {
    slug: "game-0681-wardlight-siege",
    surface: "app",
    title: "Wardlight Siege",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0681-wardlight-siege",
    canonical: "host",
    screenshot: "/shots/game-0681-wardlight-siege.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0681 wardlight siege, a small original browser game.",
  },
  // Meteor Pair Expedition. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0682-meteor-pair-expedition.
  {
    slug: "game-0682-meteor-pair-expedition",
    surface: "app",
    title: "Meteor Pair Expedition",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0682-meteor-pair-expedition",
    canonical: "host",
    screenshot: "/shots/game-0682-meteor-pair-expedition.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0682 meteor pair expedition, a small original browser game.",
  },
  // Silhouette Dock. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0683-silhouette-dock.
  {
    slug: "game-0683-silhouette-dock",
    surface: "app",
    title: "Silhouette Dock",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0683-silhouette-dock",
    canonical: "host",
    screenshot: "/shots/game-0683-silhouette-dock.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0683 silhouette dock, a small original browser game.",
  },
  // Bunship Fuel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0684-bunship-fuel.
  {
    slug: "game-0684-bunship-fuel",
    surface: "app",
    title: "Bunship Fuel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0684-bunship-fuel",
    canonical: "host",
    screenshot: "/shots/game-0684-bunship-fuel.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0684 bunship fuel, a small original browser game.",
  },
  // Tollkeeper Tactics. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0685-tollkeeper-tactics.
  {
    slug: "game-0685-tollkeeper-tactics",
    surface: "app",
    title: "Tollkeeper Tactics",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0685-tollkeeper-tactics",
    canonical: "host",
    screenshot: "/shots/game-0685-tollkeeper-tactics.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0685 tollkeeper tactics, a small original browser game.",
  },
  // Purr Pantry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0686-purr-pantry.
  {
    slug: "game-0686-purr-pantry",
    surface: "app",
    title: "Purr Pantry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0686-purr-pantry",
    canonical: "host",
    screenshot: "/shots/game-0686-purr-pantry.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0686 purr pantry, a small original browser game.",
  },
  // Airlock Crates. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0687-airlock-crates.
  {
    slug: "game-0687-airlock-crates",
    surface: "app",
    title: "Airlock Crates",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0687-airlock-crates",
    canonical: "host",
    screenshot: "/shots/game-0687-airlock-crates.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0687 airlock crates, a small original browser game.",
  },
  // Crystal Carousel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0688-crystal-carousel.
  {
    slug: "game-0688-crystal-carousel",
    surface: "app",
    title: "Crystal Carousel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0688-crystal-carousel",
    canonical: "host",
    screenshot: "/shots/game-0688-crystal-carousel.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0688 crystal carousel, a small original browser game.",
  },
  // Sixfold Bento. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0689-sixfold-bento.
  {
    slug: "game-0689-sixfold-bento",
    surface: "app",
    title: "Sixfold Bento",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0689-sixfold-bento",
    canonical: "host",
    screenshot: "/shots/game-0689-sixfold-bento.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0689 sixfold bento, a small original browser game.",
  },
  // Cloudcat Squadron. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0690-cloudcat-squadron.
  {
    slug: "game-0690-cloudcat-squadron",
    surface: "app",
    title: "Cloudcat Squadron",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0690-cloudcat-squadron",
    canonical: "host",
    screenshot: "/shots/game-0690-cloudcat-squadron.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0690 cloudcat squadron, a small original browser game.",
  },
  // Lantern Tuition. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0691-lantern-tuition.
  {
    slug: "game-0691-lantern-tuition",
    surface: "app",
    title: "Lantern Tuition",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0691-lantern-tuition",
    canonical: "host",
    screenshot: "/shots/game-0691-lantern-tuition.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0691 lantern tuition, a small original browser game.",
  },
  // Moonseal Manor. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0692-moonseal-manor.
  {
    slug: "game-0692-moonseal-manor",
    surface: "app",
    title: "Moonseal Manor",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0692-moonseal-manor",
    canonical: "host",
    screenshot: "/shots/game-0692-moonseal-manor.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0692 moonseal manor, a small original browser game.",
  },
  // Angular Orchard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0693-angular-orchard.
  {
    slug: "game-0693-angular-orchard",
    surface: "app",
    title: "Angular Orchard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0693-angular-orchard",
    canonical: "host",
    screenshot: "/shots/game-0693-angular-orchard.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0693 angular orchard, a small original browser game.",
  },
  // Confection Circuit. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0694-confection-circuit.
  {
    slug: "game-0694-confection-circuit",
    surface: "app",
    title: "Confection Circuit",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0694-confection-circuit",
    canonical: "host",
    screenshot: "/shots/game-0694-confection-circuit.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0694 confection circuit, a small original browser game.",
  },
  // Ternary Reach. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0695-ternary-reach.
  {
    slug: "game-0695-ternary-reach",
    surface: "app",
    title: "Ternary Reach",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0695-ternary-reach",
    canonical: "host",
    screenshot: "/shots/game-0695-ternary-reach.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0695 ternary reach, a small original browser game.",
  },
  // Golem Amberpath. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0696-golem-amberpath.
  {
    slug: "game-0696-golem-amberpath",
    surface: "app",
    title: "Golem Amberpath",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0696-golem-amberpath",
    canonical: "host",
    screenshot: "/shots/game-0696-golem-amberpath.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0696 golem amberpath, a small original browser game.",
  },
  // Caravan Catacomb. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0697-caravan-catacomb.
  {
    slug: "game-0697-caravan-catacomb",
    surface: "app",
    title: "Caravan Catacomb",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0697-caravan-catacomb",
    canonical: "host",
    screenshot: "/shots/game-0697-caravan-catacomb.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0697 caravan catacomb, a small original browser game.",
  },
  // Tidal Duel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0698-tidal-duel.
  {
    slug: "game-0698-tidal-duel",
    surface: "app",
    title: "Tidal Duel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0698-tidal-duel",
    canonical: "host",
    screenshot: "/shots/game-0698-tidal-duel.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0698 tidal duel, a small original browser game.",
  },
  // Prism Pegs. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0699-prism-pegs.
  {
    slug: "game-0699-prism-pegs",
    surface: "app",
    title: "Prism Pegs",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0699-prism-pegs",
    canonical: "host",
    screenshot: "/shots/game-0699-prism-pegs.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0699 prism pegs, a small original browser game.",
  },
  // Crypt Triplets. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0700-crypt-triplets.
  {
    slug: "game-0700-crypt-triplets",
    surface: "app",
    title: "Crypt Triplets",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0700-crypt-triplets",
    canonical: "host",
    screenshot: "/shots/game-0700-crypt-triplets.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0700 crypt triplets, a small original browser game.",
  },
  // Sunlit Annex. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0701-sunlit-annex.
  {
    slug: "game-0701-sunlit-annex",
    surface: "app",
    title: "Sunlit Annex",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0701-sunlit-annex",
    canonical: "host",
    screenshot: "/shots/game-0701-sunlit-annex.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0701 sunlit annex, a small original browser game.",
  },
  // Ember Reliquary. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0702-ember-reliquary.
  {
    slug: "game-0702-ember-reliquary",
    surface: "app",
    title: "Ember Reliquary",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0702-ember-reliquary",
    canonical: "host",
    screenshot: "/shots/game-0702-ember-reliquary.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0702 ember reliquary, a small original browser game.",
  },
  // Songmend Grove. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0703-songmend-grove.
  {
    slug: "game-0703-songmend-grove",
    surface: "app",
    title: "Songmend Grove",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0703-songmend-grove",
    canonical: "host",
    screenshot: "/shots/game-0703-songmend-grove.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0703 songmend grove, a small original browser game.",
  },
  // Orbital Triad. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0704-orbital-triad.
  {
    slug: "game-0704-orbital-triad",
    surface: "app",
    title: "Orbital Triad",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0704-orbital-triad",
    canonical: "host",
    screenshot: "/shots/game-0704-orbital-triad.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0704 orbital triad, a small original browser game.",
  },
  // Menagerie Tiltbox. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0705-menagerie-tiltbox.
  {
    slug: "game-0705-menagerie-tiltbox",
    surface: "app",
    title: "Menagerie Tiltbox",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0705-menagerie-tiltbox",
    canonical: "host",
    screenshot: "/shots/game-0705-menagerie-tiltbox.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0705 menagerie tiltbox, a small original browser game.",
  },
  // Paws Across Paths. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0708-paws-across-paths.
  {
    slug: "game-0708-paws-across-paths",
    surface: "app",
    title: "Paws Across Paths",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0708-paws-across-paths",
    canonical: "host",
    screenshot: "/shots/game-0708-paws-across-paths.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0708 paws across paths, a small original browser game.",
  },
  // Clawdrop Choir. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0709-clawdrop-choir.
  {
    slug: "game-0709-clawdrop-choir",
    surface: "app",
    title: "Clawdrop Choir",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0709-clawdrop-choir",
    canonical: "host",
    screenshot: "/shots/game-0709-clawdrop-choir.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0709 clawdrop choir, a small original browser game.",
  },
  // Starstone Trace. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0801-starstone-trace.
  {
    slug: "game-0801-starstone-trace",
    surface: "app",
    title: "Starstone Trace",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0801-starstone-trace",
    canonical: "host",
    screenshot: "/shots/game-0801-starstone-trace.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0801 starstone trace, a small original browser game.",
  },
  // Garden of Returning Paths. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0802-garden-of-returning-paths.
  {
    slug: "game-0802-garden-of-returning-paths",
    surface: "app",
    title: "Garden of Returning Paths",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0802-garden-of-returning-paths",
    canonical: "host",
    screenshot: "/shots/game-0802-garden-of-returning-paths.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0802 garden of returning paths, a small original browser game.",
  },
  // Ninefold Aqueduct. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0803-ninefold-aqueduct.
  {
    slug: "game-0803-ninefold-aqueduct",
    surface: "app",
    title: "Ninefold Aqueduct",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0803-ninefold-aqueduct",
    canonical: "host",
    screenshot: "/shots/game-0803-ninefold-aqueduct.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0803 ninefold aqueduct, a small original browser game.",
  },
  // Pressure Parity. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0804-pressure-parity.
  {
    slug: "game-0804-pressure-parity",
    surface: "app",
    title: "Pressure Parity",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0804-pressure-parity",
    canonical: "host",
    screenshot: "/shots/game-0804-pressure-parity.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0804 pressure parity, a small original browser game.",
  },
  // Lantern Switchyard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0805-lantern-switchyard.
  {
    slug: "game-0805-lantern-switchyard",
    surface: "app",
    title: "Lantern Switchyard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0805-lantern-switchyard",
    canonical: "host",
    screenshot: "/shots/game-0805-lantern-switchyard.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0805 lantern switchyard, a small original browser game.",
  },
  // Brookstead Charter. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0806-brookstead-charter.
  {
    slug: "game-0806-brookstead-charter",
    surface: "app",
    title: "Brookstead Charter",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0806-brookstead-charter",
    canonical: "host",
    screenshot: "/shots/game-0806-brookstead-charter.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0806 brookstead charter, a small original browser game.",
  },
  // Dream Petal Pairs. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0807-dream-petal-pairs.
  {
    slug: "game-0807-dream-petal-pairs",
    surface: "app",
    title: "Dream Petal Pairs",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0807-dream-petal-pairs",
    canonical: "host",
    screenshot: "/shots/game-0807-dream-petal-pairs.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0807 dream petal pairs, a small original browser game.",
  },
  // Primary Census. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0808-primary-census.
  {
    slug: "game-0808-primary-census",
    surface: "app",
    title: "Primary Census",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0808-primary-census",
    canonical: "host",
    screenshot: "/shots/game-0808-primary-census.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0808 primary census, a small original browser game.",
  },
  // Orbital Quartet. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0809-orbital-quartet.
  {
    slug: "game-0809-orbital-quartet",
    surface: "app",
    title: "Orbital Quartet",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0809-orbital-quartet",
    canonical: "host",
    screenshot: "/shots/game-0809-orbital-quartet.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0809 orbital quartet, a small original browser game.",
  },
  // Sixfold Ledger. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0810-sixfold-ledger.
  {
    slug: "game-0810-sixfold-ledger",
    surface: "app",
    title: "Sixfold Ledger",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0810-sixfold-ledger",
    canonical: "host",
    screenshot: "/shots/game-0810-sixfold-ledger.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0810 sixfold ledger, a small original browser game.",
  },
  // Quiet Candidates. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0811-quiet-candidates.
  {
    slug: "game-0811-quiet-candidates",
    surface: "app",
    title: "Quiet Candidates",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0811-quiet-candidates",
    canonical: "host",
    screenshot: "/shots/game-0811-quiet-candidates.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0811 quiet candidates, a small original browser game.",
  },
  // Rising Glass. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0812-rising-glass.
  {
    slug: "game-0812-rising-glass",
    surface: "app",
    title: "Rising Glass",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0812-rising-glass",
    canonical: "host",
    screenshot: "/shots/game-0812-rising-glass.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0812 rising glass, a small original browser game.",
  },
  // Bent Region Atlas. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0813-bent-region-atlas.
  {
    slug: "game-0813-bent-region-atlas",
    surface: "app",
    title: "Bent Region Atlas",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0813-bent-region-atlas",
    canonical: "host",
    screenshot: "/shots/game-0813-bent-region-atlas.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0813 bent region atlas, a small original browser game.",
  },
  // Pocket Word Voyage. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0814-pocket-word-voyage.
  {
    slug: "game-0814-pocket-word-voyage",
    surface: "app",
    title: "Pocket Word Voyage",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0814-pocket-word-voyage",
    canonical: "host",
    screenshot: "/shots/game-0814-pocket-word-voyage.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0814 pocket word voyage, a small original browser game.",
  },
  // Crossing Orchard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0815-crossing-orchard.
  {
    slug: "game-0815-crossing-orchard",
    surface: "app",
    title: "Crossing Orchard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0815-crossing-orchard",
    canonical: "host",
    screenshot: "/shots/game-0815-crossing-orchard.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0815 crossing orchard, a small original browser game.",
  },
  // Pixel Tide Postcard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0816-pixel-tide-postcard.
  {
    slug: "game-0816-pixel-tide-postcard",
    surface: "app",
    title: "Pixel Tide Postcard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0816-pixel-tide-postcard",
    canonical: "host",
    screenshot: "/shots/game-0816-pixel-tide-postcard.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0816 pixel tide postcard, a small original browser game.",
  },
  // Prism Parcel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0817-prism-parcel.
  {
    slug: "game-0817-prism-parcel",
    surface: "app",
    title: "Prism Parcel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0817-prism-parcel",
    canonical: "host",
    screenshot: "/shots/game-0817-prism-parcel.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0817 prism parcel, a small original browser game.",
  },
  // Reedbank Crossing. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0818-reedbank-crossing.
  {
    slug: "game-0818-reedbank-crossing",
    surface: "app",
    title: "Reedbank Crossing",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0818-reedbank-crossing",
    canonical: "host",
    screenshot: "/shots/game-0818-reedbank-crossing.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0818 reedbank crossing, a small original browser game.",
  },
  // Burrow Shepherd. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0819-burrow-shepherd.
  {
    slug: "game-0819-burrow-shepherd",
    surface: "app",
    title: "Burrow Shepherd",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0819-burrow-shepherd",
    canonical: "host",
    screenshot: "/shots/game-0819-burrow-shepherd.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0819 burrow shepherd, a small original browser game.",
  },
  // Counterweight Catch. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0820-counterweight-catch.
  {
    slug: "game-0820-counterweight-catch",
    surface: "app",
    title: "Counterweight Catch",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0820-counterweight-catch",
    canonical: "host",
    screenshot: "/shots/game-0820-counterweight-catch.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0820 counterweight catch, a small original browser game.",
  },
  // Copper Key Circuit. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0821-copper-key-circuit.
  {
    slug: "game-0821-copper-key-circuit",
    surface: "app",
    title: "Copper Key Circuit",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0821-copper-key-circuit",
    canonical: "host",
    screenshot: "/shots/game-0821-copper-key-circuit.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0821 copper key circuit, a small original browser game.",
  },
  // Ruleglass Laboratory. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0822-ruleglass-laboratory.
  {
    slug: "game-0822-ruleglass-laboratory",
    surface: "app",
    title: "Ruleglass Laboratory",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0822-ruleglass-laboratory",
    canonical: "host",
    screenshot: "/shots/game-0822-ruleglass-laboratory.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0822 ruleglass laboratory, a small original browser game.",
  },
  // Frontier Flags. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0823-frontier-flags.
  {
    slug: "game-0823-frontier-flags",
    surface: "app",
    title: "Frontier Flags",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0823-frontier-flags",
    canonical: "host",
    screenshot: "/shots/game-0823-frontier-flags.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0823 frontier flags, a small original browser game.",
  },
  // Midnight Skater. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0824-midnight-skater.
  {
    slug: "game-0824-midnight-skater",
    surface: "app",
    title: "Midnight Skater",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0824-midnight-skater",
    canonical: "host",
    screenshot: "/shots/game-0824-midnight-skater.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0824 midnight skater, a small original browser game.",
  },
  // Yesterday’s Footsteps. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0825-yesterday-s-footsteps.
  {
    slug: "game-0825-yesterday-s-footsteps",
    surface: "app",
    title: "Yesterday’s Footsteps",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0825-yesterday-s-footsteps",
    canonical: "host",
    screenshot: "/shots/game-0825-yesterday-s-footsteps.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0825 yesterday s footsteps, a small original browser game.",
  },
  // Kindred Polarity. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0826-kindred-polarity.
  {
    slug: "game-0826-kindred-polarity",
    surface: "app",
    title: "Kindred Polarity",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0826-kindred-polarity",
    canonical: "host",
    screenshot: "/shots/game-0826-kindred-polarity.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0826 kindred polarity, a small original browser game.",
  },
  // Folded Glide. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0827-folded-glide.
  {
    slug: "game-0827-folded-glide",
    surface: "app",
    title: "Folded Glide",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0827-folded-glide",
    canonical: "host",
    screenshot: "/shots/game-0827-folded-glide.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0827 folded glide, a small original browser game.",
  },
  // Paired Pressure. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0828-paired-pressure.
  {
    slug: "game-0828-paired-pressure",
    surface: "app",
    title: "Paired Pressure",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0828-paired-pressure",
    canonical: "host",
    screenshot: "/shots/game-0828-paired-pressure.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0828 paired pressure, a small original browser game.",
  },
  // Mischief Marsh. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0829-mischief-marsh.
  {
    slug: "game-0829-mischief-marsh",
    surface: "app",
    title: "Mischief Marsh",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0829-mischief-marsh",
    canonical: "host",
    screenshot: "/shots/game-0829-mischief-marsh.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0829 mischief marsh, a small original browser game.",
  },
  // Obsidian Picross Delve. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0830-obsidian-picross-delve.
  {
    slug: "game-0830-obsidian-picross-delve",
    surface: "app",
    title: "Obsidian Picross Delve",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0830-obsidian-picross-delve",
    canonical: "host",
    screenshot: "/shots/game-0830-obsidian-picross-delve.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0830 obsidian picross delve, a small original browser game.",
  },
  // Gravity Foundry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0831-gravity-foundry.
  {
    slug: "game-0831-gravity-foundry",
    surface: "app",
    title: "Gravity Foundry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0831-gravity-foundry",
    canonical: "host",
    screenshot: "/shots/game-0831-gravity-foundry.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0831 gravity foundry, a small original browser game.",
  },
  // Neighborhood Quilt. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0832-neighborhood-quilt.
  {
    slug: "game-0832-neighborhood-quilt",
    surface: "app",
    title: "Neighborhood Quilt",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0832-neighborhood-quilt",
    canonical: "host",
    screenshot: "/shots/game-0832-neighborhood-quilt.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0832 neighborhood quilt, a small original browser game.",
  },
  // Two Paws Warehouse. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0833-two-paws-warehouse.
  {
    slug: "game-0833-two-paws-warehouse",
    surface: "app",
    title: "Two Paws Warehouse",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0833-two-paws-warehouse",
    canonical: "host",
    screenshot: "/shots/game-0833-two-paws-warehouse.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0833 two paws warehouse, a small original browser game.",
  },
  // The Contrary Door. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0834-the-contrary-door.
  {
    slug: "game-0834-the-contrary-door",
    surface: "app",
    title: "The Contrary Door",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0834-the-contrary-door",
    canonical: "host",
    screenshot: "/shots/game-0834-the-contrary-door.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0834 the contrary door, a small original browser game.",
  },
  // Thermal Courtesy. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0835-thermal-courtesy.
  {
    slug: "game-0835-thermal-courtesy",
    surface: "app",
    title: "Thermal Courtesy",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0835-thermal-courtesy",
    canonical: "host",
    screenshot: "/shots/game-0835-thermal-courtesy.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0835 thermal courtesy, a small original browser game.",
  },
  // Picnic Portions. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0836-picnic-portions.
  {
    slug: "game-0836-picnic-portions",
    surface: "app",
    title: "Picnic Portions",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0836-picnic-portions",
    canonical: "host",
    screenshot: "/shots/game-0836-picnic-portions.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0836 picnic portions, a small original browser game.",
  },
  // Castle Footing Survey. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0837-castle-footing-survey.
  {
    slug: "game-0837-castle-footing-survey",
    surface: "app",
    title: "Castle Footing Survey",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0837-castle-footing-survey",
    canonical: "host",
    screenshot: "/shots/game-0837-castle-footing-survey.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0837 castle footing survey, a small original browser game.",
  },
  // One Quiet Check. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0838-one-quiet-check.
  {
    slug: "game-0838-one-quiet-check",
    surface: "app",
    title: "One Quiet Check",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0838-one-quiet-check",
    canonical: "host",
    screenshot: "/shots/game-0838-one-quiet-check.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0838 one quiet check, a small original browser game.",
  },
  // Clockwork Pursuit. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0839-clockwork-pursuit.
  {
    slug: "game-0839-clockwork-pursuit",
    surface: "app",
    title: "Clockwork Pursuit",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0839-clockwork-pursuit",
    canonical: "host",
    screenshot: "/shots/game-0839-clockwork-pursuit.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0839 clockwork pursuit, a small original browser game.",
  },
  // Conductive Courtesy Test. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0840-conductive-courtesy-test.
  {
    slug: "game-0840-conductive-courtesy-test",
    surface: "app",
    title: "Conductive Courtesy Test",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0840-conductive-courtesy-test",
    canonical: "host",
    screenshot: "/shots/game-0840-conductive-courtesy-test.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0840 conductive courtesy test, a small original browser game.",
  },
  // Meridian Survey. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0841-meridian-survey.
  {
    slug: "game-0841-meridian-survey",
    surface: "app",
    title: "Meridian Survey",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0841-meridian-survey",
    canonical: "host",
    screenshot: "/shots/game-0841-meridian-survey.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0841 meridian survey, a small original browser game.",
  },
  // Pigment Almanac. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0842-pigment-almanac.
  {
    slug: "game-0842-pigment-almanac",
    surface: "app",
    title: "Pigment Almanac",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0842-pigment-almanac",
    canonical: "host",
    screenshot: "/shots/game-0842-pigment-almanac.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0842 pigment almanac, a small original browser game.",
  },
  // Triad Prospect. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0843-triad-prospect.
  {
    slug: "game-0843-triad-prospect",
    surface: "app",
    title: "Triad Prospect",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0843-triad-prospect",
    canonical: "host",
    screenshot: "/shots/game-0843-triad-prospect.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0843 triad prospect, a small original browser game.",
  },
  // Gel Drift. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0844-gel-drift.
  {
    slug: "game-0844-gel-drift",
    surface: "app",
    title: "Gel Drift",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0844-gel-drift",
    canonical: "host",
    screenshot: "/shots/game-0844-gel-drift.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0844 gel drift, a small original browser game.",
  },
  // Crown Passage. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0845-crown-passage.
  {
    slug: "game-0845-crown-passage",
    surface: "app",
    title: "Crown Passage",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0845-crown-passage",
    canonical: "host",
    screenshot: "/shots/game-0845-crown-passage.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0845 crown passage, a small original browser game.",
  },
  // Lantern Uprising. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0846-lantern-uprising.
  {
    slug: "game-0846-lantern-uprising",
    surface: "app",
    title: "Lantern Uprising",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0846-lantern-uprising",
    canonical: "host",
    screenshot: "/shots/game-0846-lantern-uprising.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0846 lantern uprising, a small original browser game.",
  },
  // Coral Ink Expedition. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0847-coral-ink-expedition.
  {
    slug: "game-0847-coral-ink-expedition",
    surface: "app",
    title: "Coral Ink Expedition",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0847-coral-ink-expedition",
    canonical: "host",
    screenshot: "/shots/game-0847-coral-ink-expedition.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0847 coral ink expedition, a small original browser game.",
  },
  // Leftward Echo. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0848-leftward-echo.
  {
    slug: "game-0848-leftward-echo",
    surface: "app",
    title: "Leftward Echo",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0848-leftward-echo",
    canonical: "host",
    screenshot: "/shots/game-0848-leftward-echo.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0848 leftward echo, a small original browser game.",
  },
  // Sunstone Mosaic. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0849-sunstone-mosaic.
  {
    slug: "game-0849-sunstone-mosaic",
    surface: "app",
    title: "Sunstone Mosaic",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0849-sunstone-mosaic",
    canonical: "host",
    screenshot: "/shots/game-0849-sunstone-mosaic.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0849 sunstone mosaic, a small original browser game.",
  },
  // Folded Freight. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0850-folded-freight.
  {
    slug: "game-0850-folded-freight",
    surface: "app",
    title: "Folded Freight",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0850-folded-freight",
    canonical: "host",
    screenshot: "/shots/game-0850-folded-freight.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0850 folded freight, a small original browser game.",
  },
  // Pocket Mechanic. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0851-pocket-mechanic.
  {
    slug: "game-0851-pocket-mechanic",
    surface: "app",
    title: "Pocket Mechanic",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0851-pocket-mechanic",
    canonical: "host",
    screenshot: "/shots/game-0851-pocket-mechanic.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0851 pocket mechanic, a small original browser game.",
  },
  // Dot Conductor. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0852-dot-conductor.
  {
    slug: "game-0852-dot-conductor",
    surface: "app",
    title: "Dot Conductor",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0852-dot-conductor",
    canonical: "host",
    screenshot: "/shots/game-0852-dot-conductor.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0852 dot conductor, a small original browser game.",
  },
  // Pawprint Quilt. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0853-pawprint-quilt.
  {
    slug: "game-0853-pawprint-quilt",
    surface: "app",
    title: "Pawprint Quilt",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0853-pawprint-quilt",
    canonical: "host",
    screenshot: "/shots/game-0853-pawprint-quilt.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0853 pawprint quilt, a small original browser game.",
  },
  // Amber Fieldwork. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0854-amber-fieldwork.
  {
    slug: "game-0854-amber-fieldwork",
    surface: "app",
    title: "Amber Fieldwork",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0854-amber-fieldwork",
    canonical: "host",
    screenshot: "/shots/game-0854-amber-fieldwork.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0854 amber fieldwork, a small original browser game.",
  },
  // Causeway Workshop. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0855-causeway-workshop.
  {
    slug: "game-0855-causeway-workshop",
    surface: "app",
    title: "Causeway Workshop",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0855-causeway-workshop",
    canonical: "host",
    screenshot: "/shots/game-0855-causeway-workshop.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0855 causeway workshop, a small original browser game.",
  },
  // Atlas of Missing Things. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0856-atlas-of-missing-things.
  {
    slug: "game-0856-atlas-of-missing-things",
    surface: "app",
    title: "Atlas of Missing Things",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0856-atlas-of-missing-things",
    canonical: "host",
    screenshot: "/shots/game-0856-atlas-of-missing-things.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0856 atlas of missing things, a small original browser game.",
  },
  // Oddsignal Archive. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0857-oddsignal-archive.
  {
    slug: "game-0857-oddsignal-archive",
    surface: "app",
    title: "Oddsignal Archive",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0857-oddsignal-archive",
    canonical: "host",
    screenshot: "/shots/game-0857-oddsignal-archive.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0857 oddsignal archive, a small original browser game.",
  },
  // Vault Fellowship. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0858-vault-fellowship.
  {
    slug: "game-0858-vault-fellowship",
    surface: "app",
    title: "Vault Fellowship",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0858-vault-fellowship",
    canonical: "host",
    screenshot: "/shots/game-0858-vault-fellowship.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0858 vault fellowship, a small original browser game.",
  },
  // Sequence Lanterns. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0859-sequence-lanterns.
  {
    slug: "game-0859-sequence-lanterns",
    surface: "app",
    title: "Sequence Lanterns",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0859-sequence-lanterns",
    canonical: "host",
    screenshot: "/shots/game-0859-sequence-lanterns.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0859 sequence lanterns, a small original browser game.",
  },
  // Equation Dispatch. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0860-equation-dispatch.
  {
    slug: "game-0860-equation-dispatch",
    surface: "app",
    title: "Equation Dispatch",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0860-equation-dispatch",
    canonical: "host",
    screenshot: "/shots/game-0860-equation-dispatch.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0860 equation dispatch, a small original browser game.",
  },
  // Frost Courier. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0861-frost-courier.
  {
    slug: "game-0861-frost-courier",
    surface: "app",
    title: "Frost Courier",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0861-frost-courier",
    canonical: "host",
    screenshot: "/shots/game-0861-frost-courier.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0861 frost courier, a small original browser game.",
  },
  // Velvet Alarm. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0862-velvet-alarm.
  {
    slug: "game-0862-velvet-alarm",
    surface: "app",
    title: "Velvet Alarm",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0862-velvet-alarm",
    canonical: "host",
    screenshot: "/shots/game-0862-velvet-alarm.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0862 velvet alarm, a small original browser game.",
  },
  // Portal Circuitry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0863-portal-circuitry.
  {
    slug: "game-0863-portal-circuitry",
    surface: "app",
    title: "Portal Circuitry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0863-portal-circuitry",
    canonical: "host",
    screenshot: "/shots/game-0863-portal-circuitry.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0863 portal circuitry, a small original browser game.",
  },
  // Caged Numerals. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0864-caged-numerals.
  {
    slug: "game-0864-caged-numerals",
    surface: "app",
    title: "Caged Numerals",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0864-caged-numerals",
    canonical: "host",
    screenshot: "/shots/game-0864-caged-numerals.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0864 caged numerals, a small original browser game.",
  },
  // Clearview Survey. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0865-clearview-survey.
  {
    slug: "game-0865-clearview-survey",
    surface: "app",
    title: "Clearview Survey",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0865-clearview-survey",
    canonical: "host",
    screenshot: "/shots/game-0865-clearview-survey.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0865 clearview survey, a small original browser game.",
  },
  // Hearthbound Charts. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0866-hearthbound-charts.
  {
    slug: "game-0866-hearthbound-charts",
    surface: "app",
    title: "Hearthbound Charts",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0866-hearthbound-charts",
    canonical: "host",
    screenshot: "/shots/game-0866-hearthbound-charts.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0866 hearthbound charts, a small original browser game.",
  },
  // Form Orchard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0867-form-orchard.
  {
    slug: "game-0867-form-orchard",
    surface: "app",
    title: "Form Orchard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0867-form-orchard",
    canonical: "host",
    screenshot: "/shots/game-0867-form-orchard.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0867 form orchard, a small original browser game.",
  },
  // Hexgap Census. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0868-hexgap-census.
  {
    slug: "game-0868-hexgap-census",
    surface: "app",
    title: "Hexgap Census",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0868-hexgap-census",
    canonical: "host",
    screenshot: "/shots/game-0868-hexgap-census.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0868 hexgap census, a small original browser game.",
  },
  // Toolbound Delve. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0869-toolbound-delve.
  {
    slug: "game-0869-toolbound-delve",
    surface: "app",
    title: "Toolbound Delve",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0869-toolbound-delve",
    canonical: "host",
    screenshot: "/shots/game-0869-toolbound-delve.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0869 toolbound delve, a small original browser game.",
  },
  // Crate Orchard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0870-crate-orchard.
  {
    slug: "game-0870-crate-orchard",
    surface: "app",
    title: "Crate Orchard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0870-crate-orchard",
    canonical: "host",
    screenshot: "/shots/game-0870-crate-orchard.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0870 crate orchard, a small original browser game.",
  },
  // Prism Footpath. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0871-prism-footpath.
  {
    slug: "game-0871-prism-footpath",
    surface: "app",
    title: "Prism Footpath",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0871-prism-footpath",
    canonical: "host",
    screenshot: "/shots/game-0871-prism-footpath.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0871 prism footpath, a small original browser game.",
  },
  // Cloak Causeway. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0872-cloak-causeway.
  {
    slug: "game-0872-cloak-causeway",
    surface: "app",
    title: "Cloak Causeway",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0872-cloak-causeway",
    canonical: "host",
    screenshot: "/shots/game-0872-cloak-causeway.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0872 cloak causeway, a small original browser game.",
  },
  // Charge Pilgrimage. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0873-charge-pilgrimage.
  {
    slug: "game-0873-charge-pilgrimage",
    surface: "app",
    title: "Charge Pilgrimage",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0873-charge-pilgrimage",
    canonical: "host",
    screenshot: "/shots/game-0873-charge-pilgrimage.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0873 charge pilgrimage, a small original browser game.",
  },
  // Fourfold Observatory. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0874-fourfold-observatory.
  {
    slug: "game-0874-fourfold-observatory",
    surface: "app",
    title: "Fourfold Observatory",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0874-fourfold-observatory",
    canonical: "host",
    screenshot: "/shots/game-0874-fourfold-observatory.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0874 fourfold observatory, a small original browser game.",
  },
  // Marble Mineplan. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0875-marble-mineplan.
  {
    slug: "game-0875-marble-mineplan",
    surface: "app",
    title: "Marble Mineplan",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0875-marble-mineplan",
    canonical: "host",
    screenshot: "/shots/game-0875-marble-mineplan.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0875 marble mineplan, a small original browser game.",
  },
  // Nightshift Cart. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0876-nightshift-cart.
  {
    slug: "game-0876-nightshift-cart",
    surface: "app",
    title: "Nightshift Cart",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0876-nightshift-cart",
    canonical: "host",
    screenshot: "/shots/game-0876-nightshift-cart.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0876 nightshift cart, a small original browser game.",
  },
  // Bramble Reach. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0877-bramble-reach.
  {
    slug: "game-0877-bramble-reach",
    surface: "app",
    title: "Bramble Reach",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0877-bramble-reach",
    canonical: "host",
    screenshot: "/shots/game-0877-bramble-reach.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0877 bramble reach, a small original browser game.",
  },
  // Tidal Isles. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0878-tidal-isles.
  {
    slug: "game-0878-tidal-isles",
    surface: "app",
    title: "Tidal Isles",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0878-tidal-isles",
    canonical: "host",
    screenshot: "/shots/game-0878-tidal-isles.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0878 tidal isles, a small original browser game.",
  },
  // Thread Exchange. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0879-thread-exchange.
  {
    slug: "game-0879-thread-exchange",
    surface: "app",
    title: "Thread Exchange",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0879-thread-exchange",
    canonical: "host",
    screenshot: "/shots/game-0879-thread-exchange.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0879 thread exchange, a small original browser game.",
  },
  // Rising Prospect. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0880-rising-prospect.
  {
    slug: "game-0880-rising-prospect",
    surface: "app",
    title: "Rising Prospect",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0880-rising-prospect",
    canonical: "host",
    screenshot: "/shots/game-0880-rising-prospect.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0880 rising prospect, a small original browser game.",
  },
  // Gravity Orchard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0881-gravity-orchard.
  {
    slug: "game-0881-gravity-orchard",
    surface: "app",
    title: "Gravity Orchard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0881-gravity-orchard",
    canonical: "host",
    screenshot: "/shots/game-0881-gravity-orchard.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0881 gravity orchard, a small original browser game.",
  },
  // Lantern Footsteps. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0882-lantern-footsteps.
  {
    slug: "game-0882-lantern-footsteps",
    surface: "app",
    title: "Lantern Footsteps",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0882-lantern-footsteps",
    canonical: "host",
    screenshot: "/shots/game-0882-lantern-footsteps.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0882 lantern footsteps, a small original browser game.",
  },
  // Pepperleaf Portrait. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0883-pepperleaf-portrait.
  {
    slug: "game-0883-pepperleaf-portrait",
    surface: "app",
    title: "Pepperleaf Portrait",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0883-pepperleaf-portrait",
    canonical: "host",
    screenshot: "/shots/game-0883-pepperleaf-portrait.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0883 pepperleaf portrait, a small original browser game.",
  },
  // Tether Post. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0884-tether-post.
  {
    slug: "game-0884-tether-post",
    surface: "app",
    title: "Tether Post",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0884-tether-post",
    canonical: "host",
    screenshot: "/shots/game-0884-tether-post.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0884 tether post, a small original browser game.",
  },
  // Knightglass Ledger. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0885-knightglass-ledger.
  {
    slug: "game-0885-knightglass-ledger",
    surface: "app",
    title: "Knightglass Ledger",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0885-knightglass-ledger",
    canonical: "host",
    screenshot: "/shots/game-0885-knightglass-ledger.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0885 knightglass ledger, a small original browser game.",
  },
  // Quiet Alcove. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0886-quiet-alcove.
  {
    slug: "game-0886-quiet-alcove",
    surface: "app",
    title: "Quiet Alcove",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0886-quiet-alcove",
    canonical: "host",
    screenshot: "/shots/game-0886-quiet-alcove.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0886 quiet alcove, a small original browser game.",
  },
  // Chromatic Quilt. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0887-chromatic-quilt.
  {
    slug: "game-0887-chromatic-quilt",
    surface: "app",
    title: "Chromatic Quilt",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0887-chromatic-quilt",
    canonical: "host",
    screenshot: "/shots/game-0887-chromatic-quilt.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0887 chromatic quilt, a small original browser game.",
  },
  // Shared Ink Study. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0888-shared-ink-study.
  {
    slug: "game-0888-shared-ink-study",
    surface: "app",
    title: "Shared Ink Study",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0888-shared-ink-study",
    canonical: "host",
    screenshot: "/shots/game-0888-shared-ink-study.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0888 shared ink study, a small original browser game.",
  },
  // Apart Number Garden. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0889-apart-number-garden.
  {
    slug: "game-0889-apart-number-garden",
    surface: "app",
    title: "Apart Number Garden",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0889-apart-number-garden",
    canonical: "host",
    screenshot: "/shots/game-0889-apart-number-garden.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0889 apart number garden, a small original browser game.",
  },
  // Remote Reactor. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0890-remote-reactor.
  {
    slug: "game-0890-remote-reactor",
    surface: "app",
    title: "Remote Reactor",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0890-remote-reactor",
    canonical: "host",
    screenshot: "/shots/game-0890-remote-reactor.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0890 remote reactor, a small original browser game.",
  },
  // Pocket Switchboard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0891-pocket-switchboard.
  {
    slug: "game-0891-pocket-switchboard",
    surface: "app",
    title: "Pocket Switchboard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0891-pocket-switchboard",
    canonical: "host",
    screenshot: "/shots/game-0891-pocket-switchboard.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0891 pocket switchboard, a small original browser game.",
  },
  // Glyph Orchard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0892-glyph-orchard.
  {
    slug: "game-0892-glyph-orchard",
    surface: "app",
    title: "Glyph Orchard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0892-glyph-orchard",
    canonical: "host",
    screenshot: "/shots/game-0892-glyph-orchard.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0892 glyph orchard, a small original browser game.",
  },
  // Marsh Companions. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0893-marsh-companions.
  {
    slug: "game-0893-marsh-companions",
    surface: "app",
    title: "Marsh Companions",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0893-marsh-companions",
    canonical: "host",
    screenshot: "/shots/game-0893-marsh-companions.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0893 marsh companions, a small original browser game.",
  },
  // Fossil Mosaic. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0894-fossil-mosaic.
  {
    slug: "game-0894-fossil-mosaic",
    surface: "app",
    title: "Fossil Mosaic",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0894-fossil-mosaic",
    canonical: "host",
    screenshot: "/shots/game-0894-fossil-mosaic.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0894 fossil mosaic, a small original browser game.",
  },
  // Tea Cat Numbers. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0895-tea-cat-numbers.
  {
    slug: "game-0895-tea-cat-numbers",
    surface: "app",
    title: "Tea Cat Numbers",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0895-tea-cat-numbers",
    canonical: "host",
    screenshot: "/shots/game-0895-tea-cat-numbers.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0895 tea cat numbers, a small original browser game.",
  },
  // Between the Crusts. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0896-between-the-crusts.
  {
    slug: "game-0896-between-the-crusts",
    surface: "app",
    title: "Between the Crusts",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0896-between-the-crusts",
    canonical: "host",
    screenshot: "/shots/game-0896-between-the-crusts.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0896 between the crusts, a small original browser game.",
  },
  // Zero Loom. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0897-zero-loom.
  {
    slug: "game-0897-zero-loom",
    surface: "app",
    title: "Zero Loom",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0897-zero-loom",
    canonical: "host",
    screenshot: "/shots/game-0897-zero-loom.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0897 zero loom, a small original browser game.",
  },
  // Yesterday Gate. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0898-yesterday-gate.
  {
    slug: "game-0898-yesterday-gate",
    surface: "app",
    title: "Yesterday Gate",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0898-yesterday-gate",
    canonical: "host",
    screenshot: "/shots/game-0898-yesterday-gate.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0898 yesterday gate, a small original browser game.",
  },
  // Rainward Allotment. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0899-rainward-allotment.
  {
    slug: "game-0899-rainward-allotment",
    surface: "app",
    title: "Rainward Allotment",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0899-rainward-allotment",
    canonical: "host",
    screenshot: "/shots/game-0899-rainward-allotment.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0899 rainward allotment, a small original browser game.",
  },
  // Survey of Quiet Stars. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0900-survey-of-quiet-stars.
  {
    slug: "game-0900-survey-of-quiet-stars",
    surface: "app",
    title: "Survey of Quiet Stars",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0900-survey-of-quiet-stars",
    canonical: "host",
    screenshot: "/shots/game-0900-survey-of-quiet-stars.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0900 survey of quiet stars, a small original browser game.",
  },
  // Rolling Aqueduct. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0901-rolling-aqueduct.
  {
    slug: "game-0901-rolling-aqueduct",
    surface: "app",
    title: "Rolling Aqueduct",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0901-rolling-aqueduct",
    canonical: "host",
    screenshot: "/shots/game-0901-rolling-aqueduct.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0901 rolling aqueduct, a small original browser game.",
  },
  // Turnbound Circuit. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0902-turnbound-circuit.
  {
    slug: "game-0902-turnbound-circuit",
    surface: "app",
    title: "Turnbound Circuit",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0902-turnbound-circuit",
    canonical: "host",
    screenshot: "/shots/game-0902-turnbound-circuit.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0902 turnbound circuit, a small original browser game.",
  },
  // Mercury Steps. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0903-mercury-steps.
  {
    slug: "game-0903-mercury-steps",
    surface: "app",
    title: "Mercury Steps",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0903-mercury-steps",
    canonical: "host",
    screenshot: "/shots/game-0903-mercury-steps.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0903 mercury steps, a small original browser game.",
  },
  // Atom Atelier. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0904-atom-atelier.
  {
    slug: "game-0904-atom-atelier",
    surface: "app",
    title: "Atom Atelier",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0904-atom-atelier",
    canonical: "host",
    screenshot: "/shots/game-0904-atom-atelier.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0904 atom atelier, a small original browser game.",
  },
  // Clockwork Crown. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0905-clockwork-crown.
  {
    slug: "game-0905-clockwork-crown",
    surface: "app",
    title: "Clockwork Crown",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0905-clockwork-crown",
    canonical: "host",
    screenshot: "/shots/game-0905-clockwork-crown.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0905 clockwork crown, a small original browser game.",
  },
  // Cloud Foundry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0906-cloud-foundry.
  {
    slug: "game-0906-cloud-foundry",
    surface: "app",
    title: "Cloud Foundry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0906-cloud-foundry",
    canonical: "host",
    screenshot: "/shots/game-0906-cloud-foundry.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0906 cloud foundry, a small original browser game.",
  },
  // Sumwind Sprint. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0907-sumwind-sprint.
  {
    slug: "game-0907-sumwind-sprint",
    surface: "app",
    title: "Sumwind Sprint",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0907-sumwind-sprint",
    canonical: "host",
    screenshot: "/shots/game-0907-sumwind-sprint.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0907 sumwind sprint, a small original browser game.",
  },
  // Relay Logic Workshop. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0908-relay-logic-workshop.
  {
    slug: "game-0908-relay-logic-workshop",
    surface: "app",
    title: "Relay Logic Workshop",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0908-relay-logic-workshop",
    canonical: "host",
    screenshot: "/shots/game-0908-relay-logic-workshop.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0908 relay logic workshop, a small original browser game.",
  },
  // Three Beat Relay. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0909-three-beat-relay.
  {
    slug: "game-0909-three-beat-relay",
    surface: "app",
    title: "Three Beat Relay",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0909-three-beat-relay",
    canonical: "host",
    screenshot: "/shots/game-0909-three-beat-relay.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0909 three beat relay, a small original browser game.",
  },
  // Prism Crossing. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0910-prism-crossing.
  {
    slug: "game-0910-prism-crossing",
    surface: "app",
    title: "Prism Crossing",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0910-prism-crossing",
    canonical: "host",
    screenshot: "/shots/game-0910-prism-crossing.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0910 prism crossing, a small original browser game.",
  },
  // Keykeeper Trail. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0911-keykeeper-trail.
  {
    slug: "game-0911-keykeeper-trail",
    surface: "app",
    title: "Keykeeper Trail",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0911-keykeeper-trail",
    canonical: "host",
    screenshot: "/shots/game-0911-keykeeper-trail.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0911 keykeeper trail, a small original browser game.",
  },
  // Pawpath Shuffler. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0912-pawpath-shuffler.
  {
    slug: "game-0912-pawpath-shuffler",
    surface: "app",
    title: "Pawpath Shuffler",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0912-pawpath-shuffler",
    canonical: "host",
    screenshot: "/shots/game-0912-pawpath-shuffler.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0912 pawpath shuffler, a small original browser game.",
  },
  // Mossword Clearing. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0913-mossword-clearing.
  {
    slug: "game-0913-mossword-clearing",
    surface: "app",
    title: "Mossword Clearing",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0913-mossword-clearing",
    canonical: "host",
    screenshot: "/shots/game-0913-mossword-clearing.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0913 mossword clearing, a small original browser game.",
  },
  // Grove Sealkeeper. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0914-grove-sealkeeper.
  {
    slug: "game-0914-grove-sealkeeper",
    surface: "app",
    title: "Grove Sealkeeper",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0914-grove-sealkeeper",
    canonical: "host",
    screenshot: "/shots/game-0914-grove-sealkeeper.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0914 grove sealkeeper, a small original browser game.",
  },
  // Whispering Rooms. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0915-whispering-rooms.
  {
    slug: "game-0915-whispering-rooms",
    surface: "app",
    title: "Whispering Rooms",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0915-whispering-rooms",
    canonical: "host",
    screenshot: "/shots/game-0915-whispering-rooms.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0915 whispering rooms, a small original browser game.",
  },
  // Berry Bond. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0916-berry-bond.
  {
    slug: "game-0916-berry-bond",
    surface: "app",
    title: "Berry Bond",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0916-berry-bond",
    canonical: "host",
    screenshot: "/shots/game-0916-berry-bond.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0916 berry bond, a small original browser game.",
  },
  // Shielded Dispatch. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0917-shielded-dispatch.
  {
    slug: "game-0917-shielded-dispatch",
    surface: "app",
    title: "Shielded Dispatch",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0917-shielded-dispatch",
    canonical: "host",
    screenshot: "/shots/game-0917-shielded-dispatch.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0917 shielded dispatch, a small original browser game.",
  },
  // Softblock Press. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0918-softblock-press.
  {
    slug: "game-0918-softblock-press",
    surface: "app",
    title: "Softblock Press",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0918-softblock-press",
    canonical: "host",
    screenshot: "/shots/game-0918-softblock-press.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0918 softblock press, a small original browser game.",
  },
  // Sapper Passage. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0919-sapper-passage.
  {
    slug: "game-0919-sapper-passage",
    surface: "app",
    title: "Sapper Passage",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0919-sapper-passage",
    canonical: "host",
    screenshot: "/shots/game-0919-sapper-passage.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0919 sapper passage, a small original browser game.",
  },
  // Forty Two Tributaries. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0920-forty-two-tributaries.
  {
    slug: "game-0920-forty-two-tributaries",
    surface: "app",
    title: "Forty Two Tributaries",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0920-forty-two-tributaries",
    canonical: "host",
    screenshot: "/shots/game-0920-forty-two-tributaries.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0920 forty two tributaries, a small original browser game.",
  },
  // Copper Keykeepers. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0921-copper-keykeepers.
  {
    slug: "game-0921-copper-keykeepers",
    surface: "app",
    title: "Copper Keykeepers",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0921-copper-keykeepers",
    canonical: "host",
    screenshot: "/shots/game-0921-copper-keykeepers.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0921 copper keykeepers, a small original browser game.",
  },
  // Aperture Messengers. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0922-aperture-messengers.
  {
    slug: "game-0922-aperture-messengers",
    surface: "app",
    title: "Aperture Messengers",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0922-aperture-messengers",
    canonical: "host",
    screenshot: "/shots/game-0922-aperture-messengers.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0922 aperture messengers, a small original browser game.",
  },
  // Fresco Parcels. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0923-fresco-parcels.
  {
    slug: "game-0923-fresco-parcels",
    surface: "app",
    title: "Fresco Parcels",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0923-fresco-parcels",
    canonical: "host",
    screenshot: "/shots/game-0923-fresco-parcels.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0923 fresco parcels, a small original browser game.",
  },
  // Circuit Patchwork. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0924-circuit-patchwork.
  {
    slug: "game-0924-circuit-patchwork",
    surface: "app",
    title: "Circuit Patchwork",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0924-circuit-patchwork",
    canonical: "host",
    screenshot: "/shots/game-0924-circuit-patchwork.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0924 circuit patchwork, a small original browser game.",
  },
  // Honeycomb Relics. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0925-honeycomb-relics.
  {
    slug: "game-0925-honeycomb-relics",
    surface: "app",
    title: "Honeycomb Relics",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0925-honeycomb-relics",
    canonical: "host",
    screenshot: "/shots/game-0925-honeycomb-relics.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0925 honeycomb relics, a small original browser game.",
  },
  // Stillhour Orchard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0926-stillhour-orchard.
  {
    slug: "game-0926-stillhour-orchard",
    surface: "app",
    title: "Stillhour Orchard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0926-stillhour-orchard",
    canonical: "host",
    screenshot: "/shots/game-0926-stillhour-orchard.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0926 stillhour orchard, a small original browser game.",
  },
  // Willow Courier. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0927-willow-courier.
  {
    slug: "game-0927-willow-courier",
    surface: "app",
    title: "Willow Courier",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0927-willow-courier",
    canonical: "host",
    screenshot: "/shots/game-0927-willow-courier.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0927 willow courier, a small original browser game.",
  },
  // Cage Sum Conservatory. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0928-cage-sum-conservatory.
  {
    slug: "game-0928-cage-sum-conservatory",
    surface: "app",
    title: "Cage Sum Conservatory",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0928-cage-sum-conservatory",
    canonical: "host",
    screenshot: "/shots/game-0928-cage-sum-conservatory.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0928 cage sum conservatory, a small original browser game.",
  },
  // Silhouette Foundry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0929-silhouette-foundry.
  {
    slug: "game-0929-silhouette-foundry",
    surface: "app",
    title: "Silhouette Foundry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0929-silhouette-foundry",
    canonical: "host",
    screenshot: "/shots/game-0929-silhouette-foundry.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0929 silhouette foundry, a small original browser game.",
  },
  // Volt Orchard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0930-volt-orchard.
  {
    slug: "game-0930-volt-orchard",
    surface: "app",
    title: "Volt Orchard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0930-volt-orchard",
    canonical: "host",
    screenshot: "/shots/game-0930-volt-orchard.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0930 volt orchard, a small original browser game.",
  },
  // Sluice Spa. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0931-sluice-spa.
  {
    slug: "game-0931-sluice-spa",
    surface: "app",
    title: "Sluice Spa",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0931-sluice-spa",
    canonical: "host",
    screenshot: "/shots/game-0931-sluice-spa.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0931 sluice spa, a small original browser game.",
  },
  // Ivory Detour. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0932-ivory-detour.
  {
    slug: "game-0932-ivory-detour",
    surface: "app",
    title: "Ivory Detour",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0932-ivory-detour",
    canonical: "host",
    screenshot: "/shots/game-0932-ivory-detour.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Play game 0932 ivory detour, a small original browser game.",
  },
  // Wanted vs Got. Static Vite on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/wanted-vs-got.
  {
    slug: "wanted-vs-got",
    surface: "app",
    title: "Wanted vs Got",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/wanted-vs-got",
    canonical: "host",
    screenshot: "/shots/wanted-vs-got.webp",
    shippedAt: "2026-10-30",
    status: "live",
    blurb: "Compare what you wanted with what you actually got.",
  },
  // Scarehouse. Static Vite on digitalnative R2 techdemos-static (pool-000 Workers Free cap / 10037); no auth, storage none. Repo over9000-apps/scarehouse.
  {
    slug: "scarehouse",
    surface: "app",
    title: "Scarehouse",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/scarehouse",
    canonical: "host",
    screenshot: "/shots/scarehouse.webp",
    shippedAt: "2026-10-31",
    status: "live",
    blurb: "Slow-burn dread chamber with procedural sound, flicker, and an optional local-only webcam unease mode.",
  },
  // Pumpkin Carve. Static Vite on digitalnative R2 techdemos-static (pool-000 Workers Free cap / 10037); no auth, storage none. Repo over9000-apps/pumpkin-carve.
  {
    slug: "pumpkin-carve",
    surface: "app",
    title: "Pumpkin Carve",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/pumpkin-carve",
    canonical: "host",
    screenshot: "/shots/pumpkin-carve.webp",
    shippedAt: "2026-10-31",
    status: "live",
    blurb: "Carve a glowing jack-o'-lantern with templates, candle light-through, score, and PNG export.",
  },
  // Halloween Films 226. Static Vite on digitalnative R2 techdemos-static (pool-000 Workers Free cap / 10037); no auth, storage none. Repo over9000-apps/halloween-films-226.
  {
    slug: "halloween-films-226",
    surface: "app",
    title: "Halloween Films 226",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/halloween-films-226",
    canonical: "host",
    screenshot: "/shots/halloween-films-226.webp",
    shippedAt: "2026-10-31",
    status: "live",
    blurb: "Top 226 Halloween and horror films ranked from real IMDb dataset scores with a transparent Bayesian formula.",
  },
  // Costume Conjurer. Static Vite on digitalnative R2 techdemos-static (pool-000 Workers Free cap / 10037); no auth, storage none. Repo over9000-apps/costume-conjurer.
  {
    slug: "costume-conjurer",
    surface: "app",
    title: "Costume Conjurer",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/costume-conjurer",
    canonical: "host",
    screenshot: "/shots/costume-conjurer.webp",
    shippedAt: "2026-10-31",
    status: "live",
    blurb: "Spin wearable Halloween costume ideas with colorways, vibes, and DIY notes.",
  },
  // Candy Haul. Static Vite on digitalnative R2 techdemos-static (pool-000 Workers Free cap / 10037); no auth, storage none. Repo over9000-apps/candy-haul.
  {
    slug: "candy-haul",
    surface: "app",
    title: "Candy Haul",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/candy-haul",
    canonical: "host",
    screenshot: "/shots/candy-haul.webp",
    shippedAt: "2026-10-31",
    status: "live",
    blurb: "Estimate trick-or-treat candy pounds from blocks, door rates, and bag strategy.",
  },
  // Haunted Blueprint. Static Vite on digitalnative R2 techdemos-static (pool-000 Workers Free cap / 10037); no auth, storage none. Repo over9000-apps/haunted-blueprint.
  {
    slug: "haunted-blueprint",
    surface: "app",
    title: "Haunted Blueprint",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/haunted-blueprint",
    canonical: "host",
    screenshot: "/shots/haunted-blueprint.webp",
    shippedAt: "2026-10-31",
    status: "live",
    blurb: "Drag rooms, traps, and mercy exits into a haunted-house floor plan and watch fear climb.",
  },
  // Ouija Toy. Static Vite on digitalnative R2 techdemos-static (pool-000 Workers Free cap / 10037); no auth, storage none. Repo over9000-apps/ouija-toy.
  {
    slug: "ouija-toy",
    surface: "app",
    title: "Ouija Toy",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/ouija-toy",
    canonical: "host",
    screenshot: "/shots/ouija-toy.webp",
    shippedAt: "2026-10-31",
    status: "live",
    blurb: "A clearly labeled party toy that slides toward playful answers — math, not spirits.",
  },
  // Spooky Names. Static Vite on digitalnative R2 techdemos-static (pool-000 Workers Free cap / 10037); no auth, storage none. Repo over9000-apps/spooky-names.
  {
    slug: "spooky-names",
    surface: "app",
    title: "Spooky Names",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/spooky-names",
    canonical: "host",
    screenshot: "/shots/spooky-names.webp",
    shippedAt: "2026-10-31",
    status: "live",
    blurb: "Generate cursed pet names, villain aliases, and coven handles in one click.",
  },
  // Link Preview. Overflow Worker on over9000-pool-001 (digitalnative at 500-Worker create cap / 10037); no auth, storage none. Repo over9000-apps/linkpreview.
  {
    slug: "linkpreview",
    surface: "app",
    title: "Link Preview",
    upstream: "https://linkpreview.over9000-pool-001.workers.dev",
    canonical: "host",
    screenshot: "/shots/linkpreview.webp",
    shippedAt: "2026-10-31",
    status: "live",
    blurb: "Paste a URL and see the card it would get across iMessage, Android, X, Meta, Slack, Discord, LinkedIn, Telegram, and more.",
  },
  // Seedbox. Overflow Worker on over9000-pool-001 (digitalnative at 500-Worker create cap).
  // Private owner-only seedbox; Sign in with Over9000 public PKCE + allowlist in Worker.
  // Repo over9000-apps/seedbox. Origin: Cloudflare Tunnel → box qBittorrent API.
  {
    slug: "seedbox",
    surface: "app",
    title: "Seedbox",
    upstream: "https://seedbox.over9000-pool-001.workers.dev",
    canonical: "host",
    screenshot: "/shots/seedbox.webp",
    shippedAt: "2026-10-31",
    status: "live",
    featured: false,
    storage: "none",
    auth: {
      mode: "required",
      kind: "public",
      scopes: ["openid", "profile", "email"],
      redirectUris: ["https://seedbox.over9000.cc/api/auth/callback"],
    },
    blurb: "Private cloud seedbox: magnet or .torrent on the box, stream and download in the browser.",
  },
  // Chorus Quilt. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0066-chorus-quilt.
  {
    slug: "game-0066-chorus-quilt",
    surface: "app",
    title: "Chorus Quilt",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0066-chorus-quilt",
    canonical: "host",
    screenshot: "/shots/game-0066-chorus-quilt.webp",
    shippedAt: "2026-11-01",
    status: "live",
    blurb: "Play game 0066 chorus quilt, a small original browser game.",
  },
  // Elemental Vault. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0067-elemental-vault.
  {
    slug: "game-0067-elemental-vault",
    surface: "app",
    title: "Elemental Vault",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0067-elemental-vault",
    canonical: "host",
    screenshot: "/shots/game-0067-elemental-vault.webp",
    shippedAt: "2026-11-01",
    status: "live",
    blurb: "Play game 0067 elemental vault, a small original browser game.",
  },
  // Duskward. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0068-duskward.
  {
    slug: "game-0068-duskward",
    surface: "app",
    title: "Duskward",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0068-duskward",
    canonical: "host",
    screenshot: "/shots/game-0068-duskward.webp",
    shippedAt: "2026-11-01",
    status: "live",
    blurb: "Play game 0068 duskward, a small original browser game.",
  },
  // Spark Hatchery. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0360-spark-hatchery.
  {
    slug: "game-0360-spark-hatchery",
    surface: "app",
    title: "Spark Hatchery",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0360-spark-hatchery",
    canonical: "host",
    screenshot: "/shots/game-0360-spark-hatchery.webp",
    shippedAt: "2026-11-01",
    status: "live",
    blurb: "Play game 0360 spark hatchery, a small original browser game.",
  },

  // Token Golf. Static Next export on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/token-golf.
  {
    slug: "token-golf",
    surface: "app",
    title: "Token Golf",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/token-golf",
    canonical: "host",
    screenshot: "/shots/token-golf.webp",
    shippedAt: "2026-11-02",
    status: "live",
    blurb: "Hunt Linux path names that cost as few tokens as possible — par is one token on o200k_base.",
  },

  // Underbyte. Overflow Worker on over9000-pool-001 (pool-000 at Free 100 cap); TanStack Start/nitro SSR, no auth, storage none. Repo over9000-apps/underbyte.
  {
    slug: "underbyte",
    surface: "app",
    title: "Underbyte",
    upstream: "https://underbyte.over9000-pool-001.workers.dev",
    canonical: "host",
    screenshot: "/shots/underbyte.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Compress a pixel picture under a byte budget and see exactly what the file lost.",
  },
  // Type Tower. Overflow Worker on over9000-pool-001; TanStack Start/nitro SSR, no auth, storage none. Repo over9000-apps/type-tower.
  {
    slug: "type-tower",
    surface: "app",
    title: "Type Tower",
    upstream: "https://type-tower.over9000-pool-001.workers.dev",
    canonical: "host",
    screenshot: "/shots/type-tower.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Type the word, set the stone. Typos make the tower lean.",
  },
  // Neural Net Garden. Overflow Worker on over9000-pool-001; TanStack Start/nitro SSR, no auth, storage none. Repo over9000-apps/neural-net-garden.
  {
    slug: "neural-net-garden",
    surface: "app",
    title: "Neural Net Garden",
    upstream: "https://neural-net-garden.over9000-pool-001.workers.dev",
    canonical: "host",
    screenshot: "/shots/neural-net-garden.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Drag neurons and weights to separate two kinds of blooms. Fewer neurons score higher.",
  },
  // Consensus Clash. Overflow Worker on over9000-pool-001; TanStack Start/nitro SSR, no auth, storage none. Repo over9000-apps/consensus-clash.
  {
    slug: "consensus-clash",
    surface: "app",
    title: "Consensus Clash",
    upstream: "https://consensus-clash.over9000-pool-001.workers.dev",
    canonical: "host",
    screenshot: "/shots/consensus-clash.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Pass-and-play Raft party game. Elect a leader before the log diverges.",
  },
  // Fold. Overflow Worker on over9000-pool-001; TanStack Start/nitro SSR, no auth, storage none. Repo over9000-apps/fold.
  {
    slug: "fold",
    surface: "app",
    title: "Fold",
    upstream: "https://fold.over9000-pool-001.workers.dev",
    canonical: "host",
    screenshot: "/shots/fold.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Video prompt studio for My Paper Pet marketing videos.",
  },
  // Archive Arcade. Reused unused digitalnative Worker copy-of-p5-js-react-atelier-src-test
  // (account at create-cap). Dedicated D1+R2. Stripe checkout off until secrets exist.
  // Repo over9000-apps/archive-arcade.
  {
    // ship: archive-arcade dn-slot 20261102
  slug: "archive-arcade",
    surface: "app",
    title: "Archive Arcade",
    upstream: "https://copy-of-p5-js-react-atelier-src-test.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/archive-arcade.webp",
    shippedAt: "2026-11-02",
    status: "live",
    storage: "dedicated",
    auth: {
      mode: "optional",
      kind: "public",
      scopes: ["openid", "profile", "email"],
      redirectUris: ["https://archive-arcade.over9000.cc/api/v1/auth/callback"],
    },
    blurb: "Accession-to-digital-object intake desk for community archives — checksum-bound digitization handover.",
  },

  // Repo over9000-apps/bin-ballet.
  {
    // ship: bin-ballet dn-slot 20261103
  slug: "bin-ballet",
    surface: "app",
    title: "Bin Ballet",
    upstream: "https://testcloudflareos-backend.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/bin-ballet.webp",
    shippedAt: "2026-11-03",
    status: "live",
    storage: "dedicated",
    auth: {
      mode: "optional",
      kind: "public",
      scopes: ["openid", "profile", "email"],
      redirectUris: ["https://bin-ballet.over9000.cc/api/v1/auth/callback"],
    },
    blurb: "Compare two indoor bin layouts against the same feasible orders before moving anything.",
  },

  // Repo over9000-apps/bolt-bureau.
  {
    // ship: bolt-bureau dn-slot 20261103
  slug: "bolt-bureau",
    surface: "app",
    title: "Bolt Bureau",
    upstream: "https://testcloudflareos-gk-scheduler.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/bolt-bureau.webp",
    shippedAt: "2026-11-03",
    status: "live",
    storage: "dedicated",
    auth: {
      mode: "optional",
      kind: "public",
      scopes: ["openid", "profile", "email"],
      redirectUris: ["https://bolt-bureau.over9000.cc/api/v1/auth/callback"],
    },
    blurb: "Deadstock allocation worktable — reserve contiguous fabric intervals and issue cut/remnant tickets.",
  },

  // Repo over9000-apps/bread-balance.
  {
    // ship: bread-balance dn-slot 20261103
  slug: "bread-balance",
    surface: "app",
    title: "Bread Balance",
    upstream: "https://testcloudflareos-gk-context.digitalnative.workers.dev",
    canonical: "host",
    screenshot: "/shots/bread-balance.webp",
    shippedAt: "2026-11-03",
    status: "live",
    storage: "dedicated",
    auth: {
      mode: "optional",
      kind: "public",
      scopes: ["openid", "profile", "email"],
      redirectUris: ["https://bread-balance.over9000.cc/api/v1/auth/callback"],
    },
    blurb: "Wholesale bakery standing-order amendment desk for tomorrow's bake list.",
  },

  // TikTok X-Ray. Overflow Worker on over9000-pool-001 (digitalnative + pool-000 at Worker caps);
  // Worker + SPA assets, no auth, storage none. Repo over9000-apps/tiktok-xray.
  {
    slug: "tiktok-xray",
    surface: "app",
    title: "TikTok X-Ray",
    upstream: "https://tiktok-xray.over9000-pool-001.workers.dev",
    canonical: "host",
    screenshot: "/shots/tiktok-xray.webp",
    shippedAt: "2026-11-03",
    status: "live",
    storage: "none",
    blurb: "Swipe a For You feed while the algorithm X-ray shows rec_* scores, HAR context, emergency batches, and LIVE inject — an educational replica of ayoships' TikTok reverse-engineering.",
  },
  // Lever. Overflow Worker on over9000-pool-001; TanStack Start/nitro SSR, no auth, storage none. Repo over9000-apps/lever.
  {
    slug: "lever",
    surface: "app",
    title: "Lever",
    upstream: "https://lever.over9000-pool-001.workers.dev",
    canonical: "host",
    screenshot: "/shots/lever.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Pull a lever. Weld two arcade classics into one short game.",
  },
  // Feral. Overflow Worker on over9000-pool-001; TanStack Start/nitro SSR, no auth, storage none. Repo over9000-apps/feral.
  {
    slug: "feral",
    surface: "app",
    title: "Feral",
    upstream: "https://feral.over9000-pool-001.workers.dev",
    canonical: "host",
    screenshot: "/shots/feral.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Wild meme reactor — mash prompts into chaotic image reactions.",
  },
  // Gravity Golf. Overflow Worker on over9000-pool-001; TanStack Start/nitro SSR, no auth, storage none. Repo over9000-apps/gravity-golf.
  {
    slug: "gravity-golf",
    surface: "app",
    title: "Gravity Golf",
    upstream: "https://gravity-golf.over9000-pool-001.workers.dev",
    canonical: "host",
    screenshot: "/shots/gravity-golf.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Mini-golf where you place planets and let gravity bend the putt.",
  },
  // Don't Panic. Overflow Worker on over9000-pool-001; TanStack Start/nitro SSR, no auth, storage none. Repo over9000-apps/dont-panic.
  {
    slug: "dont-panic",
    surface: "app",
    title: "Don't Panic",
    upstream: "https://dont-panic.over9000-pool-001.workers.dev",
    canonical: "host",
    screenshot: "/shots/dont-panic.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "A pocket field guide to the galaxy — browse, search, and save entries.",
  },

  // Mineral Archive. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0069-mineral-archive.
  {
    slug: "game-0069-mineral-archive",
    surface: "app",
    title: "Mineral Archive",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0069-mineral-archive",
    canonical: "host",
    screenshot: "/shots/game-0069-mineral-archive.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0069 mineral archive, a small original browser game.",
  },
  // Tower Delver. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0070-tower-delver.
  {
    slug: "game-0070-tower-delver",
    surface: "app",
    title: "Tower Delver",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0070-tower-delver",
    canonical: "host",
    screenshot: "/shots/game-0070-tower-delver.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0070 tower delver, a small original browser game.",
  },
  // Tether Rescue. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0071-tether-rescue.
  {
    slug: "game-0071-tether-rescue",
    surface: "app",
    title: "Tether Rescue",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0071-tether-rescue",
    canonical: "host",
    screenshot: "/shots/game-0071-tether-rescue.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0071 tether rescue, a small original browser game.",
  },
  // Zero G Loom. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0072-zero-g-loom.
  {
    slug: "game-0072-zero-g-loom",
    surface: "app",
    title: "Zero G Loom",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0072-zero-g-loom",
    canonical: "host",
    screenshot: "/shots/game-0072-zero-g-loom.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0072 zero g loom, a small original browser game.",
  },
  // Orbit Orchard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0073-orbit-orchard.
  {
    slug: "game-0073-orbit-orchard",
    surface: "app",
    title: "Orbit Orchard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0073-orbit-orchard",
    canonical: "host",
    screenshot: "/shots/game-0073-orbit-orchard.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0073 orbit orchard, a small original browser game.",
  },
  // Lantern Slide. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0074-lantern-slide.
  {
    slug: "game-0074-lantern-slide",
    surface: "app",
    title: "Lantern Slide",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0074-lantern-slide",
    canonical: "host",
    screenshot: "/shots/game-0074-lantern-slide.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0074 lantern slide, a small original browser game.",
  },
  // Ripple Foundry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0075-ripple-foundry.
  {
    slug: "game-0075-ripple-foundry",
    surface: "app",
    title: "Ripple Foundry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0075-ripple-foundry",
    canonical: "host",
    screenshot: "/shots/game-0075-ripple-foundry.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0075 ripple foundry, a small original browser game.",
  },
  // Ring Reactor. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0076-ring-reactor.
  {
    slug: "game-0076-ring-reactor",
    surface: "app",
    title: "Ring Reactor",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0076-ring-reactor",
    canonical: "host",
    screenshot: "/shots/game-0076-ring-reactor.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0076 ring reactor, a small original browser game.",
  },
  // Marshmallow Press. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0077-marshmallow-press.
  {
    slug: "game-0077-marshmallow-press",
    surface: "app",
    title: "Marshmallow Press",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0077-marshmallow-press",
    canonical: "host",
    screenshot: "/shots/game-0077-marshmallow-press.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0077 marshmallow press, a small original browser game.",
  },
  // Era Staircase. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0078-era-staircase.
  {
    slug: "game-0078-era-staircase",
    surface: "app",
    title: "Era Staircase",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0078-era-staircase",
    canonical: "host",
    screenshot: "/shots/game-0078-era-staircase.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0078 era staircase, a small original browser game.",
  },
  // Twin Isle Wardens. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0080-twin-isle-wardens.
  {
    slug: "game-0080-twin-isle-wardens",
    surface: "app",
    title: "Twin Isle Wardens",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0080-twin-isle-wardens",
    canonical: "host",
    screenshot: "/shots/game-0080-twin-isle-wardens.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0080 twin isle wardens, a small original browser game.",
  },
  // Fortress Circuit. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0081-fortress-circuit.
  {
    slug: "game-0081-fortress-circuit",
    surface: "app",
    title: "Fortress Circuit",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0081-fortress-circuit",
    canonical: "host",
    screenshot: "/shots/game-0081-fortress-circuit.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0081 fortress circuit, a small original browser game.",
  },
  // Nebula Endzone. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0082-nebula-endzone.
  {
    slug: "game-0082-nebula-endzone",
    surface: "app",
    title: "Nebula Endzone",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0082-nebula-endzone",
    canonical: "host",
    screenshot: "/shots/game-0082-nebula-endzone.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0082 nebula endzone, a small original browser game.",
  },
  // Glacier Shells. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0083-glacier-shells.
  {
    slug: "game-0083-glacier-shells",
    surface: "app",
    title: "Glacier Shells",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0083-glacier-shells",
    canonical: "host",
    screenshot: "/shots/game-0083-glacier-shells.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0083 glacier shells, a small original browser game.",
  },
  // Summit Capsules. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0084-summit-capsules.
  {
    slug: "game-0084-summit-capsules",
    surface: "app",
    title: "Summit Capsules",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0084-summit-capsules",
    canonical: "host",
    screenshot: "/shots/game-0084-summit-capsules.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0084 summit capsules, a small original browser game.",
  },
  // Stencil Bloom. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0085-stencil-bloom.
  {
    slug: "game-0085-stencil-bloom",
    surface: "app",
    title: "Stencil Bloom",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0085-stencil-bloom",
    canonical: "host",
    screenshot: "/shots/game-0085-stencil-bloom.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0085 stencil bloom, a small original browser game.",
  },
  // Cardfall Gambit. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0086-cardfall-gambit.
  {
    slug: "game-0086-cardfall-gambit",
    surface: "app",
    title: "Cardfall Gambit",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0086-cardfall-gambit",
    canonical: "host",
    screenshot: "/shots/game-0086-cardfall-gambit.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0086 cardfall gambit, a small original browser game.",
  },
  // Exact Rune Duel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0087-exact-rune-duel.
  {
    slug: "game-0087-exact-rune-duel",
    surface: "app",
    title: "Exact Rune Duel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0087-exact-rune-duel",
    canonical: "host",
    screenshot: "/shots/game-0087-exact-rune-duel.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0087 exact rune duel, a small original browser game.",
  },
  // Gemlock Quarry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0088-gemlock-quarry.
  {
    slug: "game-0088-gemlock-quarry",
    surface: "app",
    title: "Gemlock Quarry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0088-gemlock-quarry",
    canonical: "host",
    screenshot: "/shots/game-0088-gemlock-quarry.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0088 gemlock quarry, a small original browser game.",
  },
  // Combo Atelier. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0089-combo-atelier.
  {
    slug: "game-0089-combo-atelier",
    surface: "app",
    title: "Combo Atelier",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0089-combo-atelier",
    canonical: "host",
    screenshot: "/shots/game-0089-combo-atelier.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0089 combo atelier, a small original browser game.",
  },
  // Dice Harbor. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0090-dice-harbor.
  {
    slug: "game-0090-dice-harbor",
    surface: "app",
    title: "Dice Harbor",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0090-dice-harbor",
    canonical: "host",
    screenshot: "/shots/game-0090-dice-harbor.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0090 dice harbor, a small original browser game.",
  },
  // Weekend Satchel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0091-weekend-satchel.
  {
    slug: "game-0091-weekend-satchel",
    surface: "app",
    title: "Weekend Satchel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0091-weekend-satchel",
    canonical: "host",
    screenshot: "/shots/game-0091-weekend-satchel.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0091 weekend satchel, a small original browser game.",
  },
  // Waxlight Vigil. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0092-waxlight-vigil.
  {
    slug: "game-0092-waxlight-vigil",
    surface: "app",
    title: "Waxlight Vigil",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0092-waxlight-vigil",
    canonical: "host",
    screenshot: "/shots/game-0092-waxlight-vigil.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0092 waxlight vigil, a small original browser game.",
  },
  // Noodle Constellation. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0093-noodle-constellation.
  {
    slug: "game-0093-noodle-constellation",
    surface: "app",
    title: "Noodle Constellation",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0093-noodle-constellation",
    canonical: "host",
    screenshot: "/shots/game-0093-noodle-constellation.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0093 noodle constellation, a small original browser game.",
  },
  // Culture Beaker. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0094-culture-beaker.
  {
    slug: "game-0094-culture-beaker",
    surface: "app",
    title: "Culture Beaker",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0094-culture-beaker",
    canonical: "host",
    screenshot: "/shots/game-0094-culture-beaker.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0094 culture beaker, a small original browser game.",
  },
  // Beacon Drop School. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0095-beacon-drop-school.
  {
    slug: "game-0095-beacon-drop-school",
    surface: "app",
    title: "Beacon Drop School",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0095-beacon-drop-school",
    canonical: "host",
    screenshot: "/shots/game-0095-beacon-drop-school.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0095 beacon drop school, a small original browser game.",
  },
  // Trench Wall. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0096-trench-wall.
  {
    slug: "game-0096-trench-wall",
    surface: "app",
    title: "Trench Wall",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0096-trench-wall",
    canonical: "host",
    screenshot: "/shots/game-0096-trench-wall.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0096 trench wall, a small original browser game.",
  },
  // Bubble Rune Parade. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0097-bubble-rune-parade.
  {
    slug: "game-0097-bubble-rune-parade",
    surface: "app",
    title: "Bubble Rune Parade",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0097-bubble-rune-parade",
    canonical: "host",
    screenshot: "/shots/game-0097-bubble-rune-parade.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0097 bubble rune parade, a small original browser game.",
  },
  // Survey Stack. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0098-survey-stack.
  {
    slug: "game-0098-survey-stack",
    surface: "app",
    title: "Survey Stack",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0098-survey-stack",
    canonical: "host",
    screenshot: "/shots/game-0098-survey-stack.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0098 survey stack, a small original browser game.",
  },
  // Reed Tugger. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0099-reed-tugger.
  {
    slug: "game-0099-reed-tugger",
    surface: "app",
    title: "Reed Tugger",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0099-reed-tugger",
    canonical: "host",
    screenshot: "/shots/game-0099-reed-tugger.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0099 reed tugger, a small original browser game.",
  },
  // Underworld Mason. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0100-underworld-mason.
  {
    slug: "game-0100-underworld-mason",
    surface: "app",
    title: "Underworld Mason",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0100-underworld-mason",
    canonical: "host",
    screenshot: "/shots/game-0100-underworld-mason.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0100 underworld mason, a small original browser game.",
  },
  // Bramble Duel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0109-bramble-duel.
  {
    slug: "game-0109-bramble-duel",
    surface: "app",
    title: "Bramble Duel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0109-bramble-duel",
    canonical: "host",
    screenshot: "/shots/game-0109-bramble-duel.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0109 bramble duel, a small original browser game.",
  },
  // Relic Rain. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0110-relic-rain.
  {
    slug: "game-0110-relic-rain",
    surface: "app",
    title: "Relic Rain",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0110-relic-rain",
    canonical: "host",
    screenshot: "/shots/game-0110-relic-rain.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0110 relic rain, a small original browser game.",
  },
  // Mosaic Melt. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0111-mosaic-melt.
  {
    slug: "game-0111-mosaic-melt",
    surface: "app",
    title: "Mosaic Melt",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0111-mosaic-melt",
    canonical: "host",
    screenshot: "/shots/game-0111-mosaic-melt.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0111 mosaic melt, a small original browser game.",
  },
  // Folded Vault. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0112-folded-vault.
  {
    slug: "game-0112-folded-vault",
    surface: "app",
    title: "Folded Vault",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0112-folded-vault",
    canonical: "host",
    screenshot: "/shots/game-0112-folded-vault.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0112 folded vault, a small original browser game.",
  },
  // Null Prism. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0113-null-prism.
  {
    slug: "game-0113-null-prism",
    surface: "app",
    title: "Null Prism",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0113-null-prism",
    canonical: "host",
    screenshot: "/shots/game-0113-null-prism.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0113 null prism, a small original browser game.",
  },
  // Vial Railway. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0114-vial-railway.
  {
    slug: "game-0114-vial-railway",
    surface: "app",
    title: "Vial Railway",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0114-vial-railway",
    canonical: "host",
    screenshot: "/shots/game-0114-vial-railway.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0114 vial railway, a small original browser game.",
  },
  // Switchback Sums. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0361-switchback-sums.
  {
    slug: "game-0361-switchback-sums",
    surface: "app",
    title: "Switchback Sums",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0361-switchback-sums",
    canonical: "host",
    screenshot: "/shots/game-0361-switchback-sums.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0361 switchback sums, a small original browser game.",
  },
  // Orbit Nursery. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0362-orbit-nursery.
  {
    slug: "game-0362-orbit-nursery",
    surface: "app",
    title: "Orbit Nursery",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0362-orbit-nursery",
    canonical: "host",
    screenshot: "/shots/game-0362-orbit-nursery.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0362 orbit nursery, a small original browser game.",
  },
  // Folded Orchard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0363-folded-orchard.
  {
    slug: "game-0363-folded-orchard",
    surface: "app",
    title: "Folded Orchard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0363-folded-orchard",
    canonical: "host",
    screenshot: "/shots/game-0363-folded-orchard.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0363 folded orchard, a small original browser game.",
  },
  // Cubicle Citrus. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0364-cubicle-citrus.
  {
    slug: "game-0364-cubicle-citrus",
    surface: "app",
    title: "Cubicle Citrus",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0364-cubicle-citrus",
    canonical: "host",
    screenshot: "/shots/game-0364-cubicle-citrus.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0364 cubicle citrus, a small original browser game.",
  },
  // Pearl Ceiling. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0365-pearl-ceiling.
  {
    slug: "game-0365-pearl-ceiling",
    surface: "app",
    title: "Pearl Ceiling",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0365-pearl-ceiling",
    canonical: "host",
    screenshot: "/shots/game-0365-pearl-ceiling.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0365 pearl ceiling, a small original browser game.",
  },
  // Velvet Pressure. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0366-velvet-pressure.
  {
    slug: "game-0366-velvet-pressure",
    surface: "app",
    title: "Velvet Pressure",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0366-velvet-pressure",
    canonical: "host",
    screenshot: "/shots/game-0366-velvet-pressure.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0366 velvet pressure, a small original browser game.",
  },
  // Epoch Lanterns. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0367-epoch-lanterns.
  {
    slug: "game-0367-epoch-lanterns",
    surface: "app",
    title: "Epoch Lanterns",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0367-epoch-lanterns",
    canonical: "host",
    screenshot: "/shots/game-0367-epoch-lanterns.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0367 epoch lanterns, a small original browser game.",
  },
  // Rival Register. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0368-rival-register.
  {
    slug: "game-0368-rival-register",
    surface: "app",
    title: "Rival Register",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0368-rival-register",
    canonical: "host",
    screenshot: "/shots/game-0368-rival-register.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0368 rival register, a small original browser game.",
  },
  // Counterfall. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0115-counterfall.
  {
    slug: "game-0115-counterfall",
    surface: "app",
    title: "Counterfall",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0115-counterfall",
    canonical: "host",
    screenshot: "/shots/game-0115-counterfall.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0115 counterfall, a small original browser game.",
  },
  // Rooftop Sprout. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0116-rooftop-sprout.
  {
    slug: "game-0116-rooftop-sprout",
    surface: "app",
    title: "Rooftop Sprout",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0116-rooftop-sprout",
    canonical: "host",
    screenshot: "/shots/game-0116-rooftop-sprout.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0116 rooftop sprout, a small original browser game.",
  },
  // Little Atmosphere. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0117-little-atmosphere.
  {
    slug: "game-0117-little-atmosphere",
    surface: "app",
    title: "Little Atmosphere",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0117-little-atmosphere",
    canonical: "host",
    screenshot: "/shots/game-0117-little-atmosphere.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0117 little atmosphere, a small original browser game.",
  },
  // Amber Apiary. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0118-amber-apiary.
  {
    slug: "game-0118-amber-apiary",
    surface: "app",
    title: "Amber Apiary",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0118-amber-apiary",
    canonical: "host",
    screenshot: "/shots/game-0118-amber-apiary.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0118 amber apiary, a small original browser game.",
  },
  // Challenge Bastion. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0119-challenge-bastion.
  {
    slug: "game-0119-challenge-bastion",
    surface: "app",
    title: "Challenge Bastion",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0119-challenge-bastion",
    canonical: "host",
    screenshot: "/shots/game-0119-challenge-bastion.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0119 challenge bastion, a small original browser game.",
  },
  // Fivefold Atelier. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0120-fivefold-atelier.
  {
    slug: "game-0120-fivefold-atelier",
    surface: "app",
    title: "Fivefold Atelier",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0120-fivefold-atelier",
    canonical: "host",
    screenshot: "/shots/game-0120-fivefold-atelier.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0120 fivefold atelier, a small original browser game.",
  },
  // Rift Custodian. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0121-rift-custodian.
  {
    slug: "game-0121-rift-custodian",
    surface: "app",
    title: "Rift Custodian",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0121-rift-custodian",
    canonical: "host",
    screenshot: "/shots/game-0121-rift-custodian.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0121 rift custodian, a small original browser game.",
  },
  // Gelatin Harbor. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0122-gelatin-harbor.
  {
    slug: "game-0122-gelatin-harbor",
    surface: "app",
    title: "Gelatin Harbor",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0122-gelatin-harbor",
    canonical: "host",
    screenshot: "/shots/game-0122-gelatin-harbor.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0122 gelatin harbor, a small original browser game.",
  },
  // Lucky Cairn. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0123-lucky-cairn.
  {
    slug: "game-0123-lucky-cairn",
    surface: "app",
    title: "Lucky Cairn",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0123-lucky-cairn",
    canonical: "host",
    screenshot: "/shots/game-0123-lucky-cairn.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0123 lucky cairn, a small original browser game.",
  },
  // Satchel Expedition. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0124-satchel-expedition.
  {
    slug: "game-0124-satchel-expedition",
    surface: "app",
    title: "Satchel Expedition",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0124-satchel-expedition",
    canonical: "host",
    screenshot: "/shots/game-0124-satchel-expedition.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0124 satchel expedition, a small original browser game.",
  },
  // Maskbreak Keep. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0125-maskbreak-keep.
  {
    slug: "game-0125-maskbreak-keep",
    surface: "app",
    title: "Maskbreak Keep",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0125-maskbreak-keep",
    canonical: "host",
    screenshot: "/shots/game-0125-maskbreak-keep.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0125 maskbreak keep, a small original browser game.",
  },
  // Wasteland Ledger. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0126-wasteland-ledger.
  {
    slug: "game-0126-wasteland-ledger",
    surface: "app",
    title: "Wasteland Ledger",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0126-wasteland-ledger",
    canonical: "host",
    screenshot: "/shots/game-0126-wasteland-ledger.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0126 wasteland ledger, a small original browser game.",
  },
  // Skillet Skyline. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0127-skillet-skyline.
  {
    slug: "game-0127-skillet-skyline",
    surface: "app",
    title: "Skillet Skyline",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0127-skillet-skyline",
    canonical: "host",
    screenshot: "/shots/game-0127-skillet-skyline.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0127 skillet skyline, a small original browser game.",
  },
  // Wordfreight. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0128-wordfreight.
  {
    slug: "game-0128-wordfreight",
    surface: "app",
    title: "Wordfreight",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0128-wordfreight",
    canonical: "host",
    screenshot: "/shots/game-0128-wordfreight.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0128 wordfreight, a small original browser game.",
  },
  // Spore Citadel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0129-spore-citadel.
  {
    slug: "game-0129-spore-citadel",
    surface: "app",
    title: "Spore Citadel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0129-spore-citadel",
    canonical: "host",
    screenshot: "/shots/game-0129-spore-citadel.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0129 spore citadel, a small original browser game.",
  },
  // Drizzle Studio. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0130-drizzle-studio.
  {
    slug: "game-0130-drizzle-studio",
    surface: "app",
    title: "Drizzle Studio",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0130-drizzle-studio",
    canonical: "host",
    screenshot: "/shots/game-0130-drizzle-studio.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0130 drizzle studio, a small original browser game.",
  },
  // Astral Brackets. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0131-astral-brackets.
  {
    slug: "game-0131-astral-brackets",
    surface: "app",
    title: "Astral Brackets",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0131-astral-brackets",
    canonical: "host",
    screenshot: "/shots/game-0131-astral-brackets.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0131 astral brackets, a small original browser game.",
  },
  // Cocoa Quarry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0132-cocoa-quarry.
  {
    slug: "game-0132-cocoa-quarry",
    surface: "app",
    title: "Cocoa Quarry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0132-cocoa-quarry",
    canonical: "host",
    screenshot: "/shots/game-0132-cocoa-quarry.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0132 cocoa quarry, a small original browser game.",
  },
  // Neon Rails. Static Vite on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/neon-rails.
  {
    slug: "neon-rails",
    surface: "app",
    title: "Neon Rails",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/neon-rails",
    canonical: "host",
    screenshot: "/shots/neon-rails.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Clean-room three.js endless runner. Not affiliated with SYBO or Subway Surfers.",
  },
  // Silhouette Dock. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0133-silhouette-dock.
  {
    slug: "game-0133-silhouette-dock",
    surface: "app",
    title: "Silhouette Dock",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0133-silhouette-dock",
    canonical: "host",
    screenshot: "/shots/game-0133-silhouette-dock.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0133 silhouette dock, a small original browser game.",
  },
  // Ember Tray. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0134-ember-tray.
  {
    slug: "game-0134-ember-tray",
    surface: "app",
    title: "Ember Tray",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0134-ember-tray",
    canonical: "host",
    screenshot: "/shots/game-0134-ember-tray.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0134 ember tray, a small original browser game.",
  },
  // Ricochet Foundry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0135-ricochet-foundry.
  {
    slug: "game-0135-ricochet-foundry",
    surface: "app",
    title: "Ricochet Foundry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0135-ricochet-foundry",
    canonical: "host",
    screenshot: "/shots/game-0135-ricochet-foundry.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0135 ricochet foundry, a small original browser game.",
  },
  // Tidal Switch. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0136-tidal-switch.
  {
    slug: "game-0136-tidal-switch",
    surface: "app",
    title: "Tidal Switch",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0136-tidal-switch",
    canonical: "host",
    screenshot: "/shots/game-0136-tidal-switch.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0136 tidal switch, a small original browser game.",
  },
  // Prism Bubble Patrol. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0137-prism-bubble-patrol.
  {
    slug: "game-0137-prism-bubble-patrol",
    surface: "app",
    title: "Prism Bubble Patrol",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0137-prism-bubble-patrol",
    canonical: "host",
    screenshot: "/shots/game-0137-prism-bubble-patrol.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0137 prism bubble patrol, a small original browser game.",
  },
  // Chromatic Rain. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0138-chromatic-rain.
  {
    slug: "game-0138-chromatic-rain",
    surface: "app",
    title: "Chromatic Rain",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0138-chromatic-rain",
    canonical: "host",
    screenshot: "/shots/game-0138-chromatic-rain.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0138 chromatic rain, a small original browser game.",
  },
  // Endless Scree. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0139-endless-scree.
  {
    slug: "game-0139-endless-scree",
    surface: "app",
    title: "Endless Scree",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0139-endless-scree",
    canonical: "host",
    screenshot: "/shots/game-0139-endless-scree.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0139 endless scree, a small original browser game.",
  },
  // Pigment Weave. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0140-pigment-weave.
  {
    slug: "game-0140-pigment-weave",
    surface: "app",
    title: "Pigment Weave",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0140-pigment-weave",
    canonical: "host",
    screenshot: "/shots/game-0140-pigment-weave.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0140 pigment weave, a small original browser game.",
  },
  // Pond Parcel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0141-pond-parcel.
  {
    slug: "game-0141-pond-parcel",
    surface: "app",
    title: "Pond Parcel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0141-pond-parcel",
    canonical: "host",
    screenshot: "/shots/game-0141-pond-parcel.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0141 pond parcel, a small original browser game.",
  },
  // Nook Fitter. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0142-nook-fitter.
  {
    slug: "game-0142-nook-fitter",
    surface: "app",
    title: "Nook Fitter",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0142-nook-fitter",
    canonical: "host",
    screenshot: "/shots/game-0142-nook-fitter.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0142 nook fitter, a small original browser game.",
  },
  // Counterweight Cove. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0143-counterweight-cove.
  {
    slug: "game-0143-counterweight-cove",
    surface: "app",
    title: "Counterweight Cove",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0143-counterweight-cove",
    canonical: "host",
    screenshot: "/shots/game-0143-counterweight-cove.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0143 counterweight cove, a small original browser game.",
  },
  // Tackle Case. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0144-tackle-case.
  {
    slug: "game-0144-tackle-case",
    surface: "app",
    title: "Tackle Case",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0144-tackle-case",
    canonical: "host",
    screenshot: "/shots/game-0144-tackle-case.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0144 tackle case, a small original browser game.",
  },
  // Noodle Terrace. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0145-noodle-terrace.
  {
    slug: "game-0145-noodle-terrace",
    surface: "app",
    title: "Noodle Terrace",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0145-noodle-terrace",
    canonical: "host",
    screenshot: "/shots/game-0145-noodle-terrace.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0145 noodle terrace, a small original browser game.",
  },
  // Hexform Harbor. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0146-hexform-harbor.
  {
    slug: "game-0146-hexform-harbor",
    surface: "app",
    title: "Hexform Harbor",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0146-hexform-harbor",
    canonical: "host",
    screenshot: "/shots/game-0146-hexform-harbor.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0146 hexform harbor, a small original browser game.",
  },
  // Amulet Causeway. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0147-amulet-causeway.
  {
    slug: "game-0147-amulet-causeway",
    surface: "app",
    title: "Amulet Causeway",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0147-amulet-causeway",
    canonical: "host",
    screenshot: "/shots/game-0147-amulet-causeway.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0147 amulet causeway, a small original browser game.",
  },
  // Rubble Exchange. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0148-rubble-exchange.
  {
    slug: "game-0148-rubble-exchange",
    surface: "app",
    title: "Rubble Exchange",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0148-rubble-exchange",
    canonical: "host",
    screenshot: "/shots/game-0148-rubble-exchange.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0148 rubble exchange, a small original browser game.",
  },
  // Snack Shift Duo. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0149-snack-shift-duo.
  {
    slug: "game-0149-snack-shift-duo",
    surface: "app",
    title: "Snack Shift Duo",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0149-snack-shift-duo",
    canonical: "host",
    screenshot: "/shots/game-0149-snack-shift-duo.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0149 snack shift duo, a small original browser game.",
  },
  // Tricolor Descent. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0150-tricolor-descent.
  {
    slug: "game-0150-tricolor-descent",
    surface: "app",
    title: "Tricolor Descent",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0150-tricolor-descent",
    canonical: "host",
    screenshot: "/shots/game-0150-tricolor-descent.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0150 tricolor descent, a small original browser game.",
  },
  // Temple Tideguard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0151-temple-tideguard.
  {
    slug: "game-0151-temple-tideguard",
    surface: "app",
    title: "Temple Tideguard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0151-temple-tideguard",
    canonical: "host",
    screenshot: "/shots/game-0151-temple-tideguard.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0151 temple tideguard, a small original browser game.",
  },
  // Third Card Atlas. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0152-third-card-atlas.
  {
    slug: "game-0152-third-card-atlas",
    surface: "app",
    title: "Third Card Atlas",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0152-third-card-atlas",
    canonical: "host",
    screenshot: "/shots/game-0152-third-card-atlas.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0152 third card atlas, a small original browser game.",
  },
  // Lockstep Furnace. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0153-lockstep-furnace.
  {
    slug: "game-0153-lockstep-furnace",
    surface: "app",
    title: "Lockstep Furnace",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0153-lockstep-furnace",
    canonical: "host",
    screenshot: "/shots/game-0153-lockstep-furnace.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0153 lockstep furnace, a small original browser game.",
  },
  // Icecore Prospect. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0154-icecore-prospect.
  {
    slug: "game-0154-icecore-prospect",
    surface: "app",
    title: "Icecore Prospect",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0154-icecore-prospect",
    canonical: "host",
    screenshot: "/shots/game-0154-icecore-prospect.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0154 icecore prospect, a small original browser game.",
  },
  // Layer Toss. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0155-layer-toss.
  {
    slug: "game-0155-layer-toss",
    surface: "app",
    title: "Layer Toss",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0155-layer-toss",
    canonical: "host",
    screenshot: "/shots/game-0155-layer-toss.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0155 layer toss, a small original browser game.",
  },
  // Chunk Pilgrim. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0156-chunk-pilgrim.
  {
    slug: "game-0156-chunk-pilgrim",
    surface: "app",
    title: "Chunk Pilgrim",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0156-chunk-pilgrim",
    canonical: "host",
    screenshot: "/shots/game-0156-chunk-pilgrim.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0156 chunk pilgrim, a small original browser game.",
  },
  // Maze Morsel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0157-maze-morsel.
  {
    slug: "game-0157-maze-morsel",
    surface: "app",
    title: "Maze Morsel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0157-maze-morsel",
    canonical: "host",
    screenshot: "/shots/game-0157-maze-morsel.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0157 maze morsel, a small original browser game.",
  },
  // Memory Furnace. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0369-memory-furnace.
  {
    slug: "game-0369-memory-furnace",
    surface: "app",
    title: "Memory Furnace",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0369-memory-furnace",
    canonical: "host",
    screenshot: "/shots/game-0369-memory-furnace.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0369 memory furnace, a small original browser game.",
  },
  // Sway Spire. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0158-sway-spire.
  {
    slug: "game-0158-sway-spire",
    surface: "app",
    title: "Sway Spire",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0158-sway-spire",
    canonical: "host",
    screenshot: "/shots/game-0158-sway-spire.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0158 sway spire, a small original browser game.",
  },
  // Phase Hexwell. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0159-phase-hexwell.
  {
    slug: "game-0159-phase-hexwell",
    surface: "app",
    title: "Phase Hexwell",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0159-phase-hexwell",
    canonical: "host",
    screenshot: "/shots/game-0159-phase-hexwell.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0159 phase hexwell, a small original browser game.",
  },
  // Handoff Yard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0160-handoff-yard.
  {
    slug: "game-0160-handoff-yard",
    surface: "app",
    title: "Handoff Yard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0160-handoff-yard",
    canonical: "host",
    screenshot: "/shots/game-0160-handoff-yard.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0160 handoff yard, a small original browser game.",
  },
  // Supper Untangled. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0161-supper-untangled.
  {
    slug: "game-0161-supper-untangled",
    surface: "app",
    title: "Supper Untangled",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0161-supper-untangled",
    canonical: "host",
    screenshot: "/shots/game-0161-supper-untangled.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0161 supper untangled, a small original browser game.",
  },
  // Shaft Mosaic. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0162-shaft-mosaic.
  {
    slug: "game-0162-shaft-mosaic",
    surface: "app",
    title: "Shaft Mosaic",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0162-shaft-mosaic",
    canonical: "host",
    screenshot: "/shots/game-0162-shaft-mosaic.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0162 shaft mosaic, a small original browser game.",
  },
  // Satchel Squires. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0163-satchel-squires.
  {
    slug: "game-0163-satchel-squires",
    surface: "app",
    title: "Satchel Squires",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0163-satchel-squires",
    canonical: "host",
    screenshot: "/shots/game-0163-satchel-squires.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0163 satchel squires, a small original browser game.",
  },
  // Clouds and Cargo. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0164-clouds-and-cargo.
  {
    slug: "game-0164-clouds-and-cargo",
    surface: "app",
    title: "Clouds and Cargo",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0164-clouds-and-cargo",
    canonical: "host",
    screenshot: "/shots/game-0164-clouds-and-cargo.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0164 clouds and cargo, a small original browser game.",
  },
  // Lantern Causeway. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0165-lantern-causeway.
  {
    slug: "game-0165-lantern-causeway",
    surface: "app",
    title: "Lantern Causeway",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0165-lantern-causeway",
    canonical: "host",
    screenshot: "/shots/game-0165-lantern-causeway.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0165 lantern causeway, a small original browser game.",
  },
  // Copper Keep. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0166-copper-keep.
  {
    slug: "game-0166-copper-keep",
    surface: "app",
    title: "Copper Keep",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0166-copper-keep",
    canonical: "host",
    screenshot: "/shots/game-0166-copper-keep.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0166 copper keep, a small original browser game.",
  },
  // Bloom Recoil. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0079-bloom-recoil.
  {
    slug: "game-0079-bloom-recoil",
    surface: "app",
    title: "Bloom Recoil",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0079-bloom-recoil",
    canonical: "host",
    screenshot: "/shots/game-0079-bloom-recoil.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0079 bloom recoil, a small original browser game.",
  },
  // Village in the Sky. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0167-village-in-the-sky.
  {
    slug: "game-0167-village-in-the-sky",
    surface: "app",
    title: "Village in the Sky",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0167-village-in-the-sky",
    canonical: "host",
    screenshot: "/shots/game-0167-village-in-the-sky.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0167 village in the sky, a small original browser game.",
  },
  // Petri Picnic. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0168-petri-picnic.
  {
    slug: "game-0168-petri-picnic",
    surface: "app",
    title: "Petri Picnic",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0168-petri-picnic",
    canonical: "host",
    screenshot: "/shots/game-0168-petri-picnic.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0168 petri picnic, a small original browser game.",
  },
  // Moonframe Atelier. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0169-moonframe-atelier.
  {
    slug: "game-0169-moonframe-atelier",
    surface: "app",
    title: "Moonframe Atelier",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0169-moonframe-atelier",
    canonical: "host",
    screenshot: "/shots/game-0169-moonframe-atelier.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0169 moonframe atelier, a small original browser game.",
  },
  // Fusefall Foundry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0170-fusefall-foundry.
  {
    slug: "game-0170-fusefall-foundry",
    surface: "app",
    title: "Fusefall Foundry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0170-fusefall-foundry",
    canonical: "host",
    screenshot: "/shots/game-0170-fusefall-foundry.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0170 fusefall foundry, a small original browser game.",
  },
  // Garden Gasworks. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0171-garden-gasworks.
  {
    slug: "game-0171-garden-gasworks",
    surface: "app",
    title: "Garden Gasworks",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0171-garden-gasworks",
    canonical: "host",
    screenshot: "/shots/game-0171-garden-gasworks.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0171 garden gasworks, a small original browser game.",
  },
  // Chrysalis Queue. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0172-chrysalis-queue.
  {
    slug: "game-0172-chrysalis-queue",
    surface: "app",
    title: "Chrysalis Queue",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0172-chrysalis-queue",
    canonical: "host",
    screenshot: "/shots/game-0172-chrysalis-queue.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0172 chrysalis queue, a small original browser game.",
  },
  // Waffle Wardrobe. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0173-waffle-wardrobe.
  {
    slug: "game-0173-waffle-wardrobe",
    surface: "app",
    title: "Waffle Wardrobe",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0173-waffle-wardrobe",
    canonical: "host",
    screenshot: "/shots/game-0173-waffle-wardrobe.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0173 waffle wardrobe, a small original browser game.",
  },
  // Capsule Conservatory. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0174-capsule-conservatory.
  {
    slug: "game-0174-capsule-conservatory",
    surface: "app",
    title: "Capsule Conservatory",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0174-capsule-conservatory",
    canonical: "host",
    screenshot: "/shots/game-0174-capsule-conservatory.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0174 capsule conservatory, a small original browser game.",
  },
  // Wandering Wallpaper. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0175-wandering-wallpaper.
  {
    slug: "game-0175-wandering-wallpaper",
    surface: "app",
    title: "Wandering Wallpaper",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0175-wandering-wallpaper",
    canonical: "host",
    screenshot: "/shots/game-0175-wandering-wallpaper.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0175 wandering wallpaper, a small original browser game.",
  },
  // Halfglass Trio. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0176-halfglass-trio.
  {
    slug: "game-0176-halfglass-trio",
    surface: "app",
    title: "Halfglass Trio",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0176-halfglass-trio",
    canonical: "host",
    screenshot: "/shots/game-0176-halfglass-trio.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0176 halfglass trio, a small original browser game.",
  },
  // Opposite Sparks. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0177-opposite-sparks.
  {
    slug: "game-0177-opposite-sparks",
    surface: "app",
    title: "Opposite Sparks",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0177-opposite-sparks",
    canonical: "host",
    screenshot: "/shots/game-0177-opposite-sparks.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0177 opposite sparks, a small original browser game.",
  },
  // Twin Comet Loom. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0178-twin-comet-loom.
  {
    slug: "game-0178-twin-comet-loom",
    surface: "app",
    title: "Twin Comet Loom",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0178-twin-comet-loom",
    canonical: "host",
    screenshot: "/shots/game-0178-twin-comet-loom.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0178 twin comet loom, a small original browser game.",
  },
  // Manyform Cabinet. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0179-manyform-cabinet.
  {
    slug: "game-0179-manyform-cabinet",
    surface: "app",
    title: "Manyform Cabinet",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0179-manyform-cabinet",
    canonical: "host",
    screenshot: "/shots/game-0179-manyform-cabinet.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0179 manyform cabinet, a small original browser game.",
  },
  // Reservoir Relay. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0180-reservoir-relay.
  {
    slug: "game-0180-reservoir-relay",
    surface: "app",
    title: "Reservoir Relay",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0180-reservoir-relay",
    canonical: "host",
    screenshot: "/shots/game-0180-reservoir-relay.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0180 reservoir relay, a small original browser game.",
  },
  // Vine Rescue. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0181-vine-rescue.
  {
    slug: "game-0181-vine-rescue",
    surface: "app",
    title: "Vine Rescue",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0181-vine-rescue",
    canonical: "host",
    screenshot: "/shots/game-0181-vine-rescue.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0181 vine rescue, a small original browser game.",
  },
  // Hexa Hearth Duel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0182-hexa-hearth-duel.
  {
    slug: "game-0182-hexa-hearth-duel",
    surface: "app",
    title: "Hexa Hearth Duel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0182-hexa-hearth-duel",
    canonical: "host",
    screenshot: "/shots/game-0182-hexa-hearth-duel.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0182 hexa hearth duel, a small original browser game.",
  },
  // Borrowed Silhouettes. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0183-borrowed-silhouettes.
  {
    slug: "game-0183-borrowed-silhouettes",
    surface: "app",
    title: "Borrowed Silhouettes",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0183-borrowed-silhouettes",
    canonical: "host",
    screenshot: "/shots/game-0183-borrowed-silhouettes.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0183 borrowed silhouettes, a small original browser game.",
  },
  // Cardblade Crypt. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0184-cardblade-crypt.
  {
    slug: "game-0184-cardblade-crypt",
    surface: "app",
    title: "Cardblade Crypt",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0184-cardblade-crypt",
    canonical: "host",
    screenshot: "/shots/game-0184-cardblade-crypt.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0184 cardblade crypt, a small original browser game.",
  },
  // Ninefold Exchange. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0185-ninefold-exchange.
  {
    slug: "game-0185-ninefold-exchange",
    surface: "app",
    title: "Ninefold Exchange",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0185-ninefold-exchange",
    canonical: "host",
    screenshot: "/shots/game-0185-ninefold-exchange.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0185 ninefold exchange, a small original browser game.",
  },
  // Letter Orchard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0186-letter-orchard.
  {
    slug: "game-0186-letter-orchard",
    surface: "app",
    title: "Letter Orchard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0186-letter-orchard",
    canonical: "host",
    screenshot: "/shots/game-0186-letter-orchard.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0186 letter orchard, a small original browser game.",
  },
  // Newt Lift. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0187-wellward-newt.
  {
    slug: "game-0187-wellward-newt",
    surface: "app",
    title: "Newt Lift",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0187-wellward-newt",
    canonical: "host",
    screenshot: "/shots/game-0187-wellward-newt.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0187 wellward newt, a small original browser game.",
  },
  // Sluice Skiff. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0188-jelly-lifeboat.
  {
    slug: "game-0188-jelly-lifeboat",
    surface: "app",
    title: "Sluice Skiff",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0188-jelly-lifeboat",
    canonical: "host",
    screenshot: "/shots/game-0188-jelly-lifeboat.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0188 jelly lifeboat, a small original browser game.",
  },
  // Dusk Parcel Works. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0189-dusk-parcel-works.
  {
    slug: "game-0189-dusk-parcel-works",
    surface: "app",
    title: "Dusk Parcel Works",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0189-dusk-parcel-works",
    canonical: "host",
    screenshot: "/shots/game-0189-dusk-parcel-works.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0189 dusk parcel works, a small original browser game.",
  },
  // Kinetic Stack. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0190-pendulum-pier.
  {
    slug: "game-0190-pendulum-pier",
    surface: "app",
    title: "Kinetic Stack",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0190-pendulum-pier",
    canonical: "host",
    screenshot: "/shots/game-0190-pendulum-pier.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0190 pendulum pier, a small original browser game.",
  },
  // Mallow Reservoir. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0191-mallow-reservoir.
  {
    slug: "game-0191-mallow-reservoir",
    surface: "app",
    title: "Mallow Reservoir",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0191-mallow-reservoir",
    canonical: "host",
    screenshot: "/shots/game-0191-mallow-reservoir.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0191 mallow reservoir, a small original browser game.",
  },
  // One Stamp Orchard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0192-one-stamp-orchard.
  {
    slug: "game-0192-one-stamp-orchard",
    surface: "app",
    title: "One Stamp Orchard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0192-one-stamp-orchard",
    canonical: "host",
    screenshot: "/shots/game-0192-one-stamp-orchard.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0192 one stamp orchard, a small original browser game.",
  },
  // Salvage Squadron. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0193-salvage-squadron.
  {
    slug: "game-0193-salvage-squadron",
    surface: "app",
    title: "Salvage Squadron",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0193-salvage-squadron",
    canonical: "host",
    screenshot: "/shots/game-0193-salvage-squadron.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0193 salvage squadron, a small original browser game.",
  },
  // Ossuary Lift. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0194-ossuary-lift.
  {
    slug: "game-0194-ossuary-lift",
    surface: "app",
    title: "Ossuary Lift",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0194-ossuary-lift",
    canonical: "host",
    screenshot: "/shots/game-0194-ossuary-lift.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0194 ossuary lift, a small original browser game.",
  },
  // Headcount Tram. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0195-headcount-tram.
  {
    slug: "game-0195-headcount-tram",
    surface: "app",
    title: "Headcount Tram",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0195-headcount-tram",
    canonical: "host",
    screenshot: "/shots/game-0195-headcount-tram.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0195 headcount tram, a small original browser game.",
  },
  // Evidence Elevator. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0196-evidence-elevator.
  {
    slug: "game-0196-evidence-elevator",
    surface: "app",
    title: "Evidence Elevator",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0196-evidence-elevator",
    canonical: "host",
    screenshot: "/shots/game-0196-evidence-elevator.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0196 evidence elevator, a small original browser game.",
  },
  // Prism Postage. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0197-prism-postage.
  {
    slug: "game-0197-prism-postage",
    surface: "app",
    title: "Prism Postage",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0197-prism-postage",
    canonical: "host",
    screenshot: "/shots/game-0197-prism-postage.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0197 prism postage, a small original browser game.",
  },
  // Mosaic Noodles. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0198-mosaic-noodles.
  {
    slug: "game-0198-mosaic-noodles",
    surface: "app",
    title: "Mosaic Noodles",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0198-mosaic-noodles",
    canonical: "host",
    screenshot: "/shots/game-0198-mosaic-noodles.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0198 mosaic noodles, a small original browser game.",
  },
  // Marble Conduit. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0199-marble-conduit.
  {
    slug: "game-0199-marble-conduit",
    surface: "app",
    title: "Marble Conduit",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0199-marble-conduit",
    canonical: "host",
    screenshot: "/shots/game-0199-marble-conduit.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0199 marble conduit, a small original browser game.",
  },
  // Ricochet Foundry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0200-ricochet-foundry.
  {
    slug: "game-0200-ricochet-foundry",
    surface: "app",
    title: "Ricochet Foundry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0200-ricochet-foundry",
    canonical: "host",
    screenshot: "/shots/game-0200-ricochet-foundry.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0200 ricochet foundry, a small original browser game.",
  },
  // Cuddle Corners. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0370-cuddle-corners.
  {
    slug: "game-0370-cuddle-corners",
    surface: "app",
    title: "Cuddle Corners",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0370-cuddle-corners",
    canonical: "host",
    screenshot: "/shots/game-0370-cuddle-corners.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0370 cuddle corners, a small original browser game.",
  },
  // Triad Pilgrimage. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0371-triad-pilgrimage.
  {
    slug: "game-0371-triad-pilgrimage",
    surface: "app",
    title: "Triad Pilgrimage",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0371-triad-pilgrimage",
    canonical: "host",
    screenshot: "/shots/game-0371-triad-pilgrimage.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0371 triad pilgrimage, a small original browser game.",
  },
  // Honeycomb Address. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0372-honeycomb-address.
  {
    slug: "game-0372-honeycomb-address",
    surface: "app",
    title: "Honeycomb Address",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0372-honeycomb-address",
    canonical: "host",
    screenshot: "/shots/game-0372-honeycomb-address.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0372 honeycomb address, a small original browser game.",
  },
  // Rune Duelist. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0373-rune-duelist.
  {
    slug: "game-0373-rune-duelist",
    surface: "app",
    title: "Rune Duelist",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0373-rune-duelist",
    canonical: "host",
    screenshot: "/shots/game-0373-rune-duelist.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0373 rune duelist, a small original browser game.",
  },
  // Exponent Exchange. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0374-exponent-exchange.
  {
    slug: "game-0374-exponent-exchange",
    surface: "app",
    title: "Exponent Exchange",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0374-exponent-exchange",
    canonical: "host",
    screenshot: "/shots/game-0374-exponent-exchange.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0374 exponent exchange, a small original browser game.",
  },
  // Cushion Cascade. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0375-cushion-cascade.
  {
    slug: "game-0375-cushion-cascade",
    surface: "app",
    title: "Cushion Cascade",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0375-cushion-cascade",
    canonical: "host",
    screenshot: "/shots/game-0375-cushion-cascade.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0375 cushion cascade, a small original browser game.",
  },
  // Rampart Arithmetic. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0376-rampart-arithmetic.
  {
    slug: "game-0376-rampart-arithmetic",
    surface: "app",
    title: "Rampart Arithmetic",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0376-rampart-arithmetic",
    canonical: "host",
    screenshot: "/shots/game-0376-rampart-arithmetic.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0376 rampart arithmetic, a small original browser game.",
  },
  // Incubator Nine. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0377-incubator-nine.
  {
    slug: "game-0377-incubator-nine",
    surface: "app",
    title: "Incubator Nine",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0377-incubator-nine",
    canonical: "host",
    screenshot: "/shots/game-0377-incubator-nine.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0377 incubator nine, a small original browser game.",
  },
  // Serpent Supper. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0378-serpent-supper.
  {
    slug: "game-0378-serpent-supper",
    surface: "app",
    title: "Serpent Supper",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0378-serpent-supper",
    canonical: "host",
    screenshot: "/shots/game-0378-serpent-supper.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0378 serpent supper, a small original browser game.",
  },
  // Paper Menagerie. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0379-paper-menagerie.
  {
    slug: "game-0379-paper-menagerie",
    surface: "app",
    title: "Paper Menagerie",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0379-paper-menagerie",
    canonical: "host",
    screenshot: "/shots/game-0379-paper-menagerie.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0379 paper menagerie, a small original browser game.",
  },
  // Treaty Couriers. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0380-treaty-couriers.
  {
    slug: "game-0380-treaty-couriers",
    surface: "app",
    title: "Treaty Couriers",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0380-treaty-couriers",
    canonical: "host",
    screenshot: "/shots/game-0380-treaty-couriers.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0380 treaty couriers, a small original browser game.",
  },
  // Mint Bubbleworks. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0381-mint-bubbleworks.
  {
    slug: "game-0381-mint-bubbleworks",
    surface: "app",
    title: "Mint Bubbleworks",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0381-mint-bubbleworks",
    canonical: "host",
    screenshot: "/shots/game-0381-mint-bubbleworks.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0381 mint bubbleworks, a small original browser game.",
  },
  // Tidal Planets. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0382-tidal-planets.
  {
    slug: "game-0382-tidal-planets",
    surface: "app",
    title: "Tidal Planets",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0382-tidal-planets",
    canonical: "host",
    screenshot: "/shots/game-0382-tidal-planets.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0382 tidal planets, a small original browser game.",
  },
  // Mana Mosaic. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0383-mana-mosaic.
  {
    slug: "game-0383-mana-mosaic",
    surface: "app",
    title: "Mana Mosaic",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0383-mana-mosaic",
    canonical: "host",
    screenshot: "/shots/game-0383-mana-mosaic.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0383 mana mosaic, a small original browser game.",
  },
  // Pulse Bastion. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0384-pulse-bastion.
  {
    slug: "game-0384-pulse-bastion",
    surface: "app",
    title: "Pulse Bastion",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0384-pulse-bastion",
    canonical: "host",
    screenshot: "/shots/game-0384-pulse-bastion.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0384 pulse bastion, a small original browser game.",
  },
  // Sugar Satchel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0385-sugar-satchel.
  {
    slug: "game-0385-sugar-satchel",
    surface: "app",
    title: "Sugar Satchel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0385-sugar-satchel",
    canonical: "host",
    screenshot: "/shots/game-0385-sugar-satchel.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0385 sugar satchel, a small original browser game.",
  },
  // Midnight Guests. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0386-midnight-guests.
  {
    slug: "game-0386-midnight-guests",
    surface: "app",
    title: "Midnight Guests",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0386-midnight-guests",
    canonical: "host",
    screenshot: "/shots/game-0386-midnight-guests.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0386 midnight guests, a small original browser game.",
  },
  // Crystal Setting. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0387-crystal-setting.
  {
    slug: "game-0387-crystal-setting",
    surface: "app",
    title: "Crystal Setting",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0387-crystal-setting",
    canonical: "host",
    screenshot: "/shots/game-0387-crystal-setting.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0387 crystal setting, a small original browser game.",
  },
  // Vault Duel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0388-vault-duel.
  {
    slug: "game-0388-vault-duel",
    surface: "app",
    title: "Vault Duel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0388-vault-duel",
    canonical: "host",
    screenshot: "/shots/game-0388-vault-duel.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0388 vault duel, a small original browser game.",
  },
  // Crimson Tithe. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0389-crimson-tithe.
  {
    slug: "game-0389-crimson-tithe",
    surface: "app",
    title: "Crimson Tithe",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0389-crimson-tithe",
    canonical: "host",
    screenshot: "/shots/game-0389-crimson-tithe.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0389 crimson tithe, a small original browser game.",
  },
  // Facet Rhythm. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0390-facet-rhythm.
  {
    slug: "game-0390-facet-rhythm",
    surface: "app",
    title: "Facet Rhythm",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0390-facet-rhythm",
    canonical: "host",
    screenshot: "/shots/game-0390-facet-rhythm.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0390 facet rhythm, a small original browser game.",
  },
  // Prism Dice. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0391-prism-dice.
  {
    slug: "game-0391-prism-dice",
    surface: "app",
    title: "Prism Dice",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0391-prism-dice",
    canonical: "host",
    screenshot: "/shots/game-0391-prism-dice.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0391 prism dice, a small original browser game.",
  },
  // Dawn Chorus. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0392-dawn-chorus.
  {
    slug: "game-0392-dawn-chorus",
    surface: "app",
    title: "Dawn Chorus",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0392-dawn-chorus",
    canonical: "host",
    screenshot: "/shots/game-0392-dawn-chorus.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0392 dawn chorus, a small original browser game.",
  },
  // Masonry Ledger. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0393-masonry-ledger.
  {
    slug: "game-0393-masonry-ledger",
    surface: "app",
    title: "Masonry Ledger",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0393-masonry-ledger",
    canonical: "host",
    screenshot: "/shots/game-0393-masonry-ledger.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0393 masonry ledger, a small original browser game.",
  },
  // Chord Register. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0394-chord-register.
  {
    slug: "game-0394-chord-register",
    surface: "app",
    title: "Chord Register",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0394-chord-register",
    canonical: "host",
    screenshot: "/shots/game-0394-chord-register.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0394 chord register, a small original browser game.",
  },
  // Arcade Alchemy. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0395-arcade-alchemy.
  {
    slug: "game-0395-arcade-alchemy",
    surface: "app",
    title: "Arcade Alchemy",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0395-arcade-alchemy",
    canonical: "host",
    screenshot: "/shots/game-0395-arcade-alchemy.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0395 arcade alchemy, a small original browser game.",
  },
  // Second Sunrise. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0396-second-sunrise.
  {
    slug: "game-0396-second-sunrise",
    surface: "app",
    title: "Second Sunrise",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0396-second-sunrise",
    canonical: "host",
    screenshot: "/shots/game-0396-second-sunrise.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0396 second sunrise, a small original browser game.",
  },
  // Masquerade Shift. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0397-masquerade-shift.
  {
    slug: "game-0397-masquerade-shift",
    surface: "app",
    title: "Masquerade Shift",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0397-masquerade-shift",
    canonical: "host",
    screenshot: "/shots/game-0397-masquerade-shift.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0397 masquerade shift, a small original browser game.",
  },
  // Countdown Grove. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0398-countdown-grove.
  {
    slug: "game-0398-countdown-grove",
    surface: "app",
    title: "Countdown Grove",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0398-countdown-grove",
    canonical: "host",
    screenshot: "/shots/game-0398-countdown-grove.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0398 countdown grove, a small original browser game.",
  },
  // Gesture Front. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0399-gesture-front.
  {
    slug: "game-0399-gesture-front",
    surface: "app",
    title: "Gesture Front",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0399-gesture-front",
    canonical: "host",
    screenshot: "/shots/game-0399-gesture-front.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0399 gesture front, a small original browser game.",
  },
  // Five Note Foundry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0400-five-note-foundry.
  {
    slug: "game-0400-five-note-foundry",
    surface: "app",
    title: "Five Note Foundry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0400-five-note-foundry",
    canonical: "host",
    screenshot: "/shots/game-0400-five-note-foundry.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0400 five note foundry, a small original browser game.",
  },
  // Four Winds Study. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0516-four-winds-study.
  {
    slug: "game-0516-four-winds-study",
    surface: "app",
    title: "Four Winds Study",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0516-four-winds-study",
    canonical: "host",
    screenshot: "/shots/game-0516-four-winds-study.webp",
    shippedAt: "2026-11-03",
    status: "live",
    blurb: "Play game 0516 four winds study, a small original browser game.",
  },
  // Familiar Keys. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0517-familiar-keys.
  {
    slug: "game-0517-familiar-keys",
    surface: "app",
    title: "Familiar Keys",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0517-familiar-keys",
    canonical: "host",
    screenshot: "/shots/game-0517-familiar-keys.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0517 familiar keys, a small original browser game.",
  },
  // Terrace Architect. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0518-terrace-architect.
  {
    slug: "game-0518-terrace-architect",
    surface: "app",
    title: "Terrace Architect",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0518-terrace-architect",
    canonical: "host",
    screenshot: "/shots/game-0518-terrace-architect.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0518 terrace architect, a small original browser game.",
  },
  // Crossmeld Garden. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0519-crossmeld-garden.
  {
    slug: "game-0519-crossmeld-garden",
    surface: "app",
    title: "Crossmeld Garden",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0519-crossmeld-garden",
    canonical: "host",
    screenshot: "/shots/game-0519-crossmeld-garden.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0519 crossmeld garden, a small original browser game.",
  },
  // Balance of Bells. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0520-balance-of-bells.
  {
    slug: "game-0520-balance-of-bells",
    surface: "app",
    title: "Balance of Bells",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0520-balance-of-bells",
    canonical: "host",
    screenshot: "/shots/game-0520-balance-of-bells.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0520 balance of bells, a small original browser game.",
  },
  // Crooked Teahouse. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0521-crooked-teahouse.
  {
    slug: "game-0521-crooked-teahouse",
    surface: "app",
    title: "Crooked Teahouse",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0521-crooked-teahouse",
    canonical: "host",
    screenshot: "/shots/game-0521-crooked-teahouse.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0521 crooked teahouse, a small original browser game.",
  },
  // Obelisk Echoes. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0522-obelisk-echoes.
  {
    slug: "game-0522-obelisk-echoes",
    surface: "app",
    title: "Obelisk Echoes",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0522-obelisk-echoes",
    canonical: "host",
    screenshot: "/shots/game-0522-obelisk-echoes.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0522 obelisk echoes, a small original browser game.",
  },
  // Purring Windows. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0523-purring-windows.
  {
    slug: "game-0523-purring-windows",
    surface: "app",
    title: "Purring Windows",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0523-purring-windows",
    canonical: "host",
    screenshot: "/shots/game-0523-purring-windows.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0523 purring windows, a small original browser game.",
  },
  // Three Oracle Seals. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0524-three-oracle-seals.
  {
    slug: "game-0524-three-oracle-seals",
    surface: "app",
    title: "Three Oracle Seals",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0524-three-oracle-seals",
    canonical: "host",
    screenshot: "/shots/game-0524-three-oracle-seals.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0524 three oracle seals, a small original browser game.",
  },
  // Evening Ready Hand. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0525-evening-ready-hand.
  {
    slug: "game-0525-evening-ready-hand",
    surface: "app",
    title: "Evening Ready Hand",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0525-evening-ready-hand",
    canonical: "host",
    screenshot: "/shots/game-0525-evening-ready-hand.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0525 evening ready hand, a small original browser game.",
  },
  // Porcelain Districts. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0526-porcelain-districts.
  {
    slug: "game-0526-porcelain-districts",
    surface: "app",
    title: "Porcelain Districts",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0526-porcelain-districts",
    canonical: "host",
    screenshot: "/shots/game-0526-porcelain-districts.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0526 porcelain districts, a small original browser game.",
  },
  // Runes Against the Regent. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0527-runes-against-the-regent.
  {
    slug: "game-0527-runes-against-the-regent",
    surface: "app",
    title: "Runes Against the Regent",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0527-runes-against-the-regent",
    canonical: "host",
    screenshot: "/shots/game-0527-runes-against-the-regent.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0527 runes against the regent, a small original browser game.",
  },
  // Parallax Pagoda. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0528-parallax-pagoda.
  {
    slug: "game-0528-parallax-pagoda",
    surface: "app",
    title: "Parallax Pagoda",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0528-parallax-pagoda",
    canonical: "host",
    screenshot: "/shots/game-0528-parallax-pagoda.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0528 parallax pagoda, a small original browser game.",
  },
  // Seven Bridges Home. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0529-seven-bridges-home.
  {
    slug: "game-0529-seven-bridges-home",
    surface: "app",
    title: "Seven Bridges Home",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0529-seven-bridges-home",
    canonical: "host",
    screenshot: "/shots/game-0529-seven-bridges-home.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0529 seven bridges home, a small original browser game.",
  },
  // Petals in Sequence. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0530-petals-in-sequence.
  {
    slug: "game-0530-petals-in-sequence",
    surface: "app",
    title: "Petals in Sequence",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0530-petals-in-sequence",
    canonical: "host",
    screenshot: "/shots/game-0530-petals-in-sequence.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0530 petals in sequence, a small original browser game.",
  },
  // Ember Oath. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0531-ember-oath.
  {
    slug: "game-0531-ember-oath",
    surface: "app",
    title: "Ember Oath",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0531-ember-oath",
    canonical: "host",
    screenshot: "/shots/game-0531-ember-oath.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0531 ember oath, a small original browser game.",
  },
  // Mothglass Conservatory. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0532-mothglass-conservatory.
  {
    slug: "game-0532-mothglass-conservatory",
    surface: "app",
    title: "Mothglass Conservatory",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0532-mothglass-conservatory",
    canonical: "host",
    screenshot: "/shots/game-0532-mothglass-conservatory.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0532 mothglass conservatory, a small original browser game.",
  },
  // Paper Hex Workshop. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0533-paper-hex-workshop.
  {
    slug: "game-0533-paper-hex-workshop",
    surface: "app",
    title: "Paper Hex Workshop",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0533-paper-hex-workshop",
    canonical: "host",
    screenshot: "/shots/game-0533-paper-hex-workshop.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0533 paper hex workshop, a small original browser game.",
  },
  // The Four-Gate Sprint. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0534-the-four-gate-sprint.
  {
    slug: "game-0534-the-four-gate-sprint",
    surface: "app",
    title: "The Four-Gate Sprint",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0534-the-four-gate-sprint",
    canonical: "host",
    screenshot: "/shots/game-0534-the-four-gate-sprint.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0534 the four gate sprint, a small original browser game.",
  },
  // Saffron Trial. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0535-saffron-trial.
  {
    slug: "game-0535-saffron-trial",
    surface: "app",
    title: "Saffron Trial",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0535-saffron-trial",
    canonical: "host",
    screenshot: "/shots/game-0535-saffron-trial.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0535 saffron trial, a small original browser game.",
  },
  // Wobble Vials. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0536-wobble-vials.
  {
    slug: "game-0536-wobble-vials",
    surface: "app",
    title: "Wobble Vials",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0536-wobble-vials",
    canonical: "host",
    screenshot: "/shots/game-0536-wobble-vials.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0536 wobble vials, a small original browser game.",
  },
  // Terrain at Dusk. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0537-terrain-at-dusk.
  {
    slug: "game-0537-terrain-at-dusk",
    surface: "app",
    title: "Terrain at Dusk",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0537-terrain-at-dusk",
    canonical: "host",
    screenshot: "/shots/game-0537-terrain-at-dusk.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0537 terrain at dusk, a small original browser game.",
  },
  // Orchard Gravity. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0538-orchard-gravity.
  {
    slug: "game-0538-orchard-gravity",
    surface: "app",
    title: "Orchard Gravity",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0538-orchard-gravity",
    canonical: "host",
    screenshot: "/shots/game-0538-orchard-gravity.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0538 orchard gravity, a small original browser game.",
  },
  // Prism Duelist. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0539-prism-duelist.
  {
    slug: "game-0539-prism-duelist",
    surface: "app",
    title: "Prism Duelist",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0539-prism-duelist",
    canonical: "host",
    screenshot: "/shots/game-0539-prism-duelist.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0539 prism duelist, a small original browser game.",
  },
  // Camp of Three. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0540-camp-of-three.
  {
    slug: "game-0540-camp-of-three",
    surface: "app",
    title: "Camp of Three",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0540-camp-of-three",
    canonical: "host",
    screenshot: "/shots/game-0540-camp-of-three.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0540 camp of three, a small original browser game.",
  },
  // Clover Courier. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0541-clover-courier.
  {
    slug: "game-0541-clover-courier",
    surface: "app",
    title: "Clover Courier",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0541-clover-courier",
    canonical: "host",
    screenshot: "/shots/game-0541-clover-courier.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0541 clover courier, a small original browser game.",
  },
  // Skiff through the Locks. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0542-skiff-through-the-locks.
  {
    slug: "game-0542-skiff-through-the-locks",
    surface: "app",
    title: "Skiff through the Locks",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0542-skiff-through-the-locks",
    canonical: "host",
    screenshot: "/shots/game-0542-skiff-through-the-locks.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0542 skiff through the locks, a small original browser game.",
  },
  // Pennant Rush. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0543-pennant-rush.
  {
    slug: "game-0543-pennant-rush",
    surface: "app",
    title: "Pennant Rush",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0543-pennant-rush",
    canonical: "host",
    screenshot: "/shots/game-0543-pennant-rush.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0543 pennant rush, a small original browser game.",
  },
  // Copperheart Engine. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0544-copperheart-engine.
  {
    slug: "game-0544-copperheart-engine",
    surface: "app",
    title: "Copperheart Engine",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0544-copperheart-engine",
    canonical: "host",
    screenshot: "/shots/game-0544-copperheart-engine.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0544 copperheart engine, a small original browser game.",
  },
  // Prism Remainder. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0545-prism-remainder.
  {
    slug: "game-0545-prism-remainder",
    surface: "app",
    title: "Prism Remainder",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0545-prism-remainder",
    canonical: "host",
    screenshot: "/shots/game-0545-prism-remainder.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0545 prism remainder, a small original browser game.",
  },
  // Thicket Breaker. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0546-thicket-breaker.
  {
    slug: "game-0546-thicket-breaker",
    surface: "app",
    title: "Thicket Breaker",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0546-thicket-breaker",
    canonical: "host",
    screenshot: "/shots/game-0546-thicket-breaker.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0546 thicket breaker, a small original browser game.",
  },
  // Echo Totems. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0547-echo-totems.
  {
    slug: "game-0547-echo-totems",
    surface: "app",
    title: "Echo Totems",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0547-echo-totems",
    canonical: "host",
    screenshot: "/shots/game-0547-echo-totems.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0547 echo totems, a small original browser game.",
  },
  // Clockwork Gemwheel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0548-clockwork-gemwheel.
  {
    slug: "game-0548-clockwork-gemwheel",
    surface: "app",
    title: "Clockwork Gemwheel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0548-clockwork-gemwheel",
    canonical: "host",
    screenshot: "/shots/game-0548-clockwork-gemwheel.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0548 clockwork gemwheel, a small original browser game.",
  },
  // Banner Columns. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0549-banner-columns.
  {
    slug: "game-0549-banner-columns",
    surface: "app",
    title: "Banner Columns",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0549-banner-columns",
    canonical: "host",
    screenshot: "/shots/game-0549-banner-columns.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0549 banner columns, a small original browser game.",
  },
  // The Brass Hunt. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0550-the-brass-hunt.
  {
    slug: "game-0550-the-brass-hunt",
    surface: "app",
    title: "The Brass Hunt",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0550-the-brass-hunt",
    canonical: "host",
    screenshot: "/shots/game-0550-the-brass-hunt.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0550 the brass hunt, a small original browser game.",
  },
  // Advent Masonry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0551-advent-masonry.
  {
    slug: "game-0551-advent-masonry",
    surface: "app",
    title: "Advent Masonry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0551-advent-masonry",
    canonical: "host",
    screenshot: "/shots/game-0551-advent-masonry.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0551 advent masonry, a small original browser game.",
  },
  // Twin Spectrum Patrol. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0552-twin-spectrum-patrol.
  {
    slug: "game-0552-twin-spectrum-patrol",
    surface: "app",
    title: "Twin Spectrum Patrol",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0552-twin-spectrum-patrol",
    canonical: "host",
    screenshot: "/shots/game-0552-twin-spectrum-patrol.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0552 twin spectrum patrol, a small original browser game.",
  },
  // The Glasshouse Memory. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0553-the-glasshouse-memory.
  {
    slug: "game-0553-the-glasshouse-memory",
    surface: "app",
    title: "The Glasshouse Memory",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0553-the-glasshouse-memory",
    canonical: "host",
    screenshot: "/shots/game-0553-the-glasshouse-memory.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0553 the glasshouse memory, a small original browser game.",
  },
  // Meteor Seam. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0554-meteor-seam.
  {
    slug: "game-0554-meteor-seam",
    surface: "app",
    title: "Meteor Seam",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0554-meteor-seam",
    canonical: "host",
    screenshot: "/shots/game-0554-meteor-seam.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0554 meteor seam, a small original browser game.",
  },
  // Facet Foundry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0555-facet-foundry.
  {
    slug: "game-0555-facet-foundry",
    surface: "app",
    title: "Facet Foundry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0555-facet-foundry",
    canonical: "host",
    screenshot: "/shots/game-0555-facet-foundry.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0555 facet foundry, a small original browser game.",
  },
  // The Small Exhibition. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0556-the-small-exhibition.
  {
    slug: "game-0556-the-small-exhibition",
    surface: "app",
    title: "The Small Exhibition",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0556-the-small-exhibition",
    canonical: "host",
    screenshot: "/shots/game-0556-the-small-exhibition.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0556 the small exhibition, a small original browser game.",
  },
  // Lantern-Bonus Lane. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0557-lantern-bonus-lane.
  {
    slug: "game-0557-lantern-bonus-lane",
    surface: "app",
    title: "Lantern-Bonus Lane",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0557-lantern-bonus-lane",
    canonical: "host",
    screenshot: "/shots/game-0557-lantern-bonus-lane.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0557 lantern bonus lane, a small original browser game.",
  },
  // Chromatic Reactor. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0558-chromatic-reactor.
  {
    slug: "game-0558-chromatic-reactor",
    surface: "app",
    title: "Chromatic Reactor",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0558-chromatic-reactor",
    canonical: "host",
    screenshot: "/shots/game-0558-chromatic-reactor.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0558 chromatic reactor, a small original browser game.",
  },
  // Supper Canopy. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0559-supper-canopy.
  {
    slug: "game-0559-supper-canopy",
    surface: "app",
    title: "Supper Canopy",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0559-supper-canopy",
    canonical: "host",
    screenshot: "/shots/game-0559-supper-canopy.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0559 supper canopy, a small original browser game.",
  },
  // Copper Rain Reserve. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0560-copper-rain-reserve.
  {
    slug: "game-0560-copper-rain-reserve",
    surface: "app",
    title: "Copper Rain Reserve",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0560-copper-rain-reserve",
    canonical: "host",
    screenshot: "/shots/game-0560-copper-rain-reserve.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0560 copper rain reserve, a small original browser game.",
  },
  // Bastion Below. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0561-bastion-below.
  {
    slug: "game-0561-bastion-below",
    surface: "app",
    title: "Bastion Below",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0561-bastion-below",
    canonical: "host",
    screenshot: "/shots/game-0561-bastion-below.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0561 bastion below, a small original browser game.",
  },
  // Thawglass Garden. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0562-thawglass-garden.
  {
    slug: "game-0562-thawglass-garden",
    surface: "app",
    title: "Thawglass Garden",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0562-thawglass-garden",
    canonical: "host",
    screenshot: "/shots/game-0562-thawglass-garden.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0562 thawglass garden, a small original browser game.",
  },
  // Little Epoch. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0563-little-epoch.
  {
    slug: "game-0563-little-epoch",
    surface: "app",
    title: "Little Epoch",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0563-little-epoch",
    canonical: "host",
    screenshot: "/shots/game-0563-little-epoch.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0563 little epoch, a small original browser game.",
  },
  // Cloudcup Counter. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0564-cloudcup-counter.
  {
    slug: "game-0564-cloudcup-counter",
    surface: "app",
    title: "Cloudcup Counter",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0564-cloudcup-counter",
    canonical: "host",
    screenshot: "/shots/game-0564-cloudcup-counter.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0564 cloudcup counter, a small original browser game.",
  },
  // Prism Silo. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0565-prism-silo.
  {
    slug: "game-0565-prism-silo",
    surface: "app",
    title: "Prism Silo",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0565-prism-silo",
    canonical: "host",
    screenshot: "/shots/game-0565-prism-silo.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0565 prism silo, a small original browser game.",
  },
  // Castaway Lanterns. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0566-castaway-lanterns.
  {
    slug: "game-0566-castaway-lanterns",
    surface: "app",
    title: "Castaway Lanterns",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0566-castaway-lanterns",
    canonical: "host",
    screenshot: "/shots/game-0566-castaway-lanterns.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0566 castaway lanterns, a small original browser game.",
  },
  // Bucket and Blade. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0567-bucket-and-blade.
  {
    slug: "game-0567-bucket-and-blade",
    surface: "app",
    title: "Bucket and Blade",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0567-bucket-and-blade",
    canonical: "host",
    screenshot: "/shots/game-0567-bucket-and-blade.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0567 bucket and blade, a small original browser game.",
  },
  // Orchard Foundry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0568-orchard-foundry.
  {
    slug: "game-0568-orchard-foundry",
    surface: "app",
    title: "Orchard Foundry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0568-orchard-foundry",
    canonical: "host",
    screenshot: "/shots/game-0568-orchard-foundry.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0568 orchard foundry, a small original browser game.",
  },
  // Polyp Atlas. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0569-polyp-atlas.
  {
    slug: "game-0569-polyp-atlas",
    surface: "app",
    title: "Polyp Atlas",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0569-polyp-atlas",
    canonical: "host",
    screenshot: "/shots/game-0569-polyp-atlas.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0569 polyp atlas, a small original browser game.",
  },
  // Twelve Lantern Locks. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0570-twelve-lantern-locks.
  {
    slug: "game-0570-twelve-lantern-locks",
    surface: "app",
    title: "Twelve Lantern Locks",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0570-twelve-lantern-locks",
    canonical: "host",
    screenshot: "/shots/game-0570-twelve-lantern-locks.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0570 twelve lantern locks, a small original browser game.",
  },
  // Neon Rent. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0571-neon-rent.
  {
    slug: "game-0571-neon-rent",
    surface: "app",
    title: "Neon Rent",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0571-neon-rent",
    canonical: "host",
    screenshot: "/shots/game-0571-neon-rent.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0571 neon rent, a small original browser game.",
  },
  // Fivefold Ascent. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0572-fivefold-ascent.
  {
    slug: "game-0572-fivefold-ascent",
    surface: "app",
    title: "Fivefold Ascent",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0572-fivefold-ascent",
    canonical: "host",
    screenshot: "/shots/game-0572-fivefold-ascent.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0572 fivefold ascent, a small original browser game.",
  },
  // Inkroof Borough. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0573-inkroof-borough.
  {
    slug: "game-0573-inkroof-borough",
    surface: "app",
    title: "Inkroof Borough",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0573-inkroof-borough",
    canonical: "host",
    screenshot: "/shots/game-0573-inkroof-borough.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0573 inkroof borough, a small original browser game.",
  },
  // Marble Chorus. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0574-marble-chorus.
  {
    slug: "game-0574-marble-chorus",
    surface: "app",
    title: "Marble Chorus",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0574-marble-chorus",
    canonical: "host",
    screenshot: "/shots/game-0574-marble-chorus.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0574 marble chorus, a small original browser game.",
  },
  // Whisker Watch. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0575-whisker-watch.
  {
    slug: "game-0575-whisker-watch",
    surface: "app",
    title: "Whisker Watch",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0575-whisker-watch",
    canonical: "host",
    screenshot: "/shots/game-0575-whisker-watch.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0575 whisker watch, a small original browser game.",
  },
  // Border Alchemy. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0576-border-alchemy.
  {
    slug: "game-0576-border-alchemy",
    surface: "app",
    title: "Border Alchemy",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0576-border-alchemy",
    canonical: "host",
    screenshot: "/shots/game-0576-border-alchemy.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0576 border alchemy, a small original browser game.",
  },
  // Shelf by the Sea. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0577-shelf-by-the-sea.
  {
    slug: "game-0577-shelf-by-the-sea",
    surface: "app",
    title: "Shelf by the Sea",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0577-shelf-by-the-sea",
    canonical: "host",
    screenshot: "/shots/game-0577-shelf-by-the-sea.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0577 shelf by the sea, a small original browser game.",
  },
  // Common Hearth. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0578-common-hearth.
  {
    slug: "game-0578-common-hearth",
    surface: "app",
    title: "Common Hearth",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0578-common-hearth",
    canonical: "host",
    screenshot: "/shots/game-0578-common-hearth.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0578 common hearth, a small original browser game.",
  },
  // Wanderstone Map. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0579-wanderstone-map.
  {
    slug: "game-0579-wanderstone-map",
    surface: "app",
    title: "Wanderstone Map",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0579-wanderstone-map",
    canonical: "host",
    screenshot: "/shots/game-0579-wanderstone-map.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0579 wanderstone map, a small original browser game.",
  },
  // Rootstack Meadow. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0580-rootstack-meadow.
  {
    slug: "game-0580-rootstack-meadow",
    surface: "app",
    title: "Rootstack Meadow",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0580-rootstack-meadow",
    canonical: "host",
    screenshot: "/shots/game-0580-rootstack-meadow.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0580 rootstack meadow, a small original browser game.",
  },
  // Aurora Flipworks. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0581-aurora-flipworks.
  {
    slug: "game-0581-aurora-flipworks",
    surface: "app",
    title: "Aurora Flipworks",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0581-aurora-flipworks",
    canonical: "host",
    screenshot: "/shots/game-0581-aurora-flipworks.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0581 aurora flipworks, a small original browser game.",
  },
  // Chromatic Operative. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0582-chromatic-operative.
  {
    slug: "game-0582-chromatic-operative",
    surface: "app",
    title: "Chromatic Operative",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0582-chromatic-operative",
    canonical: "host",
    screenshot: "/shots/game-0582-chromatic-operative.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0582 chromatic operative, a small original browser game.",
  },
  // Sproutlight Grove. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0583-sproutlight-grove.
  {
    slug: "game-0583-sproutlight-grove",
    surface: "app",
    title: "Sproutlight Grove",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0583-sproutlight-grove",
    canonical: "host",
    screenshot: "/shots/game-0583-sproutlight-grove.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0583 sproutlight grove, a small original browser game.",
  },
  // Atom Carousel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0584-atom-carousel.
  {
    slug: "game-0584-atom-carousel",
    surface: "app",
    title: "Atom Carousel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0584-atom-carousel",
    canonical: "host",
    screenshot: "/shots/game-0584-atom-carousel.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0584 atom carousel, a small original browser game.",
  },
  // Numeral Bloom. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0585-numeral-bloom.
  {
    slug: "game-0585-numeral-bloom",
    surface: "app",
    title: "Numeral Bloom",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0585-numeral-bloom",
    canonical: "host",
    screenshot: "/shots/game-0585-numeral-bloom.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0585 numeral bloom, a small original browser game.",
  },
  // Paper Unfolding. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0586-paper-unfolding.
  {
    slug: "game-0586-paper-unfolding",
    surface: "app",
    title: "Paper Unfolding",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0586-paper-unfolding",
    canonical: "host",
    screenshot: "/shots/game-0586-paper-unfolding.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0586 paper unfolding, a small original browser game.",
  },
  // Lucent Districts. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0587-lucent-districts.
  {
    slug: "game-0587-lucent-districts",
    surface: "app",
    title: "Lucent Districts",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0587-lucent-districts",
    canonical: "host",
    screenshot: "/shots/game-0587-lucent-districts.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0587 lucent districts, a small original browser game.",
  },
  // Four Winds Duel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0588-four-winds-duel.
  {
    slug: "game-0588-four-winds-duel",
    surface: "app",
    title: "Four Winds Duel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0588-four-winds-duel",
    canonical: "host",
    screenshot: "/shots/game-0588-four-winds-duel.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0588 four winds duel, a small original browser game.",
  },
  // Sunken Ruby Steps. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0589-sunken-ruby-steps.
  {
    slug: "game-0589-sunken-ruby-steps",
    surface: "app",
    title: "Sunken Ruby Steps",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0589-sunken-ruby-steps",
    canonical: "host",
    screenshot: "/shots/game-0589-sunken-ruby-steps.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0589 sunken ruby steps, a small original browser game.",
  },
  // Relic Delvers. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0590-relic-delvers.
  {
    slug: "game-0590-relic-delvers",
    surface: "app",
    title: "Relic Delvers",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0590-relic-delvers",
    canonical: "host",
    screenshot: "/shots/game-0590-relic-delvers.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0590 relic delvers, a small original browser game.",
  },
  // Stonework Caravan. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0591-stonework-caravan.
  {
    slug: "game-0591-stonework-caravan",
    surface: "app",
    title: "Stonework Caravan",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0591-stonework-caravan",
    canonical: "host",
    screenshot: "/shots/game-0591-stonework-caravan.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0591 stonework caravan, a small original browser game.",
  },
  // Greenline Mayor. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0592-greenline-mayor.
  {
    slug: "game-0592-greenline-mayor",
    surface: "app",
    title: "Greenline Mayor",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0592-greenline-mayor",
    canonical: "host",
    screenshot: "/shots/game-0592-greenline-mayor.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0592 greenline mayor, a small original browser game.",
  },
  // Serpent Constellation. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0593-serpent-constellation.
  {
    slug: "game-0593-serpent-constellation",
    surface: "app",
    title: "Serpent Constellation",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0593-serpent-constellation",
    canonical: "host",
    screenshot: "/shots/game-0593-serpent-constellation.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0593 serpent constellation, a small original browser game.",
  },
  // Packet Witch. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0594-packet-witch.
  {
    slug: "game-0594-packet-witch",
    surface: "app",
    title: "Packet Witch",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0594-packet-witch",
    canonical: "host",
    screenshot: "/shots/game-0594-packet-witch.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0594 packet witch, a small original browser game.",
  },
  // Ember Camp. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0595-ember-camp.
  {
    slug: "game-0595-ember-camp",
    surface: "app",
    title: "Ember Camp",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0595-ember-camp",
    canonical: "host",
    screenshot: "/shots/game-0595-ember-camp.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0595 ember camp, a small original browser game.",
  },
  // Cuddle Gravity. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0596-cuddle-gravity.
  {
    slug: "game-0596-cuddle-gravity",
    surface: "app",
    title: "Cuddle Gravity",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0596-cuddle-gravity",
    canonical: "host",
    screenshot: "/shots/game-0596-cuddle-gravity.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0596 cuddle gravity, a small original browser game.",
  },
  // Vowbound Company. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0597-vowbound-company.
  {
    slug: "game-0597-vowbound-company",
    surface: "app",
    title: "Vowbound Company",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0597-vowbound-company",
    canonical: "host",
    screenshot: "/shots/game-0597-vowbound-company.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0597 vowbound company, a small original browser game.",
  },
  // Sugar Signal. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0598-sugar-signal.
  {
    slug: "game-0598-sugar-signal",
    surface: "app",
    title: "Sugar Signal",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0598-sugar-signal",
    canonical: "host",
    screenshot: "/shots/game-0598-sugar-signal.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0598 sugar signal, a small original browser game.",
  },
  // Feather Passage. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0599-feather-passage.
  {
    slug: "game-0599-feather-passage",
    surface: "app",
    title: "Feather Passage",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0599-feather-passage",
    canonical: "host",
    screenshot: "/shots/game-0599-feather-passage.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0599 feather passage, a small original browser game.",
  },
  // Stormwall Village. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0600-stormwall-village.
  {
    slug: "game-0600-stormwall-village",
    surface: "app",
    title: "Stormwall Village",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0600-stormwall-village",
    canonical: "host",
    screenshot: "/shots/game-0600-stormwall-village.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0600 stormwall village, a small original browser game.",
  },
  // Radical Pocket. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0706-radical-pocket.
  {
    slug: "game-0706-radical-pocket",
    surface: "app",
    title: "Radical Pocket",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0706-radical-pocket",
    canonical: "host",
    screenshot: "/shots/game-0706-radical-pocket.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0706 radical pocket, a small original browser game.",
  },
  // Dragonload Tower. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0707-dragonload-tower.
  {
    slug: "game-0707-dragonload-tower",
    surface: "app",
    title: "Dragonload Tower",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0707-dragonload-tower",
    canonical: "host",
    screenshot: "/shots/game-0707-dragonload-tower.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0707 dragonload tower, a small original browser game.",
  },
  // Passage Loom. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0710-passage-loom.
  {
    slug: "game-0710-passage-loom",
    surface: "app",
    title: "Passage Loom",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0710-passage-loom",
    canonical: "host",
    screenshot: "/shots/game-0710-passage-loom.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0710 passage loom, a small original browser game.",
  },
  // Ninefold Archive. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0711-ninefold-archive.
  {
    slug: "game-0711-ninefold-archive",
    surface: "app",
    title: "Ninefold Archive",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0711-ninefold-archive",
    canonical: "host",
    screenshot: "/shots/game-0711-ninefold-archive.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0711 ninefold archive, a small original browser game.",
  },
  // Rookery Mosaic. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0712-rookery-mosaic.
  {
    slug: "game-0712-rookery-mosaic",
    surface: "app",
    title: "Rookery Mosaic",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0712-rookery-mosaic",
    canonical: "host",
    screenshot: "/shots/game-0712-rookery-mosaic.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0712 rookery mosaic, a small original browser game.",
  },
  // Fairy Frontline. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0713-fairy-frontline.
  {
    slug: "game-0713-fairy-frontline",
    surface: "app",
    title: "Fairy Frontline",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0713-fairy-frontline",
    canonical: "host",
    screenshot: "/shots/game-0713-fairy-frontline.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0713 fairy frontline, a small original browser game.",
  },
  // Featherline Freight. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0714-featherline-freight.
  {
    slug: "game-0714-featherline-freight",
    surface: "app",
    title: "Featherline Freight",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0714-featherline-freight",
    canonical: "host",
    screenshot: "/shots/game-0714-featherline-freight.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0714 featherline freight, a small original browser game.",
  },
  // Kana Twin Garden. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0715-kana-twin-garden.
  {
    slug: "game-0715-kana-twin-garden",
    surface: "app",
    title: "Kana Twin Garden",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0715-kana-twin-garden",
    canonical: "host",
    screenshot: "/shots/game-0715-kana-twin-garden.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0715 kana twin garden, a small original browser game.",
  },
  // Sultan’s Facets. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0716-sultan-s-facets.
  {
    slug: "game-0716-sultan-s-facets",
    surface: "app",
    title: "Sultan’s Facets",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0716-sultan-s-facets",
    canonical: "host",
    screenshot: "/shots/game-0716-sultan-s-facets.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0716 sultan s facets, a small original browser game.",
  },
  // Decade Tide. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0717-decade-tide.
  {
    slug: "game-0717-decade-tide",
    surface: "app",
    title: "Decade Tide",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0717-decade-tide",
    canonical: "host",
    screenshot: "/shots/game-0717-decade-tide.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0717 decade tide, a small original browser game.",
  },
  // Scarab Chromatic. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0718-scarab-chromatic.
  {
    slug: "game-0718-scarab-chromatic",
    surface: "app",
    title: "Scarab Chromatic",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0718-scarab-chromatic",
    canonical: "host",
    screenshot: "/shots/game-0718-scarab-chromatic.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0718 scarab chromatic, a small original browser game.",
  },
  // Gilded Current. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0719-gilded-current.
  {
    slug: "game-0719-gilded-current",
    surface: "app",
    title: "Gilded Current",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0719-gilded-current",
    canonical: "host",
    screenshot: "/shots/game-0719-gilded-current.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0719 gilded current, a small original browser game.",
  },
  // Relic Rowrunner. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0720-relic-rowrunner.
  {
    slug: "game-0720-relic-rowrunner",
    surface: "app",
    title: "Relic Rowrunner",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0720-relic-rowrunner",
    canonical: "host",
    screenshot: "/shots/game-0720-relic-rowrunner.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0720 relic rowrunner, a small original browser game.",
  },
  // Chromatic Petri. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0721-chromatic-petri.
  {
    slug: "game-0721-chromatic-petri",
    surface: "app",
    title: "Chromatic Petri",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0721-chromatic-petri",
    canonical: "host",
    screenshot: "/shots/game-0721-chromatic-petri.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0721 chromatic petri, a small original browser game.",
  },
  // Secondhand Spectrum. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0722-secondhand-spectrum.
  {
    slug: "game-0722-secondhand-spectrum",
    surface: "app",
    title: "Secondhand Spectrum",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0722-secondhand-spectrum",
    canonical: "host",
    screenshot: "/shots/game-0722-secondhand-spectrum.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0722 secondhand spectrum, a small original browser game.",
  },
  // Brewleaf Ledger. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0723-brewleaf-ledger.
  {
    slug: "game-0723-brewleaf-ledger",
    surface: "app",
    title: "Brewleaf Ledger",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0723-brewleaf-ledger",
    canonical: "host",
    screenshot: "/shots/game-0723-brewleaf-ledger.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0723 brewleaf ledger, a small original browser game.",
  },
  // Vault Spark. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0724-vault-spark.
  {
    slug: "game-0724-vault-spark",
    surface: "app",
    title: "Vault Spark",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0724-vault-spark",
    canonical: "host",
    screenshot: "/shots/game-0724-vault-spark.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0724 vault spark, a small original browser game.",
  },
  // Radish Ramble. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0725-radish-ramble.
  {
    slug: "game-0725-radish-ramble",
    surface: "app",
    title: "Radish Ramble",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0725-radish-ramble",
    canonical: "host",
    screenshot: "/shots/game-0725-radish-ramble.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0725 radish ramble, a small original browser game.",
  },
  // Salvage Bastion. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0726-salvage-bastion.
  {
    slug: "game-0726-salvage-bastion",
    surface: "app",
    title: "Salvage Bastion",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0726-salvage-bastion",
    canonical: "host",
    screenshot: "/shots/game-0726-salvage-bastion.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0726 salvage bastion, a small original browser game.",
  },
  // Ember Orchard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0727-ember-orchard.
  {
    slug: "game-0727-ember-orchard",
    surface: "app",
    title: "Ember Orchard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0727-ember-orchard",
    canonical: "host",
    screenshot: "/shots/game-0727-ember-orchard.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0727 ember orchard, a small original browser game.",
  },
  // Glimmer Garrison. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0728-glimmer-garrison.
  {
    slug: "game-0728-glimmer-garrison",
    surface: "app",
    title: "Glimmer Garrison",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0728-glimmer-garrison",
    canonical: "host",
    screenshot: "/shots/game-0728-glimmer-garrison.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0728 glimmer garrison, a small original browser game.",
  },
  // Lantern Eve. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0729-lantern-eve.
  {
    slug: "game-0729-lantern-eve",
    surface: "app",
    title: "Lantern Eve",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0729-lantern-eve",
    canonical: "host",
    screenshot: "/shots/game-0729-lantern-eve.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0729 lantern eve, a small original browser game.",
  },
  // Cloudwright Cove. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0730-cloudwright-cove.
  {
    slug: "game-0730-cloudwright-cove",
    surface: "app",
    title: "Cloudwright Cove",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0730-cloudwright-cove",
    canonical: "host",
    screenshot: "/shots/game-0730-cloudwright-cove.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0730 cloudwright cove, a small original browser game.",
  },
  // Toolshift Yard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0731-toolshift-yard.
  {
    slug: "game-0731-toolshift-yard",
    surface: "app",
    title: "Toolshift Yard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0731-toolshift-yard",
    canonical: "host",
    screenshot: "/shots/game-0731-toolshift-yard.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0731 toolshift yard, a small original browser game.",
  },
  // Rookery Crossing. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0732-rookery-crossing.
  {
    slug: "game-0732-rookery-crossing",
    surface: "app",
    title: "Rookery Crossing",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0732-rookery-crossing",
    canonical: "host",
    screenshot: "/shots/game-0732-rookery-crossing.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0732 rookery crossing, a small original browser game.",
  },
  // Neon Quarter. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0733-neon-quarter.
  {
    slug: "game-0733-neon-quarter",
    surface: "app",
    title: "Neon Quarter",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0733-neon-quarter",
    canonical: "host",
    screenshot: "/shots/game-0733-neon-quarter.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0733 neon quarter, a small original browser game.",
  },
  // Quarterturn Quilt. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0734-quarterturn-quilt.
  {
    slug: "game-0734-quarterturn-quilt",
    surface: "app",
    title: "Quarterturn Quilt",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0734-quarterturn-quilt",
    canonical: "host",
    screenshot: "/shots/game-0734-quarterturn-quilt.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0734 quarterturn quilt, a small original browser game.",
  },
  // Drumline Blocks. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0735-drumline-blocks.
  {
    slug: "game-0735-drumline-blocks",
    surface: "app",
    title: "Drumline Blocks",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0735-drumline-blocks",
    canonical: "host",
    screenshot: "/shots/game-0735-drumline-blocks.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0735 drumline blocks, a small original browser game.",
  },
  // Glyph Siege. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0736-glyph-siege.
  {
    slug: "game-0736-glyph-siege",
    surface: "app",
    title: "Glyph Siege",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0736-glyph-siege",
    canonical: "host",
    screenshot: "/shots/game-0736-glyph-siege.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0736 glyph siege, a small original browser game.",
  },
  // Coral Commons. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0737-coral-commons.
  {
    slug: "game-0737-coral-commons",
    surface: "app",
    title: "Coral Commons",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0737-coral-commons",
    canonical: "host",
    screenshot: "/shots/game-0737-coral-commons.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0737 coral commons, a small original browser game.",
  },
  // Copper Ziggurat. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0738-copper-ziggurat.
  {
    slug: "game-0738-copper-ziggurat",
    surface: "app",
    title: "Copper Ziggurat",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0738-copper-ziggurat",
    canonical: "host",
    screenshot: "/shots/game-0738-copper-ziggurat.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0738 copper ziggurat, a small original browser game.",
  },
  // Triplet Harvest. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0739-triplet-harvest.
  {
    slug: "game-0739-triplet-harvest",
    surface: "app",
    title: "Triplet Harvest",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0739-triplet-harvest",
    canonical: "host",
    screenshot: "/shots/game-0739-triplet-harvest.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0739 triplet harvest, a small original browser game.",
  },
  // Fivefold Tide. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0740-fivefold-tide.
  {
    slug: "game-0740-fivefold-tide",
    surface: "app",
    title: "Fivefold Tide",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0740-fivefold-tide",
    canonical: "host",
    screenshot: "/shots/game-0740-fivefold-tide.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0740 fivefold tide, a small original browser game.",
  },
  // Honeyglass Orders. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0741-honeyglass-orders.
  {
    slug: "game-0741-honeyglass-orders",
    surface: "app",
    title: "Honeyglass Orders",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0741-honeyglass-orders",
    canonical: "host",
    screenshot: "/shots/game-0741-honeyglass-orders.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0741 honeyglass orders, a small original browser game.",
  },
  // Moonlit Heist. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0742-moonlit-heist.
  {
    slug: "game-0742-moonlit-heist",
    surface: "app",
    title: "Moonlit Heist",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0742-moonlit-heist",
    canonical: "host",
    screenshot: "/shots/game-0742-moonlit-heist.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0742 moonlit heist, a small original browser game.",
  },
  // Quiet Quantities. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0743-quiet-quantities.
  {
    slug: "game-0743-quiet-quantities",
    surface: "app",
    title: "Quiet Quantities",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0743-quiet-quantities",
    canonical: "host",
    screenshot: "/shots/game-0743-quiet-quantities.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0743 quiet quantities, a small original browser game.",
  },
  // Balance Bloom. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0744-balance-bloom.
  {
    slug: "game-0744-balance-bloom",
    surface: "app",
    title: "Balance Bloom",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0744-balance-bloom",
    canonical: "host",
    screenshot: "/shots/game-0744-balance-bloom.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0744 balance bloom, a small original browser game.",
  },
  // Lockline Loom. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0745-lockline-loom.
  {
    slug: "game-0745-lockline-loom",
    surface: "app",
    title: "Lockline Loom",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0745-lockline-loom",
    canonical: "host",
    screenshot: "/shots/game-0745-lockline-loom.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0745 lockline loom, a small original browser game.",
  },
  // Verb Garden. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0746-verb-garden.
  {
    slug: "game-0746-verb-garden",
    surface: "app",
    title: "Verb Garden",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0746-verb-garden",
    canonical: "host",
    screenshot: "/shots/game-0746-verb-garden.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0746 verb garden, a small original browser game.",
  },
  // Pocket Within. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0747-pocket-within.
  {
    slug: "game-0747-pocket-within",
    surface: "app",
    title: "Pocket Within",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0747-pocket-within",
    canonical: "host",
    screenshot: "/shots/game-0747-pocket-within.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0747 pocket within, a small original browser game.",
  },
  // Driftwood Passage. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0748-driftwood-passage.
  {
    slug: "game-0748-driftwood-passage",
    surface: "app",
    title: "Driftwood Passage",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0748-driftwood-passage",
    canonical: "host",
    screenshot: "/shots/game-0748-driftwood-passage.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0748 driftwood passage, a small original browser game.",
  },
  // Skillet Circuit. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0749-skillet-circuit.
  {
    slug: "game-0749-skillet-circuit",
    surface: "app",
    title: "Skillet Circuit",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0749-skillet-circuit",
    canonical: "host",
    screenshot: "/shots/game-0749-skillet-circuit.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0749 skillet circuit, a small original browser game.",
  },
  // Tinworm Tunnel. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0750-tinworm-tunnel.
  {
    slug: "game-0750-tinworm-tunnel",
    surface: "app",
    title: "Tinworm Tunnel",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0750-tinworm-tunnel",
    canonical: "host",
    screenshot: "/shots/game-0750-tinworm-tunnel.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0750 tinworm tunnel, a small original browser game.",
  },
  // Valence Voyage. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0751-valence-voyage.
  {
    slug: "game-0751-valence-voyage",
    surface: "app",
    title: "Valence Voyage",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0751-valence-voyage",
    canonical: "host",
    screenshot: "/shots/game-0751-valence-voyage.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0751 valence voyage, a small original browser game.",
  },
  // Bondway Express. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0752-bondway-express.
  {
    slug: "game-0752-bondway-express",
    surface: "app",
    title: "Bondway Express",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0752-bondway-express",
    canonical: "host",
    screenshot: "/shots/game-0752-bondway-express.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0752 bondway express, a small original browser game.",
  },
  // Ashen Steps. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0753-ashen-steps.
  {
    slug: "game-0753-ashen-steps",
    surface: "app",
    title: "Ashen Steps",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0753-ashen-steps",
    canonical: "host",
    screenshot: "/shots/game-0753-ashen-steps.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0753 ashen steps, a small original browser game.",
  },
  // Snowcap Assembly. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0754-snowcap-assembly.
  {
    slug: "game-0754-snowcap-assembly",
    surface: "app",
    title: "Snowcap Assembly",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0754-snowcap-assembly",
    canonical: "host",
    screenshot: "/shots/game-0754-snowcap-assembly.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0754 snowcap assembly, a small original browser game.",
  },
  // Hexglow Census. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0755-hexglow-census.
  {
    slug: "game-0755-hexglow-census",
    surface: "app",
    title: "Hexglow Census",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0755-hexglow-census",
    canonical: "host",
    screenshot: "/shots/game-0755-hexglow-census.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0755 hexglow census, a small original browser game.",
  },
  // Hexglow Chains. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0756-hexglow-chains.
  {
    slug: "game-0756-hexglow-chains",
    surface: "app",
    title: "Hexglow Chains",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0756-hexglow-chains",
    canonical: "host",
    screenshot: "/shots/game-0756-hexglow-chains.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0756 hexglow chains, a small original browser game.",
  },
  // Hexglow Almanac. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0757-hexglow-almanac.
  {
    slug: "game-0757-hexglow-almanac",
    surface: "app",
    title: "Hexglow Almanac",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0757-hexglow-almanac",
    canonical: "host",
    screenshot: "/shots/game-0757-hexglow-almanac.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0757 hexglow almanac, a small original browser game.",
  },
  // Window Sums. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0758-window-sums.
  {
    slug: "game-0758-window-sums",
    surface: "app",
    title: "Window Sums",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0758-window-sums",
    canonical: "host",
    screenshot: "/shots/game-0758-window-sums.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0758 window sums, a small original browser game.",
  },
  // Crosssum Keeps. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0759-crosssum-keeps.
  {
    slug: "game-0759-crosssum-keeps",
    surface: "app",
    title: "Crosssum Keeps",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0759-crosssum-keeps",
    canonical: "host",
    screenshot: "/shots/game-0759-crosssum-keeps.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0759 crosssum keeps, a small original browser game.",
  },
  // Triangular Survey. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0760-triangular-survey.
  {
    slug: "game-0760-triangular-survey",
    surface: "app",
    title: "Triangular Survey",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0760-triangular-survey",
    canonical: "host",
    screenshot: "/shots/game-0760-triangular-survey.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0760 triangular survey, a small original browser game.",
  },
  // Silkway Junctions. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0761-silkway-junctions.
  {
    slug: "game-0761-silkway-junctions",
    surface: "app",
    title: "Silkway Junctions",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0761-silkway-junctions",
    canonical: "host",
    screenshot: "/shots/game-0761-silkway-junctions.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0761 silkway junctions, a small original browser game.",
  },
  // Quiet Retraction. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0762-quiet-retraction.
  {
    slug: "game-0762-quiet-retraction",
    surface: "app",
    title: "Quiet Retraction",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0762-quiet-retraction",
    canonical: "host",
    screenshot: "/shots/game-0762-quiet-retraction.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0762 quiet retraction, a small original browser game.",
  },
  // Parallax Pins. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0763-parallax-pins.
  {
    slug: "game-0763-parallax-pins",
    surface: "app",
    title: "Parallax Pins",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0763-parallax-pins",
    canonical: "host",
    screenshot: "/shots/game-0763-parallax-pins.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0763 parallax pins, a small original browser game.",
  },
  // Seamlight. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0764-seamlight.
  {
    slug: "game-0764-seamlight",
    surface: "app",
    title: "Seamlight",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0764-seamlight",
    canonical: "host",
    screenshot: "/shots/game-0764-seamlight.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0764 seamlight, a small original browser game.",
  },
  // Folded Evidence. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0765-folded-evidence.
  {
    slug: "game-0765-folded-evidence",
    surface: "app",
    title: "Folded Evidence",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0765-folded-evidence",
    canonical: "host",
    screenshot: "/shots/game-0765-folded-evidence.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0765 folded evidence, a small original browser game.",
  },
  // Honey Arithmetic. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0766-honey-arithmetic.
  {
    slug: "game-0766-honey-arithmetic",
    surface: "app",
    title: "Honey Arithmetic",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0766-honey-arithmetic",
    canonical: "host",
    screenshot: "/shots/game-0766-honey-arithmetic.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0766 honey arithmetic, a small original browser game.",
  },
  // Rainprint. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0767-rainprint.
  {
    slug: "game-0767-rainprint",
    surface: "app",
    title: "Rainprint",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0767-rainprint",
    canonical: "host",
    screenshot: "/shots/game-0767-rainprint.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0767 rainprint, a small original browser game.",
  },
  // Gallery Mending. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0768-gallery-mending.
  {
    slug: "game-0768-gallery-mending",
    surface: "app",
    title: "Gallery Mending",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0768-gallery-mending",
    canonical: "host",
    screenshot: "/shots/game-0768-gallery-mending.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0768 gallery mending, a small original browser game.",
  },
  // Glyph Observatory. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0769-glyph-observatory.
  {
    slug: "game-0769-glyph-observatory",
    surface: "app",
    title: "Glyph Observatory",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0769-glyph-observatory",
    canonical: "host",
    screenshot: "/shots/game-0769-glyph-observatory.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0769 glyph observatory, a small original browser game.",
  },
  // Deduction Foundry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0770-deduction-foundry.
  {
    slug: "game-0770-deduction-foundry",
    surface: "app",
    title: "Deduction Foundry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0770-deduction-foundry",
    canonical: "host",
    screenshot: "/shots/game-0770-deduction-foundry.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0770 deduction foundry, a small original browser game.",
  },
  // One Per Orbit. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0771-one-per-orbit.
  {
    slug: "game-0771-one-per-orbit",
    surface: "app",
    title: "One Per Orbit",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0771-one-per-orbit",
    canonical: "host",
    screenshot: "/shots/game-0771-one-per-orbit.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0771 one per orbit, a small original browser game.",
  },
  // Copper Alchemy. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0772-copper-alchemy.
  {
    slug: "game-0772-copper-alchemy",
    surface: "app",
    title: "Copper Alchemy",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0772-copper-alchemy",
    canonical: "host",
    screenshot: "/shots/game-0772-copper-alchemy.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0772 copper alchemy, a small original browser game.",
  },
  // Terrace Triples. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0773-terrace-triples.
  {
    slug: "game-0773-terrace-triples",
    surface: "app",
    title: "Terrace Triples",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0773-terrace-triples",
    canonical: "host",
    screenshot: "/shots/game-0773-terrace-triples.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0773 terrace triples, a small original browser game.",
  },
  // Spore Constellation. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0774-spore-constellation.
  {
    slug: "game-0774-spore-constellation",
    surface: "app",
    title: "Spore Constellation",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0774-spore-constellation",
    canonical: "host",
    screenshot: "/shots/game-0774-spore-constellation.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0774 spore constellation, a small original browser game.",
  },
  // Inkword Grove. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0775-inkword-grove.
  {
    slug: "game-0775-inkword-grove",
    surface: "app",
    title: "Inkword Grove",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0775-inkword-grove",
    canonical: "host",
    screenshot: "/shots/game-0775-inkword-grove.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0775 inkword grove, a small original browser game.",
  },
  // Borrowed Floor. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0776-borrowed-floor.
  {
    slug: "game-0776-borrowed-floor",
    surface: "app",
    title: "Borrowed Floor",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0776-borrowed-floor",
    canonical: "host",
    screenshot: "/shots/game-0776-borrowed-floor.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0776 borrowed floor, a small original browser game.",
  },
  // Reach Across. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0777-reach-across.
  {
    slug: "game-0777-reach-across",
    surface: "app",
    title: "Reach Across",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0777-reach-across",
    canonical: "host",
    screenshot: "/shots/game-0777-reach-across.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0777 reach across, a small original browser game.",
  },
  // Starward Companions. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0778-starward-companions.
  {
    slug: "game-0778-starward-companions",
    surface: "app",
    title: "Starward Companions",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0778-starward-companions",
    canonical: "host",
    screenshot: "/shots/game-0778-starward-companions.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0778 starward companions, a small original browser game.",
  },
  // Hinge Maze. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0779-hinge-maze.
  {
    slug: "game-0779-hinge-maze",
    surface: "app",
    title: "Hinge Maze",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0779-hinge-maze",
    canonical: "host",
    screenshot: "/shots/game-0779-hinge-maze.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0779 hinge maze, a small original browser game.",
  },
  // Mirror Mosaic. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0780-mirror-mosaic.
  {
    slug: "game-0780-mirror-mosaic",
    surface: "app",
    title: "Mirror Mosaic",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0780-mirror-mosaic",
    canonical: "host",
    screenshot: "/shots/game-0780-mirror-mosaic.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0780 mirror mosaic, a small original browser game.",
  },
  // Loop Orchard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0781-loop-orchard.
  {
    slug: "game-0781-loop-orchard",
    surface: "app",
    title: "Loop Orchard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0781-loop-orchard",
    canonical: "host",
    screenshot: "/shots/game-0781-loop-orchard.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0781 loop orchard, a small original browser game.",
  },
  // Bitloom Bench. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0782-bitloom-bench.
  {
    slug: "game-0782-bitloom-bench",
    surface: "app",
    title: "Bitloom Bench",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0782-bitloom-bench",
    canonical: "host",
    screenshot: "/shots/game-0782-bitloom-bench.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0782 bitloom bench, a small original browser game.",
  },
  // Subtract Station. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0783-subtract-station.
  {
    slug: "game-0783-subtract-station",
    surface: "app",
    title: "Subtract Station",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0783-subtract-station",
    canonical: "host",
    screenshot: "/shots/game-0783-subtract-station.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0783 subtract station, a small original browser game.",
  },
  // Packet Choreography. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0784-packet-choreography.
  {
    slug: "game-0784-packet-choreography",
    surface: "app",
    title: "Packet Choreography",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0784-packet-choreography",
    canonical: "host",
    screenshot: "/shots/game-0784-packet-choreography.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0784 packet choreography, a small original browser game.",
  },
  // Basalt Hexes. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0785-basalt-hexes.
  {
    slug: "game-0785-basalt-hexes",
    surface: "app",
    title: "Basalt Hexes",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0785-basalt-hexes",
    canonical: "host",
    screenshot: "/shots/game-0785-basalt-hexes.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0785 basalt hexes, a small original browser game.",
  },
  // Sixway Rail. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0786-sixway-rail.
  {
    slug: "game-0786-sixway-rail",
    surface: "app",
    title: "Sixway Rail",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0786-sixway-rail",
    canonical: "host",
    screenshot: "/shots/game-0786-sixway-rail.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0786 sixway rail, a small original browser game.",
  },
  // Apertureless Span. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0787-apertureless-span.
  {
    slug: "game-0787-apertureless-span",
    surface: "app",
    title: "Apertureless Span",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0787-apertureless-span",
    canonical: "host",
    screenshot: "/shots/game-0787-apertureless-span.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0787 apertureless span, a small original browser game.",
  },
  // Gumdrop Aqueduct. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0788-gumdrop-aqueduct.
  {
    slug: "game-0788-gumdrop-aqueduct",
    surface: "app",
    title: "Gumdrop Aqueduct",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0788-gumdrop-aqueduct",
    canonical: "host",
    screenshot: "/shots/game-0788-gumdrop-aqueduct.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0788 gumdrop aqueduct, a small original browser game.",
  },
  // Stitchwise Sayings. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0789-stitchwise-sayings.
  {
    slug: "game-0789-stitchwise-sayings",
    surface: "app",
    title: "Stitchwise Sayings",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0789-stitchwise-sayings",
    canonical: "host",
    screenshot: "/shots/game-0789-stitchwise-sayings.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0789 stitchwise sayings, a small original browser game.",
  },
  // Seasonal Tesserae. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0790-seasonal-tesserae.
  {
    slug: "game-0790-seasonal-tesserae",
    surface: "app",
    title: "Seasonal Tesserae",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0790-seasonal-tesserae",
    canonical: "host",
    screenshot: "/shots/game-0790-seasonal-tesserae.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0790 seasonal tesserae, a small original browser game.",
  },
  // Nook Blueprint. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0791-nook-blueprint.
  {
    slug: "game-0791-nook-blueprint",
    surface: "app",
    title: "Nook Blueprint",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0791-nook-blueprint",
    canonical: "host",
    screenshot: "/shots/game-0791-nook-blueprint.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0791 nook blueprint, a small original browser game.",
  },
  // Runlength Lantern. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0792-runlength-lantern.
  {
    slug: "game-0792-runlength-lantern",
    surface: "app",
    title: "Runlength Lantern",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0792-runlength-lantern",
    canonical: "host",
    screenshot: "/shots/game-0792-runlength-lantern.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0792 runlength lantern, a small original browser game.",
  },
  // Feline Fourfold. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0793-feline-fourfold.
  {
    slug: "game-0793-feline-fourfold",
    surface: "app",
    title: "Feline Fourfold",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0793-feline-fourfold",
    canonical: "host",
    screenshot: "/shots/game-0793-feline-fourfold.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0793 feline fourfold, a small original browser game.",
  },
  // Candidate Crossings. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0794-candidate-crossings.
  {
    slug: "game-0794-candidate-crossings",
    surface: "app",
    title: "Candidate Crossings",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0794-candidate-crossings",
    canonical: "host",
    screenshot: "/shots/game-0794-candidate-crossings.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0794 candidate crossings, a small original browser game.",
  },
  // Wordweft. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0795-wordweft.
  {
    slug: "game-0795-wordweft",
    surface: "app",
    title: "Wordweft",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0795-wordweft",
    canonical: "host",
    screenshot: "/shots/game-0795-wordweft.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0795 wordweft, a small original browser game.",
  },
  // Border and Blast. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0796-border-and-blast.
  {
    slug: "game-0796-border-and-blast",
    surface: "app",
    title: "Border and Blast",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0796-border-and-blast",
    canonical: "host",
    screenshot: "/shots/game-0796-border-and-blast.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0796 border and blast, a small original browser game.",
  },
  // Worldpatch Courier. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0797-worldpatch-courier.
  {
    slug: "game-0797-worldpatch-courier",
    surface: "app",
    title: "Worldpatch Courier",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0797-worldpatch-courier",
    canonical: "host",
    screenshot: "/shots/game-0797-worldpatch-courier.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0797 worldpatch courier, a small original browser game.",
  },
  // Pearl Drift. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0798-pearl-drift.
  {
    slug: "game-0798-pearl-drift",
    surface: "app",
    title: "Pearl Drift",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0798-pearl-drift",
    canonical: "host",
    screenshot: "/shots/game-0798-pearl-drift.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0798 pearl drift, a small original browser game.",
  },
  // Brass Relay. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0799-brass-relay.
  {
    slug: "game-0799-brass-relay",
    surface: "app",
    title: "Brass Relay",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0799-brass-relay",
    canonical: "host",
    screenshot: "/shots/game-0799-brass-relay.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0799 brass relay, a small original browser game.",
  },
  // Prismfall Chamber. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0800-prismfall-chamber.
  {
    slug: "game-0800-prismfall-chamber",
    surface: "app",
    title: "Prismfall Chamber",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0800-prismfall-chamber",
    canonical: "host",
    screenshot: "/shots/game-0800-prismfall-chamber.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0800 prismfall chamber, a small original browser game.",
  },
  // Sixfold Resonance. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0933-sixfold-resonance.
  {
    slug: "game-0933-sixfold-resonance",
    surface: "app",
    title: "Sixfold Resonance",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0933-sixfold-resonance",
    canonical: "host",
    screenshot: "/shots/game-0933-sixfold-resonance.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0933 sixfold resonance, a small original browser game.",
  },
  // Vermilion Vector. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0934-vermilion-vector.
  {
    slug: "game-0934-vermilion-vector",
    surface: "app",
    title: "Vermilion Vector",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0934-vermilion-vector",
    canonical: "host",
    screenshot: "/shots/game-0934-vermilion-vector.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0934 vermilion vector, a small original browser game.",
  },
  // Sumsmith Alley. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0935-sumsmith-alley.
  {
    slug: "game-0935-sumsmith-alley",
    surface: "app",
    title: "Sumsmith Alley",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0935-sumsmith-alley",
    canonical: "host",
    screenshot: "/shots/game-0935-sumsmith-alley.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0935 sumsmith alley, a small original browser game.",
  },
  // Foldform Passage. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0936-foldform-passage.
  {
    slug: "game-0936-foldform-passage",
    surface: "app",
    title: "Foldform Passage",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0936-foldform-passage",
    canonical: "host",
    screenshot: "/shots/game-0936-foldform-passage.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0936 foldform passage, a small original browser game.",
  },
  // Cloudline Landing. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0937-cloudline-landing.
  {
    slug: "game-0937-cloudline-landing",
    surface: "app",
    title: "Cloudline Landing",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0937-cloudline-landing",
    canonical: "host",
    screenshot: "/shots/game-0937-cloudline-landing.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0937 cloudline landing, a small original browser game.",
  },
  // Pocket Freight. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0938-pocket-freight.
  {
    slug: "game-0938-pocket-freight",
    surface: "app",
    title: "Pocket Freight",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0938-pocket-freight",
    canonical: "host",
    screenshot: "/shots/game-0938-pocket-freight.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0938 pocket freight, a small original browser game.",
  },
  // Nebula Survey. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0939-nebula-survey.
  {
    slug: "game-0939-nebula-survey",
    surface: "app",
    title: "Nebula Survey",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0939-nebula-survey",
    canonical: "host",
    screenshot: "/shots/game-0939-nebula-survey.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0939 nebula survey, a small original browser game.",
  },
  // Cadence Gallery. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0940-cadence-gallery.
  {
    slug: "game-0940-cadence-gallery",
    surface: "app",
    title: "Cadence Gallery",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0940-cadence-gallery",
    canonical: "host",
    screenshot: "/shots/game-0940-cadence-gallery.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0940 cadence gallery, a small original browser game.",
  },
  // Lamplight Toybox. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0941-lamplight-toybox.
  {
    slug: "game-0941-lamplight-toybox",
    surface: "app",
    title: "Lamplight Toybox",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0941-lamplight-toybox",
    canonical: "host",
    screenshot: "/shots/game-0941-lamplight-toybox.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0941 lamplight toybox, a small original browser game.",
  },
  // Soulthread Ruins. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0942-soulthread-ruins.
  {
    slug: "game-0942-soulthread-ruins",
    surface: "app",
    title: "Soulthread Ruins",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0942-soulthread-ruins",
    canonical: "host",
    screenshot: "/shots/game-0942-soulthread-ruins.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0942 soulthread ruins, a small original browser game.",
  },
  // Brass Fuse Agency. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0943-brass-fuse-agency.
  {
    slug: "game-0943-brass-fuse-agency",
    surface: "app",
    title: "Brass Fuse Agency",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0943-brass-fuse-agency",
    canonical: "host",
    screenshot: "/shots/game-0943-brass-fuse-agency.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0943 brass fuse agency, a small original browser game.",
  },
  // Counterpoint Machines. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0944-counterpoint-machines.
  {
    slug: "game-0944-counterpoint-machines",
    surface: "app",
    title: "Counterpoint Machines",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0944-counterpoint-machines",
    canonical: "host",
    screenshot: "/shots/game-0944-counterpoint-machines.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0944 counterpoint machines, a small original browser game.",
  },
  // Fourfold Tea Sudoku. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0945-fourfold-tea-sudoku.
  {
    slug: "game-0945-fourfold-tea-sudoku",
    surface: "app",
    title: "Fourfold Tea Sudoku",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0945-fourfold-tea-sudoku",
    canonical: "host",
    screenshot: "/shots/game-0945-fourfold-tea-sudoku.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0945 fourfold tea sudoku, a small original browser game.",
  },
  // Blackout Toolbench. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0946-blackout-toolbench.
  {
    slug: "game-0946-blackout-toolbench",
    surface: "app",
    title: "Blackout Toolbench",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0946-blackout-toolbench",
    canonical: "host",
    screenshot: "/shots/game-0946-blackout-toolbench.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0946 blackout toolbench, a small original browser game.",
  },
  // Trapward Ledger. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0947-trapward-ledger.
  {
    slug: "game-0947-trapward-ledger",
    surface: "app",
    title: "Trapward Ledger",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0947-trapward-ledger",
    canonical: "host",
    screenshot: "/shots/game-0947-trapward-ledger.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0947 trapward ledger, a small original browser game.",
  },
  // Coilgate Temple. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0948-coilgate-temple.
  {
    slug: "game-0948-coilgate-temple",
    surface: "app",
    title: "Coilgate Temple",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0948-coilgate-temple",
    canonical: "host",
    screenshot: "/shots/game-0948-coilgate-temple.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0948 coilgate temple, a small original browser game.",
  },
  // Reed Crate Crossing. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0949-reed-crate-crossing.
  {
    slug: "game-0949-reed-crate-crossing",
    surface: "app",
    title: "Reed Crate Crossing",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0949-reed-crate-crossing",
    canonical: "host",
    screenshot: "/shots/game-0949-reed-crate-crossing.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0949 reed crate crossing, a small original browser game.",
  },
  // Truthloom. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0950-truthloom.
  {
    slug: "game-0950-truthloom",
    surface: "app",
    title: "Truthloom",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0950-truthloom",
    canonical: "host",
    screenshot: "/shots/game-0950-truthloom.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0950 truthloom, a small original browser game.",
  },
  // Two Bells Mate. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0951-two-bells-mate.
  {
    slug: "game-0951-two-bells-mate",
    surface: "app",
    title: "Two Bells Mate",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0951-two-bells-mate",
    canonical: "host",
    screenshot: "/shots/game-0951-two-bells-mate.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0951 two bells mate, a small original browser game.",
  },
  // Sliding Salon. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0952-sliding-salon.
  {
    slug: "game-0952-sliding-salon",
    surface: "app",
    title: "Sliding Salon",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0952-sliding-salon",
    canonical: "host",
    screenshot: "/shots/game-0952-sliding-salon.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0952 sliding salon, a small original browser game.",
  },
  // Harvest Stitch. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0953-harvest-stitch.
  {
    slug: "game-0953-harvest-stitch",
    surface: "app",
    title: "Harvest Stitch",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0953-harvest-stitch",
    canonical: "host",
    screenshot: "/shots/game-0953-harvest-stitch.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0953 harvest stitch, a small original browser game.",
  },
  // Brass Lantern Griddler. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0954-brass-lantern-griddler.
  {
    slug: "game-0954-brass-lantern-griddler",
    surface: "app",
    title: "Brass Lantern Griddler",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0954-brass-lantern-griddler",
    canonical: "host",
    screenshot: "/shots/game-0954-brass-lantern-griddler.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0954 brass lantern griddler, a small original browser game.",
  },
  // Gemini Quarry. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0955-gemini-quarry.
  {
    slug: "game-0955-gemini-quarry",
    surface: "app",
    title: "Gemini Quarry",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0955-gemini-quarry",
    canonical: "host",
    screenshot: "/shots/game-0955-gemini-quarry.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0955 gemini quarry, a small original browser game.",
  },
  // Moonroll Nooks. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0956-moonroll-nooks.
  {
    slug: "game-0956-moonroll-nooks",
    surface: "app",
    title: "Moonroll Nooks",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0956-moonroll-nooks",
    canonical: "host",
    screenshot: "/shots/game-0956-moonroll-nooks.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0956 moonroll nooks, a small original browser game.",
  },
  // Velvet Cube Depot. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0957-velvet-cube-depot.
  {
    slug: "game-0957-velvet-cube-depot",
    surface: "app",
    title: "Velvet Cube Depot",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0957-velvet-cube-depot",
    canonical: "host",
    screenshot: "/shots/game-0957-velvet-cube-depot.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0957 velvet cube depot, a small original browser game.",
  },
  // Hexwarden. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0958-hexwarden.
  {
    slug: "game-0958-hexwarden",
    surface: "app",
    title: "Hexwarden",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0958-hexwarden",
    canonical: "host",
    screenshot: "/shots/game-0958-hexwarden.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0958 hexwarden, a small original browser game.",
  },
  // Partition Observatory. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0959-partition-observatory.
  {
    slug: "game-0959-partition-observatory",
    surface: "app",
    title: "Partition Observatory",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0959-partition-observatory",
    canonical: "host",
    screenshot: "/shots/game-0959-partition-observatory.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0959 partition observatory, a small original browser game.",
  },
  // Crumbtrail Home. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0960-crumbtrail-home.
  {
    slug: "game-0960-crumbtrail-home",
    surface: "app",
    title: "Crumbtrail Home",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0960-crumbtrail-home",
    canonical: "host",
    screenshot: "/shots/game-0960-crumbtrail-home.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0960 crumbtrail home, a small original browser game.",
  },
  // Drifting Lantern Recall. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0961-drifting-lantern-recall.
  {
    slug: "game-0961-drifting-lantern-recall",
    surface: "app",
    title: "Drifting Lantern Recall",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0961-drifting-lantern-recall",
    canonical: "host",
    screenshot: "/shots/game-0961-drifting-lantern-recall.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0961 drifting lantern recall, a small original browser game.",
  },
  // Harbor Hearths. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0962-harbor-hearths.
  {
    slug: "game-0962-harbor-hearths",
    surface: "app",
    title: "Harbor Hearths",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0962-harbor-hearths",
    canonical: "host",
    screenshot: "/shots/game-0962-harbor-hearths.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0962 harbor hearths, a small original browser game.",
  },
  // Lumen Sprouts. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0963-lumen-sprouts.
  {
    slug: "game-0963-lumen-sprouts",
    surface: "app",
    title: "Lumen Sprouts",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0963-lumen-sprouts",
    canonical: "host",
    screenshot: "/shots/game-0963-lumen-sprouts.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0963 lumen sprouts, a small original browser game.",
  },
  // Clue Cargo. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0964-clue-cargo.
  {
    slug: "game-0964-clue-cargo",
    surface: "app",
    title: "Clue Cargo",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0964-clue-cargo",
    canonical: "host",
    screenshot: "/shots/game-0964-clue-cargo.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0964 clue cargo, a small original browser game.",
  },
  // Glass Choir. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0965-glass-choir.
  {
    slug: "game-0965-glass-choir",
    surface: "app",
    title: "Glass Choir",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0965-glass-choir",
    canonical: "host",
    screenshot: "/shots/game-0965-glass-choir.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0965 glass choir, a small original browser game.",
  },
  // Quarterfall Observatory. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0966-quarterfall-observatory.
  {
    slug: "game-0966-quarterfall-observatory",
    surface: "app",
    title: "Quarterfall Observatory",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0966-quarterfall-observatory",
    canonical: "host",
    screenshot: "/shots/game-0966-quarterfall-observatory.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0966 quarterfall observatory, a small original browser game.",
  },
  // Bramble Caravan. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0967-bramble-caravan.
  {
    slug: "game-0967-bramble-caravan",
    surface: "app",
    title: "Bramble Caravan",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0967-bramble-caravan",
    canonical: "host",
    screenshot: "/shots/game-0967-bramble-caravan.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0967 bramble caravan, a small original browser game.",
  },
  // Pocket Parkway. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0968-pocket-parkway.
  {
    slug: "game-0968-pocket-parkway",
    surface: "app",
    title: "Pocket Parkway",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0968-pocket-parkway",
    canonical: "host",
    screenshot: "/shots/game-0968-pocket-parkway.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0968 pocket parkway, a small original browser game.",
  },
  // Orbital Ostinato. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0969-orbital-ostinato.
  {
    slug: "game-0969-orbital-ostinato",
    surface: "app",
    title: "Orbital Ostinato",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0969-orbital-ostinato",
    canonical: "host",
    screenshot: "/shots/game-0969-orbital-ostinato.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0969 orbital ostinato, a small original browser game.",
  },
  // Nested Current. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0970-nested-current.
  {
    slug: "game-0970-nested-current",
    surface: "app",
    title: "Nested Current",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0970-nested-current",
    canonical: "host",
    screenshot: "/shots/game-0970-nested-current.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0970 nested current, a small original browser game.",
  },
  // Veiled Freight. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0971-veiled-freight.
  {
    slug: "game-0971-veiled-freight",
    surface: "app",
    title: "Veiled Freight",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0971-veiled-freight",
    canonical: "host",
    screenshot: "/shots/game-0971-veiled-freight.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0971 veiled freight, a small original browser game.",
  },
  // Prism Prospect. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0972-prism-prospect.
  {
    slug: "game-0972-prism-prospect",
    surface: "app",
    title: "Prism Prospect",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0972-prism-prospect",
    canonical: "host",
    screenshot: "/shots/game-0972-prism-prospect.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0972 prism prospect, a small original browser game.",
  },
  // Ember Frames. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0973-ember-frames.
  {
    slug: "game-0973-ember-frames",
    surface: "app",
    title: "Ember Frames",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0973-ember-frames",
    canonical: "host",
    screenshot: "/shots/game-0973-ember-frames.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0973 ember frames, a small original browser game.",
  },
  // Tangerine Passage. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0974-tangerine-passage.
  {
    slug: "game-0974-tangerine-passage",
    surface: "app",
    title: "Tangerine Passage",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0974-tangerine-passage",
    canonical: "host",
    screenshot: "/shots/game-0974-tangerine-passage.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0974 tangerine passage, a small original browser game.",
  },
  // Polar Ferris. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0975-polar-ferris.
  {
    slug: "game-0975-polar-ferris",
    surface: "app",
    title: "Polar Ferris",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0975-polar-ferris",
    canonical: "host",
    screenshot: "/shots/game-0975-polar-ferris.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0975 polar ferris, a small original browser game.",
  },
  // Parcel Quilt. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0976-parcel-quilt.
  {
    slug: "game-0976-parcel-quilt",
    surface: "app",
    title: "Parcel Quilt",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0976-parcel-quilt",
    canonical: "host",
    screenshot: "/shots/game-0976-parcel-quilt.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0976 parcel quilt, a small original browser game.",
  },
  // Velvet Splat. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0977-velvet-splat.
  {
    slug: "game-0977-velvet-splat",
    surface: "app",
    title: "Velvet Splat",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0977-velvet-splat",
    canonical: "host",
    screenshot: "/shots/game-0977-velvet-splat.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0977 velvet splat, a small original browser game.",
  },
  // Chromatic Accord. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0978-chromatic-accord.
  {
    slug: "game-0978-chromatic-accord",
    surface: "app",
    title: "Chromatic Accord",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0978-chromatic-accord",
    canonical: "host",
    screenshot: "/shots/game-0978-chromatic-accord.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0978 chromatic accord, a small original browser game.",
  },
  // True Door Cartography. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0979-true-door-cartography.
  {
    slug: "game-0979-true-door-cartography",
    surface: "app",
    title: "True Door Cartography",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0979-true-door-cartography",
    canonical: "host",
    screenshot: "/shots/game-0979-true-door-cartography.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0979 true door cartography, a small original browser game.",
  },
  // Stackyard Sonata. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0980-stackyard-sonata.
  {
    slug: "game-0980-stackyard-sonata",
    surface: "app",
    title: "Stackyard Sonata",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0980-stackyard-sonata",
    canonical: "host",
    screenshot: "/shots/game-0980-stackyard-sonata.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0980 stackyard sonata, a small original browser game.",
  },
  // Ink Interference. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0981-ink-interference.
  {
    slug: "game-0981-ink-interference",
    surface: "app",
    title: "Ink Interference",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0981-ink-interference",
    canonical: "host",
    screenshot: "/shots/game-0981-ink-interference.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0981 ink interference, a small original browser game.",
  },
  // Cohort Keyway. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0982-cohort-keyway.
  {
    slug: "game-0982-cohort-keyway",
    surface: "app",
    title: "Cohort Keyway",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0982-cohort-keyway",
    canonical: "host",
    screenshot: "/shots/game-0982-cohort-keyway.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0982 cohort keyway, a small original browser game.",
  },
  // Rook Lantern Academy. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0983-rook-lantern-academy.
  {
    slug: "game-0983-rook-lantern-academy",
    surface: "app",
    title: "Rook Lantern Academy",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0983-rook-lantern-academy",
    canonical: "host",
    screenshot: "/shots/game-0983-rook-lantern-academy.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0983 rook lantern academy, a small original browser game.",
  },
  // Aqua Alley Dispatch. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0984-aqua-alley-dispatch.
  {
    slug: "game-0984-aqua-alley-dispatch",
    surface: "app",
    title: "Aqua Alley Dispatch",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0984-aqua-alley-dispatch",
    canonical: "host",
    screenshot: "/shots/game-0984-aqua-alley-dispatch.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0984 aqua alley dispatch, a small original browser game.",
  },
  // Gyre Galleries. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0985-gyre-galleries.
  {
    slug: "game-0985-gyre-galleries",
    surface: "app",
    title: "Gyre Galleries",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0985-gyre-galleries",
    canonical: "host",
    screenshot: "/shots/game-0985-gyre-galleries.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0985 gyre galleries, a small original browser game.",
  },
  // Allele Orchard. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0986-allele-orchard.
  {
    slug: "game-0986-allele-orchard",
    surface: "app",
    title: "Allele Orchard",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0986-allele-orchard",
    canonical: "host",
    screenshot: "/shots/game-0986-allele-orchard.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0986 allele orchard, a small original browser game.",
  },
  // Amber Chamber Atlas. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0987-amber-chamber-atlas.
  {
    slug: "game-0987-amber-chamber-atlas",
    surface: "app",
    title: "Amber Chamber Atlas",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0987-amber-chamber-atlas",
    canonical: "host",
    screenshot: "/shots/game-0987-amber-chamber-atlas.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0987 amber chamber atlas, a small original browser game.",
  },
  // Pocket Arithmetic Bureau. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0988-pocket-arithmetic-bureau.
  {
    slug: "game-0988-pocket-arithmetic-bureau",
    surface: "app",
    title: "Pocket Arithmetic Bureau",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0988-pocket-arithmetic-bureau",
    canonical: "host",
    screenshot: "/shots/game-0988-pocket-arithmetic-bureau.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0988 pocket arithmetic bureau, a small original browser game.",
  },
  // Rooftop Quartet. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0989-rooftop-quartet.
  {
    slug: "game-0989-rooftop-quartet",
    surface: "app",
    title: "Rooftop Quartet",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0989-rooftop-quartet",
    canonical: "host",
    screenshot: "/shots/game-0989-rooftop-quartet.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0989 rooftop quartet, a small original browser game.",
  },
  // Prismatic Platforms. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0990-prismatic-platforms.
  {
    slug: "game-0990-prismatic-platforms",
    surface: "app",
    title: "Prismatic Platforms",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0990-prismatic-platforms",
    canonical: "host",
    screenshot: "/shots/game-0990-prismatic-platforms.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0990 prismatic platforms, a small original browser game.",
  },
  // Bridgewright Islet. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0991-bridgewright-islet.
  {
    slug: "game-0991-bridgewright-islet",
    surface: "app",
    title: "Bridgewright Islet",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0991-bridgewright-islet",
    canonical: "host",
    screenshot: "/shots/game-0991-bridgewright-islet.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0991 bridgewright islet, a small original browser game.",
  },
  // Synchronized Spectrum. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0992-synchronized-spectrum.
  {
    slug: "game-0992-synchronized-spectrum",
    surface: "app",
    title: "Synchronized Spectrum",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0992-synchronized-spectrum",
    canonical: "host",
    screenshot: "/shots/game-0992-synchronized-spectrum.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0992 synchronized spectrum, a small original browser game.",
  },
  // Arrowbrook Ledger. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0993-arrowbrook-ledger.
  {
    slug: "game-0993-arrowbrook-ledger",
    surface: "app",
    title: "Arrowbrook Ledger",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0993-arrowbrook-ledger",
    canonical: "host",
    screenshot: "/shots/game-0993-arrowbrook-ledger.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0993 arrowbrook ledger, a small original browser game.",
  },
  // Depthloom Cubes. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0994-depthloom-cubes.
  {
    slug: "game-0994-depthloom-cubes",
    surface: "app",
    title: "Depthloom Cubes",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0994-depthloom-cubes",
    canonical: "host",
    screenshot: "/shots/game-0994-depthloom-cubes.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0994 depthloom cubes, a small original browser game.",
  },
  // Hexa Tokens. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0995-hexa-tokens.
  {
    slug: "game-0995-hexa-tokens",
    surface: "app",
    title: "Hexa Tokens",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0995-hexa-tokens",
    canonical: "host",
    screenshot: "/shots/game-0995-hexa-tokens.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0995 hexa tokens, a small original browser game.",
  },
  // Civic Pocketbook. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0996-civic-pocketbook.
  {
    slug: "game-0996-civic-pocketbook",
    surface: "app",
    title: "Civic Pocketbook",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0996-civic-pocketbook",
    canonical: "host",
    screenshot: "/shots/game-0996-civic-pocketbook.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0996 civic pocketbook, a small original browser game.",
  },
  // Quiet Company. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0997-quiet-company.
  {
    slug: "game-0997-quiet-company",
    surface: "app",
    title: "Quiet Company",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0997-quiet-company",
    canonical: "host",
    screenshot: "/shots/game-0997-quiet-company.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0997 quiet company, a small original browser game.",
  },
  // Igloo Detour. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0998-igloo-detour.
  {
    slug: "game-0998-igloo-detour",
    surface: "app",
    title: "Igloo Detour",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0998-igloo-detour",
    canonical: "host",
    screenshot: "/shots/game-0998-igloo-detour.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0998 igloo detour, a small original browser game.",
  },
  // Longstaff Vault. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-0999-longstaff-vault.
  {
    slug: "game-0999-longstaff-vault",
    surface: "app",
    title: "Longstaff Vault",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-0999-longstaff-vault",
    canonical: "host",
    screenshot: "/shots/game-0999-longstaff-vault.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 0999 longstaff vault, a small original browser game.",
  },
  // Diagonal Undertow. Static browser game on digitalnative R2 techdemos-static; no auth. Repo over9000-apps/game-1000-diagonal-undertow.
  {
    slug: "game-1000-diagonal-undertow",
    surface: "app",
    title: "Diagonal Undertow",
    upstream: "https://pub-4501701e4d0343beafbbe85c4411530e.r2.dev/game-1000-diagonal-undertow",
    canonical: "host",
    screenshot: "/shots/game-1000-diagonal-undertow.webp",
    shippedAt: "2026-11-04",
    status: "live",
    blurb: "Play game 1000 diagonal undertow, a small original browser game.",
  },
];
export function getApp(surface: string, slug: string): AppEntry | undefined {
  const s = surface.toLowerCase();
  const g = slug.toLowerCase();
  return APPS.find((app) => app.surface === s && app.slug === g);
}

/**
 * Routing lookup. Slugs are unique. If two rows ever share a slug, prefer
 * surface `app`, otherwise the first row — do not leave the door ambiguous.
 */
export function pickBySlug(apps: readonly AppEntry[], slug: string): AppEntry | undefined {
  const g = slug.toLowerCase();
  const matches = apps.filter((app) => app.slug === g);
  if (matches.length === 0) return undefined;
  return matches.find((app) => app.surface === "app") ?? matches[0];
}

export function getAppBySlug(slug: string): AppEntry | undefined {
  return pickBySlug(APPS, slug);
}

/** Two-letter ISO code. Null or omitted means English (EN). */
export const LANGUAGE_CODE = /^[A-Z]{2}$/;

/** Tag shown on the hub. English, missing, and invalid codes stay blank. */
export function languageTag(language: string | null | undefined): string | null {
  if (!language || language === "EN" || !LANGUAGE_CODE.test(language)) return null;
  return language;
}

/**
 * Former public slugs. Ambient redirects these doors to the current slug.
 * The old name is not a registry row.
 */
export const SLUG_REDIRECTS: Readonly<Record<string, string>> = {
  "woman-excite-crawl": "woman-excite-index",
  "cadrian-brand-guidelines": "brand-guidelines-kit",
  "find-backlink-sites-mwo8v": "find-backlink-sites",
  "command-pipeline-structure-zqwa0": "command-pipeline-structure",
  "organize-prompts-agents-iznp8": "organize-prompts-agents",
  "4d-game-state-system-mtxeh": "4d-game-state-system",
  "benchmark-card-storage-vytwn": "benchmark-card-storage",
  "geoquery-nearby-locations-0z8vv": "geoquery-nearby-locations",
  "mrbeast-salesforce-setup-fiecl": "mrbeast-salesforce-setup",
  "research-paper-review-dolxg": "research-paper-review",
  "dq7-3d-world-map-l8c2k": "dq7-3d-world-map",
  "socialmaxxing-charisma-guide-rtiuc": "socialmaxxing-charisma-guide",
  "friday-13th-counter-dvrnn": "friday-13th-counter",
  "notion-api-integration-gl8qw": "notion-api-integration",
  "obsidian-claude-workflow-glzwo": "obsidian-claude-workflow",
  "pet-papercraft-wizard-xx0ok": "pet-papercraft-wizard",
  "investigate-camofox-browser-0eb8e": "investigate-camofox-browser",
  "setup-karpathy-skills-kivmn": "setup-karpathy-skills",
  "paperclip-api-skill-ykpls": "paperclip-api-skill",
  "plan-implementation-kksxh": "plan-implementation",
  "vercel-ios-app-spec-ukozl": "vercel-ios-app-spec",
  "create-business-plan-jlqkp": "create-business-plan",
  "investigate-graphify-60jka": "investigate-graphify",
  "openclaw-wrapper-business-jou4p": "openclaw-wrapper-business",
  "research-rust-adb-projects-l8cqf": "research-rust-adb-projects",
  "startup-subreddit-list-pcs4q": "startup-subreddit-list",
  "backlink-checker-product-2fc9u": "backlink-checker-product",
  "password-rotation-service-ljodm": "password-rotation-service",
  "student-visa-research-iw2ay": "student-visa-research",
  "nextjs-architecture-research-nmrou": "nextjs-architecture-research",
  "extract-claude-mem-data-lxuar": "extract-claude-mem-data",
};

export function redirectSlug(slug: string): string | undefined {
  const target = SLUG_REDIRECTS[slug.toLowerCase()];
  return target || undefined;
}

/** True when the ambient Worker should reverse-proxy this row on a prod door. */
export function isRoutable(app: AppEntry): boolean {
  return app.status === "live" && app.example !== true && Boolean(app.upstream);
}

/** Staging door origin. Empty or missing means that host is not proxied. */
export function stagingUpstreamOf(app: Pick<AppEntry, "upstreamStaging">): string | undefined {
  const upstream = app.upstreamStaging?.trim();
  return upstream || undefined;
}

/**
 * Staging host may proxy while the live door stays closed (`status` not live).
 * Example rows stay off both doors.
 */
export function isStagingRoutable(
  app: Pick<AppEntry, "upstreamStaging" | "example">,
): boolean {
  return app.example !== true && Boolean(stagingUpstreamOf(app));
}

export function appKey(app: Pick<AppEntry, "surface" | "slug">): string {
  return `${app.surface}/${app.slug}`;
}

/** Missing `storage` is `"none"` — the app does not require a D1. */
export function storageOf(app: Pick<AppEntry, "storage">): AppStorage {
  return app.storage ?? "none";
}

const SLOP_SLUG_RE = /^[a-z0-9-]+$/;
const SLOP_LOGICAL_TABLE_RE = /^[a-z][a-z0-9_]*$/;

/**
 * Physical table in `over9000-slop`: `{slug}__{table}` (AppSlug.table).
 * For `storage: "shared"` only. Dedicated apps and `over9000-id` use
 * unprefixed names in their own database. A slug containing `-` must be
 * double-quoted in SQL.
 */
export function slopTable(slug: string, table: string): string {
  const g = slug.toLowerCase();
  if (!SLOP_SLUG_RE.test(g) || isReservedSlug(g)) {
    throw new Error(`slop table slug is not an app slug: ${slug}`);
  }
  if (!SLOP_LOGICAL_TABLE_RE.test(table)) {
    throw new Error(`slop logical table must match [a-z][a-z0-9_]*: ${table}`);
  }
  const name = `${g}__${table}`;
  if (name.startsWith(SLOP_PLATFORM_PREFIX)) {
    throw new Error(`o9k__* tables are reserved for the platform: ${name}`);
  }
  return name;
}
